// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { gaModuleTips } from './ga-extras.js';
// Sprint 62.57.0 Slice E — R-57: the English, for structural parity.
import { enServiceCertificates, enVendorServiceCertificate } from './en-extras.js';
import { enVehicleMonitoring, enRecordLinks } from './en-extras.js';
export const ga = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: { present: 'Present', absent: 'Absent', pending: 'Pending' },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. English copy until the R-57 deferral (sprint 57.7.0).
    controlled_testing: {
        // Sprint 63.36.0: authored English fallback for this module; locale translation remains separate.
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Sínigh isteach i do chuntas',
        email: 'Ríomhphost',
        password: 'Pasfhocal',
        sign_in: 'Sínigh isteach',
        signing_in: 'Ag síniú isteach...',
        mfa_code: 'Cód MFA',
        mfa_placeholder: 'Cuir isteach cód 6 dhigit',
        mfa_instruction: "Cuir isteach an cód 6 dhigit ó d'aip fíordheimhnithe.",
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Fíoraigh',
        verifying: 'Ag fíorú...',
        forgot_password: 'Pasfhocal dearmadta?',
        account_locked: 'Tá an cuntas glasáilte. Bain triail as arís níos déanaí.',
        account_deactivated: 'Tá an cuntas díghníomhachtaithe. Déan teagmháil le do riarthóir.',
        invalid_mfa: 'Cód MFA neamhbhailí. Bain triail as arís.',
        invalid_credentials: 'Ríomhphost nó pasfhocal neamhbhailí.',
        login_failed: 'Theip ar an síniú isteach',
        session_limit_reached: 'Tá teorainn na seisiún gníomhach bainte amach ag do chuntas. Logáil amach ar ghléas eile nó iarr ar do riarthóir seisiún atá ann cheana a chúlghairm, agus bain triail eile as.',
        session_expired: 'Tá do sheisiún imithe in éag. Athlódáil an leathanach agus bain triail as arís.',
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    auth: {
        forgot_password: {
            title: 'Pasfhocal Dearmadta',
            subtitle: 'Cuir isteach do sheoladh ríomhphoist agus seolfaimid nasc chugat chun do phasfhocal a athshocrú.',
            send: 'Seol Nasc Athshocraithe',
            sending: 'Á Sheoladh...',
            success: 'Má tá an ríomhphost sin ann, tá nasc athshocraithe pasfhocail seolta.',
            back_to_login: 'Ar Ais go dtí Logáil Isteach',
        },
        reset_password: {
            title: 'Athshocraigh Pasfhocal',
            subtitle: 'Cuir isteach do phasfhocal nua thíos.',
            new_password: 'Pasfhocal Nua',
            confirm_password: 'Dearbhaigh Pasfhocal',
            reset: 'Athshocraigh Pasfhocal',
            resetting: 'Á Athshocrú...',
            success: 'Athsocraíodh do phasfhocal go rathúil.',
            go_to_login: 'Téigh go Logáil Isteach',
            password_mismatch: 'Ní hionann na pasfhocail.',
            invalid_token: 'Comhartha athshocraithe neamhbhailí nó ar iarraidh. Iarr nasc athshocraithe pasfhocail nua le do thoil.',
            requirements_title: 'Riachtanais pasfhocail:',
            password_requirements: 'Ní mór don phasfhocal na riachtanais seo a leanas a chomhlíonadh:',
            rules: {
                min_length: '12 charachtar ar a laghad',
                uppercase: 'Litir mhór amháin ar a laghad',
                lowercase: 'Litir bheag amháin ar a laghad',
                digit: 'Digit amháin ar a laghad',
                special: 'Carachtar speisialta amháin ar a laghad',
            },
        },
        verify_email: {
            title: 'Fíorú Ríomhphoist',
            verifying: 'Ag fíorú do sheoladh ríomhphoist...',
            success: 'Fíoraíodh do ríomhphost go rathúil.',
            invalid_token: 'Comhartha fíoraithe neamhbhailí nó ar iarraidh.',
            failed: "Theip ar fhíorú ríomhphoist. D'fhéadfadh an comhartha a bheith neamhbhailí nó imithe in éag.",
            go_to_login: 'Téigh go Logáil Isteach',
        },
        offline: {
            title: 'Tá tú as líne',
            message: 'Is cosúil go bhfuil do cheangal idirlín caillte agat. Seiceáil do líonra agus bain triail as arís le do thoil.',
        },
    },
    intake: {
        title: 'Iarratas ar Chearta Sonraí',
        subtitle: 'Cuir iarratas ar chearta sonraí isteach chuig ár n-eagraíocht',
        fields: {
            full_name: 'Ainm Iomlán',
            email: 'Seoladh Ríomhphoist',
            phone: 'Uimhir Ghutháin (roghnach)',
            request_type: 'Cineál Iarratais',
            description: "Déan Cur Síos ar d'Iarratas",
            preferred_language: 'Teanga is Fearr Leat',
            consent: 'Toilím le próiseáil an iarratais seo',
        },
        submit: 'Cuir Iarratas Isteach',
        validation: {
            name_required: 'Tá an t-ainm iomlán riachtanach',
            email_required: 'Tá seoladh ríomhphoist bailí riachtanach',
            type_required: 'Roghnaigh cineál iarratais le do thoil',
            description_required: "Déan cur síos ar d'iarratas le do thoil",
            consent_required: 'Ní mór duit toiliú le próiseáil chun leanúint ar aghaidh',
        },
        error_title: 'Earráid',
        config_load_error: 'Níorbh fhéidir an chumraíocht a luchtú. Seiceáil an URL le do thoil.',
        organization_label: 'Eagraíocht',
        customer_reference: 'Uimhir Thagartha an Chustaiméara',
        country: 'Tír / Dlínse',
        preferred_channel: 'Cainéal Freagartha is Fearr',
        channels: {
            email: 'Ríomhphost',
            postal: 'Post',
            phone: 'Fón',
            portal: 'Tairseach',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Please keep your reference number.',
            title: 'Iarratas Faighte',
            message: "Tá d'iarratas taifeadta go rathúil. Is é do thagairt uimhir ná:",
            next_steps: "Próiseálfaimid d'iarratas chomh luath agus is féidir de réir an dlí is infheidhme. Is é an t-am próiseála measta ná 30 lá oibre.",
        },
        // English until Phase 25 (R-57).
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: "This kind of request is not one this organisation's installed frameworks provide for. Please choose another request type.",
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. Choose which of these you wish to raise.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    request_types: {
        access: 'Rochtain (DSAR)',
        deletion: 'Scriosadh',
        correction: 'Ceartú / Nuashonrú',
        objection: 'Agóid',
        portability: 'Iniomparthacht Sonraí',
        restriction: 'Srianadh Próiseála',
        consent_withdrawal: 'Toiliú a tharraingt siar',
        protective_measures: 'Bearta cosanta sonraí pearsanta a iarraidh',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    statuses: {
        new: 'Nua',
        in_review: 'Á Athbhreithniú',
        waiting_on_requester: 'Ag Fanacht ar an Iarratasóir',
        waiting_on_internal_data: 'Ag Fanacht ar Shonraí Inmheánacha',
        approved: 'Ceadaithe',
        closed: 'Dúnta',
        rejected: 'Diúltaithe',
    },
    admin: {
        nav: {
            rights_requests: 'Iarratais ar Chearta',
            templates: 'Teimpléid',
            response_templates: 'Teimpléid Fhreagartha',
            data_mapping: 'Mapáil Sonraí',
            governance: 'Rialachas',
            consent: 'Toiliú',
            assessments: 'Measúnuithe',
            classification: 'Aicmiú',
            monitoring: 'Monatóireacht',
            incidents: 'Teagmhais',
            onboarding: 'Ionduchtú',
            connectors: 'Nascóirí',
            vendor_risk: 'Riosca Soláthraí',
            deletion_orchestration: 'Ceapadóireacht Scriosaidh',
            discovery: 'Aimsiú Sonraí',
            incident_ops: 'Oibríochtaí Teagmhais',
            program_governance: 'Rialachas Cláir',
            access_accountability: 'Cuntasacht Rochtana',
            consent_properties: 'Airíonna Toilithe',
            localization: 'Logánú',
            regulatory: 'Rialála',
            partner_workspace: 'Spás Oibre Comhpháirtí',
            audit_trail: 'Rian Iniúchta',
            forseti: 'Forseti',
            settings: 'Socruithe',
            help_center: 'Ionad Cabhrach',
            dashboard: 'Painéal',
            platform: 'Ardán',
            integrations: 'Comhtháthuithe',
            cmp_dsar_sync: 'Sioncronú CMP DSAR',
            cmp_integration: 'Comhtháthú CMP',
            purpose_mapping: 'Mapáil Cuspóirí',
            webhook_config: 'Cumraíocht Webhook',
            bulk_sync: 'Sioncronú Baisc',
            privacy_scanner: 'Scanóir Príobháideachta',
            privacy_monitoring: 'Monatóireacht Príobháideachta',
            real_time_scanner: 'Scanóir Fíor-Ama',
            compliance_forms: 'Foirmeacha Comhlíontachta',
            forms_dashboard: 'Painéal Foirmeacha',
            form_templates: 'Teimpléid',
            form_catalog: 'Catalóg Foirmeacha',
            pack_catalog: 'Catalóg Pacáistí',
            org_groups: 'Grúpaí Eagraíochta',
            consolidation: 'Comhdhlúthú',
            organizations: 'Eagraíochtaí',
            org_list: 'Gach Eagraíocht',
            intake_configs: 'Cumraíochtaí Iontógála',
            task_management: 'Bainistíocht tascanna',
            unified_audit: 'Iniúchadh Aontaithe',
            evidence_packs: 'Pacáistí Fianaise',
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: 'Tréimhsí Iniúchta',
            regulatory_updates: 'Nuashonruithe Rialála',
            departments: 'Ranna',
        },
        sidebar_category_data_privacy_rights: 'Príobháideachas Sonraí & Cearta',
        sidebar_category_vendor_third_party: 'Bainistíocht Soláthróra & Tríú Páirtí',
        sidebar_category_risk_assessment: 'Riosca & Measúnú',
        sidebar_category_operations_automation: 'Oibríochtaí & Uathoibriú',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Bainistíocht Tasc & Sreabhadh Oibre',
        sidebar_groups: {
            consent_cmp_webhooks: 'CMP & cuacha Gréasáin',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            data_mapping_ropa: 'Clár RoPA',
            data_mapping_classification: 'Aicmiú',
            data_mapping_governance: 'Rialachas',
            access_live_monitoring: 'Monatóireacht Beo',
            access_reviews_investigations: 'Léirmheasanna & Imscrúduithe',
            access_evidence_policies: 'Fianaise & Beartais',
            classification_taxonomies: 'Tacsanomaíochtaí & Lipéid',
            classification_workflow: 'Sreabhadh oibre',
            classification_insights: 'Léargais',
            connectors_runtime: 'Am rite',
            connectors_due_diligence: 'Díchill chuí',
            connectors_coverage: 'Clúdach',
            cp_definitions: 'Sainmhínithe',
            cp_lifecycle: 'Saolré',
            cp_quality: 'Cáilíocht & Fianaise',
            deletion_execution: 'Forghníomhú',
            deletion_vendors: 'Díoltóirí',
            deletion_evidence: 'Fianaise & Comhtháthú',
            discovery_rules: 'Rialacha & Teimpléid',
            discovery_scanning: 'Scanadh',
            discovery_review: 'Sreabhadh Oibre Athbhreithnithe',
            discovery_insights: 'Léargais',
            governance_oversight: 'Maoirseacht',
            governance_reporting: 'Tuairisciú',
            governance_vendors: 'Díoltóirí & Trasteorann',
            governance_lifecycle: 'Saolré',
            incident_ops_dashboards: 'Deais',
            incident_ops_sla: 'SLA Cumraíocht',
            incident_ops_playbooks: 'Leabhair súgartha',
            incident_ops_vendor_regulator: 'Díoltóir & Rialtóir',
            incident_ops_evidence: 'Fianaise & Athbhreithniú',
            incidents_workload: 'Ualach oibre',
            incidents_regulatory: 'Rialála',
            incidents_reporting: 'Tuairisciú',
            localization_content: 'Ábhar',
            regulatory_frameworks: 'Frameworks',
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Infheictheacht',
            monitoring_metrics: 'Méadracht',
            monitoring_programs: 'Cláir',
            monitoring_tasks: 'Tascanna',
            monitoring_controls: 'Rialuithe',
            org_entities: 'Aonáin',
            org_config: 'Cumraíocht',
            org_frameworks: 'Creataí & Rolladh suas',
            partner_overview: 'Forbhreathnú',
            partner_partners: 'Comhpháirtithe',
            partner_delivery: 'Seachadadh',
            partner_support: 'Tacaíocht',
            platform_identity: 'Féiniúlacht & Róil',
            platform_access_control: 'Rialú Rochtana',
            platform_auth_session: 'Fíordheimhnithe & Seisiún',
            platform_access_reviews: 'Léirmheasanna Rochtana',
            platform_files: 'Comhaid',
            platform_notifications: 'Fógraí',
            platform_module_config: 'Cumraíocht an Mhodúil',
            platform_ops: 'Oibríochtaí & Logchomhaid',
            pg_health: 'Sláinte Clár',
            pg_maturity: 'Aibíocht',
            pg_audit: 'Iniúchadh & Fianaise',
            pg_training: 'Oiliúint',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Oibleagáidí',
            pg_documents: 'Doiciméid',
            vr_overview: 'Forbhreathnú',
            vr_onboarding: 'Ar bordáil',
            vr_assessment: 'Measúnú',
            vr_oversight: 'Maoirseacht',
        },
        tickets: {
            title: 'Cearta Iarratais',
            create: 'Cruthaigh Iarratas',
            filters: 'Scag',
            search_placeholder: 'Search tickets...',
            export_csv: 'Easpórtáil CSV',
            exporting: 'Á easpórtáil...',
            bulk_export_failed: 'Theip ar an mór-easpórtáil',
            bulk_export: {
                label: 'Easpórtáil',
                format: {
                    pdf: 'PDF Achoimre',
                    zip: 'ZIP (pacáistí fianaise in aghaidh an cháis)',
                    csv: 'CSV',
                },
                warning_over_50: "Ginfidh sé seo thart ar {count} pacáistí cás - d'fhéadfadh go dtógfadh sé roinnt nóiméad. Lean ort?",
                blocked_over_500: 'Caol an scagaire go 500 cás nó níos lú sula n-onnmhairítear ZIP (reatha: {count}).',
                success: 'Sábháladh mór-easpórtáil chuig Brabhsálaí Comhad ({count} cás)',
                failed: 'Theip ar an mór-easpórtáil',
                continue: 'Lean ort',
                cancel: 'Cealaigh',
            },
            no_results: 'Níl aon requests aimsithe',
            ticket_id: 'Ticéad ID',
            request_type: 'Iarratas Cineál',
            requester: 'Iarrthóir',
            source: 'Foinse',
            all_sources: 'Gach Foinse',
            assigned_to: 'Sannta To',
            created: 'Cruthaithe',
            load_error: 'Theip ar load cases. Bain triail eile as.',
            case_creation_failed: 'Theip ar chruthú cáis',
            business_unit: 'Aonad Gnó',
            reference: 'Tagairt',
            sla: {
                title: 'Stádas SLA',
                on_track: '{days} lá fágtha',
                approaching: 'Spriocdháta ag druidim',
                overdue: '{days} lá thar téarma',
                ack_overdue: '{hours}u thar spriocdháta admhála',
                ack_warning: 'Admháil le déanamh go luath',
                closed: 'Dúnta',
                completed_in: 'Críochnaithe i {days} lá',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Freagra Teimpléid',
            new: 'Nua Teimpléad',
            edit: 'Cuir in eagar Teimpléad',
            all_types: 'Gach Cineál',
            all_locales: 'Gach Logcháil',
            type_access: 'Rochtain/DSAR',
            type_deletion: 'Scriosadh',
            type_correction: 'Ceartúchán',
            type_objection: 'Agóid',
            type_portability: 'Iniomparthacht',
            type_other: 'Eile',
            type_restriction: 'Srian',
            type_all: 'All',
            locale_en: 'Béarla',
            locale_vi: 'Vítneaimis',
            locale_all: 'An dá cheann',
            name: 'Ainm',
            request_type: 'Iarratas Cineál',
            locale: 'Logcháil',
            subject: 'Ábhar',
            body: 'Corp',
            name_placeholder: 'Teimpléad ainm...',
            subject_placeholder: 'Ábhar ríomhphoist/litreach...',
            body_placeholder: 'Comhlacht teimpléad. Úsáid {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} mar sealbhóirí ionad.',
            available_placeholders: 'Ar fáil placeholders:',
            create_template: 'Cruthaigh Teimpléad',
            preview: 'Réamhamharc',
            close_preview: 'Dún Preview',
            default_badge: 'Réamhshocrú',
            no_templates: 'Níl aon teimpléid aimsithe',
            confirm_delete: 'Scrios this teimpléad?',
            load_error: 'Theip ar load teimpléid. Bain triail eile as.',
            create_error: 'Theip ar create teimpléad. Bain triail eile as.',
            update_error: 'Theip ar update teimpléad. Bain triail eile as.',
            delete_error: 'Theip ar delete teimpléad. Bain triail eile as.',
            preview_error: 'Theip ar preview teimpléad. Bain triail eile as.',
            response_templates_badge: 'Teimpléid Fhreagartha',
            browse_form_templates: 'Brabhsáil Teimpléid Foirmeacha Comhlíontachta',
            templates_loaded: 'Teimpléid luchtaithe',
            sample_organization: 'Eagraíocht samplach',
        },
        detail: {
            requester_info: 'Eolas Iarrthóra',
            status: 'Stádas',
            assignment: 'Sannadh',
            notes: 'Nótaí',
            audit_trail: 'Iniúchadh Trail',
            add_note: 'Cuir Nóta leis',
            case_not_found: 'Cás not found',
            load_error: 'Theip ar load case. Bain triail eile as.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: 'Theip ar add note. Bain triail eile as.',
            submitted_from: 'Curtha isteach ó',
            via_cmp: 'trí AesirX CMP',
            cmp_ticket: 'Ticéad CMP:',
            select_template: 'Roghnaigh teimpléad',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Sonraí',
                evidence: 'Fianaise',
                communications: 'Cumarsáidí',
                escalations: 'Ardú',
                audit_trail: 'Iniúchadh Trail',
                fulfillment: 'Comhlíonadh',
                response: 'Freagra',
                extension: 'Síneadh',
            },
            requester: {
                name: 'Ainm',
                email: 'Ríomhphost',
                phone: 'Fón',
                language: 'Teanga',
                source: 'Foinse',
                customer_ref: 'Tag',
                country: 'Tír',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Cainéal Roghnaithe',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'Ar an mbóthar ceart',
                due_soon: 'Dlite go luath',
                overdue: 'Thar téarma',
                days_remaining: 'd fágtha',
                due_date: 'Due Dáta',
                breached: 'Sáraithe',
                paused: 'Ar sos',
                total_paused_days: 'Paused laethanta Iomlána',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'laethanta Elapsed',
                progress: 'Dul chun cinn',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: 'Fíorú Aitheantais',
                verified: 'Fíoraithe',
                not_verified: 'Gan fíorú',
                method: 'Modh',
                notes: 'Nótaí',
                verified_by: 'Fíoraithe By',
                verified_at: 'Fíoraithe At',
                warning: 'Aitheantas gan deimhniú fós.',
                verify_button: 'Fíoraigh Identity',
                select_method: '-- Roghnaigh --',
                methods: {
                    email_confirmation: 'Ríomhphost Confirmation',
                    id_document: 'ID Doiciméad',
                    phone_call: 'Fón Call',
                    in_person: 'Go pearsanta',
                    other: 'Eile',
                },
            },
            closure: {
                reason: 'Cúis Dúnadh',
                summary: 'Closure Achoimre',
                summary_placeholder: 'Achoimre of how the request was resolved...',
                reason_required: 'Tá achoimre agus cúis dúnta riachtanach',
                response_not_sent: 'Ní mór freagra foirmiúil a sheoladh chuig an ábhar sonraí sula ndúntar an cás seo',
                reasons: {
                    fulfilled: 'Comhlíonta',
                    partially_fulfilled: 'Páirtchomhlíonta',
                    unfounded: 'Gan bhunús',
                    excessive: 'Iomarcach',
                    withdrawn: 'Aistarraingthe',
                    duplicate: 'Dúblach',
                    identity_unverified: 'Aitheantas Neamhfhíoraithe',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Easpórtáil',
            export_pdf: 'Easpórtáil PDF',
            export_zip: 'Easpórtáil ZIP',
            case_file_pdf: 'Easpórtáil PDF (Cáschomhad)',
            evidence_pack_zip: 'Easpórtáil ZIP (Pacáiste Fianaise)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            breach: {
                flag_btn: 'Bratach mar Sárú-Cúis',
                modal: {
                    title: 'Cás brataí mar chúis le sárú',
                    type_label: 'Cineál sárú',
                    type: {
                        misdirected_response: 'Freagra míthreorach',
                        unauthorised_disclosure: 'Nochtadh neamhúdaraithe',
                        identity_mismatch: 'Neamhréir aitheantais',
                        other: 'Eile',
                    },
                    description: 'Cur síos (roghnach)',
                    description_placeholder: 'Cad a tharla? (uasmhéid 1000 charachtair)',
                    confirm: 'Cuir bratach air agus cuir tús leis an gclog fógra',
                    cancel: 'Cealaigh',
                    flagging: 'Bratach...',
                },
                banner: {
                    title: 'Sárú faoi bhratach: {type}',
                    detected_at: 'Braitheadh ag {timestamp}',
                    filing_deadline: 'Spriocdháta fógra: i gceann {hours} uair an chloig',
                    overdue: 'Spriocdháta fógra: THAR TÉARMA',
                },
                toasts: {
                    flagged: 'Cás sainaitheanta mar chúis le sárú - cuireadh tús leis an gclog fógra',
                    flag_failed: 'Theip ar an gcás a chur in iúl mar chúis le sárú',
                },
            },
            export: {
                generating: 'Ag giniúint... fan le do thoil',
                success: 'Sábháladh an t-easpórtáil chuig Brabhsálaí Comhad',
                failed: 'Theip ar easpórtáil - seiceáil loga iniúchta',
            },
            no_notes: 'Níl aon nótaí fós',
            assign_placeholder: 'Úsáideoir ID to assign...',
            reassign: 'Athshann',
            evidence: {
                checklist_title: 'Fianaise Seicliosta',
                complete_pct: '% críochnaithe',
                no_checklist: 'Gan míreanna seicliosta',
                attachments: 'Ceangaltáin',
                upload_attachment: 'Uaslódáil ceangaltán',
                upload_button: 'Uaslódáil',
                uploading: 'Ag uaslódáil...',
                attachment_uploaded: 'Ceangaltán uaslódáilte go rathúil',
                upload_error: 'Theip ar uaslódáil an cheangaltáin',
                category: 'Catagóir',
                categories: {
                    identity_proof: 'Cruthúnas céannachta',
                    correspondence: 'Comhfhreagras',
                    internal_data: 'Sonraí inmheánacha',
                    legal: 'Dlíthiúil',
                    other: 'Eile',
                },
                no_attachments: 'Gan ceangaltáin',
                references_title: 'Fianaise Tagairtí',
                no_references: 'Gan tagairtí',
                add_reference: 'Cuir Tagairt leis',
                ref_type: 'Cineál',
                ref_value: 'Luach',
                ref_desc: 'Cur síos',
                ref_value_placeholder: 'URL, ríomhphost ID, or reference...',
                ref_desc_placeholder: 'Cur síos...',
                ref_types: {
                    url: 'URL',
                    email_id: 'Ríomhphost ID',
                    crm_record: 'CRM Taifead',
                    ticket_ref: 'Ticéad Tagairt',
                    other: 'Eile',
                },
            },
            communications: {
                title: 'Cumarsáidí',
                add: 'Cuir Message leis',
                no_records: 'Níor taifeadadh aon chumarsáid',
                direction: 'Treo',
                channel: 'Cainéal',
                visibility: 'Infheictheacht',
                subject: 'Ábhar',
                body: 'Corp',
                send: 'Seol',
                inbound: 'Isteach',
                outbound: 'Amach',
                internal: 'Inmheánach',
                external: 'Seachtrach',
                tab_all: 'All',
                tab_internal: 'Inmheánach',
                tab_external: 'Seachtrach',
                subject_placeholder: 'Ábhar (roghnach)',
                body_placeholder: 'Corp teachtaireachta...',
                channels: {
                    email: 'Ríomhphost',
                    portal: 'Tairseach',
                    phone: 'Fón',
                    postal: 'Poist',
                    in_person: 'Go pearsanta',
                },
                apply_template: 'Cuir Teimpléad i bhFeidhm',
                select_template: 'Roghnaigh teimpléad...',
                no_templates: 'Níl aon teimpléid ar fáil',
                templates_load_error: 'Templates could not be loaded',
                template_applied: 'Cuireadh teimpléad i bhfeidhm ar an bhfreagra.',
                template_apply_error: 'Theip ar chur i bhfeidhm an teimpléid.',
            },
            escalations: {
                title: 'Ardú Stair',
                no_events: 'No escalation imeacht',
                tier: 'Sraith',
                threshold: 'Tairseach',
                action: 'Gníomh',
                target: 'Sprioc',
                triggered_at: 'Truiceartha',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did. English until
                // Phase 25 (R-57).
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'Sioncrónú AesirX CMP',
                sync_status: 'Sync Stádas',
                last_synced: 'Sioncronaithe go Deireanach',
                not_synced: 'Gan Sioncronú',
                resync: 'Athshioncronaigh Anois',
                syncing: 'Ag sioncronú...',
                sync_history: 'Sync Stair (Last 10)',
                direction: 'Treo',
                time: 'Am',
                retries: 'Athiarachtaí',
                error_col: 'Earráid',
            },
            audit: {
                filter_event_placeholder: 'Scag by imeacht cineál...',
                filter_actor_placeholder: 'Scag by actor...',
            },
            fulfillment: {
                title: 'Comhlíonadh Roinne',
                assign_departments: 'Sann Ranna',
                department: 'Roinn',
                assignee: 'Sannaí',
                deadline: 'Spriocdháta',
                evidence_count: 'Fianaise',
                signoff: 'Síniú amach',
                signed_off: 'Sínithe amach',
                pending_signoff: 'Ar feitheamh',
                start_button: 'Tosaigh',
                complete_button: 'Marcáil mar Chríochnaithe',
                signoff_button: 'Cláraigh',
                no_tasks: 'Níl aon tascanna comhlíonta sannta fós.',
                tasks_table: 'Tascanna Comhlíonta',
                progress_label: 'Dul chun cinn Comhlíonta',
                overdue: 'thar téarma',
                assigned_success: 'Tasc comhlíonta roinne sannta go rathúil.',
                assign_error: 'Theip ar shannadh tasc comhlíonta.',
                signoff_success: 'Síniú amach roinne taifeadta.',
                signoff_error: 'Theip ar thaifeadadh síniú amach.',
                identity_required: 'Identity verification required before fulfillment actions.',
                remove_task: 'Remove Task',
                remove_success: 'Fulfillment task removed successfully.',
                add_evidence_btn: 'Cuir fianaise leis',
                evidence_uploaded: 'Fianaise uaslódáilte agus nasctha',
                evidence_upload_error: 'Theip ar uaslódáil fianaise',
                suggest_data_stores: 'Mol Stórais Sonraí',
                suggest_error: 'Theip ar luchtú moltaí stórais sonraí.',
                suggest_stores_title: 'Stórais Sonraí Molta',
                suggest_stores_none: 'Níl aon stór sonraí ag teacht leis an gcás seo. Cumraigh córais i Mapáil Sonraí chun moltaí a fheabhsú.',
                suggest_stores_add: 'Cuir le cás',
                suggest_stores_added: 'Stóras sonraí curtha leis an gcás',
                no_departments_title: 'Níl aon ranna cumraithe',
                no_departments_body: 'Ligeann Ranna duit tascanna comhlíonta DSR a threorú chuig feidhm (Príobháideachas, Dlí, Acmhainní Daonna, Comharchumainn TF). Suiteáil ranna samplacha nó iarr ar do riarthóir ranna a chumrú i socruithe na hEagraíochta.',
                no_departments_cta: 'Suiteáil sonraí samplacha',
                department_placeholder: 'Ainm na roinne...',
                assignee_placeholder: 'ID úsáideora le sannadh...',
                assign_button: 'Sann',
                status_pending: 'Ar feitheamh',
                status_in_progress: 'Ar siúl',
                status_completed: 'Críochnaithe',
                status_verified: 'Fíoraithe',
                confirm_approve: 'An bhfuil tú cinnte gur mian leat an síneadh seo a cheadú?',
                confirm_deny: 'An bhfuil tú cinnte gur mian leat an síneadh seo a dhiúltú?',
            },
            response: {
                title: 'Giniúint Freagraí',
                select_template: 'Teimpléad Freagraí',
                choose_template: 'Roghnaigh teimpléad...',
                generate: 'Gin Freagra',
                preview: 'Réamhamharc Freagraí',
                send: 'Seol Freagra',
                delivery_method: {
                    label: 'Modh Seachadta',
                    email: 'Ríomhphost',
                    portal: 'Tairseach',
                    postal: 'Ríomhphost poist',
                    in_person: 'Go pearsanta',
                },
                recipient: {
                    email: 'Ríomhphost faighteora',
                    portal: 'Aitheantas úsáideora tairseach',
                    address: 'Seoladh poist',
                },
                auto_generate_btn: 'Gin agus seol freagra',
                auto_generate_success: 'Freagra ginte agus seachadta',
                auto_generate_missing_recipient: 'Cuir faighteoir ar fáil le do thoil roimh é a sheoladh.',
                auto_fields: 'Réimsí líonta go huathoibríoch',
                case_id: 'ID an Cháis',
                requester_name: 'Ainm an Iarrthóra',
                rights_type: 'Cineál Ceart',
                history: 'Freagraí Seolta',
                generated_at: 'Ginte',
                confirmation: 'Dearbhú',
                sent_label: 'Seolta',
                draft_label: 'Dréacht',
                generated_success: 'Freagra ginte go rathúil.',
                generate_error: 'Theip ar ghiniúint an fhreagra.',
                sent_success: 'Freagra seolta go rathúil.',
                send_error: 'Theip ar sheoladh an fhreagra.',
                load_error: 'Theip ar luchtú teimpléid freagraí.',
                pdf_title: 'Litir fhreagra chuig an duine is ábhar sonraí',
                download_pdf: 'Íoslódáil PDF',
                download_all: 'Íoslódáil gach ceann',
                download_error: 'Theip ar íoslódáil an fhreagra',
            },
            extension: {
                title: 'Síntí Spriocdháta',
                acknowledgment_deadline: 'Spriocdháta Admhála',
                fulfillment_deadline: 'Spriocdháta Comhlíonta',
                request_extension: 'Iarr Síneadh',
                reason: 'Cúis',
                reason_placeholder: 'Cúis le hiarratas ar am breise...',
                additional_days: 'Laethanta Breise',
                submit_request: 'Cuir Iarratas Sínte Isteach',
                history: 'Stair Síntí',
                requested_at: 'Iarrtha',
                approve: 'Ceadaigh',
                deny: 'Diúltaigh',
                request_success: 'Iarratas sínte curtha isteach.',
                request_error: 'Theip ar chur isteach iarratas sínte.',
                approved_success: 'Síneadh ceadaithe.',
                denied_success: 'Síneadh diúltaithe.',
                decision_error: 'Theip ar phróiseáil cinneadh sínte.',
                status_pending: 'Ar feitheamh',
                status_approved: 'Ceadaithe',
                status_denied: 'Diúltaithe',
                confirm_approve: 'An bhfuil tú cinnte gur mian leat an t-iarratas sínte seo a cheadú?',
                confirm_deny: 'An bhfuil tú cinnte gur mian leat an t-iarratas sínte seo a dhiúltú?',
                decided_by: 'Cinneadh déanta ag',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'Easpórtáil CSV',
            exporting: 'Ag easpórtáil...',
            search_placeholder: 'Cuardaigh iniúchadh details...',
            filter_by_actor: 'Scag by actor...',
            all_event_types: 'Gach Cineál Imeachta',
            seq: '#',
            timestamp: 'Stampa Ama',
            event: 'Imeacht',
            case_col: 'Cás',
            actor: 'Gníomhaí',
            summary: 'Achoimre',
            no_events: 'Níl aon iniúchadh imeacht aimsithe',
            export_success: "D'éirigh le heaspórtáil an rian iniúchta.",
            export_error: 'Theip ar export iniúchadh csv. Bain triail eile as.',
            load_error: 'Theip ar load iniúchadh trail. Bain triail eile as.',
            announce_loaded: 'Rian iniúchta luchtaithe',
            start_date: 'Dáta tosaigh',
            end_date: 'Dáta deiridh',
            per_page: '{count} / leathanach',
            unknown: 'Anaithnid',
            source_filter: {
                label: 'Foinse',
                all: 'Gach',
                module: 'Modúl',
                forseti: 'Forseti',
                aria_label: 'Scag imeachtaí iniúchta de réir foinse',
            },
            event_types: {
                case_created: 'Cás Created',
                status_changed: 'Stádas Changed',
                case_assigned: 'Cás Sannta',
                note_added: 'Nóta Added',
                attachment_added: 'Ceangaltán Added',
                attachment_deleted: 'Ceangaltán Deleted',
                reference_added: 'Tagairt Added',
                checklist_updated: 'Seicliosta Updated',
                identity_verified: 'Identity Fíoraithe',
                closure_summary_added: 'Closure Achoimre',
                approval_granted: 'Ceadú Deonaithe',
                template_applied: 'Teimpléad Applied',
                export_generated: 'Easpórtáil Generated',
                sla_warning: 'SLA Rabhadh',
                sla_overdue: 'SLA Thar téarma',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: 'Rian Iniúchta Aontaithe',
                module_filter: 'Modúl',
                framework_filter: 'Creat',
                select_framework: 'Roghnaigh creat...',
                entity_type_filter: 'Cineál Aonáin',
                action_filter: 'Gníomh',
                actor_filter: 'Gníomhaí',
                select_actor: 'Roghnaigh gníomhaí...',
                search_filter: 'Cuardach',
                search_placeholder: 'Cuardaigh imeachtaí iniúchta...',
                filters_heading: 'Scagairí',
                date_range: 'Raon Dátaí',
                no_events_found: 'Níor aimsíodh aon imeachtaí iniúchta.',
                event_count: 'Líon na nImeachtaí',
                action: 'Gníomh',
                actor: 'Gníomhaí',
                date_range_start: 'Tús an raon dátaí',
                date_range_end: 'Deireadh an raon dátaí',
                filtering_events: 'Ag scagadh imeachtaí iniúchta',
                framework_codes: 'Cóid chreata',
                entity_type_col: 'Cineál aonáin',
                aria_table: 'Imeachtaí iniúchta aontaithe',
                page_of: 'Leathanach {page} as {total}',
                entity_types: {
                    user: 'Úsáideoir',
                    case: 'Cás',
                    incident: 'teagmhas',
                    assessment: 'Measúnú',
                    vendor: 'Díoltóir',
                    consent: 'Toiliú',
                    policy: 'Beartas',
                    data_map: 'Léarscáil Sonraí',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    // Sprint 62.57.0 Slice E — the processing-service certificate register's audit events.
                    service_certificate_certificate_recorded: 'Processing service certificate recorded',
                    service_certificate_certificate_corrected: 'Processing service certificate corrected',
                    service_certificate_certificate_document_added: 'Document added to a processing service certificate',
                    service_certificate_certificate_revocation_recorded: 'Certificate revocation recorded',
                    service_certificate_certificate_return_recorded: 'Return of the certificate recorded',
                    service_certificate_certificate_publication_recorded: 'Publication of a revocation recorded',
                    service_certificate_certificate_communication_recorded: 'Authority communication recorded on a certificate',
                    service_certificate_application_created: 'Certificate application opened',
                    service_certificate_application_updated: 'Certificate application updated',
                    service_certificate_application_part_recorded: 'Dossier part recorded',
                    service_certificate_application_condition_links_recorded: 'Impact-assessment filings linked',
                    service_certificate_application_filed: 'Certificate application filing recorded',
                    service_certificate_application_receipt_recorded: 'Receipt of the application recorded',
                    service_certificate_application_communication_recorded: 'Authority communication recorded on an application',
                    service_certificate_application_supplement_request_recorded: 'Deficiency notice recorded',
                    service_certificate_application_supplement_sent: 'Supplement sent recorded',
                    service_certificate_application_evaluation_recorded: 'Evaluation of the application recorded',
                    service_certificate_application_decision_recorded: 'Decision on the application recorded',
                    service_certificate_application_withdrawn: 'Certificate application withdrawn',
                    service_certificate_register_exported: 'Certificate register exported',
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'Cruthaigh',
                    update: 'Nuashonrú',
                    delete: 'Scrios',
                    view: 'Amharc',
                    export: 'Easpórtáil',
                    status_change: 'Athrú Stádais',
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: 'Pacáistí Fianaise Iniúchta',
                assemble_pack: 'Tionóil Pacáiste Fianaise',
                framework: 'Creat',
                select_framework: 'Roghnaigh creat...',
                framework_code_label: 'Cód creata',
                entity_types: 'Cineálacha Aonán',
                select_entity_types: 'Roghnaigh cineálacha aonán...',
                period: 'Tréimhse',
                period_start: 'Tús na tréimhse',
                period_end: 'Deireadh na tréimhse',
                completeness: 'Iomláine',
                no_packs_found: 'Níor aimsíodh aon phacáistí fianaise.',
                verify: 'Fíoraigh',
                export_pack: 'Easpórtáil',
                exporting: 'Ag easpórtáil...',
                pack_assembled: 'Pacáiste fianaise curtha le chéile',
                pack_exported: 'Pacáiste fianaise easpórtáilte',
                aria_table: 'Pacáistí fianaise iniúchta',
                aria_export_pack: 'Easpórtáil pacáiste',
                export_format: 'Formáid easpórtála',
                export_in_progress: 'Easpórtáil ar siúl...',
                status_draft: 'Dréacht',
                status_assembled: 'Tionólta',
                status_verified: 'Fíoraithe',
                status_submitted: 'Curtha isteach',
                entity_type_labels: {
                    user: 'Úsáideoir',
                    case: 'Cás',
                    incident: 'teagmhas',
                    assessment: 'Measúnú',
                    vendor: 'Díoltóir',
                    consent: 'Toiliú',
                    policy: 'Beartas',
                    data_map: 'Léarscáil Sonraí',
                },
                template_id: 'ID teimpléid',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'Modúl',
                    action: 'Gníomh',
                    actor_id: 'Aisteoir',
                    actor_role: 'Actor Role',
                    entity_type: 'Cineál Aonáin',
                    entity_id: 'Aitheantas aonáin',
                    summary: 'Achoimre',
                    timestamp: 'Stampa ama',
                    record_type: 'Record Type',
                    framework_code: 'Creatlach',
                },
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: 'Paca fianaise — Creat: {framework}',
                export_header_period: 'Tréimhse: {start} go {end}',
                export_header_completeness: 'Iomláine: {score}%',
                export_header_status: 'Stádas: {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: 'Tréimhsí Iniúchta',
                create_period: 'Cruthaigh Tréimhse Iniúchta',
                framework_code_label: 'Cód creata',
                select_framework: 'Roghnaigh creat...',
                period_label: 'Lipéad Tréimhse',
                period_start: 'Tús na tréimhse',
                period_end: 'Deireadh na tréimhse',
                start: 'Tosaigh',
                end: 'Deireadh',
                framework: 'Creat',
                retention_days: 'Laethanta Coinneála',
                section_period_dates: 'Dátaí Tréimhse',
                no_periods_found: 'Níor aimsíodh aon tréimhsí iniúchta.',
                period_created: 'Tréimhse iniúchta cruthaithe',
                aria_table: 'Tréimhsí iniúchta',
                suggested_boundaries: 'Teorainneacha molta',
                suggested_boundaries_info: "Díorthaítear teorainneacha tréimhse molta ó thimthriall tuairiscithe rialála gach creata. Coigeartaigh de réir riachtanais d'eagraíochta.",
                reporting_cycle: 'Timthriall tuairiscithe',
                suggested_start: 'Tús molta',
                suggested_end: 'Deireadh molta',
                suggested_next: 'An Chéad Tréimhse Eile a mholtar',
                cycle_monthly: 'go míosúil',
                cycle_quarterly: 'Ráithiúil',
                cycle_semi_annual: 'Leathbhliantúil',
                cycle_annual: 'Bliantúil',
                cycle_custom: 'Saincheaptha',
                period_deleted: 'Scriosadh an tréimhse iniúchta',
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Customer Tagairt',
            country: 'Tír / Dlínse',
            preferred_channel: 'Cainéal Roghnaithe',
            channels: {
                email: 'Ríomhphost',
                postal: 'Poist',
                phone: 'Fón',
                portal: 'Tairseach',
            },
            source: 'Foinse',
            sources: {
                admin_manual: 'Lámhleabhar admin',
                public_form: 'Foirm Phoiblí',
                cmp_gateway: 'CMP Geata',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Fón Faighte',
                postal_received: 'Poist Faighte',
                email_received: 'Ríomhphost Faighte',
                in_person: 'In-Duine',
                legacy_import: 'Iompórtáil Oidhreacht',
            },
            required_fields: 'Iarratas cineál, ainm, and ríomhphost are required',
            create_error: 'Theip ar create case. Bain triail eile as.',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Socruithe',
            request_types: 'Cineálacha Iarratas',
            request_types_desc: 'Cumraigh na cineálacha iarratas atá cumasaithe don fhoirm ghlactha phoiblí.',
            notifications: 'Fógraí',
            ticket_numbering: 'Uimhriú Ticéad',
            ticket_format: 'Formáid reatha',
            ticket_prefix: 'Réimír',
            ticket_prefix_note: 'ní féidir a chumrú',
            sla_deadlines: 'Spriocdhátaí SLA',
            sla_desc: 'Socraigh an t-uasfhreagra ama (i laethanta féilire) do gach cineál iarratais. Taispeánfar rabhaidh do chásanna atá ag druidim leis an tairseach.',
            days: 'lá',
            warning_threshold: 'Tairseach rabhaidh',
            escalation_label: 'Cumasaigh ardú uathoibríoch ar shárú SLA',
            // fix-sprint #431 F1 — English until Phase 25 (R-57).
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'Sábháil Cumraíocht SLA',
            saving: 'Á Shábháil...',
            saved: 'Sábháilte go rathúil',
            announce_loaded: 'Socruithe luchtaithe',
            email_on_creation: 'Fógra ríomhphoist ar chruthú ticéid',
            send_confirmation: 'Seol ríomhphost dearbhaithe chuig an iarratasóir',
            users: 'Úsáideoirí',
            roles: 'Róil',
            api_keys: 'API Eochracha',
            mfa: 'MFA',
            sessions: 'Seisiúin',
            webhooks: 'Crúcaí gréasáin',
            modules: 'modúil',
            // Sprint 62.45.0 (AC17) — the organisation calendar section.
            calendar: {
                heading: 'Working days and holidays',
                effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
                no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
                working_days_legend: 'Working days of the week',
                working_days_hint: 'Working-day deadlines count only these days.',
                working_days_required: 'Select at least one working day.',
                day_0: 'Sunday',
                day_1: 'Monday',
                day_2: 'Tuesday',
                day_3: 'Wednesday',
                day_4: 'Thursday',
                day_5: 'Friday',
                day_6: 'Saturday',
                holidays_heading: 'Holidays',
                holidays_caption: 'Holidays entered: {count}',
                empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
                add_legend: 'Add a holiday',
                date_label: 'Date',
                label_label: 'Label (optional)',
                label_hint: 'A short name for the day, for example why the office is closed.',
                add: 'Add date',
                remove: 'Remove',
                remove_aria: 'Remove {date}',
                col_date: 'Date',
                col_label: 'Label',
                col_actions: 'Actions',
                no_label: 'No label',
                date_required: 'Enter a date.',
                date_out_of_range: 'Enter a date between {min} and {max}.',
                duplicate: '{date} is already in the list.',
                limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
                save: 'Save calendar',
                saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
                save_error: 'The calendar was not saved. Your entries are still here.',
                conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
                reload: 'Reload calendar',
                load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                last_saved: 'Last saved {date}.',
                never_saved: 'This calendar has not been saved here yet.',
                read_only: 'You can view this calendar but not change it.',
                unsaved: 'Unsaved changes.',
                holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
                extra_heading: 'Make-up working days',
                extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
                extra_caption: 'Make-up working days entered: {count}',
                extra_empty: 'No make-up working days entered.',
                extra_add_legend: 'Add a make-up working day',
                extra_date_label: 'Date worked',
                extra_label_label: 'Label for the day worked (optional)',
                extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
                extra_add: 'Add day worked',
                extra_remove_aria: 'Remove make-up working day {date}',
                extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
                extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
                extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
                extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
            },
            // fix-sprint #431 F1 — Settings › Escalation ladder. English until Phase 25 (R-57).
            escalation_ladder: {
                heading: 'Escalation ladder',
                intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
                off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
                on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
                suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
                caption: 'Escalation tiers: {count}',
                col_threshold: 'Share of period',
                col_name: 'Name',
                col_action: 'Action',
                col_targets: 'Notifies',
                col_channels: 'Sent by',
                col_actions: 'Actions',
                action_notify: 'Notify',
                action_escalate_to_role: 'Escalate to a role',
                action_reassign: 'Reassign',
                channel_in_app: 'In the platform',
                channel_email: 'Email',
                target_assignee: 'The assignee',
                target_role: 'Holders of {role}',
                target_users: 'Named: {names}',
                reassign_to: 'Moves the case to: {target}',
                threshold_breach: '100% (due date passed)',
                inactive: 'Inactive',
                edit: 'Edit',
                edit_aria: 'Edit the tier at {pct}%',
                remove: 'Remove',
                remove_aria: 'Remove the tier at {pct}%',
                add: 'Add tier',
                add_legend: 'Add a tier',
                edit_legend: 'Edit the tier at {pct}%',
                threshold_label: 'Share of the period elapsed (%)',
                threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
                name_label: 'Name (optional)',
                name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
                action_label: 'Action',
                action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
                role_label: 'Role',
                role_none: 'No role',
                users_label: 'Named people',
                users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
                user_select_label: 'Person to add',
                user_placeholder: 'Choose a person',
                user_add: 'Add person',
                user_remove_aria: 'Remove {name}',
                users_empty: 'No one named.',
                users_retry_subject: 'the list of people',
                channels_legend: 'Send by',
                channels_hint: 'Each person still receives only what their own notification preferences allow.',
                active_label: 'Active',
                apply: 'Apply to the ladder',
                cancel: 'Cancel',
                save: 'Save escalation ladder',
                saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
                unsaved: 'Unsaved changes.',
                save_error: 'The escalation ladder was not saved. Your changes are kept.',
                conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
                reload: 'Reload ladder',
                load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                read_only: 'You can view the escalation ladder but not change it.',
                empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
                last_saved: 'Last saved {date}.',
                err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
                err_too_many_tiers: 'A ladder holds at most {max} tiers.',
                err_invalid_value: 'Tier {n}: a value is not valid.',
                err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
                err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
                err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
                err_channels_required: 'Tier {n}: choose at least one way to send.',
                err_target_role_required: 'Tier {n}: choose the role to escalate to.',
                err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
                err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
                err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
            },
            deadline_labels: {
                access: 'Rochtain (DSAR)',
                deletion: 'Scriosadh',
                correction: 'Ceartú',
                portability: 'Iniomparthacht',
                objection: 'Agóid',
                restriction: 'Srianadh',
                consent_withdrawal: 'Toiliú a tharraingt siar',
                protective_measures: 'Bearta cosanta',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Sannadh Rannóg',
            assign_section: 'Sannadh Rannóg',
            section_ref: 'Tagairt Rannóige',
            section_label: 'Lipéad Rannóige',
            department_id: 'Roinn',
            assignee_id: 'Sannóir',
            mandatory: 'Éigeantach',
            assign: 'Sann',
            no_assignments: 'Níl aon sannadh rannóige ann',
            status: 'Stádas',
            completion: 'Críochnú',
            mark_complete: 'Críochnaigh',
        },
        consolidation: {
            title: 'Dul Chun Cinn an Chomhdhlúthaithe',
            add_child: 'Cuir Fo-Tasc leis',
            child_task_id: 'Aitheantas Fo-Taisce',
            department_id: 'Roinn',
            link: 'Nasc',
            overall_completion: 'Críochnú Foriomlán',
            departments_complete: 'Ranna Críochnaithe',
            overdue: 'Thar Dáta',
            ready: 'Réidh',
            not_ready: 'Neamhréidh',
            master_status: 'Príomhstádas',
            department_breakdown: 'Miondealú Ranna',
            department: 'Roinn',
            status: 'Stádas',
            completion: 'Críochnú',
            due_date: 'Dáta Dlite',
            overdue_flag: 'Thar Dáta',
            child_tasks: 'Fo-Tascanna',
            task: 'Tasc',
        },
        conflict_detection: {
            title: 'Braitheoireacht Coinbhleachta',
            unresolved: 'gan réiteach',
            no_conflicts: 'Níor braithadh coinbhleacht ar bith',
            section: 'Rannóg',
            field: 'Réimse',
            dept_a: 'Roinn A',
            value_a: 'Luach A',
            dept_b: 'Roinn B',
            value_b: 'Luach B',
            severity: 'Déine',
            status: 'Stádas',
            resolved: 'Réitithe',
            open: 'Oscailte',
            resolve: 'Réitigh',
        },
        org_maturity: {
            title: 'Aibíocht na hEagraíochta',
            departments: 'Ranna',
            department_hierarchy: 'Ordlathas Ranna',
            create_department: 'Cruthaigh Roinn',
            department_name: 'Ainm na Roinne',
            department_code: 'Cód',
            level: 'Leibhéal',
            division: 'Rannán',
            department: 'Roinn',
            team: 'Foireann',
            parent_department: 'Máthairroinn',
            head_user: 'Ceann Roinne',
            no_departments: 'Níl aon ranna cumraithe.',
            user_mappings: 'Mapáil Úsáideoirí',
            map_user: 'Mapáil Úsáideoir',
            user_id: 'ID Úsáideora',
            role: 'Ról',
            no_users: 'Níl aon úsáideoirí mapáilte don roinn seo.',
            remove: 'Bain',
            framework_assignments: 'Sannacháin Creatlach',
            assign_framework: 'Sann Creatlach',
            entity_id: 'ID Aonáin',
            entity_type: 'Cineál Aonáin',
            framework_id: 'ID Creatlaigh',
            framework_code: 'Cód Creatlaigh',
            no_assignments: 'Níl aon sannacháin creatlach.',
            compliance_rollup: 'Achoimre Comhlíontachta',
            entity_name: 'Aonán',
            total_assessments: 'Measúnuithe Iomlán',
            completed: 'Críochnaithe',
            completion_pct: 'Críochnú %',
            open_gaps: 'Bearnaí Oscailte',
            critical_gaps: 'Criticiúil',
            compliance_score: 'Scór',
            no_rollup: 'Níl aon sonraí achoimre comhlíontachta ar fáil.',
            entity_summary: 'Achoimre Comhlíontachta an Aonáin',
            frameworks_assigned: 'Creatlaigh',
            overall_score: 'Scór Foriomlán',
            load_departments_error: 'Theip ar na ranna a lódáil',
            load_users_error: 'Theip ar na húsáideoirí a lódáil',
            department_created: 'Roinn cruthaithe',
            department_created_announce: 'Roinn cruthaithe',
            create_department_error: 'Theip ar an roinn a chruthú',
            user_mapped: 'Úsáideoir mapáilte',
            user_mapped_announce: 'Úsáideoir mapáilte le roinn',
            map_user_error: 'Theip ar an t-úsáideoir a mhapáil',
            user_removed: 'Úsáideoir bainte',
            load_rollup_error: 'Theip ar an achoimre a lódáil',
            load_assignments_error: 'Theip ar na sannacháin a lódáil',
            framework_assigned: 'Creat sannta',
            framework_assigned_announce: 'Creat sannta don eintiteas',
            assign_framework_error: 'Theip ar chreat a shannadh',
            assignment_removed: 'Sannadh bainte',
            enter_entity_id_placeholder: 'Cuir isteach ID an eintitis chun sannadh a fheiceáil',
            entity_type_organization: 'Eagraíocht',
            entity_type_department: 'Roinn',
        },
        onboarding_maturity: {
            title: 'Aibíocht Onboarding',
            quick_starts: 'Tús Tapa',
            progress: 'Dul Chun Cinn ar bord',
            personas: 'Cosáin Pearsa',
            persona_onboarding: 'Pearsa ar Bord',
            quick_start_scenarios: 'Cásanna Mearthosaithe',
            start: 'Tosaigh',
            restart: 'Atosaigh',
            estimated_minutes: 'Miontuairiscí Measta',
            steps: 'Céimeanna',
            scenario: 'Cás',
            no_scenarios: 'Níl aon chásanna mear-thosaithe ar fáil.',
            persona_id: 'Aitheantas Pearsa',
            persona_label: 'Pearsa',
            role_description: 'Cur síos ar an Ról',
            primary_modules: 'Modúil Bunscoile',
            recommended_tours: 'Turais Molta',
            sample_data_modules: 'Modúil Sonraí Samplacha',
            quick_start_scenario: 'Cás Mearthosaithe',
            no_personas: 'Níl aon chosáin persona ar fáil.',
            framework_setup: 'Creataí rialála',
            framework_code: 'Creatchóid',
            framework_name: 'Ainm an Chreata',
            related_modules: 'Modúil Ghaolmhara',
            persona_relevance: 'Daoine Ábhartha',
            no_frameworks: 'Níl roghanna socraithe creata ar fáil.',
            tier_config: 'Cumraíocht Mhodúil Sraithe',
            tier: 'Sraith',
            available_modules: 'Modúil ar Fáil',
            module_count: 'Comhaireamh Modúl',
            overall_completion: 'Críochnú Foriomlán',
            wizard_completed: 'Draoi Críochnaithe',
            tours_completed: 'Turais Críochnaithe',
            quick_starts_completed: 'Tús Tapa Críochnaithe',
            selected_frameworks: 'Creataí Roghnaithe',
            no_progress: 'Níl aon sonraí dul chun cinn bordála ar fáil.',
            detected_persona: 'Pearsa Braite',
            current_tier: 'Sraith Reatha',
            completion_pct: 'Críochnú',
            progress_page: {
                loaded: 'Dul chun cinn luchtaithe',
                no_progress: 'Níl aon sonraí dul chun cinn ar fáil don tumadh isteach.',
                overall: 'Dul chun cinn iomlán',
                detected_persona: 'Pearsana aitheanta',
                tier: 'Leibhéal',
                modules: 'modúil',
                wizard: 'Draoi',
                completed: 'Críochnaithe',
                pending: 'Ar feitheamh',
                tours: 'Turais',
                quick_starts: 'Mearthosaithe',
                frameworks: 'Creataí roghnaithe',
                no_frameworks: 'Níor roghnaíodh aon chreataí.',
                go_to_wizard: 'Críochnaigh an Draoi Socraithe',
                framework_selection: 'Roghnú creata',
                framework_toggle: 'Cumasaigh nó díchumasaigh creataí do do chlár comhlíontachta.',
                framework_updated: 'Roghnú creata nuashonraithe.',
                view_tours: 'Féach ar thurais',
                view_quick_starts: 'Féach ar mhearthosuithe',
                // English until Phase 25 (R-57).
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: 'Roghnaigh pearsana chun modúil, turais agus cásanna mearthosaithe molta a fheiceáil.',
                no_personas: 'Níl aon chosáin pearsana ar fáil.',
                modules: 'Modúil',
                tours: 'Turais',
                quick_start: 'Mearthosú',
                details: 'Sonraí',
                primary_modules: 'Príomh-mhodúil',
                recommended_tours: 'Turais molta',
                framework_options: 'Creataí rialála',
                no_frameworks: 'Níl aon roghanna socraithe creata ar fáil.',
                no_frameworks_explain: "Níl aon chreataí rialála suiteáilte do d'eagraíocht go fóill, mar sin níl aon rud le socrú anseo.",
                no_frameworks_action: 'Suiteáil pacáiste margaidh chun na creataí a bhaineann leat a chur leis',
                start_tour: 'Tosaigh',
                no_tours: 'Níl aon turais ar fáil.',
                help_center: 'Ionad Cabhrach',
                no_articles: 'Níor aimsíodh aon alt.',
                primary: 'Príomhúil',
                framework_code: 'Cód creata',
                framework_label: 'Lipéad',
                region: 'Réigiún',
                category: 'Catagóir',
                enabled: 'Cumasaithe',
                disabled: 'Díchumasaithe',
                tier_modules: 'Cumraíocht modúl leibhéil',
                tiers_loaded: 'Cumraíochtaí leibhéil luchtaithe',
                no_tier_config: 'Níl aon chumraíocht leibhéil ar fáil.',
                view_tours: 'Féach ar thurais modúl',
                view_quick_start: 'Tosaigh mearthosú',
                toggle_framework: 'Scoránaigh creat',
                framework_updated: 'Roghnú creata nuashonraithe.',
                tier_desc_light: 'Bunghnéithe príobháideachais — bunúsacha comhlíontachta PDPL',
                tier_desc_standard: 'Sraith chomhlíontachta iomlán — gach modúl le sreafaí oibre',
                tier_desc_enterprise: 'Rialachas agus uathoibriú ardleibhéil — il-eintiteas, tuairisciú grúpa',
                persona_highlight: 'Do chuid modúl',
                aria_go_to_module: '{name} — téigh go dtí an modúl',
                aria_start_tour: 'Tosaigh {title}',
                aria_quick_start: 'Tús tapa: {title}',
                aria_help_article: 'Alt cabhrach: {title}',
                aria_view_framework: '{label}{primary} — féach ar shonraí an chreata',
                aria_primary_for_persona: '{name} (bunscoil don duine seo)',
                primary_for_persona_suffix: ' (bunscoil don duine seo)',
                aria_view_in_persona_paths: '{name} — amharc in Persona Paths',
                progress_of_total: '{completed} de {total} críochnaithe',
            },
            // Sprint 63.28.0 — Persona Paths revised. English copy until the R-57 deferral (sprint 57.7.0).
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select. English copy until the R-57 deferral (sprint 57.7.0).
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: 'Cleachtaigh sreafaí oibre coitianta le cásanna mearthosaithe treoraithe.',
                no_scenarios: 'Níl aon chásanna mearthosaithe ar fáil.',
                started: 'Mearthosú tosaithe',
                status_completed: 'Críochnaithe',
                status_in_progress: 'Ar siúl',
                status_not_started: 'Gan tosú',
                minutes: 'nóim',
                steps: 'céimeanna',
                step: 'Céim',
                restart: 'Atosú',
                start: 'Tosú',
                go_to_module: 'Téigh go dtí an modúl',
                next_step: 'An chéad chéim eile',
                complete: 'Críochnaigh',
                reset: 'Athshocraigh',
                step_of: 'as',
                action_navigate: 'Nascleanúint',
                action_form_fill: 'Líon isteach foirm',
                action_review: 'Athbhreithnigh',
                action_confirm: 'Deimhnigh',
                step_changed: 'Céim {current} as {total}: {title}',
                completed_message: 'Mearthosú críochnaithe go rathúil!',
                reset_message: 'Tá an mearthosú athshocraithe.',
            },
            persona_p_vn_01_label: 'Oifigeach Cosanta Sonraí Fiontair',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Úinéir Roinne',
            persona_p_vn_02_description: 'Ceannaire roinne atá freagrach as tuairisciú comhlíonta rannógach',
            persona_p_vn_03_label: 'Comhlíonadh Ríomhthráchtála',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Ceannaire Rialachais Sonraí',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Cibearshlándáil agus Oibríochtaí Dlí',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'Ceannaire Rialachais IS',
            persona_p_vn_07_description: 'Ceannaire a dhéanann maoirseacht ar rialachas córas IS, ar mheasúnuithe riosca agus ar aicmiú',
            persona_p_vn_08_label: 'Rialachas Trasteorann agus Soláthraithe',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Ceannaire Freagartha Sáruithe',
            persona_p_vn_09_description: 'Ceannaire a chomhordaíonn fógairt sáruithe agus freagairt teagmhas',
            persona_p_vn_10_label: 'Ceannaire Iniúchóireachta Inmheánaí',
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'Stiúrthóir sonraí AD & fostaithe',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Ceannaire comhlíonta fochuideachta ilnáisiúnta',
            persona_p_vn_12_description: 'OCP i bhfochuideachta Vítneaim de chuid cuideachta eachtrach a dteastaíonn ciseal comhlíonta áitiúil uaidh',
            persona_p_vn_13_label: 'Ceannaire comhlíonta oideachais',
            persona_p_vn_13_description: 'Ceannaire comhlíonta i scoil eachtrach a dhéanann cosaint sonraí daltaí agus tuismitheoirí a bhainistiú',
            qs_hr_consent_title: 'Cláraigh toilithe fostaithe',
            qs_hr_consent_description: 'Siúl trí chlárú toilithe fostaithe agus tuairisc chumhdaigh a ghiniúint.',
            qs_subsidiary_dpia_title: 'Cruthaigh measúnú MTSC fochuideachta',
            qs_subsidiary_dpia_description: 'Siúl trí chruthú MTSC do ghníomhaíocht próiseála fochuideachta.',
            qs_student_consent_title: 'Socraigh bailiú toilithe daltaí',
            qs_student_consent_description: 'Siúl trí chruthú polasaithe toilithe tuismitheoirí do shonraí daltaí.',
            persona_p_vn_14_label: 'Comhairleoir Dlí Seachtrach',
            persona_p_vn_14_description: 'Comhpháirtí sinsearach gnólachta dlí a chomhairlíonn iliomad eagraíochtaí cliant maidir le comhlíonadh PDPL',
            persona_p_vn_15_label: 'Bainisteoir Oibríochtaí Gnólachta Dlí',
            persona_p_vn_15_description: 'Comhordaitheoir rannpháirtíochta cliant a bhainistíonn rianú soláthairtí agus comhordú spriocdhátaí',
            persona_p_vn_16_label: 'Úinéir Gnó / POF',
            persona_p_vn_16_description: 'POF nó BG atá ina rialaitheoir sonraí de réir dlí faoi PDPL, freagrach as infheistíocht chomhlíonta',
            qs_dpia_filing_title: 'Cuir do chéad chomhdú MTSC i gcrích',
            qs_dept_compliance_title: 'Seol isteach do chéad rannóg comhlíonta roinne',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Socraigh láimhseáil sonraí síntiúsóirí teileachumarsáide',
            qs_data_flow_title: 'Mapáil do chéad sreabhadh sonraí',
            qs_incident_response_title: 'Cumraigh sreabhadh oibre freagartha teagmhas',
            qs_ai_risk_title: 'Rith measúnú riosca córais IS',
            qs_cross_border_title: 'Déan measúnú ar aistriú sonraí trasteorann',
            qs_breach_notification_title: 'Insamhail sreabhadh oibre fógartha sáraithe',
            qs_audit_trail_title: 'Athbhreithnigh conair iniúchóireachta comhlíonta',
            qs_legal_advisor_portfolio_title: 'Athbhreithnigh punann chomhlíonta ilchliant',
            qs_legal_advisor_portfolio_description: 'Athbhreithnigh stádas comhlíonta thar iliomad eagraíochtaí cliant.',
            qs_law_firm_ops_title: 'Rianaigh stádas soláthairtí cliant',
            qs_law_firm_ops_description: 'Déan monatóireacht ar dhul chun cinn soláthairtí comhairleacha agus ar rannpháirtíocht cliant.',
            qs_ceo_dashboard_title: 'Féach ar phainéal comhlíonta feidhmiúcháin',
            qs_ceo_dashboard_description: 'Athbhreithnigh stádas comhlíonta ardleibhéil agus méadrachtaí infheistíochta.',
        },
        rights_requests: {
            response_templates: {
                title: 'Teimpléid Freagartha',
                subtitle: 'Bainistigh na teimpléid litreacha freagartha DSR a úsáideann ResponseGenerator chun freagraí a dhréachtú ar ábhair sonraí.',
                create_btn: 'Teimpléad Nua',
                edit_btn: 'Cuir Teimpléad in Eagar',
                filter_right_type: 'Scag de réir Cineál Ceart',
                filter_locale: 'Scag de réir Locale',
                filter_status: 'Scag de réir Stádais',
                all_right_types: 'Gach Cineál Ceart',
                any_right_type: 'Aon (baineann le gach cineál)',
                all_locales: 'Gach Logánta',
                all_statuses: 'Gach',
                status_active: 'Gníomhach',
                status_inactive: 'Neamhghníomhach',
                empty_state: 'Níl aon teimpléid ag teacht leis na scagairí reatha.',
                columns: {
                    template_id: 'Aitheantas Teimpléad',
                    outcome: 'Toradh',
                    locale: 'Logánta',
                    right_type: 'Cineál Ceart',
                    is_active: 'Stádas',
                    last_updated: 'Nuashonraithe Deiridh',
                    actions: 'Gníomhartha',
                },
                actions: {
                    edit: 'Cuir in eagar',
                    clone: 'Clón',
                    deactivate: 'Díghníomhachtaigh',
                    reactivate: 'Athghníomhachtaigh',
                    preview: 'Réamhamharc',
                },
                form: {
                    template_id: 'Aitheantas Teimpléad',
                    template_id_help: 'Aitheantóir cás íochtair, e.g. dsr_access_fulfilled_vi',
                    outcome: 'Toradh',
                    locale: 'Logánta',
                    right_type: 'Cineál Ceart',
                    subject: 'Ábhar',
                    body: 'Teimpléad Comhlacht',
                    body_help: 'Úsáid áitsealbhóirí amhail {case_reference}, {data_subject_name}, {fulfilled_at}. 15 líne ar a laghad do litreacha ilrannacha.',
                    is_active: 'Gníomhach',
                    official_form_id: 'Aitheantas na Foirme Oifigiúla (roghnach)',
                },
                outcomes: {
                    fulfilled: 'Comhlíonta',
                    partially_fulfilled: 'Comhlíonta go páirteach',
                    denied: 'Diúltaíodh',
                },
                clone_modal_title: 'Teimpléad Clón',
                clone_target_locale: 'Locale Sprioc',
                clone_help: 'Cruthófar teimpléad nua sa sprioc-locan ag baint úsáide as an teimpléad seo mar phointe tosaigh.',
                preview_modal_title: 'Réamhamharc Teimpléad',
                preview_note: 'Úsáideann an réamhamharc sealbhóir cás taispeána DSR-DEMO-0020. Cuirtear sonraí cás beo in ionad an tseachadta réadúil.',
                preview_unavailable: 'Éilíonn réamhamharc cás atá ann cheana féin. Ceangail an teimpléad seo le cás agus bain úsáid as an ngníomh Dréachtfhreagartha ar leathanach mionsonraí an cháis.',
                deactivate_confirm: "An teimpléad seo a dhíghníomhachtú? Leanfaidh sé d'fheidhm a bheith aige maidir le cásanna atá ann cheana a thagraíonn dó.",
                toasts: {
                    created: 'Teimpléad cruthaithe',
                    updated: 'Nuashonraíodh an teimpléad',
                    deactivated: 'Teimpléad díghníomhachtaithe',
                    reactivated: 'Athghníomhaíodh an teimpléad',
                    cloned: 'Teimpléad clónáilte',
                    load_error: 'Theip ar lódáil na teimpléid',
                    save_error: 'Theip ar shábháil an teimpléid',
                    clone_error: 'Theip ar an teimpléad a chlónáil',
                },
            },
        },
        organizations: {
            departments: {
                title: 'Ranna',
                subtitle: 'Bainistigh na ranna a sheolann cásanna DSR, DPIA tascanna, agus sreafaí oibre comhlíonta.',
                create_btn: 'Roinn Nua',
                edit_btn: 'Cuir Roinn in Eagar',
                columns: {
                    name: 'Ainm',
                    description: 'Cur síos',
                    active_cases: 'Cásanna Gníomhacha',
                    members: 'Baill',
                    status: 'Stádas',
                    actions: 'Gníomhartha',
                },
                status_active: 'Gníomhach',
                status_inactive: 'Neamhghníomhach',
                form: {
                    name: 'Ainm',
                    description: 'Cur síos',
                },
                actions: {
                    edit: 'Cuir in eagar',
                    deactivate: 'Díghníomhachtaigh',
                    reactivate: 'Athghníomhachtaigh',
                    delete: 'Scrios',
                    manage_members: 'Bainistigh baill',
                },
                delete_modal_title: 'Scrios Roinn',
                delete_warning: 'Tá cásanna gníomhacha fós ag an roinn seo. Roghnaigh roinn chun iad a athshannadh dó roimh scriosadh.',
                delete_no_cases: 'Níl aon chásanna gníomhacha ag an roinn seo agus is féidir iad a scriosadh go sábháilte.',
                reassign_prompt: 'Cásanna gníomhacha a athshannadh chuig:',
                reassign_none_available: 'Níl aon ranna gníomhacha eile ar fáil. Cruthaigh roinn eile ar dtús.',
                member_section_title: 'Baill',
                add_member: 'Cuir Ball leis',
                remove_member: 'Bain',
                member_role: 'Ról',
                no_members: 'Níor mhapáil ball ar bith don roinn seo.',
                toasts: {
                    created: 'Roinn cruthaithe',
                    updated: 'Roinn nuashonraithe',
                    deactivated: 'Roinn díghníomhaithe',
                    reactivated: 'Athghníomhachtaigh an Roinn',
                    deleted: 'Scriosadh an Roinn',
                    reassigned_count: '{count} chás(anna) a athshannadh',
                    member_added: 'Chuir ball leis',
                    member_removed: 'Ball bainte',
                    load_error: 'Theip ar ranna a lódáil',
                    save_error: 'Theip ar an roinn a shábháil',
                    delete_error: 'Theip ar an roinn a scriosadh',
                },
            },
            retention_policies: {
                title: 'Beartais Choinneála',
                subtitle: 'Cumraigh fuinneoga coinneála de réir an chineáil sprice. Úsáideann doiciméid fíoraithe aitheantais é seo chun retention_expires_at a stampáil ar uaslódálacha amach anseo.',
                create_btn: 'Beartas Nua',
                edit_btn: 'Cuir Polasaí in Eagar',
                columns: {
                    target_type: 'Cineál Sprioc',
                    retention_days: 'coinneáil (laethanta)',
                    owner: 'Úinéir Polasaí',
                    effective_from: 'Éifeachtach Ó',
                    last_reviewed: 'Athbhreithnithe Deiridh',
                    status: 'Stádas',
                    actions: 'Gníomhartha',
                },
                status_active: 'Gníomhach',
                status_inactive: 'Neamhghníomhach',
                form: {
                    target_type: 'Cineál Sprioc',
                    target_type_help: 'Cén chatagóir sonraí a mbaineann an fhuinneog choinneála léi. Faoi láthair ní thacaítear ach le doiciméid fíoraithe aitheantais DSR.',
                    retention_days: 'Fuinneog choinneála (laethanta)',
                    retention_days_help: 'Idir 1 agus 3650 lá (10 mbliana ar a mhéad).',
                    owner: 'Úinéir Polasaí',
                    owner_help: 'Go hiondúil an DPO. Freagrach as an gcinneadh coinneála faoi DG-2.',
                    effective_from: 'Éifeachtach Ó',
                },
                target_types: {
                    dsr_identity_verification_document: 'Doiciméid fíoraithe aitheantais DSR',
                },
                deactivate_notice: 'Ní chuireann díghníomhachtú isteach ar cheangaltáin a bhfuil stampáil déanta orthu cheana féin — ní bhfaighidh ach uaslódálacha amach anseo fuinneog choinneála a thuilleadh.',
                deactivate_confirm: 'An polasaí coinneála seo a dhíghníomhachtú?',
                toasts: {
                    created: 'Cruthaíodh polasaí coinneála',
                    updated: 'Polasaí coinneála nuashonraithe',
                    deactivated: 'Beartas coinneála díghníomhachtaithe',
                    load_error: 'Theip ar na polasaithe coinneála a lódáil',
                    save_error: 'Theip ar an mbeartas coinneála a shábháil',
                },
            },
        },
    },
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — English for key parity until Phase 25 (R-57).
        deployment_privacy: {
            audience_kinds: {
                public: 'The public',
                data_subject_group: 'Named data subjects',
            },
            channels: {
                website: 'Website',
                point_of_collection: 'At the point of collection',
                employment_documentation: 'Employment documentation',
                contract_or_terms: 'Contract or terms',
                in_app: 'In the app',
                email: 'Email',
                other: 'Other',
            },
            access_kinds: {
                view: 'View',
                extract: 'Extract',
                export: 'Export',
                delete: 'Delete',
                configure: 'Configure',
            },
            export: {
                coverage_heading: 'Privacy notices by data subject',
                coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
                col_activity: 'Processing activity',
                col_data_subject: 'Data subject',
                col_notice: 'Notice',
                col_revision: 'Revision',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_status: 'Status',
                not_recorded: 'Not recorded',
                no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
                status_replaced: '{status}, replaced by a newer revision',
                not_stated: 'Not stated',
                access_heading: 'Who may access the data',
                col_version: 'Version',
                col_reviewed_on: 'Reviewed on',
                col_role_or_position: 'Role or position',
                col_access_kinds: 'Permitted actions',
                col_means: 'By what means',
                col_conditions: 'On what conditions',
                no_access_statement: 'No access statement recorded',
                no_data_subjects: 'No data subjects recorded',
                public_linked: 'Also linked: {count} public notices, which name no data subject',
                public_linked_one: 'Also linked: 1 public notice, which names no data subject',
                coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
                access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
                col_approved_by: 'Approved by',
                col_systems: 'Systems',
            },
            notice_form: {
                heading: 'Audience, channels and deployment',
                intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
                audience_label: 'Whom the notice is given to',
                audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
                audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
                audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
                data_subjects_label: 'Data subjects the notice is given to',
                data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
                data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
                data_subjects_none: 'The linked processing activities record no data subjects.',
                data_subjects_required: 'Choose at least one data subject.',
                data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
                channels_label: 'How the audience is given the notice',
                channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
                channels_required: 'Choose at least one channel.',
                channel_note_label: 'Describe the other channel',
                channel_note_required: 'Describe the other channel.',
                effective_from_label: 'In effect from',
                effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
                activities_label: 'Processing activities the notice is given for',
                activities_hint: 'The data subjects of these activities are the ones the notice can name.',
                systems_label: 'Data-map systems the notice covers',
                links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
                systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
                picker_loading: 'Loading the list…',
                activities_empty: 'No processing activity is recorded.',
                systems_empty: 'No data-map system is recorded.',
                activities_failed: 'The processing activities could not be read.',
                systems_failed: 'The data-map systems could not be read.',
                activities_manual_label: 'Processing activity IDs',
                systems_manual_label: 'Data-map system IDs',
                manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
                off_list: '{value} (not in the list read)',
                retry: 'Reload',
                reloading: 'Reloading…',
                activities_retry_subject: 'the processing activities',
                systems_retry_subject: 'the data-map systems',
                error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
                error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
                error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
                col_audience: 'Audience',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_links: 'Linked to',
                links_activities: 'Processing activities: {names}',
                links_activities_count: '{count} processing activities',
                links_activities_count_one: '1 processing activity',
                links_systems: 'Systems: {names}',
                links_systems_count: '{count} systems',
                links_systems_count_one: '1 system',
                links_none: 'None',
                other_channel: 'Other: {note}',
                data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
                error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
                audience_with_subjects: '{label}: {subjects}',
            },
            coverage: {
                heading: 'Notices',
                intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
                not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
                no_subjects: 'This activity records no data subjects.',
                list_label: 'Notices by data subject',
                notice_line: '{title}, revision {revision}',
                notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
                load_failed: 'The notices could not be read.',
                retry_subject: 'the notices of this activity',
                pagination: 'Pages of data subjects',
                public_heading: 'Also linked: public notices',
                public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
                public_failed: 'The public notices linked to this activity could not be read.',
                public_retry_subject: 'the public notices',
                truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
                subject_state: '{subject}: {state}',
            },
            access: {
                intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
                record: 'Record a revision',
                record_first: 'Record the access statement',
                current: 'Current',
                version: 'Version {version}',
                reviewed: 'Reviewed on {date}',
                approved: 'Approved by {name}',
                not_approved: 'No approver recorded',
                recorded: 'Recorded by {name} on {date}',
                systems: 'Systems: {names}',
                entries_label: 'Entries of version {version}',
                evidence_count: 'Documents: {count}',
                evidence_none: 'No document attached',
                evidence_open: 'Documents',
                evidence_open_aria: 'Documents of version {version}',
                load_failed: 'The access statement could not be read.',
                retry_subject: 'the access statement',
                pagination: 'Pages of access statement revisions',
                history_label: 'Revisions of the access statement',
                unknown_person: 'Unknown person',
            },
            access_form: {
                title: 'Record a revision of the access statement',
                replaces: 'This revision replaces version {version}. The earlier versions are kept.',
                first: 'This is the first revision of the access statement for this activity.',
                entries_heading: 'Who may access the data',
                entry_legend: 'Entry {n}',
                role_label: 'Role or position',
                role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
                access_kinds_label: 'Permitted actions',
                means_label: 'By what means',
                means_hint: 'For example, the extraction software named, a console, or a request to IT.',
                conditions_label: 'On what conditions',
                conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
                add_entry: 'Add an entry',
                remove_entry: 'Remove entry {n}',
                reviewed_on_label: 'Reviewed on',
                reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
                approver_label: 'Approved by (optional)',
                approver_none: 'No approver',
                approver_retry_subject: 'the list of people',
                systems_label: 'Data-map systems the statement covers',
                systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
                missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
                submit: 'Record the revision',
                saving: 'Recording…',
                recorded: 'Revision recorded.',
                record_failed: 'The revision could not be recorded. What you entered is kept.',
                conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
                conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
            },
            access_files: {
                heading: 'Documents',
                intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
                slot: 'Documents of this revision',
                none: 'No document is attached to this revision.',
                file_hash: 'SHA-256: {hash}',
                not_held: 'Document no longer held',
                download: 'Download',
                remove: 'Remove',
                upload: 'Add a document',
                bound: 'Document added.',
                removed: 'Document removed.',
                bind_failed: 'The document could not be added.',
                remove_failed: 'The document could not be removed.',
                not_owned: 'The revision this document belongs to was not found.',
                remove_confirm: 'Remove this document from the revision and delete it?',
                load_failed: 'The documents could not be loaded.',
            },
        },
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'Córais',
            categories: 'Catagóirí Sonraí',
            registry: 'Clárlann Sonraí',
            purposes: 'Cuspóirí Próiseála',
            retention: 'Coinneáil',
            flows: 'Sreafaí Sonraí',
            activities: 'Gníomhaíochtaí Próiseála',
            evidence: 'Fianaise',
            exports: 'Easpórtálacha',
            attestation: 'Timthriallta Fianaithe',
            governance: 'Rialachas',
            review: 'Tascanna Athbhreithnithe',
            inventory: 'Fardal',
            exceptions: 'Eisceachtaí',
            history: 'Stair',
            ropa_completeness: 'iomláine ROPA',
        },
        ropa_completeness_autofix: {
            ask_button: 'Iarr ar Forseti',
            drafting: 'Ag dréachtú…',
            accept: 'Glac',
            reject: 'Diúltaigh',
            accepted: 'Glactha - déan iarratas tríd an eagarthóir gníomhaíochta.',
            rejected: 'Diúltaithe.',
            needs_human_input: 'Tá tuilleadh comhthéacs gníomhaíochta de dhíth ar Forseti (m.sh. cuspóir doiciméadaithe) sula ndréachtaítear an réimse seo.',
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: 'Tuarascáil iomláine ROPA',
            subtitle: 'Iniúchadh sláinteachais in aghaidh an réimse ar do Thaifid ar Ghníomhaíochtaí Próiseála, arna raon feidhme ag creat rialála.',
            framework_label: 'Creatlach',
            kpi_total: 'Gníomhaíochtaí scóráilte',
            kpi_critical: 'Criticiúil',
            kpi_high: 'Ard',
            kpi_conditional: 'Coinníollach',
            field_chart_heading: 'Réimsí ar iarraidh de réir minicíochta',
            drilldown_heading: 'Gníomhaíochtaí le bearnaí',
            col_activity: 'Gníomhaíocht',
            col_field: 'Réimse',
            col_severity: 'Déine',
            col_condition: 'Coinníoll',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Criticiúil',
            severity_high: 'Ard',
            severity_conditional: 'Coinníollach',
            severity_informational: 'Faisnéiseach',
            include_informational: 'Cuir faisnéis san áireamh',
            export_csv: 'Íoslódáil CSV',
            exporting: 'Á easpórtáil...',
            loading: 'Á luchtú…',
            empty_state: 'Tá taifid iomlána ROPA don chreat seo ag gach gníomhaíocht ghníomhach.',
            error_fetch: 'Theip ar lódáil an tuairisc iomláine.',
            error_unsupported_framework: 'Ní thacaítear leis an gcreat seo.',
            error_framework_not_installed: "Níl an creat seo suiteáilte do d'eagraíocht.",
            no_misses_for_field: 'Níl aon ghníomhaíochtaí in easnamh sa réimse seo.',
            framework_PDPL: 'PDPL (Dlí 91/2025 + Foraithne 356)',
            framework_GDPR: 'GDPR Airteagal 30',
            framework_DECREE_165: 'Foraithne 165 (Dlí Sonraí Vítneam)',
            framework_ISO_27701: 'ISO 27701 (faisnéise)',
        },
        registry: {
            // Sprint 64.72.0 — ceanntásca colún easpórtála na clárlainne sonraí.
            export_col: {
                registry_item_id: 'Aitheantóir mhír na clárlainne',
                name: 'Ainm',
                description: 'Cur síos',
                field_identifiers: 'Aitheantóirí réimse',
                category_id: 'Aitheantóir catagóire',
                category_name: 'Ainm na catagóire',
                data_type: 'Cineál sonraí',
                effective_sensitivity: 'Íogaireacht éifeachtach',
                sensitivity_override: 'Sáraitheoir íogaireachta',
                data_subjects: 'Ábhair sonraí',
                volume_band: 'Banda toirte',
                source_system_ids: 'Aitheantóirí na gcóras foinse',
                processing_activity_ids: 'Aitheantóirí na ngníomhaíochtaí próiseála',
                retention_policy_id: 'Aitheantóir an bheartais coinneála',
                effective_retention_policy_id: 'Aitheantóir an bheartais coinneála éifeachtaigh',
                purposes: 'Cuspóirí',
                collection_method: 'Modh bailithe',
                legal_basis: 'Bunús dlí',
                data_tier: 'Sraith sonraí',
                data_tier_source: 'Foinse na sraithe sonraí',
                storage_locations: 'Suíomhanna stórála',
                cross_border: 'Trasteorann',
                status: 'Stádas',
                source_form_submission_id: 'Aitheantóir aighneachta na foirme foinse',
                source_form_template_id: 'Aitheantóir teimpléid na foirme foinse',
                last_reviewed_at: 'Athbhreithnithe go deireanach',
                next_review_at: 'An chéad athbhreithniú eile',
                created_at: 'Cruthaithe',
                updated_at: 'Nuashonraithe',
                data_element: 'Eilimint sonraí',
                data_category: 'Catagóir sonraí',
                systems: 'Córais',
                retention_policy: 'Beartas coinneála',
                name_of_processing: 'Ainm na próiseála',
                categories_of_personal_data: 'Catagóirí sonraí pearsanta',
                categories_of_data_subjects: 'Catagóirí ábhar sonraí',
                recipients: 'Faighteoirí',
                third_country_transfers: 'Aistrithe chuig tríú tíortha',
                retention_period: 'Tréimhse choinneála',
                doc_title: 'Clárlann Sonraí',
                doc_generated: 'Easpórtáil cruthaithe {timestamp}',
                doc_item_count: '{count} mír chlárlainne',
                doc_template_full: 'Iomlán',
            },
            export_vocab: {
                data_type: {
                    text: 'Téacs',
                    number: 'Uimhir',
                    date: 'Dáta',
                    datetime: 'Dáta agus am',
                    boolean: 'Tá/Níl',
                    binary: 'Dénártha',
                    structured: 'Struchtúrtha',
                    file: 'Comhad',
                    identifier: 'Aitheantóir',
                    geolocation: 'Geoshuíomh',
                },
                collection_method: {
                    direct_from_subject: 'Go díreach ón ábhar sonraí',
                    third_party: 'Ó thríú páirtí',
                    derived: 'Díorthaithe ó shonraí eile',
                    observed: 'Breathnaithe',
                    inferred: 'Tuigthe',
                    public_source: 'Foinse phoiblí',
                },
                data_subject_type: {
                    customer: 'Custaiméir',
                    employee: 'Fostaí',
                    contractor: 'Conraitheoir',
                    prospect: 'Custaiméir ionchasach',
                    minor: 'Mionaoiseach',
                    patient: 'Othar',
                    student: 'Mac léinn',
                    beneficiary: 'Tairbhí',
                    vendor_contact: 'Teagmhálaí an díoltóra',
                    website_visitor: 'Cuairteoir suímh ghréasáin',
                    other: 'Eile',
                },
                volume_band: {
                    lt_1k: 'Níos lú ná 1,000 taifead',
                    '1k_to_10k': '1,000 go 10,000 taifead',
                    '10k_to_100k': '10,000 go 100,000 taifead',
                    '100k_to_1m': '100,000 go 1 mhilliún taifead',
                    gt_1m: 'Níos mó ná 1 mhilliún taifead',
                    unknown: 'Anaithnid',
                },
                tier_source: {
                    assessed_and_approved: 'Measúnaithe agus faofa',
                    manual_legacy: 'Glactha ó sheanluach',
                    legacy_platform_field: 'Luach ardáin oidhreachta',
                    scheme_scoped_only: 'Measúnaithe de réir leibhéal a bhaineann le scéim ar leith — ní chumhdaíonn aon chód amháin roinnt scálaí leibhéil reachtúla',
                },
            },
            page: {
                title: 'Clárlann Sonraí',
                subtitle: 'Eilimintí sonraí pearsanta atá i seilbh d’eagraíocht, eagraithe faoi Chatagóirí Sonraí.',
                empty_state: 'Níl aon mhíreanna clárlainne ann fós. Cuir earra leis de láimh nó iompórtáil ó chlár rialála.',
                empty_filter_state: 'Ní mheaitseálann aon mhír le do chuid scagairí.',
            },
            filter: {
                category: 'Catagóir',
                sensitivity: 'Íogaireacht',
                data_subject: 'Ábhar sonraí',
                status: 'Stádas',
                data_tier: 'Sraith sonraí',
                cross_border: 'Trasteorann',
                has_owner: 'Tá úinéir',
                has_form_source: 'Iompórtáilte ón bhfoirm',
                search_placeholder: 'Cuardaigh ainm, cur síos, nó aitheantóir réimse…',
                clear: 'Glan na scagairí',
                option_all: 'Gach',
            },
            col: {
                name: 'Ainm',
                category: 'Catagóir',
                sensitivity: 'Íogaireacht',
                data_subjects: 'Ábhair sonraí',
                systems: 'Córais',
                activities: 'Gníomhaíochtaí',
                owner: 'Úinéir',
                retention: 'Coinneáil',
                data_tier: 'Sraith sonraí',
                status: 'Stádas',
                last_reviewed: 'Athbhreithnithe go deireanach',
                actions: 'Gníomhartha',
            },
            action: {
                add_manual: 'Cuir mír leis',
                add_from_form: 'Cuir leis ón gclár rialála',
                import_history: 'Iompórtáil aighneachtaí san am atá caite',
                export: 'Easpórtáil',
                archive: 'Cartlann',
                restore: 'Athchóirigh',
                edit: 'Cuir in eagar',
                assign_owner: 'Sann úinéir',
                attach_evidence: 'Ceangail fianaise',
                open_lineage: 'Sraith oscailte',
            },
            modal_editor: {
                section_identity: 'Féiniúlacht',
                section_classification: 'Aicmiú',
                section_subjects: 'Ábhair & Toirt',
                section_location: 'Suíomh & Aistriú',
                section_systems: 'Córais & Gníomhaíochtaí',
                section_retention: 'Coinneáil & Dlí',
                section_governance: 'Rialachas',
                section_notes: 'Nótaí',
                hint_inherited: '(oidhreacht ón gcatagóir)',
                hint_overridden: '(sáraithe)',
                placeholder_field_identifiers: 'm.sh., custaiméirí.primary_email, hris.employee_id',
                validation_name_required: 'Tá ainm ag teastáil',
                validation_category_required: 'Tá gá le catagóir',
                validation_data_subjects_required: 'Tá gá le cineál ábhar sonraí amháin ar a laghad',
                label_data_type: 'Cineál sonraí',
                label_field_identifiers: 'Aitheantóirí páirce (scartha le camóg)',
                label_data_subject_types: 'Cineálacha ábhar sonraí',
                label_volume_band: 'Banna toirte',
                label_storage_locations: 'Suímh stórála (scartha le camóg)',
                placeholder_storage_locations: 'ISO-3166 cód nó téacs saor (m.sh., VN, Hetzner EU)',
                label_cross_border: 'Aistriú trasteorann',
                label_source_systems: 'Córais foinse',
                label_processing_activities: 'Gníomhaíochtaí próiseála',
                label_collection_method: 'Modh bailiúcháin',
                label_purposes: 'Cuspóirí',
                hint_multi_select: 'Coinnigh Cmd/Ctrl chun iolrach a roghnú.',
                option_inherit_category: '— (oidhreacht ón gcatagóir)',
                option_dash: '—',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: 'Réamhamharc a allmhairiú',
                label_to_create: 'A chruthú',
                label_to_update: 'Chun nuashonrú',
                label_skipped_archived: 'Léimte (i gcartlann)',
                label_conflicts: 'Coinbhleachtaí',
                commit: 'Iompórtáil',
                cancel: 'Cealaigh',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'Catagóir',
                section_systems: 'Córais foinse',
                section_activities: 'Gníomhaíochtaí próiseála',
                section_flows: 'Sreafaí sonraí',
                section_retention: 'Coinneáil éifeachtach',
                section_governance: 'Rialachas',
                section_evidence: 'Fianaise',
                section_audit: 'Rian iniúchta le déanaí',
                section_source_form: 'Foinse foirm aighneacht',
                badge_inherited: 'Oidhreacht',
                badge_overridden: 'Sáraithe',
            },
            toast: {
                created: 'Cruthaíodh an mhír chlárlainne.',
                updated: 'Mír na clárlainne nuashonraithe.',
                archived: 'Mír na clárlainne curtha i gcartlann.',
                imported: '{count} mhír iompórtáilte.',
                conflict_warning: '{count} réimse/réimsí a choinnigh do athruithe - féach an liosta coinbhleachta.',
            },
            // Sprint 62.35.0 (AC6) — English placeholder; locale work beyond EN/VI defers to Phase 25 (R-57).
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Fardal Córas',
            new: 'Córas Nua',
            edit: 'Cuir Córas in Eagar',
            name: 'Ainm',
            owner: 'Úinéir',
            criticality: 'Criticiúlacht',
            dpa_status: 'Stádas DPA',
            location: 'Suíomh',
            system_type: 'Cineál Córais',
            metadata: 'Meiteashonraí (JSON)',
            department: 'Roinn',
            hosting_location: 'Suíomh Óstála',
            hosting_provider: 'Soláthraí Óstála',
            data_residency_country: 'Tír Cónaithe Sonraí',
            hosting: 'Óstáil',
            no_systems: 'Níor aimsíodh aon chórais',
            confirm_archive: 'Cartlannaigh córas "{name}"?',
            count: 'córais',
            count_one: 'córas',
            type_internal: 'Inmheánach',
            type_external: 'Seachtrach',
            type_cloud_service: 'Seirbhís Néil',
            type_on_premise: 'Ar an Láthair',
            type_hybrid: 'Hibrideach',
            status_active: 'Gníomhach',
            status_decommissioning: 'Á Dhíchoimisiúnú',
            status_decommissioned: 'Díchoimisiúnaithe',
            status_planned: 'Beartaithe',
            dpa_not_required: 'Ní Riachtanach',
            dpa_required_missing: 'Riachtanach ar Iarraidh',
            dpa_in_progress: 'Ar Siúl',
            dpa_signed: 'Sínithe',
            all_criticalities: 'Gach Criticiúlacht',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Catagóirí Sonraí',
            new: 'Catagóir Nua',
            edit: 'Cuir Catagóir in Eagar',
            name: 'Ainm',
            sensitivity: 'Íogaireacht',
            tags: 'Clibeanna',
            used_in: 'In Úsáid I',
            examples: 'Samplaí',
            examples_hint: 'Samplaí (scartha le camóga)',
            default: 'Réamhshocrú',
            all_sensitivities: 'Gach Íogaireacht',
            all_statuses: 'Gach Stádas',
            no_categories: 'Níor aimsíodh aon chatagóirí',
            confirm_delete: 'Scrios catagóir "{name}"?',
            count: 'catagóirí',
            count_one: 'catagóir',
            status_draft: 'Dréacht',
            status_proposed: 'Molta',
            status_under_review: 'Á Athbhreithniú',
            status_approved: 'Ceadaithe',
            status_deprecated: 'Imithe i Léig',
            status_updated: 'Stádas catagóire nuashonraithe',
            propose: 'Mol',
            approve: 'Ceadaigh',
            deprecate: 'Cuir i Léig',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Cuspóirí Próiseála',
            new: 'Cuspóir Nua',
            edit: 'Cuir Cuspóir in Eagar',
            name: 'Ainm',
            legal_basis: 'Bunús Dlíthiúil',
            retention_period: 'Tréimhse Coinneála (lá)',
            justification: 'Údar Leasa Dhlisteanach',
            legal_basis_detail: 'Sonraí an Bhunúis Dlíthiúil',
            all_legal_bases: 'Gach Bunús Dlíthiúil',
            no_purposes: 'Níor aimsíodh aon chuspóirí',
            confirm_delete: 'Scrios "{name}"?',
            default: 'Réamhshocrú',
            count: '{count} cuspóirí',
            count_one: '{count} cuspóir',
        },
        retention: {
            title: 'Polasaithe Coinneála',
            new: 'Polasaí Nua',
            edit: 'Cuir Polasaí in Eagar',
            name: 'Ainm',
            retention_days: 'Laethanta Coinneála',
            disposal_method: 'Modh Diúscartha',
            data_categories: 'Catagóirí Sonraí',
            activities: 'Gníomhaíochtaí',
            review_frequency: 'Minicíocht Athbhreithnithe',
            review_frequency_days: 'Minicíocht Athbhreithnithe (lá)',
            no_policies: 'Níor aimsíodh aon pholasaithe coinneála',
            confirm_delete: 'Scrios "{name}"?',
            count: '{count} polasaithe',
            count_one: '{count} polasaí',
            days: 'lá',
            disposal_deletion: 'Scriosadh',
            disposal_anonymization: 'Anaithnidiú',
            disposal_archival: 'Cartlannú',
            review_overdue: 'Athbhreithniú thar téarma',
            review_status: 'Stádas athbhreithnithe',
            review_ok: 'Ar an mbóthar ceart',
            department: 'Roinn',
            all_departments: 'Gach roinn',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            view_tabs_aria: 'Amharc sreafa',
            view_list: 'Liosta',
            view_map: 'Léarscáil',
            title: 'Sreafaí Sonraí',
            new: 'Sreabhadh Nua',
            edit: 'Cuir Sreabhadh in Eagar',
            name: 'Ainm',
            from_system: 'Ón gCóras',
            to_system: 'Go dtí an Córas',
            flow_type: 'Cineál Sreafa',
            cross_border: 'Trasteorann',
            risk_level: 'Leibhéal Riosca',
            mechanism: 'Meicníocht',
            transfer_mechanism: 'Meicníocht Aistrithe',
            frequency: 'Minicíocht',
            source_country: 'Tír Fhoinse',
            destination_country: 'Tír Chinn Scríbe',
            transfer_safeguard: 'Coimirce Aistrithe',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'Gach Leibhéal Riosca',
            no_flows: 'Níor aimsíodh aon sreafaí sonraí',
            confirm_archive: 'Cartlannaigh "{name}"?',
            count: '{count} sreafaí',
            count_one: '{count} sreabhadh',
            status_active: 'Gníomhach',
            status_inactive: 'Neamhghníomhach',
            status_under_review: 'Faoi Athbhreithniú',
            mechanism_api: 'API',
            mechanism_file_transfer: 'Aistriú Comhaid',
            mechanism_database_replication: 'Macasamhlú Bunachair Sonraí',
            mechanism_manual: 'Láimhe',
            mechanism_email: 'Ríomhphost',
            mechanism_other: 'Eile',
            frequency_real_time: 'Fíor-Am',
            frequency_hourly: 'Gach Uair',
            frequency_daily: 'Laethúil',
            frequency_weekly: 'Seachtainiúil',
            frequency_monthly: 'Míosúil',
            frequency_on_demand: 'Ar Éileamh',
            frequency_one_time: 'Uair Amháin',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Dlínse',
        },
        activities: {
            title: 'Gníomhaíochtaí Próiseála',
            new: 'Gníomhaíocht Nua',
            edit: 'Cuir Gníomhaíocht in Eagar',
            activity_title: 'Teideal',
            category: 'Catagóir',
            legal_basis: 'Bunús Dlíthiúil',
            dpa_required: 'DPIA Required',
            ropa_generated: 'RoPA Ginte',
            version: 'Leagan',
            generate_ropa: 'Gin RoPA',
            controller: 'Rialaitheoir',
            controller_name: 'Ainm an Rialaitheora',
            controller_contact: 'Teagmháil an Rialaitheora',
            legal_basis_detail: 'Sonraí an Bhunúis Dlíthiúil',
            organization_info: 'Organization Information',
            org_address: 'Seoladh na heagraíochta',
            tax_code: 'Cód cánach',
            contact_person: 'Duine teagmhála',
            contact_email: 'Ríomhphost teagmhála',
            contact_phone: 'Fón teagmhála',
            select_entity: 'Roghnaigh…',
            no_entities: 'Níor aimsíodh aon rud',
            dpo_name: 'Ainm DPO',
            dpo_contact: 'Teagmháil DPO',
            processor_name: 'Ainm an Phróiseálaí',
            processor_contact: 'Teagmháil an Phróiseálaí',
            data_subjects: 'Ábhair Sonraí',
            data_subjects_hint: 'Ábhair Sonraí (scartha le camóga)',
            recipients: 'Faighteoirí',
            recipients_hint: 'Faighteoirí (scartha le camóga)',
            security_measures: 'Bearta Slándála',
            dpia_reference: 'Tagairt DPIA',
            no_activities: 'Níor aimsíodh aon ghníomhaíochtaí próiseála',
            confirm_archive: 'Cartlannaigh "{name}"?',
            count: '{count} gníomhaíochtaí',
            count_one: '{count} gníomhaíocht',
            ropa_success: 'Taifead RoPA ginte. Téigh go dtí Easpórtálacha chun é a íoslódáil.',
            ropa_error: 'Theip ar ghiniúint RoPA',
            required_label: 'Riachtanach',
            status_draft: 'Dréacht',
            status_active: 'Gníomhach',
            status_under_review: 'Faoi Athbhreithniú',
            status_archived: 'Cartlannaithe',
            decree_165_heading: 'Foraithne 165 sraith sonraí (Dlí Sonraí Vítneam)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Sraith sonraí',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Sraith sonraí',
            data_tier_regular: 'Rialta',
            data_tier_important: 'Tábhachtach',
            data_tier_core: 'Croílár',
            transfer_mechanism_label: 'Meicníocht aistrithe trasteorann',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: 'Tagairt measúnaithe slándála',
            security_assessment_reference_placeholder: 'e.g. MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: 'Stóras Fianaise',
            new: 'Fianaise Nua',
            edit: 'Cuir Fianaise in Eagar',
            name: 'Ainm',
            evidence_type: 'Cineál Fianaise',
            linked_entities: 'Aonáin Nasctha',
            uploaded: 'Uaslódáilte',
            expiry: 'Dul in Éag',
            file_url: 'URL Comhaid',
            source_mode_label: 'Foinse fianaise',
            source_mode_file: 'Uaslódáil comhad',
            source_mode_url: 'Nasc URL seachtrach',
            attachment_label: 'Iatán',
            link_to: 'Nasc Fianaise Le',
            entity_type: 'Cineál Aonáin',
            entity_id: 'Aitheantas Aonáin',
            no_evidence: 'Níor aimsíodh aon mhíreanna fianaise',
            manage_links: 'Bainistigh naisc',
            unlink: 'Dínascadh',
            confirm_delete: 'Scrios fianaise "{name}"?',
            count: '{count} míreanna',
            count_one: '{count} mír',
            type_document: 'Doiciméad',
            type_screenshot: 'Grianghraf Scáileáin',
            type_policy: 'Beartas',
            type_contract: 'Conradh',
            type_assessment: 'Measúnú',
            type_correspondence: 'Comhfhreagras',
            type_other: 'Eile',
            linked_type_system: 'Córas',
            linked_type_data_flow: 'Sreabhadh Sonraí',
            linked_type_processing_activity: 'Gníomhaíocht Próiseála',
            linked_type_data_category: 'Catagóir Sonraí',
            linked_type_purpose: 'Cuspóir',
            linked_type_retention_policy: 'Beartas Coinneála',
            link_entity: 'Nasc leis an Aonán',
            link_success: "D'éirigh le haonán a nascadh",
            confirm_unlink: 'An bhfuil tú cinnte gur mhaith leat an t-aonán seo a dhínascadh?',
            unlink_success: "D'éirigh le haonán a dhínascadh",
            no_linked_entities: 'Gan aonáin nasctha',
            select_entity: 'Roghnaigh…',
            no_entities: 'Uimh míreanna',
        },
        exports: {
            title: 'Easpórtálacha Sonraí',
            recent: 'Easpórtálacha Déanacha',
            export_type: 'Cineál Easpórtála',
            format: 'Formáid',
            requested_by: 'Iarrtha Ag',
            requested_at: 'Iarrtha Ar',
            download: 'Íoslódáil',
            csv_export: 'Easpórtáil CSV',
            csv_description: 'Íoslódáil sonraí i bhformáid CSV le haghaidh anailíse scarbhileog.',
            pdf_reports: 'Tuarascálacha PDF',
            pdf_description: 'Gin tuarascálacha formáidithe le haghaidh doiciméadú comhlíontachta.',
            csv_systems: 'Fardal Córas',
            csv_categories: 'Catagóirí Sonraí',
            csv_flows: 'Sreafaí Sonraí',
            csv_activities: 'Gníomhaíochtaí Próiseála',
            csv_cross_border: 'Sreafaí Trasteorann',
            csv_retention_policies: 'Beartais Choinneála',
            csv_evidence: 'Míreanna Fianaise',
            csv_governance_assignments: 'Sannacháin Rialachais',
            pdf_ropa: 'Clár RoPA',
            pdf_systems: 'Tuairisc Córas',
            pdf_cross_border: 'Tuairisc Trasteorann',
            pdf_flow_map: 'Sonraí Mapa Sreafa',
            export_failed: 'Theip ar an easpórtáil',
            pdf_generated: 'PDF ginte go rathúil.',
            ropa: 'Clár RoPA',
            retention_policies: 'Beartais Choinneála',
            governance_assignments: 'Sannacháin Rialachais',
            col_entity: 'Aonán Easpórtála',
            col_description: 'Cur síos',
            desc_ropa: 'Clár iomlán ROPA de na gníomhaíochtaí próiseála go léir a bhfuil bunús dlí leo, DPIA stádas, agus rialuithe.',
            desc_systems: 'Córais fardal leis an úinéir, clibeanna chatagóir sonraí, agus stádas comhlíonta....',
            desc_activities: 'Gníomhaíochtaí próiseála aonair le rialaitheoir, bunús dlí, agus DPIA aicmiú.',
            desc_categories: 'Catagóirí sonraí agus sraitheanna íogaireachta a úsáidtear trasna an aicmithe agus na n-iniúchtaí.',
            desc_purposes: 'Críocha próiseála leis an mbunús dlí a taifeadadh do gach ceann acu.',
            desc_flows: 'Sreafaí sonraí idir córais, lena n-áirítear bealaí inmheánacha agus trasteorann.',
            desc_cross_border: 'Sreafaí trasteorann amháin, le meicníocht aistrithe agus measúnú riosca.',
            desc_retention_policies: 'Beartais choinneála, modhanna diúscartha, agus dúnadh athbhreithnithe.',
            desc_evidence: 'Fianaise a ghabhann le gníomhaíochtaí próiseála: deimhnithe, conarthaí DPA, DPIA tuarascálacha.',
            desc_governance_assignments: 'Tascanna rialachais: úinéirí, formheastóirí, agus tagairtí ISO 27001.',
            unified_heading: 'Aonáin Easpórtála',
            unified_description: 'Easpórtáil aon eintiteas mapála sonraí i bhformáid CSV, XLSX, PDF, nó DOCX. Sábháiltear gach onnmhairiú chuig an mBrabhsálaí Comhad.',
            export_button_label: 'Easpórtáil',
            csv_download_success: 'CSV íoslódáilte go rathúil.',
            col_action: 'Export',
        },
        attestation: {
            title: 'Timthriallta Fianaise',
            create: 'Timthriall Nua',
            name: 'Ainm an Timthriall',
            year: 'Bliain',
            deadline: 'Spriocdháta',
            scope: 'Raon feidhme',
            status: 'Stádas',
            tasks: 'Tascanna Roinne',
            assign_tasks: 'Sann Tascanna',
            department: 'Roinn',
            assignee: 'Sannaí',
            progress: 'Dul chun cinn',
            stale_entries: 'Iontrálacha Seana',
            detect_stale: 'Braith Seana',
            export_bundle: 'Easpórtáil Pacáiste',
            no_cycles: 'Níl aon timthriallta fianaise fós.',
            create_success: 'Timthriall fianaise cruthaithe.',
            assign_success: 'Tascanna sannta go rathúil.',
            complete_success: 'Tasc críochnaithe.',
            stale_detected: 'Iontrálacha seana braite.',
            stale_systems: 'Córais sheana',
            stale_flows: 'Sreafaí seana',
            no_stale: 'Níor braitheadh iontrálacha seana.',
            stale_name: 'Ainm',
            stale_announcement: 'Fuarthas {0} córais sheana agus {1} sreafaí seana',
            last_updated: 'Nuashonraithe deireanach',
            status_draft: 'Dréacht',
            status_active: 'Gníomhach',
            status_completed: 'Críochnaithe',
            task_status_pending: 'Ar feitheamh',
            task_status_in_progress: 'Ar siúl',
            task_status_completed: 'Críochnaithe',
            scope_systems: 'Córais',
            scope_flows: 'Sreafaí sonraí',
            scope_activities: 'Gníomhaíochtaí',
            scope_categories: 'Catagóirí',
            scope_purposes: 'Cuspóirí',
            scope_retention: 'Coinneáil',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Sannadh Rialachais',
            assign: 'Sann Úinéireacht',
            entity_type: 'Cineál Eintitis',
            entity: 'Eintiteas',
            role: 'Ról',
            assigned_by: 'Sannta ag',
            assigned_at: 'Sannta ar',
            remove: 'Bain',
            no_assignments: 'Níl aon sannadh rialachais.',
            assign_success: 'Úinéireacht sannta.',
            remove_success: 'Sannadh bainte.',
            assignee: 'Sannaí',
            department: 'Roinn',
            all_departments: 'Gach roinn',
            roles: {
                data_owner: 'Úinéir Sonraí',
                data_steward: 'Maor Sonraí',
                data_custodian: 'Coimeádaí Sonraí',
                informed: 'Curtha ar an eolas',
            },
            entity_types: {
                system: 'Córas',
                flow: 'Sreabhadh sonraí',
                activity: 'Gníomhaíocht',
                category: 'Catagóir',
                purpose: 'Cuspóir',
                retention_policy: 'Polasaí coinneála',
            },
        },
        risk_assessment: {
            title: 'Measúnú Riosca',
            create: 'Measúnú Nua',
            safeguard_type: 'Cineál Cosanta',
            adequacy_status: 'Stádas Leordhóthanachta',
            risk_factors: 'Tosca Riosca',
            mitigations: 'Bearta Maolaithe',
            reviewer: 'Athbhreithneoir',
            decision: 'Cinneadh',
            approve: 'Ceadaigh',
            reject: 'Diúltaigh',
            no_assessments: 'Níl aon mheasúnuithe riosca.',
            create_success: 'Measúnú riosca cruthaithe.',
            approve_success: 'Measúnú ceadaithe.',
            reject_success: 'Measúnú diúltaithe.',
            one_per_line: 'Ceann amháin in aghaidh na líne',
            adequacy_adequate: 'Leordhóthanach',
            adequacy_inadequate: 'Neamhleordhóthanach',
            adequacy_under_review: 'Á athbhreithniú',
            adequacy_not_assessed: 'Neamhmheasúnaithe',
            decision_approved: 'Ceadaithe',
            decision_rejected: 'Diúltaithe',
        },
        review: {
            title: 'Tascanna Athbhreithnithe',
            description: 'Cruthaítear tascanna athbhreithnithe go huathoibríoch nuair a mholtar catagóir sonraí. Chun tasc nua a chruthú, téigh go Catagóirí Sonraí agus cliceáil Mol ar chatagóir dréachta.',
            count: 'tascanna athbhreithnithe',
            summary: 'Achoimre Athbhreithnithe',
            pending: 'Ar Feitheamh',
            resolved_today: 'Réitithe Inniu',
            escalated: 'Ardaithe',
            overdue: 'Thar Téarma (>5 lá)',
            all_statuses: 'Gach Stádas',
            status_pending: 'Ar Feitheamh',
            status_resolved: 'Réitithe',
            status_escalated: 'Ardaithe',
            category_name: 'Catagóir',
            system_name: 'Córas',
            owner: 'Úinéir',
            resolve: 'Réitigh',
            resolve_task: 'Réitigh Tasc Athbhreithnithe',
            resolution: 'Réiteach',
            resolution_approve: 'Ceadaigh',
            resolution_modify: 'Athraigh agus Ceadaigh',
            resolution_reject: 'Diúltaigh',
            resolution_escalate: 'Ardaigh',
            notes: 'Nótaí Réitigh',
            notes_required: 'Nótaí riachtanach (10 gcarachtar ar a laghad) le haghaidh Athrú, Diúltú nó Ardú',
            resolved_success: 'Tasc athbhreithnithe réitithe',
            bulk_approve: 'Ceadú Grúpa',
            bulk_reject: 'Diúltú Grúpa',
            bulk_success: '{count} tasc próiseáilte',
            no_tasks: 'Níor aimsíodh aon tascanna athbhreithnithe',
        },
        inventory: {
            title: 'Fardal Aicmithe',
            count: 'córais',
            summary: 'Achoimre Fardail',
            total_systems: 'Córais Iomlán',
            total_labels: 'Lipéid Iomlán',
            system_name: 'Córas',
            label_count: 'Lipéid',
            approved_count: 'Ceadaithe',
            pending_count: 'Ar Feitheamh',
            no_items: 'Níor aimsíodh aon mhíreanna fardail',
        },
        exceptions: {
            title: 'Eisceachtaí Aicmithe',
            count: 'eisceachtaí',
            all_severities: 'Gach Déine',
            all_statuses: 'Gach Stádas',
            system_name: 'Córas',
            severity_label: 'Déine',
            status_open: 'Oscailte',
            status_resolved: 'Réitithe',
            status_accepted_risk: 'Riosca Glactha',
            resolve: 'Réitigh',
            resolve_exception: 'Réitigh Eisceacht',
            resolution_type: 'Cineál Réitigh',
            notes: 'Nótaí',
            justification_required: 'Údar riachtanach (10 gcarachtar ar a laghad) le haghaidh riosca glactha',
            resolved_success: 'Eisceacht réitithe',
            no_exceptions: 'Níor aimsíodh aon eisceachtaí',
        },
        history: {
            title: 'Stair Aicmithe',
            count: 'imeachtaí',
            actor: 'Gníomhaí',
            action: 'Gníomh',
            entity_type: 'Cineál Aonáin',
            changes: 'Athruithe',
            view_lineage: 'Féach ar Shinsearacht',
            lineage_title: 'Sinsearacht Aicmithe',
            no_events: 'Níor aimsíodh aon imeachtaí staire',
            no_lineage: 'Níl aon sonraí sinsearachta ar fáil',
            modified: 'Athraithe',
            created: 'Cruthaithe',
            removed: 'Bainte',
            action_created: 'Cruthaithe',
            action_updated: 'Nuashonraithe',
            action_archived: 'Cartlannaithe',
            action_approved: 'Ceadaithe',
            action_deprecated: 'Imithe i Léig',
            entity_label: 'Lipéad',
            entity_taxonomy: 'Tacsanomaíocht',
            entity_review_task: 'Tasc Athbhreithnithe',
            entity_exception: 'Eisceacht',
            before: 'Roimhe',
            after: 'Ina dhiaidh',
        },
    },
    governance: {
        // Sprint 62.57.0 Slice E (§12.13.9) — the controller-side vendor certificate record.
        vendor_service_certificate: enVendorServiceCertificate,
        audit_trail_title: 'Cosán iniúchta rialachais',
        audit_trail_subtitle: 'Sruth iniúchta trasaonán do ghníomhartha rialachais — ceadanna, feidhmithe leabhair imeartha, naisc fianaise agus tuilleadh.',
        audit_trail_item_count: 'imeachtaí iniúchta',
        audit_trail_fetch_error: 'Theip ar imeachtaí iniúchta a aisghabháil.',
        audit_trail_no_events: 'Níor aimsíodh aon imeachtaí iniúchta do na scagairí reatha.',
        audit_trail_entity_type: 'Cineál aonáin',
        audit_trail_entity_id: 'ID aonáin',
        audit_trail_action: 'Gníomh',
        audit_trail_actor: 'Gníomhaí',
        audit_trail_timestamp: 'Stampa ama',
        audit_trail_date_from: 'Ón',
        audit_trail_date_to: 'Go',
        audit_trail_no_rows_to_export: 'Níl aon rónna le heaspórtáil.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Soláthraithe',
            subprocessors: 'Fo-Phróiseálaithe',
            cross_border: 'Trasteorann',
            drift: 'Monatóir Aimhréidhe',
            scores: 'Scór Comhlíontachta',
            playbooks: 'Leabhráin Súgartha',
            approvals: 'Ceaduithe',
            audit_trail: 'Cosán iniúchta',
            board_report: 'Tuarascáil an Bhoird',
            evidence: 'Fianaise',
            onboarding: 'Píblíne um bord',
            dpa_reviews: 'Athbhreithnithe DPA',
            periodic_reviews: 'Athbhreithnithe tréimhsiúla',
            offboarding: 'Díbhordáil',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Clár Soláthraithe',
            new: 'Soláthraí Nua',
            edit: 'Cuir Soláthraí in Eagar',
            name: 'Ainm',
            jurisdiction: 'Dlínse',
            dpa_status: 'Stádas DPA',
            risk_level: 'Leibhéal Riosca',
            onboarding_status: 'Stádas Ionduchtaithe',
            contact_email: 'Ríomhphost Teagmhála',
            contact_name: 'Ainm Teagmhála',
            contact_phone: 'Guthán Teagmhála',
            website: 'Suíomh Gréasáin',
            services_csv: 'Seirbhísí (scartha le camóga)',
            country: 'Tír',
            country_placeholder: 'm.sh. IE',
            last_reviewed: 'Athbhreithnithe go Deireanach',
            offboard: 'Díchláraigh',
            offboard_vendor: 'Díchláraigh Soláthraí',
            offboard_reason: 'Cúis Díchláraithe',
            no_vendors: 'Níor aimsíodh aon soláthraithe',
            vendor_count: '{count} soláthraí/soláthraithe',
            all_dpa_status: 'Gach Stádas DPA',
            all_risk_levels: 'Gach Leibhéal Riosca',
            all_onboarding: 'Gach Ionduchtú',
            fetch_error: 'Theip. Bain triail as arís le do thoil.',
            save_error: 'Theip ar shábháil an tsoláthraí',
            offboard_error: 'Theip ar an díchlárú',
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Próiseálaí',
            sub_processor: 'Fo-phróiseálaí',
            controller: 'Rialaitheoir',
            joint_controller: 'Comhrialaitheoir',
        },
        onboarding_statuses: {
            pending_review: 'Athbhreithniú ar Feitheamh',
            approved: 'Ceadaithe',
            active: 'Gníomhach',
            suspended: 'Ar Fionraí',
            offboarded: 'Díchláraithe',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'Ní Riachtanach',
            required_missing: 'Riachtanach ar Iarraidh',
            draft: 'Dréacht',
            under_review: 'Faoi Athbhreithniú',
            signed: 'Sínithe',
            expired: 'Éagtha',
        },
        risk_levels: {
            critical: 'Criticiúil',
            high: 'Ard',
            medium: 'Meánach',
            low: 'Íseal',
        },
        approval_statuses: {
            pending: 'Ar Feitheamh',
            approved: 'Ceadaithe',
            rejected: 'Diúltaithe',
            expired: 'Éagtha',
        },
        approval_action_types: {
            vendor_onboarding: 'Clárú Soláthraí',
            vendor_offboarding: 'Díchlárú Soláthraí',
            cross_border_acceptance: 'Ceadú Trasteorann',
            playbook_activation: 'Gníomhachtú Playbook',
            drift_accept_risk: 'Aontú Riosca Aimhréidhe',
            activity_activation: 'Gníomhachtú Gníomhaíochta',
        },
        drift_check_types: {
            dpa_expiry: 'Éag DPA',
            dpa_missing: 'DPA ar Iarraidh',
            review_overdue: 'Athbhreithniú Thar Téarma',
            cross_border_gap: 'Bearna Trasteorann',
            sub_processor_chain: 'Slabhra Fo-phróiseálaithe',
            risk_assessment_overdue: 'Measúnú Riosca Thar Téarma',
            retention_policy_missing: 'Beartas Coinneála ar Iarraidh',
            evidence_expiring: 'Fianaise ag Éag',
        },
        drift_statuses: {
            open: 'Oscailte',
            acknowledged: 'Admhaithe',
            in_progress: 'Ar Siúl',
            resolved: 'Réitithe',
            accepted_risk: 'Riosca Glactha',
        },
        drift_entity_types: {
            vendor: 'Soláthraí',
            system: 'Córas',
            data_flow: 'Sreabhadh Sonraí',
            processing_activity: 'Gníomhaíocht Phróiseála',
            evidence_item: 'Mír Fianaise',
        },
        playbook_statuses: {
            draft: 'Dréacht',
            active: 'Gníomhach',
            archived: 'Cartlannaithe',
        },
        playbook_action_types: {
            delete_data: 'Scrios Sonraí',
            verify_deletion: 'Deimhnigh Scriosadh',
            notify_vendor: 'Cuir in Iúl don Soláthraí',
            confirm_receipt: 'Deimhnigh Fáiltiú',
            document_evidence: 'Doiciméadaigh',
            other: 'Eile',
        },
        playbook_target_types: {
            system: 'Córas',
            vendor: 'Soláthraí',
        },
        dpa_chain_statuses: {
            complete: 'Iomlán',
            incomplete: 'Neamhiomlán',
            unknown: 'Anaithnid',
        },
        sub_processor_statuses: {
            active: 'Gníomhach',
            terminated: 'Foirceanta',
        },
        evidence_types: {
            dpa_agreement: 'Comhaontú DPA',
            risk_assessment: 'Measúnú Riosca',
            audit_report: 'Tuarascáil Iniúchta',
            certification: 'Deimhniúchán',
            deletion_confirmation: 'Deimhniú Scriosadh',
            training_record: 'Taifead Oiliúna',
            incident_report: 'Tuarascáil Teagmhais',
            document: 'Doiciméad',
            screenshot: 'Grianghraf Scáileáin',
            policy: 'Beartas',
            contract: 'Conradh',
            assessment: 'Measúnú',
            correspondence: 'Comhfhreagras',
            other: 'Eile',
        },
        linkable_entity_types: {
            vendor: 'Soláthraí',
            transfer_mechanism: 'Meicníocht Aistrithe',
            playbook: 'Leabhar imeartha',
            execution: 'Forghníomhú',
            system: 'Córas',
            activity: 'Gníomhaíocht',
        },
        subprocessors: {
            title: 'Caidrimh Fo-Phróiseálaithe',
            new: 'Nasc Nua',
            vendor: 'Soláthraí',
            subprocessor: 'Fo-Phróiseálaí',
            relationship_type: 'Cineál Caidrimh',
            countries: 'Tíortha',
            countries_csv: 'Tíortha (scartha le camóga)',
            dpa_chain: 'Slabhra DPA',
            dpa_chain_status: 'Stádas Slabhra DPA',
            no_processors: 'Níor aimsíodh aon phróiseálaithe.',
            no_subprocessors: 'Níor aimsíodh aon fho-phróiseálaithe.',
            processor_vendor: 'Soláthraí Próiseálaí',
            select_processor: 'Roghnaigh Próiseálaí',
            select_vendor: 'Roghnaigh Soláthraí',
            services: 'Seirbhísí',
            sub_processor_vendor_id: 'Aitheantas Soláthraí Fo-Phróiseálaí',
            terminate: 'Foircinn',
            confirm_terminate: 'An bhfuil tú cinnte gur mian leat an caidreamh seo a fhoirceann?',
            create_error: 'Theip ar chruthú an naisc.',
            fetch_error: 'Theip ar fháil na bhfo-phróiseálaithe.',
            processor: 'Próiseálaí',
            services_csv: 'Seirbhísí (scartha le camóga)',
            terminate_error: 'Theip ar an bhfoirceann.',
            version_history_heading: 'Leagan Athraithe Fo-Phróiseálaí',
            change_tracking_note: 'Déantar gach athrú caidrimh fophróiseálaí a leagan. Taifeadann gach modhnú an staid roimh/i ndiaidh don rian iniúchta.',
            history_retention_note: 'Coinnítear stair athruithe ar feadh na tréimhse coinneála sonraí iomlán. Athbhreithnigh logaí athruithe tríd an amharc mionsonraí an díoltóra.',
            vendor_risk_cross_view_heading: 'Clár fo-phróiseálaithe (Vendor Risk)',
            vendor_risk_cross_view_source: 'Foinse: Clár Vendor Risk',
            vendor_risk_cross_view_empty: 'Níl aon iontráil sa chlár Vendor Risk.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Tuairisc Rialachais Trasteorann',
            flows: 'Sreafaí Trasteorann',
            transfers: 'Aistrithe Trasteorann',
            detected_gaps: 'Bearnaí Braite',
            safeguards: 'Coimircí',
            cross_border_vendors: 'Soláthraithe Trasteorann',
            destination: 'Ceann Scríbe',
            no_cross_border_vendors: 'Níor aimsíodh aon soláthraithe trasteorann.',
            no_safeguard: 'Gan choimirce',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Suíomhanna Próiseála',
            export_error: 'Theip ar easpórtáil na tuairisce.',
            export_pdf: 'Easpórtáil PDF',
            export_success: 'Tuairisc easpórtáilte go rathúil.',
            exporting: 'Á hEaspórtáil...',
            fetch_error: 'Theip ar fháil na sonraí trasteorann.',
            source: 'Foinse',
            pdpl_p2_heading: 'PDPL-P2 Comhlíonadh Aistrithe Trasteorann',
            dossier_prep_note: 'Ullmhúchán sainchomhad: measúnú tionchair, bunús dlí, agus doiciméadú cosanta a chur le chéile roimh chomhdú.',
            filing_tracking_note: 'Rianú comhdaithe: monatóireacht a dhéanamh ar stádas aighneachta leis an Údarás (PDPC) ón iarratas tosaigh go dtí an ceadú.',
            supplement_loop_note: 'Lúb an fhorlíonta: freagra a thabhairt ar iarratais faisnéise ón Údarás agus sainchomhaid leasaithe a chur isteach arís de réir mar is gá.',
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Cuardach díoltóra',
            vendor_search_placeholder: 'Cuardaigh de réir ainm an díoltóra…',
            jurisdiction_filter_label: 'Dlínse',
            filings_section_heading: 'Comhduithe Trasteorann',
            filings_empty: 'Níl aon chomhdú fós. Cruthaigh ceann chun tús a chur le sainchomhad.',
            filings_create_button: 'Comhdú nua',
            filings_table_id: 'Aitheantas comhdaithe',
            filings_table_jurisdiction: 'Dlínse',
            filings_table_type: 'Cineál foirme',
            filings_table_status: 'Stádas',
            filings_table_updated: 'Nuashonraithe',
            filings_table_open: 'Oscail',
            filings_detail_title: 'Comhdú Trasteorann',
            filings_back_to_list: 'Ar ais go Trasteorann',
            filings_status_heading: 'Saolré',
            filings_evidence_heading: 'Fianaise',
            filings_history_heading: 'Rian iniúchta',
            filings_field_filing_id: 'Aitheantas comhdaithe',
            filings_field_jurisdiction: 'Dlínse',
            filings_field_type: 'Cineál foirme',
            filings_field_status: 'Stádas',
            filings_field_reference: 'Tagairt Údaráis',
            filings_field_mps_receipt: 'MPS admháil',
            filings_field_submitted_at: 'Arna chur isteach ag',
            filings_field_acknowledged_at: 'Admhaítear ag',
            filings_field_archived_at: 'Cartlannaithe ag',
            filings_field_version: 'Leagan',
            filings_field_notes: 'Nótaí',
            filings_action_advance: 'Stádas roimh ré',
            filings_action_reject: 'Diúltaigh',
            filings_action_archive: 'Cartlann',
            filings_action_submit: 'Marcáil curtha isteach',
            filings_action_acknowledge: "D'admhaigh Mark",
            filings_action_resume_draft: 'Lean mar dhréacht',
            filings_status_draft: 'Dréacht',
            filings_status_assessed: 'Mheas',
            filings_status_pending_review: 'Athbhreithniú ar feitheamh',
            filings_status_ready_to_file: 'Réidh le comhdú',
            filings_status_submitted: 'Arna chur isteach',
            filings_status_acknowledged: 'Admhaítear',
            filings_status_rejected: 'Diúltaithe',
            filings_status_archived: 'Cartlannaithe',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'Comhdú trasteorann nua',
            filings_new_form_jurisdiction: 'Dlínse',
            filings_new_form_type: 'Cineál foirme',
            filings_new_form_notes: 'Nótaí (roghnach)',
            filings_new_submit: 'Cruthaigh',
            filings_new_cancel: 'Cealaigh',
            filings_reason_label: 'Cúis',
            filings_reference_label: 'Uimhir thagartha an Údaráis',
            filings_mps_receipt_label: 'MPS tagairt admhála',
            filings_create_error: 'Theip ar chomhdú a chruthú.',
            filings_create_success: 'Comhdú cruthaithe.',
            filings_update_error: 'Theip ar an gcomhdú a nuashonrú.',
            filings_update_success: 'Comhdú nuashonraithe.',
            filings_fetch_error: 'Theip ar chomhduithe a fháil.',
            filings_archive_confirm: 'Cuir an comhad seo i gcartlann? Ní féidir é seo a chealú.',
            filings_status_rejected_detour: 'Diúltaíodh don chomhdú tar éis athbhreithniú. Lean ar aghaidh mar dhréacht chun an sreabhadh a atosú.',
            recent_exports_heading: 'Onnmhairí le déanaí',
            recent_exports_empty: 'Níl aon onnmhairiú fós. Cliceáil "Easpórtáil PDF" chun ceann a ghiniúint.',
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
            filings_type_placeholder: 'Select a form type…',
        },
        drift: {
            title: 'Monatóir Aimhréidhe Cumraíochta',
            drift_type: 'Cineál Aimhréidhe',
            severity: 'Déine',
            detected_at: 'Braitheadh Ar',
            resolve: 'Réitigh',
            all_check_types: 'Gach Cineál Seiceála',
            all_entity_types: 'Gach Cineál Aonáin',
            event_count: 'Líon Imeachtaí',
            accept_risk: 'Glac le Riosca',
            acknowledge: 'Admhaigh',
            assign_error: 'Theip ar an sannadh.',
            assign_event: 'Sann',
            assigned: 'Sannta',
            enter_resolution_notes: 'Cuir nótaí réitigh isteach...',
            enter_user_id: 'Cuir aitheantas úsáideora isteach...',
            entity: 'Aonán',
            fetch_error: 'Theip ar fháil na n-imeachtaí aimhréidhe.',
            no_events: 'Níor aimsíodh aon imeachtaí aimhréidhe.',
            resolution_notes: 'Nótaí Réitigh',
            update_status_error: 'Theip ar nuashonrú an stádais.',
        },
        scores: {
            title: 'Painéal Scóir Comhlíontachta',
            overall: 'Scór Comhlíontachta Iomlán',
            breakdown: 'Miondealú Scóir de réir Modúil',
            trend: 'Treocht Stairiúil',
            module: 'Modúl',
            score: 'Scór',
            grade: 'Grád',
            calculated_at: 'Ríofa Ar',
            generate_board_report: 'Gin Tuairisc Bhoird',
            lang_english: 'Béarla',
            lang_vietnamese: 'Vítneaimis',
            fetch_error: 'Theip ar fháil na scóir chomhlíontachta.',
            generate_report: 'Gin Tuairisc',
            generating: 'Á Ghiniúint...',
            recommendations: 'Moltaí',
            report_error: 'Theip ar ghiniúint na tuairisce.',
            factor_owner_assigned: 'Úinéir sannta',
            factor_data_categories_linked: 'Catagóirí sonraí nasctha',
            factor_purposes_linked: 'Cuspóirí nasctha',
            factor_data_flows_documented: 'Sreafaí sonraí doiciméadaithe',
            factor_processing_activity_linked: 'Gníomhaíocht phróiseála nasctha',
            factor_dpa_status: 'Stádas DPA',
            factor_retention_policy_linked: 'Beartas coinneála nasctha',
            factor_last_reviewed: 'Athbhreithnithe go deireanach laistigh den tairseach',
            factor_risk_assessment_current: 'Measúnú riosca cothrom le dáta',
            factor_sub_processors_documented: 'Fo-phróiseálaithe doiciméadaithe',
            factor_cross_border_safeguards: 'Cosaintí trasteorann',
            factor_contact_info_complete: 'Faisnéis teagmhála iomlán',
            factor_onboarding_approved: 'Ionduchtú ceadaithe',
            factor_avg_system_score: 'Meánscór córais',
            factor_avg_vendor_score: 'Meánscór soláthraí',
            factor_processing_activity_coverage: 'Clúdach gníomhaíochtaí próiseála',
            factor_ropa_completeness: 'Iomláine RoPA',
            factor_open_drift_events: 'Teagmhais imeachta oscailte',
        },
        playbooks: {
            title: 'Leabhráin Scriosaidh',
            new: 'Leabhrán Nua',
            edit: 'Cuir Leabhrán in Eagar',
            steps: 'Céimeanna',
            add_step: 'Cuir Céim Leis',
            remove_step: 'Bain Céim',
            data_categories_csv: 'Catagóirí Sonraí (scartha le camóga)',
            estimated_duration: 'Fad Measta',
            estimated_duration_label: 'Fad Measta',
            evidence_description: 'Cur Síos Fianaise',
            no_playbooks: 'Níor aimsíodh aon leabhráin.',
            playbook_count: 'Líon Leabhrán',
            requires_evidence: 'Fianaise Riachtanach',
            step_label: 'Lipéad Céime',
            target_name: 'Ainm sprice',
            target_type: 'Cineál Sprice',
            action_type: 'Cineál Gnímh',
            archive_error: 'Theip ar an gcartlannú.',
            confirm_archive: 'An bhfuil tú cinnte gur mian leat an leabhrán seo a chartlannú?',
            data_categories: 'Catagóirí Sonraí',
            days: 'lá',
            fetch_error: 'Theip ar fháil na leabhrán.',
            sla_days: 'SLA (lá)',
            target_id: 'Aitheantas Sprice',
            template_class_heading: 'Aicmiú Teimpléad',
            official: 'oifigeach',
            internal: 'Inmheánach',
            classification: 'Aicmiú',
            official_note: 'Oifigeach: teimpléid faoi shainordú rialála atá ailínithe le PDPL, GDPR, nó ceanglais earnáilsonracha.',
            internal_note: 'Inmheánach: nósanna imeachta a bhaineann go sonrach leis an eagraíocht nach bhfuil díorthaithe ó theimpléid rialála.',
            active_executions_heading: 'Rití gníomhacha',
            no_active_executions: 'Níl aon rití gníomhacha.',
            playbook_label: 'Leabhar imeartha',
            ticket_id_label: 'Ticéad',
            progress_label: 'Dul chun cinn',
            actions_label: 'Gníomhartha',
            view_steps_label: 'Féach ar chéimeanna',
            execution_detail_title: 'Sonraí na rite',
            playbook_missing: 'Níor aimsíodh an leabhar imeartha.',
            evidence_required_label: 'Fianaise ag teastáil',
            upload_evidence_label: 'Uaslódáil fianaise',
            mark_complete_label: 'Marcáil mar chríochnaithe',
            evidence_required_toast: 'Tá fianaise riachtanach roimh an gcéim a chríochnú.',
            evidence_blocking_message: 'Ceangail comhad fianaise chun leanúint ar aghaidh.',
            step_completed_toast: 'Céim críochnaithe.',
            executions_fetch_error: 'Theip ar lódáil na rití.',
            step_evidence_default_name: 'Fianaise ar chéim ritheagraithe',
            attached_evidence_label: 'Fianaisí ceangailte',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: 'Sreafaí Oibre Ceadaithe',
            new: 'Ceadú Nua',
            edit: 'Cuir Ceadú in Eagar',
            request_type: 'Cineál Iarratais',
            requester: 'Iarratasóir',
            approve: 'Ceadaigh',
            reject: 'Diúltaigh',
            justification: 'Údar',
            all_action_types: 'Gach Cineál Gnímh',
            decided_by: 'Cinnte Ag',
            no_approvals: 'Níor aimsíodh aon cheaduithe.',
            approval_count: 'Líon Ceaduithe',
            approval_notes_optional: 'Nótaí (roghnach)',
            approve_error: 'Theip ar an gceadú.',
            approve_request: 'Ceadaigh Iarratas',
            decision: 'Cinneadh',
            entity: 'Aonán',
            fetch_error: 'Theip ar fháil na gceaduithe.',
            reject_error: 'Theip ar an diúltú.',
            reject_request: 'Diúltaigh Iarratas',
            rejection_notes_required: 'Nótaí diúltaithe riachtanach',
            rejection_notes_required_msg: 'Tabhair cúis leis an diúltú le do thoil.',
            requested_at: 'Iarrtha Ar',
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: 'Fianaise tacaíochta (roghnach)',
            decision_evidence_name: 'Fianaise cinnidh',
        },
        evidence: {
            title: 'Fianaise Rialachais',
            new: 'Fianaise Nua',
            link_to: 'Nasc Le',
            entity_id_placeholder: 'Aitheantas Aonáin',
            file_path_placeholder: '/cosán/chuig/comhad',
            evidence_label: 'Lipéad Fianaise',
            file_path: 'Conair Chomhaid',
            source_mode_label: 'Foinse fianaise',
            source_mode_file: 'Uaslódáil comhad',
            source_mode_url: 'Nasc URL seachtrach',
            attachment_label: 'Iatán',
            item_count: 'Líon Míreanna',
            link_button: 'Nasc Fianaise',
            confirm_delete: 'An bhfuil tú cinnte gur mian leat an fhianaise seo a scriosadh?',
            enter_entity_id: 'Cuir aitheantas aonáin isteach...',
            entity_id: 'Aitheantas Aonáin',
            entity_type: 'Cineál Aonáin',
            expires: 'Ag Éag',
            expires_at: 'Ag Éag Ar',
            fetch_error: 'Theip ar fháil na fianaise.',
            link_error: 'Theip ar nascadh na fianaise.',
            no_evidence: 'Níor aimsíodh aon fhianaise.',
            tags_csv: 'Clibeanna (scartha le camóga)',
            uploaded: 'Uaslódáilte',
            url: 'URL',
            framework_tag_heading: 'Fianaise Chreat-Chlibithe',
            framework_tag_note: 'Gabhann gach taifead fianaise cód creata, gníomhaí, agus stampa ama. Scag de réir creata chun taifid a bhaineann le hiniúchadh a leithlisiú.',
            framework_code: 'Creatchóid',
            filter_by_framework: 'Scag de réir creatchóid...',
            actor: 'Aisteoir',
        },
        certification: {
            not_applicable: 'Neamhbhainteach',
            not_certified: 'Gan deimhniú',
            pending: 'Ar feitheamh',
            certified: 'Deimhnithe',
            revoked: 'Cúlghairthe',
        },
        onboarding_pipeline: {
            title: 'Píblíne um bord',
            new_workflow: 'Sreabhadh oibre um bord nua',
            vendor_id: 'Aitheantas soláthraí',
            stages: 'Céimeanna',
            current_stage: 'Céim reatha',
            advance: 'Céim a chur ar aghaidh',
            advance_success: 'Céim curtha ar aghaidh go rathúil',
            workflow_status: 'Stádas an tsreabhadh oibre',
            initiated: 'Tionscanta',
            in_progress: 'Ar siúl',
            completed: 'Críochnaithe',
            cancelled: 'Curtha ar ceal',
            no_workflows: 'Níor aimsíodh aon sreabhadh oibre um bord',
            stages_placeholder: 'Céim 1, Céim 2, Céim 3',
            assigned_to: 'Sannta do',
            notes: 'Nótaí',
            pdpl_lifecycle_heading: 'PDPL Saolré an Díoltóra — Dul ar bord',
            due_diligence_note: 'Tá céim an díchill chuí éigeantach sula gcuirtear ar bord aon phróiseálaí faoi PDPL. Fíoraigh raon feidhme próiseála sonraí, bunús dlí, agus bearta slándála.',
            contract_review_note: 'Ní mór d’athbhreithniú conartha clásail DPA, teorainneacha coinneála sonraí, agus srianta fophróiseálaithe a dhearbhú.',
            create_dpa_review_cta: 'Cruthaigh athbhreithniú DPA',
            dpa_review_linked: 'Athbhreithniú DPA nasctha',
        },
        dpa_review: {
            title: 'Athbhreithnithe DPA',
            new_review: 'Athbhreithniú DPA nua',
            review_type: 'Cineál athbhreithnithe',
            findings: 'Torthaí',
            outcome: 'Toradh',
            complete_review: 'Athbhreithniú a chríochnú',
            complete_success: 'Athbhreithniú críochnaithe go rathúil',
            link_assessment: 'Measúnú slándála a nascadh',
            link_success: 'Measúnú nasctha go rathúil',
            assessment_id: 'Aitheantas measúnaithe',
            combined_title: 'Measúnú comhcheangailte',
            no_reviews: 'Níor aimsíodh aon athbhreithniú DPA',
            telecom_disclosure_heading: 'Rialachas Nochtadh Telecom',
            disclosure_trigger_note: 'Gabhann athbhreithnithe arna spreagadh le nochtadh imeachtaí comhroinnte sonraí síntiúsóirí teileachumarsáide de réir Dlí VN Telecom.',
            telecom_t2_note: "TELECOM-T2 faomhadh sraithe ag teastáil sula nochtfar sonraí síntiúsóir d'údaráis.",
            quarterly_log_note: 'Bain úsáid as an loga ráithiúil chun gach teagmhas nochtaithe a dhoiciméadú agus coinnigh rian leanúnach iniúchta.',
        },
        periodic_review: {
            title: 'Athbhreithnithe tréimhsiúla',
            new_review: 'Athbhreithniú tréimhsiúil nua',
            review_type: 'Cineál athbhreithnithe',
            due_date: 'Dáta dlite',
            next_review: 'Dáta an chéad athbhreithnithe eile',
            trigger_reassessment: 'Athbhreithniú a thionscnamh',
            reassessment_reason: 'Cúis leis an athbhreithniú',
            reassessment_success: 'Athbhreithniú tionscanta go rathúil',
            complete_success: 'Athbhreithniú críochnaithe go rathúil',
            scheduled: 'Sceidealaithe',
            in_progress: 'Ar siúl',
            completed: 'Críochnaithe',
            overdue: 'Thar téarma',
            material_change: 'Athrú ábhartha',
            no_reviews: 'Níor aimsíodh aon athbhreithniú tréimhsiúil',
            mark_complete: 'Marcáil mar Críochnaithe',
            pdpl_periodic_heading: 'PDPL Saolré an Díoltóra — Léirmheasanna Tréimhsiúla',
            scheduled_review_note: 'Ní mór athbhreithnithe tréimhsiúla a sceidealú uair sa bhliain ar a laghad do na próiseálaithe gníomhacha go léir. Spreagann athbhreithnithe atá thar téarma foláirimh chomhlíonta.',
            material_change_note: 'Teastaíonn athmheasúnú láithreach ar athruithe ábhartha i bpróiseáil sonraí beag beann ar an sceideal.',
            overdue_badge: 'Thar téarma',
            overdue_aria: 'Athbhreithniú thar téarma',
            only_overdue_filter: 'Thar téarma amháin',
        },
        offboarding: {
            title: 'Díbhordáil soláthraí',
            initiate: 'Díbhordáil a thionscnamh',
            initiate_success: 'Díbhordáil tionscanta go rathúil',
            reason: 'Cúis díbhordála',
            data_return_required: 'Aisfhilleadh sonraí riachtanach',
            verify_data: 'Aisfhilleadh sonraí a fhíorú',
            verify_success: 'Aisfhilleadh sonraí fíoraithe go rathúil',
            sign_off: 'Síniú amach',
            sign_off_success: 'Díbhordáil sínithe amach go rathúil',
            status_initiated: 'Tionscanta',
            status_data_pending: 'Aisfhilleadh sonraí ar feitheamh',
            status_data_verified: 'Aisfhilleadh sonraí fíoraithe',
            status_completed: 'Críochnaithe',
            status_cancelled: 'Curtha ar ceal',
            task_id: 'ID Taisc',
            no_tasks: 'Níor aimsíodh aon tasc díbhordála',
            evidence_id: 'Aitheantas fianaise',
            pdpl_offboarding_heading: 'PDPL Saolré an Díoltóra - Lascadh & Scriosadh',
            deletion_capture_note: 'Ní mór deimhniú scriosta sonraí a ghabháil ón díoltóir le haghaidh offboarding. Fíoraigh tuairisceán sonraí roimh shíniú as.',
            evidence_attachment_note: 'Ceangail fianaise scriosta (teastais, scáileáin scáileáin, deimhnithe díoltóirí) chun an taifead as bord a chomhlánú.',
            verify_mode_label: 'Modh fíoraithe',
            verify_mode_upload: 'Uaslódáil comhad nua',
            verify_mode_link: 'Nasc fianaise atá ann cheana',
            verify_mode_none: 'Níl aon sonraí le cur ar ais',
            upload_data_return_evidence: 'Uaslódáil fianaise do chur ar ais sonraí',
            no_data_justification_label: 'Údar',
            no_data_justification_placeholder: 'Mínigh cén fáth nach bhfuil sonraí le cur ar ais',
            no_data_justification_hint: 'Ar a laghad 20 carachtar',
            upload_required_toast: 'Ceangail comhad chun leanúint ar aghaidh',
            evidence_link_required_toast: 'Roghnaigh fianaise chun leanúint ar aghaidh',
            no_data_justification_required_toast: 'Cuir údar ar a laghad 20 carachtar ar fáil',
            data_return_evidence_name: 'Fianaise ar chur ar ais sonraí',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Tosaigh',
            annual: 'Bliantúil',
            renewal: 'Athnuachan',
            incident_triggered: 'Eachtra-thionscanta',
        },
        periodic_review_types: {
            initial: 'Tosaigh',
            annual: 'Bliantúil',
            renewal: 'Athnuachan',
            incident_triggered: 'Teagmhas-spreagtha',
            material_change: 'Athrú ábhartha',
            disclosure_triggered: 'Nochtadh-spreagtha',
            quarterly_log: 'Logáil ráithiúil',
        },
        dpa_review_statuses: {
            pending: 'Ar feitheamh',
            in_progress: 'Ar siúl',
            completed: 'Críochnaithe',
            cancelled: 'Cealaithe',
        },
        onboarding_stage_names: {
            due_diligence: 'Dícheall cuí',
            contract_review: 'Athbhreithniú conartha',
            security_assessment: 'Measúnú slándála',
            data_mapping: 'Mapáil sonraí',
            approval: 'Ceadú',
            integration: 'Comhtháthú',
        },
        access_denied_title: 'Rochtain diúltaithe',
        access_denied: 'Níl cead agat an leathanach seo a fheiceáil. Déan teagmháil le do riarthóir.',
    },
    consent: {
        nav: {
            policies: 'Polasaithe',
            purposes: 'Catalóg Cuspóirí',
            workflows: 'Sreafaí Oibre',
            snapshots: 'Grianghraif CMP',
            change_awareness: 'Feasacht Athraithe',
            audit: 'Iniúchadh Toilithe',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        gap_analysis: {
            nav_label: 'Anailís Bearnaí',
            page_title: 'Anailís ar Bhearna Toiliú',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Creataí',
            org_flags_label: 'Comhthéacs eagraíochta',
            flag_withdrawal: 'An bhfuil meicníocht dhomhanda aistarraingthe doiciméadaithe',
            flag_parental: "A bhfuil sreabhadh toilithe tuismitheora d'ábhair faoi bhun 16 bliana d'aois",
            flag_cross_border: 'An bhfuil comhdú trasteorann ar taifead',
            run_label: 'Rith anailís bearna',
            running_label: 'Ag rith…',
            requires_advisor_label: 'ai_advisor:cead comhrá ag teastáil.',
            results_heading: 'Bearnaí tugtha chun solais ({count})',
            tokens_label: '≈ {count} comharthaí',
            empty_label: 'Níor aimsíodh aon bhearnaí do na creataí roghnaithe.',
            remediation_label: 'Leigheas molta:',
            dismiss_label: 'Díbhe mar nach mbaineann',
            resolve_label: 'Réitigh',
            dismissed_label: 'Briseadh as a riocht nach mbaineann',
            resolved_label: 'Réitíodh',
            // Sprint 62.27.0 gate E12.5 pass 2 — R-57: English until Phase 25.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Iompórtáil sreafaí oibre toilithe',
            nav_title: 'Iompórtáil Mhóir',
            description: 'Uaslódáil comhad CSV chun sreafaí oibre toilithe a iompórtáil go mórchóir.',
            select_file: 'Roghnaigh comhad CSV',
            file_help: 'CSV le ceannteidil a mheaitseálann réimsí sreafa oibre.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Colúin riachtanacha: Cineál Sreafa Oibre, Ábhar Sonraí, Cainéal.',
            file_too_large: 'Comhad ró-mhór. Is é 2MB an t-uasmhéid.',
            preview: 'Réamhamharc',
            columns_detected: 'Colúin aimsíodh: {count}',
            column_mapping: 'Mapáil colún',
            import_button: 'Iompórtáil',
            import_complete: 'Iompórtáil críochnaithe',
            import_error: 'Theip ar an iompórtáil',
            result: 'Toradh na hiompórtála',
            total: 'Iomlán',
            imported: 'Iompórtáilte',
            skipped: 'Léimthe',
            failed: 'Teipthe',
            errors: 'Earráidí',
            error_row: 'Ró',
            error_field: 'Réimse',
            error_message: 'Teachtaireacht',
            history: 'Stair iompórtála',
            no_history: 'Gan stair iompórtála.',
            history_file: 'Ainm comhaid',
            history_date: 'Dáta',
            create_profiles_label: "Cruthaigh próifílí Ábhar Sonraí d'ábhair nua",
            create_profiles_help: 'Cruthóidh iompórtálacha gan próifíl mheaitseáil ceann go huathoibríoch. Díthiceáil chun sreafaí oibre amháin a iompórtáil.',
            profiles_created: 'Próifílí cruthaithe',
            profiles_matched: 'Próifílí meaitseáilte',
            subject_kind_help: 'Colúin roghnacha: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Ríomhphost neamhbhailí i gcolún subject_email.',
            error_subject_unknown_kind: 'Luach subject_kind anaithnid. Luach ionchais: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'Tá external_ref_key riachtanach nuair a bhíonn subject_kind=external_ref',
        },
        policies: {
            title: 'Polasaithe Toilithe',
            new: 'Polasaí Nua',
            edit: 'Cuir Polasaí in Eagar',
            name: 'Ainm',
            version: 'Leagan',
            policy_text: 'Téacs Polasaí',
            publish: 'Foilsigh',
            versions: 'Leaganacha',
            hide_versions: 'Folaigh Leaganacha',
            submit_for_review: 'Cuir isteach for Athbhreithniú',
            suspend: 'Cuir ar fionraí',
            retire: 'Scor',
            jurisdictions: 'Dlínse',
            jurisdictions_csv: 'Dlínse (comma-separated)',
            approval_mode: 'Mód Ceadaithe',
            reviewer_ids: 'Athbhreithneoir ID (comma-separated)',
            tags_csv: 'Clibeanna (comma-separated)',
            effective_date: 'Effective Dáta',
            review_date: 'Athbhreithniú Dáta',
            consent_model_statement: 'Toiliú Model Statement',
            consent_model_placeholder: 'Roghnach toiliú model statement...',
            default_settings: 'Réamhshocrú Socruithe (JSON)',
            changes: 'Athruithe',
            fetch_error: 'Theip ar fetch toiliú policies. Bain triail eile as.',
            save_error: 'Theip ar save toiliú polasaí. Bain triail eile as.',
            submit_review_error: 'Theip ar submit polasaí for athbhreithniú. Bain triail eile as.',
            activate_error: 'Theip ar activate polasaí. Bain triail eile as.',
            suspend_error: 'Theip ar suspend polasaí. Bain triail eile as.',
            retire_error: 'Theip ar retire polasaí. Bain triail eile as.',
            submit_review_success: 'Polasaí curtha isteach lena athbhreithniú.',
            activate_success: 'Beartas gníomhachtaithe.',
            suspend_success: 'Polasaí ar fionraí.',
            retire_success: 'Beartas ar scor.',
            versions_error: 'Theip ar fetch polasaí versions. Bain triail eile as.',
            invalid_json: 'Neamhbhailí JSON in Réamhshocrú Socruithe field. Please try again.',
            page_title: 'Polasaithe Toilithe',
            version_updated: 'Nuashonraithe',
            version_initial: 'Leagan tosaigh',
            json_format: 'JSON formáid',
            sensitive: 'Íogair',
            flag_sensitive: 'Brat íogair',
            flag_sensitive_title: 'Polasaí Bratach mar Íogaire',
            require_enhanced_review: 'Athbhreithniú Feabhsaithe de dhíth',
            enhanced_review_success: 'Riachtanas athbhreithnithe feabhsaithe cumasaithe.',
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: 'Roghnaigh na catagóirí sonraí íogaire Airteagal 2 PDPL is infheidhme:',
            no_policies: 'Níor aimsíodh aon pholasaithe toilithe. Cruthaigh do chéad pholasaí chun tosú.',
            approval_success: 'Polasaí ceadaithe.',
            reviewers_label: 'Athbhreithneoirí',
            enhanced_review_title: 'Athbhreithniú Feabhsaithe ag Teastáil',
            enhanced_review_badge: 'Athbhreithniú Feabhsaithe ag Teastáil',
            enhanced_review_status_pending: 'Athbhreithniú ar Feitheamh',
            enhanced_review_status_approved: 'Athbhreithniú Ceadaithe',
            enhanced_review_status_rejected: 'Athbhreithniú Diúltaithe',
            enhanced_review_reviewed_by: 'Athbhreithnaithe ag',
            enhanced_review_notes: 'Nótaí Athbhreithnithe',
            enhanced_review_approve: 'Athbhreithniú a Cheadú',
            enhanced_review_reject: 'Athbhreithniú a Dhiúltú',
            enhanced_review_notes_required: 'Tá nótaí athbhreithnithe ag teastáil',
            enhanced_review_approved: 'Athbhreithniú feabhsaithe ceadaithe',
            enhanced_review_rejected: 'Athbhreithniú feabhsaithe diúltaithe — polasaí athshocraithe go dréacht',
            enhanced_review_reset_to_draft: 'Athbhreithniú feabhsaithe diúltaithe — polasaí athshocraithe go dréacht',
            enhanced_review_reject_warning: 'Cuirfidh diúltú an polasaí seo ar ais go stádas Dréachta. Ní mór don DPO é a athchur isteach le haghaidh athbhreithnithe tar éis na saincheisteanna a réiteach.',
            submitted_modal_title: 'Curtha isteach le haghaidh Athbhreithniú Feabhsaithe',
            submitted_modal_body: "Cuireadh an polasaí seo isteach. Ní mór d'athbhreithneoirí Dlí/Príobháideachta é a cheadú sula bhféadfar é a ghníomhachtú.",
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Sonrai slainte',
            biometric: 'Sonrai bithmheadracha',
            political: 'Tuairimi polaitiula',
            ethnic: 'Bunadh eitneach',
            genetic: 'Sonrai geiniteacha',
            criminal: 'Taifid choiriula',
        },
        purposes: {
            title: 'Catalóg Cuspóirí',
            new: 'Cuspóir Nua',
            edit: 'Cuir Cuspóir in Eagar',
            name: 'Ainm',
            legal_basis: 'Bunús Dlíthiúil',
            retention_days: 'Tréimhse Coinneála (lá)',
            is_active: 'Gníomhach',
            filter_by_category: 'Scag by catagóir',
            policy_ids: 'Polasaí ID (comma-separated)',
            ropa_activity_ids: 'ROPA Gníomhaíocht ID (comma-separated)',
            policies: 'Polasaithe',
            deprecate: 'Dímhol',
            fetch_error: 'Theip ar fetch toiliú purposes. Bain triail eile as.',
            save_error: 'Theip ar save toiliú purpose. Bain triail eile as.',
            deprecate_error: 'Theip ar deprecate toiliú purpose. Bain triail eile as.',
            page_title: 'Catalóg Cuspóirí',
            no_purposes: 'Níor aimsíodh aon chuspóirí toilithe. Cruthaigh do chéad chuspóir chun tosú.',
            category_personal_data: 'Sonraí Pearsanta',
            category_sensitive_data: 'Sonraí Íogaire',
            category_health_data: 'Sonraí Sláinte',
            category_financial_data: 'Sonraí Airgeadais',
            category_biometric_data: 'Sonraí Bithmhéadracha',
            category_children_data: 'Sonraí Leanaí',
            category_location_data: 'Sonraí Suímh',
            category_communications_data: 'Sonraí Cumarsáide',
            category_other: 'Eile',
        },
        workflows: {
            title: 'Sreafaí Oibre Toilithe',
            new: 'Sreabhadh Oibre Nua',
            edit: 'Cuir Sreabhadh Oibre in Eagar',
            workflow_type: 'Cineál Sreafa Oibre',
            user_id: 'Aitheantas Úsáideora',
            purpose_id: 'Aitheantas Cuspóra',
            purpose_ids_csv: 'Cuspóir ID (comma-separated)',
            reason: 'Cúis',
            process: 'Próiseáil',
            data_subject_ref: 'Sonraí Subject Tagairt',
            data_subject: 'Ábhar Sonraí',
            channel: 'Cainéal',
            policy_id: 'Polasaí ID',
            scope: 'Scóip',
            legal_grounds: 'Forais Dhlíthiúla',
            assessment: 'Measúnú',
            select_outcome: 'Roghnaigh outcome',
            partially_approved: 'Partially Ceadaithe',
            denied: 'Diúltaithe',
            outcome_upheld: 'Seasaithe',
            outcome_pending: 'Ar feitheamh',
            outcome_denied: 'Diúltaithe',
            status_count: 'Líon stádais',
            channel_filter: 'Scag de réir cainéil',
            add_action: '+ Gníomh',
            by_type: 'By Cineál',
            by_status: 'By Stádas',
            downstream_actions: 'Downstream Gníomhartha',
            downstream_actions_for: 'Downstream Gníomhartha for {subject} ({type})',
            add_downstream_action: 'Cuir Gníomh Iartheachtach leis',
            assigned_to: 'Sannta To',
            fetch_error: 'Theip ar fetch toiliú workflows. Bain triail eile as.',
            dashboard_error: 'Theip ar fetch workflow painéal. Bain triail eile as.',
            save_error: 'Theip ar save toiliú workflow. Bain triail eile as.',
            status_update_error: 'Theip ar update workflow stádas. Bain triail eile as.',
            assessment_error: 'Theip ar update workflow measúnú. Bain triail eile as.',
            add_action_error: 'Theip ar add workflow gníomh. Bain triail eile as.',
            update_action_error: 'Theip ar update workflow gníomh. Bain triail eile as.',
            type_refusal: 'Diúltú',
            type_withdrawal: 'Aistarraingt',
            type_restriction: 'Srian',
            type_objection: 'Agóid',
            status_received: 'Faighte',
            status_processing: 'Á phróiseáil',
            status_under_assessment: 'Á Mheasúnú',
            status_implemented: 'Curtha i bhfeidhm',
            evidence_button: 'Fianaise',
            evidence_modal_title: 'Fianaise Sreabhadh Oibre',
            no_evidence: 'Níl aon fhianaise ag gabháil leis an sreabhadh oibre seo.',
            upload_evidence_label: 'Íosluchtaigh fianaise sreabhadh oibre',
            completed_at: 'Críochnaithe Ag',
            effective_date: 'Dáta Éifeachtach',
            page_title: 'Sreafaí Oibre Toiliú',
            no_workflows: 'Níor aimsíodh aon sreabha oibre toilithe. Cruthaítear sreabha oibre nuair a fheidhmíonn ábhair sonraí a gcearta.',
            type_grant: 'Deonú',
            propagation_none: 'Aon cheann',
            propagation_pending: 'Ar feitheamh',
            propagation_pending_warning: 'Ní féidir críochnú — tá iomadú aistarraingthe fós ar feitheamh',
            propagation_completed: 'Críochnaithe',
            propagation_partial: 'Páirteach',
            propagation_incomplete_error: 'Ní féidir an sreabhadh oibre a chríochnú fad is atá iomadú aistarraingthe fós ar feitheamh',
            propagation_status: 'Stádas iomadaithe',
        },
        snapshots: {
            title: 'Grianghraif CMP',
            new: 'Grianghraf Nua',
            label: 'Lipéad',
            snapshot_date: 'Dáta Grianghraif',
            purposes_count: 'Líon Cuspóirí',
            compare: 'Cuir i gComparáid',
            snapshot_data: 'Sonraí Grianghraif (JSON)',
            select_left: 'Roghnaigh left grianghraf',
            select_right: 'Roghnaigh ceart grianghraf',
            comparison_results: 'Torthaí Comparáide',
            field: 'Réimse',
            left: 'Clé',
            right: 'Ceart',
            close_comparison: 'Dún Comparison',
            trigger: 'Truicear',
            trigger_manual: 'Láimhe',
            trigger_policy_approval: 'Polasaí Ceadú',
            cmp_version: 'CMP Leagan',
            policy_version_id: 'Polasaí Leagan ID',
            banner_config: 'Cumraíocht Meirgí (JSON)',
            active_purposes: 'Cuspóirí Gníomhacha (JSON)',
            active_legal_bases: 'Bunúis Dhlíthiúla Ghníomhacha (JSON)',
            domain_scope: 'Fearann Scóip',
            domain_scope_csv: 'Fearann Scóip (comma-separated)',
            raw_config: 'Amhchumraíocht (JSON)',
            checksum: 'Suim Sheiceála',
            created: 'Cruthaithe',
            fetch_error: 'Theip ar fetch cmp snapshots. Bain triail eile as.',
            save_error: 'Theip ar save cmp snapshot. Bain triail eile as.',
            compare_error: 'Theip ar compare snapshots. Bain triail eile as.',
            page_title: 'Léirmheasanna Toiliú',
            json_format: 'JSON formáid',
            json_array_format: 'JSON formáid eagar',
            manual_snapshot_note: "D'riarthóirí/forbritheoirí amháin. In oibriú gnáth, cruthaítear grianghraif go huathoibríoch trí webhook CMP nó ar fhaomhadh beartais. Greamaigh an JSON onnmhairithe ó do sholáthraí CMP (m.sh. OneTrust, Cookiebot).",
            banner_config_hint: "Greamaigh an réad cumraíochta meirge ó d'onnmhairiú CMP.",
            active_purposes_hint: "Eagar JSON de réada cuspóirí gníomhacha ó d'onnmhairiú CMP.",
            active_legal_bases_hint: "Eagar JSON de réada bunús dlí gníomhacha ó d'onnmhairiú CMP.",
            raw_config_hint: 'JSON cumraíochta amh iomlán ó do CMP. Baineann réimsí JSON eile as seo. Ríomhtar seiceáil SHA-256 go huathoibríoch.',
            changed_field: 'Réimse athraithe: ',
            no_snapshots: 'Níor aimsíodh aon ghrianghraif CMP. Cruthaítear grianghraif nuair a fhaomhtar polasaithe nó nuair a thruicítear de láimh iad.',
            detail_title: 'Sonraí an ghrianghraif',
            view_snapshot_detail: 'Féach ar shonraí an ghrianghraif',
            policy_version: 'Leagan beartais',
            removed: 'Roimhe',
            added: 'Ina dhiaidh',
        },
        change_awareness: {
            title: 'Feasacht Athraithe Toilithe',
            change_type: 'Cineál Athraithe',
            entity_type: 'Cineál Aonáin',
            entity_id: 'Aitheantas Aonáin',
            detected_at: 'Braitheadh Ar',
            acknowledge: 'Admhaigh',
            new_alert: 'Nua Foláireamh',
            new_alert_form: 'Nua Change Awareness Foláireamh',
            total_open: 'Oscail Iomlána',
            by_severity: 'By Déine',
            snapshot_id: 'Grianghraf ID',
            update_status: 'Nuashonraigh Stádas',
            resolution_notes: 'Resolution Nótaí',
            resolution_required: 'Resolution Nótaí (required)',
            resolution_required_msg: 'Tá nótaí réiteach riachtanach nuair a réitítear mír.',
            status_open: 'Oscail',
            status_under_review: 'Under Athbhreithniú',
            status_resolved: 'Réitithe',
            status_accepted: 'Glactha',
            severity_informational: 'Faisnéiseach',
            severity_requires_review: 'Requires Athbhreithniú',
            severity_policy_violation: 'Polasaí Violation',
            create_reconciliation: 'Cruthaigh Tasc Athmhuintearais',
            reconciliation_created: 'Cruthaíodh tasc athmhuintearais go rathúil.',
            reconciliation_error: 'Theip ar thasc athmhuintearais a chruthú. Bain triail eile as.',
            fetch_error: 'Theip ar fetch change awareness items. Bain triail eile as.',
            dashboard_error: 'Theip ar fetch change awareness painéal. Bain triail eile as.',
            create_error: 'Theip ar create change awareness item. Bain triail eile as.',
            status_update_error: 'Theip ar update change awareness stádas. Bain triail eile as.',
            page_title: 'Feasacht ar Athrú',
            no_items: 'Níor aimsíodh aon mhíreanna feasachta athraithe. Cruthaítear míreanna nuair a bhraitear athruithe ar chumraíocht toilithe.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: 'Rian Iniúchta Toilithe',
            event_type: 'Cineál Imeachta',
            user_id: 'Aitheantas Úsáideora',
            timestamp: 'Stampa Ama',
            details: 'Sonraí',
            actor: 'Gníomhaí',
            filter_by_entity_type: 'Scag by entity cineál',
            filter_by_action: 'Scag by gníomh',
            fetch_error: 'Theip ar fetch toiliú iniúchadh trail. Bain triail eile as.',
            page_title: 'Iniúchadh Toiliú',
            export_csv: 'Easpórtáil CSV',
            exporting: 'Á easpórtáil…',
            export_success: "D'éirigh le heaspórtáil an rian iniúchta.",
            export_error: 'Theip ar an rian iniúchta toiliú a onnmhairiú. Bain triail eile as.',
            no_entries: 'Níor aimsíodh aon iontrálacha iniúchóireachta. Taifeadtar imeachtaí iniúchóireachta go huathobríoch nuair a athraíonn sonraí toilithe.',
            entity_policy: 'Polasaí',
            entity_purpose: 'Cuspóir',
            entity_workflow: 'Sreabhadh oibre',
            entity_consent_record: 'Taifead toilithe',
            entity_change_awareness: 'Feasacht ar athruithe',
            entity_cmp_snapshot: 'Grianghraf CMP',
            action_created: 'Cruthaithe',
            action_updated: 'Nuashonraithe',
            action_status_changed: 'Stádas athraithe',
            action_drift_detected: 'Imeacht braite',
            action_snapshot_created: 'Grianghraf cruthaithe',
            action_approval_submitted: 'Ceadú curtha isteach',
            action_archived: 'Cartlannaithe',
            action_purpose_auto_mapped: 'Cuspóir mapaí go huathoibríoch',
            action_purpose_mapping_updated: 'Mapáil cuspóra nuashonraithe',
            action_webhook_configured: 'Webhook cumraithe',
            entity_propagation: 'Iomadú',
            entity_reconciliation: 'Athmhuintearas',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Iomadú Deimhnithe',
            action_propagation_failed: 'Theip ar Iomadú',
            action_consent_captured: 'Gabhadh Toiliú',
            action_reconciliation_created: 'Athmhuintearas Cruthaithe',
            action_reconciliation_resolved: 'Athmhuintearas Beartaithe',
            action_submitted_for_review: 'Curtha isteach lena athbhreithniú',
            action_activated: 'Gníomhachtaithe',
            action_suspended: 'Ar fionraí',
            action_retired: 'Éirithe as',
            action_enhanced_review_required: 'Athbhreithniú feabhsaithe ag teastáil',
            action_enhanced_review_approved: 'Athbhreithniú feabhsaithe ceadaithe',
            action_enhanced_review_rejected: 'Athbhreithniú feabhsaithe diúltaithe — polasaí athshocraithe go dréacht',
            action_consented: 'Toiliú',
            action_consented_customize: 'Toiliú Saincheaptha',
            action_revoked: 'Cúlghairthe',
            action_rejected: 'Diúltaithe',
        },
        web_records: {
            title: 'Taifid Toiliú Gréasáin',
            page_title: 'Taifid Toiliú Gréasáin',
            domain: 'Fearann',
            visitor_id: 'Aitheantas Cuairteora',
            consent_action: 'Gníomh Toiliú',
            received_at: 'Faighte Ag',
            admin_status: 'Stádas Riaracháin',
            revoke: 'chúlghairm',
            reject: 'Diúltaigh',
            reason: 'Cúis',
            reason_placeholder: 'An chúis leis an ngníomh seo (roghnach)',
            revoke_confirm: 'An bhfuil fonn ort an taifead toilithe seo a chúlghairm?',
            reject_confirm: 'An bhfuil fonn ort an taifead toilithe seo a dhiúltú?',
            override_success: "D'éirigh le stádas taifead a nuashonrú.",
            override_error: 'Theip ar stádas taifid a nuashonrú.',
            no_records: 'Níor aimsíodh aon taifid toilithe gréasáin.',
            fetch_error: 'Theip ar lódáil taifid toilithe gréasáin.',
            filter_by_domain: 'Scag de réir fearainn',
            filter_by_action: 'Scag de réir gnímh',
            active: 'Gníomhach',
        },
        legal_bases: {
            all: 'Gach Bunúis Dhlíthiúla',
        },
        common_statuses: {
            suspended: 'Ar fionraí',
            retired: 'Scortha',
            deprecated: 'Dímholta',
        },
        approval_modes: {
            all: 'All',
            any: 'Any',
        },
        cmp_integration: {
            title: 'AesirX CMP Comhtháthú',
            back_to_consent: 'Ar ais to Toiliú',
            back_to_integration: 'Ar ais to Comhtháthú',
            webhook_status: 'Webhook Stádas',
            events_received: 'Imeachtaí Received',
            web_consent_records: 'Web Toiliú Taifid',
            unmapped_purposes: 'Unmapped Cuspóirí',
            integration_management: 'Comhtháthú Management',
            webhook_configuration: 'Webhook Cumraíocht',
            webhook_config_desc: 'Cumraigh the CMP webhook endpoint and manage the HMAC secret.',
            purpose_mapping: 'Cuspóir Mapping',
            purpose_mapping_desc: 'Map CMP purpose identifiers to GRC toiliú catalog purposes.',
            bulk_sync: 'Sioncronú Mór',
            bulk_sync_desc: 'Cuir mapálacha cuspóra i bhfeidhm arís ar gach taifead toilithe neamh-mhapáilte.',
            consent_analytics: 'Web Toiliú Anailísíocht (Last 30 laethanta)',
            consent_grant_rate: 'Toiliú Grant Ráta',
            top_domain: 'Top Fearann',
            records: 'taifid',
            banner_interactions: 'Idirghníomhartha Meirgí',
            consent_actions: 'Gníomhartha Toilithe',
            opt_in_trend: 'Opt-in/Opt-out Treocht',
            records_by_domain: 'Taifid de réir Fearainn',
            date: 'Dáta',
            grants: 'Deontais',
            denials: 'Diúltuithe',
            not_configured: 'Gan Cumrú',
            load_error: 'Theip ar load integration sonraí. Bain triail eile as.',
            webhook_not_configured: 'Webhook gan cumrú.',
            webhook_not_configured_msg: '{link} ar dtús le do thoil.',
            configure_webhook_link: 'cumraigh an webhook',
            setup_wizard: 'Draoi Socraithe',
            step_configure_webhook: 'Cumraigh Webhook',
            step_map_purposes: 'Map Cuspóirí',
            step_run_sync: 'Rith Initial Bulk Sync',
            step_verify_data: 'Fíoraigh Sonraí in Painéal',
            configure: 'Cumraigh',
            view_dashboard: 'Féach ar painéal',
            syncing: 'Ag sioncronú...',
            sync_now: 'Sioncronaigh Anois',
            sync_completed: 'Sync críochnaithe: {imported} imported, {skipped} skipped, {failed} theip.',
            sync_failed: 'Sync theip',
            last_sync_result: 'Toradh Sioncronaithe Deireanach',
            imported: 'Iompórtáilte',
            skipped: 'Scipeáilte',
            errors: 'Earráid',
            index: 'Innéacs',
            error: 'Earráid',
            sync_history: 'Sync Stair',
            no_sync_jobs: 'Gan poist sioncronaithe taifeadta fós.',
            load_sync_error: 'Theip ar load sync sonraí. Bain triail eile as.',
            purpose_mapping_title: 'CMP Cuspóir Mapping',
            auto_mapping: 'Ag uathmhapáil...',
            auto_map_purposes: 'Auto-Map Cuspóirí',
            unmapped_warning: '{count} cuspóir(í) neamh-mhapáilte -- ní mór na CMP críocha seo a mhapáil chun críocha GRC.',
            mapping_saved: 'Mapáil sábháilte.',
            mapping_deleted: 'Mapáil scriosta.',
            auto_map_complete: 'Uathmhapáil críochnaithe: {mapped} mapáilte, {unmatched} neamh-mheaitseáilte.',
            auto_map_failed: 'Auto-map theip',
            save_mapping_error: 'Theip ar save mapping. Bain triail eile as.',
            delete_mapping_error: 'Theip ar delete mapping. Bain triail eile as.',
            load_data_error: 'Theip ar load sonraí. Bain triail eile as.',
            cmp_purpose: 'CMP Cuspóir',
            grc_purpose: 'GRC Cuspóir',
            select_grc_purpose: '-- Roghnaigh Cuspóir GRC --',
            auto_mapped: 'Uathmhapáilte',
            mapped: 'Mapáilte',
            unmapped: 'Neamhmhapáilte',
            no_mappings: 'Níor aimsíodh aon mhapálacha cuspóra. Cruthaítear mapálacha go huathoibríoch nuair a fhaightear imeachtaí toilithe.',
            total_mappings: 'Iomlán: {count} mapping(s)',
            webhook_config_title: 'Webhook Cumraíocht',
            current_configuration: 'Reatha Cumraíocht',
            setup_webhook: 'Socraigh Webhook',
            setup_webhook_desc: 'Cumraigh the AesirX CMP webhook to receive toiliú imeacht.',
            allowed_domains: 'Fearainn Ceadaithe (ceann amháin in aghaidh na líne)',
            webhook_secret_warning: 'Rún Webhook (sábháil anois -- ní thaispeánfar arís é):',
            last_event: 'Last Imeacht',
            none_received: 'Dada',
            update_domains: 'Nuashonraigh Fearainn',
            pause_webhook: 'Cuir ar sos Webhook',
            resume_webhook: 'Atosaigh Webhook',
            regenerate_secret: 'Athghin Rún',
            configuring: 'Ag cumrú...',
            create_webhook_config: 'Cruthaigh Webhook Cumraíocht',
            webhook_endpoint: 'Pointe Deiridh Webhook',
            webhook_endpoint_desc: 'Cumraigh your AesirX CMP to send toiliú imeacht to:',
            webhook_signature_note: 'Cuir an síniú HMAC-SHA256 sa cheanntásc {header}.',
            configured_success: 'Webhook cumraithe go rathúil.',
            config_updated: 'Cumraíocht updated.',
            config_error: 'Theip ar configure webhook. Bain triail eile as.',
            update_error: 'Theip ar update cumraíocht. Bain triail eile as.',
            toggle_error: 'Theip ar toggle webhook. Bain triail eile as.',
            deactivated: 'Webook díghníomhaithe.',
            deactivate_error: 'Theip ar deactivate webhook. Bain triail eile as.',
            page_title: 'CMP Comhtháthú',
            webhook_config_page_title: 'CMP Cumraíocht Webhook',
            bulk_sync_page_title: 'Taifid a ath-léarscáil',
            purpose_mapping_page_title: 'Léarscáiliú Cuspóir',
            data_loaded: 'CMP sonraí comhtháthú lódáilte',
            sync_all: 'Sioncronaí uile',
            sync_selected: 'Sioncronaí roghnaithe',
            sync_status: 'Stádas sioncronaithe',
            last_sync: 'Sioncronaí deireanach',
            no_records: 'Níor aimsíodh aon taifid.',
            sync_in_progress: 'Sioncronaí ar siúl...',
            sync_complete: 'Sioncronaí críochnaithe.',
            sync_error: 'Theip ar an sioncronaí. Bain triail eile as le do thoil.',
            map_purpose: 'Mapáil cuspóir',
            unmap: 'Dímhapáil',
            mapping_status: 'Stádas mapála',
            save_mapping: 'Sábháil mapáil',
            remap_unmapped_records: 'Ath-mhapáil Taifid Neamhmhapáilte',
            remap_records: 'Taifid a ath-léarscáil',
            add_mappings_first: 'Cuir mapálacha cuspóra leis ar dtús (Céim 2)',
            remap_result: 'Toradh Athmhapála',
            remap_result_desc: 'Próiseáladh gach taifead toilithe le cuspóirí neamh-mhapáilte nó páirt-mhapáilte.',
            total_processed: 'Iomlán Próiseáilte',
            newly_mapped: 'Nua-Mhapáilte',
            still_unmapped_label: 'Fós Neamhmapáilte',
            still_unmapped_msg: '{count} taifead(anna) fós le cuspóirí neamhmapáilte. {link} ansin cliceáil ar Re-mapáil arís.',
            add_more_mappings: 'Cuir tuilleadh mapálacha leis',
            remap_complete: 'Athmhapáil curtha i gcrích: {remapped} mapáilte as an nua, {still_unmapped} neamh-mapáilte fós.',
            add_mapping: 'Cuir Mapáil leis',
            cmp_purpose_id_label: 'CMP Aitheantas cuspóra *',
            cmp_purpose_id_required: 'CMP Tá aitheantas cuspóra ag teastáil',
            cmp_purpose_name_label: 'CMP Ainm an Chuspóra',
            grc_purpose_label: 'Cuspóir GRC',
        },
        propagation: {
            title: 'Leathadh Aistarraingthe',
            systems: 'Córais Nasctha',
            status: 'Stádas',
            confirmed: 'Deimhnithe',
            pending: 'Ar feitheamh',
            failed: 'Teipthe',
            confirm: 'Deimhnigh aistarraing',
            fail: 'Marcáil mar theipthe',
            failure_reason: 'Cúis teipe',
            can_close: 'Gach córas deimhnithe — is féidir an sreabhadh oibre a dhúnadh',
            cannot_close: 'Ag fanacht le deimhnithe córais',
            no_tasks: 'Níl aon tascanna leata ann.',
            confirm_success: 'Aistarraing córais deimhnithe.',
            fail_success: 'Córas marcáilte mar theipthe.',
            propagate_success: 'Aistarraing leata chuig córais nasctha.',
            propagate: 'Iomadú go Córais',
            add_system: 'Cuir Córas leis',
            remove_system: 'Bain',
            system_id: 'Aitheantas an Chórais',
            system_name: 'Ainm an Chórais',
            owner_id: 'Aitheantas an Úinéara',
            propagate_error: 'Theip ar an tarraingt siar a iomadú. Bain triail eile as.',
            no_systems: 'Cuir córas amháin ar a laghad leis.',
            page_title: 'Iomadú Aistarraingthe',
            workflow_id: 'Roghnaigh Sreabhadh Oibre Aistarraingthe',
            system_id_hint: 'm.sh. crm-001, erp-prod',
            owner_id_hint: 'Úinéir an chórais',
            elapsed: 'imithe',
        },
        sensitive: {
            title: 'Polasaithe Sonraí Íogaire',
            flag: 'Marcáil mar íogair',
            categories: 'Catagóirí Íogaire',
            enhanced_review: 'Athbhreithniú feabhsaithe a éileamh',
            enhanced_review_success: 'Athbhreithniú feabhsaithe éilithe.',
            flag_success: 'Polasaí marcáilte mar íogair.',
            no_sensitive: 'Níl aon pholasaithe sonraí íogaire ann.',
        },
        reconciliation: {
            title: 'Réiteach Neamhréireachtaí',
            create: 'Tasc réitigh nua',
            system: 'Córas',
            owner: 'Úinéir',
            description: 'Cur síos',
            resolution: 'Réiteach',
            resolve: 'Réitigh',
            update_grc: 'Nuashonraigh GRC',
            update_cmp: 'Nuashonraigh CMP',
            accept_difference: 'Glac leis an difríocht',
            escalate: 'Ardaigh',
            resolution_notes: 'Nótaí réitigh',
            no_tasks: 'Níl aon tascanna réitigh ann.',
            resolve_success: 'Tasc réitigh réitithe.',
            create_success: 'Tasc réitigh cruthaithe.',
            open: 'Oscailte',
            resolved: 'Réitithe',
            escalated: 'Ardaithe',
            dashboard_open: 'Tascanna oscailte',
            dashboard_resolved: 'Tascanna réitithe',
            dashboard_escalated: 'Tascanna ardaithe',
            page_title: 'Athmhuintearas Mímheaitseála',
            my_tasks: 'Mo Thascanna',
            all_tasks: 'Gach Tascanna',
            severity: 'Déine',
        },
        sync: {
            title: 'Sioncronú CMP',
            direction: 'Treo',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Coinbhleacht',
            resolve_conflict: 'Réitigh coinbhleacht',
            no_events: 'Níl aon imeachtaí sioncronaithe ann.',
            resolve_success: 'Coinbhleacht sioncronaithe réitithe.',
            create_success: 'Imeacht sioncronaithe taifeadta.',
            page_title: 'CMP Sioncrónaigh',
            detect_drift: 'Coimhlintí Braith',
            detecting: 'Ag brath...',
            drift_result: '{detected} coinbhleacht(í) a braitheadh as {total} cuspóir(í) a chur i gcomparáid.',
            no_drift: 'Níor aimsíodh aon choinbhleachtaí nua.',
            drift_error: 'Theip ar bhrath coinbhleachta a rith.',
            resolve_modal_title: 'Réitigh Coimhlint',
            resolve_field: 'Réimse',
            resolve_grc_value: 'Luach GRC',
            resolve_cmp_value: 'CMP Luach',
            accept_cmp: 'Glac le CMP luach',
            accept_cmp_desc: 'Nuashonraigh GRC chun an luach CMP — CMP a mheaitseáil leis an luach nua GRC.',
            keep_grc: 'Coinnigh Luach GRC',
            keep_grc_desc: 'Dún an choinbhleacht seo gan GRC a athrú — cuir in iúl don CMP úinéir chun ailíniú.',
            resolve_applied: 'Réitíodh coinbhleacht. Nuashonraíodh cuspóir GRC chun teacht le CMP.',
        },
    },
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'Is tuarascáil chomhairleach ullmhachta í seo. Ní fhaomhann, ní thiomsaíonn, ní ábharaíonn, ní chomhdaíonn ná ní sheolann sí an sainchomhad.',
            run_label: 'Rith réamh-eitilt',
            running: 'Ag rith réamh-eitilte…',
            assemble_label: 'Auto-assemble pacáiste',
            assembling: 'Á chur le chéile…',
            verdict_label: 'Fíorasc réamh-eitilte',
            tokens_label: '≈ {count} comharthaí',
            remediation_label: 'Feabhsúchán',
            no_gaps: 'Níor aimsíodh aon bhearnaí réamh-eitilte. Tá an comhdú réidh le chéile.',
            override_label: 'Sáraigh blocfhíorasc - déanfar stampáil iniúchta ar an tionól.',
            accept_label: 'Glac leis an tuarascáil',
            reject_label: 'Diúltú don tuarascáil',
            assembled_label: 'Pacáiste aighneachta curtha le chéile',
            package_id_label: 'Aitheantas pacáiste',
            package_filename_label: 'Ainm comhaid',
            package_hash_label: 'hash pacáiste',
            evidence_count_label: '{count} mhír fhianaise cuachta',
            error_generic: 'Níorbh fhéidir an réamheitilt a rith ná an pacáiste a chur le chéile. Bain triail eile as nó seiceáil an stádas comhdaithe.',
        },
        errors: {
            fetch: 'Theip ar load measúnú. Bain triail eile as.',
            save: 'Theip ar save measúnú. Bain triail eile as.',
            fetch_gap_report_failed: 'Theip ar fetch bearna tuarascáil. Bain triail eile as.',
            fetch_transfers_failed: 'Theip ar fetch transfers. Bain triail eile as.',
            save_transfer_failed: 'Theip ar save transfer. Bain triail eile as.',
            submit_review_failed: 'Theip ar submit for athbhreithniú. Bain triail eile as.',
            process_decision_failed: 'Theip ar process decision. Bain triail eile as.',
            deprecate_failed: 'Theip ar deprecate. Bain triail eile as.',
            evaluate_risk_failed: 'Theip ar evaluate riosca. Bain triail eile as.',
            fetch_safeguards_failed: 'Theip ar fetch safeguards. Bain triail eile as.',
            save_safeguard_failed: 'Theip ar save safeguard. Bain triail eile as.',
            update_safeguard_status_failed: 'Theip ar stádas an chosanta a nuashonrú. Bain triail eile as.',
        },
        nav: {
            dpias: 'MTCSanna',
            transfers: 'Measúnuithe Aistrithe',
            dashboard: 'Painéal',
            gap_report: 'Tuairisc Bearnaí',
        },
        risk_level_critical: 'Riosca criticiúil',
        risk_level_high: 'Riosca ard',
        risk_level_medium: 'Riosca meánach',
        risk_level_low: 'Riosca íseal',
        severity_critical: 'Déine chriticiúil',
        severity_high: 'Déine ard',
        severity_medium: 'Déine mheánach',
        severity_low: 'Déine íseal',
        overdue: 'Thar téarma',
        framework_filter_label: 'Creatlach',
        aria_filter_by_framework: 'Scag de réir creata',
        dashboard: {
            title: 'Painéal Measúnuithe',
            page_title: 'Painéal measúnachtaí',
            announce_loaded: 'Painéal measúnachtaí luchtaithe',
            announce_chart_view: 'Amharc cairte á thaispéaint',
            announce_table_view: 'Amharc tábla sonraí á thaispéaint',
            aria_metrics_table: 'Méadracht painéil measúnachtaí',
            metric_col: 'Méadrach',
            value_col: 'Luach',
            dpia_prefix: 'DPIA',
            transfer_prefix: 'Aistriú',
            risk_prefix: 'Riosca',
            aria_dpias_by_status: 'DPIAnna de réir stádais',
            aria_transfers_by_status: 'Aistrithe de réir stádais',
            aria_top_risks: 'Barr-rioscaí',
            aria_top_risks_table: 'Barr-rioscaí trasna DPIAnna',
            overdue_dpias_attention: '{count} measúnachtaí DPIA thar téarma ag teastáil aird',
            no_overdue_dpias: 'Gan measúnachtaí DPIA thar téarma',
            overdue_transfers_attention: '{count} measúnachtaí aistrithe thar téarma ag teastáil aird',
            no_overdue_transfers: 'Gan measúnachtaí aistrithe thar téarma',
            dpias_overview: 'Forbhreathnú MTCSanna',
            transfers_overview: 'Forbhreathnú Measúnuithe Aistrithe',
            overdue_items: 'Míreanna Thar Téarma',
            overdue_dpias: 'MTCSanna Thar Téarma',
            overdue_transfers: 'Measúnuithe Aistrithe Thar Téarma',
            count: 'Comhaireamh',
            no_data: 'Níl aon sonraí. aimsithe',
            no_risks: 'Níl aon rioscaí found. aimsithe',
            no_dashboard_data: 'Níl aon painéal sonraí. aimsithe',
            fetch_error: 'Theip ar fetch painéal sonraí. Bain triail eile as.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: 'DPIAnna',
            announce_submitted_for_review: 'DPIA curtha isteach le haghaidh athbhreithnithe',
            announce_deprecated: 'DPIA i léig',
            announce_risks_loaded: '{count} riosca(í) luchtaithe',
            announce_mitigations_loaded: '{count} beart maolaithe luchtaithe',
            announce_risk_deleted: 'Riosca scriosta',
            announce_mitigation_status_updated: 'Stádas birt maolaithe nuashonraithe',
            aria_filter: 'Scag DPIAnna',
            aria_filter_by_status: 'Scag de réir stádais',
            aria_filter_by_lead: 'Scag de réir ceannaire measúnachta',
            aria_table: 'Measúnachtaí tionchair cosanta sonraí',
            announce_found: '{count} DPIA(nna) aimsithe',
            announce_decision: 'DPIA {decision}',
            mitigation_start: 'Tosaigh',
            mitigation_mark_implemented: 'Marcáil mar chur i bhfeidhm',
            mitigation_verify: 'Fíoraigh',
            title: 'Measúnuithe Tionchair ar Chosaint Sonraí',
            new: 'MTCS Nua',
            edit: 'Cuir MTCS in Eagar',
            dpia_title: 'Teideal',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Gníomhaíocht',
            risk_score: 'Scór Riosca',
            risks: 'Rioscaí',
            mitigations: 'Maoluithe',
            add_risk: 'Cuir Riosca Leis',
            add_mitigation: 'Cuir Maolú Leis',
            necessity: 'Riachtanas / Comhréireacht',
            likelihood: 'Dóchúlacht',
            impact: 'Tionchar',
            severity_label: 'Déine',
            purpose: 'Cuspóir',
            consultation_notes: 'Consultation Nótaí',
            dpo_opinion: 'Tuairim DPO',
            processing_activity_ids: 'Á phróiseáil Gníomhaíocht ID (comma-separated)',
            system_ids: 'Córas ID (comma-separated)',
            vendor_ids: 'IDanna Soláthraí (camóg-scartha)',
            data_types: 'Sonraí Cineálacha (comma-separated)',
            data_types_short: 'Sonraí Cineálacha',
            data_subjects: 'Sonraí Subjects (comma-separated)',
            assessment_lead_id: 'Measúnú Lead ID',
            assessment_lead: 'Measúnú Lead',
            dpo_id: 'ID DPO',
            reviewer_ids: 'Athbhreithneoir ID (comma-separated)',
            approval_mode: 'Mód Ceadaithe',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Sínigh DPO Riachtanach',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Athbhreithniú Dáta',
            submit_for_review: 'Cuir isteach for Athbhreithniú',
            approve_reject: 'Ceadaigh/Diúltaigh',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Dímhol',
            deprecated: 'Dímholta',
            hide_risks: 'Folaigh Rioscaí',
            hide_mitigations: 'Folaigh Maoluithe',
            comments: 'Tráchtanna',
            submit_decision: 'Cuir isteach Cinneadh',
            category_label: 'Catagóir',
            category_data_breach: 'Sonraí Sárú',
            category_unauthorized_access: 'Unauthorized Rochtain',
            category_function_creep: 'Sleamhnú Feidhme',
            category_re_identification: 'Ath-aitheantú',
            category_discrimination: 'Idirdhealú',
            category_surveillance: 'Faireachas',
            category_other: 'Eile',
            affected_rights: 'Cearta a nDéantar Difear dóibh (camóg-scartha)',
            no_risks: 'Níl aon rioscaí found. aimsithe',
            no_mitigations: 'Níl aon mitigations found. aimsithe',
            no_dpias: 'Níl aon dpias found. aimsithe',
            mitigation_type: 'Cineál',
            mitigation_technical: 'Teicniúil',
            mitigation_organizational: 'Eagraíochtúil',
            mitigation_contractual: 'Conarthach',
            risk_ids: 'Riosca ID (comma-separated)',
            responsible_party: 'Páirtí Freagrach',
            responsible_party_placeholder: 'Nó cuir ainm isteach de láimh',
            affected_rights_placeholder: 'Taispeántar cearta roghnaithe anseo',
            affected_rights_freetext_placeholder: 'Cuir isteach cearta atá buailte (scartha le camóga)',
            responsible: 'Freagrach',
            target_date: 'Sprioc Dáta',
            created: 'Cruthaithe',
            fetch_error: 'Theip ar fetch dpias. Bain triail eile as.',
            save_error: 'Theip ar save dpia. Bain triail eile as.',
            submit_review_error: 'Theip ar submit dpia for athbhreithniú. Bain triail eile as.',
            decision_error: 'Theip ar process decision. Bain triail eile as.',
            deprecate_error: 'Theip ar deprecate dpia. Bain triail eile as.',
            fetch_risks_error: 'Theip ar fetch rioscaí. Bain triail eile as.',
            save_risk_error: 'Theip ar save riosca. Bain triail eile as.',
            delete_risk_error: 'Theip ar delete riosca. Bain triail eile as.',
            fetch_mitigations_error: 'Theip ar fetch mitigations. Bain triail eile as.',
            save_mitigation_error: 'Theip ar save mitigation. Bain triail eile as.',
            update_mitigation_status_error: 'Theip ar stádas an mhaolaithe a nuashonrú. Bain triail eile as.',
            link_form_template: 'Nasc Teimpléad Foirme Rialála',
            link_form_template_placeholder: 'Roghnaigh teimpléad foirme DPIA (roghnach)',
            link_form_template_none: 'Dada',
            linked_form: 'Foirm Nasctha',
            linked_form_creating: 'Foirm nasctha á cruthú...',
            linked_form_error: 'Theip ar chruthú sampla foirme nasctha. Cruthaíodh an DPIA gan foirm nasctha.',
            view_linked_form: 'Foirm Amharc',
            announce_created_with_form: 'DPIA a cruthaíodh le foirm nasctha mar shampla',
            creation_mode: 'DPIA Mód Cruthaithe',
            mode_standard: 'Caighdeán DPIA',
            mode_template: 'Úsáid Teimpléad Foirme Rialála',
            select_form_template: 'Roghnaigh Teimpléad Foirme',
            create_and_fill_form: 'Cruthaigh & Líon Foirm',
            template_sections_title: 'Rannóga na dTeimpléad',
            field_singular: 'réimse',
            field_plural: 'réimsí',
            no_sections: 'Níor aimsíodh aon chuid sa teimpléad seo.',
            template_mode_hint: 'Tar éis cruthú, déanfar tú a atreorú chun an fhoirm rialála iomlán a líonadh.',
            linked_dossier_filings: 'Comhduithe doisiéir nasctha',
            no_linked_dossier_filings: 'Níl aon chomhdú doisiéir nasctha leis an MCTSP seo.',
            view_dossier_filing: 'Féach ar an gcomhdú',
            hide_dossier_filings: 'Folaigh comhduithe doisiéir',
            supporting_evidence: 'Fianaise Tacaíochta',
            hide_supporting_evidence: 'Folaigh Fianaise',
            no_supporting_evidence: 'Níl aon fhianaise tacaíochta ceangailte.',
            upload_supporting_evidence: 'Uaslódáil fianaise tacaíochta',
            view_form_responses: 'Féach ar fhreagraí foirme',
            regulatory_framework: 'Creat rialála',
            all_frameworks: 'Gach creat',
            no_templates_for_market: 'Níl aon teimpléid ar fáil don mhargadh roghnaithe. Roghnaigh margadh eile nó cruthaigh teimpléad le do thoil.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Aistrithe sonraí',
            announce_found: '{count} measúnacht(áí) aistrithe aimsithe',
            announce_submitted_for_review: 'Aistriú curtha isteach le haghaidh athbhreithnithe',
            announce_deprecated: 'Aistriú i léig',
            announce_decision: 'Aistriú {decision}',
            announce_risk_eval_completed: 'Measúnacht riosca críochnaithe',
            announce_safeguard_status_updated: 'Stádas cosanta nuashonraithe',
            announce_safeguards_loaded: '{count} beart cosanta luchtaithe',
            aria_filter: 'Scag measúnachtaí aistrithe',
            aria_filter_by_status: 'Scag de réir stádais',
            aria_filter_by_mechanism: 'Scag de réir meicníochta aistrithe',
            aria_table: 'Measúnachtaí aistrithe sonraí',
            title: 'Measúnuithe Aistrithe Trasteorann',
            new: 'Aistriú Nua',
            edit: 'Cuir Aistriú in Eagar',
            name: 'Ainm',
            from_country: 'Ón Tír',
            to_country: 'Go dtí an Tír',
            safeguards: 'Coimircí',
            risk_level: 'Leibhéal Riosca',
            assessment_notes: 'Nótaí Measúnaithe',
            mechanism: 'Meicníocht',
            transfer_mechanism: 'Meicníocht Aistrithe',
            transfer_mechanism_detail: 'Sonra Meicníochta Aistrithe',
            all_mechanisms: 'Gach Meicníocht',
            mechanism_other: 'Eile',
            data_types: 'Sonraí Cineálacha (comma-separated)',
            data_subjects: 'Sonraí Subjects (comma-separated)',
            purpose: 'Cuspóir',
            recipient_name: 'Recipient Ainm',
            create_success: 'Cruthaíodh measúnú aistrithe.',
            update_success: 'Nuashonraíodh measúnú aistrithe.',
            recipient_details: 'Sonraí an Fhaighteora',
            recipient_details_placeholder: 'Ról, suíomh, córais an fhaighteora, agus sonraí ar aghaidh-aistrithe',
            vendor_id: 'ID Soláthraí',
            processing_activity_ids: 'Á phróiseáil Gníomhaíocht ID (comma-separated)',
            reviewer_ids: 'Athbhreithneoir ID (comma-separated)',
            approval_mode: 'Mód Ceadaithe',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Sínigh DPO Riachtanach',
            review_date: 'Athbhreithniú Dáta',
            created: 'Cruthaithe',
            deprecated: 'Dímholta',
            submit_for_review: 'Cuir isteach for Athbhreithniú',
            approve_reject: 'Ceadaigh/Diúltaigh',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Dímhol',
            risk_eval: 'Riosca Eval',
            hide_safeguards: 'Folaigh Cosaintí',
            mechanism_proof: 'Cruthúnas Meicníochta',
            hide_mechanism_proof: 'Folaigh Cruthúnas Meicníochta',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: 'Níl cruthúnas meicníochta ceangailte.',
            upload_mechanism_proof: 'Íoslódáil an deimhniú ar an meicníocht',
            comments: 'Tráchtanna',
            submit_decision: 'Cuir isteach Cinneadh',
            data_sensitivity: 'Sonraí Sensitivity (1-5)',
            country_risk: 'Tír Riosca (1-5)',
            safeguard_adequacy: 'Cosaint Adequacy (1-5)',
            volume_factor: 'Fachtóir Toirte (1-5)',
            evaluate: 'Meas',
            add_safeguard: 'Cuir Cosaint leis',
            responsible_party: 'Páirtí Freagrach',
            responsible_party_placeholder: 'Nó cuir ainm isteach de láimh',
            responsible: 'Freagrach',
            implementation_date: 'Implementation Dáta',
            implementation: 'Cur i bhfeidhm',
            expiry_date: 'Expiry Dáta',
            expiry: 'Éag',
            expire: 'Éag',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: 'Níl aon safeguards found. aimsithe',
            no_transfers: 'Níl aon transfer measúnú found. aimsithe',
            fetch_error: 'Theip ar fetch transfer measúnú. Bain triail eile as.',
            save_error: 'Theip ar save transfer measúnú. Bain triail eile as.',
            submit_review_error: 'Theip ar submit for athbhreithniú. Bain triail eile as.',
            decision_error: 'Theip ar process decision. Bain triail eile as.',
            deprecate_error: 'Theip ar deprecate transfer. Bain triail eile as.',
            risk_eval_error: 'Theip ar evaluate riosca. Bain triail eile as.',
            fetch_safeguards_error: 'Theip ar fetch safeguards. Bain triail eile as.',
            save_safeguard_error: 'Theip ar save safeguard. Bain triail eile as.',
            update_safeguard_status_error: 'Theip ar stádas an chosanta a nuashonrú. Bain triail eile as.',
            safeguard_type_encryption_in_transit: 'Criptiúchán in Transit',
            safeguard_type_encryption_at_rest: 'Criptiúchán at Rest',
            safeguard_type_access_controls: 'Rialuithe Rochtana',
            safeguard_type_contractual_clauses: 'Clásail Chonarthacha',
            safeguard_type_data_minimization: 'Sonraí Minimization',
            safeguard_type_pseudonymization: 'Bréagainmniú',
            safeguard_type_audit_rights: 'Iniúchadh Cearta',
            safeguard_type_breach_notification: 'Sárú Fógra',
            safeguard_type_data_localization: 'Sonraí Localization',
            safeguard_type_other: 'Eile',
            activate: 'Gníomhachtaigh',
            all_statuses: 'Gach Stádas',
            any: 'Any',
            mechanism_detail: 'Sonra Meicníochta',
            safeguard_types: {
                encryption_in_transit: 'Criptiú ar idirthuras',
                encryption_at_rest: 'Criptiú ar stóráil',
                access_controls: 'Rialuithe rochtana',
                contractual_clauses: 'Cláusail conarthacha',
                data_minimization: 'Íoslaghdú sonraí',
                pseudonymization: 'Ainm bréige',
                audit_rights: 'Cearta iniúchta',
                breach_notification: 'Fógra sáraithe',
                data_localization: 'Logánú sonraí',
                other: 'Eile',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: 'Tuarascáil bhearna measúnachtaí',
            announce_loaded: 'Tuarascáil bhearna luchtaithe. {count} bearna aitheanta',
            aria_activities_without_dpia: 'Gníomhaíochtaí gan measúnacht DPIA',
            aria_activities_unclassified: 'Gníomhaíochtaí le riachtanas DPIA neamhaicmithe',
            activities_unclassified_section_title: 'Gníomhaíochtaí le riachtanas DPIA neamhaicmithe',
            activities_unclassified_description: 'Níor athbhreithníodh na gníomhaíochtaí seo chun a chinneadh an bhfuil DPIA ag teastáil de réir dlí. Aicmigh gach ceann chun an comhartha a ghlanadh.',
            activities_unclassified_empty: 'Tá gach gníomhaíocht ghníomhach aicmithe.',
            aria_unmitigated_risks: 'Rioscaí arda agus criticiúla gan maolú',
            aria_transfers_without_safeguards: 'Aistrithe gan bearta cosanta leordhóthanacha',
            aria_expired_assessments: 'Measúnachtaí éagtha a dteastáíonn athnuachan',
            title: 'Tuairisc Bearnaí Measúnaithe',
            no_dpia: 'Gníomhaíochtaí Gan MTCS',
            no_assessment: 'Aistrithe Gan Measúnú',
            unmitigated: 'MTCSanna le Rioscaí Neamh-mhaolaithe',
            activity_id: 'ID Gníomhaíochta',
            dpia_id: 'ID DPIA',
            assessment_id: 'Measúnú ID',
            expired_assessments: 'Éagtha Measúnuithe',
            no_gaps_activities: 'Níl aon bearna found. all activities have dpias. aimsithe',
            no_unmitigated_risks: 'Níl aon unmitigated high/critical rioscaí found. aimsithe',
            no_gaps_safeguards: 'Níl aon bearna found. all transfers have adequate safeguards. aimsithe',
            no_expired: 'Níl aon expired measúnú found. aimsithe',
            no_report_data: 'Níl aon bearna tuarascáil sonraí. aimsithe',
            fetch_error: 'Theip ar fetch bearna tuarascáil. Bain triail eile as.',
            no_expired_assessments: 'No expired measúnú.',
            no_gaps_dpia: 'Níl aon dpia bearna found. aimsithe',
            no_data: 'Níl aon sonraí. aimsithe',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: 'Sonra comhdú doiséir',
            page_title_list: 'Cásanna comhdú doiséir',
            announce_filings_found: '{count} comhdú(anna) aimsithe',
            announce_results: '{count} torthaí',
            aria_filter_by_status: 'Scag de réir stádais',
            title: 'Cásanna Comhdaithe Sainchomhad',
            nav_label: 'Comhdú Comhaid',
            create: 'Cruthaigh Comhdú Comhad',
            form_type: 'Cineál Foirme',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Tagairt',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Dáta Curtha isteach',
            outcome: 'Toradh',
            parent_filing: 'Tuismitheoir',
            no_filings: 'Níor aimsíodh aon chomhdú.',
            fetch_error: 'Theip ar chomhdú na sainchomhad a fháil. Bain triail eile as.',
            save_error: 'Theip ar chomhdú an chomhaid a chruthú. Bain triail eile as.',
            created: 'Comhdú sainchomhad cruthaithe.',
            loaded: 'Comhdú an tsainchomhaid luchtaithe.',
            not_found: 'Níor aimsíodh comhdú an chomhaid.',
            back_to_list: 'Ar ais chuig an liosta',
            section_assignments: 'Sannacháin Alt',
            assign_sections: 'Ailt a shannadh',
            section_id: 'Alt',
            department: 'Roinn',
            assignee: 'Sannaí',
            deadline: 'Spriocdháta',
            no_assignments: 'Níl aon sannacháin alt fós.',
            sections_assigned_msg: 'Ailt sannta.',
            assign_error: 'Theip ar ailt a shannadh. Bain triail eile as.',
            lifecycle: 'Saolré a Chomhdú',
            submission: 'Sonraí Aighneachta',
            submission_channel: 'Cainéal Aighneachta',
            confirmation_number: 'Uimhir Deimhnithe',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Cuir isteach lena athbhreithniú',
            submitted_for_review: 'Curtha isteach le haghaidh athbhreithniú inmheánach.',
            submit_review_error: 'Theip ar chur isteach le haghaidh athbhreithnithe. Bain triail eile as.',
            legal_signoff: 'Comhartha Dlíthiúil',
            legal_signoff_done: 'síniú dlíthiúil críochnaithe.',
            legal_signoff_error: 'Theip ar síniú dlíthiúil a chur i gcrích. Bain triail eile as.',
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: 'Theip ar cheadú an chomhdaithe. Bain triail eile as.',
            materialisation_status_column: 'Déantán aighneacht',
            materialisation_na: 'Neamhbhainteach',
            materialisation_pending: 'Ar feitheamh',
            materialisation_success: 'Ábhartha',
            materialisation_failed: 'Theip',
            materialisation_banner_title: 'Níorbh fhéidir déantúsán aighneachta a chruthú',
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Bain triail eile as',
            materialisation_retry_count: 'Iarrachtaí eile roimhe seo:',
            materialisation_retry_success: 'Déantán Aighneacht i gcrích go rathúil.',
            materialisation_retry_announce: "D'éirigh le atriail",
            filter_materialisation_failed: 'Taispeáin teipeanna réadúcháin amháin',
            column_title: 'Teideal',
            column_form_type: 'Foirm',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Iarratas ar Fhorlíonadh',
            supplement_requests: 'Iarratais Fhorlíonta',
            supplement_response: 'Freagra forlíontach',
            record_supplement: 'Iarratas ar Fhorlíonadh Taifead',
            supplement_recorded: 'Iarratas forlíonta taifeadta.',
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: 'Theip ar an iarratas forlíonta a thaifeadadh. Bain triail eile as.',
            submit_supplement: 'Cuir Freagra Forlíonta isteach',
            supplement_submitted_msg: 'Freagra forlíonta curtha isteach.',
            supplement_submit_error: 'Theip ar fhreagra forlíonta a chur isteach. Bain triail eile as.',
            response_status: 'Stádas Freagartha',
            response_notes: 'Nótaí Freagartha',
            resubmit: 'Cuir isteach arís',
            resubmitted_msg: 'Cuireadh an comhad isteach arís.',
            resubmit_error: 'Theip ar an gcomhad a chur isteach arís. Bain triail eile as.',
            outcome_error: 'Theip ar an toradh a thaifeadadh. Bain triail eile as.',
            accepted: 'Glactha',
            rejected: 'Diúltaithe',
            close: 'Dún',
            closed_msg: 'Comhdú dúnta.',
            close_error: 'Theip ar an gcomhdú a dhúnadh. Bain triail eile as.',
            change_filing: 'Cruthaigh Comhdú Athruithe',
            change_filing_created: 'Cruthaíodh an comhdú a athrú.',
            change_filing_error: 'Theip ar chomhdú athraithe a chruthú. Bain triail eile as.',
            change_reason: 'Cúis leis an Athrú',
            filing_lineage: 'Líneáil Chomhdú',
            timers: 'Timers Gníomhacha',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Spriocdháta don Fhorlíonadh',
            timer_resubmission_review: 'Spriocdháta Aischuir',
            timer_department_input: 'Spriocdháta na Roinne',
            days_remaining: 'lá fágtha',
            days_overdue: 'lá thar téarma',
            cover_letter: 'Litir Chlúdaigh',
            checklist: 'Seicliosta',
            evidence_index: 'Innéacs Fianaise',
            status_draft: 'Dréacht',
            status_internal_review: 'Athbhreithniú inmheánach',
            status_legal_signoff: 'Comhartha Dlíthiúil',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Forlíonadh iarrtha',
            status_supplement_submitted: 'Forlíonadh curtha isteach',
            status_resubmitted: 'Athchurtha isteach',
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Dúnta',
            // Sprint 62.57.0 (gate E12.5 pass 1, D10) — step_approved, step_submitted,
            // step_accepted and step_rejected are English until Phase 25 (R-57); the
            // other step_* keys keep this locale's translation.
            step_draft: 'Dréacht',
            step_internal_review: 'Athbhreithniú inmheánach',
            step_legal_signoff: 'Comhartha Dlíthiúil',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Forlíonadh iarrtha',
            step_supplement_submitted: 'Forlíonadh curtha isteach',
            step_resubmitted: 'Athchurtha isteach',
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Dúnta',
            linked_dpia: 'MCTSP nasctha',
            linked_dpia_none: 'Níl aon MCTSP nasctha leis an gcomhdú seo.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: 'Féach ar MCTSP',
            linked_dpia_label: 'Tá an comhdú doisiéir seo nasctha leis an MCTSP seo a leanas:',
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            // English until this locale is translated (R-57).
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        materialisation_health: {
            nav_label: 'Sláinte Déantán Aighneacht',
            page_title: 'Aighneacht Déantán Sláinte',
            page_description: "Saincheisteanna a bhaineann le hábhar sainchomhad a bhrath agus a fheabhsú. Déantar liostaí a scóipeáil chuig d'eagraíocht.",
            failed_title: 'Theip ar réadúcháin',
            failed_empty: 'Níor theip ar aon réadúcháin — go maith.',
            pending_title: 'Faofa ach níor tháinig sé i gcrích',
            pending_empty: 'Tá sainchomhad tacaíochta ag gach comhdú ceadaithe.',
            orphan_title: 'Comhaid dílleachta',
            orphan_description: 'Comhaid a chruthaítear trí réadú nach ndéantar tagairt dóibh in aon chomhdú a thuilleadh — go hiondúil cailleann cine comhthráthach. Liostaithe le himscrúdú; Tá glantachán láimhe.',
            orphan_empty: 'Níor aimsíodh aon chomhaid dílleachta.',
            materialise_button: 'Ábharaigh anois',
            column_failure_reason: 'Cúis teip',
            column_failed_at: 'Theip ar',
            column_retries: 'Retriries',
            column_approved_at: 'Faofa ag',
            column_dossier_id: 'Aitheantas an tSainchomhaid',
            column_source_filing_id: 'Comhdú foinse',
            column_dossier_type: 'Cineál',
            column_created_at: 'Cruthaithe ag',
        },
        // Sprint 62.35.0 (Slice C) — structural parity; English until Phase 25 (R-57).
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    classification: {
        access_denied_title: 'Rochtain Diúltaithe',
        access_denied: 'Níl na ceadanna riachtanacha agat chun rochtain a fháil ar an modúl Aicmithe. Déan teagmháil le do riarthóir.',
        nav: {
            taxonomies: 'Tacsanomaíochtaí',
            labeling: 'Lipéadú',
            lifecycle: 'Saolré',
            gaps: 'Tuairisc Bearnaí',
            exports: 'Easpórtálacha',
        },
        exports: {
            title: 'Easpórtálacha aicmithe',
            csv_section: 'Easpórtálacha CSV',
            pdf_section: 'Tuarascálacha PDF',
            inventory: 'Fardal',
            classification_register: 'Clár aicmithe',
            raci: 'Maitrís RACI',
            exceptions: 'Eisceachtaí',
            evidence_pack: 'Pacáiste fianaise',
            delta_report: 'Tuarascáil delta',
            executive_summary: 'Achoimre feidhmiúcháin',
            exporting: 'Ag easpórtáil…',
            export_success: 'Easpórtáil críochnaithe go rathúil.',
            export_error: 'Theip ar an easpórtáil. Déan iarracht arís.',
            csv_download: 'Íoslódáil CSV réidh.',
            pdf_download: 'Giniúint PDF tosaithe.',
            no_data: 'Níl aon sonraí ar fáil le heaspórtáil.',
        },
        delta: {
            title: 'Sonraí athruithe',
            added: 'Curtha leis',
            removed: 'Bainte',
            changed: 'Athraithe',
            added_count: 'Curtha leis ({count})',
            removed_count: 'Bainte ({count})',
            changed_count: 'Athraithe ({count})',
            object_id: 'ID réada',
            object_type: 'Cineál réada',
            taxonomy: 'Tacsanomaíocht',
            level: 'Leibhéal',
            previous: 'Roimhe seo',
            current: 'Reatha',
            no_records: 'Níl aon taifid sa rannán seo.',
            empty_section: 'Tá an rannán seo folamh.',
        },
        schedule: {
            title: 'Sceideal timthriall fionnachtana',
            enabled: 'Cumasaithe',
            interval_days: 'Eatramh (laethanta)',
            interval_label: 'Rith gach N lá',
            manual_only: 'Láimhe amháin',
            every_n_days: 'Gach {n} lá',
            save: 'Sábháil sceideal',
            cancel: 'Cealaigh',
            schedule_updated: 'Sceideal nuashonraithe go rathúil.',
        },
        taxonomies: {
            title: 'Tacsanomaíochtaí Aicmithe',
            new: 'Tacsanomaíocht Nua',
            edit: 'Cuir Tacsanomaíocht in Eagar',
            name: 'Ainm',
            levels: 'Leibhéil Aicmithe',
            seed_defaults: 'Síolaigh Réamhshocrú',
            scope: 'Scóip',
            scope_organization: 'Eagraíocht',
            scope_module_specific: 'Modúl Sonrach',
            deprecate: 'Dímhol',
            hide_levels: 'Folaigh Leibhéil',
            versions: 'Leaganacha',
            hide_versions: 'Folaigh Leaganacha',
            add_level: 'Cuir Leibhéal leis',
            new_level: 'Nua Leibhéal',
            edit_level: 'Cuir in eagar Leibhéal',
            code: 'Cód',
            label: 'Lipéad',
            color: 'Dath',
            ordinal: 'Orduimhir',
            handling_requirements: 'Handling Riachtanais',
            version_history: 'Stair Leaganacha',
            changes: 'Athruithe',
            failed_fetch: 'Theip ar fetch taxonomies. Bain triail eile as.',
            failed_save: 'Theip ar save taxonomy. Bain triail eile as.',
            failed_activate: 'Theip ar activate taxonomy. Bain triail eile as.',
            failed_deprecate: 'Theip ar deprecate taxonomy. Bain triail eile as.',
            failed_seed: 'Theip ar seed default taxonomies. Bain triail eile as.',
            failed_fetch_levels: 'Theip ar fetch taxonomy leibhéal. Bain triail eile as.',
            failed_save_level: 'Theip ar save taxonomy leibhéal. Bain triail eile as.',
            failed_delete_level: 'Theip ar delete taxonomy leibhéal. Bain triail eile as.',
            failed_fetch_versions: 'Theip ar fetch taxonomy versions. Bain triail eile as.',
            loaded_count: '{count} tacsanomaíocht luchtaithe',
            defaults_seeded: 'Tacsanomaíochtaí réamhshocraithe síolaithe.',
            no_levels: "Gan leibhéil aicmithe sainithe — cuir leibhéil leis chun d'ordlathas aicmithe a shainiú.",
            no_versions: 'Gan stair leaganacha fós — cruthaítear leaganacha nuair a nuashonraítear tacsanomaíocht ghníomhach.',
        },
        labeling: {
            title: 'Lipéadú Sonraí',
            new: 'Lipéad Nua',
            edit: 'Cuir Lipéad in Eagar',
            entity_type: 'Cineál Aonáin',
            entity_id: 'Aitheantas Aonáin',
            taxonomy: 'Tacsanomaíocht',
            classification_level: 'Leibhéal Aicmithe',
            applied_by: 'Curtha i bhFeidhm Ag',
            applied_at: 'Curtha i bhFeidhm Ar',
            bulk_apply: 'Cuir i bhFeidhm go Baisc',
            bulk_remove: 'Bain go Baisc',
            all_taxonomies: 'Gach Tacsanomaíocht',
            system: 'Córas',
            dataset: 'Tacar Sonraí',
            level_code_placeholder: 'Leibhéal cód...',
            select_taxonomy: 'Roghnaigh taxonomy...',
            select_level: 'Roghnaigh leibhéal...',
            rationale: 'Réasúnaíocht',
            review_date: 'Athbhreithniú Dáta',
            review: 'Athbhreithniú',
            selected: 'roghnaithe',
            select_all_labels: 'Roghnaigh gach lipéad',
            loaded_count: '{count} lipéad luchtaithe',
            hide_stale_labels: 'Folaigh Stale Lipéid',
            show_conflicts: 'Taispeáin Conflicts',
            hide_conflicts: 'Folaigh Conflicts',
            label_conflicts: 'Coinbhleachtaí Lipéad',
            conflict_reason: 'Cúis Coinbhleachta',
            labels: 'Lipéid',
            failed_fetch: 'Theip ar fetch labels. Bain triail eile as.',
            failed_fetch_taxonomies: 'Theip ar fetch taxonomies. Bain triail eile as.',
            failed_fetch_levels: 'Theip ar fetch taxonomy leibhéal. Bain triail eile as.',
            failed_save: 'Theip ar save label. Bain triail eile as.',
            failed_review: 'Theip ar athbhreithniú label. Bain triail eile as.',
            failed_bulk: 'Theip ar bulk label. Bain triail eile as.',
            failed_stale: 'Theip ar fetch stale labels. Bain triail eile as.',
            failed_conflicts: 'Theip ar fetch label conflicts. Bain triail eile as.',
            review_permission_denied: 'Níl cead agat an lipéad seo a athbhreithniú.',
            review_not_found: "Lipéad gan aimsiú — b'fhéidir gur scriosadh é.",
            approve_selected: 'Ceadaigh na cinn roghnaithe',
            reject_selected: 'Diúltaigh na cinn roghnaithe',
            approved: 'Ceadaithe',
            rejected: 'Diúltaithe',
            labels_count: 'lipéid',
            failed_count: 'theip',
            confirm_approve: 'Deimhnigh ceadú',
            confirm_reject: 'Deimhnigh diúltú',
            approve_n_labels: 'Ceadaigh {count} lipéad?',
            reject_n_labels: 'Diúltaigh {count} lipéad?',
            submit_for_review: 'Cuir isteach le haghaidh athbhreithnithe',
            approve: 'Ceadaigh',
            reject: 'Diúltaigh',
            parent_system: 'Coras tuismitheora',
            parent_system_placeholder: 'Roghnaigh coras tuismitheora...',
            parent_system_help: 'Usaidtear le haghaidh braite coinbhleachta.',
            dataset_level: 'Leibheal an tacair sonrai',
            parent_level: 'Leibheal tuismitheora',
            bulk_created: 'Cruthaiodh {created} lipead',
            bulk_skipped: '{skipped} leimthe',
            bulk_all_skipped: 'Gach {skipped} leimthe',
            ids_hint: 'Cuir isteach IDanna scartha le camoga',
            delete_confirm: 'Scrios lipead do "{id}"?',
        },
        lifecycle: {
            title: 'Monatóireacht Saolré Sonraí',
            new: 'Iontráil Nua',
            current_stage: 'Céim Reatha',
            last_transition: 'Aistriú Deireanach',
            transition: 'Aistriú',
            view_history: 'Féach ar Stair',
            stages: {
                creation: 'Cruthú',
                collection: 'Bailiú',
                processing: 'Próiseáil',
                storage: 'Stóráil',
                retention: 'Coinneáil',
                disposal: 'Diúscairt',
                archived: 'Cartlannaithe',
                active: 'Gníomhach',
                pending_deletion: 'Ar feitheamh Deletion',
                deleted: 'Scriosta',
                on_hold: 'On Coinnigh',
            },
            all_stages: 'Gach Céim',
            object_id: 'ID Réada',
            object_type: 'Object Cineál',
            policy_reference: 'Polasaí Tagairt',
            retention_period_days: 'Coinneáil Tréimhse (laethanta)',
            retention_basis: 'Coinneáil Basis',
            deletion_method: 'Modh Scriosadh',
            deletion_secure_erase: 'Scriosadh Slán',
            deletion_anonymization: 'Anaithnidiú',
            deletion_physical_destruction: 'Scriosadh Fisiceach',
            deletion_other: 'Eile',
            responsible_party: 'Páirtí Freagrach',
            responsible_user_id: 'Responsible Úsáideoir ID',
            retention_expiry: 'Coinneáil Expiry',
            hold_reason: 'Coinnigh Reason',
            hold: 'Coinnigh',
            release: 'Scaoil',
            hide_events: 'Folaigh Imeachtaí',
            events_timeline: 'Imeachtaí Timeline',
            add_event: 'Cuir Imeacht leis',
            event_type: 'Cineál Imeachta',
            create_event: 'Cruthaigh Imeacht',
            from_stage: 'From Céim',
            to_stage: 'To Céim',
            actor: 'Gníomhaí',
            reason_placeholder: 'Cúis...',
            go: 'Go',
            show_expired_retentions: 'Taispeáin Éagtha Coinneáil',
            hide_expired_retentions: 'Folaigh Éagtha Coinneáil',
            expired_retentions_alert: 'Éagtha Coinneáil Foláireamh',
            failed_fetch: 'Theip ar fetch lifecycle taifid. Bain triail eile as.',
            failed_save: 'Theip ar save lifecycle taifead. Bain triail eile as.',
            failed_transition: 'Theip ar transition lifecycle stage. Bain triail eile as.',
            failed_hold: 'Theip ar place hold. Bain triail eile as.',
            failed_release: 'Theip ar release hold. Bain triail eile as.',
            failed_fetch_events: 'Theip ar fetch lifecycle imeacht. Bain triail eile as.',
            failed_create_event: 'Theip ar create lifecycle imeacht. Bain triail eile as.',
            failed_expired: 'Theip ar fetch expired retentions. Bain triail eile as.',
            hold_reason_prompt: 'Iontráil hold reason:',
            loaded_count: '{count} taifead saolré luchtaithe',
            stage_transition_for: 'Aistriú céime do {id}',
            event_created: 'Cruthaithe',
            event_updated: 'Nuashonraithe',
            event_approved: 'Ceadaithe',
            event_deprecated: 'As feidhm',
            event_proposed: 'Molta',
            event_reviewed: 'Athbhreithnithe',
            event_archived: 'Cartlannaithe',
            event_restored: 'Athchóirithe',
        },
        gap_report: {
            title: 'Tuairisc Bearnaí Aicmithe',
            unlabeled: 'Sonraí Gan Lipéad',
            missing_lifecycle: 'Saolré ar Iarraidh',
            stale_labels: 'Lipéid Stálaí',
            run_detection: 'Rith Bearna Detection',
            detecting: 'Ag brath...',
            detection_complete: 'Bearna detection complete. {count} gap(s) detected.',
            detection_failed: 'Bearna detection theip.',
            by_severity: 'By Déine',
            by_gap_type: 'By Bearna Cineál',
            all_gap_types: 'Gach Cineál Bearna',
            conflicting_labels: 'Conflicting Lipéid',
            expired_retention: 'Éagtha Coinneáil',
            missing_handling: 'Láimhseáil Easnamh',
            info: 'Eolas',
            gap_type: 'Cineál Bearna',
            object_id: 'ID Réada',
            object_type: 'Object Cineál',
            detected_at: 'Braite',
            open: 'Oscail',
            acknowledged: 'Admhaithe',
            remediated: 'Leigheasta',
            accepted: 'Glactha',
            acknowledge: 'Admhaigh',
            remediate: 'Leigheas',
            accept_risk: 'Glac le Riosca',
            accept_risk_prompt: 'Iontráil riosca acceptance údar:',
            failed_fetch: 'Theip ar fetch bearna. Bain triail eile as.',
            failed_acknowledge: 'Theip ar acknowledge bearna. Bain triail eile as.',
            failed_remediate: 'Theip ar remediate bearna. Bain triail eile as.',
            failed_accept_risk: 'Theip ar accept bearna riosca. Bain triail eile as.',
            failed_detect: 'Theip ar run bearna detection. Bain triail eile as.',
            loaded_count: '{count} bearna aicmithe luchtaithe',
            related_object_id: 'ID an réad gaolmhair',
            related_object_type: 'Cineál an réad gaolmhair',
            gap_types: {
                unlabeled_asset: 'Socmhainn gan lipead',
                missing_lifecycle: 'Saolre ar iarraidh',
                expired_retention: 'Coinneail imithe in eag',
                extended_pending_deletion: 'Scriosadh fada ar feitheamh',
            },
        },
        label_workflow: {
            title: 'Sreabhadh Oibre Lipéad',
            proposed: 'Molta',
            under_review: 'Á athbhreithniú',
            approved: 'Ceadaithe',
            deprecated: 'As feidhm',
            submit_review: 'Cuir isteach le hathbhreithniú',
            approve: 'Ceadaigh lipéad',
            deprecate: 'Cuir as feidhm',
            bulk_approve: 'Ceadú ollmhór',
            bulk_approve_success: 'Lipéid ceadaithe go rathúil.',
            submit_success: 'Lipéad curtha isteach le hathbhreithniú.',
            approve_success: 'Lipéad ceadaithe.',
            deprecate_success: 'Lipéad curtha as feidhm.',
        },
        review_tasks: {
            title: 'Tascanna Athbhreithnithe',
            create: 'Tasc athbhreithnithe nua',
            system: 'Córas',
            owner: 'Úinéir',
            description: 'Cur síos',
            resolution: 'Réiteach',
            resolve: 'Réitigh',
            approve: 'Ceadaigh',
            modify: 'Athraigh',
            reject: 'Diúltaigh',
            escalate: 'Ardaigh',
            escalated: 'Tascanna ardaithe',
            no_tasks: 'Níl aon tascanna athbhreithnithe.',
            resolve_success: 'Tasc athbhreithnithe réitithe.',
            status_pending: 'Ar feitheamh',
            status_resolved: 'Réitithe',
            status_escalated: 'Ardaithe',
            notes: 'Nótaí',
            create_success: 'Tasc athbhreithnithe cruthaithe.',
            no_tasks_detail: "Gan tascanna athbhreithnithe fós — cruthaigh tasc chun athbhreithnithe lipéad a shannadh d'úinéirí córais.",
            label_id: 'ID lipéid',
            system_id: 'ID córais',
            system_name: 'Ainm an chórais',
            owner_id: 'ID an úinéara',
            resolution_col: 'Reiteach',
            notes_col: 'Notai',
        },
        exceptions: {
            title: 'Scuaine Eisceachtaí',
            create: 'Eisceacht nua',
            severity: 'Déine',
            system: 'Córas',
            owner: 'Úinéir',
            resolve: 'Réitigh',
            no_exceptions: 'Níor aimsíodh aon eisceachtaí.',
            resolve_success: 'Eisceacht réitithe.',
            dashboard_open: 'Eisceachtaí oscailte',
            dashboard_resolved: 'Eisceachtaí réitithe',
            dashboard_total: 'Iomlan',
            dashboard_by_severity: 'De réir déine',
            status_open: 'Oscailte',
            status_resolved: 'Réitithe',
            status_accepted_risk: 'Riosca glactha',
            create_success: 'Eisceacht cruthaithe.',
            no_exceptions_detail: 'Gan eisceachtaí fós — cruthaítear eisceachtaí ó bhearnaí ardtromchúise a éilíonn láimhseáil speisialta.',
            gap_id: 'ID bearna',
            system_id: 'ID córais',
            system_name: 'Ainm an chórais',
            owner_id: 'ID an úinéara',
            resolution_notes: 'Nótaí réitigh',
            resolution_notes_placeholder: 'Déan cur síos ar conas a réitíodh an eisceacht seo...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Léarscáil Sonraí Fiontar',
            inventory: 'Fardal sonraí',
            total_systems: 'Córais iomlána',
            total_labels: 'Lipéid iomlána',
            approved_count: 'Lipéid ceadaithe',
            proposed_count: 'Lipéid molta',
            no_data: 'Níl aon sonraí aicmithe ar fáil.',
            no_approved_labels: 'Gan réad lipéadaithe fós — taispeántar réad ar an léarscáil sonraí chomh luath agus a bhfuil lipéad aicmithe aige, pé acu molta, faoi athbhreithniú nó ceadaithe é.',
            loaded_count: 'Íoslódáil an léarscáil na fiontraíochta',
        },
        discovery: {
            title: 'Timthriallta Fionnachtana',
            create: 'Timthriall nua',
            cycle_name: 'Ainm an timthriall',
            run_delta: 'Rith brath delta',
            delta_success: 'Brath delta críochnaithe.',
            create_success: 'Timthriall fionnachtana cruthaithe.',
            no_cycles: 'Níl aon timthriallta fionnachtana.',
            status_created: 'Cruthaithe',
            status_scanning: 'Scanadh',
            status_review: 'Léirmheas',
            status_approved: 'Ceadaithe',
            status_published: 'Foilsithe',
            status_draft: 'Dréacht',
            running: 'Ag rith',
            completed: 'Críochnaithe',
            cancelled: 'Cealaithe',
            delta_result_summary: 'Aimsíodh {added} curtha leis, {removed} bainte, {changed} lipéad athraithe.',
            delta_results: 'Torthaí delta',
            view_results: 'Féach ar thorthaí',
            scope_type: 'Cineál Scóip',
            scope_type_all: 'Gach',
            scope_type_systems: 'Córais',
            scope_type_datasets: 'tacair sonraí',
            scope_ids: 'Aitheantais raon feidhme',
            scope_ids_placeholder: 'Cuir isteach IDanna scartha le camóga',
            cycle_explanation: 'Déanann timthriall fionnachtana scanadh ar do bhonneagar sonraí chun sócmhainní sonraí a shainaithint, a rangú agus a rianú. Cumraigh an scóip chun díriú ar chórais nó ar thacair sonraí ar leith.',
            empty_state_title: 'Uimh Timthriallta Fionnachtana Fós',
            empty_state_description: 'Cruthaigh do chéad timthriall fionnachtana chun tús a chur le do shócmhainní sonraí a scanadh agus a rangú.',
            empty_state_cta: 'Cruthaigh An Chéad Timthriall',
            validation_name_required: 'Tá ainm rothaíochta ag teastáil',
            validation_scope_type_required: 'Tá gá le cineál scóip',
            validation_scope_ids_required: 'Tá ID scóip amháin ar a laghad ag teastáil',
        },
        regulatory: {
            sensitivity_importance_info: 'Éilíonn Dlí Sonraí (Dlí 60/2024/QH15, req. 18e4bfd1) aicmiú de réir íogaireacht agus tábhacht araon mar thoisí ar leith. Cruthaigh tacsanomaíocht amháin do gach diminsean chun an riachtanas seo a shásamh.',
            handling_rules_info: 'Ba cheart do gach leibhéal aicmithe rialacha láimhseála de réir req a shainiú. b89981ac ("Sainmhínigh rialacha láimhseála de réir leibhéal an aicmithe").',
            template_internal_ids_info: 'Úsáideann gach teimpléad aicmithe aitheantais inmheánacha (m.sh. vn_data_law_data_classification_record). Ní chuirtear teimpléad ar bith i láthair mar fhoirm oifigiúil aireacht.',
            ai_risk_tiers_info: 'Sainmhíníonn an Dlí AI (Dlí 134/2025/QH15, req. 3a87ad63) ceithre shraith riosca do chórais AI: íosta, teoranta, ard, agus do-ghlactha. Cruthaigh tacsanomaíocht riosca AI leis na leibhéil seo.',
            ai_risk_tier_minimal: 'Iosta',
            ai_risk_tier_limited: 'Teoranta',
            ai_risk_tier_high: 'Ard',
            ai_risk_tier_unacceptable: 'Do-ghlactha',
            ai_register_info: 'Ba cheart go mbeadh nasc ag AI taifid aicmithe le AI iontráil i gClár an Chórais (req. 0f4475d3) a ghabháil: ainm an chórais, úinéir, cuspóir, comhthéacs imlonnaithe, leibhéal riosca, rannpháirtíocht díoltóirí.',
            ai_template_internal_info: 'Úsáideann teimpléid aicmithe AI aitheantais inmheánacha (m.sh. AI-RISK-01). Ní úsáidtear aon aitheantais oifigiúla áitsealbhóra AI (AI-HC-01 go AI-HC-08).',
            ai_audit_trail_info: 'Cruthaíonn AI athruithe aicmithe rian iniúchta leagan de réir UC-VN-06: gníomhaí, stampa ama, luach roimhe seo, luach nua, agus réasúnaíocht.',
            lifecycle_data_d1_info: 'Léarscáil aistrithe saolré go nós imeachta Dlí Sonraí DATA-D1 céimeanna: catalógú, rangú, sainiú rialacha láimhseála, gabháil fianaise, athbhreithnithe sceidil.',
            lifecycle_deletion_link_info: 'Ba cheart go spreagfadh sócmhainn sonraí rúnaicmithe a shroicheann deireadh a ré taifead sceidil scriosta tríd an modúl Ceolfhoirne Scriosta.',
            lifecycle_evidence_info: 'Gabhann gach athrú stáit saolré (dréacht, gníomhach, fo-athbhreithniú, cartlannaithe, scriosta) gníomhaí, stampa ama agus fírinniú mar iontráil rian iniúchta.',
            escalation_timers_info: 'UC-VN-10 spriocdhátaí formhéadaithe: 5 lá gnó le haghaidh athbhreithniú úinéara, 48 uair an chloig le haghaidh géarú eisceachta, 72 uair le haghaidh míreanna ardriosca/íogaire.',
            escalation_owner_review: '5 la oibre',
            escalation_exception: '48 uair',
            escalation_high_risk: '72 uair',
            gap_risk_areas_info: 'Dlí Sonraí req. f6c3060a ("measúnú riosca sonraí") is gá anailís ar bhearnaí thar 4 réimse riosca: príobháideacht, cibearshlándáil, rialú rochtana, agus riosca oibriúcháin.',
            gap_risk_area_privacy: 'Priobhaideacht',
            gap_risk_area_cybersecurity: 'Cibearshlandail',
            gap_risk_area_access: 'Rialu rochtana',
            gap_risk_area_operational: 'Oibriochtuil',
            gap_framework_filter_info: 'Scag bearnaí de réir creata rialála (VN_DATA_LAW nó VN_AI_LAW_2026) chun bearnaí ábhartha a fheiceáil do gach raon feidhme rialála.',
            framework_vn_data_law: 'Dli Sonrai VN',
            framework_vn_ai_law: 'Dli IS VN 2026',
            enterprise_map_approved_info: 'Ní thaispeánann an léarscáil sonraí seo ach taifid aicmithe cheadaithe in aghaidh na UC-VN-10 céim sreafa oibre 10 ("Déantar lipéid cheadaithe mar fhírinne an fhiontair faoi láthair"). Ní áirítear dréacht-taifid agus taifid mholta.',
            exports_bundle_info: 'I measc na mbeart onnmhairithe tá: clár aicmithe, rialacha láimhseála, úinéireacht/RACI, agus pacáiste fianaise — UC-VN-10 ceanglais easpórtála agus DATA-EVD-01 formáid bileog fianaise a chomhoiriúnú.',
        },
    },
    monitoring: {
        no_permission_title: 'Ceadanna neamhleor',
        no_permission_message: 'Nil cead agat an modul monatoireachta seo a rochtain.',
        errors: {
            fetch: 'Theip ar load. Bain triail eile as.',
            save: 'Theip ar save. Bain triail eile as.',
            fetch_programs_failed: 'Theip ar fetch programs. Bain triail eile as.',
            fetch_kpi_failed: 'Theip ar fetch kpi sonraí. Bain triail eile as.',
            create_snapshot_failed: 'Theip ar create snapshot. Bain triail eile as.',
            fetch_periods_failed: 'Theip ar fetch periods. Bain triail eile as.',
            create_period_failed: 'Theip ar create period. Bain triail eile as.',
            auto_generate_failed: 'Theip ar auto-generate periods. Bain triail eile as.',
            start_period_failed: 'Theip ar start period. Bain triail eile as.',
            close_period_failed: 'Theip ar close period. Bain triail eile as.',
            save_program_failed: 'Theip ar save program. Bain triail eile as.',
            activate_program_failed: 'Theip ar activate program. Bain triail eile as.',
            pause_program_failed: 'Theip ar pause program. Bain triail eile as.',
            complete_program_failed: 'Theip ar complete program. Bain triail eile as.',
            archive_program_failed: 'Theip ar archive program. Bain triail eile as.',
            fetch_ric_templates_failed: 'Theip ar fetch ric teimpléid. Bain triail eile as.',
            fetch_ric_cases_failed: 'Theip ar fetch ric cases. Bain triail eile as.',
            create_ric_case_failed: 'Theip ar create ric case. Bain triail eile as.',
            transition_ric_failed: 'Theip ar transition ric case. Bain triail eile as.',
            add_follow_up_failed: 'Theip ar add follow-up. Bain triail eile as.',
            respond_follow_up_failed: 'Theip ar respond to follow-up. Bain triail eile as.',
            link_evidence_failed: 'Theip ar link fianaise. Bain triail eile as.',
            fetch_task_templates_failed: 'Theip ar fetch tasc teimpléid. Bain triail eile as.',
            save_task_template_failed: 'Theip ar save tasc teimpléad. Bain triail eile as.',
            delete_task_template_failed: 'Theip ar delete tasc teimpléad. Bain triail eile as.',
            delete_framework_link_failed: 'Theip ar an nasc creatlaí a scriosadh. Bain triail eile as.',
            seed_defaults_failed: 'Theip ar seed defaults. Bain triail eile as.',
            fetch_tasks_failed: 'Theip ar fetch tasc. Bain triail eile as.',
            create_task_failed: 'Theip ar create tasc. Bain triail eile as.',
            instantiate_tasks_failed: 'Theip ar instantiate tasc. Bain triail eile as.',
            start_task_failed: 'Theip ar start tasc. Bain triail eile as.',
            complete_task_failed: 'Theip ar complete tasc. Bain triail eile as.',
            defer_task_failed: 'Theip ar defer tasc. Bain triail eile as.',
            block_task_failed: 'Theip ar block tasc. Bain triail eile as.',
            override_complete_failed: 'Theip ar override complete. Bain triail eile as.',
            assign_task_failed: 'Theip ar assign tasc. Bain triail eile as.',
            update_checklist_failed: 'Theip ar update checklist. Bain triail eile as.',
        },
        nav: {
            programs: 'Cláir',
            periods: 'Tréimhsí',
            task_templates: 'Teimpléid Tascanna',
            tasks: 'Tascanna',
            kpi: 'Painéal KPI',
            ric: 'Fiosrúcháin Rialtóra',
            framework_link: 'Monatóireacht Creata',
            authority_readiness: 'Ullmhacht Údaráis',
            change_review: 'Athbhreithniú Athruithe',
            raci: 'Maitrís RACI',
        },
        programs: {
            page_title: 'Cláir Mhonatóireachta',
            title: 'Cláir Mhonatóireachta',
            new: 'Clár Nua',
            edit: 'Cuir Clár in Eagar',
            name: 'Ainm',
            scope: 'Raon Feidhme',
            frequency: 'Minicíocht',
            owner: 'Úinéir',
            activate: 'Gníomhachtaigh',
            pause: 'Cuir ar Sos',
            resume: 'Lean Ar Aghaidh',
            complete: 'Comhlánaigh',
            archive: 'Cartlannaigh',
            custom_frequency_days: 'Saincheaptha Minicíocht (laethanta)',
            select_user: 'Roghnaigh úsáideoir…',
            assign_training: 'Sann oiliúint',
            linked_forms: 'Foirmeacha nasctha',
            linked_forms_none: 'Níl aon fhoirm nasctha go fóill',
            loaded_count: '{0} clár luchtaithe',
            scope_labels: {
                full: 'Iomlán',
                partial: 'Páirteach',
                targeted: 'Spriocdhírithe',
                consent: 'Toiliú',
                dpia: 'DPIA',
                vendors: 'Soláthróirí',
                classification: 'Aicmiú',
                rights_requests: 'Iarratais Cearta',
                data_mapping: 'Mapáil Sonraí',
                transfers: 'Aistrithe',
                security_monitoring: 'Monatóireacht Slándála',
                authority_response: 'Freagra an Údaráis',
                evidence_preservation: 'Caomhnú Fianaise',
                localization_review: 'Athbhreithniú Logánú',
                ai_system_registration: 'AI Clárú Córais',
                ai_pre_deployment: 'AI Measúnú Réamh-Imlonnaithe',
                ai_transparency: 'AI Seiceáil Trédhearcachta',
                ai_incident_review: 'AI Athbhreithniú Teagmhais',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous/monthly/quarterly cadences. AI Law programs typically use annual review cadences.',
        },
        periods: {
            page_title: 'Tréimhsí Monatóireachta',
            title: 'Tréimhsí Athbhreithnithe',
            new: 'Tréimhse Nua',
            label: 'Lipéad Tréimhse',
            start_date: 'Dáta Tosaithe',
            end_date: 'Dáta Deiridh',
            reviewers: 'Athbhreithnithe',
            auto_generate: 'Uathghintear Tréimhsí',
            start: 'Tosaigh',
            close: 'Dún',
            change_digest: 'Achoimre Athraithe',
            no_digest: 'Níl aon digest. aimsithe',
            reviewers_count: 'Athbhreithneoir Comhaireamh',
            select_program: 'Roghnaigh clár',
            select_program_prompt: 'Roghnaigh a program to view periods.',
            hide_digest: 'Folaigh Digest',
            loaded_count: '{0} tréimhsí lódáilte',
            periods_auto_generated: 'Tréimhsí uathghinte',
            label_placeholder: 'm.sh. 2026-Q1',
        },
        task_templates: {
            page_title: 'Teimpléid Tasc',
            title: 'Teimpléid Tascanna',
            new: 'Teimpléad Nua',
            edit: 'Cuir Teimpléad in Eagar',
            category: 'Catagóir',
            priority: 'Tosaíocht',
            checklist_items: 'Míreanna Seicliosta',
            effort: 'Iarracht Mheasta (uaireanta)',
            pre_seeded: 'Réamhshíolta',
            seed_defaults: 'Síol Réamhshocruithe',
            add_checklist: 'Cuir Mír Seicliosta Leis',
            guidance: 'Nótaí Treorach',
            all_categories: 'Gach Catagóir',
            loaded_count: 'Lódaíodh {0} teimpléad tascanna',
            defaults_seeded: 'Teimpléid réamhshocraithe tasc curtha síol',
            checklist_item_label: 'Mír seicliosta {0}',
            remove_checklist_item: 'Bain mír seicliosta {0}',
            category_info: 'Categories cover PDPL compliance, Cybersecurity Law tasks, and AI Law tasks.',
        },
        tasks: {
            page_title: 'Tascanna Athbhreithnithe',
            title: 'Tascanna Athbhreithnithe',
            new: 'Tasc Nua',
            assigned_to: 'Sannta Do',
            due_date: 'Spriocdháta',
            checklist: 'Seicliosta',
            start: 'Tosaigh',
            complete: 'Comhlánaigh',
            defer: 'Cuir Siar',
            block: 'Cosc',
            override_complete: 'Comhlánaigh le Sárú',
            link_evidence: 'Nasc Fianaise',
            direct_upload_label: 'Uaslódáil comhad fianaise',
            instantiate: 'Cruthaigh ó Theimpléid',
            defer_reason: 'Cúis le cur siar',
            override_justification: 'Údar le sárú',
            filter_assignee: 'Scag by assignee',
            select_period: 'Roghnaigh tréimhse',
            select_program: 'Roghnaigh clár',
            select_program_period_prompt: 'Roghnaigh a program and period to view tasc.',
            assign_placeholder: 'Sann to...',
            loaded_count: '{0} tasc luchtaithe',
            tasks_instantiated: 'Tascanna cruthaithe ó theimpléid',
            overdue: 'Thar téarma',
            checklist_for: 'seicliosta do',
            manage_checklist: 'Bainistigh seicliosta',
            add_checklist_item: 'Cuir mír leis',
            checklist_item_label: 'Mír',
            checklist_item_description: 'Cur síos',
            remove_checklist_item: 'Bain mír',
            save_checklist: 'Sábháil seicliosta',
            no_checklist_items: 'Níl aon mhír seicliosta ann fós.',
            confirm_delete_attachment: 'An bhfuil tú cinnte gur mian leat an comhad fianaise seo a scriosadh? Ní féidir é seo a chur ar ceal.',
        },
        kpi: {
            page_title: 'Painéal KPI',
            title: 'Painéal KPI',
            kpi_name: 'Ainm KPI',
            current_value: 'Luach Reatha',
            target: 'Sprioc',
            trends: 'Treochtaí',
            capture_snapshot: 'Capture Grianghraf',
            select_program: 'Roghnaigh clár',
            select_program_prompt: 'Roghnaigh a program to view kpis.',
            snapshot: 'Grianghraf',
            kpi_labels: {
                on_track: 'Ar an mbóthar ceart',
                at_risk: 'At Riosca',
                behind: 'Ar gcúl',
                rights_request_sla_rate: 'Iarratas um Chearta SLA Ráta',
                vendor_review_currency: 'Airgeadra Athbhreithnithe Díoltóra',
                dpia_completion_rate: 'DPIA Ráta Críochnaithe',
                classification_coverage: 'Clúdach Aicmithe',
                consent_policy_currency: 'Airgeadra Beartais Toiliú',
                security_event_detection_rate: 'Ráta Braite Imeachta Slándála',
                evidence_preservation_compliance_rate: 'Ráta Comhlíonta um Chaomhnú Fianaise',
                authority_response_timeliness: 'Tráthúlacht Freagartha an Údaráis',
                localization_readiness_score: 'Scór Ullmhachta Logánaithe',
                ai_register_completeness: 'AI iomláine Chlár an Chórais',
                pre_deployment_gate_pass_rate: 'Ráta Pas Geata Réamh-Imscartha',
                transparency_disclosure_coverage: 'Clúdach Nochtadh Trédhearcachta',
                ai_incident_readiness_score: 'AI Scór Ullmhachta Teagmhais',
            },
            pdpl_kpi_info: 'The predefined KPI codes are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: 'KPIanna Dlí Cibearshlándála: Ráta Braite Imeachtaí Slándála, Ráta Comhlíonta um Chaomhnú Fianaise, Tráthúlacht Freagartha an Údaráis, Scór Ullmhachta Logánaithe.',
            ai_law_kpi_info: 'KPIanna an Dlí AI: Iomláine Chlár na gCóras AI, Ráta Pas Geata Réamh-Imscartha, Clúdach Nochta Trédhearcachta, Scór Ullmhachta Teagmhais AI.',
            no_data: 'Níl aon sonraí. aimsithe',
            snapshot_date: 'Grianghraf Dáta',
            loaded_count: '{0} grianghraf KPI luchtaithe',
            snapshot_captured: 'Grianghraf KPI glactha',
            showing_chart: 'Ag taispeáint cairt',
            showing_table: 'Ag taispeáint tábla sonraí',
            aria_current_values: 'Luachanna reatha KPI',
            kpi_label: 'KPI',
            actual: 'Iarbhír',
            trend: 'Treocht',
            notes: 'Nótaí',
            trend_up: 'Ag ardú',
            trend_down: 'Ag titim',
            trend_stable: 'Cobhsaí',
        },
        ric: {
            page_title: 'Cásanna RIC',
            title: 'Cásanna Fiosrúchán Rialtóra',
            new: 'Cás RIC Nua',
            reference: 'Tagairt',
            regulator: 'Rialtóir',
            inquiry_type: 'Cineál Fiosrúcháin',
            deadline: 'Spriocdháta Freagartha',
            lead: 'Ceannaire',
            follow_ups: 'Leantaí',
            add_follow_up: 'Cuir Leantach Leis',
            respond: 'Freagair',
            question: 'Ceist',
            response: 'Freagra',
            asked_at: 'Ceistiú',
            no_follow_ups: 'Gan obair leantach fós.',
            received_date: 'Received Dáta',
            responded_at: 'Freagartha',
            hide_follow_ups: 'Folaigh Follow-ups',
            inquiry_types: {
                general: 'Ginearálta',
                investigation: 'Imscrúdú',
                audit: 'Iniúchadh',
                complaint: 'Gearán',
                general_audit: 'Iniúchadh ginearálta',
                rights_requests_review: 'Athbhreithniú iarratas cearta',
                cross_border_transfer_review: 'Athbhreithniú aistrithe trasteorann',
                vendor_governance_review: 'Athbhreithniú rialachas soláthróirí',
                consent_management_review: 'Athbhreithniú bainistíocht toilithe',
                data_mapping_ropa_review: 'Athbhreithniú mapáil sonraí / ROPA',
                incident_readiness_review: 'Athbhreithniú ullmhacht teagmhas',
                security_access_control_review: 'Athbhreithniú slándáil agus rialú rochtana',
                targeted_processing_review: 'Athbhreithniú próiseáil spriocdhírithe',
                bank_readiness_pack: 'Pacáiste ullmhacht bainc',
            },
            link_evidence: 'Nasc Fianaise',
            loaded_count: '{0} chásanna RIC lódáilte',
            to_follow_up: 'le ceist leantach',
            overdue: 'thar téarma',
            overdue_indicator: 'Sáraíodh an spriocdháta freagartha',
            template_info: 'RIC templates are internal platform templates. They are not official government-mandated forms.',
            status_labels: {
                received: 'Faighte',
                in_triage: 'I dtríáis',
                evidence_gathering: 'Bailiú fianaise',
                response_drafting: 'Dréachtú freagartha',
                internal_review: 'Athbhreithniú inmheánach',
                submitted: 'Curtha isteach',
                follow_up: 'Leanúint',
                closed: 'Dúnta',
            },
        },
        framework_link: {
            title: 'Monatóireacht Nasctha le Creat',
            link_program: 'Nasc Clár le Creat',
            framework_code: 'Cód Creata',
            select_framework: 'Roghnaigh creat...',
            select_program: 'Roghnaigh clár...',
            readiness_score: 'Scór Ullmhachta',
            no_links: 'Níor aimsíodh cláir nasctha le creat',
            seed_defaults: 'Cruthaigh Teimpléid Réamhshocraithe',
            program_id: 'Aitheantas an chláir',
            program: 'Clar',
        },
        authority_readiness: {
            title: "Ullmhacht d'Iarratas Údaráis",
            create_entry: 'Iontráil Ullmhachta Nua',
            select_framework: 'Roghnaigh creat...',
            framework_code: 'Creat',
            readiness_status: 'Stádas Ullmhachta',
            readiness_package: 'Pacáiste Ullmhachta',
            run_drill: 'Rith Druil Ullmhachta',
            drill_success: 'Druil ullmhachta cláraithe',
            last_drill: 'Druil is déanaí',
            never_drilled: 'Riamh',
            no_readiness: 'Níor aimsíodh sonraí ullmhachta údaráis',
            entry_created: 'Iontráil ullmhachta cruthaithe',
            ready: 'Réidh',
            partial: 'Páirteach',
            not_ready: 'Níl Réidh',
            items: 'items',
        },
        change_review: {
            title: 'Athbhreithniú Athruithe Rialála',
            new_review: 'Athbhreithniú Athraithe Nua',
            change_id: 'ID Athraithe',
            select_change: 'Roghnaigh athrú...',
            select_framework: 'Roghnaigh creat...',
            impact_assessment: 'Measúnú Tionchair',
            decision: 'Cinneadh',
            no_action: 'Gan Gníomh',
            internal_update: 'Nuashonrú Inmheánach',
            authority_filing: 'Comhdú Údaráis',
            no_reviews: 'Níor aimsíodh athbhreithnithe athruithe',
            auto_cycle: 'Timthriall Athbhreithnithe Uathoibríoch',
            cadence: 'Minicíocht Athbhreithnithe',
            framework: 'Creat',
        },
        export_csv: 'Easpórtáil CSV',
        exporting: 'Á easpórtáil…',
        export_success: "D'éirigh le CSV a easpórtáil.",
        export_failed: 'Theip ar CSV easpórtáil.',
        export_attestation_pack: 'Pacáiste Fianaithe Easpórtála',
        staleness: {
            badge_label: '{0} míreanna seanchaite',
            days_since: '{0} lá ón nuashonrú is déanaí',
            detail_title: 'Míreanna Stale',
            accept_justification: 'Glac le Fírinniú',
            justification_label: 'Fírinniú',
            no_stale: 'Níor aimsíodh aon earraí seanchaite.',
            threshold_label: 'Tairseach Staleness (laethanta)',
            threshold_min: '30 lá ar a laghad',
            threshold_max: '730 lá ar a mhéad',
            threshold_saved: 'Nuashonraíodh an tairseach staleness.',
        },
        csv_columns: {
            name: 'Ainm',
            status: 'Stádas',
            owner: 'Úinéir',
            created_at: 'Cruthaithe Ag',
            updated_at: 'Nuashonraithe Ag',
            scope: 'Raon feidhme',
            frameworks: 'Creataí',
            period_label: 'Lipéad Tréimhse',
            start_date: 'Dáta Tosaigh',
            end_date: 'Dáta Deiridh',
            task_count: 'Comhaireamh Tasc',
            completion_rate: 'Ráta Críochnaithe',
            title: 'Teideal',
            assigned_to: 'Sannta do',
            due_date: 'Dáta Dlite',
            priority: 'Tosaíocht',
            completed_at: 'Críochnaithe Ag',
            kpi_code: 'KPI Cód',
            kpi_label: 'KPI Lipéad',
            target: 'Sprioc',
            actual: 'Iarbhír',
            trend: 'Treocht',
            reference: 'Tagairt',
            regulator: 'Rialtóir',
            inquiry_type: 'Cineál Fiosrúcháin',
            lead: 'Luaidhe',
            received_date: 'Dáta Faighte',
            deadline: 'Spriocdháta',
            framework_code: 'Creatchóid',
            readiness_status: 'Stádas Ullmhachta',
            package_count: 'Líon Pacáiste',
            change_id: 'Athraigh ID',
            impact_assessment: 'Measúnú Tionchair',
            decision: 'Cinneadh',
            program: 'Clár',
            readiness_score: 'Scór Ullmhachta',
            category: 'Catagóir',
            checklist_count: 'Comhaireamh Seicliosta',
            effort_hours: 'Iarracht (uair an chloig)',
            version: 'Leagan',
            department: 'Roinn',
            function_area: 'Réimse Feidhme',
            role: 'Ról',
            assignee: 'Sannaí',
            frequency: 'Minicíocht',
        },
        raci: {
            page_title: 'Rialachas Maitrís RACI',
            title: 'Rialachas Maitrís RACI',
            department: 'Roinn',
            function_area: 'Réimse Feidhme',
            responsible: 'Freagrach',
            accountable: 'Cuntasach',
            consulted: 'Chuathas i gcomhairle',
            informed: 'Ar an eolas',
            assign_role: 'Sann Ról RACI',
            select_program: 'Roghnaigh Clár',
            select_program_prompt: 'Roghnaigh clár chun amharc ar mhaitrís RACI.',
            no_assignments: 'Níor aimsíodh aon sannadh RACI.',
            role_updated: 'Nuashonraíodh sannadh RACI.',
            role_labels: {
                R: 'Freagrach',
                A: 'Cuntasach',
                C: 'Chuathas i gcomhairle',
                I: 'Ar an eolas',
            },
            loaded_count: 'Lódáilte {0} tasc RACI',
            single_accountability_warning: 'Ní cheadaítear ach ról Cuntasach (A) amháin in aghaidh an réimse feidhme. Cuirfear ról Cuntasach nua in ionad an róil reatha.',
            cybersec_function_areas: 'Réimsí feidhme Dlí Cibearshlándála: Freagairt ar Theagmhais, Caomhnú Fianaise, Comhar Údaráis, Athbhreithniú Logánaithe.',
            ai_law_function_areas: 'AI Réimsí feidhm dlí: Clárú Córais, Measúnú Réamh-Imscartha, Trédhearcacht, Láimhseáil Teagmhais.',
            suggested_function_areas: 'Réimsí Feidhme Molta',
        },
        prior_cycle: {
            link_label: 'Nasc leis an tSraith Roimh Ré',
            select_prior: 'Roghnaigh an timthriall roimhe seo…',
            linked: 'Bliain thar Bhliana',
            no_prior: 'Níl aon timthriallta roimh ré ar fáil.',
            linked_success: 'Timthriall roimhe nasctha.',
        },
        digest_table: {
            module: 'Modúl',
            entity_type: 'Cineál Aonáin',
            change_type: 'Athrú Cineál',
            description: 'Cur síos',
            changed_at: 'Athraithe Ag',
        },
        view_related_ric: 'Féach ar Chásanna Gaolmhara RIC',
        linked_program: 'Clár Nasctha',
        nav_raci: 'Maitrís RACI',
    },
    incidents: {
        authority_response_draft: {
            draft_label: 'Dréachtfhreagra',
            drafting: 'Ag dréachtú…',
            results_heading: 'Freagraí dréachtaithe ({count})',
            tokens_label: '≈ {count} comharthaí',
            counsel_label: 'Athbhreithniú abhcóide ag teastáil:',
            counsel_default: 'Teastaíonn athbhreithniú abhcóide ó fhreagra amháin ar a laghad sula seolfar é.',
            requires_counsel_label: 'Teastaíonn athbhreithniú abhcóide.',
            accept_label: 'Glac leis an dréacht',
            reject_label: 'Diúltaigh dréacht',
            error_generic: 'Níorbh fhéidir an freagra a dhréachtú. Bain triail eile as nó déan téacs an fhiosrúcháin a bheachtú.',
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        breach_timeline: {
            compute_label: 'Ríomh amlíne fógra sáraithe',
            computing: 'Ríomhaireacht…',
            results_heading: 'Sárú spriocdhátaí fógra ({count})',
            tokens_label: '≈ {count} comharthaí',
            handoff_label: 'Molann Forseti:',
            empty_label: 'Ní dhearbhaíonn aon chreat suiteáilte dualgas fógra don teagmhas seo.',
            mandatory_label: 'Réimsí éigeantacha:',
            draft_label: 'Dréacht fógra',
            remaining_label: '{count}h fágtha',
            overdue_label: 'THAR LEAR ag {count}h',
            acknowledge_label: 'Admhaigh an amlíne',
            acknowledging: 'Ag admháil…',
            error_generic: 'Níorbh fhéidir amlíne an tsáraithe a ríomh. Bain triail eile as.',
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Confirmed Sárú',
            no: 'Not a Sárú',
            uncertain: 'Ar feitheamh',
        },
        errors: {
            fetch: 'Theip ar load teagmhas. Bain triail eile as.',
            save: 'Theip ar save teagmhas. Bain triail eile as.',
            fetch_failed: 'Theip ar fetch teagmhas. Bain triail eile as.',
            save_failed: 'Theip ar save teagmhas. Bain triail eile as.',
            transition_failed: 'Theip ar transition teagmhas. Bain triail eile as.',
            close_failed: 'Theip ar close teagmhas. Bain triail eile as.',
            fetch_timeline_failed: 'Theip ar fetch timeline. Bain triail eile as.',
            fetch_decisions_failed: 'Theip ar fetch decisions. Bain triail eile as.',
            triage_failed: 'Theip ar update triage. Bain triail eile as.',
            impact_update_failed: 'Theip ar update impact measúnú. Bain triail eile as.',
            breach_update_failed: 'Theip ar update sárú measúnú. Bain triail eile as.',
            create_timeline_failed: 'Theip ar create timeline iontráil. Bain triail eile as.',
            create_decision_failed: 'Theip ar create decision. Bain triail eile as.',
            notification_update_failed: 'Theip ar update notification. Bain triail eile as.',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — English until Phase 25 (R-57)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Theip ar fetch tasc. Bain triail eile as.',
            create_task_failed: 'Theip ar create tasc. Bain triail eile as.',
            update_task_status_failed: 'Theip ar stádas an taisc a nuashonrú. Bain triail eile as.',
            update_checklist_failed: 'Theip ar update checklist. Bain triail eile as.',
            assign_task_failed: 'Theip ar assign tasc. Bain triail eile as.',
            escalate_task_failed: 'Theip ar escalate tasc. Bain triail eile as.',
            fetch_templates_failed: 'Theip ar fetch teimpléid. Bain triail eile as.',
            permission_denied: 'Nil cead agat rochtain a fhail ar an roinn seo. Dean teagmhail le do riarthoir chun rochtain a iarraidh.',
        },
        harm_likelihood: {
            unlikely: 'Neamhdhóchúil',
            possible: 'Féideartha',
            likely: 'Dóchúil',
            very_likely: 'An-dóchúil',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'Ríomhphost',
            phone: 'Fón',
            internal_system: 'Inmheánach Córas',
            third_party: 'Tríú Páirtí',
            automated_detection: 'Uathoibrithe Detection',
        },
        task_transition_labels: {
            start: 'Tosaigh',
            complete: 'Comhlánaigh',
            block: 'Blocáil',
            defer: 'Cuir siar',
            cancel: 'Cealaigh',
            unblock: 'Díbhlocáil',
        },
        transition_labels: {
            start: 'Tosaigh',
            complete: 'Comhlánaigh',
            block: 'Blocáil',
            defer: 'Cuir siar',
        },
        nav: {
            all: 'Gach Teagmhas',
            tasks: 'Tascanna Freagartha',
            reports: 'Tuairiscí',
            breach_notifications: 'Fógraí Sáraithe',
            supplements: 'Forlíontaí',
            remediation: 'Leigheas',
            authority_requests: 'Iarratais Údaráis',
            disclosure_register: 'Clár Nochta',
        },
        list: {
            page_title: 'Teagmhais',
            title: 'Teagmhais',
            new: 'Teagmhas Nua',
            edit: 'Cuir Teagmhas in Eagar',
            incident_title: 'Teideal',
            severity: 'Déine',
            reported_at: 'Tuairiscithe Ar',
            reported_by: 'Tuairiscithe Ag',
            assigned_to: 'Sannta Do',
            view_detail: 'Féach ar Shonraí',
            reference: 'Tagairt',
            lead: 'Ceannaire',
            reported_via: 'Tuairiscithe Trí',
            discovery_date: 'Discovery Dáta',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Folaigh Sonraí',
            close_incident: 'Dún Teagmhas',
            close_justification_prompt: 'Please provide a údar for closing this teagmhas.',
            justification: 'Údar',
            lead_id: 'ID Ceannaire',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Teagmháil',
            category: 'Catagóir',
            not_assessed: 'Gan measúnú',
        },
        detail: {
            page_title: 'Sonraí Teagmhais',
            title: 'Sonraí Teagmhais',
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: 'Forbhreathnú',
                triage: 'Tríáis',
                impact: 'Measúnú Tionchair',
                breach: 'Measúnú Sáraithe',
                timeline: 'Amlíne',
                decisions: 'Cinntí',
                evidence: 'Fianaise',
                audit_trail: 'Rian Iniúchta',
            },
            evidence_upload_label: 'Ceangail comhad fianaise',
            evidence_uploaded: 'Fianaise uaslódáilte',
            evidence_deleted: 'Fianaise scriosta',
            evidence_delete_error: 'Theip ar fhianaise a scriosadh',
            evidence_uploaded_at: 'Uaslódáilte',
            evidence_empty: 'Níl aon chomhad fianaise ceangailte leis an eachtra seo.',
            evidence_size: 'Méid',
            triage_notes: 'Nótaí Tríáise',
            initial_classification: 'Aicmiú Tosaigh',
            individuals_affected: 'Líon na nDaoine a nDearnadh Difear Dóibh',
            data_categories: 'Catagóirí Sonraí i gCeist',
            systems_compromised: 'Córais Comhbhrúite',
            geographic_scope: 'Raon Geografach',
            breach_determination: 'Cinneadh ar Shárú',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Fógra Riachtanach',
            notification_deadline: 'Spriocdháta Fógra',
            pdpc_reportable: 'In-tuairiscithe do PDPC',
            add_timeline: 'Cuir Iontráil Amlíne Leis',
            add_decision: 'Cuir Cinneadh Leis',
            not_found: 'Teagmhas not found.',
            team_members: 'Baill Foirne',
            checklist: 'Seicliosta',
            completed: 'Críochnaithe',
            impact_severity_score: 'Calculated Tionchar Déine Scór',
            records_affected: 'Taifid Affected',
            potential_consequences: 'Iarmhairtí Féideartha',
            harm_likelihood: 'Harm Dóchúlacht',
            legal_basis: 'Bunús Dlíthiúil',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Dlínse',
            jurisdictions_comma: 'Dlínse (comma-separated)',
            jurisdiction: 'Dlínse',
            authority_status: 'Authority Stádas',
            subject_status: 'Subject Stádas',
            authority_deadline: 'Spriocdháta Údaráis',
            subject_deadline: 'Spriocdháta Ábhair',
            mark_authority_done: 'Marcáil Údarás Críochnaithe',
            mark_subject_done: 'Marcáil Ábhar Críochnaithe',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2) — English until Phase 25 (R-57)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — English until Phase 25 (R-57)
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'Gan measúnú',
            event_type: 'Cineál Imeachta',
            timestamp: 'Stampa Ama',
            actor: 'Gníomhaí',
            no_timeline_events: 'No timeline imeacht yet.',
            decision_type: 'Cinneadh Cineál',
            decision: 'Cinneadh',
            rationale: 'Réasúnaíocht',
            decision_made: 'Cinneadh Made',
            no_decisions: 'Níl aon chinntí taifeadta fós.',
            no_audit_events: 'Níor taifeadadh aon imeachtaí iniúchta go fóill.',
            export_pdf: 'Easpórtáil PDF',
            pdf_title: 'Tuarascáil Achoimre Teagmhais',
            description: 'Cur Síos',
            linked_records: 'Taifid Nasctha',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Tascanna Freagartha Teagmhais',
            title: 'Tascanna Freagartha Teagmhais',
            new: 'Tasc Nua',
            edit: 'Cuir Tasc in Eagar',
            task_type: 'Cineál Taisc',
            assignee: 'Sannaí',
            due_date: 'Spriocdháta',
            dependencies: 'Spleáchais',
            mark_complete: 'Marcáil Críochnaithe',
            templates: 'Teimpléid Tascanna',
            select_incident: 'Roghnaigh Teagmhas',
            all_statuses: 'Gach Stádas',
            all_categories: 'Gach Catagóir',
            new_response_task: 'Tasc Freagartha Nua',
            category: 'Catagóir',
            priority: 'Tosaíocht',
            depends_on: 'Ag Brath Ar (aitheantais tascanna deighilte le camóg)',
            checklist_items: 'Míreanna Seicliosta',
            remove: 'Bain',
            add: 'Cuir Leis',
            new_checklist_item: 'Mír seicliosta nua',
            no_templates: 'Níl aon teimpléid ar fáil.',
            assign_task: 'Sann Tasc',
            assign_to: 'Sann Do',
            assign: 'Sann',
            select_incident_prompt: 'Roghnaigh teagmhas le do thoil chun a thascanna freagartha a fheiceáil.',
            checklist: 'Seicliosta',
            hide_checklist: 'Folaigh Seicliosta',
            escalate: 'Ardaigh',
            escalations: 'Arduithe',
            no_checklist_items: 'Níl aon mhíreanna seicliosta ann.',
        },
        reports: {
            page_title: 'Tuarascálacha Teagmhais',
            title: 'Tuairiscí Teagmhas',
            summary: 'Staitisticí Achoimre',
            by_severity: 'De réir Déine',
            by_status: 'De réir Stádais',
            recent: 'Teagmhais Déanacha',
            total_incidents: 'Teagmhais Iomlána',
            open: 'Oscailte',
            closed: 'Dúnta',
            export_csv: 'Easpórtáil CSV',
            export_pdf: 'Easpórtáil PDF',
            export_success: 'Tuarascáil easpórtáilte go rathúil.',
            export_error: 'Theip ar easpórtáil an tuarascáil.',
            reference: 'Tagairt',
            view_report: 'Féach ar Thuairisc',
        },
        statuses: {
            reported: 'Tuairiscithe',
            triage: 'Tríáis',
            contained: 'Srianta',
            investigation: 'Imscrúdú',
            remediation: 'Feabhsúchán',
            closed: 'Dúnta',
            false_positive: 'Dearfach Bréagach',
        },
        task_statuses: {
            pending: 'Ar Feitheamh',
            in_progress: 'Ar Siúl',
            completed: 'Críochnaithe',
            blocked: 'Coiscthe',
            cancelled: 'Cealaithe',
        },
        severity: {
            low: 'Íseal',
            medium: 'Meánach',
            high: 'Ard',
            critical: 'Criticiúil',
        },
        task_types: {
            containment: 'Srianadh',
            investigation: 'Imscrúdú',
            remediation: 'Feabhsúchán',
            communication: 'Cumarsáid',
            documentation: 'Doiciméadú',
            notification: 'Fógra',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Sonraí Sárú',
            unauthorized_access: 'Unauthorized Rochtain',
            data_loss: 'Sonraí Loss',
            system_compromise: 'Córas Compromise',
            malware: 'Bogearraí mailíseacha',
            phishing: 'Fioscaireacht',
            insider_threat: 'Bagairt Inmheánach',
            other: 'Eile',
        },
        reported_via_options: {
            email: 'Ríomhphost',
            phone: 'Fón',
            internal_system: 'Inmheánach Córas',
            third_party: 'Tríú Páirtí',
            automated_detection: 'Uathoibrithe Detection',
        },
        harm_likelihood_options: {
            unlikely: 'Neamhdhóchúil',
            possible: 'Féideartha',
            likely: 'Dóchúil',
            very_likely: 'An-dóchúil',
        },
        breach_determination_options: {
            confirmed_breach: 'Confirmed Sárú',
            suspected_breach: 'Suspected Sárú',
            not_a_breach: 'Not a Sárú',
            pending: 'Ar feitheamh',
        },
        timeline_event_types: {
            detection: 'Brath',
            escalation: 'Ardú',
            containment: 'Cosc',
            investigation_start: 'Imscrúdú Tosaigh',
            evidence_collected: 'Fianaise Collected',
            notification_sent: 'Fógra Sent',
            remediation_action: 'Leigheas Gníomh',
            status_change: 'Stádas Change',
            communication: 'Cumarsáid',
            other: 'Eile',
        },
        decision_types: {
            containment: 'Cosc',
            notification: 'Fógra',
            remediation: 'Leigheas',
            escalation: 'Ardú',
            closure: 'Dúnadh',
            other: 'Eile',
        },
        task_transitions: {
            start: 'Tosaigh',
            complete: 'Comhlánaigh',
            block: 'Blocáil',
            cancel: 'Cealaigh',
            unblock: 'Díbhlocáil',
        },
        announce: {
            loaded: '{count} teagmhas lódáilte',
            reports_loaded: '{count} tuairisc teagmhais lódáilte',
            tasks_loaded: '{count} thasc lódáilte',
            created: "D'éirigh le heachtra a cruthaíodh",
            updated: "D'éirigh le heachtra a nuashonrú",
            closed: "D'éirigh leis an teagmhas a dhúnadh",
            status_changed: 'Athraíodh stádas teagmhais',
            triage_completed: 'Críochnaíodh triáis go rathúil',
            impact_updated: 'Measúnú tionchair nuashonraithe',
            breach_updated: 'Nuashonraíodh an measúnú ar sháruithe',
            timeline_added: 'Imeacht amlíne curtha leis',
            decision_recorded: 'Cinneadh taifeadta',
            notification_updated: 'Nuashonraíodh stádas an fhógra',
            task_created: "D'éirigh leis an tasc a chruthú",
            task_status_changed: 'Athraíodh stádas an taisc',
            checklist_completed: 'Mír seicliosta críochnaithe',
            checklist_unchecked: 'Mír seicliosta gan seiceáil',
            task_assigned: "D'éirigh leis an tasc a sannadh",
            task_escalated: 'Méadaíodh an tasc',
            export_initiated: 'Easpórtáil tosaithe',
        },
        breach_notifications: {
            page_title: 'Fógraí Sáraithe',
            title: 'Fógraí Sáraithe',
            new_notification: 'Fógra Nua',
            no_notifications: 'Níor aimsíodh aon fhógra sáraithe',
            status: 'Stádas',
            submit_to_mps: 'Cuir isteach chuig MPS',
            decision: 'Cinneadh',
            decision_maker: 'Cinnteoir',
            notify: 'Fógra a thabhairt',
            do_not_notify: 'Ná Fógra',
            rationale: 'Réasúnaíocht',
            filing_deadline: 'Spriocdháta Comhdaithe',
            overdue: 'Thar téarma',
            due_soon: 'Dlite go luath',
            on_track: 'Ar an mbóthar ceart',
            legal_review: 'Athbhreithniú Dlí',
            incident_id: 'Aitheantas teagmhais',
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Measúnú',
                decision_to_notify: 'Cinneadh Fógra a thabhairt',
                notification_drafted: 'Fógra Dréachtaithe',
                legal_review: 'Athbhreithniú Dlí',
                submitted_to_authority: 'Curtha faoi bhráid an údaráis',
                supplement_requested: 'Forlíonadh Iarrtha',
                supplement_submitted: 'Forlíonadh curtha isteach',
                acknowledged: 'Admhaítear',
                closed: 'Dúnta',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Iarratais Fhorlíonta',
            title: 'Iarratais Fhorlíonta',
            new_supplement: 'Forlíonadh Nua',
            no_supplements: 'Níor aimsíodh aon iarratas forlíonta',
            request_details: 'Sonraí Iarratas',
            response_package: 'Pacáiste Freagartha',
            submit_response: 'Cuir Freagra',
            deadline: 'Spriocdháta',
            notification_id: 'Aitheantas fógra',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Cuir isteach aitheantas fógra chun forlíontaí a fheiceáil.',
            supplement_statuses: {
                requested: 'Iarrtha',
                drafting: 'Dréachtadh',
                submitted: 'Arna chur isteach',
                accepted: 'Glactar leis',
                rejected: 'Diúltaithe',
            },
        },
        remediation: {
            page_title: 'Gníomhartha Leasúcháin',
            title: 'Gníomhartha Leasúcháin',
            new_action: 'Gníomh Nua',
            no_actions: 'Níor aimsíodh aon ghníomhartha feabhais',
            description: 'Cur síos',
            owner: 'Úinéir',
            department: 'Roinn',
            deadline: 'Spriocdháta',
            statuses: {
                planned: 'Pleanáilte',
                in_progress: 'Ar Siúl',
                completed: 'Críochnaithe',
                verified: 'Fíoraithe',
            },
            verify: 'Fíoraigh',
            incident_id: 'Aitheantas teagmhais',
            enter_incident_id: 'Cuir isteach aitheantas teagmhais chun gníomhartha leasúcháin a fheiceáil.',
            start: 'Tosaigh',
            complete_action: 'Críochnaigh',
        },
        authority_requests: {
            page_title: 'Iarratais Údaráis',
            title: 'Iarratais Údaráis',
            new_request: 'Iarratas Nua',
            no_requests: 'Níor aimsíodh aon iarratas údaráis',
            authority_name: 'Ainm an Údaráis',
            request_details: 'Sonraí Iarratas',
            response_package: 'Pacáiste Freagartha',
            submission_channel: 'Cainéal Aighneachta',
            submit_response: 'Cuir Freagra',
            deadline: 'Spriocdháta',
            confirmation: 'Daingniú',
            incident_id_optional: 'Aitheantas teagmhais (roghnach)',
            response_context_label: 'Comhthéacs freagartha (le haghaidh ábharú an doiciméid aighneachta)',
            response_context_none: '(gabh thar ábharú)',
            response_context_supplement: 'Freagra forlíontach',
            response_context_information: 'Freagra ar iarratas eolais',
            response_context_inspection: 'Freagra ar chigireacht',
            materialisation_status_column: 'Doiciméad aighneachta',
            materialisation_pending: 'Ar feitheamh',
            materialisation_success: 'Ábharaithe',
            materialisation_failed: 'Teipthe',
            materialisation_retry_button: 'Bain triail eile as',
            ar_statuses: {
                received: 'Faighte',
                verified: 'Fíoraithe',
                assembling: 'Ag cur le chéile',
                submitted: 'Curtha isteach',
                closed: 'Dúnta',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Athbhreithniú Dlí',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            checklist: 'Seicliosta',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Clár Nochta',
            title: 'Clár Nochta',
            new_entry: 'Iontráil Nua',
            no_entries: 'Níor aimsíodh aon iontrálacha sa chlár nochta',
            disclosure_type: 'Cineál Nochta',
            disclosure_date: 'Dáta Nochta',
            recipient: 'Faighteoir',
            authority_name: 'Ainm an Údaráis',
            channel: 'Cainéal',
            content_summary: 'Achoimre ar an Ábhar',
            incident_id_optional: 'Aitheantas teagmhais (roghnach)',
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    onboarding: {
        nav: {
            wizard: 'Draoi Socraithe',
            tours: 'Turais Modúl',
            sample_data: 'Sonraí Samplacha',
        },
        wizard: {
            title: 'Draoi Socraithe',
            welcome: 'Fáilte go AesirX ComplianceOne',
            welcome_message: "Socraímis d'eagraíocht. Treorófaidh an draoi seo tú tríd an gcumraíocht tosaigh.",
            setup_complete: 'Socrú Críochnaithe',
            skipped_message: "Scipeáladh an draoi socraithe. Is féidir leat é a rith am ar bith chun d'eagraíocht a chumrú.",
            start_setup: 'Tosaigh Socrú',
            skip_wizard: 'Scipeáil Draoi',
            go_to_admin: 'Téigh go dtí an Riarachán',
            skip_for_now: 'Scipeáil Anois',
            next_step: 'An Chéad Chéim Eile',
            previous_step: 'An Chéim Roimhe Seo',
            finish_setup: 'Críochnaigh Socrú',
            rerun: 'Rith an Draoi Socraithe Arís',
            step_label: 'Céim {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: 'Tá ainm na heagraíochta riachtanach.',
            validation_admin_email_required: 'Tá ríomhphost an riarthóra riachtanach.',
            validation_admin_email_invalid: 'Cuir isteach seoladh ríomhphoist bailí le do thoil.',
            validation_cmp_endpoint_required: 'Tá críochphointe CMP riachtanach nuair atá CMP cumasaithe.',
            steps: {
                license_activation: 'Gníomhachtaithe Ceadúnais',
                org_profile: 'Próifíl na hEagraíochta',
                module_selection: 'Roghnú Modúl',
                cmp_integration: 'Comhtháthú CMP',
                notification_preferences: 'Fógraí',
                review: 'Athbhreithniú & Dearbhú',
            },
            step_descriptions: {
                license_activation: 'Cuir isteach d’eochair cheadúnais chun do phacáiste a ghníomhachtú agus cinntigh cé na modúil atá ar fáil.',
                org_profile: 'Socraigh ainm na heagraíochta, dlínse, tionscal, méid agus teanga roghnaithe.',
                module_selection: "Roghnaigh cé na modúil chomhlíonta atá le gníomhachtú do d'eagraíocht.",
                cmp_integration: 'Cumraigh comhtháthú an Ardáin Bainistíochta Toilithe (roghnach).',
                notification_preferences: 'Cumraigh conas a fhaigheann tú fógraí faoi imeachtaí comhlíontachta.',
                review: 'Athbhreithnigh do chumraíocht agus críochnaigh an socrú.',
            },
            license_step: {
                validate_button: 'Ceadúnas a bhailíochtú',
                skip_warning: 'Beidh rochtain ar mhodúil teoranta don tsraith phacáiste réamhshocraithe. Is féidir leat ceadúnas a ghníomhachtú níos déanaí in Ardán > Socruithe Ceadúnais.',
            },
            module_descriptions: {
                rights_requests: 'Déileáil le hiarratais ar rochtain agus scriosadh sonraí pearsanta.',
                data_mapping: 'Mapáil córais, sreafaí sonraí agus gníomhaíochtaí próiseála.',
                governance: 'Déan maoirseacht ar sholáthróirí, ar fhaomhadh, ar shraonadh agus ar na cláir fianaise.',
                consent: 'Bainistigh polasaithe toilithe agus catalóga cuspóirí.',
                consent_properties: 'Bainistigh toiliú trasna maoine digiteacha iolracha.',
                assessments: 'Déan measúnuithe tionchair agus aistrithe sonraí trasteorann.',
                classification: 'Aicmigh agus lipéadaigh sócmhainní sonraí le rianú saolré.',
                monitoring: 'Athbhreithnithe comhlíonta sceidealta agus rianú KPI.',
                incidents: 'Tuairiscigh agus bainistigh teagmhais slándála agus príobháideachta.',
                connectors: 'Ceangail le córais sheachtracha le haghaidh malartú sonraí uathoibríoch.',
                vendor_risk: 'Scóráil soláthraithe, rianú conarthaí agus bainistíocht díchill chuí.',
                deletion_orchestration: 'Comhordaigh scriosadh sonraí trasna córas agus soláthraithe.',
                discovery: 'Aimsigh agus rangaigh sonraí ar fud do bhonneagair.',
                incident_ops: 'Leabhair súgartha teagmhas ardleibhéil, rianú SLA agus géaruithe.',
                program_governance: 'Measúnachtaí aibíochta, tuairisciú feidhmiúcháin agus ullmhacht iniúchta.',
                access_accountability: 'Monatóirigh rochtain úsáideoirí, seisiúin agus sreafaí oibre údaraithe.',
                localization: 'Pacáistí comhlíontachta margaidh-shonracha agus creataí rialála.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Spás oibre comhpháirtithe agus athdhíoltóirí.',
                privacy_scanner: 'Monatóireacht príobháideachais fíor-ama ar mhaoine digiteacha.',
                onboarding: 'Draoi socraithe, turais threoraithe agus bainistíocht sonraí samplacha.',
                compliance_forms: 'Teimpléid chomhlíontachta idirghníomhacha agus giniúint doiciméad.',
                audit_trail: 'Logáil iniúchta dho-athraithe chuimsitheach agus coinneáil.',
                organizations: 'Bainistíocht il-eagraíochta agus grúpaí.',
                task_management: 'Pleanáil tasc aontaithe, inneall sreafa oibre agus radharcanna féilire.',
            },
            module_categories: {
                data_privacy_rights: 'Príobháideacht & Cearta Sonraí',
                vendor_third_party: 'Bainistíocht Díoltóra & Tríú Páirtí',
                risk_assessment: 'Riosca & Measúnú',
                operations_automation: 'Oibríochtaí & Uathoibriú',
                platform_admin: 'Ardán & Riarachán',
            },
            package_tier_label: 'Do Phacáiste',
            package_module_unavailable: 'Ní áirítear i do phacáiste',
            select_all: 'Roghnaigh Uile',
            deselect_all: 'Díroghnaigh Uile',
            load_sample_data: 'Lódáil sonraí samplacha tar éis socraithe',
            load_sample_data_description: 'Suiteáil sonraí taispeána réadúla chun an t-ardán a iniúchadh. Is féidir iad a bhaint amach níos déanaí ó Tosú > Sonraí Samplacha.',
            language_label: 'Teanga Roghnaithe',
            language_description: "Roghnaigh an teanga réamhshocraithe do d'eagraíocht.",
            admin_account_title: 'Cuntas Riarthóra',
            admin_account_created: 'Cuntas riarthóra cruthaithe',
            admin_full_name: 'Ainm iomlán',
            admin_email: 'Seoladh ríomhphoist',
            admin_password: 'Pasfhocal',
            admin_confirm_password: 'Deimhnigh pasfhocal',
            admin_show_password: 'Taispeáin pasfhocal',
            admin_hide_password: 'Folaigh pasfhocal',
            admin_name_placeholder: 'e.g. Jane Smith',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: '8 gcarachtar ar a laghad',
            admin_password_confirm_placeholder: 'Athiontráil pasfhocal',
            validation_admin_name_required: 'Tá ainm an riarthóra riachtanach',
            validation_admin_password_required: 'Tá pasfhocal riachtanach',
            validation_admin_password_min_length: 'Caithfidh an pasfhocal 8 gcarachtar ar a laghad a bheith ann',
            validation_passwords_mismatch: 'Ní hionann na pasfhocail',
            notification_email_enabled: 'Cumasaigh Fógraí Ríomhphoist',
            notification_email_description: 'Faigh fógraí ríomhphoist maidir le sannta tasc, sáruithe SLA agus imeachtaí comhlíontachta.',
            notification_digest_frequency: 'Minicíocht Achoimre',
            notification_digest_daily: 'Laethúil',
            notification_digest_weekly: 'Seachtainiúil',
            notification_digest_none: 'Gan achoimre',
            notification_in_app_enabled: 'Cumasaigh Fógraí Ardáin',
            notification_in_app_description: 'Taispeáin suaitheantas agus tarraiceán fógraí le haghaidh foláirimh fíor-ama.',
            jurisdictions: {
                vietnam: 'Vítneam',
                asean: 'ASEAN',
                eu_eea: 'AE/LEE',
                global: 'Domhanda',
            },
            languages: {
                en: 'Béarla',
                vi: 'Faigh Việt',
                da: 'Dansc',
                sv: 'Svenska',
                nb: 'Norsk Bokmål',
                nn: 'Ioruais Nynorsk',
                de: 'Deutsch',
                fi: 'Suomi',
                nl: 'An Ísiltír',
                fr: 'Français',
                lb: 'Lëtzebuergesch',
                ga: 'Gaeilge',
                es: 'Español',
                ca: 'Catalá',
                eu: 'Euscair',
                pt: 'Português',
            },
            industries: {
                financial_services: 'Seirbhísí Airgeadais',
                healthcare: 'Cúram Sláinte',
                technology: 'Teicneolaíocht',
                telecommunications: 'Teileachumarsáid',
                government: 'Rialtas',
                education: 'Oideachas',
                manufacturing: 'Déantúsaíocht',
                retail: 'Miondíol',
                other: 'Eile',
            },
            placeholders: {
                org_name: 'm.sh., ABC',
                admin_email: 'admin@company.vn',
                team_emails: 'user1@company.vn, user2@company.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: 'Ainm na hEagraíochta',
                jurisdiction: 'Dlínse',
                industry: 'Tionscal',
                org_size: 'Méid na hEagraíochta',
                language: 'Teanga Roghnaithe',
                admin_email: 'Ríomhphost Riaracháin',
                team_emails: 'Ríomhphoist Comhaltaí Foirne (deighilte le camóg)',
                enable_cmp: 'Cumasaigh Comhtháthú CMP',
                cmp_endpoint: 'Críochphointe API CMP',
                email_notifications: 'Fógraí Ríomhphoist',
                in_app_notifications: 'Fógraí Ardáin',
                digest_frequency: 'Minicíocht Achoimre',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Turais Ionduchtaithe Modúl',
            start: 'Tosaigh Turas',
            continue_tour: 'Lean leis an Turas',
            replay: 'Athsheinn Turas',
            exit: 'Scoir ón Turas',
            skip: 'Scipeáil Turas',
            next: 'Ar Aghaidh',
            previous: 'Roimhe Seo',
            finish: 'Críochnaigh Turas',
            step_of: 'Céim {current} de {total}',
            info: 'Glac turais threoraithe ar gach modúl chun príomhghnéithe agus sreafaí oibre a fhoghlaim. Is féidir turais a athsheinm am ar bith.',
            autoplay: 'Uath-imirt',
            interactive: 'Idirghníomhach',
            play_all: 'Seinn ar fad',
            search_tours: 'Cuardaigh turais...',
            duration: 'Fad',
            steps_label: '{count} chéim',
            mode: 'Mód',
            pause: 'Sos',
            resume: 'Atosaigh',
            speed: 'Luas',
            restart: 'Atosaigh',
            reset: 'Athshocraigh',
            reset_success: 'Athshocraíodh dul chun cinn an turais.',
            category_core_privacy: 'Príobháideacht Croí',
            category_governance: 'Rialachas',
            category_operations: 'Oibríochtaí',
            category_platform: 'Ardán',
            category_advanced: 'Casta',
            category_data_privacy_rights: 'Príobháideacht & Cearta Sonraí',
            category_vendor_third_party: 'Bainistíocht Díoltóra & Tríú Páirtí',
            category_risk_assessment: 'Riosca & Measúnú',
            category_operations_automation: 'Oibríochtaí & Uathoibriú',
            category_platform_admin: 'Ardán & Riarachán',
            steps_count: '{count} chéim',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: 'Bainistíocht Sonraí Samplacha',
            select_modules: 'Roghnaigh Modúil',
            current_status: 'Stádas Reatha',
            operation_history: 'Stair Oibríochta',
            load: 'Lódáil Sonraí Samplacha',
            clear: 'Glan Sonraí Samplacha',
            processing: 'Á Phróiseáil...',
            loaded: 'Lódáilte',
            not_loaded: 'Gan Lódáil',
            records: 'Taifid Shamplacha',
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: 'An bhfuil tú cinnte gur mian leat gach sonraí samplacha a ghlanadh do na modúil roghnaithe?',
            confirm_clear_title: 'Scrios sonraí samplacha',
            confirm_load_title: 'Luchtaigh sonraí samplacha',
            confirm_load: 'An bhfuil tú cinnte gur mhaith leat sonraí samplacha a luchtú do na modúil roghnaithe?',
            action_load: 'Luchtaigh',
            action_clear: 'Scrios',
            info: 'Lódáil sonraí taispeána réadúla le haghaidh oiliúna agus taispeántas. Tá na sonraí samplacha clibeáilte agus is féidir iad a bhaint go sábháilte gan tionchar ar shonraí fíora.',
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: 'Ionad Cabhrach',
            search: 'Cuardaigh',
            search_placeholder: 'Cuardaigh ailt chabhrach...',
            clear: 'Glan',
            all_categories: 'Gach Catagóir',
            all_modules: 'Gach Modúl',
            back: 'Ar Ais go dtí an tIonad Cabhrach',
            callout_important: 'Tábhachtach',
            callout_tip: 'Leid',
            no_results: 'Níor aimsíodh aon ailt a mheaitseálann do chuardach.',
            no_articles: 'Níl aon ailt chabhrach ar fáil go fóill. Beidh ailt le feiceáil anseo de réir mar a chuirtear ábhar leis.',
            results_for: '{count} toradh/torthaí ar "{query}"',
            quick_links: {
                getting_started: 'Tosú',
                getting_started_desc: 'Suiteáil, socrú, agus treoir mheartosaithe.',
                user_guide: 'Treoir Úsáideora',
                user_guide_desc: 'Doiciméadú úsáide modúl ar mhodúl.',
                admin_guide: 'Treoir Riaracháin',
                admin_guide_desc: 'Cumrú, cothabháil, agus fabhtcheartú.',
                faq: 'Ceisteanna Coitianta',
                faq_desc: 'Ceisteanna a chuirtear go minic agus freagraí.',
            },
            search_articles_aria: 'Cuardaigh altanna cabhrach',
            article_metadata_aria: 'Meiteashonraí an ailt',
            faq_editor_title: 'Eagarthóir CCanna an Ionaid Chabhrach',
            faq_editor_description: 'Cuir péirí ceiste agus freagra CC le halt ar bith san Ionad Cabhrach, cuir in eagar iad nó bain iad. Níl an t-eagarthóir ar fáil fós; tá an seoladh seo curtha i leataobh dó.',
            faq_editor_empty: 'Níl eagarthóir na CCanna ar fáil fós. Go dtí sin, iarr ar do riarthóir ranna CC a chur le halt san Ionad Cabhrach ar do shon.',
            articles_list_aria: 'Altanna cabhrach',
            category_nav_aria: 'Nascleanúint catagóire',
            quick_links_aria: 'Naisc thapa chuig catagóirí cabhrach',
            returned_to_list: 'Filleadh ar liosta na n-alt',
            opening_article: 'Ag oscailt ailt: {title}',
            read_article_aria: 'Léigh alt: {title}',
            pagination_nav_aria: 'Uimhriú leathanaigh alt',
            error_unavailable: 'Níl an t-ionad cabhrach ar fáil faoi láthair. Bain triail as arís níos déanaí.',
            retry: 'Bain triail as arís',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        // English seed for structural parity (R-57): translation is
        // docs/product/phases/phase-25/sprint-58.7.0.md.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    common: {
        vendor: 'Vendor',
        // R-57 — structural parity with English values; real translations are EN and VI.
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 'm.sh. {value}',
        /* R-57 — structural parity; real translation deferred to the locale programme. */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Sábháil',
        cancel: 'Cealaigh',
        system_actor: 'Córas',
        delete: 'Scrios',
        edit: 'Cuir in Eagar',
        create: 'Cruthaigh',
        update: 'Nuashonraigh',
        loading: 'Á Lódáil...',
        error: 'Tharla earráid',
        success: 'Rath',
        logout: 'Logáil Amach',
        actions: 'Gníomhartha',
        retrying: 'Ag triail arís…',
        permission_denied: 'Níl rochtain agat ar an leathanach seo.',
        status: 'Stádas',
        description: 'Cur Síos',
        name: 'Ainm',
        created_at: 'Cruthaithe Ar',
        updated_at: 'Nuashonraithe Ar',
        yes: 'Sea',
        no: 'Ní hea',
        confirm: 'Dearbhaigh',
        confirm_dialog_title: 'Confirm action',
        back: 'Ar Ais',
        next: 'Ar Aghaidh',
        previous: 'Roimhe Seo',
        search: 'Cuardaigh',
        filter: 'Scag',
        page_x_of_y: 'Leathanach {page} de {total}',
        pagination_label: 'Leathanaigh',
        view_mode_label: 'Mód amhairc',
        export: 'Easpórtáil',
        exporting: 'Á easpórtáil…',
        sidebar_navigation: 'Nascleanúint barra taoibh admin',
        items_count: '{count} mhíreanna',
        items_count_one: '{count} mhír',
        export_success: 'Gineadh an doiciméad go rathúil.',
        generate_pdf: 'Gin PDF',
        import: 'Iompórtáil',
        download: 'Íoslódáil',
        upload: 'Uaslódáil',
        view: 'Féach',
        close: 'Dún',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Cuir Isteach',
        saved: 'Sábháilte go rathúil',
        saving: 'Á shábháil...',
        submitting: 'Á chur isteach...',
        created: 'Cruthaithe go rathúil',
        all: 'Uile',
        none: 'Ceann ar Bith',
        select: 'Roghnaigh',
        select_user: 'Roghnaigh úsáideoir...',
        custom_input: 'Clóscríobh de láimh',
        required: 'Riachtanach',
        optional: 'Roghnach',
        notes: 'Nótaí',
        tags: 'Clibeanna',
        archive: 'Cartlannaigh',
        clone: 'Clónáil',
        refresh: 'Athnuaigh',
        retry: 'Bain Triail Eile As',
        // fix-sprint-20260810-2128 residue — English placeholder for structural
        // parity; locale work beyond EN/VI defers to Phase 25 (R-57, sprint-57.7.0).
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Athshocraigh',
        add: 'Cuir Leis',
        remove: 'Bain',
        removing: 'Á bhaint…',
        attachment: 'Iatán',
        upload_file: 'Uaslódáil comhad',
        upload_file_required: 'Uaslódáil comhad (riachtanach)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Tarraing agus scaoil nó cliceáil chun brabhsáil',
        upload_max_size: 'Uasmhéid: {size} MB',
        upload_accepted_types: 'Glactha: {types}',
        upload_in_progress: '{name} á uaslódáil...',
        upload_size_exceeded: 'Sáraíonn méid an chomhaid an t-uasmhéid ceadaithe de {size} MB.',
        upload_type_not_accepted: 'Ní ghlactar leis an gcineál comhaid. Cineálacha a nglactar leo: {types}',
        upload_succeeded: 'Uaslódáladh an comhad "{name}".',
        upload_failed: 'Theip ar uaslódáil an chomhaid.',
        select_entity_type_first: 'Roghnaigh cineál aonáin ar dtús',
        failed_to_remove_file: 'Theip ar an gcomhad a bhaint.',
        entity_type_label: 'Cineál aonáin',
        entity_label: 'Aonán',
        entity_reference: 'Tagairt aonáin',
        failed_to_load_entities: 'Theip ar na haonáin a luchtú',
        enable: 'Cumasaigh',
        disable: 'Díchumasaigh',
        no_data: 'Níor aimsíodh aon sonraí',
        confirm_delete: 'An bhfuil tú cinnte gur mian leat an mhír seo a scriosadh?',
        confirm_action: 'An bhfuil tú cinnte gur mian leat leanúint ar aghaidh?',
        save_success: 'Sábháilte go rathúil',
        save_error: 'Theip ar shábháil',
        delete_success: 'Scriosta go rathúil',
        delete_error: 'Theip ar scriosadh',
        hour: 'uair',
        hours: 'uaire',
        load_error: 'Theip ar lódáil sonraí',
        operation_error: 'Theip ar an oibríocht',
        something_went_wrong: 'Chuaigh rud éigin mícheart',
        go_home: 'Téigh go dtí an Painéal',
        access_denied: 'Rochtain Diúltaithe',
        no_permission: 'Níl cead agat rochtain a fháil ar an leathanach seo.',
        no_permission_title: 'Ceadanna neamhleora',
        no_permission_message: 'Níl cead agat rochtain a fháil ar an leathanach seo.',
        unknown: 'Anaithnid',
        back_to_dashboard: 'Ar Ais go dtí an Painéal',
        id: 'ID',
        type: 'Cineál',
        date: 'Dáta',
        created_by: 'Cruthaithe Ag',
        updated_by: 'Nuashonraithe Ag',
        page_of: 'Leathanach {page} de {total}',
        showing_items: '{count} mír á thaispeáint',
        items_per_page: 'Míreanna in aghaidh an leathanaigh',
        no_results: 'Níl aon taifid ag meaitseáil do scagairí',
        no_items: 'Níor aimsíodh aon mhíreanna',
        sort_by: 'Sórtáil de réir',
        ascending: 'Ardú',
        descending: 'Laghdú',
        all_statuses: 'Gach Stádas',
        all_types: 'Gach Cineál',
        all_categories: 'Gach Catagóir',
        all_severities: 'Gach Déine',
        date_range: 'Raon Dátaí',
        from_date: 'Ó',
        to_date: 'Go',
        clear_filters: 'Glan Scagairí',
        apply_filters: 'Cuir Scagairí i bhFeidhm',
        save_changes: 'Sábháil Athruithe',
        discard_changes: 'Caith Athruithe ar Ceal',
        confirm_discard: 'Tá athruithe neamhshábháilte agat. An bhfuil fonn ort iad a chaitheamh ar ceal?',
        copy: 'Cóipeáil',
        duplicate: 'Dúblach',
        move: 'Bog',
        assign: 'Sann',
        unassign: 'Díshann',
        approve: 'Ceadaigh',
        reject: 'Diúltaigh',
        deleting: 'Á Scriosadh...',
        processing: 'Á Phróiseáil...',
        loading_page: 'Leathanach á lódáil...',
        operation_success: "D'éirigh leis an oibríocht",
        operation_failed: 'Theip ar an oibríocht. Bain triail as arís le do thoil.',
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
            gov_vendor_read: 'read vendors',
            vendor_questionnaire_read: 'read vendor questionnaires',
            ai_governance_read: 'read AI systems',
            dpia_read: 'read data processing impact assessments',
            incident_read: 'read incidents',
            dm_evidence_read: 'read Data Mapping evidence',
            vehicle_monitoring_read: 'read vehicle monitoring devices',
            forms_instance_read: 'read form records',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '—',
        network_error: 'Earráid líonra. Seiceáil do cheangal.',
        session_expired: 'Seisiún imithe in éag. Logáil isteach arís le do thoil.',
        test: 'Tástáil',
        pause: 'Cuir ar Sos',
        resume: 'Lean Ar Aghaidh',
        activate: 'Gníomhachtaigh',
        last_sync: 'Sioncronú Deireanach',
        health: 'Sláinte',
        email: 'Ríomhphost',
        role: 'Ról',
        title: 'Teideal',
        owner: 'Úinéir',
        priority: 'Tosaíocht',
        category: 'Catagóir',
        details: 'Sonraí',
        overview: 'Forbhreathnú',
        configuration: 'Cumraíocht',
        history: 'Stair',
        new_item: 'Nua',
        edit_item: 'Cuir in Eagar',
        select_option: 'Roghnaigh...',
        total: 'Iomlán',
        active: 'Gníomhach',
        inactive: 'Neamhghníomhach',
        enabled: 'Cumasaithe',
        disabled: 'Díchumasaithe',
        last_login: 'Logáil Isteach Dheireanach',
        never: 'Riamh',
        password_changed: 'Athraíodh an pasfhocal. Logálfar amach thú.',
        password_change_failed: 'Theip ar athrú pasfhocail',
        passwords_mismatch: 'Ní hionann na pasfhocail',
        install: 'Suiteáil',
        install_named: 'Suiteáil {name}',
        uninstall: 'Dísuiteáil',
        installed: 'Suiteáilte',
        available: 'Ar Fáil',
        catalog: 'Catalóg',
        published: 'Foilsithe',
        version: 'Leagan',
        market: 'Margadh',
        language: 'Teanga',
        framework: 'Creat',
        template: 'Teimpléad',
        connector_type: 'Cineál Nascóra',
        polling_interval: 'Eatramh Vótálaíochta (nóiméad)',
        new_instance: 'Cás Nua',
        change_password: 'Athraigh Pasfhocal',
        current_password: 'Pasfhocal Reatha',
        new_password: 'Pasfhocal Nua',
        confirm_password: 'Dearbhaigh Pasfhocal',
        comments: 'Tráchtanna',
        completed: 'Críochnaithe',
        contact: 'Teagmháil',
        hide: 'Folaigh',
        hide_details: 'Folaigh Sonraí',
        label: 'Lipéad',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'Ag taispeáint',
        page: 'Leathanach',
        preview: 'Réamhamharc',
        print: 'Priontáil',
        show: 'Taispeáin',
        status_active: 'Gníomhach',
        status_completed: 'Críochnaithe',
        status_draft: 'Dréacht',
        status_in_progress: 'Ar siúl',
        status_pending: 'Ar feitheamh',
        view_details: 'Féach ar Shonraí',
        all_priorities: 'Gach Tosaíocht',
        pipeline: 'Píblíne',
        all_requests: 'Gach Iarratas',
        schedule: 'Sceideal',
        deploy: 'Imscaradh',
        rollback: 'Aischur',
        advance_stage: 'Céim ar aghaidh',
        submit_for_approval: 'Cuir isteach for Ceadú',
        approval_log: 'Logáil Ceadaithe',
        approver: 'Ceadaitheoir',
        decision: 'Cinneadh',
        decided_at: 'Cinnte',
        rollout_stages: 'Rollout Céimeanna',
        approval_comment: 'Trácht Ceadaithe',
        optional_comment: 'Roghnach comment...',
        impact_summary: 'Tionchar Achoimre',
        target_property_ids: 'Sprioc Maoin ID (comma-separated)',
        policy_id: 'Polasaí ID',
        target_version: 'Sprioc Leagan',
        target_version_placeholder: 'm.sh. 2.0.0',
        scheduled_label: 'Sceidealta',
        scheduled_start: 'Sceidealta Tosaigh',
        scheduled_end: 'Sceidealta End',
        properties_count: '{count} maoin',
        results: 'Torthaí',
        comparison_results: 'Torthaí Comparáide',
        past_comparison_reports: 'Past Comparison Tuarascálacha',
        scanning: 'Ag scanadh...',
        run_inconsistency_scan: 'Rith Inconsistency Scanadh',
        property_ids_label: 'Maoin ID (comma-separated, min 2)',
        comparison_type: 'Comparison Cineál',
        golden_template_id: 'Golden Teimpléad ID (optional)',
        leave_blank_compare: 'Fág folamh chun maoine a chur i gcomparáid lena chéile',
        add_notes_placeholder: 'Cuir do nótaí anailíse anseo...',
        covered_of_active: '{covered} as {active} maoin ghníomhach clúdaithe',
        fetch_error: 'Theip ar fetch sonraí. Bain triail eile as.',
        run_comparison_error: 'Theip ar run comparison. Bain triail eile as.',
        save_notes_error: 'Theip ar save notes. Bain triail eile as.',
        scan_complete: 'Inconsistency scanadh críochnaithe successfully.',
        scan_error: 'Theip ar run inconsistency scanadh. Bain triail eile as.',
        min_two_properties: 'Please provide at least 2 property ID for comparison.',
        comma_separated: 'scartha le camóga',
        language_english: 'Béarla',
        language_vietnamese: 'Vítneaimis',
        language_vietnamese_native: 'Faigh Việt',
        author: 'Údar',
        dependencies: 'Spleáchais',
        checksum: 'Suim Sheiceála',
        changelog: 'Logáil Athruithe',
        tier: 'Sraith',
        phone: 'Fón',
        launch: 'Seoladh',
        complete: 'Comhlánaigh',
        back_to_list: 'Ar ais to List',
        clear_selection: 'Glan Roghnú',
        deactivate: 'Díghníomhachtaigh',
        scope: 'Scóip',
        deadline: 'Spriocdháta',
        progress: 'Dul chun cinn',
        frequency: 'Minicíocht',
        reviewer: 'Athbhreithneoir',
        user: 'Úsáideoir',
        campaign: 'Feachtas',
        items: 'Míreanna',
        next_run: 'Ar aghaidh Rith',
        last_run: 'Last Rith',
        overdue: 'OVERDUE',
        creating: 'Ag cruthú...',
        launching: 'Ag seoladh...',
        loaded: 'loaded',
        page_size: 'Méid an leathanaigh',
        per_page: '/ leathanach',
        urgent: 'Práinneach',
        pagination: 'Leathanaigh',
        metric: 'méadrach',
        value: 'Luach',
        country: 'Tír',
        showing_chart_view: 'Amharc cairteacha á thaispeáint',
        showing_table_view: 'Amharc tábla sonraí á thaispeáint',
        policies: 'Polasaithe',
        compliant: 'Comhlíontach',
        non_compliant_status: 'Neamhchomhlíontach',
        all_data: 'Na sonraí go léir',
        sr_urgent: 'Práinneach',
        n_per_page: '{n} / leathanach',
        select_user_placeholder: '-- Roghnaigh úsáideoir --',
        field_required: 'Tá an réimse seo riachtanach',
        results_found: 'torthaí aimsithe',
        select_placeholder: '-- Roghnaigh --',
        unknown_user: 'Úsáideoir anaithnid',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // English until Phase 25 (R-57).
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'Easpórtáil CSV',
        not_applicable: 'Ní bhaineann',
        aria_overdue_since: 'Thar téarma ó {date}',
        placeholders: {
            enter_justification: 'Iontráil údar',
            justification: 'Údar',
            campaign_name_example: 'Q1 2026 Rochtain Athbhreithniú',
            optional_description: 'Roghnach cur síos',
            enter_user_id: 'Iontráil the úsáideoir id of the reviewer',
            enter_user_id_short: 'Iontráil úsáideoir id',
            schedule_name_example: 'Ráithiúil Rochtain Attestation',
            enter_property_id: 'Iontráil property id',
            enter_policy_id: 'Iontráil polasaí id',
            property_or_group_id: 'Maoin or group ID',
            describe_template_purpose: 'Describe the teimpléad purpose...',
            step_title: 'Céim teideal...',
            detailed_instructions: 'Treoracha mionsonraithe...',
            filter_by_taxonomy_id: 'Scag by Tacsanomaíocht ID...',
            describe_classification_rule: 'Déan cur síos ar an riail aicmithe...',
            describe_dedup_rule: 'Déan cur síos ar an riail dídúblála...',
            describe_import_template: 'Describe the import teimpléad...',
            reason_for_rejection: 'Cúis le diúltú...',
            jurisdiction_codes_example: 'VN, SG, AE',
            filter_by_locale: 'Scag by locale...',
            filter_by_event_type: 'Scag by imeacht cineál',
            filter_by_actor_id: 'Scag by actor ID',
            filter_by_market_code: 'Scag by market cód...',
            filter_by_framework_id: 'Scag by creat ID...',
            add_comment: 'Cuir trácht leis...',
            provide_rejection_reason: 'Tabhair cúis le diúltú na haighneachta seo...',
            filter_by_jurisdiction: 'Scag by jurisdiction cód...',
            enter_report_title: 'Iontráil tuarascáil teideal',
            enter_name: 'Iontráil ainm',
            enter_description: 'Iontráil cur síos',
            enter_org_id: 'Iontráil org id to add',
            describe_evidence_gap: 'Describe the fianaise bearna...',
            expected_evidence: 'Cén fhianaise a bhfuiltear ag súil leis',
            entity_identifier: 'Aitheantóir aonáin',
            remediation_steps: 'Céimeanna to remediate this bearna',
            user_id_or_name: 'ID Úsáideora nó ainm',
            reporting_period_id: 'Tuairisciú period ID',
            maturity_model_id: 'Aibíocht model ID',
            describe_maturity_model: 'Déan cur síos ar an mhúnla aibíochta seo...',
            describe_role_purpose: "Describe this role's purpose...",
            provide_deny_reason: 'Tabhair cúis le diúltú an iarratais seo',
            describe_emergency: 'Describe the emergency situation requiring elevated rochtain (minimum 20 characters)',
            enter_delegate_id: 'Iontráil úsáideoir id of the delegate',
            provide_delegation_reason: 'Tabhair cúis leis an toscaireacht seo',
            client_identifier: 'Aitheantóir cliaint',
            client_secret: 'Rún cliaint',
            time_format: 'UU:NN',
            describe_sod_rule: 'Déan cur síos ar chuspóir na rialach SoD seo...',
            enter_role_id: 'Iontráil ról id',
            explain_exception: 'Mínigh cén fáth a bhfuil an eisceacht seo ag teastáil...',
            describe_compensating_controls: 'Describe compensating rialú in place...',
            full_name: 'Iomlán ainm',
            min_chars: 'Íosmhéid 12 carachtar',
            enter_override_justification: 'Iontráil údar for overriding this gate evaluation',
            enter_evidence_id: 'Cuir isteach ID fianaise chun an nasc a dhéanamh:',
            date_format: 'LL/MM/BBBB',
            describe_pack_contents: 'Déan cur síos ar ábhar agus cuspóir an phacáiste',
            select_reviewers: 'Roghnaigh athbhreithneoir...',
        },
        aria_previous_page: 'An leathanach roimhe seo',
        aria_next_page: 'An chéad leathanach eile',
        aria_results_per_page: 'Torthaí in aghaidh an leathanaigh',
        per_page_25: '25 / leathanach',
        per_page_50: '50 / leathanach',
        per_page_100: '100 / leathanach',
        select_language: 'Roghnaigh teanga',
        today: 'Inniu',
        tomorrow: 'Amárach',
        dashboard_load_error: 'Theip ar an deais a luchtú. Bain triail eile as.',
        enum_grade: {
            red: 'Dearg',
            amber: 'Ómra',
            yellow: 'Buí',
            green: 'Glas',
        },
        enum_severity: {
            critical: 'Criticiúil',
            high: 'Ard',
            medium: 'Meánach',
            low: 'Íseal',
        },
        enum_priority: {
            critical: 'Criticiúil',
            high: 'Ard',
            medium: 'Meánach',
            low: 'Íseal',
        },
        enum_status: {
            pending: 'Ar feitheamh',
            in_progress: 'Ar siúl',
            completed: 'Críochnaithe',
            blocked: 'Coiscthe',
            escalated: 'Ardaithe',
            cancelled: 'Cealaithe',
            overdue: 'Thar téarma',
            not_started: 'Gan Tosú',
            new: 'Nua',
            open: 'Oscail',
            closed: 'Dúnta',
            acknowledged: 'Admhaítear',
            resolved: 'Réitíodh',
        },
        enum_department: {
            unassigned: 'Neamhshannta',
        },
        enum_form_category: {
            assessment: 'Measúnú',
            assessment_report: 'Tuarascáil Mheasúnaithe',
            dpia: 'DPIA',
            authority_submission: 'Aighneacht an Údaráis',
            cross_border_transfer: 'Aistriú Trasteorann',
            vendor: 'Díoltóir',
            breach: 'Sárú',
            consent: 'Toiliú',
            governance: 'Rialachas',
            register: 'Clár',
            monitoring: 'Monatóireacht',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Príobháideacht',
            incident: 'teagmhas',
            org: 'Eagraíocht',
            sector: 'Earnáil',
            classification: 'Aicmiú',
            discovery: 'Fionnachtain',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Iarratais ar Chearta',
            assessments: 'Measúnachtaí',
            incidents: 'Teagmhais',
            incident_ops: 'Oibríochtaí Teagmhais',
            monitoring: 'Monatóireacht',
            vendor_risk: 'Riosca Díoltóra',
            governance: 'Rialachas',
            deletion_orchestration: 'scriosadh',
            access_review: 'Athbhreithniú Rochtana',
            consent: 'Toiliú',
            consent_properties: 'Airíonna Toiliú',
            forms: 'Foirmeacha',
            localization: 'Logánú',
            program_governance: 'Rialachas Clár',
            privacy_scanner: 'Scanóir Príobháideachta',
            classification: 'Aicmiú',
            discovery: 'Fionnachtain',
            manual: 'Lámhleabhar',
            data_mapping: 'Mapáil Sonraí',
        },
        enum_risk_level: {
            critical: 'Criticiúil',
            high: 'Ard',
            medium: 'Meánach',
            low: 'Íseal',
        },
        enum_request_type: {
            all: 'All',
            access: 'Rochtain (DSAR)',
            deletion: 'scriosadh',
            correction: 'Ceartú',
            objection: 'Agóid',
            portability: 'Inaistritheacht',
            restriction: 'Srian',
            consent_withdrawal: 'Toiliú a Tharraingt siar',
            protective_measures: 'Bearta cosanta',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'Béarla',
            vi: 'Vítneaimis',
            all: 'An dá',
        },
        enum_event_type: {
            case_created: 'Cás Cruthaithe',
            status_changed: 'Athraíodh an Stádas',
            case_assigned: 'Cás Sannta',
            note_added: 'Nóta Curtha leis',
            attachment_added: 'Iatán Curtha leis',
            attachment_deleted: 'Scriosadh an ceangaltán',
            reference_added: 'Tagairt Curtha leis',
            checklist_updated: 'Seicliosta Nuashonraithe',
            identity_verified: 'Aitheantais Deimhnithe',
            closure_summary_added: 'Achoimre ar Dhúnta',
            approval_granted: 'Cead Deonaithe',
            template_applied: 'Teimpléad i bhfeidhm',
            export_generated: 'Easpórtáil Ginte',
            sla_warning: 'SLA Rabhadh',
            sla_overdue: 'SLA Thar téarma',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: 'Theip ar an easpórtáil',
        you: 'You',
        export_failed: 'Theip ar an easpórtáil. Déan iarracht arís.',
        filters: 'Scag',
        failed: 'Teipthe',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    common_statuses: {
        removed: 'Bainte',
        draft: 'Dréacht',
        active: 'Gníomhach',
        inactive: 'Neamhghníomhach',
        pending: 'Ar Feitheamh',
        in_progress: 'Ar Siúl',
        completed: 'Críochnaithe',
        archived: 'Cartlannaithe',
        published: 'Foilsithe',
        approved: 'Ceadaithe',
        rejected: 'Diúltaithe',
        cancelled: 'Cealaithe',
        blocked: 'Coiscthe',
        deferred: 'Curtha Siar',
        not_started: 'Gan Tosú',
        dismissed: 'Díbeartha',
        failed: 'Theip Air',
        in_review: 'In Athbhreithniú',
        deprecated: 'Dímholta',
        scheduled: 'Sceidealta',
        deploying: 'Ag imscaradh',
        deployed: 'Imscartha',
        paused: 'Ar sos',
        rolled_back: 'Rolled Ar ais',
        received: 'Faighte',
        processing: 'Á phróiseáil',
        under_assessment: 'Á Mheasúnú',
        implemented: 'Curtha i bhfeidhm',
        pending_approval: 'Ar feitheamh Ceadú',
        pending_deletion: 'Ar feitheamh Deletion',
        on_hold: 'On Coinnigh',
        deleted: 'Scriosta',
        expired: 'Éagtha',
        outdated: 'As dáta',
        drifted: 'Seachránta',
        decommissioned: 'Díchoimisiúnaithe',
        match: 'Meaitseáil',
        different: 'Éagsúil',
        missing: 'Ar iarraidh',
        confirmed: 'Deimhnithe',
        revoked: 'Cúlghairthe',
        escalated: 'Ardaithe',
        system: 'Córas',
        custom: 'Saincheaptha',
        warning: 'Rabhadh',
        blocking: 'Ag blocáil',
        analyst: 'Anailísí',
        viewer: 'Breathnóir',
        daily: 'Laethúil',
        weekly: 'Seachtainiúil',
        policies: 'Polasaithe',
        denied: 'Diúltaithe',
        verified: 'Fíoraithe',
        detected: 'Braitheadh',
        ignored: 'Neamhaird',
        open: 'Oscail',
        resolved: 'Réitíodh',
        suspended: 'Ar fionraí',
        retired: 'Ar scor',
        review: 'Léirmheas',
        generated: 'Gineadh',
        exported: 'Onnmhairithe',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    severity: {
        low: 'Íseal',
        medium: 'Meánach',
        high: 'Ard',
        critical: 'Criticiúil',
    },
    sensitivity: {
        standard: 'Caighdeánach',
        sensitive: 'Íogair',
        special_category: 'Catagóir Speisialta',
        badge_sensitive: 'Íogair',
    },
    change_types: {
        policy_update: 'Polasaí Nuashonraigh',
        new_binding: 'Nua Binding',
        remove_binding: 'Bain Binding',
        configuration_change: 'Cumraíocht Change',
        banner_update: 'Banner Nuashonraigh',
    },
    comparison_types: {
        full: 'Iomlán',
        banner_only: 'Meirge Amháin',
        purposes_only: 'Cuspóirí Only',
        legal_bases_only: 'Legal Bunúis Only',
        consent_settings: 'Toiliú Socruithe',
    },
    lifecycle_stages: {
        active: 'Gníomhach',
        archived: 'Cartlannaithe',
        pending_deletion: 'Ar feitheamh Deletion',
        deleted: 'Scriosta',
        on_hold: 'On Coinnigh',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    object_types: {
        personal_data: 'Personal Sonraí',
        consent_record: 'Toiliú Taifead',
        user_account: 'Cuntas Úsáideora',
        audit_log: 'Iniúchadh Logáil',
        document: 'Doiciméad',
        backup: 'Cúltaca',
    },
    risk_categories: {
        data_breach: 'Sonraí Sárú',
        unauthorized_access: 'Unauthorized Rochtain',
        data_loss: 'Sonraí Loss',
        regulatory_non_compliance: 'Neamhchomhlíonadh Rialála',
        third_party_risk: 'Third-Party Riosca',
        operational: 'Oibríochtúil',
        technical: 'Teicniúil',
        legal: 'Dlíthiúil',
    },
    mitigation_types: {
        technical: 'Teicniúil',
        organizational: 'Eagraíochtúil',
        contractual: 'Conarthach',
        procedural: 'Nósimeachta',
    },
    safeguard_types: {
        encryption_in_transit: 'Criptiú in Idirthuras',
        encryption_at_rest: 'Criptiú ag an gcuid eile',
        access_controls: 'Rialuithe Rochtana',
        contractual_clauses: 'Clásail Chonarthacha',
        data_minimization: 'Íoslaghdú Sonraí',
        pseudonymization: 'Ainmniú',
        audit_rights: 'Cearta Iniúchta',
        breach_notification: 'Fógra Sárú',
        data_localization: 'Logánú Sonraí',
        other: 'Eile',
    },
    scoring_methods: {
        weighted_average: 'Meán Ualaithe',
        simple_average: 'Simplí Meán',
        maximum: 'Uasmhéid',
        manual: 'Láimhe',
    },
    dpa_status: {
        not_required: 'Neamhriachtanach',
        required: 'Riachtanach',
        in_progress: 'Ar Siúl',
        completed: 'Críochnaithe',
        signed: 'Sínithe',
    },
    disposal_methods: {
        anonymize: 'Anaithnidigh',
        pseudonymize: 'Bréagainmnigh',
        aggregate: 'Comhiomlánú',
        delete: 'Scrios',
    },
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Painéal',
        no_data: 'Níl aon sonraí painéil ar fáil.',
        no_data_heading: 'Níl sonraí beo ar fáil go fóill',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        consent_coverage: 'Clúdach toilithe',
        ctia_filings: 'Comhdúcháin CTIA',
        next_dpia_review: 'Chéad athbhreithniú DPIA eile',
        next_review_none: 'Gan sceidealú',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: 'Comhdúcháin MPS eachtra',
        compliance_score: 'Scór Comhlíontachta',
        grade: 'Grád',
        no_score: 'Níl aon scór comhlíontachta ríofa fós.',
        rights_requests: 'Iarratais ar Chearta',
        open: 'Oscailte',
        sla_breached: 'SLA Sáraithe',
        sla_approaching: 'SLA ag Druidim',
        closed_month: 'Dúnta (Mí)',
        incidents: 'Teagmhais & Sáruithe',
        active: 'Gníomhach',
        breaches: 'Sáruithe',
        overdue_tasks: 'Tascanna Thar Téarma',
        breach_72h_notice: 'Tá feidhm ag spriocdháta fógra rialála 72 uair an chloig maidir le sáruithe gníomhacha',
        recommendations: 'Moltaí',
        take_action: 'Déan Gníomh',
        drift_events: 'Aimhréidhe & Ceaduithe',
        open_events: 'Imeachtaí Oscailte',
        pending_approvals: 'Ceaduithe ar Feitheamh',
        monitoring: 'Tascanna Monatóireachta',
        programs: 'Cláir',
        overdue: 'Thar Téarma',
        pending: 'Ar Feitheamh',
        ric_cases: 'Cásanna RIC',
        coverage: 'Clúdach Sonraí',
        systems: 'Córais',
        activities: 'Gníomhaíochtaí',
        vendors: 'Soláthraithe',
        dpias: 'MTCSanna',
        open_gaps: 'bearnaí oscailte',
        view_as_table: 'Feach mar thabhla',
        view_as_chart: 'Feach mar chairt',
        table_metric_header: 'Meadrach',
        table_value_header: 'Luach',
        announce_chart_view: 'Radharc cairte a thaispeaint',
        announce_table_view: 'Radharc tabhla a thaispeaint',
        section_core_metrics: 'Meadraigh Larnaicha',
        section_task_management: 'Bainistiocht Tascanna',
        section_compliance_insights: 'Leargais Chomhlionadh',
        configure_dashboard: 'Cumraigh an paineal',
        config_col_active: 'Gníomhach',
        config_col_widget: 'Giuirléid',
        collapse_section: 'Comhbhrugh',
        expand_section: 'Leathnaigh',
        reset_to_defaults: 'Athshocraigh go réamhshocruithe',
        preferences_saved: 'Sainroghanna deais sábháilte',
        preferences_reset: 'Deasc athshocraithe go réamhshocruithe róil',
        preferences_load_error: 'Theip ar lódáil sainroghanna deais',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Meánscór córais',
        factor_avg_vendor_score: 'Meánscór díoltóirí',
        factor_processing_coverage: 'Clúdach gníomhaíochta próiseála',
        factor_ropa_completeness: 'iomláine RoPA',
        factor_drift_penalty: 'Pionós imeachtaí drift',
        rec_improve_system_gov: 'Feabhas a chur ar dhoiciméadú rialachais an chórais',
        rec_improve_vendor_gov: 'Feabhas a chur ar dhoiciméid rialachais díoltóirí',
        rec_document_processing: 'Gníomhaíochtaí próiseála doiciméad do chórais neamhchumhdaithe',
        rec_generate_ropa: 'Gin taifid RoPA do ghníomhaíochtaí próiseála gníomhacha',
        rec_resolve_drift: 'Réitigh teagmhais srutha chriticiúla oscailte agus ard-dhéine',
        score_good: 'Maith',
        score_fair: 'Réasúnta',
        score_poor: 'Lag',
        score_critical: 'Criticiúil',
        drag_to_reorder: 'Tarraing chun athordú',
        widget_moved: 'Giuirléid bogtha',
        press_space_to_grab: 'Brúigh Spás chun breith air, eochracha saigheada chun bogadh, Spás chun scaoileadh',
        reorder_instructions: 'Úsáid tarraing agus scaoil nó aicearra méarchláir chun giuirléidí a athordú',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Forbhreathnú Tascanna',
            pending: 'Ar feitheamh',
            in_progress: 'Ar Siúl',
            overdue: 'thar téarma',
            done_7d: 'Déanta (7d)',
            by_priority: 'De réir Tosaíochta',
            view_all_tasks: 'Féach ar Gach Tasc',
            upcoming_tasks: 'Tascanna atá le Teacht (Na 7 Lá Eile)',
            no_upcoming: 'Níl aon tascanna le déanamh sna 7 lá atá romhainn.',
            view_calendar: 'Féach ar an bhFéilire',
            overdue_alerts: 'thar téarma',
            no_overdue: 'Uimh tascanna thar téarma. Tá gach rud ar an mbóthar.',
            assigned_to: 'Sannta do',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'Féach ar Gach Tá Amárach',
            notifications: 'Fógraí le Déanaí',
            unread: 'unread',
            no_notifications: 'Gan fógraí. Tá tú gafa go léir!',
            view_all_notifications: 'Féach ar Gach Fógra',
            load_error: 'Theip ar luchtú sonraí.',
            day_late: 'lá déanach',
            days_late_plural: 'laethanta déanacha',
            due_today: 'Dlite inniu',
            just_now: 'díreach anois',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 'd',
            cross_org_stats: 'Staitisticí Tras-Eagraíochta',
            by_organization: 'De réir Eagraíochta',
            overdue_across_orgs: 'tascanna thar téarma thar eagraíochtaí',
            overdue_count_label: '{count} overdue tasks',
            total: 'Iomlán',
            tasks: 'tasks',
            organization: 'Eagraíocht',
            current_org: 'Eagraíocht Reatha',
            all_orgs: 'Gach org',
            all_organizations: 'Gach Eagraíocht',
            filter_by_org: 'Scag de réir eagraíochta',
            cross_org: 'Tras-org',
            empty_calendar: 'Gan aon tascanna don tréimhse seo. Cruthaigh do chéad tasc nó seiceáil do scagairí.',
            empty_my_tasks: 'Níl aon tascanna sannta agat. Obair iontach!',
            empty_overdue: 'Uimh tascanna thar téarma. Tá gach rud ar an mbóthar.',
            empty_notifications: 'Gan fógraí. Tá tú gafa go léir!',
            empty_workflows: 'Níl aon sreabhadh oibre cumraithe. Brabhsáil teimpléid le tosú.',
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'Mo Chuid Tascanna',
            no_tasks: 'Nil aon tascanna sannta duit.',
            load_error: 'Theip ar lódáil na dtascanna.',
            overdue: 'thar téarma',
            remaining: 'fágtha',
            view_all: 'Féach ar Gach Tasc',
        },
        my_section_status_widget: {
            title: 'Stádas Mo Rannáin',
            overall_completion: 'Iomlán',
            sections_count: 'Rannáin',
            no_sections: 'Nil aon rannáin sannta do do roinn.',
            load_error: 'Theip ar lódáil stádas an rannáin.',
            department_tasks_title: 'Tascanna na roinne',
            no_department_tasks: 'Níl aon tascanna ag do roinn.',
            department_tasks_error: 'Theip ar lódáil thascanna na roinne.',
            no_department: 'Níl tú mapáilte chuig roinn.',
        },
        revision_requests_widget: {
            title: 'Iarratais Athbhreithnithe',
            no_revisions: 'Nil aon iarratais athbhreithnithe. Tá gach rannán ar an mbóthar ceart.',
            load_error: 'Theip ar lódáil na n-iarratas athbhreithnithe.',
        },
        consent_summary: {
            title: 'Toiliú',
            active: 'Gníomhach',
            withdrawn_30d: 'Tarraingthe siar (30d)',
            expiring_7d: 'Ag dul in éag (7d)',
        },
        transfer_assessments: {
            title: 'Measúnachtaí Aistrithe',
            open: 'Oscail',
            approved: 'Ceadaithe',
            gaps_flagged: 'Bearnaí le feiceáil',
        },
        breach_notifications: {
            title: 'Fógraí Sáraithe',
            deadline_72h: 'spriocdháta 72h',
            submitted: 'Arna chur isteach',
            pending: 'Ar feitheamh',
        },
        authority_requests: {
            title: 'Iarratais Údaráis',
            pending: 'Ar feitheamh',
            overdue: 'thar téarma',
            responded_30d: "D'fhreagair (30d)",
        },
        data_subjects_widget: {
            title: 'Ábhair Sonraí',
            total: 'Iomlán',
            high_risk: 'Riosca ard',
            active_30d: 'Gníomhach (30d)',
        },
        audit_activity_summary: {
            title: 'Gníomhaíocht Iniúchta',
            events_today: 'Imeachtaí inniu',
            events_7d: 'Imeachtaí (7d)',
            unique_actors_30d: 'Aisteoirí uathúla (30d)',
        },
        onboarding_progress: {
            title: 'Dul Chun Cinn ar bord',
            completion_pct: 'Críochnaigh',
            status_label: 'Stádas',
            current_step: 'Céim reatha',
            status: {
                in_progress: 'Ar siúl',
                completed: 'Críochnaithe',
                skipped: 'Léim',
                not_started: 'Gan tús',
            },
        },
        sector_overlay_reports_due: {
            title: 'Tuarascálacha Forleagan Earnála Dlite',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'of active consent workflows',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No frameworks installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No frameworks installed. Install a regulatory pack to see its coverage here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'No automated work is recorded against controls yet',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'No mandatory framework installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'No automated work recorded',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'Scag de réir creata',
            all_frameworks: 'Gach creat',
            filing_lifecycle: 'Saolré comhdaithe',
            filing_pipeline: 'Píblíne comhdaithe',
            draft: 'Dréacht',
            internal_review: 'Athbhreithniú Inmheánach',
            approved: 'Ceadaithe',
            submitted: 'Arna chur isteach',
            supplement_requested: 'Forlíonadh Iarrtha',
            resubmitted: 'Cuireadh isteach arís',
            accepted: 'Glactar leis',
            rejected: 'Diúltaithe',
            closed: 'Dúnta',
            deadline: 'Spriocdháta',
            days_remaining: 'laethanta fágtha',
            days_overdue: 'laethanta thar téarma',
            no_filings: 'Níl aon chomhdú gníomhach.',
            department_consolidation: 'Comhdhlúthú ranna',
            departments_completed: 'Ranna críochnaithe',
            departments_pending: 'Ranna ar feitheamh',
            departments_overdue: 'Ranna thar téarma',
            no_consolidation_cases: 'Níl aon chás ilrannach ar siúl.',
            vendor_risk: 'Riosca soláthraí agus tríú páirtí',
            pending_assessment: 'Measúnú ar feitheamh',
            overdue_questionnaires: 'Ceistneoirí thar téarma',
            dpa_review_queue: 'Scuaine athbhreithnithe DPA',
            risk_distribution: 'Dáileadh Riosca',
            total_vendors: 'Soláthraithe iomlán',
            by_risk_level: 'De réir leibhéal riosca',
            no_vendor_data: 'Níl aon sonraí riosca soláthraí ar fáil.',
            discovery_summary: 'Fionnachtain Sonraí',
            discovery_classification: 'Aimsiú agus aicmiú sonraí',
            active_scans: 'Scanadh gníomhach',
            labels_pending: 'Lipéid ar Feitheamh Léirmheas',
            exceptions_unresolved: 'Eisceachtaí gan réiteach',
            pending_review: 'Athbhreithniú ar feitheamh',
            exceptions: 'Eisceachtaí',
            approved_labels: 'Lipéid cheadaithe',
            total_classified: 'Iomlán aicmithe',
            no_discovery_data: 'Níl aon sonraí aimsithe ar fáil.',
            ai_governance: 'Rialachas córas IS',
            systems_inventory: 'Córais i bhFardal',
            systems_in_inventory: 'Córais san fhardal',
            classification_breakdown: 'Miondealú Aicmithe',
            dossier_completion: 'Comhlánú comhaid',
            by_classification: 'De réir aicmiú',
            labeling_status: 'Stádas lipéadaithe',
            not_started: 'Gan tosú',
            no_ai_data: 'Níl aon sonraí rialachais IS ar fáil.',
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Freagra ar sháruithe agus teagmhais',
            active_breaches: 'Sáruithe gníomhacha',
            notification_status: 'Stádas fógraíochta',
            remediation_progress: 'Dul chun cinn leighis',
            supplement_loops: 'Lúba forlíonta',
            no_breach_data: 'Níl aon chás sáraithe gníomhach.',
            audit_readiness: 'Ullmhacht iniúchóireachta agus measúnaithe',
            assessments: 'Measúnuithe',
            gap_findings: 'Torthaí bearna',
            evidence_completion: 'Comhlánú fianaise',
            export_ready: 'Réidh le heaspórtáil',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'Níl réidh',
            no_audit_data: 'Níl aon sonraí ullmhachta iniúchóireachta ar fáil.',
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Cibearshlandail 2018',
            framework_label_vn_ecommerce: 'VN Riomhthradail 2023',
            framework_label_vn_telecom: 'VN Teileachumarsaid 2023',
            framework_label_vn_ai: 'VN Dlí II 2025',
            framework_label_gdpr: 'GDPR',
            framework_label_unspecified: 'Neamhshonraithe',
            my_department: 'Mo roinn',
            my_tasks: 'Mo thascanna',
            section_status: 'Stádas an rannáin',
            revision_requests: 'Iarratais athbhreithnithe',
            completion: 'Comhlánú',
            reason: 'Fáth',
            requested_at: 'Iarrtha ar',
            no_tasks_assigned: 'Níl aon tascanna sannta duit.',
            no_sections: 'Níl aon rannáin sannta do do roinn.',
            no_revisions: 'Níl aon iarratais athbhreithnithe.',
            load_error: 'Theip ar lódáil na sonraí.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    connectors: {
        nav: {
            instances: 'Cásanna',
            credentials: 'Stóras Dintiúir',
            event_bus: 'Bus Imeachtaí',
            definitions: 'Sainmhínithe',
            brds: 'Gabháil BRD',
            security_reviews: 'Athbhreithnithe Slándála',
            poc_scorecards: 'Cártaí Scóir POC',
            automation_planning: 'Pleanáil Uathoibrithe',
            system_inventory: 'Fardal Córas',
            traceability_matrix: 'Maitrís Inrianaitheachta',
            gap_register: 'Clár Bearnaí',
        },
        instances: {
            title: 'Cásanna Nascóirí',
            page_title: 'Nascóirí',
            add: 'Cuir Cás Leis',
            test: 'Tástáil Ceangal',
            sync_now: 'Sioncronaigh Anois',
            new_instance: 'Nua Nascóir Instance',
            no_instances: 'Níl aon connector instances aimsithe',
            instance_count: '{count} cás(anna)',
            confirm_delete: 'Scrios connector instance "{name}"?',
            all_connector_types: 'Gach Cineál Nascóra',
            select_connector: 'Roghnaigh a connector...',
            fetch_error: 'Theip ar fetch connector instances. Bain triail eile as.',
            fetch_definitions_error: 'Theip ar fetch connector definitions. Bain triail eile as.',
            create_error: 'Theip ar create connector instance. Bain triail eile as.',
            test_error: 'Theip ar test connector. Bain triail eile as.',
            status_error: 'Theip ar stádas an nascóra a nuashonrú. Bain triail eile as.',
            delete_error: 'Theip ar delete connector instance. Bain triail eile as.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Nuashonraíodh an stádas',
            reactivate: 'Athghníomhachtaigh',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: 'Stóras Dintiúir',
            page_title: 'Dintiúir nascóra',
            add: 'Cuir Dintiúr Leis',
            rotate: 'Rothlaigh',
            expired: 'Imithe in Éag',
            expiring: 'Ag Dul in Éag Go Luath',
            orphaned: 'Dílleachta',
            new_credential: 'Nua Dintiúr',
            no_credentials: 'Níl aon credentials aimsithe',
            no_expiry: 'Gan Éag',
            linked_connectors: 'Linked Nascóirí',
            expires_at: 'Éagann ag',
            value_label: 'Luach * (scríobh amháin)',
            confirm_delete_title: 'Deimhnigh Scriosadh',
            confirm_delete_message: 'Scrios an dintiúr seo? Caillfidh nascóirí nasctha rochtain.',
            rotate_title: 'Rothlaigh dintiúr',
            new_credential_value: 'Luach nua dintiúir',
            fetch_error: 'Theip ar load credentials. Bain triail eile as.',
            health_error: 'Theip ar load health. Bain triail eile as.',
            create_error: 'Theip ar create credential. Bain triail eile as.',
            rotate_error: 'Theip ar rotate credential. Bain triail eile as.',
            delete_error: 'Theip ar delete credential. Bain triail eile as.',
        },
        event_bus: {
            title: 'Bus Imeachtaí',
            page_title: 'Bus imeachtaí',
            events: 'Imeachtaí',
            subscriptions: 'Síntiúis',
            dlq: 'Ciú Litreacha Marbha',
            publish: 'Foilsigh Imeacht',
            retry: 'Bain Triail Eile As',
            dismiss: 'Díbir',
            search_events: 'Cuardaigh imeacht...',
            filter_event_type: 'Scag by imeacht cineál...',
            event_type: 'Cineál Imeachta',
            source_module: 'Foinse Modúl',
            timestamp: 'Stampa Ama',
            delivery_status: 'Delivery Stádas',
            correlation_id: 'ID Comhghaolaithe',
            tracking_id: 'ID Rianaithe',
            delivery_status_hint: 'Déantar stádas seachadta a nuashonrú go huathoibríoch de réir mar a phróiseáiltear imeachtaí.',
            no_events: 'Níl aon imeacht aimsithe',
            total_with_count: '{count} iomlán',
            prev: 'Roimhe',
            new_subscription: '+ Síntiús Nua',
            subscriber_type: 'Subscriber Cineál',
            subscriber_id: 'ID Síntiúsóra',
            event_type_pattern: 'Imeacht Cineál Pattern',
            event_type_pattern_placeholder: 'm.sh. incident.*',
            filter_expression: 'Scag Expression',
            filter_expression_placeholder: 'Roghnach filter',
            subscriber_type_hint: 'Roghnaigh conas a sheachadfar imeachtaí: seolann webhook iarratais HTTP, seolann ríomhphost fógraí, seolann scuaine chuig scuaine teachtaireachtaí.',
            subscriber_id_hint: 'Cuir isteach ceann scríbe na n-imeachtaí. Le haghaidh webhooks, úsáid URL. Le haghaidh ríomhphost, úsáid seoladh. Le haghaidh scuainí, úsáid ainm na scuaine.',
            event_type_pattern_hint: 'Meaitseáil imeachtaí de réir cineáil le carachtair ionaid. Úsáid * chun aon mhír a mheaitseáil (m.sh. incident.* meaitseálann incident.created, incident.resolved).',
            filter_expression_hint: 'Roghnach. Scag imeachtaí breise de réir ábhair (m.sh. severity=high).',
            subscriber_id_placeholder_webhook: 'm.sh. https://d-aip.com/webhook',
            subscriber_id_placeholder_email: 'm.sh. foláirimh@do-chuideachta.com',
            subscriber_id_placeholder_queue: 'm.sh. scuaine-imeachtaí',
            event_pattern: 'Imeacht Pattern',
            filter: 'Scag',
            enabled: 'Cumasaithe',
            delivery_stats: 'Staitisticí Seachadta',
            no_subscriptions: 'Níl aon subscriptions aimsithe',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} seachadta / {failed} theip',
            confirm_delete_subscription: 'Scrios this subscription?',
            unresolved: 'Neamhréitithe',
            resolved: 'Réitithe',
            event_id: 'Imeacht ID',
            subscription_id: 'Síntiús ID',
            error_code: 'Earráid Cód',
            error_message: 'Earráid Message',
            attempts: 'Iarachtaí',
            last_failed: 'Last Theip',
            no_dlq_entries: 'Níl aon dead letter entries aimsithe',
            dlq_count: '{count} iontráil/iontrálacha iomlán',
            fetch_events_error: 'Theip ar fetch imeacht. Bain triail eile as.',
            fetch_subscriptions_error: 'Theip ar fetch subscriptions. Bain triail eile as.',
            fetch_dlq_error: 'Theip ar fetch dlq entries. Bain triail eile as.',
            create_subscription_error: 'Theip ar create subscription. Bain triail eile as.',
            toggle_subscription_error: 'Theip ar toggle subscription. Bain triail eile as.',
            delete_subscription_error: 'Theip ar delete subscription. Bain triail eile as.',
            retry_error: 'Bain triail as arís theip',
            dismiss_error: 'Dismiss theip',
            event_published: 'Imeacht foilsithe',
            publish_event_error: 'Níorbh fhéidir an imeacht a fhoilsiú. Bain triail eile as.',
            priority_label: 'Tosaíocht',
            priority_low: 'Íseal',
            priority_normal: 'Gnáth',
            priority_high: 'Ard',
            payload_helper_text: 'Cuir isteach sonraí an imeachta mar JSON. Sampla: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Síntiús cruthaithe',
            subscription_toggled: 'Síntiús scoránaithe',
            subscription_deleted: 'Síntiús scriosta',
            dlq_retried: 'Atriaileadh teachtaireacht DLQ',
            dlq_dismissed: 'Díbheadh teachtaireacht DLQ',
        },
        definitions: {
            title: 'Sainmhínithe Nascóirí',
            page_title: 'Sainmhínithe nascóra',
            add: 'Cuir Sainmhíniú leis',
            new_definition: 'Sainmhíniú Ceanglóir Nua',
            edit_definition: 'Cuir Sainmhíniú Ceanglóra in Eagar',
            no_definitions: 'Níor aimsíodh aon sainmhíniú ar chónascaire',
            definition_count: '{count} sainmhíniú(anna)',
            all_categories: 'Gach Catagóir',
            select_category: 'Roghnaigh catagóir...',
            capabilities: 'Cumais',
            capabilities_hint: 'Cumais (scartha le camóg)',
            capabilities_placeholder: 'e.g. léamh, scríobh, sioncrónú',
            created: 'Sainmhíniú nascóirí cruthaithe',
            updated: 'Nuashonraíodh sainmhíniú an chónascaire',
            fetch_error: 'Theip ar na sainmhínithe cónascaire a fháil. Bain triail eile as.',
            create_error: 'Theip ar shainiú an chónascaire a chruthú',
            update_error: 'Theip ar shainmhíniú an nascóra a nuashonrú',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'Gabháil BRD',
            page_title: 'Gabháil BRD',
            create_brd: 'Cruthaigh BRD',
            brd_title: 'Teideal BRD',
            description: 'Cur síos',
            requirements: 'Riachtanais',
            status: 'Stádas',
            stakeholders: 'Páirtithe leasmhara',
            connector_label: 'Nascóir',
            select_connector: 'Roghnaigh nascóir...',
            approval: 'Faomhadh',
            no_brds_found: 'Níor aimsíodh aon BRD',
            decision_records: 'Taifid chinntí',
            create_decision: 'Cruthaigh cinneadh',
            alternatives_considered: 'Roghanna eile a breithníodh',
            alternative_name: 'Ainm an rogha eile',
            reason_rejected: 'Fáth diúltaithe',
            add_alternative: 'Cuir rogha eile leis',
            remove_alternative: 'Bain rogha eile {name}',
            alternatives: 'Roghanna eile',
            rationale: 'Réasúnaíocht',
            selected_connector: 'Nascóir roghnaithe',
            comma_separated_names: 'Ainmneacha scartha le camóga',
            brd_created: 'BRD cruthaithe',
            decision_record_created: 'Taifead cinnidh cruthaithe',
            no_decision_records_found: 'Níor aimsíodh aon taifead cinnidh',
            aria_brds_table: 'BRDanna',
            aria_decision_records_table: 'Taifid chinntí',
            aria_approve: 'Ceadaigh {name}',
            aria_archive: 'Cartlannaigh {name}',
            brd_title_help: 'Ainm gairid, inléite ag an duine don BRD seo — m.sh. "Comhtháthú ticéadú Jira", "rolladh amach Okta SSO".',
            brd_title_placeholder: 'e.g. Comhtháthú ticéadú Jira',
            description_help: 'An fhadhb gnó a réitíonn an cónascaire seo. Cén toradh a bhfuil tú ag súil leis nuair a bheidh sé beo?',
            description_placeholder: 'e.g. Cruthú ticéad teagmhais a uathoibriú ó fholáirimh mhonatóireachta ionas go mbeidh ar ghlao leathanach laistigh de 60s.',
            connector_help: 'Roghnach. Roghnaigh cónascaire a bhfuil an BRD seo dó - fág folamh mura bhfuil an cónascaire roghnaithe fós.',
            requirements_help: 'Liosta de na cumais nach mór a bheith scartha le camóga — m.sh. "Logáil isteach OAuth2, seachadadh cuaille gréasáin, onnmhairiú iniúchta".',
            requirements_placeholder: 'Logáil isteach OAuth2, seachadadh cuas gréasáin, onnmhairiú iniúchta',
            stakeholders_help: 'Ainmneacha nó ríomhphoist camógdheighilte daoine nach mór dóibh síniú.',
            selected_connector_help: 'Roghnaigh an cónascaire a mbaineann an cinneadh seo leis.',
            rationale_help: 'Cén fáth ar roghnaíodh an cónascaire seo. Clúdaigh na fachtóirí cinntitheacha ionas go dtuigeann léitheoir sa todhchaí an glaoch.',
            rationale_placeholder: 'e.g. Roghnaigh Jira toisc go bhfuil stair ár dticéad aige cheana féin agus go bhfuil an fhoireann oilte air.',
            alternatives_considered_help: 'Rinne cónaisc eile measúnú agus cén fáth nár roghnaíodh iad. Cuirtear inchosanta leis le haghaidh iniúchta.',
            alternative_name_placeholder: 'e.g. Seirbhís Anois',
            reason_rejected_placeholder: 'e.g. Costas 3 × níos airde, gan aon bhuntáiste dár gcás úsáide',
        },
        security_reviews: {
            title: 'Athbhreithnithe Slándála',
            page_title: 'Athbhreithnithe slándála',
            create_review: 'Cruthaigh athbhreithniú',
            connector_name: 'Ainm an nascóra',
            connector_label: 'Nascóir',
            select_connector: 'Roghnaigh nascóir...',
            reviewer: 'Athbhreithneoir',
            select_reviewer: 'Roghnaigh athbhreithneoir...',
            findings: 'Torthaí (ceann amháin in aghaidh na líne)',
            risk_level: 'Leibhéal riosca',
            risk_level_placeholder: 'm.sh. ard, meánach, íseal',
            notes: 'Nótaí',
            review_status: 'Stádas athbhreithnithe',
            checklist: 'Seicliosta',
            privacy_impact: 'Tionchar príobháideachais',
            approval_gate: 'Geata faomhaithe',
            no_reviews_found: 'Níor aimsíodh aon athbhreithniú',
            review_created: 'Athbhreithniú slándála cruthaithe',
            create_control_mapping: 'Cruthaigh mapáil rialaithe',
            control_mapping_created: 'Mapáil rialaithe cruthaithe',
            connector_id_label: 'ID nascóra',
            control_id_label: 'ID rialaithe',
            control_name_label: 'Ainm rialaithe',
            aria_approve: 'Ceadaigh {name}',
            aria_reject: 'Diúltaigh {name}',
            scan_imported: 'Scanadh iompórtáilte go rathúil',
            import_scan: 'Iompórtáil scanadh',
            scan_format: 'Formáid scanadh',
            scan_data: 'Sonraí scanadh',
            import_results: 'Torthaí iompórtála',
            recommendations: 'Moltaí',
            auto_populated_from: 'Uathdhaonra ó {tool}',
            manual_entry: 'Iontráil láimhe',
            automation_type: 'Cineál Uathoibrithe',
            select_automation_type: 'Roghnaigh cineál uathoibrithe...',
            automation_full: 'Iomlán - déanann cónascaire an rialú seo a uathoibriú go hiomlán',
            automation_partial: 'Páirteach — cuireann cónascaire fianaise; gníomhaíonn daoine fós',
            automation_monitoring: 'Monatóireacht amháin - feiceann cónascaire, ní fhorfheidhmíonn sé',
            control_id_help: 'Úsáid an cód ó do scéim um chomhlíonadh (m.sh. ISO-27001-A.12.1, SOC2-CC6.1, nó d’aitheantas rialaithe inmheánaigh).',
            control_name_help: 'Ainm gairid inléite ag an duine (m.sh. "srian le rochtain ar an líonra").',
            description_label: 'Cur síos',
            load_sarif_example: 'Íosluchtaigh sampo do SAI',
            load_json_example: 'Luchtaigh JSON sampla saincheaptha',
            scan_format_help: 'Is é SARIF an caighdeán OASIS a astaíonn formhór na scanóirí (Snyk, CodeQL, Semgrep). Úsáid saincheaptha JSON le haghaidh uirlisí nach n-astaíonn SARIF.',
            scan_data_help: 'Greamaigh an t-aschur scanadh iomlán, uaslódáil comhad .sarif/.json, nó cliceáil ar an gcnaipe Luchtaigh Sampla chun an cruth a fheiceáil.',
            upload_file_label: 'Uaslódáil comhad .sarif nó .json',
            invalid_scan_json: 'Níorbh fhéidir sonraí an scanta a pharsáil mar JSON. Seiceáil an bhfuil earráidí comhréire in aice leis an gcarachtar aibhsithe.',
            save_as_review: 'Sábháil mar Athbhreithniú Slándála',
            discard_preview: 'Scrios réamhamharc',
            preview_hint: 'Seo réamhamharc - níl aon rud sábháilte go fóill. Cliceáil "Sábháil mar Athbhreithniú Slándála" chun athbhreithniú ar feitheamh a chruthú atá réamhlíonta leis na luachanna seo.',
            reviewer_required_for_save: 'Roghnaigh léirmheastóir chun an scanadh seo a shábháil mar athbhreithniú slándála.',
        },
        poc_scorecards: {
            title: 'Cártaí Scóir POC',
            page_title: 'Scóirchártaí POC',
            create_scorecard: 'Cruthaigh cárta scóir',
            connector_label: 'Nascóir',
            select_connector: 'Roghnaigh nascóir...',
            vendor_name: 'Ainm an tsoláthraí',
            recommendation: 'Moladh',
            recommendation_placeholder: 'm.sh. Lean ar aghaidh, Ná lean ar aghaidh, Teastaíonn tuilleadh meastóireachta',
            criteria: 'Critéir mheastóireachta',
            criterion_name: 'Ainm an chritéir',
            score: 'Scór',
            max_score: 'Uasscór',
            notes: 'Nótaí',
            add_criterion: 'Cuir critéar leis',
            remove_criterion: 'Bain critéar {name}',
            total_score: 'Scór iomlán',
            api_quality: 'Cáilíocht API',
            reliability: 'Iontaofacht',
            support_quality: 'Cáilíocht tacaíochta',
            cost_score: 'Scór costais',
            overall_score: 'Scór iomlán',
            evidence: 'Fianaise',
            no_scorecards_found: 'Níor aimsíodh aon chárta scóir',
            scorecard_created: 'Scóirchárta POC cruthaithe',
            connector_help: 'Roghnaigh an cónascaire ar a bhfuil an mheastóireacht POC seo.',
            vendor_name_help: 'An díoltóir nó an táirge atá á mheas — e.g. "Okta", "Datadog", "Cáithnínísneachta".',
            vendor_name_placeholder: 'e.g. Okta, Datadog, gcáithnínísneachta',
            recommendation_help: 'Do ghlao deiridh tar éis na meastóireachta. Coinnigh sé gearr agus cinntitheach.',
            criteria_help: 'Scóráil an díoltóir ar gach critéar meastóireachta. Cuir sraith amháin leis in aghaidh an chritéir (API cáilíocht, iontaofacht, tacaíocht, costas, etc.).',
            criterion_name_help: 'An méid atá á scóráil agat - m.sh. "API Cáilíocht", "Iontaofacht", "Tacaíocht", "Costas".',
            criterion_name_placeholder: 'e.g. API Cáilíocht',
            score_help: 'Scór bronnta don chritéar seo. Ní mór gan an Scór Uasta a shárú.',
            max_score_help: 'Scór is airde ar fáil don chritéar seo. Úsáid an scála céanna trasna sraitheanna ionas go mbeidh na hiomláin inchomparáide.',
            notes_help: 'Roghnach. Cuir fianaise, caveats, nó naisc leis - rud ar bith a mhíníonn an scór.',
        },
        common_labels: {
            aria_filter_instances: 'Scag cásanna nascóra',
            aria_filter_definitions: 'Scag sainmhínithe nascóra',
            aria_filter_credentials: 'Scag dintiúir',
            aria_filter_events: 'Scag imeachtaí',
            aria_filter_dlq: 'Scag ciú litreacha marbha',
            aria_instance_summary: 'Achoimre ar chás nascóra',
            aria_active_connectors: 'Nascóirí gníomhacha',
            aria_error_connectors: 'Nascóirí le hearráid',
            aria_credential_health: 'Achoimre ar shláinte dintiúir',
            aria_items_per_page: 'Míreanna in aghaidh an leathanaigh',
            show_password: 'Taispeáin pasfhocal',
            hide_password: 'Folaigh pasfhocal',
            payload_json_label: 'Pálasta (JSON)',
            payload_invalid_json: 'Ní mór don pálasta a bheith JSON bailí',
            placeholder_event_type: 'm.sh. incident.created',
            placeholder_source_module: 'm.sh. monitoring',
            placeholder_version: 'm.sh. 1.0',
        },
        statuses: {
            configuring: 'Cumraíocht',
            testing: 'Tástáil',
            active: 'Gníomhach',
            paused: 'Sos',
            error: 'Earráid',
            disabled: 'faoi mhíchumas',
            draft: 'Dréacht',
            approved: 'Ceadaithe',
            archived: 'Cartlannaithe',
            pending: 'Ar feitheamh',
            rejected: 'Diúltaithe',
        },
        system_types: {
            application: 'Feidhmchlár',
            database: 'Bunachar sonraí',
            service: 'Service',
            infrastructure: 'Bonneagar',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'Ar an láthair',
            cloud: 'Néal',
            hybrid: 'Hibrideach',
            managed: 'Bainistithe',
        },
        system_statuses: {
            active: 'Gníomhach',
            decommissioned: 'Díchoimisiúnaithe',
            planned: 'Beartaithe',
            under_review: 'Á athbhreithniú',
        },
        automation_statuses: {
            planning: 'Pleanáil',
            in_progress: 'Ar siúl',
            completed: 'Críochnaithe',
            on_hold: 'Ar fionraí',
        },
        gap_types: {
            functional: 'Feidhmiúil',
            technical: 'Teicniúil',
            security: 'Slándáil',
            compliance: 'Comhlíonadh',
            process: 'Próiseas',
        },
        severity_labels: {
            critical: 'Criticiúil',
            high: 'Ard',
            medium: 'Meánach',
            low: 'Íseal',
        },
        gap_statuses: {
            open: 'Oscailte',
            in_progress: 'Ar siúl',
            mitigated: 'Maolaithe',
            closed: 'Dúnta',
            accepted: 'Glactha',
        },
        verification_statuses: {
            not_started: 'Gan tosú',
            in_progress: 'Ar siúl',
            verified: 'Fíoraithe',
            failed: 'Theip',
        },
        subscriber_types: {
            webhook: 'Crúca gréasáin',
            internal: 'Inmheánach',
            email: 'Ríomhphost',
            queue: 'Ciú',
        },
        credential_types: {
            password: 'Pasfhocal',
            api_key: 'Eochair API',
            oauth2_client: 'Cliant OAuth2',
            certificate: 'Deimhniú',
            token: 'Comhartha',
            ssh_key: 'Eochair SSH',
            imap_credentials: 'Dintiúir IMAP',
        },
        categories: {
            hr: 'AD',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'Scamall',
            security: 'Slándáil',
            itsm: 'ITSM',
            custom: 'Saincheaptha',
        },
        delivery_statuses: {
            pending: 'Ar feitheamh',
            delivered: 'Seachadta',
            partial: 'Páirteach',
            failed: 'Theip',
        },
        automation_planning: {
            title: 'Pleanáil Uathoibrithe',
            page_title: 'Pleanáil Uathoibrithe',
            program_name: 'Ainm an chláir',
            program_status: 'Stádas',
            description: 'Cur síos',
            linked_artifacts: 'Déantáin nasctha',
            create_program: 'Cruthaigh clár',
            edit_program: 'Cuir clár in eagar',
            no_programs_found: 'Níor aimsíodh aon chlár',
            program_created: 'Clár cruthaithe',
            program_updated: 'Clár nuashonraithe',
            target_date: 'Sprioc-dháta',
            owner: 'Úinéir',
            select_status: 'Roghnaigh stádas...',
            aria_programs_table: 'Cláir uathoibrithe',
            aria_edit: 'Cuir {name} in eagar',
            export_programs: 'Easpórtáil cláir',
            title_label: 'Teideal an Chláir',
            title_help: 'Ainm gairid, inléite ag an duine don chlár uathoibrithe — e.g. "Rochtain R3 teagmhas-go-Jira", "PDPL uathoibriú iarratas rochtana".',
            title_placeholder: 'e.g. R3 cur i bhfeidhm eachtra-go-Jira',
            description_help: 'Cad a chlúdaíonn an clár seo agus an toradh a mbeifeá ag súil leis nuair a bheidh sé beo. Roghnach ach moltar.',
            description_placeholder: 'e.g. Foláireamh monatóireachta sreinge isteach Jira ionas go mbeidh ar-ghlao leathanach laistigh de 60s.',
            owner_label: 'Úinéir an Chláir',
            owner_help: 'An duine atá freagrach as an gclár seo. Gheobhaidh siad fógraí faoi dhul chun cinn agus geataí.',
            select_owner: 'Roghnaigh úinéir...',
            frameworks_in_scope: 'Creataí sa Scóip',
            frameworks_in_scope_help: 'Creataí comhlíonta a thacaíonn an clár seo. Cuidíonn an rian iniúchta a thaispeáint cé na rialacháin a rialaíonn an t-uathoibriú.',
            select_frameworks: 'Roghnaigh creataí...',
            status_draft: 'Dréacht',
            status_active: 'Gníomhach',
            status_completed: 'Críochnaithe',
            status_archived: 'Cartlannaithe',
            change_status: 'Athrú Stádas',
            status_updated: 'Nuashonraíodh an stádas',
            aria_change_status: 'Athraigh stádas le haghaidh {name}',
        },
        system_inventory: {
            title: 'Fardal Córas',
            page_title: 'Fardal Córas',
            system_name: 'Ainm an chórais',
            system_type: 'Cineál córais',
            hosting: 'Óstáil',
            status: 'Stádas',
            description: 'Cur síos',
            create_system: 'Cruthaigh córas',
            edit_system: 'Cuir córas in eagar',
            no_systems_found: 'Níor aimsíodh aon chóras',
            system_created: 'Córas cruthaithe',
            system_updated: 'Córas nuashonraithe',
            select_type: 'Roghnaigh cineál...',
            select_hosting: 'Roghnaigh óstáil...',
            select_status: 'Roghnaigh stádas...',
            owner: 'Úinéir',
            department: 'Roinn',
            data_categories: 'Catagóirí Sonraí',
            data_categories_placeholder: 'Camóg-scartha, e.g. PII, Financial, Health',
            jurisdiction: 'Dlínse',
            jurisdiction_placeholder: 'e.g. EU, US, Global',
            interfaces: 'Comhéadain',
            interfaces_placeholder: 'Camóg-scartha, e.g. REST API, SFTP, JDBC',
            owner_placeholder: 'Roghnaigh úinéir cuntasach...',
            department_placeholder: 'Roghnaigh an t-aonad org atá freagrach...',
            system_name_hint: 'Bain úsáid as an gcóras aitheanta gnó nó ainm an fheidhmchláir.',
            system_type_hint: 'Déan an córas a rangú ionas gur féidir le tuairisciú sócmhainní comhchosúla a ghrúpáil.',
            hosting_hint: 'Sainaithin an áit a bhfuil an córas ag rith le haghaidh cónaitheachta, slándála, agus athbhreithniú díoltóirí.',
            status_hint: 'Socraigh an staid saolré a úsáideann athbhreithnithe, onnmhairí agus fianaise iniúchta.',
            owner_hint: 'Roghnaigh an t-úsáideoir gníomhach atá freagrach as rialachas córais agus tascanna leantacha.',
            department_hint: 'Úsáid an t-aonad org atá freagrach as an gcóras seo a oibriú nó a rialú.',
            data_categories_hint: 'Liostaigh na haicmí sonraí arna bpróiseáil ag an gcóras seo, scartha le camóga.',
            jurisdiction_hint: 'Taifead a dhéanamh ar an bpríomhdhlínse dlíthiúil nó óstála le haghaidh mapála comhlíonta.',
            interfaces_hint: 'Liosta nascóirí nó cosáin chomhtháthaithe a úsáidtear chun sonraí a mhalartú.',
            description_hint: 'Déan achoimre ar cad a dhéanann an córas agus cén fáth a bhfuil tábhacht aige leis an léarscáil sonraí.',
            owner_department_required: 'Úinéir agus Roinn ag teastáil.',
            aria_systems_table: 'Fardal córas',
            aria_edit: 'Cuir {name} in eagar',
            export_systems: 'Easpórtáil córais',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Maitrís Inrianaitheachta',
            page_title: 'Maitrís Inrianaitheachta',
            brd_selector: 'Roghnaigh BRD',
            requirement: 'Riachtanas',
            linked_artifact: 'Déantán nasctha',
            verification_status: 'Stádas fíoraithe',
            verified_by: 'Fíoraithe ag',
            create_entry: 'Cruthaigh iontráil',
            edit_entry: 'Cuir iontráil in eagar',
            no_entries_found: 'Níor aimsíodh aon iontráil',
            entry_created: 'Iontráil cruthaithe',
            entry_updated: 'Iontráil nuashonraithe',
            select_brd: 'Roghnaigh BRD...',
            select_status: 'Roghnaigh stádas...',
            aria_matrix_table: 'Maitrís inrianaitheachta',
            aria_edit: 'Cuir iontráil in eagar: {name}',
            export_matrix: 'Easpórtáil maitrís',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: 'ID riachtanais',
        },
        gap_register: {
            title: 'Clár Bearnaí',
            page_title: 'Clár Bearnaí',
            gap_title: 'Teideal bearna',
            gap_type: 'Cineál bearna',
            severity: 'Déine',
            status: 'Stádas',
            description: 'Cur síos',
            remediation_plan: 'Plean leighis',
            assigned_to: 'Sannta do',
            due_date: 'Spriocdháta',
            create_gap: 'Cruthaigh bearna',
            edit_gap: 'Cuir bearna in eagar',
            no_gaps_found: 'Níor aimsíodh aon bhearna',
            gap_created: 'Bearna cruthaithe',
            gap_updated: 'Bearna nuashonraithe',
            select_type: 'Roghnaigh cineál...',
            select_severity: 'Roghnaigh déine...',
            select_status: 'Roghnaigh stádas...',
            aria_gaps_table: 'Clár bearnaí',
            aria_edit: 'Cuir {name} in eagar',
            export_gaps: 'Easpórtáil bearnaí',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'System inventory aligns with Data Law enterprise governance. System types map to regulatory categories, hosting classification determines jurisdiction-specific controls. Use framework linkage to connect systems to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. Framework scope determines which requirements the program addresses.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. Verification status tracks whether implementation satisfies the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. Risk level captures assessed exposure, findings document deviations from control requirements.',
            gap_register_framework_info: 'Gap Register captures compliance gaps per regulatory framework. Use framework filter to view gaps for a specific framework. Severity levels align with framework risk classification.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria. Criteria structure supports weighted scoring with evidence notes. Vendor linkage connects to vendor record.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity.',
        },
        system_inventory_extra: {
            framework_linkage: 'Nascáil creatlaí',
            framework_linkage_placeholder: 'm.sh. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Raon feidhme creatlaí',
            framework_scope_placeholder: 'm.sh. GDPR, PCI-DSS',
            procedure_linkage: 'Nascáil nós imeachta',
            procedure_linkage_placeholder: 'm.sh. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Foinse rialála',
            regulatory_source_placeholder: 'm.sh. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Léarscáiliú rialuithe',
            control_mappings_section: 'Léarscáileanna Rialaithe',
            no_control_mappings: 'Níl aon mhapáil rialaithe fós. Cliceáil "Cruthaigh Mapáil Rialaithe" chun ceann a chur leis.',
            automation_type_col: 'Uathoibriú',
        },
        gap_register_extra: {
            framework_filter: 'Creatlach',
            framework_id: 'Aitheantas creatlaí',
            framework_id_placeholder: 'm.sh. GDPR, ISO 27001',
            all_frameworks: 'Gach creatlach',
        },
        poc_scorecards_extra: {
            criteria_count: 'Critéir',
        },
        export_labels: {
            export_brds: 'Easpórtáil BRDanna',
            export_decision_records: 'Easpórtáil taifid chinntí',
            export_security_reviews: 'Easpórtáil athbhreithnithe slándála',
            export_poc_scorecards: 'Easpórtáil scóirchártaí POC',
            export_instances: 'Cásanna Easpórtála',
            export_definitions: 'Sainmhínithe Easpórtála',
            export_credentials: 'Dintiúir Easpórtála',
            export_events: 'Imeachtaí Easpórtála',
            exporting: 'Ag easpórtáil...',
            export_success: 'Easpórtáil críochnaithe',
            export_error: 'Theip ar easpórtáil',
        },
    },
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Sliocht oibleagáidí',
            extracting: 'Ag baint…',
            results_heading: 'Oibleagáidí asbhainte ({count})',
            tokens_label: '≈ {count} comharthaí',
            no_obligations: 'Níor aimsíodh patrúin oibleagáide ar bith i dtéacs an chonartha seo.',
            col_class: 'Aicme',
            col_summary: 'Achoimre (in eagar)',
            col_excerpt: 'Sliocht clásail',
            col_due_date: 'Dáta dlite (in eagar)',
            col_priority: 'Tosaíocht',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Tréimhse fógra',
            class_audit: 'Ceart iniúchta',
            class_insurance: 'Árachas',
            class_sla: 'Response SLA',
            class_breach: 'Fógra um shárú',
            class_renewal: 'Fuinneog athnuachana',
            citations_label: 'Comhlua',
            accept_hint: 'Glacfar cruthófar tasc amháin in aghaidh na hoibleagáide (uasteorainn 8) agus an tosaíocht + an dáta dlite léirithe.',
            accept_label: 'Glac le hoibleagáidí',
            reject_label: 'Diúltú don tuarascáil',
            resolve_summary: 'Taifeach: {status} · {tasks} tasc(eanna) cruthaithe',
            error_generic: 'Níorbh fhéidir oibleagáidí a bhaint. Bain triail eile as nó seiceáil téacs an chonartha.',
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        risk_acceptance_reasoning: {
            draft_label: 'Dréacht-réasúnaíocht',
            drafting: 'Réasúnaíocht á dhréachtú…',
            results_heading: 'AI-réasúnaithe dréachtaithe ({risk_level})',
            tokens_label: '≈ {count} comharthaí',
            narrative_label: 'Insint fírinnithe',
            controls_heading: 'Rialuithe cúitimh',
            no_controls: 'Ní mholtar aon rialuithe cúitimh.',
            citations_label: 'Comhlua',
            accept_label: 'Glac le réasúnaíocht',
            reject_label: 'Diúltaigh dréacht',
            accept_hint: 'Glac taifid na réasúnaíochta ar an dréacht leantach le haghaidh lineage IAT. Ní dhéanann na rialuithe insint + an taifead glactha a nuashonrú go huathoibríoch — tá gá le haisíoc an oibreora.',
            resolve_summary: 'Taifeach: {status}',
            error_generic: 'Níorbh fhéidir réasúnaíocht a dhréachtú. Bain triail eile as nó seiceáil an stádas glactha riosca.',
        },
        redline_assistant: {
            run_label: 'Líne dearg leis an leabharlann',
            running: 'Redline á rith…',
            results_heading: 'Moltaí línte dearga ({count})',
            tokens_label: '≈ {count} comharthaí',
            no_suggestions: 'Níor aimsíodh aon bhearnaí sa chlásal caighdeánach.',
            jurisdiction_label: 'Dlínse',
            col_kind: 'cineál',
            col_severity: 'Déine',
            col_title: 'Moladh',
            col_excerpt: 'Sliocht comhoiriúnaithe',
            col_apply: 'Cuir iarratas isteach',
            kind_missing: 'Caighdeán in easnamh',
            kind_weaker: 'Níos laige ná an caighdeán',
            kind_jurisdiction: 'Forleagan dlínse',
            proposed_label: 'Téacs atá beartaithe',
            citations_label: 'Comhlua',
            accept_label: 'Glac roghnaithe',
            reject_label: 'Diúltú don tuarascáil',
            partial_label: 'Cuir rogha i bhfeidhm',
            accept_hint: 'Cuir taifid roghnóireachta i bhfeidhm ar na moltaí roghnaithe ar an dréacht do shliocht IAT. Ní chuirtear aon eagarthóireacht uathoibríoch i bhfeidhm ar théacs an chonartha — is é an t-oibreoir a thiomáineann an dearglíne.',
            resolve_summary: 'Taifeach: {status} · {count} moladh(í) curtha i bhfeidhm',
            error_generic: 'Níorbh fhéidir líne dhearg a tháirgeadh. Bain triail eile as nó seiceáil stádas an chonartha.',
            clause_library_link: 'Bainistigh leabharlann clásail',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        clause_library: {
            page_title: 'Leabharlann Chlásail',
            add_label: 'Cuir clásal leis',
            edit_label: 'Cuir in eagar',
            delete_label: 'Scrios',
            slug_label: 'Drúchtín',
            title_label: 'Teideal',
            body_label: 'Corp',
            jurisdiction_label: 'Dlínse',
            tags_label: 'Clibeanna (comma scartha)',
            must_contain_label: 'Marcóirí nach mór a bheith iontu (comma scartha)',
            severity_label: 'Déine agus iad ar iarraidh',
            no_clauses: 'No clauses yet. Cuir ceann amháin leis chun an cúntóir dearglíne a shíolrú.',
            save_label: 'Sábháil',
            cancel_label: 'Cealaigh',
            load_error: 'Níorbh fhéidir leabharlann clásail a lódáil.',
            save_error: 'Níorbh fhéidir clásal a shábháil.',
            delete_error: 'Níorbh fhéidir clásal a scriosadh.',
            delete_confirm: 'Scrios clásal "{slug}"? Ní féidir é seo a chealú.',
        },
        subprocessor_impact: {
            assess_label: 'Déan tionchar a mheas',
            assessing: 'Tionchar á measúnú…',
            results_heading: 'Tionchar fophróiseálaí ({severity})',
            tokens_label: '≈ {count} comharthaí',
            risk_delta_label: 'Riosca deilt',
            notification_heading: 'Fógra do chustaiméirí',
            subject_label: 'Ábhar',
            deadline_label: 'Spriocdháta fógra',
            body_label: 'Comhlacht fógartha',
            required_fields_label: 'Réimsí an fhógra (Art.28(2))',
            mitigation_heading: 'Céimeanna maolaithe',
            citations_label: 'Comhlua',
            accept_label: 'Glac le measúnú',
            reject_label: 'Measúnú a dhiúltú',
            accept_hint: 'Glacfar cruthófar tasc fógartha custaiméara a mbeidh an SLA spriocdháta mar an dáta dlite.',
            resolve_summary: 'Taifeach: {status}{task}',
            error_generic: 'Níorbh fhéidir an t-athrú fophróiseálaí seo a mheas. Bain triail eile as nó seiceáil an taifead athraithe.',
        },
        onboarding_evidence: {
            collect_label: 'Rith AI bailiú fianaise',
            collecting: 'Fianaise á bailiú…',
            results_heading: 'Bailiúchán fianaise ({tier})',
            tokens_label: '≈ {count} comharthaí',
            findings_heading: 'Fianaise atá ann cheana féin',
            missing_heading: 'Déantáin ar iarraidh',
            no_findings: 'Níor aimsíodh aon fhianaise reatha.',
            no_missing: 'Níl aon déantáin riachtanacha in easnamh don tsraith riosca seo.',
            next_step_label: 'An chéad chéim eile',
            class_dpa: 'Comhaontú Próiseála Sonraí',
            class_soc2: 'Tuarascáil SOC 2',
            class_iso27001: 'Deimhniú ISO 27001',
            class_insurance: 'Polasaí árachais cibear',
            class_incident_history: 'Eachtra roimh ré',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: 'Glac leis an tuarascáil',
            reject_label: 'Diúltú don tuarascáil',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: 'Níorbh fhéidir AI bailiúchán fianaise a rith. Bain triail eile as nó seiceáil stádas an cháis ar bordála.',
        },
        no_permission_title: 'Ceadanna Neamhleor',
        no_permission_message: 'Níl cead agat rochtain a fháil ar bhainistíocht riosca an díoltóra.',
        unknown_vendor: 'Díoltóir Anaithnid',
        nav: {
            dashboard: 'Painéal riosca',
            scoring: 'Scóráil riosca',
            questionnaires: 'Ceistneoirí',
            contracts: 'Conarthaí',
            gates: 'Geataí ceadaithe',
            reviews: 'Athbhreithnithe soláthróra',
            signals: 'Comharthaí riosca',
            reassessments: 'Athchomhairlithe',
            portfolio: 'Punann riosca',
            acceptances: 'Glacadh le riosca',
            review_routing: 'Ródú athbhreithnithe',
            onboarding_cases: 'Cásanna iontrála',
            sub_processor_register: 'Clár fo-phróiseálaithe',
        },
        dashboard: {
            title: 'Painéal riosca soláthróra',
            page_title: 'Painéal Riosca Díoltóra',
            loading: 'Ag lódáil painéal sonraí...',
            risk_tier_header: 'Sraith Riosca',
            count_header: 'Áireamh',
            percentage_header: 'Céatadán',
            sections_nav: 'Rannóga riosca díoltóirí',
            urgent_expiry: 'Práinneach: rachaidh sé in éag laistigh de 14 lá',
            no_previous_tier: 'Uimh sraith roimhe seo',
            view_as_chart: 'Féach mar chairt',
            view_as_table: 'Féach mar tábla',
            showing_chart: 'Amharc cairte á thaispeáint',
            showing_table: 'Amharc tábla sonraí',
            load_error: 'Theip ar load soláthraí riosca painéal. Bain triail eile as.',
            risk_distribution: 'Riosca Distribution Forbhreathnú',
            recent_tier_changes: 'Le déanaí Sraith Changes',
            no_tier_changes: 'Gan athruithe sraithe le déanaí.',
            review_kpis: 'Athbhreithniú KPIs',
            total_vendors_reviewed: 'Soláthraithe Athbhreithnithe Iomlána',
            reviews_by_outcome: 'Athbhreithnithe by Outcome',
            contract_coverage: 'Conradh Clúdach',
            total_vendors: 'Soláthraithe Iomlána',
            with_active_dpa: 'Le DPA Gníomhach',
            without_dpa: 'Gan DPA',
            dpa_coverage: 'DPA Clúdach',
            active_contracts_by_type: 'Conarthaí Gníomhacha de réir Cineáil',
            expiring_contracts: 'Expiring Conarthaí (Ar aghaidh 90 laethanta)',
            no_expiring_contracts: 'No contracts expiring within the next 90 laethanta.',
            vendor: 'Soláthraí',
            previous_tier: 'Roimhe seo Sraith',
            current_tier: 'Reatha Sraith',
            changed_at: 'Athraithe',
            expires_at: 'Éagann ag',
            days_remaining: 'laethanta fágtha',
            days: 'laethanta',
            vendors_label: 'soláthraithe',
            no_data: 'No sonraí',
            critical: 'Criticiúil',
            high: 'Ard',
            medium: 'Meánach',
            low: 'Íseal',
            expiring_acceptances_heading: 'Ceaduithe riosca ag dul in éag',
            expiring_30d_label: 'Ag dul in éag i gceann 30 lá',
            expiring_60d_label: 'Ag dul in éag i gceann 60 lá',
            expiring_90d_label: 'Ag dul in éag i gceann 90 lá',
        },
        scoring: {
            title: 'Scóráil riosca',
            page_title: 'Scóráil Riosca Díoltóra',
            filter_scores: 'Scag scóir riosca',
            filter_by_tier: 'Scag de réir sraithe riosca',
            scores_pagination: 'Leathanach scóir riosca',
            config_dimensions: 'Cumraíocht toisí scórála',
            scores_loaded: '{count} scóir riosca lódáilte',
            weight_must_equal_100: 'Caithfidh an meáchan iomlán a bheith cothrom le 100 (reatha: {current})',
            out_of_10: 'as 10, rátáilte {tier}',
            dimension_name: 'Toise {index} ainm',
            dimension_weight: '{name} meáchan (céatadán)',
            must_equal_100: 'Ní mór comhionann 100',
            config_title: 'Scoring Cumraíocht',
            scores_title: 'Scóir Riosca Soláthraithe',
            new_score_title: 'Scór a Soláthraí',
            new: 'Nua Scór',
            vendor_id: 'ID Soláthraí',
            risk_tier: 'Riosca Sraith',
            score: 'Scór',
            scored_at: 'Scórtha',
            no_scores: 'Níl aon riosca scór found. aimsithe',
            all_tiers: 'Gach Sraith',
            load_config_error: 'Theip ar load riosca scoring cumraíocht. Bain triail eile as.',
            update_config: 'Nuashonraigh cumraíocht scórála',
            update_config_error: 'Theip ar update riosca scoring cumraíocht. Bain triail eile as.',
            load_scores_error: 'Theip ar load soláthraí riosca scór. Bain triail eile as.',
            calculate_error: 'Theip ar calculate soláthraí riosca scór. Bain triail eile as.',
            weight: 'weight',
            score_range: 'Scór (1-5)',
            method: 'Modh',
        },
        questionnaires: {
            title: 'Ceistneoirí',
            page_title: 'Ceistneoirí Soláthraithe',
            filter_questionnaires: 'Scagairí ceistneoirí',
            questionnaire_views: 'Amhairc ceistneoirí',
            templates_list: 'Liosta na dteimpléad ceistneora',
            instances_list: 'Liosta na n-eiseamláirí ceistneora',
            filter_by_status: 'Scag de réir stádas an cheistneora',
            templates_per_page: 'Teimpléid in aghaidh an leathanaigh',
            instances_per_page: 'Eiseamláirí in aghaidh an leathanaigh',
            select_template: 'Roghnaigh teimpléad ceistneora',
            template_category: 'Catagóir teimpléid',
            templates_tab: 'Teimpléid',
            instances_tab: 'Eiseamláirí',
            new: 'Ceistneoir Nua',
            new_template: 'Teimpléad Nua',
            new_instance: 'Eiseamláir Nua',
            vendor_id: 'Aitheantas an tSoláthraí',
            template_id: 'Aitheantas an Teimpléid',
            assigned_to: 'Sannta do',
            due_date: 'Spriocdháta',
            responses: 'Freagraí',
            risk_score: 'Scór Riosca',
            no_questionnaires: 'Níor aimsíodh aon cheistneoir.',
            load_templates_error: 'Theip ar lódáil na dteimpléad ceistneora.',
            load_instances_error: 'Theip ar lódáil na gceistneoirí.',
            create_template_error: 'Theip ar chruthú an teimpléid ceistneora.',
            initiate_error: 'Theip ar chur tús leis an gceistneoir.',
            submit_error: 'Theip ar an gceistneoir a chur isteach.',
            review_error: 'Theip ar athbhreithniú an cheistneora.',
            resubmit: 'Cuir isteach arís',
            approve_error: 'Theip ar cheadú an cheistneora.',
            reject_error: 'Theip ar dhiúltú don cheistneoir.',
            resubmit_error: 'Theip ar athchur an cheistneora.',
            templates_loaded: '{count} teimpléad ceistneora lódáilte',
            instances_loaded: '{count} eiseamláir cheistneora lódáilte',
            fill_responses_cta: 'Líon na freagraí',
            fill_responses_title: 'Líon freagraí an cheistneora',
            template_missing: 'Níor aimsíodh an teimpléad.',
            responses_saved: 'Sábháladh na freagraí.',
            load_template_error: 'Theip ar lódáil an teimpléid.',
            version_history: 'Stair na leaganacha',
            download_previous_version: 'Íoslódáil an leagan roimhe seo',
            trigger: 'Truicear',
            template_label: 'Teimpléad',
            reviewer: 'Athbhreithneoir',
            reviewer_required: 'Ní mór athbhreithneoir a shannadh do cheistneoirí {trigger} (deighilt na ndualgas).',
            select_option: 'Roghnaigh rogha',
            multi_choice_hint: 'Roghnaigh gach ceann is infheidhme',
            category_initial_onboarding: 'Iontráil tosaigh',
            category_annual_review: 'Athbhreithniú bliantúil',
            category_incident_triggered: 'Spreagtha ag teagmhas',
            category_contract_renewal: 'Athnuachan conartha',
            template_editor: {
                page_title: 'Cuir teimpléad ceistneora in eagar',
                preview_page_title: 'Réamhamharc ar an teimpléad ceistneora',
                edit_structure: 'Cuir an struchtúr in eagar',
                preview: 'Réamhamharc',
                actions: 'Gníomhartha',
                back_to_list: 'Ar ais chuig na teimpléid',
                load_error: 'Theip ar lódáil an teimpléid.',
                save_error: 'Theip ar shábháil struchtúr an teimpléid.',
                saved: 'Sábháladh struchtúr an teimpléid.',
                sections_heading: 'Rannáin',
                questions_heading: 'Ceisteanna',
                add_section: 'Cuir rannán leis',
                edit_section: 'Cuir rannán in eagar',
                delete_section: 'Scrios rannán',
                move_section_up: 'Bog rannán suas',
                move_section_down: 'Bog rannán síos',
                confirm_delete_section: 'Scrios an rannán "{title}"? Bainfear gach ceist atá ann.',
                section_title: 'Teideal an rannáin',
                section_description: 'Cur síos ar an rannán',
                section_id: 'Aitheantas an rannáin',
                section_id_hint: 'Ginte go huathoibríoch mura socraíonn tú ceann go sainráite é.',
                no_sections: 'Níl aon rannán fós ann. Cuir rannán leis chun tosú.',
                no_questions: 'Níl aon cheist sa rannán seo go fóill.',
                add_question: 'Cuir ceist leis',
                edit_question: 'Cuir ceist in eagar',
                delete_question: 'Scrios ceist',
                move_question_up: 'Bog ceist suas',
                move_question_down: 'Bog ceist síos',
                confirm_delete_question: 'Scrios an cheist "{text}"?',
                question_text: 'Téacs na ceiste',
                question_type: 'Cineál ceiste',
                question_id: 'Aitheantas na ceiste',
                evidence_required: 'Fianaise riachtanach',
                risk_weight: 'Meáchan riosca',
                scoring_map: 'Léarscáil scórála',
                scoring_map_json_hint: 'Réad JSON a léarscáilíonn luachanna freagra ar scóir uimhriúla.',
                scoring_map_invalid_json: 'Caithfidh an léarscáil scórála a bheith ina JSON bailí.',
                options_heading: 'Roghanna freagra',
                add_option: 'Cuir rogha leis',
                remove_option: 'Bain rogha',
                option_label: 'Lipéad',
                option_value: 'Luach',
                option_risk_weight: 'Meáchan riosca',
                min_options_error: 'Caithfidh dhá rogha ar a laghad a bheith ag ceisteanna roghnaithe.',
                option_label_required: 'Tá lipéad de dhíth ar gach rogha.',
                option_value_required: 'Tá luach de dhíth ar gach rogha.',
                option_value_unique: 'Caithfidh luachanna na roghanna a bheith uathúil.',
                branching_rules_heading: 'Rialacha craobhaithe',
                branching_rules_hint: 'Taispeáin nó folaigh an cheist seo bunaithe ar an bhfreagra ar cheist roimhe seo.',
                add_branching_rule: 'Cuir riail chraobhaithe leis',
                remove_branching_rule: 'Bain riail chraobhaithe',
                branching_condition_question: 'Más rud é go bhfuil',
                branching_condition_value: 'leis an luach',
                branching_action: 'ansin',
                branching_action_show: 'taispeáin an cheist seo',
                branching_action_hide: 'folaigh an cheist seo',
                branching_select_condition_question: 'Roghnaigh ceist roimhe seo',
                branching_select_condition_value: 'Roghnaigh luach',
                save: 'Sábháil an struchtúr',
                saving: 'Á shábháil...',
                cancel: 'Cealaigh',
                close: 'Dún',
                question_text_required: 'Tá téacs na ceiste riachtanach.',
                section_title_required: 'Tá teideal an rannáin riachtanach.',
                type_single_choice: 'Rogha aonair',
                type_multi_choice: 'Ilrogha',
                type_free_text: 'Téacs saor',
                type_numeric: 'Uimhriúil',
                type_date: 'Dáta',
                type_file_upload: 'Uaslódáil comhaid',
                type_yes_no: 'Tá / Níl',
                preview_heading: 'Réamhamharc ar an teimpléad',
                preview_subtitle: 'Réamhamharc inléite amháin ar an gcaoi a bhfeictear an ceistneoir do sholáthraí.',
                preview_no_content: 'Níl aon rannán ná ceist sa teimpléad seo go fóill.',
                preview_branching_notice: 'Déantar rialacha craobhaithe a mheas le linn an líonta; taispeánann an réamhamharc gach ceist.',
            },
            review_modal: {
                title: 'Athbhreithnigh freagraí an cheistneora',
                open_cta: 'Athbhreithnigh',
                loading: 'Ceistneoir á lódáil...',
                template_missing: 'Níor aimsíodh an teimpléad.',
                load_error: 'Theip ar lódáil an cheistneora le hathbhreithniú.',
                annotation_heading: 'Anótálacha',
                annotation_placeholder: 'Cuir anótáil athbhreithneora leis don cheist seo...',
                annotation_status_label: 'Stádas anótála',
                annotation_status_acceptable: 'Inghlactha',
                annotation_status_concern: 'Ábhar imní',
                annotation_status_requires_action: 'Gníomh de dhíth',
                review_comments_label: 'Tráchtanna foriomlána athbhreithnithe',
                review_comments_placeholder: "Tabhair achoimre ar thorthaí d'athbhreithnithe...",
                justification_label: 'Údar an chinnidh',
                justification_placeholder: 'Mínigh an réasúnaíocht le do chinneadh...',
                reject_requires_comments: 'Tá tráchtanna athbhreithnithe ag teastáil chun ceistneoir a dhiúltú.',
                decision_heading: 'Cinneadh',
                decision: {
                    approved: 'Ceadaigh',
                    rejected: 'Diúltaigh',
                    additional_info_requested: 'Iarr eolas breise',
                },
                submit: 'Cuir isteach an t-athbhreithniú',
                submitting: 'Athbhreithniú á chur isteach...',
                cancel: 'Cealaigh',
                submit_success: 'Cuireadh an t-athbhreithniú isteach.',
                submit_error: 'Theip ar an athbhreithniú a chur isteach.',
                separation_of_duties_error: 'Ní féidir leat ceistneoir a athbhreithniú a sannadh duitse le líonadh isteach. Éilíonn deighilt na ndualgas athbhreithneoir difriúil.',
                decision_required: 'Roghnaigh cinneadh athbhreithnithe le do thoil.',
                announce_opened: 'Modal athbhreithnithe oscailte.',
                announce_submitted: 'Cuireadh an t-athbhreithniú isteach.',
            },
            instance_detail: {
                submitted_at: 'Curtha isteach',
                reviewed_at: 'Athbhreithnithe',
                reviewer: 'Athbhreithneoir',
                review_decision: 'Cinneadh',
                review_comments: 'Tráchtanna athbhreithnithe',
                decision_approved: 'Ceadaithe',
                decision_rejected: 'Diúltaithe',
                decision_additional_info_requested: 'Eolas breise iarrtha',
                decision_pending: 'Athbhreithniú ar feitheamh',
                expand_comments: 'Taispeáin na tráchtanna iomlána',
                collapse_comments: 'Folaigh na tráchtanna iomlána',
                no_reviewer: 'Níor sannadh aon athbhreithneoir',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            instance_statuses: {
                draft: 'Dréacht',
                in_progress: 'Ar Siúl',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Ceadaithe',
                rejected: 'Diúltaithe',
                expired: 'Éagtha',
            },
            export: {
                portfolio_label: 'Easpórtáil punann',
                instance_label: 'Easpórtáil',
                portfolio_success: 'Tá easpórtáil na punainne réidh.',
                instance_success: 'Tá easpórtáil an cheistneora réidh.',
                portfolio_error: 'Theip ar easpórtáil punann na gceistneoirí.',
                instance_error: 'Theip ar easpórtáil an cheistneora.',
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Tá ceisteanna áirithe folaithe ag rialacha craobhaithe.',
                cycle_warning: 'Braitheadh timthriall i rialacha craobhaithe. Taispeántar na ceisteanna a mbaineann sé leo de réir réamhshocraithe.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Conarthaí',
            page_title: 'Conarthaí Díoltóra',
            new: 'Nua Conradh',
            vendor_id: 'ID Soláthraí',
            filter_contracts: 'Conarthaí a scagadh',
            filter_by_document_type: 'Scag de réir cineáil doiciméid',
            filter_by_status: 'Scag de réir stádas conartha',
            per_page: 'Conarthaí in aghaidh an leathanaigh',
            expired_notice: 'Tá an conradh seo imithe in éag',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Cineál Doiciméid',
            expiry_date: 'Expiry Dáta',
            no_contracts: 'Níl aon contracts found. aimsithe',
            all_document_types: 'Gach Cineál Doiciméid',
            load_error: 'Theip ar load contracts. Bain triail eile as.',
            create_error: 'Theip ar create contract. Bain triail eile as.',
            delete_error: 'Theip ar delete contract. Bain triail eile as.',
            update_error: 'Theip ar update stádas. Bain triail eile as.',
            confirm_delete: 'Scrios contract "{title}"?',
            renewal_date: 'Renewal Dáta',
            effective_date: 'Effective Dáta',
            parties: 'Páirtithe',
            summary: 'Achoimre',
            n_contracts: '{count} conradh',
            n_contract: '{count} conradh',
            contracts_loaded: '{count} conradh lódáilte',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Aguisín slándála',
            doc_type_scc_annex: 'Iarscríbhinn SCC',
            doc_type_sub_processor_list: 'Liosta fo-phróiseálaithe',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Eile',
            status_draft: 'Dréacht',
            status_under_negotiation: 'Faoi chaibidlíocht',
            status_pending_signature: 'Ag fanacht le síniú',
            status_active: 'Gníomhach',
            status_expired: 'Éagtha',
            status_terminated: 'Foirceanta',
            status_superseded: 'Ionadaithe',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: 'Geataí ceadaithe',
            page_title: 'Meastóireacht Geata',
            new: 'Nua Geata Evaluation',
            vendor_id: 'ID Soláthraí',
            gate_name: 'Geata Ainm',
            outcome: 'Toradh',
            evaluator: 'Measúnóir',
            no_gates: 'Níl aon gate evaluations found. aimsithe',
            load_error: 'Theip ar load gate evaluations. Bain triail eile as.',
            evaluate_error: 'Theip ar evaluate soláthraí gate. Bain triail eile as.',
            detail_error: 'Theip ar fetch evaluation detail. Bain triail eile as.',
            override_error: 'Theip ar override gate evaluation. Bain triail eile as.',
            criteria_results: 'Critéir Results',
            evaluate_form: 'Déan geata an díoltóra a mheas',
            per_page: 'Meastóireachtaí in aghaidh an leathanaigh',
            passed: 'Caite',
            failed: 'Theip',
            override: 'Sáraigh',
            override_justification: 'Sáraigh Údar',
            override_placeholder: 'Iontráil údar for overriding this gate evaluation',
            approval_id: 'ID Ceadaithe',
            overall_passed: 'Ar éirigh leis',
            overall_failed: 'Theip air',
            overall_overridden: 'Sáraithe',
            evaluations_loaded: '{count} meastáchán geata lódáilte',
            create_approval_cta: 'Cruthaigh ceadú',
        },
        reviews: {
            title: 'Athbhreithnithe soláthróra',
            page_title: 'Léirmheasanna Díoltóra',
            new: 'Nua Athbhreithniú',
            vendor_id: 'ID Soláthraí',
            filter_by_outcome: 'Scag de réir toradh athbhreithnithe',
            per_page: 'Léirmheasanna in aghaidh an leathanaigh',
            kpi_summary: 'Athbhreithnigh KPI achoimre',
            review_outcome_label: 'Athbhreithniú a dhéanamh ar an toradh',
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: 'Cuir toradh an athbhreithnithe in eagar',
            reviews_loaded: '{count} léirmheas díoltóirí lódáilte',
            reviewer: 'Athbhreithneoir',
            review_type: 'Athbhreithniú Cineál',
            outcome: 'Toradh',
            findings: 'Torthaí',
            no_reviews: 'Níl aon soláthraí athbhreithniú found. aimsithe',
            load_kpis_error: 'Theip ar fetch soláthraí athbhreithniú kpis. Bain triail eile as.',
            load_error: 'Theip ar fetch soláthraí athbhreithniú. Bain triail eile as.',
            update_error: 'Theip ar nuashonrú athbhreithniú an díoltóra',
            create_error: 'Theip ar create soláthraí athbhreithniú. Bain triail eile as.',
            total_reviewed: 'Soláthraithe Athbhreithnithe Iomlána',
            total_reviews: 'Athbhreithnithe Iomlána',
            satisfactory: 'Sásúil',
            concerns_identified: 'Ábhair Imní Aitheanta',
            remediation_required: 'Leigheas Riachtanach',
            escalated: 'Ardaithe',
            program_id: 'Clár ID',
            period_id: 'Tréimhse ID',
            task_id: 'Tasc ID',
            recommendations: 'Moltaí',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Comharthaí Riosca',
            page_title: 'Comharthaí Riosca',
            new_signal: 'Comhartha riosca nua',
            signal_type: 'Cineál comhartha',
            severity: 'Déine',
            description: 'Cur síos',
            source: 'Foinse',
            vendor_id: 'Aitheantas an díoltóra',
            certification_expiry: 'Éag deimhniúcháin',
            breach_notification: 'Fógra sáraithe',
            financial_instability: 'Éagobhsaíocht airgeadais',
            compliance_change: 'Athrú comhlíontachta',
            custom: 'Saincheaptha',
            active: 'Gníomhach',
            acknowledged: 'Aitheanta',
            resolved: 'Réitithe',
            no_signals: 'Níor aimsíodh aon chomhartha riosca',
            source_external_feed: 'Fotha seachtrach',
            source_manual: 'Láimhe',
            source_integration: 'Comhtháthú',
            source_regulatory: 'Rialála',
            source_media: 'Meáin',
            trigger_reassessment_prompt: 'Tá an comhartha seo ar ardleibhéal tromchúise. Tosaigh athmheasúnú an díoltóra anois?',
            reassessment_triggered: 'Athmheasúnú tosaithe.',
            reassessment_trigger_error: 'Theip ar thús an athmheasúnaithe.',
        },
        reassessments: {
            title: 'Tascanna Athmheasúnaithe',
            page_title: 'Tascanna Athmheasúnaithe',
            trigger: 'Tosaigh athmheasúnú',
            trigger_success: 'Athmheasúnú tosaithe go rathúil',
            reason: 'Fáth',
            deadline: 'Spriocdháta',
            assigned_to: 'Sannta do',
            vendor_id: 'Aitheantas an díoltóra',
            outcome: 'Toradh',
            new_risk_level: 'Leibhéal riosca nua',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Críochnaigh athmheasúnú',
            complete_success: 'Athmheasúnú críochnaithe go rathúil',
            pending: 'Ar feitheamh',
            in_progress: 'Ar siúl',
            completed: 'Críochnaithe',
            cancelled: 'Cealaithe',
            no_tasks: 'Níor aimsíodh aon tasc athmheasúnaithe',
            risk_trend: 'Treocht riosca',
            notes: 'Nótaí',
        },
        portfolio: {
            title: 'Punann Riosca',
            page_title: 'Punann Riosca',
            dashboard: 'Painéal punainn',
            vendor_id: 'Aitheantas an díoltóra',
            total_vendors: 'Soláthraithe iomlána',
            average_score: 'Meánscór riosca',
            high_risk_count: 'Soláthraithe ardriosca',
            trend_direction: 'Treo treochta',
            improved: 'Feabhsaithe',
            stable: 'Cobhsaí',
            degraded: 'Díghrádaithe',
            vendor_trend: 'Treocht soláthróra',
            outstanding_questionnaires: 'Ceistneoirí gan chríochnú',
            overdue_assessments: 'Measúnuithe thar téarma',
            no_outstanding: 'Níl aon cheistneoir gan chríochnú',
            no_overdue: 'Níl aon mheasúnú thar téarma',
            due_date: 'Dáta dlite',
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: 'Clár Glactha le Riosca',
            page_title: 'Clár Glactha Riosca',
            new_acceptance: 'Glacadh le riosca nua',
            vendor_id: 'Aitheantas an díoltóra',
            risk_level: 'Leibhéal riosca',
            conditions: 'Coinníollacha',
            justification: 'Údar',
            expiry_date: 'Dáta éaga',
            create_success: 'Glacadh le riosca cruthaithe go rathúil',
            review_condition: 'Athbhreithnigh coinníoll',
            condition_met: 'Coinníoll comhlíonta',
            condition_not_met: 'Coinníoll neamhchomhlíonta',
            review_success: 'Coinníoll athbhreithnithe go rathúil',
            export_register: 'Easpórtáil clár',
            active: 'Gníomhach',
            expired: 'Éagtha',
            revoked: 'Cúlghairthe',
            no_acceptances: 'Níor aimsíodh aon ghlacadh le riosca',
            met_count: 'Tháinig {met}/{total} le chéile',
        },
        review_routing: {
            title: 'Ródú Athbhreithnithe',
            page_title: 'Routing Athbhreithnithe',
            route_to_reviewers: 'Seol chuig athbhreithneoirí',
            route_success: 'Seolta chuig athbhreithneoirí go rathúil',
            vendor_id: 'Aitheantas an díoltóra',
            reviewer_id: 'Aitheantas athbhreithneora',
            reviewer_role: 'Ról athbhreithneora',
            questionnaire_id: 'Aitheantas ceistneora',
            review_status: 'Stádas athbhreithnithe',
            reviewers_label: 'Athbhreithneoirí (user_id, ról in aghaidh an líne)',
            pending: 'Ar feitheamh',
            in_review: 'Á athbhreithniú',
            completed: 'Críochnaithe',
            no_routing: 'Níor aimsíodh aon ródú athbhreithnithe',
            primary_reviewer: 'Príomhathbhreithneoir',
            secondary_reviewer: 'Athbhreithneoir tánaisteach',
            approver: 'Ceadaitheoir',
        },
        onboarding: {
            title: 'Cásanna Iontrála Soláthraí',
            page_title: 'Cásanna Iontrála Soláthraí',
            new_case: 'Cás iontrála nua',
            case_detail: 'Cás Iontrála',
            open_mps_dossier_filing: 'Oscail comhdú MPS',
            dossier_filing_description: 'Cruthaigh comhdú MPS nua réamhlíonta ón gcás onboarding seo. Nascfar an comhdú ar ais chuig an gcás.',
            dossier_filing_dpia_id: 'ID DPIA',
            dossier_filing_dpia_id_placeholder: 'm.s. dpia_01TEST000000000000000001',
            dossier_filing_title: 'Teideal an chomhdaithe',
            dossier_filing_form_type: 'Cineál foirme',
            dossier_filing_submit: 'Cruthaigh comhdú',
            dossier_filing_validation_error: 'Tá ID DPIA agus teideal riachtanach.',
            dossier_filing_created: 'Comhdú MPS cruthaithe',
            dossier_filing_error: 'Theip ar chomhdú MPS a chruthú.',
            linked_questionnaire_due_date: 'Dáta dlite',
            linked_questionnaire_assignee: 'Sannta do',
            linked_questionnaire_submitted_at: 'Curtha isteach',
            score_last_scored: 'Scór deireanach',
            score_dimensions: 'Toisí',
            linked_contract_document_type: 'Cineál cáipéise',
            linked_contract_effective_date: 'Dáta éifeachtach',
            linked_contract_expiry_date: 'Dáta éaga',
            linked_sub_processor_countries: 'Tíortha próiseála',
            linked_sub_processor_last_assessment: 'Measúnú deireanach',
            linked_sub_processor_next_assessment: 'Céad mheasúnú eile',
            case_id: 'ID an cháis',
            vendor: 'Soláthraí',
            case_type: 'Cineál cáis',
            service_description: 'Cur síos ar an tseirbhís',
            data_categories: 'Catagóirí sonraí',
            systems_accessed: 'Córais rochtana',
            jurisdiction: 'Dlínse',
            hosting_model: 'Samhail óstála',
            controller_role: 'Ról rialaitheora/próiseálaithe',
            status_pipeline: 'Píblíne stádais',
            tabs_label: 'Rannóga an cháis iontrála',
            per_page: 'Cásanna in aghaidh an leathanaigh',
            filter_cases: 'Scag cásanna iontrála',
            cases_count: 'cás',
            cases_loaded: 'cás iontrála luchtaithe',
            no_cases: 'Níor aimsíodh aon chás iontrála.',
            vendor_lookup_placeholder: 'Cuir isteach ID an tsoláthraí',
            data_categories_placeholder: 'm.sh., sonraí_pearsanta, airgeadais, sláinte',
            systems_placeholder: 'm.sh., CRM, ERP, Córas AD',
            status_open: 'Oscailte',
            status_in_progress: 'Ar siúl',
            status_awaiting_approval: 'Ag fanacht le ceadú',
            status_approved: 'Ceadaithe',
            status_remediation_required: 'Leigheas ag teastáil',
            status_closed: 'Dúnta',
            type_new_vendor: 'Soláthraí nua',
            type_service_change: 'Athrú seirbhíse',
            type_renewal: 'Athnuachan',
            type_reassessment: 'Athphróiseáil',
            tab_overview: 'Forléargas',
            tab_due_diligence: 'Dícheall cuí',
            tab_security_assessment: 'Measúnú slándála',
            tab_contractual_controls: 'Rialuithe conartha',
            tab_sub_processors: 'Fo-phróiseálaithe',
            tab_approval_gates: 'Geataí ceadaithe',
            tab_evidence: 'Fianaise',
            tab_placeholder: 'Líonfar ábhar an rannóige seo le linn an phróisis iontrála.',
            completion_not_started: 'Gan tosú',
            completion_in_progress: 'Ar siúl',
            completion_completed: 'Críochnaithe',
            completion_requiring_attention: 'Aird ag teastáil',
            gate_name: 'Ainm an gheata',
            approver: 'Ceadaitheoir',
            decision: 'Cinneadh',
            comments: 'Nótaí tráchta',
            decide: 'Déan cinneadh',
            decision_approved: 'Ceadaithe',
            decision_rejected: 'Diúltaithe',
            decision_deferred: 'Curtha ar athlá',
            decision_pending: 'Pending',
            no_approvals: 'Níl aon gheataí ceadaithe cumraithe don chás seo.',
            approval_success: 'Cinneadh ceadaithe taifeadta go rathúil',
            approval_error: 'Níorbh fhéidir an cinneadh ceadaithe a thaifeadadh',
            transition_status: 'Aistrigh stádas',
            confirm_transition: 'Aistrigh cás ó "{from}" go "{to}"?',
            transition_success: 'Stádas an cháis aistrithe go rathúil',
            transition_error: 'Níorbh fhéidir stádas an cháis a aistriú',
            create_success: 'Cás iontrála cruthaithe go rathúil',
            create_error: 'Níorbh fhéidir cás iontrála a chruthú',
            load_error: 'Níorbh fhéidir cásanna iontrála a luchtú',
            detail_error: 'Níorbh fhéidir sonraí an cháis a luchtú',
            export: 'Easpórtáil',
            export_error: 'Níorbh fhéidir an cás iontrála a easpórtáil',
            evidence_tab_description: 'Uaslódáil agus easpórtáil fianaise don chás bordála.',
            upload_evidence_label: 'Uaslódáil fianaise',
            attached_evidence_heading: 'Fianaise cheangailte',
            no_evidence_attached: 'Níl aon fhianaise ceangailte fós.',
            export_bundle_button: 'Easpórtáil beart fianaise',
            load_attachments_error: 'Theip ar lódáil fianaise.',
            linked_questionnaires_heading: 'Ceistneoirí nasctha',
            no_linked_questionnaires: 'Níl aon cheistneoirí nasctha.',
            linked_scores_heading: 'Scóir riosca nasctha',
            no_linked_scores: 'Níl aon scóir nasctha.',
            latest_score_label: 'Scór is déanaí',
            linked_contracts_heading: 'Conarthaí nasctha',
            no_linked_contracts: 'Níl aon chonarthaí nasctha.',
            linked_sub_processors_heading: 'Fo-phróiseálaithe nasctha',
            no_linked_sub_processors: 'Níl aon fho-phróiseálaithe nasctha.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Marcáil mar Chríochnaithe',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Clár Fo-phróiseálaithe',
            page_title: 'Clár Fo-phróiseálaithe',
            new_record: 'Fo-phróiseálaí nua',
            edit_record: 'Cuir fo-phróiseálaí in eagar',
            onboarding_case: 'Cás iontrála',
            services: 'Seirbhísí curtha ar fáil',
            data_categories: 'Catagóirí sonraí',
            processing_countries: 'Tíortha próiseála',
            dpa_chain_status: 'Stádas slabhra DPA',
            certifications: 'Deimhniúcháin',
            last_assessment: 'Measúnú deireanach',
            next_assessment: 'Measúnú eile',
            change_history: 'Stair athruithe',
            history: 'Stair',
            changed_by: 'Athraithe ag',
            comma_separated: 'Luachanna scartha le camóga',
            filter_records: 'Scag taifid',
            filter_by_dpa: 'Scag de réir stádas slabhra DPA',
            all_dpa_statuses: 'Gach stádas DPA',
            per_page: 'Taifid in aghaidh an leathanaigh',
            records_count: 'fo-phróiseálaí',
            records_loaded: 'taifead luchtaithe',
            no_records: 'Níor aimsíodh aon fho-phróiseálaí.',
            no_history: 'Níl stair athruithe ar fáil.',
            dpa_active: 'Gníomhach',
            dpa_pending: 'Ar feitheamh',
            dpa_expired: 'As feidhm',
            dpa_not_required: 'Ní gá',
            create_success: 'Fo-phróiseálaí cruthaithe go rathúil',
            create_error: 'Níorbh fhéidir fo-phróiseálaí a chruthú',
            update_success: 'Fo-phróiseálaí nuashonraithe go rathúil',
            update_error: 'Níorbh fhéidir fo-phróiseálaí a nuashonrú',
            load_error: 'Níorbh fhéidir an clár a luchtú',
            history_error: 'Níorbh fhéidir stair athruithe a luchtú',
            export_csv: 'Easpórtáil CSV',
            export_json: 'Easpórtáil JSON',
            export_error: 'Níorbh fhéidir sonraí a easpórtáil',
        },
    },
    deletion_orchestration: {
        nav: {
            dashboard: 'Painéal ceolfhoirne',
            runs: 'Ritheanna ceolfhoirne',
            tasks: 'Tascanna feidhmithe',
            templates: 'Teimpléid seicliosta',
            vendor_requests: 'Iarratais soláthróra',
            proof_chain: 'Slabhra cruthúnais',
            dsr_integration: 'Comhtháthú DSR',
            vendor_verification: 'Fíorú soláthróra',
            completeness: 'Deais iomláine',
        },
        dashboard: {
            title: 'Painéal ceolfhoirne',
            loading: 'Ag lódáil painéal sonraí...',
            active_runs: 'Ritheanna Gníomhacha',
            completion_rate: 'Completion Ráta',
            avg_completion_time: 'Avg Completion Am',
            overdue_tasks: 'Thar téarma Tascanna',
            vendor_compliance: 'Comhlíonadh Soláthraí',
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Le déanaí Rith',
            no_recent_runs: 'Gan ritheanna ceolfhoirne le déanaí.',
            run_id: 'Rith ID',
            ticket_id: 'Ticéad ID',
            progress: 'Dul chun cinn',
            systems: 'Córais',
            vendors: 'Soláthraithe',
            load_error: 'Theip ar lódáil an deais ceolfhoirne. Bain triail eile as.',
            aria_table: 'Ritheann ceolfhoireann le déanaí',
        },
        runs: {
            title: 'Ritheanna ceolfhoirne',
            new: 'Nua Rith',
            run_id: 'Rith ID',
            ticket_id: 'Ticéad ID',
            progress: 'Dul chun cinn',
            systems: 'Córais',
            vendors: 'Soláthraithe',
            data_subject_ref: 'Sonraí Subject Ref',
            data_categories: 'Sonraí Catagóirí',
            enable_proof_chain: 'Cumasaigh Cruthúnas Slabhra',
            start: 'Tosaigh',
            started: 'Thosaigh rith',
            mark_ready: 'Marcáil Réidh',
            marked_ready: 'Rith marcáilte mar réidh',
            complete: 'Críochnaigh',
            complete_exceptions: 'Críochnaigh w/ Eisceachtaí',
            pause: 'Sos',
            resume: 'Atosaigh',
            cancel: 'Cealaigh',
            no_runs: 'Níl aon orchestration runs found. aimsithe',
            load_error: 'Theip ar ritheanna ceolfhoirne a lódáil. Bain triail eile as.',
            create_error: 'Theip ar rith ceolfhoirne a chruthú',
            aria_filter_status: 'Scag de réir stádais rith',
            aria_table: 'Ritheann ceolfhoirne',
            placeholder_ticket_id: 'm.sh., TKT-000123',
            placeholder_data_subject_ref: 'm.sh., DS-REF-001',
            placeholder_data_categories: 'e.g., personal_data, airgeadais, sláinte',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            title_label: 'Title',
            run_type: 'Run Type',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: 'Tascanna feidhmithe',
            task_id: 'Tasc ID',
            run_id: 'Rith ID',
            system_name: 'Córas Ainm',
            assigned_to: 'Sannta To',
            sla_deadline: 'Spriocdháta SLA',
            progress: 'Dul chun cinn',
            enter_run_id: 'Iontráil rith id to filter...',
            select_run: 'Roghnaigh rith chun tascanna a fheiceáil',
            unassigned: 'Neamhshannta',
            overdue: 'Thar téarma',
            assign_task: 'Sann Tasc',
            assignee_id: 'Sannaí ID',
            update_status: 'Nuashonraigh Stádas',
            new_status: 'Nua Stádas',
            no_transitions: 'Tá an tasc seo i staid deiridh agus ní féidir an stádas a athrú.',
            select_status_placeholder: 'Roghnaigh stádas nua',
            skip_justification: 'Bonú don Scipeáil',
            failure_reason: 'Cúis na Teipe',
            no_tasks: 'Níl aon execution tasc found. aimsithe',
            new_task: 'Tasc Nua',
            system_id: 'Aitheantas an Chórais',
            data_classification: 'Aicmiú Sonraí',
            load_error: 'Theip ar luchtú execution tasks. Bain triail eile as.',
            assign_error: 'Theip ar assign task',
            status_error: 'Theip ar stádas an taisc a nuashonrú',
            no_tasks_for_run: 'Níl aon tascanna forghníomhaithe ag an rith scriosta seo go fóill.',
            aria_filter_run_id: 'Scag le haitheantas a rith',
            aria_filter_status: 'Scag de réir stádais taisc',
            aria_table: 'Tascanna forghníomhaithe',
            view_steps: 'Féach ar na Céimeanna',
            steps_modal_title: 'Céimeanna Forghníomhaithe',
            step_title: 'Céim',
            step_status: 'Stádas na Céime',
            step_notes: 'Nótaí',
            step_evidence: 'Comhaid Fianaise',
            step_evidence_required: 'Tá fianaise de dhíth don chéim seo',
            step_evidence_missing: 'Níor uaslódáladh aon fhianaise go fóill',
            step_save: 'Sábháil an Chéim',
            step_saved: 'Céim nuashonraithe',
            step_save_error: 'Theip ar an gcéim a nuashonrú',
            create_success: 'Tasc forghníomhaithe cruthaithe',
            create_error: 'Theip ar an tasc forghníomhaithe a chruthú',
            step_upload_label: 'Ceangail fianaise',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Teimpléid seicliosta',
            new: 'Nua Teimpléad',
            template_id: 'Teimpléad ID',
            system_type: 'Cineál Córais',
            est_duration: 'Est. Fad',
            steps: 'Céimeanna',
            action_type: 'Cineál Gnímh',
            instructions: 'Treoracha',
            mandatory: 'Éigeantach',
            add_step: 'Cuir Céim leis',
            step_n: 'Céim',
            no_templates: 'Níl aon checklist teimpléid found. aimsithe',
            all_system_types: 'Gach Cineál Córais',
            load_error: 'Theip ar lódáil teimpléid seicliosta. Bain triail eile as.',
            create_error: 'Theip ar chruthú teimpléad seicliosta',
            aria_filter_system_type: 'Scag de réir cineáil córais',
            aria_filter_status: 'Scag de réir stádas teimpléid',
            aria_table: 'Teimpléid seicliosta',
            placeholder_name: 'm.sh., Seicliosta Scriosta Bunachar Sonraí',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Teimpléad nuashonraithe go rathúil',
            create_success_msg: 'Teimpléad cruthaithe go rathúil',
            update_error: 'Theip ar nuashonrú an teimpléid',
            activate_success: 'Teimpléad gníomhachtaithe go rathúil',
            activate_error: 'Theip ar ghníomhachtú an teimpléid',
            archive_success: 'Teimpléad cartlannaithe go rathúil',
            archive_error: 'Theip ar chartlannú an teimpléid',
            clone_success: 'Teimpléad clónáilte go rathúil',
            clone_error: 'Theip ar chlónáil an teimpléid',
        },
        vendor_requests: {
            title: 'Soláthraí Deletion Iarratais',
            new: 'Nua Iarratas',
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Iarratas ID',
            vendor_name: 'Ainm Soláthraí',
            vendor_id: 'ID Soláthraí',
            data_subject_ref: 'Sonraí Subject Ref',
            data_categories: 'Sonraí Catagóirí',
            legal_basis: 'Bunús Dlíthiúil',
            sla_days: 'SLA laethanta',
            requested_completion: 'Críochnú Iarrtha',
            communications: 'Cumarsáidí',
            total_requests: 'Iarratais Iomlána',
            no_requests: 'Níl aon soláthraí deletion requests found. aimsithe',
            filter_vendor_id: 'Scag by Soláthraí ID...',
            change_status: 'Athraigh Stádas',
            new_status: 'Stádas Nua',
            actions: 'Gníomhartha',
            load_error: 'Theip ar iarratais scriosta an díoltóra a lódáil. Bain triail eile as.',
            create_error: 'Theip ar iarratas scriosta an díoltóra a chruthú',
            dashboard_error: 'Theip ar lódáil painéal scriosta an díoltóra. Bain triail eile as.',
            aria_filter_status: 'Scag de réir stádas iarratais',
            aria_filter_vendor_id: 'Scag de réir aitheantais an díoltóra',
            aria_table: 'Iarratais scriosta díoltóirí',
            placeholder_vendor_id: 'm.sh., VND-001',
            placeholder_vendor_name: 'm.sh., Acme Analytics',
            placeholder_data_subject_ref: 'm.sh., DS-REF-001',
            placeholder_data_categories: 'e.g., personal_data, anailísíocht',
            placeholder_legal_basis: 'e.g., PDPL Airteagal 19',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Cruthúnas Slabhra Breathnóir',
            enter_run_id: 'Iontráil rith id...',
            load_chain: 'Lódáil Slabhra',
            verify_chain: 'Fíoraigh Slabhra',
            verifying: 'Ag fíorú...',
            chain_valid: 'Slabhra is valid',
            chain_invalid: 'Slabhra is broken',
            chain_length: 'Slabhra Length',
            broken_at_index: 'Briste ag innéacs',
            no_proof_records: 'Níl aon cruthúnas taifid found for this run. aimsithe',
            proof_id: 'Cruthúnas ID',
            entity_type: 'Entity Cineál',
            entity_id: 'ID Aonáin',
            event_hash: 'Imeacht Hash',
            previous_hash: 'Roimhe seo Hash',
            attestation: 'Deimhniú',
            attestation_actor: 'Déanta ag',
            attestation_signed_at: 'Sínithe ar',
            attestation_signature: 'Síniú',
            attestation_evidence_refs: 'Tagairtí fianaise',
            run_field_label: 'Rith Scriosta',
            select_run: 'Roghnaigh rith scriosta…',
            aria_select_run: 'Rith scriosta le haghaidh cuardach slabhra cruthúnais',
            chain_broken_here: 'Slabhra broken here',
            load_error: 'Theip ar an slabhra cruthúnais a lódáil. Bain triail eile as.',
            verify_error: 'Theip ar an slabhra cruthúnais a fhíorú. Bain triail eile as.',
            aria_run_id: 'Rith ID le haghaidh cuardaigh slabhra cruthúnais',
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — English until Phase 25 (R-57).
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Athraigh stádas',
            request_type: 'Cineál Iarratais',
            data_subject_hint: 'Aitheantóir an ábhair sonraí a scriosfar a chuid sonraí (m.sh., ríomhphost nó ID custaiméara). De ghnáth meaitseálann sé leis an iarratasóir ar an gcás DSR roghnaithe.',
            title: 'Comhtháthú Scriosta DSR',
            create_run: 'Cruthaigh Rith Scriosta DSR',
            dsr_case_id: 'Aitheantas Cáis DSR',
            data_subject_id: 'Aitheantas an Ábhair Sonraí',
            systems: 'Sprioccórais',
            route_to_department: 'Seol chuig an roinn',
            confirm_deletion: 'Deimhnigh scriosadh',
            no_runs: 'Níor aimsíodh aon rith scriosta DSR',
            status: 'Stádas Scriosta',
            deadline: 'Spriocdháta',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: 'Fíorú Scriosta Soláthróra',
            create_followup: 'Cruthaigh leantach',
            deadline: 'Spriocdháta deimhnithe',
            escalation_contact: 'Teagmháil ardaithe',
            escalate: 'Ardaigh',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: 'Níor aimsíodh aon leantach soláthróra',
            vendor_request: 'Iarratas Díoltóra',
            vendor: 'Soláthróir',
            unknown_vendor: 'Soláthróir anaithnid',
            overdue_badge: 'Thar téarma',
            mark_confirmed: 'Marcáil mar dheimhnithe',
            mark_confirmed_for: 'Marcáil scriosadh mar dheimhnithe do {vendor}',
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Scriosadh soláthróra deimhnithe',
            confirmation_failed: 'Theip ar scriosadh soláthróra a dheimhniú',
            select_request: 'Roghnaigh iarratas díoltóra…',
            escalation_contact_placeholder: 'm.sh. dpo@vendor.example',
            create_success: 'Cruthaíodh leantach díoltóra go rathúil',
            create_failed: 'Theip ar leantach díoltóra a chruthú',
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            escalation_success: "D'éirigh le hardú scriosta soláthraí",
            escalation_failed: 'Theip ar ardú scriosta soláthraí',
            overdue_filter: 'Overdue (pending, deadline passed)',
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: 'Iomláine Scriosta',
            check_conflicts: 'Seiceáil coinbhleachtaí coinneála',
            no_conflicts: 'Níor aimsíodh aon choinbhleachtaí coinneála',
            conflict_resolution: 'Réiteach coinbhleachta',
            extend_retention: 'Fadaigh coinneáil',
            proceed_deletion: 'Lean ar aghaidh le scriosadh',
            escalate_conflict: 'Ardaigh',
            resolve: 'Réitigh coinbhleacht',
            data_subject_id: 'Aitheantas Ábhar Sonraí',
            placeholder_data_subject_id: 'Cuir isteach ID an ábhair sonraí',
            data_subject_hint: 'Aitheantóir an duine a bhfuil a scriosadh á fhíorú agat (m.sh., ríomhphost, ID custaiméara nó tagairt DSR). Roghnaigh ábhar aitheanta nó clóscríobh ceann.',
            guidance: 'Cuir isteach nó roghnaigh ID ábhair sonraí, roghnaigh córais más mian leat, ansin rith an tseiceáil chun iomláine an scriosta agus coinbhleachtaí coinneála a fheiceáil.',
            completion: 'Críochnú Scriosta',
            pending_items: 'Míreanna ar feitheamh',
            system: 'Córas',
            policy: 'Polasaí',
            retention_end: 'Deireadh Coinneála',
            completed_items: 'Míreanna Críochnaithe',
            last_review_label: 'Léirmheas deireanach',
            days_ago: 'lá ó shin',
            review_overdue: 'Sáraíodh an timthriall athbhreithnithe. Ath-athbhreithniú ag teastáil.',
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        run_status_labels: {
            pending: 'Ar feitheamh',
            planning: 'Pleanáil',
            ready: 'Réidh',
            in_progress: 'Ar Siúl',
            paused: 'Curtha ar Sos',
            completed: 'Críochnaithe',
            completed_with_exceptions: 'Críochnaithe le hEisceachtaí',
            cancelled: 'Curtha ar Ceal',
        },
        task_status_labels: {
            pending: 'Ar Feitheamh',
            assigned: 'Sannta',
            in_progress: 'Ar Siúl',
            awaiting_verification: 'Ag Fanacht le Fíorú',
            verified: 'Fíoraithe',
            failed: 'Teipthe',
            skipped: 'Scipeáilte',
        },
        system_type_labels: {
            database: 'Bunachar Sonraí',
            saas_application: 'Feidhmchlár SaaS',
            file_storage: 'Stóráil Comhad',
            email_system: 'Córas Ríomhphoist',
            analytics_platform: 'Ardán Anailísíochta',
            backup_system: 'Córas Cúltaca',
            vendor_managed: 'Bainistiú Soláthraí',
            custom: 'Saincheaptha',
        },
        template_status_labels: {
            draft: 'Dréacht',
            active: 'Gníomhach',
        },
        action_type_labels: {
            manual_check: 'Seiceáil Láimhe',
            api_call: 'Glao API',
            sql_query: 'Iarratas SQL',
            script_execution: 'Rith Script',
            file_deletion: 'Scriosadh Comhaid',
            verification: 'Fíorú',
            notification: 'Fógra',
            approval: 'Ceadú',
        },
        request_status_labels: {
            draft: 'Dréacht',
            sent: 'Seolta',
            acknowledged: 'Admhaithe',
            in_progress: 'Ar Siúl',
            completed: 'Críochnaithe',
            overdue: 'Thar Téarma',
            escalated: 'Ardaithe',
            disputed: 'Faoi Dhíospóid',
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Tionscnamh',
            data_deletion: 'Scriosadh Sonraí',
            completion: 'Críochnú',
            verification: 'Fíorú',
            rollback: 'Aischur',
        },
        count_labels: {
            one_template: '1 teimpléad',
            n_templates: '{n} teimpléad',
            one_run: '1 rith',
            n_runs: '{n} rith',
            one_task: '1 tasc',
            n_tasks: '{n} tasc',
            one_request: '1 iarratas',
            n_requests: '{n} iarratas',
        },
        aria: {
            completion_rate: 'Ráta críochnaithe: {pct}%',
            vendor_compliance: 'Comhlíonadh soláthraí: {pct}%',
            run_progress: 'Dul chun cinn: {pct}%',
            run_progress_systems: 'Dul chun cinn: {completed} as {total} córas próiseáilte',
            chain_broken: 'Slabhra briste ag an taifead seo',
            chain_valid: 'Slabhra bailí ag an taifead seo',
            chain_unknown: 'Stádas slabhra anaithnid',
            escalate_vendor: 'Ardaigh {id}',
            resolve_conflict: 'Réitigh coinbhleacht do {system}',
        },
        regulatory: {
            annual_review_cycle_info: 'Ní mór iomláine an scriosta a athbhreithniú gach 365 lá ar a laghad. Tá taifid a sháraíonn an tairseach seo sonraithe le haghaidh ath-athbhreithniú éigeantach chun comhlíonadh rialála a choinneáil.',
            runs_data_d1_lifecycle_info: 'Léarscáil de stáit rith ceolfhoirne chuig an DATA-D1 saolré: pleanáil = catalóg, réidh = rangú, in_progress = rialacha a chur i bhfeidhm, críochnaithe = fianaise ginte, completed_with_exceptions = athbhreithniú ag teastáil. Taifeadtar gach trasdul sa slabhra cruthúnais.',
            vendor_record_linkage_info: 'Tá gach iarratas scriosta díoltóra nasctha lena thaifead díoltóirí trí vendor_id. Teastaíonn fianaise chripteagrafach ón slabhra cruthúnais maidir le deimhniú críochnaithe sula bhféadfar an t-iarratas a mharcáil mar chríoch.',
            templates_internal_classification_info: 'Aicmítear gach teimpléad seicliosta scriosta mar dhoiciméid eagraíochtúla inmheánacha. Níl foirm aitheantais oifigiúil rialála acu agus níor cheart iad a chur isteach mar chomhduithe rialála.',
            proof_chain_evidence_info: 'Gabhann gach taifead cruthúnais an t-ábhar/catagóir sonraí, stampa ama, gníomhaí, bunús dlí, agus hais SHA-256. Táirgeann na réimsí seo le chéile fianaise ar scriosadh cripteagrafach a shásaíonn ceanglais iniúchta rialála.',
            retention_data_law_info: 'Díorthaítear sceidil choinneála ar an deais seo ón rangú Dlí Sonraí a shanntar do gach catagóir sonraí. Nuair a athraíonn aicmiú, nuashonraítear na rialacha coinneála nasctha go huathoibríoch chun ailíniú rialála a áirithiú.',
            dsr_erasure_route_info: 'Tá rití scriosta DSR nasctha leis an gcás Iarratas ar Chearta tionscnaimh. Déantar an spriocdháta rialála (30 lá go hiondúil faoi GDPR Airteagal 17) a rianú go huathoibríoch. Overdue cases are flagged for escalation.',
        },
    },
    discovery: {
        nav: {
            dashboard: 'Painéal aimsithe',
            scans: 'Scananna aimsithe',
            staging_area: 'Limistéar ullmhúcháin',
            dedup_rules: 'Rialacha dídhúbailt',
            classification_rules: 'Rialacha aicmithe',
            suggestions: 'Scuaine moltaí',
            analytics: 'Anailísíocht moltaí',
            import_templates: 'Teimpléid iompórtála',
            connectors: 'Bainistiú nascóirí',
            discovery_cycles: 'Timthriallta aimsithe',
            owner_review: 'Athbhreithniú úinéara',
            classification_lineage: 'Stair aicmithe',
        },
        dashboard: {
            title: 'Painéal aimsithe',
            loading: 'Ag lódáil painéal sonraí...',
            total_scans: 'Scananna Iomlána',
            active_scans: 'Scananna Gníomhacha',
            completed_30d: 'Críochnaithe (30d)',
            failed_30d: 'Theip (30d)',
            systems_discovered: 'Córais Discovered',
            pending_staged_records: 'Ar feitheamh Staged Taifid',
            recent_scans: 'Le déanaí Scananna',
            no_recent_scans: 'No recent scanadh.',
            scan_id: 'Scanadh ID',
            discovered: 'Aimsithe',
            view_all_scans: 'Féach ar Gach Scanadh',
            staging_area: 'Limistéar Réamhcheaptha',
            suggestion_queue: 'Moladh Queue',
            records_reviewed: 'Taifid athbhreithnithe',
            records_approved: 'Taifid ceadaithe',
            exceptions_pending: 'Eisceachtaí ar feitheamh',
            view_as_chart: 'Taispeáin mar chairt',
            view_as_table: 'Taispeáin mar thábla',
        },
        scans: {
            title: 'Scananna aimsithe',
            new: 'Nua Scanadh',
            scan_id: 'Scanadh ID',
            scan_type: 'Cineál Scanta',
            progress: 'Dul chun cinn',
            discovered: 'Aimsithe',
            no_scans: 'Níl aon discovery scanadh found. aimsithe',
            all_scan_types: 'Gach Cineál Scanta',
            source_config: 'Foinse Config (JSON)',
            invalid_json: 'Neamhbhailí JSON in source config',
            load_error: 'Theip ar load discovery scanadh. Bain triail eile as.',
            create_error: 'Theip ar create discovery scanadh. Bain triail eile as.',
            change_status: 'Athrú Stádas',
            new_status: 'Stádas Nua',
            actions: 'Gníomhartha',
            type_endpoint: 'Críochphointe',
            type_spreadsheet_import: 'Iompórtáil scarbhileog',
            type_cmdb_sync: 'Sioncronú CMDB',
            type_cloud_sync: 'Sioncronú néil',
            status_pending: 'Ar feitheamh',
            status_running: 'Ag rith',
            status_completed: 'Críochnaithe',
            status_failed: 'Theip air',
            status_cancelled: 'Cealaithe',
            source_config_placeholder: '{ "óstach": "...", "port": 443}',
            adapter_not_implemented_toast: 'Níl an cineál scanadh seo ar fáil fós. Ní féidir ach scananna críochphointe a chruthú.',
            adapter_not_implemented_warning: 'Níl sé ar fáil fós. Ní féidir scananna den chineál seo a chruthú go dtí go bhfoilseofar an tacaíocht dóibh.',
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Limistéar ullmhúcháin',
            record_id: 'ID Taifid',
            source_scan: 'Foinse Scanadh',
            entity_type: 'Entity Cineál',
            suggested_name: 'Suggested Ainm',
            confidence: 'Muiníne',
            no_records: 'Níl aon staged taifid found. aimsithe',
            promote: 'Ardaigh',
            discard: 'Cuir ar ceal',
            review: 'Athbhreithniú',
            merge: 'Cumaisc',
            merge_title: 'Taifead Cumaisc',
            merge_target_id: 'Cumaisc Sprioc ID',
            reject_reason_placeholder: 'Cúis le diúltú...',
            promotion_status: 'Promotion Stádas',
            dedup_status: 'Dedup Stádas',
            all_source_types: 'Gach Cineál Foinse',
            all_promotion_statuses: 'Gach Stádas Ardaithe',
            all_dedup_statuses: 'Gach Stádas Dídúblála',
            load_error: 'Theip ar load staged taifid. Bain triail eile as.',
            promote_error: 'Theip ar promote taifead. Bain triail eile as.',
            reject_error: 'Theip ar reject taifead. Bain triail eile as.',
            merge_error: 'Theip ar merge taifead. Bain triail eile as.',
            review_error: 'Theip ar mark taifead for athbhreithniú. Bain triail eile as.',
            bulk_promote_error: 'Theip ar bulk promote taifid. Bain triail eile as.',
            bulk_reject_error: 'Theip ar bulk reject taifid. Bain triail eile as.',
            service_type_web_server: 'Freastaláí gréasáin',
            service_type_database: 'Bunachar sonraí',
            service_type_api_gateway: 'Geata API',
            service_type_storage: 'Stóráil',
            service_type_authentication: 'Fíordheimhniú',
            service_type_analytics: 'Anailísíocht',
            service_type_unknown: 'Anaithnid',
            source_type_endpoint_scan: 'Scan críochphointe',
            source_type_spreadsheet: 'Scarbhileog',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Fardal néil',
            promotion_status_new: 'Nua',
            promotion_status_under_review: 'Á athbhreithniú',
            promotion_status_promoted: 'Ardaithe',
            promotion_status_rejected: 'Diúltaithe',
            promotion_status_merged: 'Cumaisc',
            dedup_status_unmatched: 'Gan mheaitseáil',
            dedup_status_potential_match: 'Meaitseáil fhéideartha',
            dedup_status_exact_match: 'Meaitseáil bheacht',
            dedup_status_resolved: 'Réitithe',
            confirm_bulk_reject: 'An bhfuil tú cinnte gur mhaith leat na taifid roghnaithe a dhiúltú?',
            scan_batch_id: 'Aitheantas baisc',
            promote_to_registry_title: 'Cur chun cinn chuig an gClárlann Sonraí',
            promote_modal_instructions: 'Cruthaítear mír sa Chlárlann Sonraí le collection_method: nasc breathnaithe agus foinse leis an taifead fionnachtana seo. The item starts in draft status for review.',
            promote_label_category: 'Catagóir',
            promote_label_name_override: 'Ainm (sárú roghnach)',
            placeholder_name_override: 'Réamhshocruithe le hainm/óstainm an taifid fhionnachtana',
            promote_label_sensitivity: 'Íogaireacht bheartaithe',
            inherit_from_category: 'Oidhreacht ón gcatagóir',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Rialacha dídhúbailt',
            new: 'Nua Riail',
            rule_name: 'Riail Ainm',
            match_fields: 'Match Réimsí',
            threshold: 'Tairseach',
            no_rules: 'Níl aon dedup rules found. aimsithe',
            seed_defaults: 'Síolaigh Réamhshocrú',
            match_type: 'Match Cineál',
            similarity_threshold: 'Similarity Tairseach',
            field_name: 'Réimse Ainm',
            load_error: 'Theip ar load dedup rules. Bain triail eile as.',
            create_error: 'Theip ar create dedup rule. Bain triail eile as.',
            toggle_error: 'Theip ar toggle dedup rule. Bain triail eile as.',
            seed_error: 'Theip ar seed defaults. Bain triail eile as.',
            priority: 'Tosaíocht',
            enabled_status: 'Cumasaithe',
            disabled_status: 'faoi mhíchumas',
            default_badge: 'Réamhshocrú',
            enable: 'Cumasaigh',
            disable: 'Díchumasaigh',
            match_type_exact: 'Beacht',
            match_type_fuzzy: 'Doiléir',
            match_type_regex: 'Regex',
            match_type_ip_range: 'Raon IP',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Rialacha aicmithe',
            new: 'Nua Riail',
            rule_name: 'Riail Ainm',
            pattern: 'Patrún',
            classification: 'Aicmiú',
            no_rules: 'Níl aon classification rules found. aimsithe',
            seed_defaults: 'Síolaigh Réamhshocrú',
            run_rules_now: 'Rith Rialacha Anois',
            run_rules_success: 'Gineadh {count} moladh/moltaí aicmithe nua',
            run_rules_no_results: 'Níor gineadh aon mholtaí nua — tá moltaí ar feitheamh cheana féin ag na rialacha comhoiriúnaithe go léir',
            run_rules_error: 'Theip ar na rialacha aicmithe a rith. Bain triail eile as.',
            taxonomy_id: 'Tacsanomaíocht ID',
            level_id: 'Leibhéal ID',
            condition_logic: 'Loighic Coinníollacha',
            weight: 'Meáchan',
            conditions: 'Coinníollacha',
            condition_field: 'Réimse',
            condition_operator: 'Oibreoir',
            condition_value: 'Luach',
            effectiveness: 'Éifeachtacht',
            match_count: 'Meaitseálacha',
            approval_count: 'Ceaduithe',
            rejection_count: 'Diúltuithe',
            filter_taxonomy: 'Scag by Tacsanomaíocht ID...',
            load_error: 'Theip ar load classification rules. Bain triail eile as.',
            create_error: 'Theip ar create classification rule. Bain triail eile as.',
            seed_error: 'Theip ar seed defaults. Bain triail eile as.',
            toggle_error: 'Theip ar toggle rule. Bain triail eile as.',
            priority: 'Tosaíocht',
            enabled_status: 'Cumasaithe',
            disabled_status: 'faoi mhíchumas',
            enable: 'Cumasaigh',
            disable: 'Díchumasaigh',
            logic_and: 'AGUS',
            logic_or: 'NÓ',
            operator_equals: 'Cothrom le',
            operator_contains: 'Cuimsíonn',
            operator_matches_regex: 'Meaitseálann Regex',
            operator_in_list: 'Sa liosta',
            operator_not_equals: 'Neamhionann le',
            operator_not_contains: 'Ní chuimsíonn',
            enabled_label: 'Cumasaithe',
            disabled_label: 'Díchumasaithe',
            taxonomy_label: 'Tacsanomaíocht',
            operator_label: 'Oibreoir',
            value_label: 'Luach',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'Scuaine moltaí',
            entity_type: 'Entity Cineál',
            suggested_name: 'Suggested Ainm',
            source: 'Foinse',
            confidence: 'Muiníne',
            accept: 'Glac le',
            reject: 'Diúltaigh',
            no_suggestions: 'Gan moltaí sa scuaine.',
            modify_approve: 'Modify & Ceadaigh',
            delegate: 'Toscair',
            delegate_user_id: 'Toscair Úsáideoir ID',
            new_level_id: 'Nua Leibhéal ID',
            reject_reason_placeholder: 'Cúis le diúltú...',
            current_level: 'Reatha Leibhéal',
            suggested_level: 'Suggested Leibhéal',
            filter_taxonomy: 'Scag by Tacsanomaíocht ID...',
            all_confidence: 'Gach Banda Muiníne',
            load_error: 'Theip ar load suggestions. Bain triail eile as.',
            approve_error: 'Theip ar approve suggestion. Bain triail eile as.',
            reject_error: 'Theip ar reject suggestion. Bain triail eile as.',
            modify_error: 'Theip ar modify & approve suggestion. Bain triail eile as.',
            delegate_error: 'Theip ar delegate suggestion. Bain triail eile as.',
            bulk_approve_error: 'Theip ar bulk approve suggestions. Bain triail eile as.',
            bulk_reject_error: 'Theip ar bulk reject suggestions. Bain triail eile as.',
            suggestion_type_new_label: 'Lipéad nua',
            suggestion_type_validation: 'Bailíochtú',
            suggestion_type_reclassification: 'Athrangú',
            status_pending: 'Ar feitheamh',
            status_approved: 'Ceadaithe',
            status_rejected: 'Diúltaithe',
            status_superseded: 'Ionadaithe',
            confidence_high: 'Ard',
            confidence_medium: 'Meánach',
            confidence_low: 'Íseal',
            confirm_bulk_reject: 'An bhfuil tú cinnte gur mhaith leat na moltaí roghnaithe a dhiúltú?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'Gach Tacsanomaíocht',
        },
        analytics: {
            title: 'Anailísíocht moltaí',
            total_suggestions: 'Moltaí Iomlána',
            accepted: 'Glactha',
            rejected: 'Diúltaithe',
            pending: 'Ar feitheamh',
            acceptance_rate: 'Ráta Glactha',
            by_entity_type: 'By Entity Cineál',
            by_source: 'By Foinse',
            avg_decision_time: 'Avg Cinneadh Am',
            rule_effectiveness: 'Riail Effectiveness',
            by_status: 'By Stádas',
            by_confidence: 'De réir Banda Muiníne',
            load_error: 'Theip ar load suggestion analytics. Bain triail eile as.',
            confidence_high: 'Ard',
            confidence_medium: 'Meánach',
            confidence_low: 'Íseal',
            by_confidence_band: 'De réir banda muiníne',
            approval_rate: 'Ráta ceadaithe',
            by_label_state: 'De réir staid lipéid',
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: 'Teimpléid iompórtála',
            new: 'Nua Teimpléad',
            template_name: 'Teimpléad Ainm',
            file_format: 'Comhad Format',
            column_mappings: 'Mapálacha Colún',
            no_templates: 'Níl aon import teimpléid found. aimsithe',
            source_column: 'Foinse Column',
            target_field: 'Sprioc Réimse',
            transform: 'Claochlaigh',
            source_column_label: 'Colún Foinse',
            target_field_label: 'Réimse Sprioc',
            transform_label: 'Claochlú',
            confirm_delete: 'An bhfuil tú cinnte gur mian leat scriosadh this import template?',
            load_error: 'Theip ar load import teimpléid. Bain triail eile as.',
            save_error: 'Theip ar save import teimpléad. Bain triail eile as.',
            delete_error: 'Theip ar delete import teimpléad. Bain triail eile as.',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Bainistiú nascóirí',
            new: 'Nua Nascóir',
            connector_name: 'Nascóir Ainm',
            connector_type: 'Cineál',
            last_sync: 'Sioncronú Deireanach',
            no_connectors: 'Níl aon connectors found. aimsithe',
            cmdb_syncs: 'Sioncronuithe CMDB',
            cloud_syncs: 'Sioncronuithe Néil',
            new_sync: 'Nua Sync',
            no_syncs: 'Níl aon syncs found. aimsithe',
            cmdb_error: 'Theip ar load cmdb scanadh. Bain triail eile as.',
            cloud_error: 'Theip ar load cloud scanadh. Bain triail eile as.',
            trigger_error: 'Theip ar trigger sync. Bain triail eile as.',
            trigger_sync_error: 'Theip ar trigger {type}',
            cmdb_section_title: 'Sioncrónuithe CMDB',
            cloud_section_title: 'Sioncrónuithe néil',
            column_connector: 'Nascóir',
            action_start: 'Tosaigh',
            confirm_cancel_sync: 'An sioncrónú seo a chealú?',
            start_error: 'Theip ar thús an tsioncrónaithe',
            cancel_error: 'Theip ar chealú an tsioncrónaithe',
            type_endpoint: 'Scanadh críochphointe',
            type_spreadsheet_import: 'Iompórtáil scarbhileog',
            type_cmdb_sync: 'Sioncrónú CMDB',
            type_cloud_sync: 'Sioncrónú néil',
        },
        discovery_cycles: {
            title: 'Timthriallta aimsithe',
            new_cycle: 'Timthriall aimsithe nua',
            cycle_name: 'Ainm an timthriall',
            scope_type: 'Cineál raoin fheidhme',
            scope_ids: 'Aitheantais raoin fheidhme',
            cadence_days: 'Minicíocht (laethanta)',
            status: 'Stádas',
            no_cycles: 'Níor aimsíodh aon timthriall aimsithe',
            compare: 'Cuir timthriallta i gcomparáid',
            delta_report: 'Tuarascáil athruithe',
            new_records: 'Taifid nua',
            changed_records: 'Taifid athraithe',
            removed_records: 'Taifid bainte',
            schedule: 'Sceideal scanadh',
            scope_organization: 'Eagraíocht',
            scope_department: 'Roinn',
            scope_system: 'Córas',
            scope_systems: 'Córais',
            bulk_actions: 'Gníomhartha ollmhóra',
            selected: '{count} roghnaithe',
            bulk_create: 'Cruthaigh go hollmhór',
            bulk_assign_scope: 'Sann raon go hollmhór',
            bulk_advance: 'Ardaigh stádas go hollmhór',
            confirm_bulk_action: 'Deimhnigh gníomh ollmhór',
            bulk_confirm_message: 'An bhfuil tú cinnte gur maith leat an gníomh seo a dhéanamh?',
            cycles_affected: 'Beidh {count} timthriall i gceist',
            select_all: 'Roghnaigh uile',
            select_cycle: 'Roghnaigh timthriall {name}',
            from_template: 'ó theimpléad',
            bulk_results: 'Torthaí ollmhór',
            failed_label: 'theip',
            template_id: 'Aitheantas teimpléid',
            count: 'Líon',
            advance_to: 'Stádas sprice',
            bulk_success: 'Oibríocht ollmhór críochnaithe',
        },
        owner_review: {
            title: 'Tascanna athbhreithnithe úinéara',
            assign_owner: 'Sann don úinéir',
            owner_id: 'Úinéir',
            decision: 'Cinneadh',
            approve: 'Ceadaigh',
            modify: 'Athraigh',
            reject: 'Diúltaigh',
            escalate: 'Ardaigh',
            no_tasks: 'Níor aimsíodh aon tascanna athbhreithnithe',
            bulk_resolve: 'Réiteach ollmhór',
            exceptions: 'Míreanna eisceachtúla',
            no_exceptions: 'Níor aimsíodh aon mhíreanna eisceachtúla',
            record_id_label: 'Aitheantas Taifeadta',
            selected: 'roghnaithe',
            notes: 'Nótaí',
            select_all: 'Roghnaigh Gach Ceann',
            select: 'Roghnaigh',
            confirm_resolve: 'Deimhnigh',
        },
        classification_lineage: {
            title: 'Stair aicmithe',
            link_suggestion: 'Nasc le lipéad molta',
            suggestion_id: 'Moladh',
            proposed_label_id: 'Lipéad molta',
            no_lineage: 'Níor aimsíodh aon sonraí staire',
            full_lineage: 'Stair iomlán',
            record_id_label: 'Aitheantas Taifeadta',
            staged_record_label: 'Taifead ar stáitse',
            enter_staged_record_id: 'Cuir isteach ID taifead céimnithe',
            scan_id_label: 'Scan ID',
            select_record: 'Roghnaigh taifead',
            select_suggestion: 'Roghnaigh moladh',
            select_label: 'Roghnaigh lipéad',
        },
        common_labels: {
            n_rules: '{n} rialacha',
            one_rule: '1 riail',
            n_scans: '{n} scanadh',
            one_scan: '1 scanadh',
            n_syncs: '{n} shioncronú',
            one_sync: '1 shioncronú',
            n_records: '{n} taifead',
            one_record: '1 taifead',
            n_templates: '{n} teimpléad',
            one_template: '1 teimpléad',
            n_suggestions: '{n} moltaí',
            one_suggestion: '1 mholadh',
            n_selected: '{n} roghnaithe',
            dashboard_loaded: 'Painéal na bhfionnachtain lódáilte',
            n_scans_loaded: '{n} scanadh lódáilte',
            n_staged_records_loaded: '{n} taifead stáitse lódáilte',
            confirm_delete_import_template: 'An bhfuil tú cinnte gur mhaith leat an teimpléad iompórtála seo a scriosadh?',
            invalid_json_source_config: 'Neamhbhailí JSON i source_config',
            scope_organization: 'Eagraíocht',
            scope_department: 'Roinn',
            scope_system: 'Córas',
            aria_filter_scans: 'Scag scananna',
            aria_filter_staged_records: 'Scag taifid stáitse',
            aria_filter_suggestions: 'Scagairí',
            aria_filter_by_taxonomy_id: 'Scag de réir aitheantais tacsanomaíochta',
            aria_filter_by_enabled_status: 'Scag de réir stádais chumasaithe',
            aria_filter_by_source_type: 'Scag de réir cineáil foinse',
            aria_filter_by_promotion_status: 'Scag de réir stádais ardú céime',
            aria_filter_by_dedup_status: 'Scag de réir stádais asbhainte',
            aria_filter_by_scan_type: 'Scag de réir cineáil scanta',
            aria_filter_by_status: 'Scag de réir stádais',
            aria_filter_by_confidence: 'Scag de réir banna muiníne',
            aria_select_all_records: 'Roghnaigh gach taifead',
            aria_select_record: 'Roghnaigh taifead {id}',
            aria_select_all_suggestions: 'Roghnaigh gach moladh',
            aria_select_suggestion: 'Roghnaigh moladh {id}',
            aria_promote_record: 'Cuir taifead chun cinn {id}',
            aria_discard_record: 'Caith an taifead {id}',
            aria_merge_record: 'Cumaisc taifead {id}',
            aria_review_record: 'Athbhreithnigh an taifead {id}',
            aria_accept_suggestion: 'Glac le moladh {id}',
            aria_reject_record: 'Taifead a dhiúltú',
            aria_merge_record_dialog: 'Cumaisc taifead',
            aria_create_new_scan: 'Cruthaigh scanadh nua',
            aria_sync_progress: 'Dul chun cinn sioncronaithe: {processed} de {total} mhíreanna próiseáilte',
            aria_scan_progress: 'Dul chun cinn a scanadh: {processed} de {total} mhíreanna próiseáilte',
            aria_confidence: 'Muinín: {pct}%',
            aria_effectiveness: 'Éifeachtúlacht: {pct}',
            aria_promote_n_records: 'Cuir {n} taifead roghnaithe chun cinn',
            aria_discard_n_records: 'Scrios {n} taifead roghnaithe',
            placeholder_pii_detection_rule: 'e.g., Riail Braite PII',
            placeholder_tax_id: 'm.sh., TAX-001',
            placeholder_lvl_id: 'm.sh., LVL-001',
            placeholder_service_type: 'm.sh., service_type',
            placeholder_database: 'e.g., bunachar sonraí',
            placeholder_hostname_match: 'm.sh., Óstainm Meaitseáil Beacht',
            placeholder_hostname: 'm.sh., óstainm',
            placeholder_hr_systems_csv: 'e.g., Córais AD CSV Iompórtáil',
            placeholder_server_name: 'm.sh., Ainm Freastalaí',
            placeholder_hostname_field: 'm.sh., óstainm',
            placeholder_lowercase: 'm.sh., cás íochtair',
            placeholder_source_config: '{ "óstach": "...", "port": 443}',
            placeholder_stg_id: 'm.sh., STG-000123',
            placeholder_lvl_002: 'm.sh., LVL-002',
            placeholder_usr_id: 'm.sh., USR-000123',
            decision_for: 'Cinneadh le haghaidh {id}',
        },
        exports: {
            title: 'Easpórtáil',
            inventory: 'Fardal sonraí',
            classification_register: 'Clár aicmithe',
            raci: 'Maitrís RACI',
            exceptions: 'Tuairisc eisceachtaí',
            evidence_pack: 'Pacáiste fianaise',
            delta_report: 'Tuairisc athruithe',
            executive_summary: 'Achoimre feidhmiúcháin',
            export_button: 'Easpórtáil',
            downloading: 'Á íoslódáil...',
            download_complete: 'Íoslódáil críochnaithe.',
            download_error: 'Theip ar íoslódáil na heaspórtála. Bain triail eile as.',
        },
        flags: {
            title: 'Marcóirí íochtaracha',
            pending: 'Marcóirí ar feitheamh',
            cross_border_review_needed: 'Athbhreithniú trasteorann de dhíth',
            sharing_disclosure_needed: 'Nochtadh comhroinnte de dhíth',
            dpia_review_needed: 'Athbhreithniú MCTSC de dhíth',
            acknowledge: 'Admháil',
            resolve: 'Réitigh',
            no_flags: 'Níl aon mharcóirí íochtaracha ann.',
        },
        delta: {
            title: 'Achoimre athruithe',
            new_count: '{n} nua',
            removed_count: '{n} bainte',
            changed_count: '{n} athraithe',
            re_review_triggered: 'Ath-athbhreithniú gníomhachtaithe',
            create_re_review: 'Cruthaigh ath-athbhreithniú',
            recent_alerts: 'Foláirimh athruithe le déanaí',
            no_alerts: 'Níl aon fholáirimh athruithe le déanaí.',
            threshold_exceeded: 'Tairseach athruithe sáraithe (>10%)',
        },
        regulatory: {
            dashboard_governance_info: 'Léiríonn an painéal seo stádas rialachais ar leibhéal timthrialla: córais a scanadh, taifid a chur ar stáitse, taifid a athbhreithníodh, taifid a faomhadh, agus eisceachtaí ar feitheamh. This matches the governance lead success criteria: "live dashboard of who has updated their inventory, classification, and sharing logs" (P-VN-05).',
            export_artifacts_info: 'Áirítear le honnmhairí fionnachtana gach cineál déantán riachtanach de réir UC-VN-10 agus DATA-EVD-01: fardal sonraí ceadaithe, clár aicmithe, mapáil úinéireachta/RACI, scuaine eisceachta, achoimre feidhmiúcháin, pacáiste fianaise, agus tuarascáil deilte.',
            scan_inventory_pipeline_info: 'Táirgeann gach scanadh críochnaithe taifid atá comhoiriúnach leis an teimpléad vn_data_law_data_inventory_register (riachtanas 88dd9877). Discovered data assets automatically populate the data inventory register to satisfy the Data Law obligation to maintain a digital data inventory.',
            connector_linkage_info: 'Each connector configuration must include: system type (e.g., CMDB, cloud platform), data store reference (database, file store, SaaS), and business process linkage (UC-VN-10 required supporting records). Cinntíonn na réimsí seo go bhfuil sócmhainní aimsithe inrianaithe chuig a gcóras foinseach agus a gcomhthéacs gnó.',
            owner_routing_info: 'Cuirtear tascanna athbhreithnithe chuig an roinn cheart nó chuig úinéir an fheidhmchláir bunaithe ar úinéireacht an chórais/sonraí (UC-VN-10 céim 5). Úsáideann ródú clár úinéireachta an chórais, ní sannadh ad hoc.',
            owner_actions_audit_info: 'Is féidir le húinéirí gach aicmiú a cheadú, a mhodhnú, a dhiúltú nó a ardú (UC-VN-10 céim 6). Every action captures actor, timestamp, and reason, producing governance audit evidence events per requirement f12fc090 (Annual governance attestation). Léiríonn an rian iniúchta cé a rinne athbhreithniú, cad a athraíodh, agus cathain.',
            escalation_timers_info: 'Amadóirí formhéadaithe: is é 5 lá gnó an spriocdháta athbhreithnithe úinéara; is ionann formhéadú eisceachta agus 48 uair (UC-VN-10 amadóir). Ardmhéadaíonn míreanna nár réitíodh go huathoibríoch nuair a théann an spriocdháta in éag. Is féidir amadóirí a chumrú de réir polasaí eagraíochta.',
            original_suggestion_preserved_info: 'Coinnítear an moladh bunaidh maidir le haicmiú meaisín/córais i dteannta le gach riachtanas fianaise a sháraíonn úinéir (UC-VN-10). Ní dhéantar an moladh bunaidh riamh a fhorscríobh agus tá sé fós le feiceáil chun críocha iniúchta.',
            dedup_lineage_info: 'Cuireann rialacha dídhúbailte cosc ​​ar iontrálacha fardail dúblacha nuair a aimsítear an tsócmhainn sonraí céanna thar scananna iolracha nó nascóirí. Coinnítear na meiteashonraí scanta is déanaí mar an príomhthaifead agus coinnítear tagairtí línéadaigh do gach fionnachtain roimh ré le haghaidh inrianaitheachta iniúchta.',
            label_states_info: 'Déanann anailísí idirdhealú ar 4 stát lipéad le haghaidh athbhreithniú rialachais: meaisín-mholtar (ar feitheamh), úinéir-cheadaithe, úinéir-mhodhnaithe, agus diúltaithe. Cuireann na stáit seo ar chumas iniúchóirí cruinneas uathoibrithe agus rannpháirtíocht úinéirí a mheas.',
            import_pipeline_info: 'Ní mór do theimpléid iompórtála taifid a tháirgeadh atá comhoiriúnach leis an bpíblíne fionnachtana. Iontrálann taifid iompórtáilte an stáitsiú leis na ceanglais meiteashonraí céanna le taifid scanta: sainaithint an chórais foinse, ID bhaisc, agus stampa ama. Ba cheart go ndíreodh mapálacha colúin ar réimsí atá sainmhínithe in vn_data_law_data_inventory_register.',
            lineage_full_path_info: 'Rianaíonn lineage aicmithe an chonair iomlán: scanadh/allmhairiú -> stáitsiú -> athbhreithniú úinéara -> lipéad ceadaithe -> iontráil clár (UC-VN-10 céimeanna 1-10). Taispeánann gach céim taifead ID, gníomhaí, stampa ama, agus trasdul stáit le haghaidh inrianaitheachta iniúchta iomlán.',
            staging_workflow_info: 'Déantar taifid chéimnithe a idirdhealú ó thaobh amhairc ó iontrálacha fardail fhaofa ag a suaitheantas stádais ardú céime (UC-VN-10 céim 3). Coinníonn gach taifead meiteashonraí tionscnaimh: córas foinse, scanadh bhaisc aitheantais, agus stampa ama fionnachtana le haghaidh inrianaitheachta iniúchta iomlán.',
            suggestion_data_d1_info: 'Ailíníonn moltaí leis an nós imeachta um aicmiú Dlí Sonraí DATA-D1: (1) sócmhainní sonraí catalóg, (2) aicmiú de réir leibhéal íogaireachta, (3) sainmhínítear rialacha láimhseála. Áiríonn gach moladh toisí íogaireachta agus tábhachta de réir riachtanas 18e4bfd1.',
            downstream_flags_info: 'Míreanna ar a bhfuil bratach le haghaidh comhroinnte, aistrithe trasteorann, nó sreafaí oibre iartheachtach nasctha le truicear cóireála ardriosca: measúnú aistrithe trasteorann, measúnú riosca a roinnt, agus DPIA athbhreithniú (UC-VN-10 céim 8).',
            delta_detection_info: 'Tacaíonn timthriallta fionnachtana le braite deilte in aghaidh na UC-VN-10 céim 11: taispeánann gach timthriall cé na taifid atá nua, athraithe nó bainte ón mbunlíne cheadaithe dheireanach. Spreagann athruithe ábhair (>10% deilt) sreafaí oibre athbhreithnithe go huathoibríoch.',
        },
    },
    incident_ops: {
        errors: {
            fetch: 'Theip ar lódáil.',
            save: 'Theip ar shábháil.',
            fetch_escalation_log_failed: 'Theip ar aisghabháil an logála géaraithe.',
            fetch_activations_failed: 'Theip ar aisghabháil na ngníomhachtuithe.',
            deactivate_failed: 'Theip ar an díghníomhachtú.',
            complete_activation_failed: 'Theip ar chríochnú an ghníomhachtaithe.',
            fetch_playbook_templates_failed: 'Theip ar aisghabháil na dteimpléad leabhar imeartha.',
            create_playbook_template_failed: 'Theip ar chruthú an teimpléid leabhar imeartha.',
            clone_template_failed: 'Theip ar chlónáil an teimpléid.',
            update_status_failed: 'Theip ar nuashonrú an stádais.',
            deprecate_template_failed: 'Theip ar dhímholadh an teimpléid.',
            seed_defaults_failed: 'Theip ar shíolú na réamhshocruithe.',
            fetch_regulator_exports_failed: 'Theip ar aisghabháil easpórtálacha an rialtóra.',
            create_regulator_export_failed: 'Theip ar chruthú easpórtáil rialtóra.',
            update_submission_status_failed: 'Theip ar nuashonrú stádas na haighneachta.',
            fetch_response_metrics_failed: 'Theip ar aisghabháil na méadrachtaí freagartha.',
            fetch_sla_dashboard_failed: 'Theip ar aisghabháil an phainéil SLA.',
            fetch_sla_definitions_failed: 'Theip ar aisghabháil na sainmhínithe SLA.',
            create_sla_definition_failed: 'Theip ar chruthú an tsainmhínithe SLA.',
            seed_sla_defaults_failed: 'Theip ar shíolú réamhshocruithe SLA.',
            fetch_vendor_coordination_failed: 'Theip ar aisghabháil chomhordú na soláthraithe.',
            fetch_vendor_detail_failed: 'Theip ar aisghabháil shonraí an tsoláthraí.',
            create_vendor_coordination_failed: 'Theip ar chruthú comhordú soláthraí.',
            add_communication_failed: 'Theip ar chur cumarsáide leis.',
            add_info_request_failed: 'Theip ar chur iarraidh eolais leis.',
            add_action_request_failed: 'Theip ar chur iarraidh gnímh leis.',
        },
        nav: {
            playbooks: 'Teimpléid Leabhar Imeartha',
            sla_definitions: 'Sainmhínithe SLA',
            sla_dashboard: 'Painéal SLA',
            escalation_log: 'Logáil Géaraithe',
            regulator_exports: 'Easpórtálacha Rialtóra',
            deadlines: 'Spriocdhátaí Fógraíochta',
            vendor_coordination: 'Comhordú Soláthraithe',
            metrics: 'Méadrachtaí Freagartha',
            playbook_links: 'Naisc Leabhar Imeartha',
            regulatory_sla: 'SLA Rialála',
            coordination_dashboard: 'Comhordú',
            evidence_chain: 'Slabhra Fianaise',
            post_review: 'Athbhreithniú Iar-Theagmhais',
        },
        playbooks: {
            page_title: 'Teimpléid Leabhar Imeartha',
            activations_page_title: 'Gníomhachtuithe Leabhar Imeartha',
            title: 'Teimpléid Leabhar Imeartha',
            new: 'Teimpléad Nua',
            seed_defaults: 'Síolaigh Réamhshocruithe',
            scenario_type: 'Cineál Cáis',
            severity_applicability: 'Infheidhmeacht Déine',
            all_scenario_types: 'Gach Cineál Cáis',
            new_playbook: 'Teimpléad Leabhar Imeartha Nua',
            clone: 'Clónáil',
            clone_template: 'Clónáil Teimpléad',
            template_name: 'Ainm an teimpléid',
            deprecate: 'Dímhol',
            confirm_deprecate: 'Dímhol an teimpléad "{name}"?',
            create_template: 'Cruthaigh Teimpléad',
            update_status: 'Nuashonraigh Stádas',
            no_templates: 'Níor aimsíodh aon teimpléad leabhar imeartha',
            deactivation_reason_placeholder: 'Cúis leis an díghníomhachtú...',
            description_placeholder: 'Cur síos ar an leabhar imeartha...',
            name_placeholder: 'Ainm an leabhair imeartha...',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'Sainmhínithe SLA',
            title: 'Sainmhínithe SLA',
            new: 'Sainmhíniú SLA Nua',
            sla_name: 'Ainm SLA',
            target_hours: 'Sprioc-Uaireanta',
            escalation_after: 'Géarú Tar Éis',
            applies_to: 'Bainfidh le',
            no_definitions: 'Níor aimsíodh aon sainmhíniú SLA.',
            target_value_placeholder: 'Sprioc-luach',
            name_placeholder: 'Ainm an SLA...',
            threshold_hours_label: 'Uaireanta tairsí',
            severity_label: 'Déine',
            all_types: 'Gach cineál',
            all_severities: 'Gach déine',
            default_label: 'Réamhshocrú',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Comhlíontach',
            page_title: 'Painéal SLA',
            title: 'Painéal SLA',
            loading: 'Sonraí SLA á lódáil...',
            compliance_rate: 'Ráta Comhlíonta',
            breached: 'Sáraithe',
            at_risk: 'I mBaol',
            on_track: 'Ar Bharr a Mhaitheasa',
            by_severity: 'De Réir Déine',
            total_records: 'Iomlán na dTaifead',
            met: 'Comhlíonta',
            sla_records: 'Taifid SLA',
            sla_reference: 'Tagairt SLA',
            record_id: 'Aitheantas an Taifid',
            metric_type: 'Cineál Méadraigh',
            severity: 'Déine',
            elapsed: 'Caite',
            clock_start: 'Tús an Chloig',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: 'Logáil Géaraithe',
            title: 'Logáil Géaraithe',
            incident_id: 'Aitheantas an Teagmhais',
            escalated_by: 'Géaraithe ag',
            escalated_to: 'Géaraithe chuig',
            reason: 'Cúis',
            escalated_at: 'Géaraithe ag',
            no_escalations: 'Níor aimsíodh aon ghéarú.',
            filter_incident_id: 'Scag de réir Aitheantas Teagmhais',
            escalation_id: 'Aitheantas an Ghéaraithe',
            level: 'Leibhéal',
            level_range: 'Leibhéal (L1–L4)',
            create_escalation: 'Cruthaigh Géarú',
            incident_id_placeholder: 'm.sh., INC-001',
            target_role_placeholder: 'm.sh., dpo, incident_manager',
            select_placeholder: '— roghnaigh —',
        },
        regulator_exports: {
            page_title: 'Easpórtálacha Rialtóra',
            title: 'Easpórtálacha Rialtóra',
            new: 'Easpórtáil Nua',
            incident_id: 'Aitheantas an Teagmhais',
            regulator: 'Rialtóir',
            export_format: 'Formáid',
            exported_at: 'Easpórtáilte ag',
            no_exports: 'Níor aimsíodh aon easpórtáil rialtóra.',
            enter_incident_id: 'Iontráil Aitheantas an Teagmhais',
            export_id: 'Aitheantas na hEaspórtála',
            watermark: 'Comhartha Uisce',
            submission_status: 'Stádas na hAighneachta',
            all_jurisdictions: 'Gach Dlínse',
            no_transitions: 'Níl aon aistriú breise ar fáil ó',
            select_new_status: '— roghnaigh stádas nua —',
            sections_included: 'Rannáin san Áireamh',
            form_classification: 'Aicmiú',
            submission_channel: 'Cainéal',
        },
        deadlines: {
            title: 'Spriocdhátaí Fógraíochta',
            incident_id: 'Aitheantas an Teagmhais',
            deadline: 'Spriocdháta',
            regulator: 'Rialtóir',
            remaining: 'Fágtha',
            no_deadlines: 'Níor aimsíodh aon spriocdháta fógraíochta.',
        },
        vendor_coordination: {
            page_title: 'Comhordú Soláthraithe',
            title: 'Comhordú Soláthraithe',
            new: 'Comhordú Nua',
            incident_id: 'Aitheantas an Teagmhais',
            vendor_name: 'Ainm an tSoláthraí',
            contact: 'Teagmháil',
            coordination_status: 'Stádas an Chomhordaithe',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'Níor aimsíodh aon mhír chomhordú soláthraithe.',
            assessment_placeholder: 'Réamhmheasúnú ón soláthraí...',
            coordination_id: 'Aitheantas an Chomhordaithe',
            communications: 'Cumarsáidí',
            info_requests: 'Iarrataí Eolais',
            action_requests: 'Iarrataí Gnímh',
            direction: 'Treo',
            channel_label: 'Cainéal',
            no_transitions: 'Níl aon aistriú breise ar fáil.',
            vendor_ref_number: 'Uimhir Thagartha',
            report_date: 'Dáta Tuairiscithe',
            contact_email: 'Ríomhphost',
            contact_phone: 'Fón',
            channel_email: 'Ríomhphost',
            channel_meeting: 'Cruinniú',
            channel_phone: 'Fón',
            channel_portal: 'Tairseach',
            incident_id_placeholder: 'Scag de réir Aitheantas Teagmhais...',
            priority_critical: 'Criticiúil',
            priority_high: 'Ard',
            priority_low: 'Íseal',
            priority_medium: 'Meánach',
            vendor_id_placeholder: 'Scag de réir Aitheantas Soláthraí...',
            description_placeholder: 'Cur síos...',
            filter_vendor_id: 'Scag de réir Aitheantas Soláthraí',
            inbound: 'Isteach',
            outbound: 'Amach',
            summary_placeholder: 'Achoimre...',
            priority_label: 'Tosaíocht',
            due_date_label: 'Spriocdháta',
            info_request_desc: 'Cur síos ar iarraidh eolais',
            action_request_desc: 'Cur síos ar iarraidh gnímh',
            content_label: 'Inneachar',
            select_incident: 'Roghnaigh Teagmhas',
            select_vendor: 'Roghnaigh Soláthraí',
            discovery_date: 'Discovery Dáta',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Gníomhaí',
        },
        metrics: {
            page_title: 'Méadrachtaí Freagartha',
            title: 'Méadrachtaí Freagartha',
            loading: 'Sonraí na méadrachtaí á lódáil...',
            avg_response_time: 'Meán-Am Freagartha',
            avg_resolution_time: 'Meán-Am Réitigh',
            total_incidents: 'Iomlán na dTeagmhas',
            by_severity: 'De Réir Déine',
            by_status: 'De Réir Stádais',
            trend: 'Treocht',
            start_sla_clock: 'Tosaigh Clog SLA',
            sla_records: 'Taifid SLA',
            no_sla_records: 'Níl aon taifead SLA fós. Cliceáil "Tosaigh Clog SLA" chun ceann a chruthú.',
            record_id: 'Aitheantas an Taifid',
            severity: 'Déine',
            progress: 'Dul Chun Cinn',
            incident_id_required: 'Tá Aitheantas an Teagmhais riachtanach',
            sla_definition_required: 'Tá Sainmhíniú SLA riachtanach',
            metric_type_required: 'Tá cineál an mhéadraigh riachtanach',
            threshold_required: 'Tá uaireanta tairsí riachtanach',
            sla_clock_started: 'Tá clog SLA tosaithe',
            sla_definition: 'Sainmhíniú SLA',
            no_definitions: 'Níor aimsíodh aon sainmhíniú SLA. Cruthaigh ceann faoi Sainmhínithe SLA ar dtús.',
            select_definition: 'Roghnaigh sainmhíniú SLA…',
            select_metric_type: 'Roghnaigh cineál méadraigh…',
            select_severity: 'Roghnaigh déine…',
            threshold_hours: 'Tairseach (uaireanta)',
            warning_threshold_pct: 'Tairseach rabhaidh (%)',
            start_clock: 'Tosaigh Clog',
            starting: 'Á thosú…',
            metric_label: 'Méadrach',
            value_label: 'Luach',
            incident_id: 'Aitheantas an Teagmhais',
            threshold_placeholder: 'm.sh., 24',
            metric_types: {
                triage_time: 'Am Tríáise',
                containment_time: 'Am Srianta',
                notification_time: 'Am Fógartha',
                resolution_time: 'Am Réitigh',
            },
            severities: {
                critical: 'Criticiúil',
                high: 'Ard',
                medium: 'Meánach',
                low: 'Íseal',
            },
            sla_reference: 'Tagairt SLA',
        },
        playbook_links: {
            page_title: 'Naisc Leabhar Imeartha',
            title: 'Naisc Fógra Leabhar Imeartha',
            new_link: 'Nasc Leabhar Imeartha le Fógra',
            activation_id: 'Aitheantas an Ghníomhachtaithe',
            notification_id: 'Aitheantas an Fhógra',
            select_activation: 'Roghnaigh gníomhachtú...',
            select_notification: 'Roghnaigh fógra...',
            no_links: 'Níor aimsíodh aon nasc leabhar imeartha',
            seed_pdpl: 'Cuir naisc leabhar imeartha teimpléid leis',
            created: 'Cruthaíodh nasc leabhar imeartha',
            seeded: 'Cuireadh naisc leabhar imeartha teimpléid leis',
            create_failed: 'Theip ar chruthú nasc leabhar imeartha',
            seed_failed: 'Theip ar naisc leabhar imeartha teimpléid a chur leis',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: 'Ní rialaíonn aon chreat suiteáilte freagairt teagmhais, mar sin níl aon spriocdhátaí le cur leis.',
                declared_but_none_active: 'Tá creat a rialaíonn freagairt teagmhais suiteáilte ach níl sé i bhfeidhm.',
                no_declared_deadlines: 'Ní shonraíonn na creataí freagartha teagmhais suiteáilte aon spriocdhátaí fógartha.',
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Spriocdháta',
            deadline_units: {
                hours: '{count} uair an chloig',
                working_days: '{count} lá oibre',
                calendar_days: '{count} lá féilire',
                days: '{count} lá',
                weeks: '{count} seachtain',
                months: '{count} mí',
                years: '{count} bliain',
            },
            deadline_unit_unknown: '{count} {unit}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Mapálacha SLA Rialála',
            title: 'Mapálacha SLA Rialála',
            new_mapping: 'Mapáil SLA Nua',
            sla_definition: 'Sainmhíniú SLA',
            select_sla_definition: 'Roghnaigh sainmhíniú SLA...',
            framework_code: 'Cód an Chreata',
            deadline_type: 'Cineál an Spriocdháta',
            deadline_hours: 'Spriocdháta in Uaireanta',
            seed_pdpl: 'Cuir mapálacha ó spriocdhátaí suiteáilte leis',
            no_mappings: 'Níor aimsíodh aon mhapáil SLA rialála',
            dashboard: 'Painéal SLA Rialála',
            deadline_types: {
                initial_notification: 'Fógra Tosaigh',
                supplement_response: 'Freagra Forlíontach',
                authority_response: 'Freagra an Údaráis',
                remediation_report: 'Tuarascáil Feabhsúcháin',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Tagairt Reachtúil',
        },
        coordination_dashboard: {
            page_title: 'Painéal Comhordaithe',
            title: 'Painéal Comhordaithe',
            incident_id: 'Aitheantas an Teagmhais',
            select_incident: 'Roghnaigh teagmhas...',
            team_role: 'Ról na Foirne',
            members: 'Baill',
            tasks: 'Tascanna',
            sla_status: 'Stádas SLA',
            no_teams: 'Níor aimsíodh aon fhoireann chomhordaithe',
            new_team: 'Foireann Chomhordaithe Nua',
            enter_incident_id: 'Cuir isteach Aitheantas an Teagmhais chun foirne comhordaithe a fheiceáil.',
            member_ids_placeholder: 'úsáideoir1, úsáideoir2',
            edit_team: 'Cuir Foireann in Eagar',
            task_count: 'Comhaireamh Tasc',
            tasks_completed: 'Críochnaithe',
            team_roles: {
                security: 'Slándáil',
                legal: 'Dlí',
                it: 'TF',
                management: 'Bainistíocht',
                department_owner: 'Úinéir na Roinne',
                external: 'Seachtrach',
            },
        },
        evidence_chain: {
            page_title: 'Slabhra Fianaise',
            title: 'Slabhra Coimeádta Fianaise',
            new_entry: 'Iontráil Fianaise Nua',
            select_incident: 'Roghnaigh teagmhas...',
            actor: 'Gníomhaí',
            select_actor: 'Roghnaigh gníomhaí...',
            evidence_id: 'Aitheantas Fianaise',
            action: 'Gníomh',
            recipient: 'Faighteoir',
            no_entries: 'Níor aimsíodh aon iontráil sa slabhra fianaise',
            enter_incident_id: 'Cuir isteach Aitheantas an Teagmhais chun an slabhra fianaise a fheiceáil.',
            evidence_id_placeholder: 'm.sh. EVD-2026-0001',
            evidence_id_help: 'Tagairt uathúil don mhír fianaise — comhad logála, íomhá diosca nó gabháil scáileáin. Tá moladh réamhlíonta; cuir in eagar é chun teacht le do chatalóg fianaise.',
            select_incident_first: 'Roghnaigh teagmhas thuas sula gcuireann tú iontráil fianaise leis.',
            for_incident: 'Don teagmhas',
            actions: {
                collected: 'Bailithe',
                shared: 'Roinnte',
                exported: 'Easpórtáilte',
                archived: 'Cartlannaithe',
            },
        },
        post_review: {
            page_title: 'Athbhreithniú Iar-Theagmhais',
            title: 'Athbhreithniú Iar-Theagmhais',
            new_review: 'Athbhreithniú Nua',
            select_incident: 'Roghnaigh teagmhas...',
            lessons_learned: 'Ceachtanna Foghlamtha',
            recommendations: 'Moltaí',
            improvement_actions: 'Gníomhartha Feabhsúcháin',
            submit_for_review: 'Cuir isteach lena Athbhreithniú',
            aria_submit_review: 'Cuir athbhreithniú {name} isteach lena athbhreithniú',
            aria_sign_off: 'Formheas athbhreithniú {name}',
            sign_off: 'Formheas',
            no_reviews: 'Níor aimsíodh aon athbhreithniú iar-theagmhais',
            enter_incident_id: 'Cuir isteach Aitheantas an Teagmhais chun an t-athbhreithniú iar-theagmhais a fheiceáil.',
            one_per_line: 'Ceann in aghaidh na líne',
            statuses: {
                draft: 'Dréacht',
                in_review: 'Á Athbhreithniú',
                signed_off: 'Formheasta',
                closed: 'Dúnta',
            },
        },
        activations: {
            title: 'Gníomhachtuithe Leabhar Imeartha',
            activation_id: 'Aitheantas an Ghníomhachtaithe',
            phases: 'Céimeanna',
            activated_at: 'Gníomhachtaithe ag',
            activated_by: 'Gníomhachtaithe ag',
            deactivate: 'Díghníomhachtaigh',
            complete: 'Críochnaigh',
            deactivation_reason: 'Cúis an Díghníomhachtaithe',
            evidence_button: 'Fianaise',
            evidence_modal_title: 'Fianaise Ghníomhachtaithe',
            no_evidence: 'Níl aon fhianaise ceangailte leis an ngníomhachtú seo.',
            upload_evidence_label: 'Uaslódáil fianaise ghníomhachtaithe',
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        maturity_walkthrough: {
            run_label: 'Siúil tríd bearnaí mé',
            running: 'Bearnaí siúil…',
            results_heading: 'Treochlár feabhsúcháin ({count} bearna{plural})',
            tokens_label: '≈ {count} comharthaí',
            no_gaps: 'Níor aimsíodh aon bhearnaí — gach fearann ag an sprioc nó os a chionn.',
            col_priority: 'Tosaíocht',
            col_domain: 'Fearann',
            col_levels: 'Reatha → Sprioc',
            col_severity: 'Déine',
            col_actions: 'Gníomhartha leasúcháin',
            citations_label: 'Comhlua',
            accept_label: 'Glac le treochlár',
            reject_label: 'Diúltaigh dréacht',
            export_label: 'Easpórtáil DOCX',
            exported_label: 'Easpórtáladh treochlár',
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Taifeach: {status}',
            resolve_status_accepted: 'Glactha',
            resolve_status_rejected: 'Diúltaithe',
            error_generic: 'Níorbh fhéidir siúl tríd an mbearna a rith. Bain triail eile as nó seiceáil an stádas measúnaithe.',
        },
        policy_gap: {
            run_label: 'Braith bearnaí polasaí',
            running: 'Bearnaí á bhrath…',
            results_heading: 'Bearnaí beartais ({count})',
            tokens_label: '≈ {count} comharthaí',
            no_gaps: 'Níor aimsíodh aon bhearnaí clúdaigh.',
            col_severity: 'Déine',
            col_kind: 'cineál',
            col_clause: 'Clásal/riachtanas',
            kind_missing: 'Clásal ar iarraidh',
            kind_weak: 'Clúdach lag',
            kind_stale: 'Stale (athbhreithniú thar téarma)',
            remediation_label: 'Leigheas molta',
            citations_label: 'Comhlua',
            accept_label: 'Glac le tuarascáil bearna',
            reject_label: 'Diúltú don tuarascáil',
            export_label: 'Easpórtáil DOCX',
            accept_hint: 'Glac le taifid an tuairisc bhearna ar an dréacht fós. Úsáid Easpórtáil DOCX chun Tuarascáil Iniúchta Beartais a ghiniúint; thiomáineann an t-oibreoir an t-eagar beartais iarbhír.',
            resolve_summary: 'Taifeach: {status}',
            error_generic: 'Níorbh fhéidir bearnaí beartais a bhrath. Bain triail eile as nó seiceáil stádas an doiciméid.',
        },
        remediation_plan_generator: {
            run_label: 'Gin plean ó bhearnaí',
            running: 'Plean á ghiniúint…',
            results_heading: 'Plean feabhsúcháin ({phase_count} chéim{plural})',
            tokens_label: '≈ {count} comharthaí',
            phase_label: 'Céim {index}',
            window_label: 'Fuinneog',
            owner_label: 'Ról úinéara',
            evidence_label: 'Cineálacha fianaise a theastaíonn',
            tasks_label: 'Tascanna',
            no_phases: 'Gan aon phasanna a tháirgeadh — féadfaidh tacar bearna ionchuir a bheith folamh.',
            citations_label: 'Comhlua',
            accept_label: 'Glac leis an bplean',
            reject_label: 'Diúltú plean',
            export_label: 'Easpórtáil DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Taifeach: {status} · {tasks} tasc(eanna) cruthaithe',
            error_generic: 'Níorbh fhéidir plean leasúcháin a ghiniúint. Bain triail eile as nó seiceáil an t-ionchur socraithe bearna.',
            source_picker_label: 'Foinse',
            source_all: 'Gach',
            source_consent: 'Anailís bearna toilithe',
            source_policy: 'Brath bearna beartais',
            source_vendor: 'Díoltóir ar bord',
            load_upstream_label: 'Íosluchtaigh ó in aghaidh srutha',
            loading_upstream: 'Á lódáil…',
            upstream_count: '{count} bearna(í) luchtaithe',
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Samhlacha aibíochta',
            maturity_assessments: 'Measúnuithe aibíochta',
            trend_dashboard: 'Painéal treochtaí',
            reporting_periods: 'Tréimhsí tuairiscithe',
            executive_summaries: 'Achoimrí feidhmiúcháin',
            audit_readiness: 'Ullmhacht iniúchta',
            evidence_gaps: 'Bearnaí fianaise',
            program_health: 'Sláinte an chláir',
            obligation_mappings: 'Mapálacha Oibleagáidí',
            governance_documents: 'Doiciméid Rialachais',
            remediation_plans: 'Pleananna Feabhsúcháin',
            governance_model: 'Múnla rialachais',
            document_drafting: 'Dréachtú doiciméad',
            document_issuance: 'Eisiúint doiciméad',
            acknowledgment_tracking: 'Rianú admhála',
            change_log: 'Loga athruithe',
            review_cadence: 'Cadans athbhreithnithe',
        },
        maturity_models: {
            title: 'Samhlacha aibíochta',
            new: 'Nua Model',
            seed_defaults: 'Síolaigh Réamhshocrú',
            domains: 'Fearainn',
            activate: 'Gníomhachtaigh',
            no_models: 'Níl aon maturity models aimsithe',
            new_maturity_model: 'Nua Aibíocht Model',
            create_model: 'Cruthaigh Model',
            load_error: 'Níorbh fhéidir samhlacha aibíochta a lódáil. Bain triail eile as.',
            create_error: 'Níorbh fhéidir samhail aibíochta a chruthú',
            status_error: 'Níorbh fhéidir stádas an tsamhail a nuashonrú',
            seed_error: 'Níorbh fhéidir luachanna réamhshocraithe a ghiniúint',
            aria_seed_defaults: 'Gin samhlacha aibíochta réamhshocraithe',
            aria_create_new: 'Cruthaigh samhail aibíochta nua',
            loading_sr: 'Samhlacha aibíochta á lódáil',
            model_name_placeholder: 'm.sh., Samhail aibíochta príobháideachais',
            status_changed: 'Athraíodh stádas an tsamhail go {status}',
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Measúnuithe aibíochta',
            new: 'Nua Measúnú',
            model: 'Múnla',
            assessor: 'Measúnóir',
            overall_score: 'Scór Foriomlán',
            overall_score_help: 'Rátáil aibíochta ualaithe ó 0–5, a ríomhtar go huathoibríoch nuair a chuirtear measúnú i gcrích. Méadaigh é trí scór níos airde a thabhairt do gach fearann le linn na meastóireachta, agus an measúnú a bhogadh ansin trí Cuir isteach lena athbhreithniú → Críochnaigh.',
            assessed_at: 'Measúnaithe',
            no_assessments: 'Níl aon maturity measúnú found. aimsithe',
            load_error: 'Níorbh fhéidir measúnuithe aibíochta a lódáil. Bain triail eile as.',
            create_error: 'Níorbh fhéidir measúnú a chruthú',
            start_error: 'Níorbh fhéidir measúnú a thosú',
            archive_error: 'Níorbh fhéidir measúnú a chartlannú',
            eval_error: 'Níorbh fhéidir measúnú a lódáil le meas',
            eval_min_score: 'Scóráil fearann amháin ar a laghad',
            eval_save_error: 'Níorbh fhéidir measúnuithe fearainn a shábháil',
            review_error: 'Níorbh fhéidir cur isteach lena athbhreithniú',
            finalize_error: 'Níorbh fhéidir an measúnú a chríochnú',
            start: 'Tosaigh',
            start_confirm_title: 'Tosaigh measúnú',
            start_confirm_message: 'An bhfuil tú cinnte gur mhaith leat an measúnú seo a thosú?',
            archive_confirm_title: 'Cartlannaigh measúnú',
            archive_confirm_message: 'An bhfuil tú cinnte gur mhaith leat an measúnú seo a chartlannú?',
            evaluate_domains: 'Meas',
            evaluate_domains_title: 'Meas fearainn',
            submit_for_review: 'Cuir isteach lena athbhreithniú',
            finalize: 'Críochnaigh',
            archive: 'Cartlannaigh',
            all_reviewers: 'Ní mór do gach athbhreithneoir ceadú',
            any_reviewer: 'Is féidir le haon athbhreithneoir ceadú',
            select_level: 'Roghnaigh leibhéal aibíochta...',
            findings_placeholder: 'Torthaí, nótaí, tagairtí fianaise...',
            no_domains: 'Níor aimsíodh aon fhearann don tsamhail seo.',
        },
        trend_dashboard: {
            title: 'Painéal treochtaí',
            loading: 'Ag lódáil trend sonraí...',
            score_trend: 'Scór Treocht',
            domain_comparison: 'Fearann Comparison',
            period: 'Tréimhse',
            load_error: 'Níorbh fhéidir achoimre treochtaí a lódáil. Bain triail eile as.',
            metrics_load_error: 'Níorbh fhéidir méadraigh treochtaí a lódáil. Bain triail eile as.',
            module: 'Modúl',
            metric: 'Méadrach',
            value: 'Luach',
            trend: 'Treocht',
            aria_filters: 'Scagairí méadraigh treochtaí',
            showing_chart: 'Amharc cairte á thaispeáint',
            showing_table: 'Amharc tábla sonraí',
            create_metric: 'Cruthaigh méadracht',
            data_points: 'Pointí sonraí',
            trend_direction: 'Treo na treochta',
            unit: 'Aonad',
        },
        reporting_periods: {
            title: 'Tréimhsí tuairiscithe',
            new: 'Nua Tréimhse',
            period_name: 'Tréimhse Ainm',
            start_date: 'Dáta Tosaigh',
            end_date: 'Dáta Deiridh',
            no_periods: 'Níl aon reporting periods found. aimsithe',
            load_error: 'Níorbh fhéidir tréimhsí tuairiscithe a lódáil. Bain triail eile as.',
            create_error: 'Níorbh fhéidir tréimhse a chruthú',
            auto_generate_error: 'Níorbh fhéidir tréimhsí a ghiniúint go huathoibríoch',
            status_error: 'Níorbh fhéidir stádas na tréimhse a nuashonrú',
            start_collecting: 'Tosaigh ag bailiú',
            mark_as_draft: 'Marcáil mar dhréacht',
            submit_for_review: 'Cuir isteach lena athbhreithniú',
            publish: 'Foilsigh',
            auto_generate: 'Gin tréimhsí tuairiscithe go huathoibríoch',
            period_label_placeholder: 'm.sh., R1 2026',
            status_updated: 'Stádas na tréimhse nuashonraithe',
        },
        executive_summaries: {
            title: 'Achoimrí feidhmiúcháin',
            new: 'Nua Achoimre',
            period: 'Tréimhse',
            generated_at: 'Ginte ag',
            generated_by: 'Ginte ag',
            no_summaries: 'Níl aon executive summaries found. aimsithe',
            generate: 'Gin',
            load_error: 'Níorbh fhéidir achoimrí feidhmiúcháin a lódáil. Bain triail eile as.',
            generate_error: 'Níorbh fhéidir achoimre feidhmiúcháin a ghiniúint',
            save_error: 'Níorbh fhéidir achoimre feidhmiúcháin a shábháil',
        },
        audit_readiness: {
            title: 'Ullmhacht iniúchta',
            loading: 'Ag lódáil iniúchadh readiness sonraí...',
            overall_readiness: 'Ullmhacht Fhoriomlán',
            by_domain: 'By Fearann',
            evidence_coverage: 'Fianaise Clúdach',
            gaps: 'Bearnaí',
            load_error: 'Níorbh fhéidir an ullmhacht is déanaí a lódáil. Bain triail eile as.',
            history_error: 'Níorbh fhéidir stair ullmhachta a lódáil. Bain triail eile as.',
            calculate_error: 'Níorbh fhéidir ullmhacht iniúchta a ríomh',
            aria_recalculate: 'Athríomh scór ullmhachta iniúchta',
            aria_current_readiness: 'Ullmhacht iniúchta reatha',
            aria_history: 'Stair scóir ullmhachta',
            recalculated: 'Ullmhacht iniúchta athríofa',
            loading_sr: 'Sonraí ullmhachta iniúchta á lódáil',
        },
        evidence_gaps: {
            title: 'Bearnaí fianaise',
            domain: 'Fearann',
            control: 'Rialú',
            gap_type: 'Cineál Bearna',
            severity: 'Déine',
            no_gaps: 'Níl aon fianaise bearna found. aimsithe',
            load_error: 'Níorbh fhéidir bearnaí fianaise a lódáil. Bain triail eile as.',
            stats_error: 'Níorbh fhéidir staitisticí bearna a lódáil. Bain triail eile as.',
            create_error: 'Níorbh fhéidir bearna fianaise a chruthú',
            update_error: 'Níorbh fhéidir bearna fianaise a nuashonrú',
            status_error: 'Níorbh fhéidir an stádas a nuashonrú',
            aria_create_new: 'Cruthaigh bearna fianaise nua',
            aria_statistics: 'Staitisticí bearnaí fianaise',
            loading_sr: 'Bearnaí fianaise á lódáil',
            status_updated: 'Stádas na bearna nuashonraithe',
            entity_type_placeholder: 'm.sh., soláthraí, córas',
            all_modules: 'Gach fearann',
            all_severities: 'Gach déine',
        },
        program_health: {
            title: 'Sláinte an chláir',
            loading: 'Ag lódáil program health sonraí...',
            overall_health: 'Overall Sláinte',
            by_module: 'By Modúl',
            risk_areas: 'Riosca Areas',
            recommendations: 'Moltaí',
            load_error: 'Níorbh fhéidir sonraí sláinte cláir a lódáil. Bain triail eile as.',
            aria_key_metrics: 'Méadraigh eochair sláinte cláir',
            aria_module_scorecard: 'Miondealú cárta scóir modúil',
            loading_sr: 'Sonraí sláinte cláir á lódáil',
            trending_up: 'Treocht ar ardú',
            trending_down: 'Treocht ar laghdú',
            trending_stable: 'Cobhsaí',
            data_loaded: 'Sonraí sláinte cláir lódáilte',
        },
        obligation_mappings: {
            title: 'Mapálacha Oibleagáidí',
            create_mapping: 'Cruthaigh mapáil oibleagáidí',
            framework: 'Creatlach',
            select_framework: 'Roghnaigh creat...',
            obligation: 'Oibleagáid',
            obligation_ref: 'Tagairt oibleagáide',
            obligation_description: 'Cur síos ar oibleagáid',
            role: 'Ról',
            assigned_role: 'Ról sannta',
            select_role: 'Roghnaigh ról...',
            assigned_department: 'Roinn sannta',
            select_department: 'Roghnaigh roinn...',
            control_ids: 'IDanna rialaithe',
            control_ids_placeholder: 'm.sh. A.5.1, CC6.1',
            control_ids_hint: 'Cóid tagartha rialaithe scartha le camóga.',
            raci_assignments: 'Sannacháin RACI',
            create_raci: 'Cruthaigh sannachán RACI',
            activity: 'Gníomhaíocht',
            activity_name: 'Ainm na gníomhaíochta',
            activity_description: 'Cur síos ar ghníomhaíocht',
            responsible: 'Freagrach',
            select_users: 'Roghnaigh úsáideoirí...',
            accountable: 'Cuntasach',
            select_user: 'Roghnaigh úsáideoir...',
            consulted: 'Comhairlithe',
            informed: 'Curtha ar an eolas',
            review_frequency: 'Minicíocht athbhreithnithe',
            frequency_monthly: 'Míosúil',
            frequency_quarterly: 'Ráithiúil',
            frequency_semi_annual: 'Leathbhliantúil',
            frequency_annual: 'Bliantúil',
            no_mappings_found: 'Níor aimsíodh mapálacha oibleagáidí.',
            no_raci_found: 'Níor aimsíodh sannacháin RACI.',
            mapping_created: 'Mapáil oibleagáide cruthaithe',
            mapping_updated: 'Mapáil oibleagáide nuashonraithe',
            mapping_deleted: 'Mapáil oibleagáide scriosta',
            raci_created: 'Sannadh RACI cruthaithe',
            raci_updated: 'Sannadh RACI nuashonraithe',
            raci_deleted: 'Sannadh RACI scriosta',
            aria_edit_mapping: 'Cuir mapáil {name} in eagar',
            aria_delete_mapping: 'Scrios mapáil {name}',
            aria_edit_raci: 'Cuir RACI {name} in eagar',
            aria_delete_raci: 'Scrios RACI {name}',
            confirm_delete_mapping: 'An mapáil oibleagáide seo a scriosadh?',
            confirm_delete_raci: 'An sannadh RACI seo a scriosadh?',
            table_view: 'Amharc tábla',
            matrix_view: 'Amharc maitrís',
            print_export: 'Priontáil / Easpórtáil',
            filter_framework: 'Scag de réir creata',
            no_matrix_data: 'Níl aon sannacháin RACI le taispeáint san amharc maitrís.',
            aria_print_export: 'Priontáil nó easpórtáil an mhaitrís RACI',
        },
        governance_documents: {
            title: 'Doiciméid Rialachais',
            create_document: 'Cruthaigh doiciméad rialachais',
            document_title: 'Teideal an Doiciméid',
            document_type: 'Cineál Doiciméid',
            content_url: 'URL ábhair',
            content_url_placeholder: 'https://...',
            framework_codes: 'Cóid chreata',
            select_frameworks: 'Roghnaigh creataí...',
            effective_date: 'Dáta éifeachtach',
            review_date: 'Dáta athbhreithnithe',
            status: 'Stádas',
            version: 'Leagan',
            acknowledgments: 'Admhálacha',
            acknowledge: 'Admháil',
            acknowledged_by: 'Admhaithe ag',
            department: 'Roinn',
            no_documents_found: 'Níor aimsíodh doiciméid rialachais.',
            draft: 'Dréacht',
            review: 'Athbhreithniú',
            approved: 'Ceadaithe',
            issued: 'Eisithe',
            superseded: 'Ionadaithe',
            document_created: 'Doiciméad cruthaithe',
            document_acknowledged: 'Doiciméad aitheanta',
            policy: 'Polasaí',
            procedure: 'Nós imeachta',
            standard: 'Caighdeán',
            guideline: 'Treoirlíne',
        },
        governance_model: {
            title: 'Múnla rialachais',
            create_model: 'Múnla rialachais',
            edit_model: 'Múnla rialachais',
            model_name: 'Model',
            description: 'Cur síos',
            governance_type: 'Type',
            type_centralized: 'láraithe',
            type_federated: 'Cónaidhme',
            type_hybrid: 'hibrideach',
            framework_codes: 'Framework',
            select_frameworks: '...',
            review_frequency: 'Frequency',
            freq_monthly: 'go míosúil',
            freq_quarterly: 'Ráithiúil',
            freq_semi_annual: 'Leathbhliantúil',
            freq_annual: 'Bliantúil',
            committees: 'Coistí',
            committee_name: 'Name',
            committee_members: 'Members',
            committee_mandate: 'Sainordú',
            add_committee: '+',
            no_committees: '—',
            delegation_rules: 'Delegation',
            from_role: 'From',
            to_role: 'To',
            scope: 'Raon feidhme',
            add_delegation_rule: '+',
            control_owner_summary: 'RACI',
            no_raci_data: '—',
            activity: 'Gníomhaíocht',
            responsible: 'R',
            accountable: 'A',
            consulted: 'C',
            informed: 'I',
            no_models_found: '—',
            model_created: 'OK',
            model_updated: 'OK',
            model_deleted: 'OK',
            confirm_delete: '?',
            expand: '+',
            collapse: '-',
            aria_expand: '{name}',
            aria_edit: '{name}',
            aria_delete: '{name}',
            aria_export_csv: '{name} CSV',
            aria_export_json: '{name} JSON',
            framework_read_permission_warning: 'Teastaíonn an regulatory_framework:cead léite uait chun creataí a roghnú. Iarr ar riarthóir é a dheonú.',
            select_members: 'Baill an roghchoiste',
            select_from_role: 'Roghnaigh ról tarmligin',
            select_to_role: 'Roghnaigh ról glactha',
            mandate_markdown_hint: 'Markdown tacaithe: **trom**, liostaí, naisc.',
            scope_tab_obligation: 'Oibleagáid',
            scope_tab_framework: 'Creatlach',
            scope_tab_free_text: 'Téacs saor in aisce,',
            select_obligation: 'Roghnaigh oibleagáid',
            select_framework: 'Roghnaigh creat',
            aria_remove_committee: 'Bain coiste {n}',
            aria_remove_delegation_rule: 'Bain riail tarmligin {n}',
            no_frameworks_available: 'Níl aon chreat ar fáil. Cuir creataí rialála leis ar dtús.',
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Dréachtú doiciméad',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Stádas',
            drafter: 'Dréachtóir',
            deadline: 'Spriocdháta',
            assign_drafter: 'Assign',
            select_drafter: '...',
            assign: 'Sann',
            submit_for_review: 'Submit',
            legal_review: 'Review',
            reviewer: 'Athbhreithneoir',
            select_reviewer: '...',
            comments: 'Tuairimí',
            review_action: 'Action',
            approve: 'Cheadaigh',
            request_changes: 'Athruithe Iarratas',
            submit_review: 'Submit',
            executive_approval: 'Approval',
            no_drafts_found: '—',
            drafter_assigned: 'OK',
            status_updated: 'OK',
            review_approved: 'OK',
            changes_requested: 'OK',
            document_approved: 'OK',
            aria_assign: '{name}',
            aria_submit_review: '{name}',
            aria_review: '{name}',
            aria_approve: '{name}',
        },
        document_issuance: {
            title: 'Eisiúint doiciméad',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Stádas',
            version: 'Leagan',
            effective_date: 'Date',
            distribution_scope: 'Distribution',
            distribution_placeholder: '...',
            issuance_number: 'Number',
            issuance_number_placeholder: 'ISS-2026-001',
            supersedes: 'Supersedes',
            none: '—',
            issue_document: 'Issue',
            ready_for_issuance: 'Ready',
            issued_documents: 'Issued',
            no_issued_documents: '—',
            view_lineage: 'Lineage',
            supersession_lineage: 'Supersession',
            document_issued: 'OK',
            document_superseded: 'OK',
            aria_issue: '{name}',
            aria_view_lineage: '{name}',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Rianú admhála',
            document_title: 'Doiciméad',
            acknowledged_count: 'Admhaítear',
            pending_count: 'Ar feitheamh',
            percentage: '%',
            details: 'Sonraí',
            send_reminder: 'Remind',
            acknowledgment_details: 'Details',
            user: 'Úsáideoir',
            department: 'Roinn',
            acknowledged_at: 'Date',
            status: 'Stádas',
            acknowledged: 'OK',
            no_issued_documents: '—',
            no_acknowledgments: '—',
            reminder_sent: 'OK',
            aria_expand: '{name}',
            aria_send_reminder: '{name}',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            // English until Phase 25 (R-57).
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Loga athruithe',
            date: 'Dáta',
            document: 'Doiciméad',
            change_type: 'Type',
            actor: 'Aisteoir',
            summary: 'Achoimre',
            export_csv: 'Easpórtáil CSV',
            filters: 'Filters',
            filter_document: 'Document',
            all_documents: 'All',
            filter_change_type: 'Type',
            all_types: 'All',
            date_from: 'From',
            date_to: 'To',
            version_history: 'History',
            version_archive: 'Archive',
            no_entries: '—',
            no_history: '—',
            exported: 'OK',
            aria_export: 'CSV',
            aria_view_history: '{name}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Cadans athbhreithnithe',
            configure_cadence: 'Cadans athbhreithnithe',
            edit_cadence: 'Cadans athbhreithnithe',
            document: 'Doiciméad',
            select_document: '...',
            review_frequency: 'Frequency',
            freq_30: '30',
            freq_60: '60',
            freq_90: '90',
            freq_180: '180',
            freq_365: '365',
            last_reviewed: 'Last',
            next_review: 'Next',
            status: 'Stádas',
            overdue: 'thar téarma',
            on_track: 'OK',
            days: 'days',
            horizon: 'Horizon',
            overdue_warning: 'Overdue',
            overdue_count: '{count}',
            no_cadences: '—',
            cadence_created: 'OK',
            cadence_updated: 'OK',
            aria_edit: '{name}',
        },
        remediation_plans: {
            title: 'Pleananna Feabhsúcháin',
            create_plan: 'Cruthaigh plean feabhsúcháin',
            gap_id: 'Tagairt bearna',
            select_gap: 'Roghnaigh bearna...',
            plan_title: 'Teideal',
            description: 'Cur síos',
            priority: 'Tosaíocht',
            priority_critical: 'Criticiúil',
            priority_high: 'Ard',
            priority_medium: 'Meánach',
            priority_low: 'Íseal',
            assigned_to: 'Sannta do',
            select_user: 'Roghnaigh úsáideoir...',
            target_date: 'Sprioc-dáta',
            framework_code: 'Creat',
            select_framework: 'Roghnaigh creat...',
            gap_finding: 'Aimsiú bearna',
            action_items: 'Míreanna gníomhaíochta',
            owner: 'Úinéir',
            deadline: 'Spriocdháta',
            planned: 'Beartaithe',
            in_progress: 'Ar siúl',
            completed: 'Críochnaithe',
            verified: 'Fíoraithe',
            no_plans_found: 'Níor aimsíodh pleananna feabhsúcháin.',
            evidence: 'Fianaise',
            plan_created: 'Plean feabhais cruthaithe',
            action_items_hint: 'Míreanna gníomhaíochta (ceann in aghaidh na líne)',
        },
        statuses: {
            identified: 'Aitheanta',
            assigned: 'Sannta',
            in_remediation: 'I Leigheas',
            resolved: 'Réitíodh',
            accepted_risk: 'Riosca Glactha',
            planned: 'Pleanáilte',
            in_progress: 'Ar Siúl',
            under_review: 'Faoi Léirmheas',
            finalized: 'Críochnaithe',
            archived: 'Cartlannaithe',
            draft: 'Dréacht',
            active: 'Gníomhach',
            upcoming: 'Le teacht',
            collecting: 'Ag bailiú',
            published: 'Foilsithe',
            review: 'Léirmheas',
            approved: 'Ceadaithe',
            issued: 'Eisithe',
            superseded: 'curtha ina ionad',
            completed: 'Críochnaithe',
            verified: 'Fíoraithe',
        },
        severities: {
            critical: 'Criticiúil',
            high: 'Ard',
            medium: 'Meánach',
            low: 'Low',
        },
        priorities: {
            critical: 'Criticiúil',
            high: 'Ard',
            medium: 'Meánach',
            low: 'Low',
        },
        gap_types: {
            missing: 'Ar iarraidh',
            expired: 'imithe in éag',
            insufficient: 'Neamhleor',
        },
        document_types: {
            policy: 'Beartas',
            procedure: 'Nós imeachta',
            standard: 'Caighdeán',
            guideline: 'Treoirlíne',
            template: 'Teimpléad (Inmheánach)',
        },
        maturity_levels: {
            level_1: 'Leibhéal 1 - Tosaigh',
            level_2: 'Leibhéal 2 — Ag Forbairt',
            level_3: 'Leibhéal 3 - Sainithe',
            level_4: 'Leibhéal 4 - Bainistithe',
            level_5: 'Leibhéal 5 - Optimizing',
        },
        grades: {
            excellent: 'Ar fheabhas',
            good: 'Maith',
            fair: 'Cóir',
            poor: 'bocht',
            critical: 'Criticiúil',
            needs_improvement: 'Feabhsú ar Riachtanais',
            not_assessed: 'Gan Mheasúnú',
        },
        modules: {
            data_mapping: 'Mapáil Sonraí',
            consent: 'Toiliú',
            governance: 'Rialachas',
            incidents: 'Teagmhais',
            monitoring: 'Monatóireacht',
            classification: 'Aicmiú',
            vendor_risk: 'Riosca soláthraí',
            discovery: 'Fionnachtain',
            deletion: 'scriosadh',
        },
        cadences: {
            monthly: 'go míosúil',
            quarterly: 'Ráithiúil',
            semi_annual: 'Leathbhliantúil',
            annual: 'Bliantúil',
        },
        section_labels: {
            opening: 'Ag oscailt',
            key_metrics: 'Eochairmhéadracht',
            highlights: 'Buaicphointí',
            concerns: 'Imní',
            recommendations: 'Moltaí',
            outlook: 'Outlook',
        },
        out_of_100: '/100',
        count_gaps: '{count} bearna',
        count_gaps_plural: '{count} bearnaí',
        count_summaries: '{count} achoimre',
        count_summaries_plural: '{count} achoimrí',
        count_assessments: '{count} measúnú',
        count_assessments_plural: '{count} measúnuithe',
        count_models: '{count} samhail',
        count_models_plural: '{count} samhail',
        count_periods: '{count} tréimhse',
        count_periods_plural: '{count} tréimhse',
        count_metrics: '{count} méadrach',
        count_metrics_plural: '{count} méadracht',
        unknown_user: 'Úsáideoir Anaithnid',
        unknown_gap: 'Bearna Anaithnid',
        no_permission_title: 'Ceadanna Neamhleor',
        no_permission_message: 'Níl cead agat an leathanach seo a rochtain.',
        ac_vn_frameworks_title: 'Clúdach Creata VN',
        ac_vn_frameworks_body: 'Clúdaíonn measúnuithe aibíochta na 6 chreat rialála Vítneaimis: PDPL, Dlí Sonraí, Dlí Teileachumarsáide, Dlí Cibearshlándála, AI Dlí, agus Dlí Ríomhthráchtáil — gach ceann acu le critéir measúnaithe a bhaineann go sonrach leis an gcreat.',
        ac_controller_processor_title: 'Rialaitheoir vs Próiseálaí',
        ac_controller_processor_body: 'Déanann mapálacha oibleagáide idirdhealú idir freagrachtaí an rialtóra sonraí agus an phróiseálaí sonraí. Sann róil ag baint úsáide as maitrís RACI chun a chinntiú go bhfuil úinéir soiléir ag gach oibleagáid, cibé acu an ngníomhaíonn an eagraíocht mar rialaitheoir nó mar phróiseálaí.',
        ac_versioned_evidence_title: 'Rian Fianaise Leaganacha',
        ac_versioned_evidence_body: 'Cruthaíonn gach trasdul stádais (Dréacht, Athbhreithniú, Formheasta, Eisithe) taifead le stampa ama agus leagan a fheidhmíonn mar fhianaise in-iniúchta. Gabhann an Loga Athraithe stair shaolré iomlán an doiciméid.',
        ac_remediation_gap_link_title: 'Nasctha le Bearnaí Measúnaithe',
        ac_remediation_gap_link_body: 'Nascann gach plean leasúcháin go díreach le bearna measúnaithe aitheanta. Sann úinéir agus leag amach sprioc-am chun dul chun cinn réitigh a rianú ó bhearna go dúnadh.',
        ac_annual_attestation_title: 'Timthriall Fianaithe Bliantúil',
        ac_annual_attestation_body: 'Tá tréimhsí tuairiscithe ar aon dul le ceanglas fianaithe bliantúil Dlí Sonraí Vítneaimis. Bain úsáid as an deireadh "Bliantúil" (timthriall 365 lá) chun tréimhsí fianuithe-ailínithe a ghiniúint go huathoibríoch.',
        ac_evidence_per_obligation_title: 'Fianaise de réir Oibleagáide an Chreata',
        ac_evidence_per_obligation_body: 'Aithníonn bearnaí san fhianaise cruthúnas atá in easnamh, imithe in éag, nó cruthúnas neamhleor do gach oibleagáid réime. Scag de réir fearainn féachaint cé na ceanglais rialála a bhfuil easpa fianaise tacaíochta iontu.',
        ac_cross_framework_title: 'Comhláine Tras-Chreata',
        ac_cross_framework_body: 'Soláthraíonn ullmhacht iniúchta léargas iomláine tras-chreat. Léiríonn scóir na gcomhpháirt clúdach thar na creataí léarscáilithe go léir, ag leagan béime ar réimsí ar gá aird a thabhairt orthu roimh iniúchadh.',
        ac_cadence_default_title: 'Cadence Athbhreithnithe Réamhshocraithe',
        ac_cadence_default_body: 'Is é 180 lá an deireadh athbhreithnithe réamhshocraithe. Leagtar amach go huathoibríoch athbhreithnithe atá thar téarma. Coigeartaigh an mhinicíocht in aghaidh an doiciméid de réir mar is gá — bíonn na roghanna idir 30 agus 365 lá.',
        ac_templates_internal_title: 'Aicmiú Teimpléad',
        ac_templates_internal_body: 'Rangaítear teimpléid mar dhoiciméid inmheánacha. Úsáid an cineál doiciméid "Teimpléad" chun déantáin rialachais ath-inúsáidte a mharcáil a bhfuil raon feidhme dáileacháin inmheánaí amháin acu.',
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    mcp_pending: {
        title: 'MCP — Scríbhinní ar feitheamh',
        subtitle: 'Molann cliaint Claude seachtracha scríbhinní anseo. Ceadaigh nó diúltaigh gach iontráil — ní fheidhmíonn Forseti ach amháin tar éis ceadú an riarthóra.',
        no_pending: 'Níl aon scríbhinní ar feitheamh.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Uirlis',
        col_preview: 'Réamhamharc',
        col_key: 'API eochair',
        col_requested: 'Iarrtha',
        col_expires: 'Téann i léig',
        col_status: 'Stádas',
        status_pending: 'Ar feitheamh',
        status_approved: 'Ceadaithe',
        status_declined: 'Diúltaithe',
        status_expired: 'Imithe in éag',
        status_executed: 'Curtha i bhfeidhm',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Ceadaigh',
        decline_label: 'Diúltaigh',
        refresh_label: 'Athnuaigh',
        error_generic: 'Níorbh fhéidir scríbhinní ar feitheamh a luchtú.',
        confirm_approve: 'Le ceadú, cuirfear an uirlis i bhfeidhm i d’eagraíocht le ceadanna shealbhóir an API key. Lean ar aghaidh?',
        confirm_decline: 'Diúltaigh an scríbhinn ar feitheamh seo? Ní féidir é seo a chealú.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'Téann scríbhinní ar feitheamh in éag 24 uair an chloig tar éis a gcruthaithe. Diúltaíonn an scuabadh GC uairiúil iontrálacha as dáta go huathoibríoch.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Gníomhartha',
    },
    audit_walkthrough: {
        title: 'Forseti — Gineadóir treoirturas iniúchta',
        subtitle: 'Gin treoirturas struchtúrtha do thús iniúchta: ceist oscailte de réir rialaithe, fianaise lena bhfuiltear ag súil, ceisteanna leantacha, bearnaí féideartha.',
        audit_picker_label: 'Iniúchadh',
        audit_picker_hint: 'Roghnaigh iniúchadh atá beartaithe chun treoirturas a ghiniúint.',
        run_label: 'Gin treoirturas',
        running: 'Á ghiniúint…',
        results_heading: 'Treoirturas — {count} rialú',
        tokens_label: '≈ {count} token',
        no_controls: 'Níl aon rialuithe sa raon. Cuir rialuithe leis an taifead iniúchta.',
        col_control: 'Rialú',
        col_question: 'Ceist oscailte',
        col_evidence: 'Fianaise',
        col_gaps: 'Bearnaí féideartha',
        expected_evidence_label: 'Fianaise lena bhfuiltear ag súil',
        followup_probes_label: 'Ceisteanna leantacha',
        accept_label: 'Glac leis an treoirturas',
        reject_label: 'Diúltaigh',
        export_label: 'Easpórtáil DOCX',
        resolve_summary: 'Réiteach: {status}',
        resolve_status_accepted: 'Glactha',
        resolve_status_rejected: 'Diúltaithe',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: 'Níorbh fhéidir an treoirturas a ghiniúint. Bain triail eile as nó seiceáil stádas an iniúchta.',
    },
    privilege_creep: {
        title: 'Forseti — Aimsiú leathnaithe pribhléidí',
        subtitle: 'Scanadh tréimhsiúil ar shanntaí róil. Tá torthaí comhairleach — ní chúlghaireann Forseti go huathoibríoch riamh.',
        run_label: 'Scan anois',
        running: 'Á scanadh…',
        findings_heading: '{count} toradh',
        no_findings: 'Níor aithníodh leathnú pribhléidí ar bith.',
        col_user: 'Úsáideoir',
        col_job_function: 'Feidhm phoist',
        col_severity: 'Déine',
        col_unexpected: 'Ceadanna gan choinne',
        col_remediation: 'Leigheas molta',
        col_status: 'Stádas',
        severity_critical: 'Criticiúil',
        severity_high: 'Ard',
        severity_medium: 'Meánach',
        severity_low: 'Íseal',
        status_open: 'Oscailte',
        status_dismissed: 'Curtha as an áireamh',
        status_remediated: 'Leigheasta',
        dismiss_label: 'Cuir as an áireamh',
        remediate_label: 'Marcáil mar leigheasta',
        note_label: 'Nóta (roghnach)',
        error_generic: 'Níorbh fhéidir leathnú pribhléidí a scanadh. Bain triail eile as ar ball.',
        advisory_banner: 'Tá na torthaí seo comhairleach. Ní chúlghaireann Forseti ceadanna go huathoibríoch riamh — déan athbhreithniú leis an úinéir róil sula ngníomhaíonn tú.',
    },
    compliance_rules: {
        rule_mapping: {
            title: 'Uathmhapáil na rialacha um chomhlíonadh',
            subtitle: 'Siúlfaidh Forseti pacáiste rialaitheach suiteáilte agus molann sé rialacha comhlíonta nua nó athnuaite atá bunaithe ar na hailt fhoinseacha.',
            pack_picker_label: 'Pacáiste rialála',
            pack_picker_hint: 'Roghnaigh pacáiste suiteáilte chun a mhapáil.',
            run_label: 'Léarscáil ón bpacáiste',
            running: 'Ag mapáil…',
            results_heading: '{count} mholadh(í)',
            tokens_label: '≈ {count} comharthaí ó do bhuicéad míosúil',
            no_suggestions: 'Gan aon mholtaí — féadfaidh inneachar an phacáiste a bheith clúdaithe go hiomlán cheana féin leis na rialacha atá ann cheana.',
            kind_new: 'Riail nua',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'Ní theastaíonn aon athrú',
            col_kind: 'cineál',
            col_title: 'Teideal beartaithe',
            col_existing: 'Riail atá ann cheana',
            col_source: 'Foinse',
            proposed_body_label: 'Comhlacht beartaithe',
            source_quote_label: 'Foinse athfhriotail',
            accept_selected: 'Glac roghnaithe',
            reject_all: 'Diúltaigh go léir',
            accept_all: 'Glac le gach',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: 'Níorbh fhéidir léarscáiliú rialacha a rith. Bain triail eile as nó seiceáil an cód pacáiste.',
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    access_accountability: {
        nav: {
            session_dashboard: 'Painéal seisiúin',
            access_log: 'Logáil rochtana',
            justification_review: 'Athbhreithniú údaraithe',
            investigations: 'Imscrúduithe',
            pattern_analysis: 'Anailís phatrúin',
            anchoring_status: 'Stádas ancaire',
            configuration: 'Cumraíocht',
            access_policies: 'Polasaithe rochtana',
            review_campaigns: 'Feachtais athbhreithnithe',
            evidence_packages: 'Pacáistí fianaise',
        },
        session_dashboard: {
            title: 'Painéal seisiúin',
            loading: 'Ag lódáil sonraí an phainéil...',
            total_sessions: 'Seisiúin Iomlána',
            active_sessions: 'Seisiúin Ghníomhacha',
            avg_duration: 'Meánfhad',
            peak_day_sessions: 'Lá is Gnóthaí',
            auth_method_breakdown: 'Miondealú Modha Údaraithe',
            geo_country_breakdown: 'Miondealú de réir tíre',
            recent_session_events: 'Imeachtaí seisiúin le déanaí',
            user_id: 'ID Úsáideora',
            session_id: 'ID an tseisiúin',
            ip_address: 'Seoladh IP',
            auth_method: 'Modh Údaraithe',
            started_at: 'Tosaithe ag',
            event_type: 'Cineál Imeachta',
            all_event_types: 'Gach Cineál Imeachta',
            filter_by_user: 'Scag de réir úsáideora',
            no_active_sessions: 'Níl aon seisiún gníomhach',
            no_session_events: 'Níor aimsíodh aon imeacht seisiúin',
            revoke: 'Cúlghairm',
            revoke_session: 'Cúlghair an seisiún',
            aria_revoke_session: 'Cúlghairm seisiún {user}',
            revoke_reason: 'Cúis',
            revoke_placeholder: 'Tabhair cúis le cúlghairm an tseisiúin seo...',
            session_label: 'Seisiún:',
            load_failed: 'Theip ar lódáil phainéal an tseisiúin. Bain triail eile as.',
            events_load_failed: 'Theip ar luchtú imeachtaí seisiúin. Bain triail eile as.',
            revoke_failed: 'Theip ar chúlghairm an tseisiúin. Bain triail eile as.',
            session_events_loaded: '{n} imeacht seisiúin luchtaithe',
            event_types: {
                session_started: 'Seisiún Tosaithe',
                session_refreshed: 'Seisiún Athnuaite',
                session_expired: 'Seisiún in éag',
                session_revoked: 'Seisiún Cúlghairthe',
                session_terminated: 'Cuireadh deireadh leis an Seisiún',
            },
        },
        access_log: {
            title: 'Logáil rochtana',
            user_id: 'ID Úsáideora',
            resource: 'Acmhainn',
            action: 'Gníomh',
            ip_address: 'IP Seoladh',
            timestamp: 'Stampa Ama',
            no_logs: 'Níl aon rochtain logáil entries found. aimsithe',
            filter_by_user: 'Scag by Úsáideoir ID...',
            load_failed: 'Theip ar load rochtain logáil. Bain triail eile as.',
            export_success: 'Easpórtáil initiated successfully',
            export_failed: 'Theip ar export rochtain logáil. Bain triail eile as.',
            record_count: 'taifead',
            record_count_plural: 'taifid',
            unclassified: 'Neamhaicmithe',
            classification_missing: 'Aicmiú ar iarraidh',
            classification_level: 'Leibhéal Aicmithe',
            response_code: 'Cód Freagartha',
            access_surface: 'Cainéal Rochtana',
            api_key: 'Eochair API',
            classification_levels: {
                public: 'Poiblí',
                internal: 'Inmheánach',
                confidential: 'Faoi rún',
                restricted: 'Srianta',
                unclassified: 'Neamhrangaithe',
            },
        },
        justification_review: {
            title: 'Athbhreithniú údaraithe',
            user_id: 'ID Úsáideora',
            resource: 'Acmhainn',
            justification: 'Údar',
            submitted_at: 'Curtha isteach',
            reviewer: 'Athbhreithneoir',
            no_reviews: 'Níl aon údar athbhreithniú found. aimsithe',
            load_failed: 'Theip ar load údair. Bain triail eile as.',
            review_failed: 'Theip ar athbhreithniú údar. Bain triail eile as.',
            decline_failed: 'Theip ar decline rochtain. Bain triail eile as.',
            justification_count: 'údar',
            justification_count_plural: 'údair',
            reviewed: 'Athbhreithnithe',
            concerning: 'Ábhar imní',
            escalated: 'Ardaithe',
            link_investigation_placeholder: 'Nasc to an imscrúdú (optional)',
            review_queue: 'Athbhreithniú Queue',
            access_id_label: 'ID rochtana',
            access_id_placeholder: 'ID iontrála loga rochtana',
            resource_type_label: 'Cineál acmhainne',
            resource_type_placeholder: 'm.sh. document, database',
            resource_id_label: 'ID acmhainne',
            resource_id_placeholder: 'Aitheantóir acmhainne',
            reason_placeholder: 'Fáth le rochtain a fháil ar an acmhainn seo...',
            create_failed: 'Theip ar create justification',
            unclassified: 'Neamhaicmithe',
            review_statuses: {
                pending: 'Ar feitheamh',
                reviewed: 'Athbhreithnithe',
                concerning: 'Maidir le',
                escalated: 'Ardaithe',
            },
            reason_codes: {
                business_need: 'Gá Gnó',
                legal_obligation: 'Oibleagáid Dlí',
                data_subject_request: 'Iarratas Ábhar Sonraí',
                audit_requirement: 'Ceanglas Iniúchta',
                incident_response: 'Freagra Teagmhais',
                research: 'Taighde',
                administrative: 'Riaracháin',
                other: 'Eile',
            },
            classification_level: 'Leibhéal Aicmithe',
            review_decision: 'Cinneadh Athbhreithnithe',
        },
        investigations: {
            title: 'Imscrúdú Workspace',
            new: 'Nua Imscrúdú',
            subject: 'Ábhar',
            investigator: 'Imscrúdaitheoir',
            opened_at: 'Oscailte At',
            findings: 'Torthaí',
            no_investigations: 'Níl aon imscrúduithe found. aimsithe',
            load_failed: 'Theip ar load imscrúduithe. Bain triail eile as.',
            create_failed: 'Theip ar create imscrúdú. Bain triail eile as.',
            timeline_add_failed: 'Theip ar add timeline iontráil. Bain triail eile as.',
            link_access_failed: 'Theip ar link rochtain id. Bain triail eile as.',
            link_justification_failed: 'Theip ar link údar id. Bain triail eile as.',
            close_failed: 'Theip ar close imscrúdú. Bain triail eile as.',
            export_success: 'Easpórtáil initiated successfully',
            export_failed: 'Theip ar export imscrúdú. Bain triail eile as.',
            refresh_failed: 'Theip ar refresh imscrúdú detail. Bain triail eile as.',
            update_failed: 'Theip ar an imscrúdú a nuashonrú',
            investigation_count: 'imscrúdú',
            investigation_count_plural: 'imscrúduithe',
            title_placeholder: 'Imscrúdú teideal',
            description_placeholder: 'Detailed cur síos of the imscrúdú...',
            assignee_placeholder: 'ID Úsáideora an tsannaí (roghnach)',
            timeline_action_placeholder: 'm.sh. Logálacha rochtana athbhreithnithe',
            timeline_note_placeholder: 'Roghnach note',
            access_ids_placeholder: 'rochtain-id-1, rochtain-id-2',
            justification_ids_placeholder: 'justification-id-1, justification-id-2',
            findings_placeholder: 'Summarize the imscrúdú findings...',
            by_actor: 'by',
            close_investigation: 'Dún Imscrúdú',
            add_timeline_failed: 'Theip ar add timeline iontráil. Bain triail eile as.',
            fetch_failed: 'Theip ar fetch imscrúduithe. Bain triail eile as.',
            change_status: 'Athraigh stádas an imscrúdaithe',
            details: 'Sonraí an imscrúdaithe',
            link_evidence: 'Nasc fianaise',
            link_justifications: 'Nasc údaruithe',
            access_ids_label: 'IDanna rochtana',
            justification_ids_label: 'IDanna údaraithe',
            access_ids_link_placeholder: 'rochtain-id-1, rochtain-id-2',
            justification_ids_link_placeholder: 'justification-id-1, justification-id-2',
            investigations_loaded: '{n} investigations luchtaithe',
            status_changed_to: 'Stádas athraithe go {status}',
            add_timeline_entry: 'Cuir iontráil amlíne leis',
            add_access_ids: 'Cuir IDanna rochtana leis',
            add_justification_ids: 'Cuir IDanna údaraithe leis',
            investigation_types: {
                anomaly_review: 'Léirmheas ar Aimhrialtacht',
                complaint_driven: 'Tiomáinte Gearán',
                scheduled_review: 'Athbhreithniú Sceidealta',
                whistleblower_report: 'Tuarascáil an sceithire',
            },
            severity_labels: {
                low: 'Íseal',
                medium: 'Meánach',
                high: 'Ard',
                critical: 'Criticiúil',
            },
            status_labels: {
                open: 'Oscail',
                in_progress: 'Ar Siúl',
                pending_review: 'Athbhreithniú ar Feitheamh',
                closed_substantiated: 'Dúnta (bunaithe)',
                closed_unsubstantiated: 'Dúnta (gan bhunús)',
                closed_inconclusive: 'Dúnta (Inconclusive)',
            },
            disposition_labels: {
                substantiated: 'Tá bunús leis',
                unsubstantiated: 'Gan bunús',
                inconclusive: 'neamhchinntitheach',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Anailís phatrúin',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'Ag lódáil pattern sonraí...',
            anomalies_detected: 'Aimhrialtachtaí Aimsíodh',
            patterns: 'Patrúin',
            by_user: 'By Úsáideoir',
            by_resource: 'De réir Acmhainne',
            time_range: 'Am Raon',
            load_failed: 'Theip ar analyze patterns. Bain triail eile as.',
            top_resources: 'Na 10 is fearr',
            access_by_hour: 'Rochtain de réir Uair an Lae',
            access_by_day: 'Rochtain de réir Lá na Seachtaine',
            fetch_failed: 'Theip ar fetch pattern analysis. Bain triail eile as.',
            analysis_loaded: 'Anailís patrúin luchtaithe do {user}, {n} seisiún aimsithe',
            date_format_placeholder: 'LL/MM/BBBB',
            access_by_hour_heading: 'Rochtain de réir uair an lae',
            access_by_day_heading: 'Rochtain de réir lá den tseachtain',
            by_classification: 'De réir leibhéal aicmithe',
            top_accessed_resources: 'Na hacmhainní is mó a rochtanaítear',
            user_access_summary: 'Achoimre ar rochtain úsáideora',
            days: {
                sunday: 'Dé Domhnaigh',
                monday: 'Dé Luain',
                tuesday: 'Dé Máirt',
                wednesday: 'Dé Céadaoin',
                thursday: 'Déardaoin',
                friday: 'Dé hAoine',
                saturday: 'Dé Sathairn',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: 'Stádas ancaire',
            loading: 'Ag lódáil anchoring sonraí...',
            anchored: 'Ancairthe',
            pending: 'Ar feitheamh',
            failed: 'Theip',
            last_anchor: 'Last Ancaire',
            no_records: 'Níl aon anchoring taifid found. aimsithe',
            status_load_failed: 'Theip ar load anchoring stádas. Bain triail eile as.',
            proofs_load_failed: 'Theip ar load rochtain cruthúnais. Bain triail eile as.',
            verify_failed: 'Theip ar verify cruthúnas chain. Bain triail eile as.',
            anchor_failed: 'Theip ar anchor rochtain cruthúnas. Bain triail eile as.',
            batch_anchor_failed: 'Theip ar batch anchor rochtain cruthúnais. Bain triail eile as.',
            proof_count: 'cruthúnas',
            proof_count_plural: 'cruthúnais',
            anchor_single: 'Ancaire Singil',
            anchor_batch: 'Baisc Ancaire',
            access_id_placeholder: 'Iontráil rochtain id to anchor',
            batch_ids_placeholder: 'rochtain-id-1, rochtain-id-2, rochtain-id-3',
            verify_chain: 'Fíoraigh Slabhra',
            chain_breaks: 'Slabhra Breaks',
            total_checked: 'Checked Iomlána',
            anchor_placeholder: 'Iontráil rochtain id to anchor',
            fetch_failed: 'Theip ar fetch anchoring stádas. Bain triail eile as.',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Theip ar fetch cruthúnais. Bain triail eile as.',
            access_id: 'ID rochtana',
            chain_sequence: 'Seic. slabhra',
            proof_hash_label: 'Haiseáil cruthúnais',
            batch_id: 'ID baisce',
            proofs_loaded: '{n} access proofs luchtaithe',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: 'The chain still verifies for these proofs. The access-log row each one anchors has passed its retention date and been destroyed, so its content can no longer be proven.',
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'Ar feitheamh',
                confirmed: 'Deimhnithe',
                failed: 'Theip',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Slabhra',
                blockchain_anchor: 'Ancaire Blockchain',
                zkp_stub: 'Stub ZKP',
            },
        },
        configuration: {
            title: 'Cumraíocht',
            session_settings: 'Seisiún Socruithe',
            log_retention: 'Logáil Coinneáil (laethanta)',
            anomaly_threshold: 'Tairseach Aimhrialtachta',
            anchoring_enabled: 'Ancaireacht Cumasaithe',
            save_config: 'Sábháil Cumraíocht',
            load_failed: 'Theip ar load cumraíocht. Bain triail eile as.',
            save_success: 'Cumraíocht saved successfully',
            save_failed: 'Theip ar save cumraíocht. Bain triail eile as.',
            justification_rules: 'Údar Rialacha',
            reason_codes: 'Reason Cód',
            anchoring_settings: 'Socruithe Ancaireachta',
            anchoring_method: 'Modh Ancaireachta',
            anchoring_frequency: 'Minicíocht Ancaireachta',
            batch_interval: 'Baisc Interval (minutes)',
            eligibility_criteria: 'Incháilitheacht Critéir',
            add_criterion_placeholder: 'Cuir critéar incháilitheachta leis...',
            retention_settings: 'Coinneáil Socruithe',
            standard_retention: 'Caighdeánach Coinneáil (laethanta)',
            sensitive_retention: 'Sensitive Coinneáil (laethanta)',
            working_hours: 'Uaireanta Oibre',
            work_start: 'Tosaigh Am',
            work_end: 'End Am',
            timezone: 'Crios Ama',
            timezone_placeholder: 'm.sh. Asia/Ho_Chi_Minh',
            working_days: 'Laethanta Oibre',
            concurrent_sessions: 'Concurrent Seisiún Teorainn',
            concurrent_threshold: 'Concurrent Seisiún Tairseach',
            classification_level: 'Aicmiú Leibhéal',
            prompt_mode: 'Mód Leid',
            grace_period: 'Grace Tréimhse (s)',
            code_placeholder: 'cód',
            is_custom: 'Saincheaptha',
            label_placeholder: 'Lipéad',
            description_placeholder: 'Cur síos',
            custom: 'Saincheaptha',
            fetch_failed: 'Theip ar fetch cumraíocht. Bain triail eile as.',
            classification_levels: {
                public: 'Poiblí',
                internal: 'Inmheánach',
                confidential: 'Faoi rún',
                restricted: 'Srianta',
                unclassified: 'Neamhrangaithe',
            },
            prompt_modes: {
                reason_only: 'Fáth amháin',
                reason_text: 'Cúis + Téacs',
                reason_text_supervisor: 'Cúis + Téacs + Maoirseoir',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Slabhra',
                blockchain_anchor: 'Ancaire Blockchain',
                zkp_stub: 'Stub ZKP',
            },
            anchoring_frequencies: {
                realtime: 'Fíor-ama',
                batched: 'Baisceáilte',
                on_demand: 'Ar Éileamh',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: 'Polasaithe rochtana',
            create_policy: 'Cruthaigh polasaí rochtana',
            policy_name: 'Ainm an Pholasaí',
            framework_code: 'Creatlach',
            select_framework: 'Roghnaigh creatlach...',
            review_frequency: 'Minicíocht athbhreithnithe',
            scope: 'Raon feidhme',
            no_policies_found: 'Níor aimsíodh polasaithe rochtana ar bith.',
            policy_created: 'Polasaí rochtana cruthaithe',
            frequency_labels: {
                monthly: 'Míosúil',
                quarterly: 'Ráithiúil',
                semi_annual: 'Leathbhliantúil',
                annual: 'Bliantúil',
            },
        },
        review_campaigns: {
            title: 'Feachtais athbhreithnithe rochtana',
            create_campaign: 'Cruthaigh feachtas athbhreithnithe',
            reviewer: 'Athbhreithneoir',
            due_date: 'Dáta dlite',
            certify: 'Deimhnigh',
            no_campaigns_found: 'Níor aimsíodh feachtais athbhreithnithe rochtana ar bith.',
            status_open: 'Oscailte',
            status_in_review: 'Á athbhreithniú',
            status_completed: 'Críochnaithe',
            campaign_created: 'Feachtas athbhreithnithe cruthaithe',
            review_certified: 'Athbhreithniú rochtana deimhnithe',
            certify_campaign: 'Deimhnigh feachtas {id}',
            policy_id: 'ID polasaí',
            select_policy: 'Roghnaigh polasaí...',
            reviewer_ids_label: 'Athbhreithneoir',
            select_reviewers: 'Roghnaigh athbhreithneoir...',
            status_labels: {
                open: 'Oscailte',
                in_review: 'Á athbhreithniú',
                in_progress: 'Ar siúl',
                cancelled: 'Ar ceal',
                completed: 'Críochnaithe',
            },
        },
        evidence_packages: {
            title: 'Pacáistí fianaise',
            generate_package: 'Gin pacáiste fianaise',
            export_type: 'Cineál easpórtála',
            package_type: 'Cineál pacáiste',
            framework_scope: 'Raon feidhme an chreatlach',
            period: 'Tréimhse',
            generated_by: 'Ginte ag',
            generated_at: 'Ginte ar',
            no_packages_found: 'Níor aimsíodh pacáistí fianaise ar bith.',
            package_generated: 'Pacáiste fianaise ginte',
            export_type_full: 'Easpórtáil iomlán',
            export_type_summary: 'Achoimre',
            export_type_certification_only: 'Deimhniúchán amháin',
            package_type_access_review: 'Athbhreithniú rochtana',
            package_type_investigation: 'Imscrúdú',
            package_type_certification: 'Deimhniúchán',
            package_type_authority_request: 'Iarratas údaráis',
            period_start: 'Tús na tréimhse',
            period_end: 'Deireadh na tréimhse',
            select_frameworks: 'Roghnaigh creataí...',
            completeness_col: 'Iomláine',
            status_col: 'Stádas',
            generated_by_col: 'Ginte Ag',
        },
        evidence_package_detail: {
            title: 'Sonraí pacáiste fianaise',
            not_found: 'Pacáiste fianaise gan aimsiú.',
            package_metadata: 'Meiteashonraí an phacáiste',
            status: 'Stádas',
            item_count: 'Líon míreanna',
            disposition: 'Diúscairt',
            closed_at: 'Dúnta ar',
            completeness_dashboard: 'Painéal iomláine',
            no_completeness_data: 'Níl sonraí iomláine ar fáil.',
            controls_covered: 'rialuithe clúdaithe',
            missing: 'Ar iarraidh',
            sign_off_tracker: 'Rianaire formheasa',
            stage_framework_owner: 'Úinéir an chreata',
            stage_legal_compliance: 'Dlí agus Comhlíonadh',
            stage_internal_audit: 'Iniúchadh inmheánach',
            approved: 'Faofa',
            returned: 'Curtha ar ais',
            pending: 'Ar feitheamh',
            approve: 'Faoigh',
            return_btn: 'Cuir ar ais',
            sign_off_submitted: 'Formheas curtha isteach',
            artifacts: 'Déantáin',
            no_artifacts: 'Níor aimsíodh déantáin.',
            template_id: 'ID teimpléid',
            description: 'Cur síos',
            classification: 'Aicmiú',
            evidence_type: 'Cineál fianaise',
            framework_section: 'Rannán creata',
            history: 'Stair formheasa',
            expand: 'Leathnaigh',
            collapse: 'Laghdaigh',
            action: 'Gníomh',
            actor: 'Aisteoir',
            timestamp: 'Stampa ama',
            comment: 'Trácht',
            close_case: 'Dún cás',
            confirm_close: 'Deimhnigh dúnadh',
            closure_notes: 'Nótaí dúnta',
            disposition_compliant: 'Comhlíontach',
            disposition_non_compliant: 'Neamh-chomhlíontach',
            disposition_partial: 'Páirteach',
            case_closed: 'Pacáiste fianaise dúnta',
            export_package: 'Easpórtáil pacáiste',
            export_complete: 'Pacáiste fianaise easpórtáilte',
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    consent_properties: {
        nav: {
            dashboard: 'Painéal',
            properties: 'Airíonna',
            groups: 'Grúpaí',
            bindings: 'Ceangail',
            change_requests: 'Iarratais athraithe',
            comparison: 'Comparáid',
            inconsistencies: 'Neamhréireachtaí',
            snapshot_schedules: 'Sceidil grianghraf',
            golden_templates: 'Teimpléid óir',
            evidence_packs: 'Pacáistí fianaise',
            audit: 'Iniúchadh',
        },
        select_property: 'Roghnaigh maoin…',
        select_policy: 'Roghnaigh polasaí…',
        select_properties: 'Roghnaigh maoine…',
        select_target_properties: 'Roghnaigh maoine sprice…',
        select_owner: 'Roghnaigh úinéir…',
        select_technical_contact: 'Roghnaigh teagmhálaí teicniúil…',
        property_statuses: {
            active: 'Gníomhach',
            inactive: 'Neamhghníomhach',
            pending: 'Ar feitheamh',
            decommissioned: 'Díchoimisiúnaithe',
        },
        dashboard: {
            title: 'Painéal tras-airí',
            loading: 'Ag lódáil painéal sonraí...',
            total_properties: 'Maoine Iomlána',
            active_properties: 'Maoine Gníomhacha',
            snapshot_coverage: 'Clúdach Grianghraif',
            properties_covered: 'maoine clúdaithe',
            total_bindings: 'Iomlán Ceangal',
            deployed_bindings: 'Imscartha',
            pending_bindings: 'Imscaradh ar feitheamh',
            deployment_progress: 'Dul chun cinn Imscartha',
            on_target: 'Ar sprioc',
            below_target: 'Faoi bhun sprice',
            target_label: 'Sprioc',
            total_groups: 'Grúpaí Iomlána',
            pending_changes: 'Ar feitheamh Changes',
            inconsistencies: 'Neamhréireachtaí',
            recent_activity: 'Le déanaí Gníomhaíocht',
            no_recent_activity: 'Gan ghníomhaíocht le déanaí.',
            fetch_error: 'Theip ar load painéal. Bain triail eile as.',
        },
        properties: {
            title: 'Clár airí',
            new: 'Nua Maoin',
            edit: 'Cuir in eagar Maoin',
            property_name: 'Ainm',
            property_type: 'Cineál',
            identifier: 'Aitheantóir',
            environment: 'Timpeallacht',
            owner_id: 'Úinéir ID',
            technical_contact_id: 'ID Teagmhála Teicniúil',
            decommission: 'Díchoimisiúnú',
            confirm_decommission: 'An bhfuil tú cinnte gur mian leat decommission this property?',
            no_properties: 'Níl aon properties found. aimsithe',
            all_environments: 'Gach Timpeallacht',
            all_types: 'Gach Cineál',
            cmp_instance_id: 'CMP Aitheantas Ceacht',
            deployment_date: 'Dáta Imscartha',
            identifier_placeholder: 'm.sh., www.example.com nó com.example.app',
            owner_name: 'Úinéir',
            technical_contact_name: 'Teagmháil theicniúil',
            sensitive_data_categories: 'Catagóirí Sonraí Íogaire',
            sensitive_data_categories_placeholder: 'Roghnaigh catagóirí sonraí íogaire',
            requires_enhanced_review: 'Athbhreithniú Feabhsaithe de dhíth',
            sensitive_review_status: 'Athbhreithniú ar Shonraí Íogaireachta',
            sensitive_review_pending: 'Athbhreithniú ar Feitheamh',
            sensitive_review_approved: 'Ceadaithe',
            sensitive_review_rejected: 'Diúltaithe',
            sensitive_review_submit: 'Cuir Athbhreithniú',
            sensitive_review_approve: 'Cheadaigh',
            sensitive_review_reject: 'Diúltaigh',
            sensitive_review_notes: 'Nótaí Athbhreithnithe',
            sensitive_review_notes_placeholder: 'Cuir isteach nótaí athbhreithnithe...',
            record_withdrawal: 'Aistarraingt Taifead',
            withdrawal_subject_id: 'Aitheantas an ábhair',
            withdrawal_consent_id: 'Aitheantas Toilithe',
            withdrawal_reason: 'Cúis',
            withdrawal_downstream_systems: 'Córais iartheachtach',
            withdrawal_downstream_placeholder: 'córas-1, córas-2',
            withdrawal_success: "D'éirigh leis an tarraingt siar a iomadaíodh.",
            withdrawal_error: 'Theip ar an tarraingt siar a iomadú.',
            withdrawal_data_subject_id: 'Aitheantas Ábhar Sonraí',
            withdrawal_data_subject_id_placeholder: 'm.sh. usr_abc123',
            withdrawal_consent_record_id: 'Aitheantas Taifead Toiliú',
            withdrawal_consent_record_id_placeholder: 'm.sh. consent_xyz789',
            withdrawal_purposes: 'Cuspóirí Aistarraingthe',
            withdrawal_purposes_placeholder: 'Roghnaigh críocha chun toiliú a tharraingt siar le haghaidh...',
            withdrawal_withdrawn_at: 'Dáta Aistarraingthe',
            withdrawal_tasks_created: 'Tascanna Cruthaithe',
            withdrawal_affected_bindings: 'Ceangailteacha a bhfuil tionchar orthu',
            withdrawal_affected_activities: 'Gníomhaíochtaí Próiseála a mbeidh tionchar orthu',
            withdrawal_history: 'Stair Aistarraingthe',
        },
        groups: {
            title: 'Grúpaí airí',
            new: 'Nua Grúpa',
            group_name: 'Grúpa Ainm',
            member_count: 'Baill',
            no_groups: 'Níl aon property groups found. aimsithe',
            policies: 'Polasaithe',
            confirm_delete: 'An bhfuil tú cinnte gur mhaith leat an grúpa seo a scriosadh?',
        },
        bindings: {
            title: 'Maitrís cheangal',
            property: 'Maoin',
            purpose: 'Cuspóir',
            legal_basis: 'Bunús Dlíthiúil',
            bound: 'Ceangailte',
            no_bindings: 'Níl aon bindings found. aimsithe',
            bind_policy: 'Beartas Ceangail',
            bulk_bind: 'Ceangal Bulc',
            bind_policy_to_property: 'Beartas Ceangail le Maoin',
            bulk_bind_policy: 'Beartas um Cheangal Mórchóir',
            unbound: 'Gan cheangal',
            policy_id: 'ID polasaí',
            version_placeholder: 'm.sh., 1.0.0',
            property_ids_placeholder: 'prop-id-1, prop-id-2, prop-id-3',
            binding_name: 'Ceangal',
            property_name: 'Ainm na maoine',
            policy_name: 'Ainm an bheartais',
            bindings_list: 'Liosta ceangal',
            binding_actions: {
                start_deploy: 'Tosaigh imlonnú',
                mark_deployed: 'Marcáil mar imlonnaithe',
                rollback: 'Slocht siar',
            },
        },
        change_requests: {
            title: 'Próiseas iarratas athraithe',
            new: 'Nua Change Iarratas',
            requester: 'Iarrthóir',
            change_type: 'Change Cineál',
            property: 'Maoin',
            submitted_at: 'Curtha isteach',
            no_requests: 'Níl aon change requests found. aimsithe',
            fetch_pipeline_error: 'Theip ar fetch pipeline. Bain triail eile as.',
            fetch_list_error: 'Theip ar fetch change requests. Bain triail eile as.',
            create_error: 'Theip ar create change request. Bain triail eile as.',
            action_error: 'Theip ar perform gníomh. Bain triail eile as.',
            approver_name: 'Ceadaitheoir',
            placeholder_target_props: 'prop-id-1, prop-id-2',
            view_all: 'Gach ceann',
            view_my_properties: 'Mo Shealúchais',
            stage_requests: 'Iarratais {stage}',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Spás oibre comparáide',
            select_properties: 'Roghnaigh maoine to compare',
            compare: 'Comparáid',
            no_comparison: 'Roghnaigh at least two properties to compare.',
            fetch_error: 'Theip ar fetch comparison tuarascáil. Bain triail eile as.',
            run_error: 'Theip ar run comparison. Bain triail eile as.',
            save_notes_error: 'Theip ar save notes. Bain triail eile as.',
            scan_complete: 'Inconsistency scanadh críochnaithe successfully.',
            scan_error: 'Theip ar run inconsistency scanadh. Bain triail eile as.',
            min_two_properties: 'Please provide at least 2 property ID for comparison.',
            view_inconsistencies: 'Féach ar neamhréireachtaí',
            click_to_select: 'Cliceáil chun dhá mhaoin nó níos mó a roghnú',
            no_properties_available: 'Níl aon mhaoin ar fáil',
            no_matching_properties: 'Níl aon mhaoin meaitseála ann',
            select_at_least_two_props: 'Roghnaigh dhá mhaoin ar a laghad ón liosta anuas thuas.',
            no_differences_found: 'Níor aimsíodh difríochtaí idir na maoine roghnaithe.',
            found_inconsistencies: 'Aimsíodh {count} neamhréireacht i {properties} maoin agus {bindings} nasc.',
        },
        inconsistencies: {
            title: 'Liosta neamhréireachtaí',
            property: 'Maoin',
            field: 'Réimse',
            expected: 'Ionchais',
            actual: 'Iarbhír',
            severity: 'Déine',
            no_inconsistencies: 'Níl aon inconsistencies found. aimsithe',
            trend: 'Treocht Neamh-chomhsheasmhachta',
            trend_chart_label: 'Cairt treochtaí neamh-chomhsheasmhachta a thaispeánann comhairimh thar am',
            acknowledge: 'Admhaigh',
            resolve: 'Réitigh',
            suppress: 'Ceilt',
            cr_id_placeholder: 'ID CR',
            view_all: 'Gach ceann',
            view_my_properties: 'Mo Shealuchain',
            view_dpo_review: 'DPO scuaine athbhreithnithe',
            change_request_id_required: 'Teastaíonn aitheantas an iarratais a athrú chun an neamhréireacht seo a réiteach.',
        },
        snapshot_schedules: {
            title: 'Sceidil grianghraf',
            new: 'Nua Sceideal',
            schedule_name: 'Sceideal Ainm',
            frequency: 'Minicíocht',
            next_run: 'Ar aghaidh Rith',
            last_run: 'Last Rith',
            no_schedules: 'Níl aon snapshot schedules found. aimsithe',
            target_type: 'Cineál Sprioc',
            target_id: 'Aitheantas sprice',
            custom_frequency_days: 'Minicíocht Chustaim (laethanta)',
            time_of_day: 'Am an Lae',
            time: 'Am',
            retention_days: 'coinneáil (laethanta)',
            execute_now: 'Rith Anois',
            placeholder_days: 'm.sh., 10',
            target_name: 'Ainm sprice',
        },
        golden_templates: {
            title: 'Teimpléid óir',
            new: 'Nua Teimpléad',
            template_name: 'Teimpléad Ainm',
            property_type: 'Maoin Cineál',
            no_templates: 'Níl aon golden teimpléid found. aimsithe',
            applicable_group_ids: 'Aitheantas Grúpa Infheidhme (scartha le camóg)',
            applicable_groups: 'Grúpaí Infheidhme',
            purposes: 'Cuspóirí',
            legal_bases: 'Bunús Dlí',
            banner_config: 'Cumraíocht Meirge',
            consent_settings: 'Socruithe toilithe',
            group_ids_placeholder: 'grúpa-id-1, grúpa-id-2',
            apply_to_property: 'Cuir i bhfeidhm ar Mhaoin',
            apply_to_group: 'Cuir i bhfeidhm ar Ghrúpa',
            apply_success: 'Teimpléad curtha i bhfeidhm go rathúil.',
            applications: 'Stair na bhFeidhmeanna',
            no_applications: 'Níl aon fheidhmiú teimpléid fós.',
            no_configuration_details: 'Níl mionsonraí cumraíochta ag an teimpléad. Cuir an teimpléad in eagar chun cuspóirí, bunúis dhlíthiúla, socruithe meirge nó socruithe toilithe a chur leis.',
        },
        rollouts: {
            title: 'Rolluithe Teimpléad',
            rollout_status: 'Stádas Rolluíochta',
            confirmed: 'Deimhnithe',
            pending: 'Ar feitheamh',
            failed: 'Theip air',
            confirm: 'Deimhnigh Maoin',
            confirm_success: 'Maoin deimhnithe sa rollú.',
            can_close: 'Gach maoin deimhnithe — rollú críochnaithe',
            cannot_close: 'Ag fanacht le deimhnithe maoine',
            no_rollouts: 'Níor aimsíodh aon rolluithe.',
            in_progress: 'Ar siúl',
            completed: 'Críochnaithe',
            cancelled: 'Curtha ar ceal',
            reject: 'Diúltaigh Maoin',
            reject_success: 'Maoin diúltaithe ón rollú.',
            confirm_all: 'Deimhnigh Uile',
            reject_all: 'Diúltaigh Uile',
            confirm_label: 'Deimhnigh rollú do',
            reject_label: 'Diúltaigh rollú do',
            confirm_all_label: 'Deimhnigh Gach Rollú ar Feitheamh',
            reject_all_label: 'Diúltaigh Gach Rollú ar Feitheamh',
            no_properties_in_rollout: 'Níl aon airíonna ag an gcur i bhfeidhm seo.',
        },
        analytics: {
            title: 'Anailísíocht Maoine',
            opt_in_rate: 'Ráta Roghnaithe Isteach',
            opt_out_rate: 'Ráta Roghnaithe Amach',
            withdrawal_rate: 'Ráta Aistarraingthe',
            total_consents: 'Toilithe Iomlán',
            total_withdrawals: 'Aistarraingthe Iomlán',
            compliance_score: 'Scór Comhlíonta',
            non_compliant: 'Maoine Neamhchomhlíontacha',
            no_non_compliant: 'Tá gach maoin comhlíontach.',
            threshold: 'Tairseach Comhlíonta',
            property_id: 'Aitheantas Maoine',
            compliant: 'Comhlíontach',
            non_compliant_status: 'Neamhchomhlíontach',
            compliance_help_summary: 'Conas a ríomhtar an scór comhlíonta, agus conas a cheartóidh mé é?',
            compliance_help_intro: 'Léirítear maoin mar neamhchomhlíonta nuair a bhíonn a scór faoi bhun 80%. Ríomhtar an scór mar:',
            compliance_help_with_template_label: 'Má chuirtear Teimpléad Órga i bhfeidhm:',
            compliance_help_with_template_text: "% d'eochracha cumraíochta (cuspóirí, legal_bases, banner_config, consent_settings) a mheaitseálann go díreach leis an teimpléad.",
            compliance_help_no_template_label: 'Mura gcuirtear Teimpléad Órga i bhfeidhm:',
            compliance_help_no_template_text: '% de cheangail (cineálacha toilithe a shanntar don airí) le deployment_status = imlonnaithe. Mainneachtainí réadmhaoin nach bhfuil aon cheangail ann go dtí 100%.',
            compliance_help_fix_intro: 'Chun maoin a bhaint den liosta seo, déan ceann amháin díobh seo a leanas:',
            compliance_help_fix_deploy_label: 'Imscaradh a cheangail',
            compliance_help_fix_deploy_text: 'oscail an leathanach Maitrís Ceangailteach agus socraigh stádas imscartha gach ceangailteach le himscaradh.',
            compliance_help_fix_template_label: 'Cuir Teimpléad Órga i bhfeidhm',
            compliance_help_fix_template_text: 'úsáid Iarratas a Dhéanamh ar an nGrúpa ar an teimpléad, ansin deimhnigh rolladh amach na maoine seo. Cinntigh go bhfuil cumraíocht na maoine ag teacht leis an teimpléad.',
            compliance_help_fix_decommission_label: 'An mhaoin a dhíchoimisiúnú',
            compliance_help_fix_decommission_text: 'úsáid an cnaipe Díchoimisiúnaithe sa mhód sonraí maoine mura bhfuil sé in úsáid a thuilleadh.',
            compliance_help_fix_no_bindings_label: 'Bain na ceangail go léir',
            compliance_help_fix_no_bindings_text: 'mura bhfuil teimpléad ag réadmhaoin agus mura bhfuil aon cheangail ann, scórálann sé 100% de réir réamhshocraithe.',
        },
        evidence_packs: {
            title: 'Pacáistí fianaise',
            new: 'Nua Pack',
            pack_name: 'Pack Ainm',
            properties: 'Maoine',
            generated_at: 'Ginte ag',
            no_packs: 'Níl aon fianaise packs found. aimsithe',
            generate: 'Gin',
            content_type_consent_records: 'Toiliú Taifid',
            content_type_policy_versions: 'Polasaí Leaganacha',
            content_type_binding_history: 'Binding Stair',
            content_type_change_requests: 'Iarratais Athraithe',
            content_type_inconsistency_reports: 'Inconsistency Tuarascálacha',
            content_type_snapshot_data: 'Grianghraf Sonraí',
            content_type_audit_trail: 'Iniúchadh Trail',
        },
        audit: {
            title: 'Iniúchadh airí toilithe',
            event_type: 'Cineál Imeachta',
            entity_type: 'Entity Cineál',
            entity_id: 'ID Aonáin',
            actor: 'Gníomhaí',
            timestamp: 'Stampa Ama',
            no_events: 'Níl aon iniúchadh imeacht found. aimsithe',
            entity_type_property: 'Maoin',
            entity_type_group: 'Grúpa',
            entity_type_binding: 'Ceangal',
            entity_type_change_request: 'Iarratas ar athrú',
            entity_type_golden_template: 'Teimpléad órga',
            entity_type_snapshot_schedule: 'Sceideal grianghraif',
            entity_type_inconsistency: 'Neamhréiteach',
            entity_type_evidence_pack: 'Pacáiste fianaise',
            action_created: 'Cruthaithe',
            action_updated: 'Nuashonraithe',
            action_deleted: 'Scrioste',
            action_decommissioned: 'Díchoimisiúnaithe',
            action_status_changed: 'Stádas athraithe',
            action_submitted: 'Curtha isteach',
            action_approved: 'Faofa',
            action_rejected: 'Diúltaithe',
            action_deployed: 'Imscartha',
            action_rolled_back: 'Rollta siar',
            action_paused: 'Ar sos',
            action_resumed: 'Atosaíodh',
            action_cancelled: 'Cealaithe',
            action_acknowledged: 'Admhaithe',
            action_resolved: 'Réitithe',
            action_suppressed: 'Curtha faoi chois',
            action_executed: 'Forghníomhaithe',
        },
        property_types: {
            website: 'Suíomh gréasáin',
            mobile_app: 'Aip mhóibíleach',
            ott: 'OTT',
            iot_device: 'Gléas IoT',
            kiosk: 'Ciosc',
            api_service: 'Seirbhís API',
            sdk: 'SDK',
        },
        environments: {
            production: 'Táirgeadh',
            staging: 'Stáitseáil',
            development: 'Forbairt',
            testing: 'Tástáil',
        },
        group_types: {
            brand: 'Branda',
            region: 'Réigiún',
            product_line: 'Líne táirgí',
            business_unit: 'Aonad gnó',
            custom: 'Saincheaptha',
        },
        target_types: {
            property: 'Maoin',
            group: 'Grúpa',
            all: 'Gach',
        },
        frequencies: {
            hourly: 'Gach uair',
            daily: 'Laethúil',
            weekly: 'Seachtainiúil',
            monthly: 'Míosúil',
            custom: 'Saincheaptha',
        },
        binding_statuses: {
            current: 'Reatha',
            outdated: 'As dáta',
            pending: 'Ar feitheamh',
            deploying: 'Á imscaradh',
            rolled_back: 'Rollta siar',
        },
        sensitive_categories: {
            health: 'Sonraí Sláinte',
            biometric: 'Sonraí Bithmhéadracha',
            genetic: 'Sonraí Géiniteacha',
            racial_ethnic: 'Bunús Ciníoch / Eitneach',
            political_opinions: 'Tuairimí Polaitiúla',
            religious_beliefs: 'Creidimh Chreidimh',
            trade_union: 'Ballraíocht Cheardchumann',
            sexual_orientation: 'Claonadh Gnéis',
            criminal_records: 'Taifid Choiriúla',
            financial: 'Sonraí Airgeadais',
            children: 'Sonraí Leanaí',
            geolocation: 'Geolocation Beacht',
        },
        export_csv: 'Easportail CSV',
        exporting: 'Ag easportail...',
        page_x_of_y: 'Leathanach {page} as {total}',
        regulatory: {
            capture_method_guidance: 'Teastaíonn cruthúnas toilithe ó PDPL chun teacht leis an gcainéal bailiúcháin. Léarscáil de chineálacha réadmhaoine go modhanna gabhála toilithe: suíomh Gréasáin/OTT = toiliú bunaithe ar mheirge, aip shoghluaiste/SDK = sreabhadh toilithe in-aip, both = gabháil toiliú fisiciúil/ar an scáileán, API seirbhís = toiliú ríomhchláraithe, gléas IoT = meicníocht toilithe ar leibhéal an ghléis.',
            dashboard_metrics_info: 'Soláthraíonn an deais seo radharc aonair don DPO ar staidiúir an toilithe thar na hairíonna digiteacha go léir. I measc na bpríomh-mhéadracht tá: céatadán clúdaigh ceangailteach (imlonnaithe vs ceangail iomlána), cloí le teimpléid tríd an ngiuirléid neamhréireachtaí, iarratais ar athrú ar feitheamh a dteastaíonn athbhreithniú uathu, agus miondealú ar stádas maoine a thaispeánann maoine le stáit ghníomhacha, neamhghníomhacha nó díchoimisiúnaithe. Déantar airíonna le catagóirí sonraí íogaire a rianú tríd an gclár réadmhaoine.',
            change_request_impact_info: 'Ní mór d’iarratais ar athrú a dhéanann difear d’airíonna toilithe tionchar rialála a dhoiciméadú: a bhfuil tionchar ag PDPL ar cheanglais, cibé an ndéanann an t-athrú difear do phróiseáil sonraí íogaire (Airt. 2.4), agus an gá don athrú aththoiliú ó dhaoine is ábhar do na sonraí. Sonraíonn an achoimre tionchair i ngach iarratas ar athrú na PDPL tagairtí rialála le haghaidh fianaise iniúchóireachta.',
            outdated_binding_warning: 'Léiríonn ceangail le stádas “as dáta” go ritheann réadmhaoin leagan beartais nach bhfuil reatha. Seans nach léireoidh na hairíonna seo na PDPL riachtanais is déanaí nó nuashonruithe beartais eagraíochtúla. Athbhreithnigh agus nuashonraigh ceangail atá as dáta go pras chun PDPL comhlíonta a choinneáil ar fud gach maoine toilithe.',
        },
    },
    localization: {
        nav: {
            dashboard: 'Painéal',
            market_packs: 'Pacáistí margaidh',
            language_packs: 'Pacáistí teanga',
            frameworks: 'Creataí rialála',
            mappings: 'Mapáil modúil',
            submissions: 'Aighneachtaí pacáiste',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Réamhamharc aistriúcháin',
            audit: 'Rian iniúchta',
        },
        dashboard: {
            title: 'Painéal logánaithe',
            loading: 'Ag lódáil painéal sonraí...',
            active_market_packs: 'Pacáistí Margaidh Gníomhacha',
            published_packs_available: 'Foilsithe market packs available',
            language_packs: 'Teanga Packs',
            total_language_packs: 'Pacáistí teanga cláraithe iomlán',
            active_regulatory_frameworks: 'Creataí Rialála Gníomhacha',
            frameworks_active: 'Creataí with active stádas',
            pending_pack_submissions: 'Ar feitheamh Pack Submissions',
            submissions_awaiting_review: 'Submissions awaiting athbhreithniú',
            recent_audit_events: 'Le déanaí Iniúchadh Imeachtaí',
            no_recent_audit: 'No recent iniúchadh imeacht.',
            event_type: 'Cineál Imeachta',
            entity_type: 'Entity Cineál',
            entity_id: 'ID Aonáin',
            actor: 'Gníomhaí',
            timestamp: 'Stampa Ama',
        },
        market_packs: {
            title: 'Catalóg pacáistí margaidh',
            new: 'Nua Margadh Pack',
            pack_name: 'Pack Ainm',
            market: 'Margadh',
            locale: 'Logcháil',
            no_packs: 'Níl aon market packs found. aimsithe',
        },
        language_packs: {
            title: 'Pacáistí teanga',
            new: 'Nua Teanga Pack',
            language: 'Teanga',
            locale_code: 'Logcháil Cód',
            completeness: 'Iomláine',
            completeness_detail: 'Sonraí Comhláine',
            overall_completeness: 'Comhláine Foriomlán',
            total_keys: 'Eochracha Iomlán',
            translated_keys: 'Eochracha Aistrithe',
            missing_keys: 'Eochracha ar Iarraidh',
            namespace_breakdown: 'Miondealú Ainmspáis',
            namespace: 'Ainmspás',
            translated: 'Aistrithe',
            no_packs: 'Níl aon language packs found. aimsithe',
            install_language_pack: 'Suiteáil pacáiste teanga',
            available_packs: 'Pacáistí teanga ar fáil',
            install: 'Suiteáil',
            all_installed: 'Tá gach pacáiste teanga suiteáilte cheana féin.',
            install_success: 'Pacáiste teanga suiteáilte go rathúil.',
            market: 'Margadh',
            featured: 'Roghnaithe',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Creataí rialála',
            new: 'Nua Creat',
            framework_name: 'Creat Ainm',
            jurisdiction: 'Dlínse',
            effective_date: 'Effective Dáta',
            no_frameworks: 'Níl aon regulatory creat found. aimsithe',
            view_requirements: 'Féach ar riachtanais',
            hide_requirements: 'Folaigh riachtanais',
            show_legacy: 'Taispeáin creataí oidhreachta',
            effective_until: 'I bhfeidhm go dtí',
            effective_on: 'I bhfeidhm ó',
            replaced_by: 'Ionadaithe ag',
            replaces_label: 'Ionadaíonn',
            status_active: 'Gníomhach',
            status_draft: 'Dréacht',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Oidhreacht',
            status_upcoming: 'Ag teacht',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Liosta faire',
            watchlist_empty: 'Níl dréacht ná creata ar bith ag teacht',
            watchlist_empty_desc: 'Beidh creata le stádas "dréacht" nó "le teacht" le feiceáil anseo le haghaidh monatóireachta.',
            expected_effective: 'Bhíothas ag súil go héifeachtach',
            effective_date_tbd: 'Dáta éifeachtach: TBD',
            activate: 'Gníomhachtaigh',
            ai_law_title: 'Vítneam AI Dlí',
            ai_risk_classification: 'AI Aicmiú Riosca',
            ai_system_dossier: 'AI Sainchomhad an Chórais',
            ai_labeling_checklist: 'AI Lipéadú & Nochtadh',
            ai_incident_report: 'AI Tuairisc Teagmhais',
            ai_impact_assessment: 'AI Measúnú Tionchair',
            ai_sandbox_application: 'Feidhmchlár Bosca Gainimh',
            ai_sandbox_monitoring: 'Monatóireacht Bosca Gainimh',
            ai_sandbox_completion: 'Críochnú Bosca Gainimh',
            ai_submission_tracker: 'Lorgaire Aighneachta',
            ai_evidence_capture: 'Gabháil Fianaise',
            risk_level_minimal: 'Riosca Íosta',
            risk_level_limited: 'Riosca Teoranta',
            risk_level_high: 'Riosca Ard',
            risk_level_unacceptable: 'Riosca Do-ghlactha',
            submission_status_draft: 'Dréacht',
            submission_status_submitted: 'Arna chur isteach',
            submission_status_returned: 'Ar ais le haghaidh Leasú',
            submission_status_accepted: 'Glactar leis',
            submission_status_rejected: 'Diúltaithe',
            submission_status_closed: 'Dúnta',
            ecommerce_law_title: 'Dlí Ríomhthráchtáil Vítneam',
            ecommerce_platform_classification: 'Aicmiú Ardán',
            ecommerce_seller_register: 'Clár Díoltóra',
            ecommerce_complaint_register: 'Clár Gearán',
            ecommerce_takedown_register: 'Clár Takedown',
            ecommerce_submission_tracking: 'Rianú Aighneachtaí',
            ecommerce_dossier_checklist: 'Seicliosta Comhaid',
            ecommerce_cross_border: 'Comhlíonadh Trasteorann',
            ecommerce_trustmark: 'Clár Trustmark',
            ecommerce_notification: 'Fógra',
            ecommerce_registration: 'Clárú',
            ecommerce_termination: 'Foirceannadh',
            ecommerce_reporting_readiness: 'Ullmhacht Tuairiscithe',
            ecommerce_evidence_capture: 'Gabháil Fianaise',
            submission_status_approved: 'Ceadaithe',
            cybersec_law_2025_title: 'Dlí Cibearshlándála Vítneam (2025)',
            cybersec_system_classification: 'Aicmiú Córais',
            cybersec_risk_assessment: 'Measúnú Riosca',
            cybersec_incident_report: 'Tuarascáil Teagmhais',
            cybersec_authority_cooperation: 'Comhar Údaráis',
            cybersec_data_localization: 'Logánú Sonraí',
            cybersec_vendor_security: 'Slándáil Díoltóra',
            cybersec_submission_tracker: 'Lorgaire Aighneachta',
            cybersec_evidence_capture: 'Gabháil Fianaise',
            cybersec_dossier_checklist: 'Seicliosta Comhaid',
            cybersec_classification_general: 'Ginearálta',
            cybersec_classification_important: 'Tábhachtach',
            cybersec_classification_critical: 'Criticiúil',
            data_law_title: 'Dlí Sonraí Vítneam',
            data_inventory: 'Fardal Sonraí',
            data_classification: 'Aicmiú Sonraí',
            data_sharing: 'Comhroinnt Sonraí',
            data_governance: 'Rialachas Sonraí',
            data_submission_tracker: 'Lorgaire Aighneachta',
            data_evidence_capture: 'Gabháil Fianaise',
            data_dossier_checklist: 'Seicliosta Comhaid',
            telecom_law_title: 'Dlí Teileachumarsáide Vítneam',
            telecom_authority_request: 'Iarratas Údaráis',
            telecom_disclosure: 'Nochtadh',
            telecom_confidentiality: 'Rúndacht',
            telecom_submission_tracker: 'Lorgaire Aighneachta',
            telecom_evidence_capture: 'Gabháil Fianaise',
            telecom_dossier_checklist: 'Seicliosta Comhaid',
            pdpl_law_title: 'Dlí Vítneam um Chosaint Sonraí',
            pdpl_breach_notification: 'Fógra Sárú',
            pdpl_dpo_notification: 'DPO Fógra Coinne',
            pdpl_data_subject_notification: 'Fógra Ábhar Sonraí',
            pdpl_submission_tracker: 'Lorgaire Aighneachta',
            pdpl_evidence_capture: 'Gabháil Fianaise',
            pdpl_dossier_checklist: 'Seicliosta Comhaid',
            pdpl_authority_filing: 'Comhdú Údaráis',
            pdpl_mops_filing: 'Comhdú MoPS',
        },
        mappings: {
            title: 'Mapáil modúil',
            new: 'Nua Mapping',
            module: 'Modúl',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Toiliú',
            module_dpia: 'DPIA',
            module_incident: 'teagmhas',
            module_rights_request: 'Iarratas Cearta',
            module_data_mapping: 'Mapáil Sonraí',
            module_classification: 'Aicmiú',
            module_monitoring: 'Monatóireacht',
            module_governance: 'Rialachas',
            module_vendor: 'Díoltóir',
            module_risk: 'Riosca',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            module_service_certificate: 'Processing Service Certificate',
            market_pack: 'Pacáiste Margaidh',
            mapping_status: 'Mapping Stádas',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: 'Níl aon module mappings found. aimsithe',
            incomplete_entries_warning: '{count} mapáil(í) i bhfolach mar gheall ar ID creata, ID riachtanais nó modúl ar iarraidh.',
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Aighneachtaí pacáiste',
            new: 'Nua Submission',
            submitter: 'Cuireoir Isteach',
            pack: 'Pacáiste',
            submitted_at: 'Curtha isteach',
            no_submissions: 'Níl aon pack submissions found. aimsithe',
            installed_sector_overlays_title: 'Forleagan Earnála ({count})',
            installed_sector_overlays_aria: 'Forleagan Earnála Suiteáilte',
            installed_col_name: 'Ainm',
            installed_col_code: 'Cód',
            installed_col_sector: 'Earnáil',
            installed_col_mode: 'Mód',
            installed_col_status: 'Stádas',
            installed_refresh: 'Athnuaigh',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Réamhamharc aistriúcháin',
            select_locale: 'Roghnaigh logcháil',
            select_module: 'Roghnaigh modúl',
            key: 'Key',
            value: 'Luach',
            no_translations: 'Níl aon translations found. aimsithe',
        },
        audit: {
            title: 'Localization Iniúchadh Trail',
            event_type: 'Cineál Imeachta',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: 'Entity Cineál',
            entity_id: 'ID Aonáin',
            entity_name: 'Ainm an eintitis',
            actor: 'Gníomhaí',
            timestamp: 'Stampa Ama',
            payload: 'Pálasta',
            collapse_payload: 'Comhbhrúigh sonraí pálasta',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Leathnaigh sonraí pálasta',
            collapse: 'Comhbhrúigh',
            expand: 'Leathnaigh',
            all_entity_types: 'Gach cineál eintitis',
            no_events: 'Níl aon iniúchadh imeacht found. aimsithe',
        },
        market_packs_detail: {
            installed_at: 'Suiteáilte ar',
            pack_details_label: 'Sonraí pacáiste',
            market_code: 'Cód margaidh',
            supported_locales: 'Logánachtaí tacaithe',
            min_platform_version: 'Leagan íosta ardáin',
            pack_information: 'Faisnéis pacáiste',
            pack_contents: 'Ábhar an Phacáiste',
            included_frameworks: 'Creataí san áireamh',
            pack_artifacts: 'Déantáin pacáiste',
            no_artifacts: 'Níor aimsíodh aon déantán don phacáiste seo',
            pack_not_found: 'Pacáiste gan aimsiú',
        },
        mappings_form: {
            framework_id: 'ID creata',
            requirement_id: 'ID riachtanais',
            module_label: 'Modúl',
            mapping_type: 'Cineál mapála',
            configuration_json: 'Cumraíocht (JSON)',
            conflict_resolution: 'Réiteach coinbhleachta',
            is_override: 'Is sárú é',
            all_modules: 'Gach modúl',
            all_mapping_types: 'Gach cineál mapála',
        },
        submissions_detail: {
            submission_id: 'ID aighneachta',
            validation: 'Bailíochtú',
            reviewers: 'Athbhreithnithe',
            start_review: 'Tosaigh athbhreithniú',
            hide_comments: 'Folaigh nótaí tráchta',
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Nótaí tráchta athbhreithnithe',
            loading_comments: 'Ag luchtú nótaí tráchta...',
            no_comments: 'Gan nótaí tráchta fós.',
            add_comment: 'Cuir nóta tráchta leis',
            reject_submission: 'Diúltaigh aighneacht',
            reason_for_rejection: 'Fáth an diúltaithe',
            reviewer_ids: 'IDanna athbhreithnithe (scartha le camóga)',
            submit_new_pack: 'Cuir pacáiste nua isteach',
            pack_type: 'Cineál pacáiste',
            market_code: 'Cód margaidh',
            target_locales: 'Logánachtaí sprice',
            pack_file: 'Comhad pacáiste',
            pack_format_info: 'Formáid an phacáiste: Ba cheart go n-áireofaí i bpacáistí margaidh comhaid a bhaineann go sonrach le logánta JSON le hainmspás/aistriúcháin eochrach. Ba cheart go soláthródh pacáistí teanga aistriúcháin do na logaí sonraithe. Féach ar an doiciméadú le haghaidh scéimre mionsonraithe.',
            accepted_formats: 'Formáidí a nglactar leo: .json nó .zip. JSON comhad ba chóir go mbeadh réad cothrom nó ainmspás de péirí eochair-luach aistriúcháin. Ba cheart go mbeadh JSON comhad locale amháin nó níos mó i gcomhaid ZIP.',
            comma_separated_locales: 'Cóid logánaithe scartha le camóga don phacáiste teanga seo',
            confirm_approval: 'Deimhnigh ceadú',
            approve_message: 'Ceadaigh an cur isteach pacáiste seo?',
            submission_label: 'Aighneacht',
            all_pack_types: 'Gach cineál pacáiste',
            passed: "d'éirigh",
            failed_label: 'theip',
            market_code_required: 'Tá cód margaidh riachtanach',
            version_required: 'Tá leagan riachtanach',
            pack_type_regulatory: 'Rialála',
            framework_code: 'Cód creata',
            jurisdiction_code: 'Cód dlínse',
            pack_source: 'Foinse',
            pack_source_manual: 'Láimhe',
            pack_version: 'Leagan an phacáiste',
            install_regulatory_pack: 'Suiteáil pacáiste rialála',
            regulatory_pack_desc: 'Uaslódáil pacáiste creata rialála (.zip)',
            framework_required: 'Tá cód an chreata rialála ag teastáil',
            jurisdiction_required: 'Tá cód dlínse ag teastáil',
        },
        catalog_detail: {
            packs_installed_of: 'as {total} pacáiste suiteáilte',
            install_all: 'Suiteáil gach ceann',
            installing: 'Ag suiteáil...',
            removing: 'Á bhaint...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Riachtanach',
            tab_catalog: 'Catalóg',
            tab_installed: 'Suiteáilte',
            details: 'Sonraí',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: 'Logánacht foinse',
            target_locale: 'Logánacht sprice',
            locales_must_differ: 'Caithfidh na logánachtaí foinse agus sprice a bheith éagsúil.',
            all_keys_translated: 'Gach eochair aistrithe',
            keys_missing: '{count} eochair(eanna) in easnamh',
            show_missing_only: 'Taispeáin ar iarraidh amháin',
            namespace: 'Ainmspás',
            source: 'Foinse',
            target: 'Sprioc',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Creataí',
            tab_watchlist: 'Liosta faire',
            authority: 'Údarás',
            requirements_count: 'Riachtanais',
            all_types: 'Gach cineál',
            collapse_all: 'Comhbhrúigh gach ceann',
            expand_all: 'Leathnaigh gach ceann',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Scagtha go',
            clear_filter: 'Glan scagaire',
            no_requirements: 'Níor aimsíodh aon riachtanas don chreat seo.',
            requirements_label: 'Riachtanais',
            status_updated: 'Stádas nuashonraithe go rathúil',
            overview_tab: 'Forbhreathnú',
            forms_tab: 'Foirmeacha',
            procedures_tab: 'Nósanna imeachta',
            fines_risks_tab: 'Fíneálacha agus rioscaí',
            deadlines_tab: 'Spriocdhátaí',
            guidance_tab: 'Treoir',
            guidance_empty: 'Níl aon ábhar treorach don chreat seo.',
            guidance_loading: 'Treoir á lódáil…',
            guidance_section: 'Rannóg treorach',
            guidance_view: 'Féach ar Threoir',
            guidance_map_to_workflow: 'Léarscáil go Sreabhadh Oibre',
            guidance_link_evidence: 'Fianaise Nasc',
            guidance_filter_type: 'Cineál',
            guidance_filter_category: 'Catagóir',
            guidance_filter_module: 'Modúl',
            guidance_filter_status: 'Stádas',
            guidance_filter_search: 'Treoir chuardaigh',
            guidance_filter_all: 'Gach',
            guidance_table_reference: 'Tagairt',
            guidance_table_name: 'Ainm Treorach',
            guidance_table_type: 'Cineál',
            guidance_table_category: 'Catagóir',
            guidance_table_effective_date: 'Dáta Éifeachtach',
            guidance_table_modules: 'Modúil Ghaolmhara',
            guidance_table_actions: 'Gníomhartha',
            guidance_reference_only_banner: "Tagairt amháin - baineann sé leis an earnáil {{sector}} ach ní le d'eagraíocht.",
            guidance_open_official_source: 'Oscail an fhoinse oifigiúil',
            guidance_no_detail: 'Níl aon sonraí breise ar fáil don treoir seo.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Treoir Ghaolmhara',
            deadlines_empty: 'Níor aimsíodh spriocdhátaí rialála don chreat seo.',
            deadlines_sla_type: 'Cineál SLA',
            deadlines_value: 'Spriocdháta',
            deadlines_unit: 'Aonad',
            deadlines_extension: 'Síneadh',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Tagairt dlí',
            framework_stack: 'Struchtúr an chreata',
            layer_parent_law: 'Príomhdhlí',
            layer_implementing_decree: 'Foraithne cur chun feidhme',
            layer_enforcement_decree: 'Foraithne forfheidhmithe',
            layer_admin_procedures: 'Nósanna imeachta riaracháin',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Ionstraimí faoi réir',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Gníomhach',
            status_draft: 'Dréacht',
            draft_enforcement_badge: 'Dréacht — nach bhfuil achtaithe fós',
            effective_date_draft: 'Dréacht — níl i bhfeidhm fós',
            effective_date: 'Dáta éifeachtach',
            forms_table_id: 'ID foirme',
            forms_table_name: 'Ainm',
            forms_table_source: 'Foinse',
            forms_table_category: 'Catagóir',
            forms_table_type: 'Cineál',
            fill_form: 'Líon isteach foirm',
            view_reference: 'Amharc ar Thagairt',
            forms_empty: 'Níor aimsíodh aon fhoirm don chreat seo.',
            forms_loading: 'Foirmeacha á lódáil...',
            filter_by_source_layer: 'Scag de réir ciseal foinse',
            all_layers: 'Gach Sraitheanna',
            procedures_id: 'ID nós imeachta',
            procedures_name: 'Nós imeachta',
            procedures_trigger: 'Truicear',
            procedures_steps: 'Céimeanna',
            procedures_outputs: 'Aschuir',
            procedures_related_forms: 'Foirmeacha gaolmhara',
            procedures_submission_channels: 'Cainéil Aighneachta',
            procedures_empty: 'Níor aimsíodh aon nós imeachta don chreat seo.',
            fines_area: 'Limistéar sáraithe',
            fines_articles: 'Ailt',
            fines_individual: 'Fíneáil (duine aonair)',
            fines_organization: 'Fíneáil (eagraíocht)',
            fines_revenue: 'Ardú ioncaim',
            fines_deadlines: 'Spriocdhátaí',
            fines_remediation: 'Bearta ceartaitheacha',
            fines_related: 'Nósanna imeachta gaolmhara',
            fines_empty: 'Níl aon sonraí forfheidhmithe ar fáil don chreat seo.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Cineál riosca',
            fines_risk_triggers: 'Cad a spreagann é',
            fines_risk_only_note: 'Cuireann na hiontrálacha seo síos ar riosca comhlíonta agus oibríochtúil, ní ar phionóis airgeadaíochta. Nuair a bhaineann fíneálacha, sainmhínítear iad sa chreat tuismitheora.',
            fines_enforcement_pending: 'Níl an creat forfheidhmithe foilsithe fós. Cuirfear sonraí smachtbhannaí leis nuair a fhógraítear an ionstraim cur chun feidhme.',
            view_details: 'Féach ar shonraí',
            back_to_list: 'Ar ais go dtí an liosta',
            description: 'Cur síos',
            jurisdiction: 'Dlínse',
            lineage: 'Líne shinsearachta',
            replaces: 'Cuireann in ionad',
            replaced_by: 'Curtha in ionad ag',
            requirements: 'Riachtanais',
            tab_navigation: 'Cluaisíní sonraí an chreata',
            forms_table_actions: 'Gníomhartha',
            create_form: 'Cruthaigh foirm',
            mark_promulgated: 'Marcáil mar fhógartha',
            status_promulgated: 'Fógartha',
            promulgated_success: 'Stádas na sraithe nuashonraithe go fógartha',
            transition_review: 'Athbhreithniú aistrithe',
            transition_review_desc: 'Tá athbhreithniú aistrithe ar feitheamh ag an gcreat seo le haghaidh glacadh le dlí comharbais.',
            transition_review_by: 'Spriocdháta athbhreithnithe',
            transition_review_status: 'Stádas athbhreithnithe',
            transition_review_notes: 'Nótaí',
            transition_review_pending: 'Ar feitheamh',
            transition_review_in_progress: 'Ar siúl',
            transition_review_completed: 'Críochnaithe',
            transition_review_successor: 'Creat comharbais',
            transition_review_lead_time: 'Am ullmhúcháin',
            transition_review_lead_time_days: '{days} lá roimh an dáta éifeachta',
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Athbhreithnithe aistrithe foirmeacha',
            transition_review_forms_desc: 'Déan athbhreithniú ar gach teimpléad oibríochtúil maidir le hábharthacht faoin gcreat comharbais.',
            transition_review_outcome: 'Toradh',
            transition_review_outcome_carry_forward: 'Coinneáil',
            transition_review_outcome_carry_forward_revised: 'Coinneáil (athbhreithnithe)',
            transition_review_outcome_retire: 'Scor',
            transition_review_outcome_split: 'Scoilt',
            transition_review_outcome_awaiting_decree: 'Ag fanacht le foraithne',
            transition_review_no_forms: 'Níl athbhreithnithe aistrithe foirmeacha cumraithe.',
            transition_review_update_success: 'Athbhreithniú aistrithe nuashonraithe go rathúil',
            transition_review_operational_note: 'Is teimpléid oibríochtúla tacaíochta próisis iad seo, ní áirimh reachtúla foirmeacha. Fanann an lua dlíthiúil leis an gcreat oidhreachta go dtí go dtagann an dlí comharbais i bhfeidhm.',
            rights_tab: 'Cearta',
            rights_empty: 'Níl aon chearta damhna sonraí sainithe don chreat seo.',
            rights_code: 'Cód',
            rights_name: 'Ceart',
            rights_legal_basis: 'Bunús Dlí',
            rights_elaborating_articles: 'Airteagail Mhínithe',
            rights_description: 'Cur síos',
            requirements_reference: 'Tagairt',
            requirements_mandatory: 'Éigeantach',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Foinse',
            category_legal_basis: 'Bunús Dlí',
            category_data_subject_rights: 'Cearta Ábhar Sonraí',
            category_notification: 'Fógra',
            category_assessment: 'Measúnú',
            category_governance: 'Rialachas',
            category_record_keeping: 'Coimeád Taifead',
            category_cross_border: 'Trasteorann',
            category_technical_measures: 'Bearta Teicniúla',
            category_incident_reporting: 'Tuairisciú Teagmhais',
            category_risk_management: 'Bainistíocht Riosca',
            category_supply_chain: 'Slabhra Soláthair',
            category_certification: 'Deimhniú',
            child_frameworks: '{count} creat leanaí',
            child_frameworks_plural: '{count} creat leanaí',
            replaces_prefix: 'Cuirtear in ionad:',
            replaced_by_prefix: 'Arna ionadú ag:',
            responsible_prefix: 'Freagrach:',
            watchlist_empty_hint: 'Beidh creata le stádas "dréacht" nó "le teacht" le feiceáil anseo le haghaidh monatóireachta.',
            framework_count: '{count} creat',
            framework_count_plural: '{count} creat',
            hiding_legacy: '({count} oidhreacht i bhfolach)',
            label_VN_PDPD_DECREE_13: 'Foraithne PDPD 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Dlí 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Foraithne 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: 'PDPL Dréacht Forfheidhmithe 2026',
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Dlí Sonraí',
            label_VN_DATA_LAW_DECREE_165_2025: 'Foraithne Dlí Sonraí 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Dréacht-Mhalartuithe Sonraí 2026',
            label_VN_TELECOM_LAW_2023: 'An Dlí Teileachumarsáide, 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Foraithne Teileachumarsáide 163/2024',
            label_VN_CYBERSEC_LAW: 'Dlí Cibearshlándála (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Foraithne Cybersec 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Dlí Cibearshlándála 116/2025',
            label_VN_ECOMMERCE_LAW_2025: 'Dlí Ríomhthráchtáil 122/2025',
            label_VN_AI_LAW_2026: 'AI Dlí 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            issuer_company: 'Cuideachta',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Údarás',
            sla_type_rights_request_ack: 'Admháil iarratais um chearta',
            'sla_type_Access control review': 'Athbhreithniú rialaithe rochtana',
            'sla_type_Annual governance attestation': 'Fianú rialachais bliantúil',
            'sla_type_Audit review cycle': 'Timthriall athbhreithnithe iniúchta',
            'sla_type_Authority request response': 'Freagra ar iarratas an Údaráis',
            'sla_type_Change notification processing': 'Athraigh próiseáil fógraí',
            'sla_type_Complaint acknowledgment': 'Admháil ghearáin',
            'sla_type_Complaint resolution': 'Réiteach gearáin',
            'sla_type_Confidentiality policy review': 'Athbhreithniú ar bheartas rúndachta',
            'sla_type_Cybersecurity incident full report': 'Tuarascáil iomlán ar theagmhas Cybersecurity',
            'sla_type_Cybersecurity incident initial report': 'Tuarascáil tosaigh ar theagmhais Cybersecurity',
            'sla_type_Data inventory review': 'Athbhreithniú fardail sonraí',
            'sla_type_Disclosure log review cycle': 'Timthriall athbhreithnithe loga nochta',
            'sla_type_Incident classification decision': 'Cinneadh aicmithe teagmhas',
            'sla_type_Information system classification review': 'Athbhreithniú ar aicmiú córais faisnéise',
            'sla_type_Notification processing (sales website/app)': 'Próiseáil fógraí (láithreán gréasáin/aip díolacháin)',
            'sla_type_Pen test cycle': 'Timthriall tástála peann',
            'sla_type_Registration processing (service platform)': 'Próiseáil clárúcháin (ardán seirbhíse)',
            'sla_type_Risk assessment cycle': 'Timthriall measúnaithe riosca',
            'sla_type_Takedown decision (after review)': 'Cinneadh éirí anuas (tar éis athbhreithniú)',
            'sla_type_Termination notice (public)': 'Fógra foirceanta (poiblí)',
            sla_type_ai_impact_assessment_completion: 'AI measúnú tionchair críochnaithe',
            sla_type_ai_sandbox_application_review: 'AI léirmheas feidhmchlár bosca gainimh',
            sla_type_ai_serious_incident_report: 'AI tuarascáil teagmhais thromchúiseach',
            sla_type_ai_system_registration: 'AI clárúchán córais',
            sla_type_authority_request_response: 'Freagra ar iarratas an Údaráis',
            sla_type_certificate_issuance_review: 'Léirmheas ar eisiúint deimhnithe',
            sla_type_complaint_handling_approach_notice: 'Fógra cur chuige láimhseála gearán',
            sla_type_complaint_incomplete_info_request: 'Iarratas faisnéise neamhiomlán a dhéanamh',
            sla_type_data_governance_attestation: 'Fianú rialachais sonraí',
            sla_type_dsr_acknowledgment: 'Admháil DSR',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Cl.2) — Tarraing siar / srian / réad',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Cl.2) — Tríú páirtí',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Cl.3) — Rochtain / ceart / soláthar',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Cl.3) — Tríú páirtí',
            sla_type_dsr_cl4_deletion: 'DSR (Cl.4) — Scriosadh',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Cl.4) — Scriosadh, tríú páirtí',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Cl.5) — Bearta cosanta',
            sla_type_telecom_authority_request_response: 'Freagra ar iarratas údaráis teileachumarsáide',
            sla_type_rights_request_completion: 'Iarratas um chearta a chomhlánú',
            sla_type_incident_authority_notification: 'Fógra údaráis teagmhas',
            sla_type_incident_subject_notification: 'Fógra faoi ábhar teagmhas',
            sla_type_dpia_completion: 'DPIA críochnaithe',
            sla_type_data_retention_review: 'Athbhreithniú ar choinneáil sonraí',
            sla_type_authority_filing_review: 'Athbhreithniú comhdaithe an Údaráis',
            sla_type_authority_supplement_response: 'Freagra forlíonta an Údaráis',
            sla_type_authority_resubmission: 'Ath-aighneacht an Údaráis',
            sla_type_authority_filing_supplement_response: 'Freagra forlíonta comhdaithe an Údaráis',
            sla_type_authority_filing_resubmission: 'Ath-aighneacht comhdaithe an Údaráis',
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            // Sprint 62.57.0 Slice E — the certificate register's period rows (stream P's sla_types).
            sla_type_certificate_application_evaluation: 'Evaluation of the certificate application',
            sla_type_certificate_deficiency_notice: 'Notice that the certificate application must be supplemented',
            sla_type_certificate_replacement_decision: 'Decision on re-issue or exchange of the certificate',
            sla_type_certificate_revoked_return: 'Return of a revoked certificate',
            unit_working_days: 'laethanta oibre',
            unit_calendar_days: 'laethanta féilire',
            unit_hours: 'uair an chloig',
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Toiliú',
            form_category_privacy: 'Príobháideacht',
            form_category_breach: 'Sárú',
            form_category_dpia: 'DPIA',
            form_category_register: 'Clár',
            form_category_monitoring: 'Monatóireacht',
            form_category_vendor: 'Díoltóir',
            form_category_org: 'Eagrúcháin',
            form_category_sector: 'Earnáil',
            form_category_data_mapping: 'Mapáil Sonraí',
            form_category_governance: 'Rialachas',
            form_category_incident: 'teagmhas',
            form_category_authority_submission: 'Aighneacht an Údaráis',
            form_category_notice: 'Fógra',
            form_category_assessment_report: 'Tuarascáil Mheasúnaithe',
            form_category_request: 'Iarratas',
            form_category_dossier: 'Comhad',
            form_category_filing: 'Comhdú',
            module_consent: 'Toiliú',
            module_rights_request: 'Iarratas Cearta',
            module_classification: 'Aicmiú',
            module_dpia: 'DPIA',
            module_governance: 'Rialachas',
            module_monitoring: 'Monatóireacht',
            module_register: 'Clár',
            module_risk: 'Riosca',
            module_data_mapping: 'Mapáil Sonraí',
            module_incident: 'teagmhas',
            module_assessment: 'Measúnú',
            module_vendor: 'Díoltóir',
            module_policy: 'Beartas',
            module_audit: 'Iniúchadh',
            module_training: 'Traenáil',
            module_discovery: 'Fionnachtain',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            enum_guidance_type_calculation_guidance: 'Treoir Ríomh',
            enum_guidance_type_implementation_guidance: 'Treoir Forfheidhmithe',
            enum_guidance_type_interpretive_note: 'Nóta Léirmhínithe',
            enum_guidance_type_legal_guidance: 'Treoir Dlí',
            enum_guidance_type_procedural_guidance: 'Treoir Nós Imeachta',
            enum_guidance_type_sector_guidance: 'Treoir Earnála',
            enum_guidance_type_technical_methodology: 'Modheolaíocht Theicniúil',
            enum_guidance_category_annual_reporting: 'Tuairisciú Bliantúil',
            enum_guidance_category_authority_cooperation: 'Comhar Údaráis',
            enum_guidance_category_authority_coordination: 'Comhordú an Údaráis',
            enum_guidance_category_business_continuity: 'Leanúnachas Gnó',
            enum_guidance_category_capital_adequacy: 'Leordhóthanacht Chaipitil',
            enum_guidance_category_complaint_handling: 'Láimhseáil Gearán',
            enum_guidance_category_consent: 'Toiliú',
            enum_guidance_category_consent_management: 'Bainistiú Toilithe',
            enum_guidance_category_consumer_protection: 'Cosaint Tomhaltóirí',
            enum_guidance_category_control_environment: 'Timpeallacht Rialaithe',
            enum_guidance_category_cross_border: 'Trasteorann',
            enum_guidance_category_cross_border_transfers: 'Aistrithe Trasteorann',
            enum_guidance_category_data_classification: 'Aicmiú Sonraí',
            enum_guidance_category_data_localization: 'Logánú Sonraí',
            enum_guidance_category_data_protection_impact_assessment: 'Measúnú Tionchair ar Chosaint Sonraí',
            enum_guidance_category_direct_marketing: 'Margaíocht Dhíreach',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Rialachas',
            enum_guidance_category_ict_risk_management: 'Bainistíocht Riosca TFC',
            enum_guidance_category_incident_management: 'Bainistíocht Teagmhais',
            enum_guidance_category_internal_audit: 'Iniúchadh Inmheánach',
            enum_guidance_category_isms_documentation: 'Doiciméadú ISMS',
            enum_guidance_category_lawful_basis: 'Bunús Dlíthiúil',
            enum_guidance_category_licensing: 'Ceadúnú',
            enum_guidance_category_local_presence: 'Láithreacht Áitiúil',
            enum_guidance_category_logging_and_retention: 'Logáil & Coinneáil',
            enum_guidance_category_logical_and_physical_access: 'Rochtain Loighciúil & Fhisiciúil',
            enum_guidance_category_operational_resilience_testing: 'Tástáil Athléimneachta Oibriúcháin',
            enum_guidance_category_outsourcing: 'Foinsiú allamuigh',
            enum_guidance_category_platform_registration: 'Clárú Ardán',
            enum_guidance_category_quality_of_service: 'Cáilíocht Seirbhíse',
            enum_guidance_category_risk_classification: 'Aicmiú Riosca',
            enum_guidance_category_risk_management: 'Bainistíocht Riosca',
            enum_guidance_category_scope_definition: 'Scóip Sainmhíniú',
            enum_guidance_category_sectoral_localisation: 'Logánú Earnála',
            enum_guidance_category_subscriber_protection: 'Cosaint do Shíntiúsóirí',
            enum_guidance_category_supply_chain: 'Slabhra Soláthair',
            enum_guidance_category_system_classification: 'Aicmiú Córais',
            enum_guidance_category_system_operations: 'Oibríochtaí Córais',
            enum_guidance_category_telecommunications_data: 'Sonraí Teileachumarsáide',
            enum_guidance_category_third_party_risk: 'Riosca Tríú Páirtí',
            enum_guidance_category_transitional_provisions: 'Forálacha Idirthréimhseacha',
            enum_guidance_category_vulnerability_management: 'Bainistíocht Leochaileachta',
            enum_guidance_status_active: 'Gníomhach',
            enum_guidance_status_draft: 'Dréacht',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: 'Bainistíocht Rochtana',
            enum_module_ai_governance: 'AI Rialachas',
            enum_module_asset_management: 'Bainistíocht Sócmhainní',
            enum_module_audit_trail: 'Rian Iniúchta',
            enum_module_breach_notification: 'Fógra Sárú',
            enum_module_capital_adequacy: 'Leordhóthanacht Chaipitil',
            enum_module_classification: 'Aicmiú',
            enum_module_consent: 'Toiliú',
            enum_module_consent_management: 'Bainistiú Toilithe',
            enum_module_cross_border_transfers: 'Aistrithe Trasteorann',
            enum_module_data_localization: 'Logánú Sonraí',
            enum_module_data_mapping: 'Mapáil Sonraí',
            enum_module_data_protection: 'Cosaint Sonraí',
            enum_module_data_subject_rights: 'Cearta Ábhar Sonraí',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Criptiú',
            enum_module_governance: 'Rialachas',
            enum_module_incident: 'teagmhas',
            enum_module_incident_management: 'Bainistíocht Teagmhais',
            enum_module_internal_audit: 'Iniúchadh Inmheánach',
            enum_module_isms_documentation: 'Doiciméadú ISMS',
            enum_module_isms_governance: 'Rialachas ISMS',
            enum_module_logging_and_monitoring: 'Logáil & Monatóireacht',
            enum_module_monitoring: 'Monatóireacht',
            enum_module_policy_management: 'Bainistíocht Beartais',
            enum_module_risk: 'Riosca',
            enum_module_risk_management: 'Bainistíocht Riosca',
            enum_module_training: 'Traenáil',
            enum_module_vendor: 'Díoltóir',
            enum_module_vendor_management: 'Bainistíocht Díoltóra',
            enum_module_vendor_risk: 'Riosca Díoltóra',
            enum_module_vulnerability_management: 'Bainistíocht Leochaileachta',
            // Sprint 62.58.0 (walk O-6) — English copy; translated in Phase 25 (R-57).
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Rochtain',
            category_availability: 'Infhaighteacht',
            category_change_management: 'Bainistíocht Athruithe',
            category_classification: 'Aicmiú',
            category_communication_and_information: 'Cumarsáid & Faisnéis',
            category_compliance: 'Comhlíonadh',
            category_confidentiality: 'Rúndacht',
            category_control_activities: 'Gníomhaíochtaí Rialaithe',
            category_control_environment: 'Timpeallacht Rialaithe',
            category_incident: 'teagmhas',
            category_internal_audit: 'Iniúchadh Inmheánach',
            category_internal_control: 'Rialú Inmheánach',
            category_logical_and_physical_access: 'Rochtain Loighciúil & Fhisiciúil',
            category_mis_data: 'Sonraí an Chórais Faisnéise Bainistíochta',
            category_monitoring: 'Monatóireacht',
            category_monitoring_activities: 'Gníomhaíochtaí Monatóireachta',
            category_outsourcing: 'Foinsiú allamuigh',
            category_privacy: 'Príobháideacht',
            category_processing_integrity: 'Ionracas Próiseála',
            category_reporting: 'Tuairisciú',
            category_risk_assessment: 'Measúnú Riosca',
            category_risk_mitigation: 'Maolú Riosca',
            category_sector_applicability: 'Infheidhmeacht Earnála',
            category_system_operations: 'Oibríochtaí Córais',
            category_training: 'Traenáil',
            category_vendor: 'Díoltóir',
            guidance_related_forms_advisory: 'Tá treoir ghaolmhar ag foirmeacha áirithe — dearcadh sa',
            guidance_related_procedures_advisory: 'Tá treoir ghaolmhar ag roinnt nósanna imeachta — dearcadh sa',
            guidance_related_requirements_advisory: 'Tá treoir ghaolmhar ag roinnt ceanglais — dearcadh sa',
        },
        channels: {
            title: 'Cainéil aighneachta agus spriocdháta',
            channels: 'Cainéil aighneachta',
            add_channel: 'Cuir cainéal leis',
            channel_type: 'Cineál',
            name: 'Ainm',
            instructions: 'Treoracha',
            active: 'Gníomhach',
            no_channels: 'Níl cainéil aighneachta cumraithe',
            portal: 'Tairseach',
            in_person: 'Go pearsanta',
            postal: 'Poist',
            email: 'Ríomhphost',
            add: 'Cuir leis',
            deadlines: 'Spriocdháta rialála',
            add_deadline: 'Cuir spriocdháta leis',
            label: 'Lipéad',
            days: 'Laethanta',
            day_type: 'Cineál lae',
            calendar_days: 'Laethanta féilire',
            working_days: 'Laethanta oibre',
            no_deadlines: 'Níl spriocdháta cumraithe',
            description: 'Cur síos',
            failed_load: 'Theip ar load channel data',
            channel_added: 'Cainéal curtha leis',
            channel_added_announce: 'Cainéal aighneachta curtha leis',
            failed_add_channel: 'Theip ar add channel',
            deadline_added: 'Spriocdháta curtha leis',
            deadline_added_announce: 'Spriocdháta rialála curtha leis',
            failed_add_deadline: 'Theip ar add deadline',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Nascadh creata',
            modules: 'Modúil nasctha',
            add_module: 'Cuir modúl leis',
            module_code: 'Cód modúil',
            required: 'Riachtanach',
            link: 'Nasc',
            no_modules: 'Níl aon mhodúl nasctha',
            notes: 'Nótaí',
            personas: 'Pearsana nasctha',
            add_persona: 'Cuir pearsana leis',
            persona_id: 'ID pearsana',
            role_description: 'Cur síos ar an ról',
            no_personas: 'Níl aon phearsana nasctha',
            failed_load: 'Theip ar load framework links',
            module_linked: 'Modúl nasctha',
            module_linked_announce: 'Modúl nasctha go rathúil',
            failed_link_module: 'Theip ar link module',
            persona_linked: 'Pearsana nasctha',
            persona_linked_announce: 'Pearsana nasctha go rathúil',
            failed_link_persona: 'Theip ar link persona',
        },
        regulatory_updates: {
            statuses: {
                pending: 'Ar feitheamh',
                assessed: 'Measúnaithe',
                applied: 'Curtha i bhfeidhm',
                dismissed: 'Curtha ar leataobh',
            },
            title: 'Nuashonruithe rialála agus deais creata',
            page_description: 'Sracfhéachaint ar an gcumhdach do gach creat rialála suiteáilte, mar aon leis an loga athraithe intáirgí de nuashonruithe rialála: leasuithe, dátaí éifeachtacha, agus measúnuithe tionchair.',
            coverage_dashboard: 'Deais chlúdach creata',
            no_frameworks: 'Níor aimsíodh aon chreat',
            framework: 'Creat',
            modules: 'Modúil',
            templates: 'Teimpléid',
            forms: 'Foirmeacha',
            channels: 'Cainéil',
            deadlines: 'Spriocdháta',
            pending: 'Nuashonruithe ar feitheamh',
            updates: 'Nuashonruithe rialála',
            create_update: 'Cruthaigh nuashonrú',
            framework_id: 'ID creata',
            update_title: 'Teideal',
            description: 'Cur síos',
            effective_date: 'Dáta éifeachtach',
            amendment_ref: 'Tagairt Leasaithe',
            create: 'Cruthaigh',
            no_updates: 'Gan nuashonruithe rialála',
            status: 'Stádas',
            impact: 'Tionchar',
            change_summary: 'Achoimre ar athruithe',
            assess: 'Measúnaigh',
            failed_load: 'Theip ar load data',
            update_created: 'Nuashonrú cruthaithe',
            update_created_announce: 'Nuashonrú rialála cruthaithe',
            failed_create: 'Theip ar create update',
            update_assessed: 'Nuashonrú measúnaithe',
            failed_assess: 'Theip ar assess update',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        framework_relationships: {
            panel_title: 'Creataí gaolmhara',
            panel_intro: 'An chaoi a mbaineann an creat seo le creataí eile. Is féidir le forleagan feidhmiú faoi roinnt máthairchreataí ag an am céanna; taispeántar gach ceann anseo aon uair amháin.',
            outgoing_heading: 'Baineann an creat seo le',
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'Feidhmíonn faoin gcreat seo',
            outgoing_caption: 'Creataí a dtagraíonn an creat seo dóibh',
            incoming_caption: 'Creataí a thagann faoin gcreat seo',
            col_framework: 'Creat',
            col_relationship: 'Gaol',
            col_scope: 'Foinse',
            scope_global: 'Ó ábhar an phacáiste',
            scope_org: 'Sainiúil don eagraíocht',
            load_failed: 'Theip ar luchtú na gcreataí gaolmhara',
            relationship_kinds: {
                parent: 'Máthairchreat',
                applies_to: 'Baineann le',
                cross_reference: 'Crostagairt',
                supersedes: 'Sáraíonn',
            },
        },
        sector_overlay: {
            sector_labels: {
                banking: 'Baincéireacht',
                insurance: 'Árachas',
                healthcare: 'Cúram sláinte',
                education: 'Education',
                ict_critical: 'TFC ríthábhachtach',
                telecom: 'Teileachumarsáid',
                energy: 'Fuinneamh',
                transport: 'Iompar',
                digital_services: 'Seirbhísí digiteacha',
                digital_technology_industry: 'Tionscal na teicneolaíochta digití',
                manufacturing: 'Manufacturing',
                public_administration: 'Riarachán poiblí',
                payments: 'Íocaíochtaí',
                publishing: 'Foilsitheoireacht',
                crypto_assets: 'Criptashócmhainní',
                other: 'Eile',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Earnáil',
            direct_scope_label: 'Raon feidhme dlíthiúil díreach',
            chip_direct: 'Infheidhme go díreach',
            chip_reference: 'Tagairt amháin',
            banner_direct_copy: "Go díreach infheidhme maidir le d'eagraíocht. Tá tuarascálacha earnála agus spriocdhátaí gníomhach.",
            banner_reference_copy: 'Reference only — your organization is not classified in this sector.',
            edit_mode_button: 'Modh in eagar',
            edit_form_title: 'Athraigh modh suiteála',
            mode_direct_label: 'Díreach — infheidhme go dlíthiúil',
            mode_direct_help: 'Schedules sector deadlines and dashboard widgets.',
            mode_reference_label: 'Tagairt amháin',
            mode_reference_help: 'Browseable but no deadlines or widgets.',
            reason_note_label: 'Fáth (roghnach)',
            reason_note_placeholder: 'e.g. Fochuideachta bainc faighte',
            cancel: 'Cealaigh',
            confirm: 'Deimhnigh',
            confirming: 'Ag sábháil…',
            save: 'Sábháil',
            saving: 'Ag sábháil…',
            toast_mode_changed: 'Mód forleagan earnála nuashonraithe.',
            toast_installed: 'Forleagan earnála suiteáilte.',
            toast_sectors_saved: 'Sábháladh féin-aithint earnála.',
            install_modal_title: 'Suiteáil forleagan earnála',
            install_modal_intro: 'Sector overlays are opt-in regulatory packs scoped to a single industry.',
            install_modal_footer: 'Is féidir leat modh suiteála a athrú níos déanaí ó leathanach mionsonraí an chreata.',
            install_button: 'Suiteáil',
            installed_label: 'Suiteáilte',
            uninstall_button: 'Díshuiteáil',
            uninstall_confirm: 'Deimhnigh an díshuiteáil',
            uninstall_cancel: 'Cealaigh',
            suggested_label: 'Molta',
            catalog_page_title: 'Forleagan earnála',
            catalog_page_intro: 'Optional regulatory packs scoped to a single industry sector.',
            catalog_empty_title: 'Níl aon fhorleagan earnála ar fáil',
            catalog_empty_description: 'Beidh pacáistí forleagan earnála arna bhfoilsiú ar d’ardán le feiceáil anseo.',
            authority_label: 'Údarás',
            org_sectors_title: 'Earnálacha tionscail',
            org_sectors_intro: 'Select the industry sectors your organization operates in.',
            widget_title: 'Tuarascálacha forleagan earnála dlite',
            widget_aria_label: 'Tuarascálacha forleagan earnála dlite',
            widget_empty: 'Níl aon spriocdhátaí forleagan earnála le teacht.',
            audit_event_mode_changed: 'Modh suiteála forleagain earnála athraithe',
            audit_event_installed: 'Forleagan earnála suiteáilte',
            view_details_button: 'Féach ar na sonraí →',
            banner_aria_label: 'Stádas forleagan earnála',
            toast_load_failed: 'Theip ar fhorleagain earnála a lódáil',
            toast_install_failed: 'Theip ar shuiteáil forleagan earnála',
            toast_uninstall_confirm_required: 'Clóscríobh "{packCode}" chun an díshuiteáil a dhearbhú.',
            toast_uninstalled: 'Díshuiteáilte {packCode}.',
            toast_uninstall_failed: 'Theip ar dhíshuiteáil forleagan earnála',
            uninstall_warning_destructive_label: 'Gníomh millteach.',
            uninstall_warning_body: 'Má dhéantar an díshuiteáil, scriosfar an taifead creata agus aon spriocdhátaí rialála atá fós ann maidir le {frameworkCode} ón eagraíocht seo.',
            uninstall_warning_type_to_confirm: 'Clóscríobh {packCode} lena dheimhniú.',
            uninstall_in_progress: 'Ag díshuiteáil…',
            widget_view_all: 'Féach ar gach forleagan earnála →',
            widget_due_in_days: 'Dlite i {days}d',
            widget_overdue_days: 'Dlite thar téarma {days}d',
            widget_due_today: 'Dlite inniu',
            widget_due_unknown: '—',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
                product_identification_certificate_receipt: 'Certificate synchronisation receipt',
                product_identification_connection_readiness: 'Connection and transition readiness record',
                product_identification_event_record: 'Significant supply-chain event record',
                product_identification_provider_agreement: 'Service-provider data-responsibility agreement',
                product_identification_scope_determination: 'Product scope and risk-tier determination',
                product_identification_uid_register: 'UID and master-data register entry',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_identification_scope_determinations: 'Product Scope Determinations',
                product_identification_sector_watch: 'Sector Watch',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        phased_obligations: {
            panel_title: 'Oibleagáidí céimnithe',
            panel_aria_label: 'Oibleagáidí céimnithe',
            panel_intro: 'Some clauses in this framework activate later than the framework itself.',
            phased_effective_date_label: 'Éifeachtach céimnithe:',
            status_pending: 'Ar feitheamh',
            status_active: 'Gníomhach',
            status_early: 'Gníomhach (luath-chur i bhfeidhm)',
            notice_window_label: 'Fuinneog fógra an Údaráis:',
            days_label: 'days',
            recipient_label: 'Faighteoir:',
            early_implement_button: 'Marcáil curtha i bhfeidhm go luath',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Marcáil oibleagáid chéimnithe mar oibleagáid atá curtha i bhfeidhm go luath',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent.',
            effective_at_label: 'Éifeachtach ó',
            reason_label: 'Fáth (roghnach)',
            reason_placeholder: 'e.g. Ghlac an Bord go luath lena nglacadh 2026-09-01',
            confirm: 'Deimhnigh',
            toast_early_implemented: 'Cur i bhfeidhm luath taifeadta.',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Oibleagáid chéimnithe marcáilte curtha i bhfeidhm go luath',
            widget_rolling_window_label: 'Gníomhachtaithe céimnithe laistigh de 90 lá',
            notice_due_label: 'Notice due by the end of',
            notice_due_time_suffix: '(organisation time)',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day.',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent.',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday.',
                day_after_is_day_one: 'Computed: the day after implementation is day 1.',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period.',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies.',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states.',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read.',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due.',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run.',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read.',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        deadline_activation: {
            token_live: 'In force',
            token_upcoming: 'Not yet in force',
            in_force_from: 'In force from {date}',
            start_date_not_stated: 'Start date not stated',
            token_unknown: 'Timing unknown',
            unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
            retry: 'Retry',
            retry_busy: 'Retrying…',
            // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
            token_phase_unresolved: 'Phase start not dated',
            phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
            token_not_stated: 'No effective date stated',
            not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
            retry_subject: 'the regulatory deadlines',
            column_heading: 'When it applies',
            widget_overlays_failed: 'The installed sector overlays could not be read.',
            widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
            widget_retry_subject: 'the sector overlay deadlines',
            settings_sources_label: 'Deadlines behind this suggestion',
            settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
            settings_unknown_not_counted: 'Not used for the suggestion.',
            settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
            settings_retry_subject: 'the framework deadlines',
            channels_retry_subject: 'the submission channels and deadlines',
            token_not_owed: 'Not owed',
            not_owed_framework_status: 'The framework is not in force.',
            not_owed_row_suspended: 'This duty is suspended.',
            not_owed_superseded_by: 'Superseded by {framework}.',
            not_owed_superseded_unnamed: 'Superseded by another framework.',
            not_owed_row_legacy: 'Legacy — no longer in force.',
            not_owed_unrecognised: 'The record states that this duty is not owed.',
            settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
        },
        // Sprint 62.58.0 (Slice C) — English for key parity until Phase 25 (R-57).
        framework_briefings: {
            summary_export: {
                title: 'Framework summary: {name}',
                section_status: 'Status and dates',
                col_field: 'Field',
                col_value: 'Value',
                field_code: 'Framework code',
                field_status: 'Status',
                field_issued: 'Issued',
                field_effective: 'In force from',
                draft_not_in_force: 'Draft — not yet in force',
                not_stated: 'Not stated',
                section_parent: 'Parent framework',
                parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
                parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
                no_parent: 'This framework names no parent framework.',
                parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
                col_belongs_to: 'Belongs to',
                section_guidance: 'Guidance',
                no_guidance: 'No guidance notes are installed for this framework.',
                col_title: 'Title',
                col_summary: 'Summary',
                section_requirements: 'Requirements',
                no_requirements: 'No requirements are installed for this framework.',
                col_requirement_id: 'ID',
                col_reference: 'Reference',
                col_addressed_to: 'Addressed to',
                col_mandatory: 'Mandatory',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_no_draft: 'No — proposed in a draft',
                mandatory_no_legacy: 'No — the framework is legacy',
                mandatory_no_suspended: "No — the framework's application is suspended",
                section_deadlines: 'Deadlines',
                no_deadlines: 'No deadline rows are installed for this framework.',
                col_deadline: 'Deadline',
                col_date_or_period: 'Date or period',
                col_note: 'Note',
                stated_date: '{date} (as stated)',
                authority_clock: "The authority's period, not a duty of the organization.",
                section_pack: 'Pack',
                pack_line: 'Installed from pack {code}, version {version}.',
                pack_not_recorded: 'The pack this framework was installed from is not recorded.',
                generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
            },
            record: {
                tab: 'Briefings',
                heading: 'Briefings and consultation comments',
                intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
                boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
                kind_briefing: 'Briefing',
                kind_consultation_comment: 'Consultation comment',
                channel_meeting: 'Meeting',
                channel_email: 'Email',
                channel_document: 'Document',
                channel_webinar: 'Webinar',
                channel_other: 'Other',
                col_kind: 'Kind',
                col_audience: 'Audience',
                col_held_on: 'Date',
                col_channel: 'Channel',
                col_approver: 'Approver',
                col_approval: 'Approval',
                col_pack_version: 'Pack version',
                approval_none: 'No approver named',
                approval_pending: 'Awaiting approval',
                approval_approved: 'Approved on {date}',
                record: 'Record an entry',
                recording: 'Recording…',
                approve: 'Approve',
                approving: 'Approving…',
                approve_aria: 'Approve the entry of {date} for {audience}',
                field_kind: 'Kind',
                field_audience: 'Audience',
                field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
                field_held_on: 'Date held or sent',
                field_held_on_hint: 'A calendar date, not after today.',
                field_channel: 'Channel',
                field_channel_note: 'Channel details',
                field_channel_note_required: 'Describe the channel when it is Other.',
                field_reference: 'Reference',
                field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
                field_summary: 'Summary',
                field_approver: 'Approver',
                field_correction_of: 'Corrects entry',
                field_correction_note: 'Correction note',
                empty: 'No briefings or consultation comments are recorded for this framework.',
                load_failed: 'The briefings and consultation comments could not be loaded.',
                record_failed: 'The entry could not be recorded.',
                approve_failed: 'The entry could not be approved.',
                recorded_toast: 'Entry recorded.',
                approved_toast: 'Entry approved.',
                pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
                corrected_by: 'Corrected by a later entry',
                correction_label: 'Correction',
                evidence_count: 'Documents: {count}',
                attach_summary_offer: 'Attach this summary to an entry',
                kind_placeholder: 'Choose a kind',
                channel_placeholder: 'Choose a channel',
                approver_placeholder: 'No approver',
                correction_none: 'Not a correction',
                correction_option: '{kind} of {date} for {audience}',
                correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
                error_required: 'This field is required.',
                error_held_on_future: 'The date cannot be after today.',
                error_correction_note_required: 'Explain what this entry corrects.',
                error_approver_not_found: 'The approver is not a user of this organization.',
                error_not_approver: 'Only the named approver can approve this entry.',
                error_already_approved: 'This entry is already approved.',
                error_self_approval: 'The approver must be someone other than the person recording the entry.',
                form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
                summary_count: '{count} of {max} characters',
                loading: 'Loading the entries…',
                retry_subject: 'the entries',
                reload: 'Reload',
                reloading: 'Reloading…',
                pagination: 'Pages of entries',
                col_summary: 'Summary',
                col_documents: 'Documents',
                documents_open: 'Documents',
                documents_open_aria: 'Documents of the entry of {date} for {audience}',
                reference_line: 'Reference: {reference}',
                channel_note_line: 'Channel details: {note}',
                correction_note_line: 'Correction note: {note}',
                corrects_line: 'Corrects the entry of {date}',
                corrects_unknown: 'Corrects an earlier entry',
                pack_changed_open_comparison: 'See what changed',
                pack_changed_open_updates: 'Open Regulatory Updates',
                replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
                pack_version_not_recorded: 'Not recorded',
                recorded_while: 'Framework status when recorded: {status}',
                framework_status_suspended: 'Suspended',
                recorded_line: 'Recorded by {person} on {date}',
                files_heading: 'Documents',
                files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
                files_slot: 'Documents of this entry',
                files_none: 'No document is attached to this entry.',
                file_hash: 'SHA-256: {hash}',
                file_not_held: 'Document no longer held',
                file_download: 'Download',
                file_remove: 'Remove',
                file_upload: 'Add a document',
                file_bound: 'Document added.',
                file_removed: 'Document removed.',
                file_bind_failed: 'The document could not be added.',
                file_remove_failed: 'The document could not be removed.',
                file_not_owned: 'The entry this document belongs to was not found.',
                file_remove_confirm: 'Remove this document from the entry and delete it?',
                files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
                documents_load_failed: 'The documents could not be loaded.',
                export_label: 'Export summary',
                export_done: 'Framework summary exported.',
                export_failed: 'The framework summary could not be exported.',
                attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
                directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
                approver_retry_subject: 'the list of people',
            },
        },
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: {
            page: {
                module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
                title: 'What changed: {name}',
                title_fallback: 'What changed',
                description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
                back: 'Back to Regulatory Updates',
                loading: 'Loading the comparison…',
                load_failed: 'The comparison could not be loaded.',
                not_found: 'This regulatory update was not found.',
                retry_subject: 'the comparison',
                reload: 'Reload',
                reloading: 'Reloading…',
                no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
                status_heading: 'Status change',
                status_change: '{from} → {to}',
                replaced_by: 'Replaced by {name}',
                status_not_recorded: 'Not recorded',
                basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
                basis_declared: 'Compared using the correspondence the replacing framework declares.',
                derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
                from_heading: 'Earlier text',
                to_heading: 'Text installed now',
                side_status: 'Status: {status}',
                side_pack_version: 'Pack version: {version}',
                counts_heading: 'Summary of changes',
                count_line: '{label}: {count}',
                change_changed: 'Changed',
                change_removed: 'Removed',
                change_new: 'New',
                change_unchanged: 'Unchanged',
                rows_heading: 'Requirements',
                filter_label: 'Show',
                filter_all: 'All requirements',
                showing: 'Showing {shown} of {total}',
                col_requirement: 'Requirement',
                col_change: 'Change',
                col_from_title: 'Title in the earlier text',
                col_to_title: 'Title in the text installed now',
                col_mandatory: 'Mandatory',
                col_note: 'Note',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_change: '{from} → {to}',
                not_in_text: 'Not in this text',
                also_corresponds: 'Also corresponds to: {ids}',
                rows_empty: 'No requirement matches this filter.',
                rows_pagination: 'Pages of requirements',
                superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
                withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
                withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
            },
            review: {
                heading: "Your organization's reading",
                intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
                record: 'Record comparison review',
                recording: 'Recording…',
                none: 'No comparison review is recorded yet.',
                current_heading: 'Current review',
                recorded_line: 'Recorded by {person} on {date}',
                conclusion: 'Conclusion',
                next_steps: 'Next steps',
                no_next_steps: 'No next steps recorded.',
                readings_heading: 'Readings',
                col_reading: 'Reading',
                reading_affects_us: 'Affects us',
                reading_does_not_affect_us: 'Does not affect us',
                reading_to_assess: 'To assess',
                no_rows_read: 'This review gives no reading for a single requirement.',
                history_heading: 'Earlier reviews',
                history_pagination: 'Pages of earlier reviews',
                col_recorded: 'Recorded',
                col_recorded_by: 'Recorded by',
                col_conclusion: 'Conclusion',
                col_readings: 'Readings',
                readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
                modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
                row_legend: '{id}: {title}',
                field_reading: 'Reading',
                field_reading_placeholder: 'Choose a reading',
                field_note: 'Note',
                field_conclusion: 'Conclusion',
                field_next_steps: 'Next steps',
                text_count: '{count} of {max} characters',
                error_reading_required: 'Choose a reading for this requirement.',
                error_conclusion_required: 'State your conclusion.',
                record_failed: 'The comparison review could not be recorded.',
                recorded_toast: 'Comparison review recorded.',
                no_comparison_error: 'This update holds no comparison to review.',
                rows_loading: 'Loading the requirements to read…',
                rows_load_failed: 'The requirements to read could not be loaded.',
                no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
            },
            reread: {
                heading: 'Records filled under the replaced text',
                intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
                forms_heading: 'Form records',
                col_record: 'Record',
                col_form: 'Form',
                col_status: 'Status',
                col_updated: 'Last updated',
                forms_none: 'No form record was filled under the replaced text.',
                forms_withheld: 'Form records are listed only to people who may read form records.',
                untitled: 'Untitled record',
                forms_pagination: 'Pages of form records',
                questionnaires_heading: 'Vendor questionnaires',
                notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
                notice_link: 'See what changed',
                notice_link_aria: 'See what changed between {from} and {to}',
                notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_link_aria: 'See what changed in {name}',
            },
            recheck: {
                will_draft: 'The pack publisher will recheck this draft on {date}.',
                will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
                will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
                was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
                was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
                was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
                records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
            },
            list: {
                col_changed: 'Changed requirements',
                no_comparison_short: 'No comparison',
                pagination: 'Pages of regulatory updates',
                view_comparison: 'View comparison',
                view_comparison_aria: 'View comparison for {title}',
                reviews_count: 'Reviews: {count}',
            },
        },
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section.
        framework_suspension: {
            heading: 'Suspension',
            intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
            start_label: 'Began',
            start_value: 'Suspended from {date}',
            basis_label: 'Basis for that day',
            steps_caption: 'Instruments that suspended this framework, in the order they took effect',
            col_instrument: 'Instrument',
            col_issued: 'Issued',
            col_effective: 'Took effect',
            col_until: 'Suspends until',
            col_replaces: 'Replaces',
            replaces_none: 'None',
            until_date: 'Until {date}',
            source_line: 'Read from: {source}',
            end_label: 'Ends',
            end_date: 'On {date}',
            end_event: 'When this happens: {event}',
            event_not_occurred: 'Not yet occurred',
            event_occurred_on: 'Occurred on {date}',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Painéal',
            partner_orgs: 'Eagraíochtaí comhpháirtíochta',
            service_tiers: 'Leibhéil seirbhíse',
            deliverable_templates: 'Teimpléid seachadta',
            deliverables: 'Seachadtaí',
            brand_profiles: 'Próifílí branda',
            support_tickets: 'Ticéid tacaíochta',
            workflow_templates: 'Teimpléid sreabha oibre',
            workflows: 'Sreabha oibre',
            escalation: 'Géarú',
            reports: 'Tuarascálacha',
            audit: 'Rian iniúchta',
        },
        dashboard: {
            title: 'Painéal comhpháirtíochta',
            loading: 'Ag lódáil painéal sonraí...',
            total_managed_tenants: 'Managed Tenants Iomlána',
            avg_health_score: 'Avg Sláinte Scór',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Oscail Ticéid',
            upcoming_deliverables: 'Insoláthairtí Le Teacht',
            escalated_tickets: 'Ardaithe Ticéid',
            managed_tenants: 'Tionóntaí Bainistíthe',
            partner_tier: 'Partner Sraith',
            managed_tenants_count: 'Managed Tenants Comhaireamh',
            onboarding_date: 'Onboarding Dáta',
            view_details: 'Féach ar Shonraí',
            no_partner_orgs: 'Níl aon partner organizations found. aimsithe',
        },
        partner_orgs: {
            title: 'Eagraíochtaí comhpháirtíochta',
            new: 'Nua Partner Org',
            contact_name: 'Contact Ainm',
            contact_email: 'Ríomhphost Teagmhála',
            partner_tier: 'Partner Sraith',
            managed_orgs: 'Eagraíochtaí Bainistaithe',
            onboarding_date: 'Onboarding Dáta',
            no_orgs: 'Níl aon partner organizations found. aimsithe',
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Contact Fón',
            metadata_json: 'Meiteashonraí (JSON)',
            change_status: 'Change Stádas',
            new_status: 'Nua Stádas',
            invalid_json_metadata: 'Neamhbhailí JSON in metadata field',
            suspended: 'Ar fionraí',
            offboarded: 'Díbhordáilte',
            all_tiers: 'Gach Sraith',
            silver: 'Airgead',
            gold: 'Óir',
            platinum: 'Platanam',
            status_offboarded: 'Díbhordáilte',
            status_suspended: 'Ar fionraí',
            tier_gold: 'Óir',
            tier_platinum: 'Platanam',
            tier_silver: 'Airgead',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Leibhéil seirbhíse',
            new: 'Nua Sraith',
            tier_name: 'Sraith Ainm',
            monthly_fee: 'Míosúil Fee',
            features: 'Gnéithe',
            no_tiers: 'Níl aon service tiers found. aimsithe',
            tier_code: 'Sraith Cód',
            response_time_hrs: 'Freagra Am (hrs)',
            resolution_time_hrs: 'Resolution Am (hrs)',
            review_frequency: 'Athbhreithniú Minicíocht',
            sla_response_time: 'SLA Freagra Am (hours)',
            sla_resolution_time: 'SLA Resolution Am (hours)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'Ag comparáid...',
            compare: 'Comparáid',
            comparison: 'Seirbhís Sraith Comparison',
            attribute: 'Airí',
            deprecate: 'Dímhol',
            deprecated: 'Dímholta',
            select_compare: 'Roghnaigh at least 2 tiers to compare',
            comparison_title: 'Seirbhís Sraith Comparison',
            status_deprecated: 'Dímholta',
            activate: 'Gníomhachtaigh',
            resolution_time: 'Resolution Am',
            response_time: 'Freagra Am',
            select_at_least_two: 'Roghnaigh at least 2 tiers',
        },
        deliverable_templates: {
            title: 'Teimpléid seachadta',
            new: 'Nua Teimpléad',
            template_name: 'Teimpléad Ainm',
            deliverable_type: 'Insoláthairt Cineál',
            no_templates: 'Níl aon deliverable teimpléid found. aimsithe',
            frequency: 'Minicíocht',
            est_effort_hrs: 'Iarracht mheasta (uaire)',
            estimated_effort: 'Iarracht Mheasta (uaireanta)',
            associated_module: 'Associated Modúl',
            linked_tiers_count: 'Linked Sraitheanna Comhaireamh',
            evidence_requirements: 'Fianaise Riachtanais (comma-separated)',
            tier_ids: 'Sraith ID (comma-separated)',
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'One Am',
            frequency_monthly: 'Míosúil',
            frequency_quarterly: 'Ráithiúil',
            frequency_annually: 'Go bliantúil',
            type_report: 'Tuarascáil',
            type_review: 'Athbhreithniú',
            type_assessment: 'Measúnú',
            type_meeting: 'Cruinniú',
            type_configuration: 'Cumraíocht',
            freq_one_time: 'One Am',
            freq_monthly: 'Míosúil',
            freq_quarterly: 'Ráithiúil',
            freq_annually: 'Go bliantúil',
            status_deprecated: 'Dímholta',
        },
        deliverables: {
            title: 'Seachadtaí',
            new: 'Nua Insoláthairt',
            partner: 'Comhpháirtí',
            deliverable_type: 'Insoláthairt Cineál',
            due_date: 'Due Dáta',
            no_deliverables: 'Níl aon deliverables found. aimsithe',
            assigned_to: 'Sannta To',
            completion_date: 'Completion Dáta',
            update_status: 'Nuashonraigh Stádas',
            hide_details: 'Folaigh Sonraí',
            view_details: 'Féach ar Shonraí',
            evidence_ids: 'Fianaise ID',
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Client Ceadú',
            bulk_update: 'Bulk Nuashonraigh',
            bulk_update_status: 'Bulk Nuashonraigh Stádas',
            generate_schedule: 'Gin Sceideal',
            generate_deliverable_schedule: 'Gin Insoláthairt Sceideal',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'ID Eagraíochta',
            service_tier_id: 'Seirbhís Sraith ID',
            filter_assigned_to: 'Scag by Sannta To',
            select_status: 'Roghnaigh stádas...',
            new_status: 'Nua Stádas',
            upcoming: 'Le teacht',
            overdue: 'Thar téarma',
            status_deferred: 'Curtha siar',
            status_overdue: 'Thar téarma',
            status_upcoming: 'Le teacht',
        },
        brand_profiles: {
            title: 'Próifílí branda',
            new: 'Nua Brand Próifíl',
            partner: 'Comhpháirtí',
            brand_name: 'Brand Ainm',
            primary_color: 'Príomhúil Color',
            logo_url: 'URL Lógó',
            no_profiles: 'Níl aon brand profiles found. aimsithe',
            secondary_color: 'Tánaisteach Color',
            header_text: 'Téacs Ceanntáisc',
            footer_text: 'Téacs Buntáisc',
            contact_email: 'Ríomhphost Teagmhála',
            contact_phone: 'Contact Fón',
            contact_website: 'Contact Suíomh gréasáin',
            contact_address: 'Contact Seoladh',
            is_default: 'Is Réamhshocrú',
            set_default: 'Set Réamhshocrú',
            preview: 'Réamhamharc',
            report_header_preview: 'Tuarascáil Header Preview',
            report_footer_preview: 'Tuarascáil Footer Preview',
            preview_description: 'This is a preview of the tuarascáil header with the roghnaithe brand colors.',
            contact_website_short: 'Suíomh gréasáin',
            logo_preview: 'Réamhamharc lógó',
        },
        support_tickets: {
            title: 'Ticéid tacaíochta',
            new: 'Nua Ticéad',
            partner: 'Comhpháirtí',
            subject: 'Ábhar',
            assigned_to: 'Sannta To',
            no_tickets: 'Níl aon support tickets found. aimsithe',
            category: 'Catagóir',
            priority: 'Tosaíocht',
            escalation_tier: 'Ardú Sraith',
            sla_status: 'SLA Stádas',
            update_status: 'Nuashonraigh Stádas',
            escalate: 'Ardaigh',
            add_note: 'Cuir Nóta leis',
            escalate_ticket: 'Ardaigh Ticéad',
            escalate_to_tier: 'Ardaigh To Sraith',
            notes_timeline: 'Nótaí Timeline',
            no_notes: 'Gan nótaí fós.',
            attachments: 'Ceangaltáin',
            no_attachments: 'Gan ceangaltáin.',
            client_org_id: 'ID Eagraíochta Cliaint',
            content: 'Ábhar',
            note_type: 'Cineál',
            view_detail: 'Féach ar Shonra',
            hide: 'Folaigh',
            new_status: 'Nua Stádas',
            all_categories: 'Gach Catagóir',
            all_priorities: 'Gach Tosaíocht',
            all_tiers: 'Gach Sraith',
            ticket_not_found: 'Ticéad not found',
            ticket_id: 'Ticéad ID',
            client_org: 'Eagraíocht Cliaint',
            deescalate: 'Dí-ardaigh',
            deescalate_ticket: 'De-escalate Ticéad',
            deescalate_to_tier: 'De-escalate To Sraith',
            add_attachment: 'Cuir Ceangaltán leis',
            file: 'Comhad',
            linked_entity: 'Aonán Nasctha',
            sla_target: 'Sprioc',
            breached: 'Sáraithe',
            remaining_hours: 'fágtha',
            na: 'N/A',
            tickets_found: 'ticéid aimsithe',
            reason: 'Cúis',
            target: 'Sprioc',
            upload: 'Uaslódáil',
        },
        workflow_templates: {
            title: 'Teimpléid sreabha oibre',
            new: 'Nua Teimpléad',
            template_name: 'Teimpléad Ainm',
            steps: 'Céimeanna',
            no_templates: 'Níl aon workflow teimpléid found. aimsithe',
            category: 'Catagóir',
            view_steps: 'Féach ar Chéimeanna',
            hide_steps: 'Folaigh Céimeanna',
            step: 'Céim',
            step_title: 'Teideal',
            step_description: 'Cur síos',
            assignee_role: 'Sannaí Ról',
            sla_hours: 'Uaireanta SLA',
            evidence_required: 'Fianaise Riachtanach',
            guidance_notes: 'Guidance Nótaí',
            add_step: 'Cuir Céim leis',
            no_steps: 'Gan céimeanna sainithe.',
            no_steps_defined: 'Gan céimeanna sainithe.',
            templates_found: 'teimpléid aimsithe',
            all_categories: 'Gach Catagóir',
        },
        workflows: {
            client_org: 'Eagraíocht Cliaint',
            title: 'Sreabhadh oibre Instances',
            new: 'Nua Sreabhadh oibre',
            template: 'Teimpléad',
            partner: 'Comhpháirtí',
            current_step: 'Reatha Céim',
            progress: 'Dul chun cinn',
            no_workflows: 'Níl aon workflow instances found. aimsithe',
            instances_found: 'cásanna aimsithe',
        },
        escalation: {
            title: 'Ardú Painéal',
            ticket: 'Ticéad',
            partner: 'Comhpháirtí',
            escalated_at: 'Ardaithe At',
            reason: 'Cúis',
            no_escalations: 'Níl aon escalations found. aimsithe',
            time_in_tier: 'Am in Sraith',
            auto_evaluate: 'Uathmheasúnú',
            priority_critical: 'Criticiúil',
            priority_high: 'Ard',
            priority_medium: 'Meánach',
            priority_low: 'Low',
            deescalated_from_dashboard: 'De-escalated from painéal',
            sla_breached: 'SLA Sáraithe',
            sla_remaining: 'SLA fágtha: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Eagraíocht Cliaint',
            title: 'Tuarascálacha',
            partner: 'Comhpháirtí',
            report_type: 'Tuarascáil Cineál',
            generated_at: 'Ginte ag',
            no_reports: 'Níl aon tuarascáil found. aimsithe',
            generate: 'Gin Tuarascáil',
        },
        audit: {
            title: 'Partner Iniúchadh Trail',
            event_type: 'Cineál Imeachta',
            entity_type: 'Entity Cineál',
            entity: 'Aonán',
            entity_id: 'ID Aonáin',
            actor: 'Gníomhaí',
            timestamp: 'Stampa Ama',
            no_events: 'Níl aon iniúchadh imeacht found. aimsithe',
            events_found: 'imeachtaí aimsithe',
            client_org: 'Eagraíocht Chliaint',
            all_actors: 'Gach aisteoir',
            event_type_search: 'Cuardaigh cineál teagmhais',
            event_type_created: 'Cruthaithe',
            event_type_updated: 'Nuashonraithe',
            event_type_deleted: 'Scriosta',
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Comhpháirtí Org',
            entity_type_service_tier: 'Sraith Seirbhíse',
            entity_type_deliverable_template: 'Teimpléad Inseachadta',
            entity_type_deliverable_instance: 'Cás Inseachadta',
            entity_type_brand_profile: 'Próifíl Branda',
            entity_type_support_ticket: 'Ticéad Tacaíochta',
            entity_type_workflow_template: 'Teimpléad Sreabhadh Oibre',
            entity_type_workflow_instance: 'Méid Sreafa Oibre',
            entity_type_generated_report: 'Tuarascáil Ginte',
            entity_type_audit_export: 'Easpórtáil Iniúchta',
        },
        no_permission_title: 'Ceadanna Neamhleor',
        no_permission_message: 'Níl cead agat rochtain a fháil ar spás oibre an chomhpháirtí.',
        status_active: 'Gníomhach',
        status_suspended: 'Ar fionraí',
        status_offboarded: 'Offboarded',
        status_completed: 'Críochnaithe',
        status_cancelled: 'Ar ceal',
        status_deprecated: 'Dímheasta',
        status_new: 'Nua',
        status_triaged: 'Triáiseadh',
        status_in_progress: 'Ar Siúl',
        status_waiting_on_client: 'Ag feitheamh ar an gCliant',
        status_waiting_on_vendor: 'Ag fanacht leis an Díoltóir',
        status_escalated: 'Ardaithe',
        status_resolved: 'Réitíodh',
        status_closed: 'Dúnta',
        status_pending: 'Ar feitheamh',
        status_skipped: 'Léim',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Ar bordáil',
        category_incident_escalation: 'Ardú Teagmhais',
        category_config_change: 'Athrú Cumraíochta',
        category_periodic_review: 'Athbhreithniú Tréimhsiúil',
        category_offboarding: 'Offboarding',
        category_custom: 'Saincheaptha',
        category_configuration: 'Cumraíocht',
        category_incident: 'teagmhas',
        category_compliance_question: 'Ceist Chomhlíonta',
        category_feature_request: 'Iarratas Gné',
        category_bug_report: 'Tuairisc ar Fhabht',
        priority_critical: 'Criticiúil',
        priority_high: 'Ard',
        priority_medium: 'Meánach',
        priority_low: 'Íseal',
        tier_silver: 'Airgid',
        tier_gold: 'Óir',
        tier_platinum: 'Platanam',
        report_type_compliance_summary: 'Achoimre Chomhlíonta',
        report_type_risk_overview: 'Forbhreathnú Riosca',
        report_type_incident_report: 'Tuarascáil Teagmhais',
        report_type_consent_status: 'Stádas Toilithe',
        report_type_program_maturity: 'Aibíocht Cláir',
        report_type_executive_pack: 'Pacáiste Feidhmiúcháin',
        note_type_internal: 'Inmheánach',
        note_type_client_visible: 'Cliant Infheicthe',
        status_updated: "D'éirigh leis an stádas a nuashonrú",
        deliverable_status_updated: "D'éirigh le stádas inseachadta a nuashonrú",
        ticket_status_updated: "D'éirigh le stádas na dticéad a nuashonrú",
        ticket_escalated: "D'éirigh leis an ticéad a mhéadú",
        note_added: "D'éirigh leis an nóta a chur leis",
        attachment_uploaded: "D'éirigh leis an gceangaltán a uaslódáil",
        ticket_deescalated: "D'éirigh le dí-ardú ticéad",
        auto_evaluation_completed: 'Uath-mheastóireacht curtha i gcrích',
        template_created: 'Cruthaíodh teimpléad sreabhadh oibre go rathúil',
        template_updated: 'Nuashonraíodh an teimpléad sreabhadh oibre go rathúil',
        brand_set_default: 'Socraíodh próifíl branda mar réamhshocrú go rathúil',
        brand_created: "D'éirigh le próifíl branda a chruthú",
        brand_updated: "D'éirigh le próifíl branda a nuashonrú",
        tier_created: 'Cruthaíodh sraith seirbhíse go rathúil',
        tier_updated: "D'éirigh le sraith seirbhíse a nuashonrú",
        tier_deprecated: "D'éirigh le sraith seirbhíse a dhímheas",
        tier_activated: 'Cuireadh an tsraith seirbhíse i ngníomh go rathúil',
        period: 'Tréimhse',
        format: 'Formáid',
        period_start: 'Tréimhse Tosaigh',
        period_end: 'Deireadh na Tréimhse',
        reports_count: '{count} tuarascálacha',
        reports_count_one: '{count} tuairisc',
        metric: 'méadrach',
        value: 'Luach',
        showing_chart_view: 'Amharc cairte á thaispeáint',
        showing_table_view: 'Amharc tábla sonraí á thaispeáint',
        evidence_ids_placeholder: 'e.g. ev-001, ev-002',
        confirm_cancel_workflow: 'An bhfuil tú cinnte gur mhaith leat an sreabhadh oibre seo a chealú? Ní féidir an gníomh seo a chealú.',
        export_csv: 'Easpórtáil CSV',
        compliance_qualifications_title: 'Cáilíochtaí Seirbhíse Comhlíonta',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Ailíniú Aschuir Rialála',
        regulatory_output_desc: 'Mapálann gach cineál teimpléid chuig catagóir aschuir rialála: Táirgeann tuarascálacha doiciméadú comhlíonta, bailíochtaíonn Athbhreithnithe éifeachtacht rialaithe, clúdaíonn Measúnuithe meastóireachtaí riosca (DPIA, anailís ar bhearnaí), seisiúin rialachais doiciméad Cruinnithe. Sainíonn Ceanglais Fianaise na déantáin nach mór do gach insoláthartha tagairt a dhéanamh dóibh le haghaidh ullmhachta iniúchta.',
        sla_compliance_title: 'SLA Rianú do Sheachadadh Comhlíonta',
        sla_compliance_desc: 'Am freagartha agus réitigh Tá CLSanna ceangailteach de réir conartha do sheachadadh seirbhíse comhlíonta. Cinneann Minicíocht Athbhreithnithe cé chomh minic agus a dhéantar táirgí insoláthartha comhlíonta a mheas. Spreagann sáruithe SLA ar thicéid atá ríthábhachtach maidir le comhlíonadh ardú uathoibríoch agus déantar iad a logáil isteach sa Rian Iniúchta Comhpháirtithe le haghaidh fianaise rialála.',
        regulatory_engagement_title: 'Patrúin Rannpháirtíochta Rialála',
        regulatory_engagement_desc: 'Léarscáil de chatagóirí sreafa oibre de réir cineálacha rannpháirtíochta rialála: Clúdaíonn bordáil socrú comhlíonta tosaigh agus anailís ar bhearnaí, láimhseálann Athbhreithniú Tréimhsiúil measúnuithe comhlíonta athfhillteacha, bainistíonn Ardú Teagmhais freagairt ar sháruithe rialála, cinntíonn Asboarding go gcomhlíontar oibleagáidí coinneála sonraí. Féadfaidh bailiú fianaise a bheith de dhíth le haghaidh iomláine an rian iniúchta.',
        compliance_escalation_title: 'Comhlíonadh-Nósanna Imeachta Ardaithe Chriticiúil',
        compliance_escalation_desc: 'Láimhseálann L1 saincheisteanna oibriúcháin caighdeánacha. Clúdaíonn L2 teipeanna a mbíonn tionchar acu ar chomhlíonadh (spriocanna rialála caillte, bearnaí rialaithe). Tá L3 forchoimeádta do chásanna sáraithe rialála a dteastaíonn aird feidhmiúcháin láithreach uathu. SLA gineann sárú ar thicéid L2/L3 iontrálacha rian iniúchta go huathoibríoch. Déantar gach gníomh formhéadaithe agus dí-ardaithe a logáil go buan.',
        audit_regulatory_title: 'Rian Fianaise Rialála',
        audit_regulatory_desc: 'Soláthraíonn an rian iniúchta seo taifid is léir go bhfuil cur isteach orthu maidir le comhlíonadh rialála. Déantar gach gníomh comhpháirtíochta (cruthaigh, nuashonraigh, scrios, formhéadú, athrú stádais) a logáil le haitheantas aisteoir, stampa ama, agus difreálach pá-ualaithe iomlán. Úsáid scagairí de Chineál Aonáin chun fianaise a leithlisiú de réir fearann ​​comhlíonta. Easpórtáil torthaí scagtha chun doiciméadú atá réidh le haghaidh iniúchta a tháirgeadh le haghaidh iniúchtaí rialála.',
        evidence_trail_title: 'Rian Fianaise Trasmhodúil',
        evidence_trail_desc: 'Is féidir le gach céim den sreabhadh oibre IDanna Fianaise a bhailiú a nascann le táirgí insoláthartha, imeachtaí iniúchta, agus déantáin chomhlíonta thar modúil. Trí chéim le haitheantas fianaise a chur ar aghaidh cruthaítear slabhra infhíoraithe a nascann sreafaí oibre na gcomhpháirtithe le doiciméadacht rialála. Feidhmíonn sreafaí oibre críochnaithe le slabhraí fianaise iomlána mar chruthúnas ar sheachadadh rannpháirtíochta comhlíonta.',
        evidence_ids_label: 'Aitheantais Fianaise',
    },
    org_groups: {
        title: 'Grúpaí eagraíochta',
        new: 'Cruthaigh Grúpa',
        group_name: 'Grúpa Ainm',
        member_count: 'Member Comhaireamh',
        root_org: 'Bun-Eagraíocht',
        no_groups: 'Níl aon org groups found. aimsithe',
        add_organization: 'Cuir Eagraíocht Leis',
        select_organization: 'Roghnaigh eagraíocht...',
        no_available_orgs: 'Níl aon eagraíochtaí ar fáil le cur leis.',
        no_members: 'Níl aon eagraíochtaí baill sa ghrúpa seo.',
        member_added: 'Cuireadh eagraíocht leis an ngrúpa.',
        member_removed: 'Baineadh eagraíocht ón ngrúpa.',
        already_in_group: 'Tá an eagraíocht seo i ngrúpa cheana féin.',
        org_not_found: 'Níor aimsíodh an eagraíocht.',
        add_member_error: 'Theip ar eagraíocht a chur leis an ngrúpa.',
        remove_member_error: 'Theip ar eagraíocht a bhaint ón ngrúpa.',
        confirm_remove_member: 'An bhfuil tú cinnte gur mian leat "{name}" a bhaint ón ngrúpa seo?',
        load_error: 'Theip ar sonraí an ghrúpa eagraíochtaí a lódáil.',
        page_title_list: 'Grúpaí Eagrúcháin',
        page_title_detail: 'Sonraí an Ghrúpa Eagraíochta',
        page_title_create: 'Cruthaigh Grúpa Eagraíochta',
        page_title_settings: 'Socruithe Grúpa Eagraíochta',
        page_title_users: 'Úsáideoirí Grúpa',
        page_title_reports: 'Tuarascálacha Grúpa',
        page_title_dashboard: 'Painéal na nGrúpa',
        page_title_audit: 'Iniúchadh Grúpa',
        page_title_org_audit: 'Iniúchadh Grúpa Eagrúcháin',
        page_title_shared_vendors: 'Díoltóirí Roinnte',
        page_title_shared_frameworks: 'Creataí Comhroinnte',
        page_title_shared_forms: 'Teimpléid Foirm Roinnte',
        page_title_merged_vendors: 'Díoltóirí Cumaisc',
        page_title_settings_inheritance: 'Oidhreacht Socruithe',
        announce_loaded_groups: 'Luchtaíodh {count} ghrúpa eagraíochta',
        announce_loaded_group: 'Grúpa luchtaithe {name}',
        announce_loaded_users: 'Luchtaíodh {count} úsáideoir grúpa',
        announce_loaded_reports: '{count} tuarascálacha lódáilte',
        announce_loaded_vendors: 'Luchtaíodh {count} díoltóirí comhroinnte',
        announce_loaded_frameworks: 'Lódaíodh {count} chreatlach chomhroinnte',
        announce_loaded_templates: 'Lódaíodh {count} teimpléad foirme roinnte',
        announce_loaded_audit: '{count} teagmhais iniúchta lódáilte',
        announce_loaded_merged: 'Lódáilte {shared} díoltóirí roinnte agus {local} díoltóirí áitiúla',
        announce_dashboard_loaded: 'Painéal lódáilte.',
        announce_dashboard_refreshed: 'Athnuadh an painéal.',
        announce_dashboard_refresh_failed: 'Theip ar an painéal a athnuachan.',
        announce_refreshing: 'Deais á athnuachan...',
        announce_settings_loaded: 'Oidhreacht socruithe luchtaithe.',
        announce_settings_saved: "D'éirigh leis na socruithe a shábháil.",
        announce_saving_settings: 'Socruithe á sábháil...',
        announce_error_saving: 'Earráid agus socruithe á sábháil.',
        announce_group_dissolved: 'Grúpa díscaoilte.',
        announce_creating_group: 'Grúpa eagraíochta á chruthú...',
        announce_group_created: "D'éirigh le grúpa eagraíochta a chruthú.",
        announce_error_creating: 'Earráid agus grúpa eagraíochta á chruthú.',
        announce_vendor_published: "D'fhoilsigh an díoltóir go rathúil.",
        announce_vendor_retired: 'Díoltóir ar scor.',
        announce_framework_published: 'Creat foilsithe go rathúil.',
        announce_framework_retired: 'Creat ar scor.',
        announce_template_published: 'Foilsíodh teimpléad foirme go rathúil.',
        announce_template_retired: 'Teimpléad foirm ar scor.',
        announce_role_assigned: "D'éirigh leis an ról a sannadh.",
        announce_error_assign_role: 'Earráid agus ról á shannadh.',
        announce_role_revoked: "D'éirigh leis an ról a chúlghairm.",
        announce_report_generating: 'Tuairisc á giniúint...',
        announce_report_requested: 'Iarradh tuairisc a ghiniúint.',
        announce_error_report: 'Earráid agus tuairisc á iarraidh.',
        announce_report_deleted: 'Scriosadh an tuairisc.',
        announce_search_applied: 'Cuardach curtha i bhfeidhm.',
        announce_search_cleared: 'Glanadh an cuardach.',
        announce_tab_selected: '{tab} chluaisín roghnaithe',
        announce_error_add_member: 'Earráid agus ball á chur leis an ngrúpa.',
        announce_category_reverted: '{category} ar ais chuig an oidhreacht ó thuismitheoir.',
        announce_category_overridden: '{category} socraithe chun sáraíocht áitiúil.',
        announce_override_saved: '{category} sáruithe a shábháil.',
        announce_export_started: 'Iniúchadh á easpórtáil mar {format}...',
        announce_export_completed: 'Easpórtáil mar {format} críochnaithe.',
        announce_export_failed: 'Theip ar easpórtáil.',
        announce_filter_org: 'Scagtha de réir eagraíochta {name}',
        announce_filter_org_cleared: 'Glanadh an scagaire eagraíochta',
        announce_filter_event: 'Scagtha de réir cineáil imeachta {type}',
        announce_filter_cleared: 'Glanadh an scagaire',
        announce_download: '{name} á n-íoslódáil mar {format}',
        error_load_groups: 'Theip ar lódáil grúpaí org. Bain triail eile as.',
        error_load_users: 'Theip ar lódáil úsáideoirí grúpa.',
        error_load_reports: 'Theip ar lódáil tuarascálacha grúpa.',
        error_load_dashboard: 'Theip ar lódáil painéal an ghrúpa.',
        error_load_settings: 'Theip ar lódáil socruithe grúpa.',
        error_load_vendors: 'Theip ar lódáil díoltóirí cumaisc.',
        error_save_settings: 'Theip ar na socruithe a shábháil.',
        error_dissolve_group: 'Theip ar dhíscaoileadh an ghrúpa.',
        error_assign_role: 'Theip ar an ról a shannadh.',
        error_create_group: 'Theip ar chruthú grúpa org. Bain triail eile as.',
        error_request_report: 'Theip ar an tuarascáil a iarraidh.',
        error_group_name_required: 'Tá ainm an ghrúpa ag teastáil.',
        success_settings_saved: "D'éirigh leis na socruithe a shábháil.",
        confirm_retire_vendor: 'An bhfuil tú cinnte gur mhaith leat an díoltóir "{name}" a scor?',
        confirm_retire_framework: 'An bhfuil tú cinnte gur mhaith leat an creat "{name}" a scor?',
        confirm_retire_template: 'An bhfuil tú cinnte gur mhaith leat teimpléad foirme "{name}" a scor?',
        confirm_dissolve_group: 'An bhfuil tú cinnte gur mhaith leat "{name}" a dhíscaoileadh? Ní féidir an gníomh seo a chealú.',
        confirm_revoke_role: 'An bhfuil tú cinnte gur mhaith leat an ról "{role}"" a chúlghairm ó "{name}"?',
        confirm_delete_report: 'An bhfuil tú cinnte gur mhaith leat an tuairisc "{name}" a scriosadh?',
        dissolve_group: 'Grúpa a dhíscaoileadh',
        dissolve_warning: 'Déanfaidh sé seo an grúpa eagraíochta a dhíscaoileadh go buan. Bainfear na balleagraíochtaí go léir.',
        count_groups: '{count} ghrúpa',
        count_groups_plural: '{count} ghrúpa',
        count_users: '{count} úsáideoir',
        count_users_plural: '{count} úsáideoir a bhfuil róil ghrúpa acu',
        count_events: '{count} imeacht',
        count_events_plural: '{count} imeacht',
        count_reports: '{count} tuairisc',
        count_reports_plural: '{count} tuarascálacha',
        count_vendors: '{count} díoltóir roinnte ar fud an ghrúpa',
        count_vendors_plural: '{count} díoltóirí roinnte ar fud an ghrúpa',
        count_frameworks: '{count} creat roinnte ar fud an ghrúpa',
        count_frameworks_plural: '{count} creata roinnte ar fud an ghrúpa',
        count_templates: '{count} teimpléad roinnte ar fud an ghrúpa',
        count_templates_plural: '{count} teimpléad roinnte ar fud an ghrúpa',
        count_shared_vendors: '{count} díoltóirí comhroinnte',
        count_local_vendors: '{count} díoltóirí áitiúla',
        merged_combined_view: 'Amharc comhcheangailte de {shared} díoltóirí roinnte agus {local} díoltóirí áitiúla',
        dashboard: {
            title: 'Painéal an ghrúpa',
            loading: 'Ag lódáil painéal sonraí...',
            total_groups: 'Grúpaí Iomlána',
            total_members: 'Members Iomlána',
            active_groups: 'Grúpaí Gníomhacha',
            overall_compliance_score: 'Scór Comhlíonta Foriomlán',
            group_risk_level: 'Leibhéal Riosca Grúpa',
            sla_compliance: 'SLA Comhlíonadh',
            compliance_posture: 'Staidiúir Chomhlíonta',
            risk_heatmap: 'Léarscáil Teas Riosca',
            sla_performance: 'SLA Feidhmíocht',
            recent_incidents: 'Teagmhais le Déanaí',
            recent_activity: 'Gníomhaíocht le Déanaí',
            refresh_label: 'Athnuaigh sonraí an deais',
            score_good: 'Maith',
            score_needs_attention: 'Teastaíonn aird',
            score_critical: 'Criticiúil',
            trend_up: 'Ag treocht suas',
            trend_down: 'Ag treocht síos',
            trend_stable: 'Cobhsaí',
            risk_low: 'Low',
            risk_medium: 'Meánach',
            risk_high: 'Ard',
            risk_critical: 'Criticiúil',
            risk_unknown: 'Anaithnid',
            col_organization: 'Eagraíocht',
            col_score: 'Scór',
            col_trend: 'Treocht',
            col_open_issues: 'Ceisteanna Oscailte',
            col_critical_issues: 'Saincheisteanna Criticiúla',
            col_health: 'Sláinte',
            col_vendor_risk: 'Riosca Díoltóra',
            col_data_risk: 'Riosca Sonraí',
            col_compliance_risk: 'Riosca Comhlíonta',
            col_total_cases: 'Cásanna Iomlána',
            col_on_time: 'Ar Am',
            col_breached: 'Sáraíodh',
            col_sla_percent: 'SLA %',
            col_avg_days: 'Laethanta Meán Fómhair',
            sr_vendor_risk: 'Scór riosca an díoltóra: {score}',
            sr_data_risk: 'Scór riosca sonraí: {score}',
            sr_compliance_risk: 'Scór riosca comhlíonta: {score}',
            sr_sla_breaches: 'Rabhadh: {count} SLA sárú',
            sr_severity: 'Déine: {severity}',
        },
        detail: {
            title: 'Sonraí an ghrúpa',
            members: 'Baill',
            settings: 'Socruithe',
            audit: 'Iniúchadh',
            sr_loading: 'Sonraí grúpa eagraíochta á lódáil',
            sr_loading_hierarchy: 'Ordlathas á lódáil',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'balleagraíocht',
            root_org_label: 'eagraíocht fhréamh',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Úsáideoirí an ghrúpa',
            add_user: 'Cuir Úsáideoir leis',
            no_users: 'No úsáideoir in this group.',
            role_group_admin: 'Riarachán Grúpa',
            role_group_reader: 'Léitheoir Grúpa',
            sr_loading: 'Grúpa úsáideoirí á lódáil',
        },
        reports: {
            title: 'Tuarascálacha an ghrúpa',
            no_reports: 'Níl aon tuarascáil found. aimsithe',
            generate_report: 'Gin tuairisc nua',
            report_type_compliance: 'Achoimre Chomhlíonta',
            report_type_risk: 'Forbhreathnú Riosca',
            report_type_sla: 'SLA Feidhmíocht',
            report_type_incident: 'Tuarascáil Teagmhais',
            report_type_executive: 'Achoimre Feidhmiúcháin',
            report_type_board: 'Tuarascáil an Bhoird',
            status_queued: 'Ciúáilte',
            status_generating: 'Ag giniúint',
            status_completed: 'Críochnaithe',
            status_failed: 'Theip',
            period_start: 'Tréimhse Tosaigh',
            period_end: 'Deireadh na Tréimhse',
            sr_loading: 'Tuarascálacha grúpa á lódáil',
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Soláthróirí comhroinnte',
            no_vendors: 'Níl aon shared soláthraithe found. aimsithe',
            merged_vendors: 'Merged Soláthraithe',
            publish_form_label: 'Foilsigh díoltóir comhroinnte',
            sr_loading: 'Díoltóirí comhroinnte á lódáil',
            sr_loading_merged: 'Díoltóirí cumaisc á lódáil',
            local_vendors: 'Díoltóirí Áitiúla',
            source_shared: 'Roinnte ón ngrúpa',
            source_local: 'Díoltóir áitiúil',
            category_technology: 'Teicneolaíocht',
            category_consulting: 'Dul i gcomhairle',
            category_outsourcing: 'Foinsiú allamuigh',
            category_cloud: 'Scamall',
            category_data_processing: 'Próiseáil Sonraí',
            category_other: 'Eile',
            risk_tier_low: 'Low',
            risk_tier_medium: 'Meánach',
            risk_tier_high: 'Ard',
            risk_tier_critical: 'Criticiúil',
            compliance_compliant: 'Comhlíontach',
            compliance_non_compliant: 'Neamhchomhlíontach',
            compliance_pending: 'Ar feitheamh',
            compliance_under_review: 'Faoi Léirmheas',
            status_active: 'Gníomhach',
            status_retired: 'Ar scor',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Cód creata',
            title: 'Creataí comhroinnte',
            no_frameworks: 'Níl aon shared creat found. aimsithe',
            publish_form_label: 'Foilsigh creat comhroinnte',
            sr_loading: 'Creataí roinnte á lódáil',
            status_active: 'Gníomhach',
            status_retired: 'Ar scor',
        },
        shared_forms: {
            title: 'Teimpléid foirmeacha comhroinnte',
            no_forms: 'Níl aon shared form teimpléid found. aimsithe',
            publish_form_label: 'Foilsigh teimpléad foirm roinnte',
            sr_loading: 'Foirmeacha comhroinnte á lódáil',
            status_active: 'Gníomhach',
            status_retired: 'Ar scor',
        },
        audit: {
            title: 'Iniúchadh an ghrúpa',
            consolidated_title: 'Consolidated Iniúchadh Trail',
            consolidated_subtitle: 'Cross-organization iniúchadh imeacht for this group',
            audit_log_title: 'Grúpa Iniúchadh Logáil',
            event_type: 'Cineál Imeachta',
            actor: 'Gníomhaí',
            timestamp: 'Stampa Ama',
            source_org: 'Foinse Org',
            org: 'Org',
            summary: 'Achoimre',
            details: 'Sonraí',
            no_events: 'Níl aon iniúchadh imeacht found. aimsithe',
            all_events: 'Gach Imeacht',
            all_orgs: 'Gach Eagraíocht',
            organization: 'Eagraíocht',
            from: 'Ó',
            to: 'To',
            search_events: 'Cuardaigh imeacht...',
            clear: 'Glan',
            events_24h: 'Imeachtaí (24h)',
            events_7d: 'Imeachtaí (7d)',
            active_orgs: 'Eagraíochtaí Gníomhacha',
            exporting: 'Ag easpórtáil...',
            export_csv: 'Easpórtáil CSV',
            export_json: 'Easpórtáil JSON',
            back_to_group: 'Ar ais to Grúpa',
            back_to_detail: 'Ar ais to Sonra',
            sr_loading: 'Imeachtaí iniúchta á lódáil',
            sr_loading_summary: 'Achoimre iniúchta á lódáil',
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Grúpa Socruithe',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Socruithe Inheritance',
            inheritance_subtitle: 'Cumraigh na catagóirí socruithe a gheobhaidh tú mar oidhreacht ón ngrúpa vs úsáid sáruithe áitiúla',
            sla_settings: 'SLA Socruithe',
            calendar_settings: 'Calendar Socruithe',
            notification_settings: 'Socruithe Fógra',
            intake_settings: 'Intake Socruithe',
            cmp_integration: 'CMP Comhtháthú',
            overridden: 'Sáraithe',
            inheriting: 'Ag oidhriú',
            hide_defaults: 'Folaigh Réamhshocrú',
            show_group_defaults: 'Taispeáin Grúpa Réamhshocrú',
            group_defaults: 'Grúpa Réamhshocrú',
            inherit_from_group: 'Inherit from Grúpa',
            use_local_override: 'Úsáid Sárú Áitiúil',
            local_override_json: 'Áitiúil Sáraigh (JSON)',
            save_override: 'Sábháil Sáraigh',
            saving: 'Ag sábháil...',
            sr_loading: 'Oidhreacht socruithe á lódáil',
            sr_loading_settings: 'Socruithe grúpa á lódáil',
            sr_enabled: 'Cumasaithe',
            sr_disabled: 'faoi mhíchumas',
            sr_override_active: 'Sárú áitiúil gníomhach',
            sr_inherited: 'Oidhreacht ó thuismitheoir',
            danger_zone: 'Crios Contúirte',
            sharing_options: 'Roghanna Comhroinnte',
            advanced_options: 'Ardroghanna',
            max_member_orgs: 'Maximum member organizations',
        },
        status_active: 'Gníomhach',
        status_dissolved: 'Tuaslagtha',
        status_pending: 'Ar feitheamh',
        status_suspended: 'Ar fionraí',
    },
    integrations: {
        nav: {
            webhooks: 'Webhookeanna',
            domains: 'Fearainn',
            export_schedules: 'Sceidil Easpórtála',
            email_log: 'Loga Ríomhphoist',
        },
    },
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Úsáideoirí',
            roles: 'Róil',
            profile: 'Mo Phróifíl',
        },
        security: {
            mfa: 'Socruithe MFA',
            sso: 'Cumraíocht SSO',
            api_keys: 'Eochracha API',
            service_accounts: 'Cuntais Seirbhíse',
            ip_allowlist: 'Liosta Ceadaithe IP',
            delegations: 'Toscaireachtaí',
            break_glass: 'Rochtain Éigeandála',
            session_settings: 'Socruithe Seisiúin',
            active_sessions: 'Seisiúin Ghníomhacha',
            access_reviews: 'Athbhreithnithe Rochtana',
            my_reviews: 'Mo Athbhreithnithe',
            role_analytics: 'Anailísíocht Ról',
            attestation_schedules: 'Sceidil Fhianaithe',
            sod_rules: 'Rialacha SoD',
            sod_violations: 'Sáruithe SoD',
        },
        module_config: {
            title: 'Cumraíocht Modúl',
            subtitle: "Cumasaigh nó díchumasaigh modúil ghnéithe do d'eagraíocht. Bíonn modúil dhíchumasaithe i bhfolach ón nascleanúint.",
            enabled: 'Cumasaithe',
            disabled: 'Díchumasaithe',
            save: 'Sábháil Cumraíocht',
            saving: 'Á Shábháil...',
            saved: 'Cumraíocht sábháilte go rathúil',
            nav_label: 'Cumraíocht Modúl',
            settings: 'Socruithe',
            webhooks: 'Webhookeanna',
            domains: 'Fearainn',
            export_schedules: 'Sceidil Easpórtála',
            email_log: 'Loga Ríomhphoist',
            announce_loaded: 'Module configuration loaded',
        },
        files_nav: {
            section: 'Comhaid',
            configuration: 'Cumraíocht',
            browser: 'Brabhsálaí Comhad',
            dashboard: 'Painéal Comhad',
        },
        immutable_audit_trails: {
            nav_group_label: 'Iniúchóireacht agus Cruthúnas',
            nav_settings: 'Socruithe',
            nav_status: 'Stádas an Chruthúnais',
            nav_integrity_center: 'Ionad Sláine',
            nav_usage: 'Úsáid agus Creidmheasanna',
            settings_title: 'Rianta Iniúchóireachta Do-Athraithe — Socruithe',
            settings_subtitle: 'Socruithe in aghaidh na heagraíochta don bhreiseán Rianta Iniúchóireachta Do-Athraithe.',
            settings_banner: "Níl an breiseán Rianta Iniúchóireachta Do-Athraithe cumasaithe d'eagraíocht. Cumasaigh anseo é chun tús a chur le hancairiú cruthúnais d'imeachtaí iniúchóireachta Sraith A agus Sraith B.",
            status_title: 'Rianta Iniúchóireachta Do-Athraithe — Stádas an Chruthúnais',
            status_subtitle: 'Caidrimh chruthúnais in aghaidh an imeachta thar mhodúil.',
            status_banner: "Níl an breiseán Rianta Iniúchóireachta Do-Athraithe cumasaithe d'eagraíocht. Téigh chuig na Socruithe lena chumasú.",
            integrity_center_title: 'Rianta Iniúchóireachta Do-Athraithe — Ionad Sláine',
            integrity_center_subtitle: 'Fíoraigh cruthúnais agus easpórtáil Pacáistí Sláine.',
            integrity_center_banner: "Níl an breiseán Rianta Iniúchóireachta Do-Athraithe cumasaithe d'eagraíocht. Téigh chuig na Socruithe lena chumasú sula reáchtálann tú fíorú nó sula n-easpórtálann tú Pacáistí Sláine.",
            usage_title: 'Rianta Iniúchóireachta Do-Athraithe — Úsáid agus Creidmheasanna',
            usage_subtitle: 'Iarmhéid creidmheasa, ceannacháin pacáistí agus stair úsáide.',
            usage_banner: 'Cumasaigh Rianta Iniúchóireachta Do-Athraithe sna Socruithe chun tús a chur le creidmheasanna cruthúnais a ídiú.',
            module_tips_placeholder: 'Tá leideanna agus treoir don dromchla seo ar fáil san ionad cabhrach.',
            column_label: 'Cruthúnas',
            no_proof_aria: 'Níor taifeadadh aon chruthúnas',
            proof_status_receipt_label: 'admháil {id}',
            in_progress_hint: 'Ar siúl — nuashonruithe go huathoibríoch',
            status_pills: {
                pending: 'Ar feitheamh',
                submitted: 'Curtha isteach',
                anchored: 'Ancaireáilte',
                confirmed: 'Deimhnithe',
                retrying: 'Á atriail',
                paused_cap: 'Sosaithe (uasteorainn)',
                failed: 'Theip air',
                failed_auth: 'Theip ar fhíordheimhniú',
            },
            status_tooltips: {
                pending: 'Ar feitheamh — scuaine don tseirbhís cruthúnais. Bogfar go dtí Curtha isteach gan mhoill; níl gá le gníomh.',
                submitted: "Arna chur isteach - cuireadh d'imeacht chuig an tseirbhís cruthúnais. Maidir le modh baisc tá sé ag fanacht le bheith séalaithe agus ar ancaire ar slabhra. Nuashonruithe go huathoibríoch; níl gá le gníomh.",
                anchored: 'Ar ancaire — tá an bhaisc scríofa ar shlabhra agus táthar ag fanacht ar dheimhniú deiridh. Nuashonruithe go huathoibríoch; níl gá le gníomh.',
                confirmed: 'Deimhnithe - tá an cruthúnas ar ancaire ar slabhra agus infhíoraithe.',
                retrying: 'Á thriail arís - tharla earráid neamhbhuan. Tá an comhréiteoir ag triail arís go huathoibríoch; níl gá le gníomh.',
                paused_cap: 'Ar sos — tá do theorainn chreidmheasa cruthúnais míosúil sroichte. Faigh tuilleadh creidmheasanna nó fan go dtí an chéad mhí eile billeála.',
                failed: 'Theip - níor éirigh leis an ancaireacht. Bainfidh an comhréiteoir arís go huathoibríoch; déan teagmháil le tacaíocht má leanann sé.',
                failed_auth: 'Theip ar Auth - dhiúltaigh an tseirbhís cruthúnais ár dintiúir. Seiceáil an leathanach Socruithe Conairí Iniúchta Neamh-inmheánach.',
            },
            mode_pills: {
                direct: 'Díreach',
                batch: 'Baisc',
            },
            drawer: {
                title: 'Sonraí an chruthúnais',
                close: 'Dún',
                section_proof: 'Cruthúnas',
                section_batch: 'Ballraíocht baisce',
                status: 'Stádas',
                proof_reference: 'Tagairt chruthúnais',
                receipt_id: 'Aitheantas admhála',
                taxonomy_class: 'Aicme tacsanomaíochta',
                units_consumed: 'Aonaid ídithe',
                created_at: 'Cruthaithe',
                verification_ref: 'Tagairt fíoraithe an tslabhra',
                chain: 'Slabhra',
                tx_hash: 'Hais idirbhirt',
                block_ref: 'Tagairt bhloic',
                batch_reference: 'Tagairt bhaisce',
                leaf_index: 'Innéacs duille',
                local_event_hash: 'Hais áitiúil an imeachta',
                show_inclusion: 'Taispeáin an cosán cuimsithe',
                hide_inclusion: 'Folaigh an cosán cuimsithe',
                privacy_note: 'Déantar an pálasta a haiseáil; níor fhág aon sonraí pearsanta ComplianceOne don imeacht seo.',
                verify_now: 'Fíoraigh anois',
                verify_pending: 'Fíorú ar siúl…',
                verify_verified: 'Fíoraithe — d’éirigh leis na seiceálacha áitiúla agus le seiceálacha an tslabhra.',
                verify_issue_prefix: 'Saincheist fíoraithe: ',
                verify_failed: 'Theip ar an nglao fíoraithe.',
                verify_result_prefix: 'Breithiúnas an fhíoraithe: ',
                verify_pending_note: 'Fíorú ar feitheamh: tá an cruthúnas seo fós ag fanacht ar ancaireacht slabhra. Bain triail eile as tar éis don bhaisc a bheith séalaithe.',
                awaiting_anchor_note: "Is cuid de bhaisc oscailte é an cruthúnas seo. Bíonn fíorú ar fáil a luaithe a bheidh an bhaisc séalaithe agus curtha ar ancaire ar slabhra - go hiondúil laistigh d'fhuinneog bhaisc chumraithe an org. Déanfar an suaitheantas stádais a nuashonrú go huathoibríoch.",
                terminal_error_note: 'Theip ar an cruthúnas seo a dhaingniú. Bainfidh an comhréiteoir stádas cruthúnais arís go huathoibríoch; níl aon rud le seiceáil fós ag fíorú láimhe.',
            },
            integrity_center: {
                section_run: 'Reáchtáil fíorú',
                section_progress: 'Dul chun cinn an reáchtála',
                section_issues: 'Saincheisteanna',
                period_start: 'Tús na tréimhse',
                period_end: 'Deireadh na tréimhse',
                module_filter: 'Modúil (scartha le camóga, roghnach)',
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Reáchtáil fíorú',
                export_button: 'Easpórtáil Pacáiste Sláine',
                export_success: 'Pacáiste Sláine easpórtáilte.',
                export_failed: 'Theip ar an easpórtáil.',
                empty_disabled: 'Cumasaigh Rianta Iniúchóireachta Do-Athraithe sna Socruithe chun reáchtáin fíoraithe a chur ar siúl.',
                error_period_required: 'Roghnaigh dáta tosaigh agus dáta deiridh.',
                error_start_run: 'Theip ar thús an reáchtála.',
                processed: 'Próiseáilte: {processed} as {total}',
                col_verdict: 'Breithiúnas',
                col_count: 'Líon',
                col_proof_link: 'Nasc cruthúnais',
                col_module: 'Modúl',
                col_event_type: 'Cineál imeachta',
                col_expected: 'Hais a bhfuiltear ag súil léi',
                col_recomputed: 'Hais athríofa',
                col_raised_at: 'Ardaithe ag',
                any_verdict: 'Aon bhreithiúnas',
                no_issues: 'Níor aimsíodh aon saincheist.',
            },
            settings_page: {
                load_error: 'Theip ar lódáil na socruithe.',
                save: 'Sábháil',
                save_success: 'Socruithe sábháilte.',
                save_error: 'Theip ar an sábháil.',
                discard: 'Caith uait na hathruithe',
                read_only_banner: 'Tá rochtain inléite amháin agat. Déan teagmháil le Riarthóir an Ardáin chun athruithe a dhéanamh.',
                section_status: 'Stádas agus cumasú',
                enabled_label: 'Breiseán cumasaithe',
                enabled_help: 'Nuair a bhíonn sé cumasaithe, déantar gach imeacht iniúchóireachta Sraith A agus Sraith B a haiseáil agus a ancaireáil.',
                section_mode: 'Modh cruthúnais réamhshocraithe',
                mode_direct: 'Díreach (ancaire amháin in aghaidh an imeachta)',
                mode_batch: 'Baisc (ancaire fréimhe Merkle in aghaidh an ghrúpa)',
                mode_hybrid: 'Hibrideach (réamhshocrú clárlainne in aghaidh na haicme)',
                batch_window_label: 'Fuinneog bhaisce (nóiméid, 1–60)',
                section_taxonomy: 'Tacsanomaíocht imeachtaí cosanta',
                taxonomy_help: 'Sáruithe in aghaidh na haicme imeachta. Sraith A → modh díreach, Sraith B → modh baisce, As → ní chruthaítear é.',
                taxonomy_search: 'Cuardaigh sáruithe',
                taxonomy_reset: 'Athshocraigh chuig na réamhshocruithe',
                taxonomy_event_class: 'Aicme imeachta',
                taxonomy_override: 'Sárú',
                taxonomy_actions: 'Gníomhartha',
                taxonomy_clear: 'Glan',
                taxonomy_no_overrides: 'Gan aon sárú — réamhshocruithe na clárlainne á n-úsáid.',
                taxonomy_add_class: 'event_class.cód',
                taxonomy_add: 'Cuir sárú leis',
                section_caps: 'Uasteorainneacha agus teorainneacha',
                cap_label: 'Uasteorainn mhíosúil (aonaid; bán = gan teorainn)',
                warn_label: 'Tairseach rabhaidh (aonaid; ní mór di a bheith níos lú ná an uasteorainn)',
                auto_pause_label: 'Cuir cruthúnais nua ar sos go huathoibríoch nuair a shroichtear an uasteorainn',
                section_credentials: 'Dintiúir',
                credential_id: 'Aitheantas dintiúir',
                credential_rotated_at: 'Rothlaithe go deireanach',
                rotate_button: 'Rothlaigh dintiúr',
                rotate_success: 'Dintiúr rothlaithe. Tá an seandintiúr bailí ar feadh {n} nóiméad eile.',
                rotate_failed: 'Theip ar rothlú an dintiúir.',
                rotate_modal_title: 'An dintiúr AesirX a rothlú?',
                rotate_modal_body: 'Eisíonn an rothlú dintiúr nua. Glacann AesirX leis an seandintiúr ar feadh fuinneog ghrásta 60 nóiméad ionas gur féidir le hoibrithe atá ar siúl a gcuid jabanna reatha a chríochnú. Tar éis 60 nóiméad cúlghairtear an seandintiúr.',
                rotate_modal_confirm: 'Rothlaigh anois',
                tier_a_off_modal_title: 'An gcuirfear cruthúnas as feidhm d’aicme imeachta?',
                tier_a_off_modal_body: 'Stopann “As” a shocrú in aghaidh aicme imeachta sconna an chruthúnais ó na himeachtaí sin a ancaireáil. Mínítear i Reachtaireacht §6 an impleacht rialála: tá imeachtaí Sraith A ábhartha don rialtóir; má chuirtear as iad d’fhéadfadh sé inchosanta do rian iniúchóireachta a bhriseadh.',
                tier_a_off_modal_confirm: 'Tuigim — cuir an sárú i bhfeidhm',
                error_warn_lt_cap: 'Ní mór don tairseach rabhaidh a bheith níos lú ná an uasteorainn mhíosúil.',
                error_window_range: 'Ní mór don fhuinneog bhaisce a bheith idir 1 agus 60 nóiméad.',
                error_run_in_progress: 'Tá reáchtáil fíoraithe ar siúl; ní féidir an breiseán a dhíchumasú faoi láthair.',
            },
            status_page: {
                filter_status: 'Stádas',
                filter_mode: 'Modh cruthúnais',
                filter_module: 'Modúl',
                filter_source_collection: 'Bailiúchán foinse',
                filter_from_date: 'Ó',
                filter_to_date: 'Go dtí',
                filter_search: 'Cuardach',
                any_status: 'Aon stádas',
                any_mode: 'Aon mhodh',
                col_module: 'Modúl',
                col_event_type: 'Cineál imeachta',
                col_proof_status: 'Stádas',
                col_proof_mode: 'Modh',
                col_created_at: 'Cruthaithe',
                col_units: 'Aonaid',
                empty_disabled: "Níl an breiseán Rianta Iniúchóireachta Do-Athraithe cumasaithe d'eagraíocht. Téigh chuig na Socruithe lena chumasú.",
                empty_enabled_no_rows: 'Níl aon nasc cruthúnais a mheaitseálann na scagairí reatha.',
                export_csv: 'Easpórtáil CSV',
                export_xlsx: 'Easpórtáil XLSX',
                export_success: 'Easpórtáil curtha i gcrích',
                export_failed: 'Theip ar an easpórtáil',
            },
            usage_page: {
                section_alerts: 'Foláirimh',
                alert_dismiss: 'Díbhe',
                alert_dismiss_aria: 'Ruaig an foláireamh {id}',
                alert_dismissed: 'Díbheadh an foláireamh',
                alert_dismiss_failed: 'Theip ar an bhfoláireamh a dhíbhe',
                section_balance: 'Iarmhéid creidmheasa',
                balance_label: 'Creidmheasanna ar fáil',
                balance_hint: 'Aonaid fágtha (1 aonad = cruthúnas amháin imeachta cosanta).',
                section_current_month: 'An mhí reatha',
                consumed_label: 'Ídithe',
                granted_label: 'Deonaithe',
                refunded_label: 'Aisíoctha',
                net_label: 'Glan',
                section_history: 'Stair úsáide',
                history_kind: 'Cineál',
                history_units: 'Aonaid',
                history_balance_after: 'Iarmhéid ina dhiaidh',
                history_notes: 'Nótaí',
                history_at: 'Cathain',
                cap_warning_banner: 'Tá tú tar éis dul thar do thairseach rabhaidh don mhí billeála seo. Smaoinigh ar chreidmheasanna breise a fhuascailt.',
                cap_paused_banner: 'Sroicheadh an uasteorainn mhíosúil. Tá cur isteach cruthúnas curtha ar sos go dtí an chéad mhí eile nó go méadófar an uasteorainn.',
                section_redeem: 'Fuascail pacáiste creidmheasa',
                redeem_purchase_id: 'Uimhir sonraisc',
                redeem_package: 'Pacáiste',
                redeem_button: 'Fuascail',
                redeem_pending: 'Ag fuascailt…',
                redeem_success: 'Creidmheasanna fuascailte',
                redeem_failed: 'Theip ar an bhfuascailt',
                redeem_option_template: '{name} - {units} {units_label} - {price}',
                redeem_option_units: 'aonaid',
                alert_dismissing: 'Ag briseadh as…',
                alert_kind: {
                    cap_reached: 'caipín míosúil bainte amach',
                    low_balance: 'Cothromaíocht íseal',
                    auth_failure: 'Teip fíordheimhnithe',
                    merkle_mismatch: 'Mímheaitseáil Merkle',
                    batch_terminal_failure: 'Teip críochfoirt bhaisc',
                    direct_terminal_failure: 'Teip críochfoirt aighneachta díreach',
                },
                usage_kind: {
                    consume: 'Ithe',
                    refund: 'Aisíocaíocht',
                    grant: 'Deontas',
                    adjustment: 'Coigeartú',
                    reservation: 'Áirithint',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'Tús',
                package_growth: 'Fás',
                package_enterprise: 'Fiontar',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Ceannaigh tuilleadh creidmheasanna',
                    purchase_title: 'Ceannaigh tuilleadh creidmheasanna',
                    purchase_subtitle: 'Roghnaigh pacáiste. Tabharfar chuig seiceáil shlán Stripe thú, agus tiocfaidh do shonrasc chugat trí ríomhphost.',
                    checkout_button: 'Lean ar aghaidh chun íoc',
                    checkout_pending: 'Seiceáil á hoscailt…',
                    cancel_button: 'Cealaigh',
                    purchase_error: 'Níorbh fhéidir an tseiceáil a thosú. Bain triail eile as go gairid.',
                    redeem_subtitle: 'Íoctha cheana? Roghnaigh an pacáiste a cheannaigh tú agus cuir isteach aitheantas an tsonraisc ó do ríomhphost chun na creidmheasanna a chur leis.',
                    error_not_found: 'Níor aimsíodh aon sonrasc leis an uimhir sin. Seiceáil uimhir an tsonraisc agus bain triail eile as.',
                    error_not_paid: 'Níl an sonrasc seo marcáilte mar íoctha go fóill.',
                    error_org_mismatch: "Níor eisíodh an sonrasc seo chuig d'eagraíocht.",
                    error_package_mismatch: 'Ní mheaitseálann an sonrasc seo an pacáiste roghnaithe.',
                    error_billing_unavailable: 'Níorbh fhéidir teagmháil a dhéanamh leis an tseirbhís billeála. Bain triail eile as go gairid.',
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Cumraíocht Comhad',
            subtitle: 'Cumraigh teorainneacha uaslódála comhad, beartais choinneála agus sáruithe in aghaidh an chineáil úinéara don dromchla ceangaltán ardáin.',
            section_global: 'Réamhshocruithe domhanda',
            section_global_help: "Baineann na teorainneacha seo le gach comhad a uaslódálann úsáideoirí i d'eagraíocht mura n-athraíonn sárú in aghaidh an chineáil úinéara thíos iad.",
            section_overrides: 'Sáruithe in aghaidh an chineáil úinéara',
            section_overrides_help: 'Sáraigh na teorainneacha domhanda do chineál úinéara ar leith. Bíonn an lámh in uachtar i gcónaí ag luachanna in aghaidh an chineáil úinéara ar an socrú domhanda.',
            max_file_size_label: 'Uasmhéid an chomhaid',
            max_file_size_hint: 'In aghaidh an uaslódála aonair (MiB)',
            max_total_attachments_label: 'Uaslíon iomlán ceangaltán',
            max_total_attachments_hint: 'Ar fud na heagraíochta ar fad',
            max_total_size_label: 'Uasmhéid stórála iomláine',
            max_total_size_hint: 'Ar fud na heagraíochta ar fad (MiB)',
            allowed_mime_types_label: 'Cineálacha comhad ceadaithe',
            allowed_mime_types_placeholder: 'Roghnaigh cineálacha MIME',
            retention_days_label: 'Tréimhse coinneála (laethanta)',
            retention_days_hint: 'Cá fhad a chaithfear comhaid a choinneáil sular féidir iad a scriosadh. Fág bán le haghaidh aon bheartais choinneála.',
            legal_hold_label: 'Coinneáil dhlíthiúil',
            legal_hold_hint: 'Nuair atá sé cumasaithe, éilíonn crua-scriosadh comhaid den chineál úinéara seo cead sárú coinneála dlíthiúla agus rogha shainráite ón oibreoir.',
            save: 'Sábháil athruithe',
            saving: 'Á shábháil…',
            reset: 'Athshocraigh',
            save_success: 'Socruithe sábháilte',
            empty_state_title: 'Réamhshocruithe an ardáin á n-úsáid',
            empty_state_body: "Tá d'eagraíocht ag rith faoi láthair ar bheartas comhad réamhshocraithe an ardáin. Cuir aon luach in eagar thíos chun sárú ar leibhéal eagraíochta a chruthú.",
            read_only_banner: 'Tá rochtain inléite amháin agat ar chumraíocht na gcomhad. Déan teagmháil le Riarthóir an Ardáin chun athruithe a dhéanamh.',
            owner_type_default_badge: 'Réamhshocrú',
            owner_type_overridden_badge: 'Sáraithe',
            owner_type_label: 'Cineál úinéara',
            owner_type_collapse: 'Cuach',
            owner_type_expand: 'Leathnaigh',
            override_save: 'Sábháil sárú',
            override_remove: 'Bain sárú',
            mime_labels: {
                'application/pdf': 'Doiciméad PDF',
                'image/png': 'Íomhá PNG',
                'image/jpeg': 'Íomhá JPEG',
                'image/jpg': 'Íomhá JPG',
                'application/msword': 'Doiciméad Word (oidhreachta)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Doiciméad Word',
                'application/vnd.ms-excel': 'Scarbhileog Excel (oidhreachta)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Scarbhileog Excel',
                'text/csv': 'Comhad CSV',
                'text/plain': 'Gnáth-théacs',
                'message/rfc822': 'Teachtaireacht ríomhphoist',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'Brabhsálaí Comhad',
            subtitle: 'Cuardaigh, scag agus bainistigh gach ceangaltán a uaslódáladh ar fud na heagraíochta.',
            filter_owner_type: 'Cineál úinéara',
            filter_uploader: 'Uaslódáilte ag',
            filter_from: 'Uaslódáilte i ndiaidh',
            filter_to: 'Uaslódáilte roimh',
            filter_size_min: 'Méid íosta (MiB)',
            filter_size_max: 'Méid uasta (MiB)',
            filter_mime: 'Cineál comhaid',
            filter_deleted_state: 'Staid scriosta',
            filter_deleted_any: 'Gach comhad',
            filter_deleted_only: 'Scriosta amháin',
            filter_deleted_exclude: 'Eisigh comhaid scriosta',
            filter_orphaned: 'Dílleachta amháin',
            filter_search_placeholder: 'Cuardaigh ainm comhaid…',
            filter_apply: 'Cuir scagairí i bhfeidhm',
            filter_clear: 'Glan na scagairí',
            col_filename: 'Ainm comhaid',
            col_owner_type: 'Cineál úinéara',
            col_owner_entity: 'Úinéir',
            col_uploader: 'Uaslódáilte ag',
            col_uploaded_at: 'Uaslódáilte',
            col_size: 'Méid',
            col_mime: 'Cineál',
            col_state: 'Staid',
            col_actions: 'Gníomhartha',
            state_active: 'Gníomhach',
            state_deleted: 'Scriosta',
            state_orphaned: 'Dílleachta',
            action_view_detail: 'Féach ar shonraí',
            action_download: 'Íoslódáil',
            action_ai_edits: 'Eagarthóireachtaí IS',
            action_soft_delete: 'Bog-scriosadh',
            action_restore: 'Athchóirigh',
            action_hard_delete: 'Crua-scriosadh',
            bulk_soft_delete: 'Bog-scrios na cinn roghnaithe',
            bulk_export_csv: 'Easpórtáil CSV',
            bulk_select_all: 'Roghnaigh uile',
            reason_label: 'Cúis',
            reason_min_10_chars: 'Tá cúis riachtanach (10 gcarachtar ar a laghad)',
            reason_min_20_chars: 'Tá cúis riachtanach (20 carachtar ar a laghad)',
            legal_hold_override_label: 'Sáraigh coinneáil dhlíthiúil',
            legal_hold_override_warning: 'Tá an comhad seo faoi choinneáil dhlíthiúil. Taifeadfar an crua-scriosadh sa rian iniúchóireachta le do chúis agus leis an mbratach sáraithe.',
            empty_state_title: 'Níl aon chomhad ag teacht le do scagairí',
            empty_state_body: 'Coigeartaigh do scagairí chun torthaí a fheiceáil, nó glan iad chun gach comhad a fheiceáil.',
            pagination_prev: 'Roimhe seo',
            pagination_next: 'Ar aghaidh',
            pagination_page: 'Leathanach {page} de {total}',
            pagination_size_label: 'In aghaidh an leathanaigh',
            pagination_total: 'Iomlán: {count}',
            toast_soft_deleted: 'Comhad bog-scriosta',
            toast_restored: 'Comhad athchóirithe',
            toast_hard_deleted: 'Comhad scriosta go buan',
            toast_bulk_deleted: '{count} comhad scriosta',
            toast_error: 'Theip ar an oibríocht',
            confirm_soft_delete_title: 'Bog-scrios comhad',
            confirm_restore_title: 'Athchóirigh comhad',
            confirm_hard_delete_title: 'Scrios comhad go buan',
            confirm_bulk_soft_delete_title: 'Bog-scrios na comhaid roghnaithe',
            confirm_submit: 'Deimhnigh',
            confirm_cancel: 'Cealaigh',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'Sonraí an chomhaid',
            close: 'Dún',
            meta_filename: 'Ainm comhaid',
            meta_size: 'Méid',
            meta_mime: 'Cineál',
            meta_owner_type: 'Cineál úinéara',
            meta_owner_entity: 'Aonán úinéara',
            meta_uploader: 'Uaslódáilte ag',
            meta_uploaded_at: 'Uaslódáilte',
            meta_storage_path: 'Cosán stórála',
            meta_deleted_state: 'Staid scriosta',
            audit_section_title: 'Rian iniúchóireachta',
            audit_load_more: 'Lódáil tuilleadh imeachtaí',
            audit_empty: 'Níor taifeadadh aon imeacht iniúchóireachta don chomhad seo.',
            audit_config_changed: 'Athraíodh an chumraíocht',
            audit_soft_deleted: 'Bog-scriosta',
            audit_restored: 'Athchóirithe',
            audit_hard_deleted: 'Scriosta go buan',
            audit_downloaded: 'Íoslódáilte',
            audit_quota_breached: 'Rinneadh iarracht an cuóta a shárú',
            audit_owner_type_download_denied: 'Diúltaíodh don aisghabháil',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{event} ag {actor} ar {at}',
            action_download: 'Íoslódáil',
            action_soft_delete: 'Bog-scriosadh',
            action_restore: 'Athchóirigh',
            action_hard_delete: 'Crua-scriosadh',
            effective_settings_title: 'Beartas i bhfeidhm',
            effective_max_file_size: 'Uasmhéid an chomhaid',
            effective_retention_days: 'Coinneáil',
            effective_legal_hold: 'Coinneáil dhlíthiúil',
            effective_legal_hold_yes: 'Gníomhach',
            effective_legal_hold_no: 'Gan a bheith',
        },
        files_dashboard: {
            title: 'Painéal Comhad',
            subtitle: 'Úsáid stórála, fás agus comhaid dhílleachta ar fud na heagraíochta.',
            kpi_total_files: 'Comhaid iomlána',
            kpi_total_size: 'Stóráil iomlán',
            kpi_near_quota: 'Úsáid an chuóta',
            kpi_orphaned: 'Comhaid dhílleachta',
            chart_by_owner_type: 'Comhaid de réir cineáil úinéara',
            chart_growth_30d: 'Fás 30 lá',
            toggle_count: 'De réir líon',
            toggle_size: 'De réir méide',
            orphaned_panel_title: 'Comhaid dhílleachta (na 20 is mó)',
            orphaned_view_all: 'Féach ar gach comhad dílleachta',
            orphaned_empty: 'Níor aimsíodh aon chomhad dílleachta.',
            growth_empty: 'Gan aon ghníomhaíocht uaslódála sa 30 lá deireanach.',
            retention_n_days: '{days} lá',
            retention_none: 'Gan beartas',
        },
    },
    category_labels: {
        'user-guide': 'Treoir Úsáideora',
        'admin-guide': 'Treoir Riaracháin',
        'getting-started': 'Tús a chur leis',
        faq: 'CCanna',
    },
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Eochracha',
        webhook: 'Crúca gréasáin',
        webhooks: 'Crúcaí gréasáin',
        admin: 'Riarachán',
        aesirx: 'AesirX',
        purposes: 'Cuspóirí',
        preferences: 'Roghanna',
        onboarding: 'Ar bordáil',
        'first-steps': 'Na Chéad Chéimeanna',
        login: 'Logáil isteach',
        navigation: 'Loingseoireacht',
        users: 'Úsáideoirí',
        user: 'Úsáideoir',
        roles: 'Róil',
        role: 'Ról',
        'access-control': 'Rialú Rochtana',
        'access-management': 'Bainistíocht Rochtana',
        permissions: 'Ceadanna',
        permission: 'Cead',
        consent: 'Toiliú',
        'consent-management': 'Bainistiú Toilithe',
        privacy: 'Príobháideacht',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Iniúchadh',
        auditing: 'Iniúchadh',
        breach: 'Sárú',
        incident: 'teagmhas',
        incidents: 'Teagmhais',
        notification: 'Fógra',
        notifications: 'Fógraí',
        reporting: 'Tuairisciú',
        risk: 'Riosca',
        vendor: 'Díoltóir',
        vendors: 'Díoltóirí',
        'vendor-risk': 'Riosca Díoltóra',
        assessment: 'Measúnú',
        assessments: 'Measúnachtaí',
        dpia: 'DPIA',
        'cross-border': 'Trasteorann',
        transfer: 'Aistriú',
        transfers: 'Aistrithe',
        deletion: 'scriosadh',
        retention: 'Coinneáil',
        'data-mapping': 'Mapáil Sonraí',
        discovery: 'Fionnachtain',
        classification: 'Aicmiú',
        workflow: 'Sreabhadh oibre',
        workflows: 'Sreafaí oibre',
        template: 'Teimpléad',
        templates: 'Teimpléid',
        catalog: 'Catalóg',
        monitoring: 'Monatóireacht',
        tasks: 'Tascanna',
        task: 'Tasc',
        'task-management': 'Bainistíocht Tasc',
        organizations: 'Eagraíochtaí',
        org: 'Eagraíocht',
        governance: 'Rialachas',
        compliance: 'Comhlíonadh',
        forms: 'Foirmeacha',
        form: 'Foirm',
        connectors: 'Nascóirí',
        connector: 'Cónascaire',
        integration: 'Comhtháthú',
        integrations: 'Comhtháthaithe',
        security: 'Slándáil',
        encryption: 'Criptiú',
        backup: 'Cúltaca',
        recovery: 'Aisghabháil',
        maintenance: 'Cothabháil',
        settings: 'Socruithe',
        configuration: 'Cumraíocht',
        sla: 'SLA',
        escalation: 'Ardú',
        remediation: 'Feabhsúchán',
        evidence: 'Fianaise',
        'case-management': 'Bainistiú Cásanna',
        profile: 'Próifíl',
        delegation: 'Toscaireacht',
        'service-accounts': 'Cuntais Seirbhíse',
        license: 'Ceadúnas',
        'license-management': 'Bainistiú Ceadúnais',
        plan: 'Plean',
        plans: 'Pleananna',
        dashboard: 'Deais',
        'web-consent': 'Toiliú Gréasáin',
        'partner-marketplace': 'Margadh Comhpháirtithe',
        partner: 'Comhpháirtí',
        partners: 'Comhpháirtithe',
        deliverables: 'Nithe inseachadta',
        'service-tiers': 'Sraitheanna Seirbhíse',
        'support-tickets': 'Ticéid Tacaíochta',
        'audit-trail': 'Rian Iniúchta',
        'first-time-setup': 'Socrú Céaduaire',
        setup: 'Socrú',
        quickstart: 'Tús Tapa',
        tour: 'Turas',
        tours: 'Turais',
        'sample-data': 'Sonraí Samplacha',
        'data-subject': 'Ábhar Sonraí',
        'data-subjects': 'Ábhair Sonraí',
        pseudonymous: 'Pseudonymous',
        identified: 'Aitheanta',
        'help-center': 'Ionad Cabhrach',
        article: 'Airteagal',
        articles: 'Ailt',
        search: 'Cuardach',
        'platform-settings': 'Socruithe Ardáin',
        'platform-config': 'Cumraíocht Ardáin',
        'module-config': 'Cumraíocht an Mhodúil',
        'module-configuration': 'Cumraíocht an Mhodúil',
        'rights-requests': 'Iarratais ar Chearta',
        'access-accountability': 'Cuntasacht Rochtana',
        'data-discovery': 'Fionnachtain Sonraí',
        'incident-ops': 'Oibríochtaí Teagmhais',
        'deletion-orchestration': 'Ceolfhoireann Scriosta',
        'compliance-forms': 'Foirmeacha Comhlíonta',
        'program-governance': 'Rialachas Clár',
        localization: 'Logánú',
        'privacy-scanner': 'Scanóir Príobháideachta',
        'consent-properties': 'Airíonna Toiliú',
        acceptances: 'Glactha',
        access: 'Rochtain',
        'access-review': 'Athbhreithniú Rochtana',
        'access-reviews': 'Léirmheasanna Rochtana',
        accountability: 'Cuntasacht',
        acknowledgment: 'Admháil',
        'action-plans': 'Pleananna Gníomhaíochta',
        activation: 'Gníomhachtú',
        activations: 'Gníomhachtaí',
        'active-response': 'Freagra Gníomhach',
        activity: 'Gníomhaíocht',
        adequacy: 'Leordhóthanacht',
        'admin-guide': 'Treoir Riaracháin',
        aggregation: 'Comhiomlánú',
        agreements: 'Comhaontuithe',
        ai: 'AI',
        alerts: 'Foláirimh',
        analytics: 'Anailísíocht',
        anchoring: 'Ancaireacht',
        approval: 'Faomhadh',
        approvals: 'Ceaduithe',
        apps: 'Feidhmchláir',
        assets: 'Sócmhainní',
        assignments: 'Tascanna',
        attestation: 'Fianú',
        'audit-log': 'Loga Iniúchta',
        'audit-pack': 'Pacáiste Iniúchta',
        authentication: 'Fíordheimhniú',
        authority: 'Údarás',
        'auto-play': 'Uath-Seinn',
        automated: 'Uathoibrithe',
        automation: 'Uathoibriú',
        baseline: 'Bunlíne',
        batches: 'Baisceanna',
        bidirectional: 'Déthreo',
        bilingual: 'Dátheangach',
        bindings: 'ceangail',
        blockchain: 'Blockchain',
        board: 'Bord',
        brand: 'Branda',
        brds: 'BRDs',
        'break-glass': 'Briseadh-Gloine',
        builder: 'Tógálaí',
        bulk: 'Bulc',
        'bulk-sync': 'Bulk Sync',
        bundles: 'Bundles',
        calendar: 'Féilire',
        campaigns: 'Feachtais',
        cases: 'Cásanna',
        categories: 'Catagóirí',
        category: 'Catagóir',
        certification: 'Deimhniú',
        chain: 'Slabhra',
        change: 'Athrú',
        'change-awareness': 'Feasacht ar Athrú',
        'change-log': 'Athraigh Loga',
        'change-management': 'Bainistíocht Athruithe',
        'change-requests': 'Iarratais ar Athrú',
        'cmp-validation': 'CMP Bailíochtú',
        collection: 'Bailiúchán',
        comparison: 'Comparáid',
        completeness: 'Comhláine',
        completion: 'Críochnú',
        'compliance-posture': 'Staidiúir Chomhlíonta',
        'compliance-proof': 'Cruthúnas Comhlíonta',
        'compliance-reviews': 'Léirmheasanna Comhlíonta',
        config: 'Cumraíocht',
        confirmation: 'Daingniú',
        conflicts: 'Coinbhleachtaí',
        consolidation: 'Comhdhlúthú',
        continuous: 'Leanúnach',
        contracts: 'Conarthaí',
        controls: 'Rialuithe',
        cookies: 'Fianáin',
        cooperation: 'Comhar',
        coordination: 'Comhordú',
        'corporate-governance': 'Rialachas Corparáideach',
        'corrective-actions': 'Gníomhartha Ceartúcháin',
        coverage: 'Clúdach',
        'credential-vault': 'Cruinneachán credential',
        credentials: 'Dintiúir',
        cycles: 'Timthriallta',
        dashboards: 'Deais',
        'data-assets': 'Sócmhainní Sonraí',
        'data-flows': 'Sreabhadh Sonraí',
        'data-sensitivity': 'Íogaireacht Sonraí',
        'data-steward': 'Maor Sonraí',
        deadlines: 'Spriocdhátaí',
        'decree-356': 'Foraithne 356',
        dedup: 'Dedup',
        definitions: 'Sainmhínithe',
        deliverable: 'Inseachadta',
        departments: 'Ranna',
        'dependency-order': 'Ordú Spleáchais',
        deployment: 'Imscaradh',
        detection: 'Brath',
        diff: 'Difriúil',
        'digital-properties': 'Airíonna Digiteacha',
        dimensions: 'Toisí',
        disable: 'Díchumasaigh',
        'disaster-recovery': 'Aisghabháil Tubaiste',
        disclosure: 'Nochtadh',
        documentation: 'Doiciméadúchán',
        documents: 'Doiciméid',
        domains: 'Fearainn',
        dossier: 'Comhad',
        dpa: 'DPA',
        drift: 'Sruthán',
        'drift-detection': 'Brath Srutha',
        'due-diligence': 'Díchill chuí',
        email: 'Email',
        emergency: 'Éigeandála',
        enable: 'Cumasaigh',
        'enterprise-map': 'Léarscáil Fiontair',
        erasure: 'Scriosadh',
        evaluation: 'Meastóireacht',
        'event-bus': 'Bus Imeachta',
        exceptions: 'Eisceachtaí',
        execution: 'Forghníomhú',
        executive: 'Feidhmeannach',
        exercises: 'Cleachtaí',
        export: 'Easpórtáil',
        exports: 'Onnmhairí',
        filing: 'Comhdú',
        filter: 'Scagaire',
        filtering: 'Scagadh',
        findings: 'Torthaí',
        flows: 'Sreabhadh',
        'follow-up': 'Leantach',
        forensic: 'Fóiréinseach',
        forensics: 'Fóiréinseach',
        'form-filler': 'Foirm Filler',
        framework: 'Creatlach',
        frameworks: 'Creataí',
        'gap-analysis': 'Anailís Bearnaí',
        'gap-report': 'Tuairisc Bearnaí',
        gaps: 'bearnaí',
        gates: 'Geataí',
        'getting-started': 'Tús a chur leis',
        'golden-templates': 'Teimpléid Órga',
        grc: 'GRC',
        'group-management': 'Bainistíocht Grúpa',
        groups: 'Grúpaí',
        'guided-tours': 'Turais Threoraithe',
        guides: 'treoracha',
        handling: 'Láimhseáil',
        'hands-on': 'Lámha-Ar',
        health: 'Sláinte',
        help: 'Cabhrú',
        hierarchy: 'Ordlathas',
        history: 'Stair',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': 'Soláthraí Aitheantais',
        implementation: 'Cur i bhfeidhm',
        import: 'Iompórtáil',
        improvement: 'Feabhsú',
        'incident-response': 'Freagra Teagmhais',
        inconsistencies: 'Neamhréireachtaí',
        instances: 'cásanna',
        intake: 'Iontógáil',
        'intake-key': 'Eochair Iontrála',
        interactive: 'Idirghníomhach',
        international: 'Idirnáisiúnta',
        inventory: 'Fardal',
        investigation: 'Imscrúdú',
        investigations: 'Imscrúduithe',
        'ip-allowlist': 'Liosta Cheadaithe IP',
        issues: 'Saincheisteanna',
        justification: 'Fírinniú',
        justifications: 'Fírinnithe',
        kanban: 'Kanban',
        keyboard: 'Méarchlár',
        keys: 'Eochracha',
        'knowledge-base': 'Bonn Eolais',
        koncernstyring: 'Rialachas Corparáideach',
        kpi: 'KPI',
        labeling: 'Lipéadú',
        language: 'Teanga',
        'language-packs': 'Pacáistí Teanga',
        languages: 'Teangacha',
        lawfulness: 'Dlíthiúlacht',
        layout: 'Leagan Amach',
        'legal-basis': 'Bunús Dlí',
        'lessons-learned': 'Ceachtanna Foghlamtha',
        levels: 'Leibhéil',
        library: 'Leabharlann',
        lifecycle: 'Saolré',
        lineage: 'lineage',
        links: 'Naisc',
        listings: 'Liostaí',
        live: 'Beo',
        locale: 'Logánta',
        log: 'Log',
        logging: 'Logáil',
        'managed-service': 'Seirbhís Bainistithe',
        mapping: 'Mapáil',
        mappings: 'Mapálacha',
        'market-packs': 'Pacáistí Margaidh',
        marketplace: 'Margadh',
        maturity: 'Aibíocht',
        metrics: 'Méadracht',
        models: 'Múnlaí',
        module: 'Modúl',
        modules: 'modúil',
        mps: 'MPS',
        'multi-factor': 'Il-Fachtóir',
        'multi-market': 'Il-Mhargadh',
        'multi-org': 'Il-Org',
        'multi-property': 'Il-Mhaoine',
        'my-tasks': 'Mo Thascanna',
        obligations: 'Oibleagáidí',
        offboarding: 'Offboarding',
        oidc: 'OIDC',
        orchestration: 'Ceolfhoireann',
        'org-groups': 'Grúpaí Org',
        organization: 'Eagraíocht',
        overdue: 'thar téarma',
        overview: 'Forbhreathnú',
        'owner-review': 'Léirmheas Úinéir',
        package: 'Pacáiste',
        packages: 'Pacáistí',
        packs: 'Pacáistí',
        pagination: 'Leathanaigh',
        'parent-child': 'Tuismitheoir-Leanaí',
        patterns: 'Patrúin',
        performance: 'Feidhmíocht',
        periodic: 'Tréimhsiúil',
        periods: 'Tréimhsí',
        personal: 'Pearsanta',
        'personal-data': 'Sonraí Pearsanta',
        personas: 'Daoine',
        planning: 'Pleanáil',
        platform: 'Ardán',
        playbooks: 'Leabhair súgartha',
        poc: 'PoC',
        'point-in-time': 'Pointe in Am',
        policies: 'Polasaithe',
        portfolio: 'Punann',
        'post-incident': 'Iar-Teagmhais',
        'power-user': 'Úsáideoir Cumhachta',
        practice: 'Cleachtadh',
        pricing: 'Praghsáil',
        'privacy-impact': 'Tionchar Príobháideachta',
        procedures: 'Nósanna Imeachta',
        processes: 'Próisis',
        processing: 'Próiseáil',
        'processing-activities': 'Gníomhaíochtaí Próiseála',
        procurement: 'Soláthar',
        productivity: 'Táirgiúlacht',
        profiles: 'Próifílí',
        programs: 'Cláir',
        progress: 'Dul chun cinn',
        proof: 'Cruthúnas',
        propagation: 'Iomadú',
        properties: 'Airíonna',
        'pub-sub': 'Tábhairne-Fo',
        'public-form': 'Foirm Phoiblí',
        quality: 'Cáilíocht',
        questionnaires: 'Ceistneoirí',
        'quick-links': 'Naisc Thapa',
        'quick-start': 'Tús Tapa',
        'quick-starts': 'Tús Tapa',
        raci: 'RACI',
        readiness: 'ullmhacht',
        'real-time': 'Fíor-Ama',
        reassessments: 'Athmheasúnuithe',
        reconciliation: 'Athmhuintearas',
        record: 'Taifead',
        records: 'Taifid',
        recurring: 'Athfhillteach',
        register: 'Clár',
        registry: 'Clárlann',
        regulations: 'Rialacháin',
        regulator: 'Rialtóir',
        regulatory: 'Rialála',
        'regulatory-frameworks': 'Creataí Rialála',
        remap: 'Remap',
        reminders: 'Meabhrúcháin',
        reports: 'Tuarascálacha',
        requests: 'Iarratais',
        requirements: 'Riachtanais',
        response: 'Freagra',
        'response-plans': 'Pleananna Freagartha',
        'response-team': 'Foireann Freagartha',
        'response-templates': 'Teimpléid Freagartha',
        'response-time': 'Am Freagartha',
        responses: 'Freagraí',
        responsibility: 'Freagracht',
        results: 'Torthaí',
        review: 'Léirmheas',
        reviews: 'Léirmheasanna',
        'right-to-delete': 'Ceart Scriosta',
        rights: 'Cearta',
        'risk-tiers': 'Sraitheanna Riosca',
        rollouts: 'Rolláin',
        rollup: 'Rolladh suas',
        ropa: 'ROPA',
        routing: 'Ródú',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Rialacha',
        runs: 'Ritheann',
        saml: 'SAML',
        scan: 'Scanadh',
        scanning: 'Scanadh',
        scc: 'SCC',
        schedule: 'Sceideal',
        scheduled: 'Sceidealta',
        schedules: 'Sceidil',
        scheduling: 'Sceidealú',
        scores: 'Scóir',
        scoring: 'Scóráil',
        scripts: 'Scripteanna',
        'security-reviews': 'Léirmheasanna Slándála',
        'sensitive-data': 'Sonraí Íogaire',
        sensitivity: 'Íogaireacht',
        'separation-of-duties': 'Scaradh Dualgais',
        service: 'Seirbhís',
        sessions: 'Seisiúin',
        'setup-wizard': 'Treoraí Socrú',
        severity: 'Déine',
        'shared-governance': 'Rialachas Comhroinnte',
        'shared-settings': 'Socruithe Roinnte',
        shortcuts: 'Aicearraí',
        sidebar: 'Barra Taoibh',
        signals: 'Comharthaí',
        'single-sign-on': 'Síniú Aonair',
        snapshots: 'Snapshots',
        sod: 'SoD',
        sorting: 'Sórtáil',
        sources: 'Foinsí',
        staging: 'Ag stáitsiú',
        standards: 'Caighdeáin',
        status: 'Stádas',
        structure: 'Struchtúr',
        'sub-processor': 'Fo-phróiseálaí',
        'sub-processors': 'Fo-Phróiseálaithe',
        submissions: 'Aighneachtaí',
        subprocessors: 'Fophróiseálaithe',
        subscription: 'Síntiús',
        suggestions: 'Moltaí',
        summaries: 'Achoimrí',
        supplements: 'Forlíonta',
        support: 'Tacaíocht',
        sync: 'Sioncrónaigh',
        system: 'Córas',
        systems: 'Córais',
        tables: 'Táblaí',
        tagging: 'Clibeáil',
        'tamper-proof': 'Tamper-Cruthúnas',
        taxonomies: 'Tacsanomaíochtaí',
        taxonomy: 'Tacsanomaíocht',
        tenants: 'Tionóntaithe',
        termination: 'Foirceannadh',
        'third-party': 'Tríú Páirtí',
        'ticket-sync': 'Sioncronú Ticéad',
        tickets: 'Ticéid',
        tiers: 'Sraitheanna',
        tokens: 'Comharthaí',
        totp: 'TOTP',
        traceability: 'Inrianaitheacht',
        tracking: 'Rianú',
        trail: 'Conaire',
        training: 'Traenáil',
        translation: 'Aistriúchán',
        translations: 'Aistriúcháin',
        transparency: 'Trédhearcacht',
        trends: 'Treochtaí',
        unified: 'Aontaithe',
        updates: 'Nuashonruithe',
        validation: 'Bailíochtú',
        vault: 'cruinneachán',
        'vendor-requests': 'Iarratais Díoltóra',
        verification: 'Fíorú',
        versioning: 'Leagan',
        versions: 'Leaganacha',
        vietnam: 'Vítneam',
        violations: 'Sáruithe',
        visualization: 'Amharcléiriú',
        waivers: 'Tarscaoiltí',
        'web-records': 'Taifid Ghréasáin',
        website: 'Suíomh Gréasáin',
        websites: 'Láithreáin Ghréasáin',
        weights: 'Meáchain',
        'white-label': 'Lipéad Bán',
        withdrawal: 'A tharraingt siar',
    },
    module_labels: {
        // Sprint 62.57.0 Slice E — the audit module of the certificate register.
        service_certificates: 'Processing Service Certificate',
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: 'Iarratais ar Chearta',
        data_mapping: 'Mapáil Sonraí',
        governance: 'Rialachas',
        consent: 'Rialachas Toilithe',
        consent_properties: 'Airíonna toilithe',
        assessments: 'MTCS & Measúnuithe',
        classification: 'Aicmiú Sonraí',
        monitoring: 'Cláir Mhonatóireachta',
        program: 'Clár Monatóireachta',
        incidents: 'Freagairt ar Theagmhais',
        connectors: 'Nascóirí',
        vendor_risk: 'Riosca Díoltóra',
        deletion_orchestration: 'Comhordú scriosta',
        discovery: 'Aimsiú sonraí',
        incident_ops: 'Oibríochtaí teagmhas',
        program_governance: 'Rialachas clár',
        access_accountability: 'Cuntasacht rochtana',
        localization: 'Logánú',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Spás oibre comhpháirtithe',
        privacy_scanner: 'Scanóir príobháideachta',
        onboarding: 'Ar bordáil',
        compliance_forms: 'Foirmeacha Comhlíontachta',
        audit_trail: 'Rian Iniúchóireachta',
        data_subjects: 'Ábhair Sonraí',
        organizations: 'Eagraíochtaí',
        task_management: 'Bainistíocht Tasc',
        deletion_orch: 'Comhordú scriosta',
        forms: 'Foirmeacha comhlíonta',
        partner_marketplace: 'Margadh comhpháirtithe',
        dashboard: 'Painéal',
        platform_settings: 'Socruithe ardáin',
        help_center: 'Ionad Cabhrach',
    },
    module_descriptions: {
        rights_requests: 'Bainistigh iarratais ar chearta ábhair sonraí lena n-áirítear rochtain, scriosadh agus iniomparthacht. Rianaigh saolré na n-iarratas, sann láimhseálaithe agus comhlíon spriocdhátaí rialála.',
        data_mapping: "Mapáil sreafaí sonraí ar fud d'eagraíochta. Rianaigh gníomhaíochtaí próiseála, catagóirí sonraí, cuspóirí, polasaithe coinneála agus comhtháthuithe córais.",
        governance: 'Maoirseacht agus an fhianaise atá taobh thiar di. Bainistigh clár na soláthróirí agus a shaolré, faomh na cinntí a rialaíonn an modúl seo, coinnigh súil ar shraonadh comhlíonta, agus coinnigh na cláir fianaise le haghaidh iontaobhais agus céannachta, déantán rialála, íocaíochtaí agus eKYC, agus fianaise ardáin.',
        consent: 'Bainistigh bailiú, stóráil agus aistarraingt toilithe. Rianaigh taifid toilithe, cuspóirí agus comhtháthú le hardáin bhainistíochta toilithe.',
        consent_properties: 'Cumraigh airíonna toilithe, grúpaí agus teimpléid óir. Sainmhínigh cumraíochtaí toilithe in-athúsáidte chun imscaradh comhsheasmhach a chinntiú.',
        assessments: 'Déan Measúnuithe Tionchair Cosanta Sonraí (MTCS) agus measúnuithe riosca príobháideachais. Meas gníomhaíochtaí próiseála i leith rioscaí comhlíonta.',
        classification: 'Aicmigh sócmhainní sonraí de réir leibhéal íogaireachta. Cuir lipéid tacsanomaíochta i bhfeidhm, rianaigh taifid saolré agus aithin bearnaí aicmithe.',
        monitoring: 'Socraigh cláir mhonatóireachta chun méadracht comhlíonta, príomhtháscairí riosca agus éifeachtacht rialuithe a rianú le himeacht ama.',
        incidents: 'Tuairiscigh agus bainistigh teagmhais phríobháideachais agus sáruithe sonraí. Rianaigh sreafaí oibre imscrúdaithe, fógraíochta agus feabhsúcháin.',
        connectors: 'Cumraigh nascóirí foinse sonraí agus comhtháthuithe. Nasc córais sheachtracha le haghaidh aimsiú sonraí uathoibríoch agus monatóireacht.',
        vendor_risk: 'Meas agus déan monatóireacht ar rioscaí soláthróirí tríú páirtí. Scóráil soláthróirí, rianaigh maolú riosca agus bainistigh cumraíochtaí riosca soláthróirí.',
        deletion_orchestration: 'Ceapdóirigh scriosadh sonraí ar fud córas. Rianaigh iarratais scriosaidh, fíoraigh slabhraí cruthúnais agus cinntigh baint iomlán sonraí.',
        discovery: 'Aimsigh sonraí pearsanta ar fud córas nasctha. Scan foinsí sonraí chun fardal cuimsitheach sonraí a thógáil.',
        incident_ops: 'Bainistíocht teagmhas oibríochtúil le leabhair imeartha, sreafaí oibre ardaithe agus rianú SLA chun teagmhais a réiteach.',
        program_governance: "Rianaigh aibíocht fhoriomlán an chláir phríobháideachais, scóir chomhlíonta, bearnaí fianaise agus tuairisciú feidhmiúcháin ar fud d'eagraíochta.",
        access_accountability: 'Imscrúdaigh agus iniúch patrúin rochtana úsáideoirí. Rianaigh cumraíochtaí rochtana, déan monatóireacht ar ghníomhaíocht amhrasach agus forfheidhmigh cuntasacht.',
        localization: 'Suiteáil agus bainistigh pacáistí logánaithe margaidh. Cuir creataí rialála, pacáistí teanga agus ábhar comhlíonta réigiúnach leis.',
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Comhoibrigh le comhpháirtithe seachtracha. Comhroinn sonraí comhlíonta, rianaigh oibleagáidí comhpháirtithe agus bainistigh caidrimh chomhpháirtithe.',
        privacy_scanner: 'Scan suíomhanna gréasáin le haghaidh saincheisteanna comhlíonta príobháideachais. Déan monatóireacht ar úsáid fianán, scripteanna rianaithe agus láithreacht polasaí príobháideachais i bhfíor-am.',
        onboarding: 'Treoraigh eagraíochtaí nua trí shocrú an ardáin. Draoi ionduchtaithe céim ar chéim le haghaidh cumraíocht tosaigh agus iompórtáil sonraí.',
        compliance_forms: 'Cruthaigh agus bainistigh foirmeacha comhlíontachta ó chatalóg teimpléad. Líon isteach foirmeacha rialála, rianaigh aighneachtaí agus gin tuairiscí.',
        audit_trail: 'Féach ar rian iniúchóireachta iomlán de gach gníomh córais. Rianaigh cé a rinne céard, cathain agus cá háit ar fud gach modúl.',
        organizations: 'Bainistigh eagraíochtaí agus socruithe ilthionóntacha. Cumraigh próifílí eagraíochtaí, ordlathaí agus caidrimh trasengraíochta.',
    },
    privacy_scanner: {
        anomaly_explain: {
            explain_label: 'Mínigh aimhrialtachtaí',
            explaining: 'Ag míniú…',
            results_heading: 'Mínithe ar aimhrialtacht ({severity})',
            tokens_label: '≈ {count} comharthaí',
            items_label: '{count} mhíreanna',
            remediation_label: 'Céimeanna leasúcháin',
            items_detail_label: 'Míreanna a bhfuil tionchar orthu',
            accept_label: 'Glac le míniú',
            reject_label: 'Diúltaigh míniú',
            error_generic: 'Níorbh fhéidir na haimhrialtachtaí a mhíniú. Bain triail eile as nó seiceáil an scanadh críochnaithe go rathúil.',
        },
        cookie_insight: {
            insight_label: 'Fianán & rianaire AI léargas',
            producing: 'Léargas á léiriú…',
            results_heading: 'Léargas fianán & rianaithe ({severity})',
            tokens_label: '≈ {count} comharthaí',
            items_label: '{count} mhíreanna',
            classification_label: 'Aicmiú',
            severity_label: 'Déine',
            banner_copy_label: 'Cóip banner molta',
            retention_label: 'Comhairle coinneála',
            citations_label: 'Comhlua',
            kind_cookie: 'Fianán',
            kind_tracker: 'Lorgaire',
            class_necessary: 'Riachtanach',
            class_analytics: 'Anailísíocht',
            class_marketing: 'Margaíocht',
            class_social: 'Sóisialta',
            accept_label: 'Glac le léargas',
            reject_label: 'Diúltaigh léargas',
            no_items: 'Níor aimsíodh aon fhianán nó rianairí sa scanadh seo.',
            error_generic: 'Níorbh fhéidir an léargas fianán a tháirgeadh. Bain triail eile as nó seiceáil an scanadh críochnaithe go rathúil.',
        },
        monitoring: {
            title: 'Príobháideachas Monatóireacht',
            new_scan: 'Nua Scanadh',
            scan_url: 'URL to Scanadh',
            scan_frequency: 'Scanadh Minicíocht',
            last_scan: 'Last Scanadh',
            score: 'Príobháideachas Scór',
            issues: 'Fadhbanna Aimsithe',
            history: 'Scanadh Stair',
            settings: 'Monatóireacht Socruithe',
        },
        real_time: {
            title: 'Scanóir Fíor-Ama',
            scan_now: 'Scanadh Now',
            scanning: 'Ag scanadh...',
            results: 'Scanadh Results',
            no_results: 'No scanadh results yet.',
            cookies: 'Fianáin',
            trackers: 'Rianairí',
            consent_banners: 'Toiliú Banners',
            third_party: 'Scripteanna Tríú Páirtí',
            archive: 'Results Cartlannaigh',
            settings: 'Scanner Socruithe',
        },
        dashboard: {
            page_title: 'Painéal Monatóireachta Príobháideachta',
            privacy_monitoring: 'Monatóireacht Príobháideachta',
            plan_label: '{package} plean',
            expires_label: 'in éag {date}',
            settings: 'Socruithe',
            refresh: 'Athnuaigh',
            refreshing_dashboard: 'Painéal monatóireachta á athnuachan',
            refresh_aria: 'Athnuaigh an painéal monatóireachta',
            not_configured_title: 'Monatóireacht Príobháideachta Gan Cumraithe',
            not_configured_desc: 'Ceangail do sheirbhís Monatóireachta Príobháideachta AesirX chun comhlíonadh an láithreáin ghréasáin a rianú.',
            configure_monitoring: 'Cumraigh Monatóireacht',
            license_expired_title: 'Ceadúnas in éag',
            license_expired_desc: 'Chuaigh do cheadúnas Monatóireachta Príobháideachta AesirX in éag. Déan é a athnuachan le do thoil chun briseadh seirbhíse a sheachaint.',
            compliance_score: 'Scór Comhlíonta',
            cookies_detected: 'Fianáin Braitheadh',
            trackers_label: 'Lorgairí',
            open_issues: 'Ceisteanna Oscailte',
            score_good: 'Maith',
            score_needs_improvement: 'Feabhsú de dhíth',
            score_critical: 'Criticiúil',
            action_required: 'Gníomh de dhíth',
            cookies_sr: 'fianáin aimsithe',
            trackers_sr: 'lorgairí aimsithe',
            open_issues_sr: 'ceisteanna oscailte',
            action_required_sr: 'gníomh de dhíth',
            metric_label: 'méadrach',
            value_label: 'Luach',
            status_label: 'Stádas',
            aria_metrics: 'Méadracht monatóireachta príobháideachta',
            plan_limit_warning: 'Ag scanadh URL: {current}/{max} — tá teorainn do phlean sroichte agat.',
            manage_domains: 'Bainistigh fearainn nó uasghrádaigh do phlean.',
            monitored_domains: 'Fearainn Monatóireachta',
            domain_added: 'Fearann {domain} curtha leis',
            domain_removed: 'Baineadh an fearann {domain}',
            failed_add_domain: 'Theip ar an bhfearann ​​a chur leis',
            failed_remove_domain: 'Theip ar bhaint an fhearainn',
            failed_load: 'Theip ar lódáil an deais mhonatóireachta',
            new_domain_aria: 'Fearann nua URL',
            add_domain: 'Cuir Fearann leis',
            adding_domain: 'Ag cur…',
            no_domains: 'Níl aon fhearann monatóireachta cumraithe fós.',
            aria_domains_table: 'Fearainn faoi mhonatóireacht',
            col_domain: 'Fearann',
            col_compliance_score: 'Scór Comhlíonta',
            col_status: 'Stádas',
            col_cookies: 'Fianáin',
            col_trackers: 'Lorgairí',
            col_issues: 'Saincheisteanna',
            col_last_scan: 'Scanadh Deireanach',
            col_action: 'Gníomh',
            compliance_status_sr: 'stádas comhlíonta',
            showing_chart: 'Amharc cairte á thaispeáint',
            showing_table: 'Amharc tábla sonraí á thaispeáint',
            loading_sr: 'Painéal monatóireachta á lódáil',
            col_framework_tags: 'Clibeanna Creata',
            col_last_monitored: 'Déanta Monatóireachta',
            framework_tags_sr: 'creataí rialála is infheidhme',
            last_monitored_sr: 'stampa ama monatóireachta leanúnaí',
            audit_trail_heading: 'Rian Iniúchta',
            audit_trail_info: 'Cuirtear stampa ama ar gach scanadh monatóireachta agus déantar é a logáil. Cruthaíonn athruithe comhlíonta ar leibhéal an fhearainn taifead iniúchta do-iniúchta ar féidir a onnmhairiú le haghaidh athbhreithniú rialála.',
            cybersecurity_law_info_heading: 'Brath Dlí Cybersecurity',
            cybersecurity_law_info: 'Déantar fearainn a ndéantar faireachán orthu a sheiceáil go huathoibríoch i gcoinne ceanglais dhlí na cibearshlándála (m.sh., Dlí Cibearshlándála Vítneam, NIS2 Treoir). Léiríonn clibeanna creata na rialacháin atá i bhfeidhm. Déantar saincheisteanna a chatagóiriú de réir creat rialála le haghaidh feabhsúcháin spriocdhírithe.',
        },
        scan: {
            page_title: 'Scanadh Fíor-Ama',
            title: 'Scanóir Príobháideachta Fíor-Ama',
            settings: 'Socruithe',
            results_archive: 'Cartlann Torthaí',
            launch_scan: 'Seoladh Scan',
            target_url: 'Sprioc URL',
            protocol_aria: 'Prótacal',
            url_required: 'Tá URL ag teastáil',
            url_whitespace: 'Bain spás bán den URL le do thoil',
            url_invalid: 'Cuir isteach fearann bailí (m.sh. sampla.com)',
            add_subpages: 'Cuir Fo-leathanaigh leis',
            add_subpages_aria: 'Cuir fo-leathanaigh leis, {count} curtha leis',
            start_scan: 'Tosaigh Scan',
            launching: 'Ag seoladh…',
            scan_launched: 'Scan seolta go rathúil',
            scan_failed: 'Theip ar an scanadh a sheoladh',
            scan_progress: 'Scan Dul Chun Cinn',
            scanning_in_progress: 'Scanadh ar siúl…',
            scan_id: 'Scan ID',
            status_label: 'Stádas',
            status_unknown: 'unknown',
            progress_label: 'Dul chun cinn',
            pages_label: 'Leathanaigh',
            refresh_status: 'Stádas Athnuaigh',
            new_scan: 'Scanadh Nua',
            scan_completed: 'Scanadh críochnaithe, torthaí ar fáil',
            failed_check_status: 'Theip ar stádas an scanadh a sheiceáil',
            scan_results: 'Torthaí Scan',
            main_page: 'Príomhleathanach',
            page_n: 'Leathanach {n}',
            result_pages_nav: 'Leathanaigh torthaí',
            page_url: 'Leathanach URL',
            risk_score: 'Scór Riosca',
            risk_score_sr: 'scór riosca',
            cookies_label: 'Fianáin',
            cookies_sr: 'fianáin aimsithe',
            scripts_label: 'Scripteanna',
            scripts_sr: 'scripteanna aimsithe',
            issues_label: 'Saincheisteanna',
            issues_sr: 'saincheisteanna aimsithe',
            issues_found: 'Saincheisteanna a Fuarthas',
            aria_issues_table: 'Saincheisteanna a aimsíodh le linn scanadh',
            col_severity: 'Déine',
            col_category: 'Catagóir',
            col_title: 'Teideal',
            col_remediation: 'Feabhsúchán',
            severity_sr: 'severity',
            scan_result_metrics: 'Scan méadracht na dtorthaí',
            view_full_detail: 'Féach Mionsonraí Iomlán',
            col_consent_category: 'Catagóir Toilithe',
            consent_category_sr: 'catagóiriú toilithe',
            compliance_evidence_heading: 'Fianaise Chomhlíonta',
            compliance_evidence_info: 'Nasctar torthaí scanadh le cásanna comhlíonta go huathoibríoch. Is féidir gach saincheist a rianú go catagóir toilithe (riachtanach, feidhmiúil, anailísíocht, margaíocht) agus cuireann sé fianaise ar fáil atá réidh le haghaidh iniúchta le haghaidh athbhreithniú rialála.',
            rule_config_heading: 'Cumraíocht Riail',
            rule_config_info: 'Is féidir rialacha scanadh a chumrú de réir an chreata rialála. Is féidir seiceálacha fianán, braite scripte, agus anailís díoltóirí a chumasú nó a dhíchumasú trí Socruithe. Is féidir rialacha saincheaptha a shainiú chun freastal ar riachtanais chomhlíonta d’eagraíochta.',
            case_linkage_heading: 'Nasc Cás Comhlíonta',
            case_linkage_info: 'Is féidir saincheisteanna a aimsítear le linn scananna fíor-ama a nascadh le cásanna comhlíonta le haghaidh rianú agus feabhsúcháin. Déan nascleanúint go dtí an leathanach Scan Result Detail chun cumainn chásanna a fheiceáil.',
            subpages_modal_title: 'Cuir Fo-leathanaigh leis',
            subpages_modal_desc: 'Cuir suas le 5 URL fo-leathanach leis lena scanadh taobh leis an bpríomhfhearann.',
            subpage_n: 'Foleathanach {n}',
        },
        result_detail: {
            page_title: 'Scan Sonraí an Toraidh',
            title: 'Toradh Scanta',
            back_to_archive: 'Ar ais go Cartlann',
            failed_load: 'Theip ar an toradh scanadh a lódáil',
            loading_sr: 'Toradh an scanta á lódáil…',
            scan_id: 'Scan ID',
            target_url: 'Sprioc URL',
            status_label: 'Stádas',
            status_sr: 'status',
            started: 'Thosaigh',
            completed: 'Críochnaithe',
            pages_scanned: 'Leathanaigh Scanta',
            https_label: 'HTTPS',
            highest_risk_score: 'Scór Riosca is Airde',
            most_cookies: 'Fianáin is mó',
            most_vendors: 'An chuid is mó de na Díoltóirí 3ú páirtí',
            most_issues: 'Saincheisteanna is mó',
            across_pages: 'trasna leathanaigh',
            highest_values_aria: 'Na luachanna is airde ar gach leathanach',
            scanned_pages_nav: 'Leathanaigh scanta',
            main_page: 'Príomhleathanach',
            page_n: 'Leathanach {n}',
            page_url: 'Leathanach URL',
            risk_score: 'Scór Riosca',
            overall_risk_sr: 'scór riosca foriomlán',
            cookies_label: 'Fianáin',
            cookies_sr: 'fianáin aimsithe',
            vendors_label: 'Díoltóirí 3ú páirtí',
            vendors_sr: 'díoltóirí tríú páirtí braite',
            issues_label: 'Saincheisteanna',
            issues_sr: 'saincheisteanna aimsithe',
            risk_breakdown_aria: 'Miondealú scór riosca',
            issues_found: 'Saincheisteanna a Fuarthas',
            aria_issues_table: 'Saincheisteanna a aimsíodh le linn scanadh',
            col_severity: 'Déine',
            col_category: 'Catagóir',
            col_title: 'Teideal',
            col_remediation: 'Feabhsúchán',
            severity_sr: 'severity',
            cookies_heading: 'Fianáin ({count})',
            aria_cookies_table: 'Braitheadh fianáin',
            col_name: 'Ainm',
            col_domain: 'Fearann',
            col_path: 'Conair',
            col_secure: 'Slán',
            col_httponly: 'Http Amháin',
            col_samesite: 'SameSite',
            vendors_heading: 'Díoltóirí Tríú Páirtí ({count})',
            aria_vendors_table: 'Braitheadh díoltóirí tríú páirtí',
            col_hostname: 'Óstainm',
            scripts_heading: 'Scripteanna / Rabhcháin ({count})',
            aria_scripts_table: 'Scripteanna agus rabhcháin aimsithe',
            col_url: 'URL',
            col_type: 'Cineál',
            links_heading: 'Naisc',
            first_party: 'Céad-pháirtí',
            third_party: 'Tríú páirtí',
            col_risk_category: 'Catagóir Riosca',
            col_regulatory_framework: 'Creat Rialála',
            col_actionable_detail: 'Sonraí Inghníomhaithe',
            risk_category_sr: 'catagóir riosca rialála',
            risk_category_mapping_heading: 'Mapáil Chatagóir Riosca',
            risk_category_mapping_info: 'Déantar gach toradh scanadh a mhapáil chuig catagóir riosca rialála (bailiú sonraí, aistriú trasteorann, bainistíocht toilithe, coinneáil sonraí, rialuithe slándála). Cumasaíonn an mhapáil seo feabhsúchán spriocdhírithe arna chur in ord tosaíochta ag tionchar rialála.',
            compliance_case_heading: 'Comhlachas Cásanna Comhlíonta',
            compliance_case_info: 'Tá torthaí scanadh nasctha le cásanna comhlíonta le haghaidh rianú ceann go ceann. Is féidir saincheisteanna a ardú go díreach ó thorthaí scanadh go sreabhadh oibre an cháis chomhlíonta.',
        },
        results_archive: {
            page_title: 'Scan Cartlann Torthaí',
            title: 'Scan Cartlann Torthaí',
            back_to_scanner: 'Ar ais go Scanóir',
            failed_load: 'Theip ar na torthaí scanadh a lódáil',
            results_loaded: 'Luchtaíodh na torthaí scanadh, {count} taifead',
            total_label: 'Iomlán: {count}',
            aria_table: 'Scan cartlann na dtorthaí',
            col_scan_date: 'Dáta Scanadh',
            col_target_url: 'Sprioc URL',
            col_duration: 'Fad',
            col_issues_found: 'Saincheisteanna a Fuarthas',
            col_risk_score: 'Scór Riosca',
            col_status: 'Stádas',
            col_actions: 'Gníomhartha',
            scan_status_sr: 'stádas scanadh',
            view_result: 'Amharc',
            no_results: 'Níor aimsíodh aon torthaí scanadh.',
            loading_sr: 'Torthaí scanadh á lódáil',
            pagination_aria: 'Leathanaigh na dtorthaí a scanadh',
            col_risk_category: 'Catagóir Riosca',
            risk_category_sr: 'catagóir riosca rialála',
            archive_retention_heading: 'Coinneáil Cartlainne',
            archive_retention_info: 'Coinnítear torthaí scanadh ar feadh na tréimhse atá sonraithe i bpolasaí coinneála sonraí d’eagraíochta. I measc na dtorthaí cartlainne tá comhthéacs scanadh iomlán (clibeanna creata, catagóirí riosca, treoir leasúcháin) agus is féidir iad a onnmhairiú mar CSV le haghaidh fianaise rialála.',
            export_context_info: 'Áirítear le CSV comhaid a easpórtáladh comhthéacs rialála: catagóir riosca, creata is infheidhme, agus sonraí feabhsúcháin inghníomhaithe do gach cinneadh.',
        },
        scanner_settings: {
            page_title: 'Socruithe Scanóir',
            title: 'Socruithe Scanóir',
            back_to_scanner: 'Ar ais go Scanóir',
            failed_load: 'Theip ar chumraíocht an scanóir a lódáil',
            endpoint_url: 'Críochphointe URL',
            save_success: "D'éirigh le cumraíocht an scanóir a shábháil.",
            save_failed: 'Theip ar chumraíocht an scanóir a shábháil',
            test_connection: 'Ceangal Tástála',
            testing: 'Ag tástáil...',
            saving: 'Ag sábháil...',
            loading_sr: 'Íosluchtaigh socruithe scanóir',
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Socruithe Monatóireachta Príobháideachta',
            title: 'Socruithe Monatóireachta',
            back_to_dashboard: 'Ar ais go Painéal',
            failed_load: 'Theip ar chumraíocht monatóireachta a lódáil',
            credentials_heading: 'AesirX Dintiúir',
            connected_as: 'Ceangailte mar {username}',
            credentials_desc: 'Cuir isteach do dhintiúir chuntais AesirX chun Monatóireacht Príobháideachta a nascadh. Aimsigh do Bhall ID ar leathanach próifíle do chuntais AesirX.',
            client_id: 'Aitheantas Cliant',
            client_secret: 'Rúnda Cliant',
            username: 'AesirX Ainm úsáideora (ríomhphost)',
            password: 'AesirX Pasfhocal',
            placeholder_keep: '(fág bán chun é a choinneáil ann)',
            credential_form_aria: 'AesirX fhoirm inchreidte',
            test_connection: 'Ceangal Tástála',
            testing: 'Ag tástáil…',
            test_aria: 'Tástáil AesirX nasc',
            testing_aria: 'Tástáil nasc',
            save_configuration: 'Sábháil Cumraíocht',
            saving: 'Ag sábháil…',
            save_aria: 'Sábháil dintiúir',
            saving_aria: 'Dintiúir a shábháil',
            credentials_saved: 'AesirX dintiúir sábháil go rathúil.',
            credentials_saving: 'AesirX dintiúir sábháilte. cumraíocht á hathlódáil…',
            credentials_save_failed: 'Theip ar shábháil na ndintiúir',
            client_id_required: 'Tá ID an chliaint agus ainm úsáideora ag teastáil.',
            all_fields_required: 'Tá gach réimse creidiúnaithe ag teastáil.',
            email_heading: 'Fógraí Ríomhphoist',
            receive_emails: 'Faigh ríomhphoist tuairiscithe scanta',
            email_toggle_aria: 'Scoránaigh scanadh ríomhphoist tuairisce',
            only_on_change: 'Seol ríomhphost amháin nuair a athraíonn an stádas riosca',
            only_on_change_aria: 'Scoránaigh ríomhphost amháin ar athrú stádais riosca',
            emails_enabled: 'Ríomhphoist tuairisce scanta cumasaithe',
            emails_disabled: 'Díchumasaíodh ríomhphoist tuairisce scanta',
            only_change_on: 'Seol ríomhphost ar athrú riosca amháin',
            only_change_off: 'Seol ríomhphost ar gach scanadh',
            email_failed: 'Theip ar an socrú ríomhphoist a nuashonrú',
            email_settings_aria: 'Socruithe fógra ríomhphoist',
            loading_sr: 'Socruithe monatóireachta á lódáil',
        },
        entity_types: {
            user: 'Úsáideoir',
            case: 'Cás',
            incident: 'teagmhas',
            assessment: 'Measúnú',
            vendor: 'Díoltóir',
            consent: 'Toiliú',
            policy: 'Beartas',
            data_map: 'Léarscáil Sonraí',
        },
        actions: {
            create: 'Cruthaigh',
            update: 'Nuashonrú',
            delete: 'Scrios',
            view: 'Amharc',
            export: 'Easpórtáil',
            status_change: 'Athrú Stádais',
        },
    },
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Painéal foirmeacha',
            total_templates: 'Teimpléid Iomlána',
            installed: 'Suiteáilte',
            available: 'Ar fáil',
            by_market: 'By Margadh',
            back_dashboard: 'Ar ais to Painéal',
            browse_templates: 'Browse Teimpléid',
            catalog_coverage: 'Catalog Clúdach',
            quick_start: 'Quick Tosaigh',
            recent_instances: 'Le déanaí Instances',
            total_instances: 'Instances Iomlána',
            fetch_failed: 'Theip ar fetch painéal sonraí. Bain triail eile as.',
            no_instances: 'Níl aon instances found. aimsithe',
            form_metrics: 'Forbhreathnú ar mhéadrachta foirmeacha',
            templates_total: 'teimpléid san iomlán',
            instances_total: 'cásanna san iomlán',
            installed_of_available: '{installed} de {available} suiteáilte',
            templates_in_category: 'teimpléid i {category}',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Teimpléid foirmeacha',
            new: 'Nua Teimpléad',
            edit: 'Cuir in eagar Teimpléad',
            name: 'Teimpléad Ainm',
            form_type: 'Foirm Cineál',
            market: 'Margadh',
            language: 'Teanga',
            schema: 'Foirm Schema',
            preview: 'Réamhamharc',
            duplicate: 'Duplicate Teimpléad',
            grid_view: 'Grid Féach',
            list_view: 'List Féach',
            fetch_failed: 'Theip ar fetch teimpléid. Bain triail eile as.',
            filter_templates: 'Scag teimpléid',
            version_history: 'Stair na leaganacha',
            no_version_history: 'Gan stair leaganacha — is é seo an chéad leagan.',
            form_id: 'ID foirme',
            source_instrument: 'Ionstraim foinse',
            issuer: 'Eisitheoir',
            all_frameworks: 'Gach creat',
            view_mode: 'Mód amhairc',
            classification: 'Aicmiú',
        },
        catalog: {
            title: 'Catalóg foirmeacha',
            install: 'Suiteáil',
            uninstall: 'Dísuiteáil',
            open_form: 'Open form',
            install_all: 'Suiteáil All',
            installed_tab: 'Suiteáilte',
            catalog_tab: 'Catalóg',
            search_placeholder: 'Cuardaigh teimpléid...',
            no_templates: 'Níl aon teimpléid found. aimsithe',
            template_count: '{count} teimpléid',
            all_markets: 'Gach Margadh',
            sections: 'Ailt',
            all_categories: 'Gach Catagóir',
            category: 'Catagóir',
            category_install_failed: 'Theip ar install catagóir teimpléid. Bain triail eile as.',
            category_install_success: 'Catagóir teimpléid installed.',
            fetch_failed: 'Theip ar fetch teimpléid. Bain triail eile as.',
            fetch_installed_failed: 'Theip ar fetch installed teimpléid. Bain triail eile as.',
            fields: 'Réimsí',
            install_failed: 'Theip ar install teimpléad. Bain triail eile as.',
            install_success: 'Teimpléad installed successfully.',
            installing: 'Ag suiteáil...',
            removing: 'Ag baint...',
            uninstall_confirm: 'An bhfuil tú cinnte gur mian leat uninstall this template?',
            uninstall_failed: 'Theip ar uninstall teimpléad. Bain triail eile as.',
            uninstall_success: 'Teimpléad uninstalled successfully.',
            installation_summary: 'Achoimre suiteála',
            filter_catalog: 'Scag catalóg',
            all_frameworks: 'Gach creat',
            templates_installed_count: '{count} teimpléad(í) suiteáilte go rathúil',
            all_already_installed: 'Tá gach teimpléad sa chatagóir seo suiteáilte cheana',
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'ID foirme',
            issuer: 'Eisitheoir',
            catalog_tabs: 'Cluaisíní catalóg foirmeacha',
        },
        filler: {
            back_templates: 'Ar ais to Teimpléid',
            no_sections: 'Níl aon sections found. aimsithe',
            draft_created: 'Dréacht created.',
            draft_saved: 'Dréacht saved.',
            fetch_template_failed: 'Theip ar fetch teimpléad. Bain triail eile as.',
            save_draft: 'Sábháil Dréacht',
            save_draft_failed: 'Theip ar save draft. Bain triail eile as.',
            saving: 'Ag sábháil...',
            select_option: 'Roghnaigh an option',
            submit: 'Cuir isteach',
            submit_failed: 'Theip ar submit. Bain triail eile as.',
            submitting: 'Ag cur isteach...',
            title: 'Draoi Foirme',
            form_id_label: 'ID foirme',
            schema_label: 'Scéimre',
            form_number_label: 'Form',
            framework_label: 'Creat',
            issuer_label: 'Eisitheoir',
            source_label: 'Foinse',
            market_label: 'Margadh',
            section_of: 'Roinn {current} as {total}',
            form_navigation: 'Nascleanúint foirme',
            form_progress: 'Dul chun cinn foirme: Roinn {current} as {total}',
            removed_item: 'Mír {index} bainte as {field}',
            added_item: 'Mír nua curtha le {field}',
            go_to_previous: 'Téigh go dtí an roinn roimhe seo, roinn {current} as {total}',
            go_to_next: 'Téigh go dtí an chéad roinn eile, roinn {current} as {total}',
            risk_matrix_placeholder: '1–5',
        },
        filing: {
            title: 'Stádas comhdaithe',
            advance: 'Ardaigh stádas comhdaithe...',
            not_started: 'Gan tosú',
            drafting: 'Ag dréachtú',
            internal_review: 'Athbhreithniú inmheánach',
            approved_for_submission: 'Ceadaithe le haghaidh aighneachta',
            submitted_to_authority: 'Curtha faoi bhráid an údaráis',
            supplement_requested: 'Forlíonadh iarrtha',
            supplement_in_progress: 'Forlíonadh ar siúl',
            resubmitted: 'Athchurtha',
            accepted: 'Glactha',
            rejected: 'Diúltaithe',
            closed: 'Dúnta',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Ceadaithe Ag',
            form_answers: 'Foirm Answers',
            column_header: 'Form',
            no_answers: 'Níl aon answers found. aimsithe',
            view_outputs: 'Féach ar Aschuir',
            approve: 'Ceadaigh',
            approve_failed: 'Theip ar approve. Bain triail eile as.',
            approving: 'Ag ceadú...',
            detail_title: 'Instance Sonra',
            fetch_failed: 'Theip ar fetch instances. Bain triail eile as.',
            generate_failed: 'Theip ar generate output. Bain triail eile as.',
            generate_output: 'Gin Aschur',
            generating: 'Ag giniúint...',
            submitted_at: 'Curtha isteach',
            instance_metadata: 'Meiteashonraí cás foirme',
            filing_status_steps: 'Céimeanna stádais chomhdaithe',
            current_status: 'stádas reatha',
            filing_status_update_failed: 'Theip ar stádas an chomhdaithe a nuashonrú',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Load the dossier again',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Ar ais to Instance',
            no_outputs_msg: 'Níl aon aschuir ginte fós.',
            no_outputs_title: 'No Aschuir',
            output_name: 'Aschur Ainm',
            download: 'Íoslódáil',
            download_failed: 'Theip ar download. Bain triail eile as.',
            fetch_failed: 'Theip ar fetch outputs. Bain triail eile as.',
            format: 'Formáid',
            generated_at: 'Ginte ag',
            generated_by: 'Ginte ag',
            size: 'Méid',
            title: 'Foirm Aschuir',
            print: 'Priontáil',
            print_outputs: 'Priontáil aschuir foirme',
            printing: 'Ag priontáil aschuir foirme',
            pdf_preview: 'Réamhamharc PDF',
            downloading: 'Ag íoslódáil {name}',
            preview_in_new_window: 'Réamhamharc ar {name} ({format}) i bhfuinneog nua',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        cybersec_domains: {
            scope: 'Raon feidhme agus infheidhmeacht',
            risk: 'Measúnú riosca agus mapáil rialuithe',
            policy: 'Polasaithe slándála eagraíochtúla',
            localization: 'Logánú sonraí agus láithreacht áitiúil',
            vendor: 'Tríú páirtithe agus slabhra soláthair',
            authority: 'Láimhseáil iarrataí údaráis',
            incident: 'Freagairt ar theagmhais agus fianaise',
            training: 'Oiliúint agus ullmhacht',
        },
        market_finalization_domains: {
            data_law: 'Dlí Sonraí — Rialachas agus Rialuithe Oibríochtúla',
            pdpl_2025: 'PDPL 2025 — Rialachas Príobháideachais agus Cearta Ábhar Sonraí',
            decree_356: 'Foraithne 356/2025 — Rialuithe Soláthraí agus Trasteorann',
            telecom: 'Dlí Teileachumarsáide — Rúndacht agus Iarratais Údarás',
        },
        nav: {
            filing_case_links: 'Naisc Chás Comhdaithe',
            section_routing: 'Ródú Rannóige',
            submission_packages: 'Pacáistí Aighneachta',
        },
        filing_case_links: {
            title: 'Naisc Chás Comhdaithe',
            link_form: 'Nasc foirm le cás comhdaithe',
            filing_case_type: 'Cineál cás comhdaithe',
            form_instance: 'Ásc foirme',
            select_form_instance: 'Roghnaigh ásc foirme...',
            select_filing_case: 'Roghnaigh cás comhdaithe...',
            no_links_found: 'Níor aimsíodh aon naisc chás comhdaithe.',
            linked: 'Nasctha',
            filing_case_id: 'ID cáis chomhdaithe',
            link_created: 'Nasc cáis chomhdaithe cruthaithe',
            link_deleted: 'Nasc cáis chomhdaithe scriosta',
            aria_delete_link: 'Scrios nasc {name}',
            case_type_dpia_dossier: 'Comhad MTTSP',
            case_type_cross_border_filing: 'Comhdú trasteorann',
            case_type_breach_notification: 'Fógra sáraithe',
            no_instances_guidance: 'Níl aon chásanna foirme fós. Cruthaigh ceann ó Theimpléid Foirme.',
            no_filings_guidance: 'Níl aon chásanna comhdaithe fós. Cruthaigh ceann ó Mheasúnachtaí.',
        },
        section_routing: {
            title: 'Ródú Rannóige',
            assign_section: 'Sann rannóg',
            department: 'Roinn',
            select_department: 'Roghnaigh roinn...',
            assignee: 'Sannta do',
            select_assignee: 'Roghnaigh sannta...',
            section: 'Rannóg',
            select_section: 'Roghnaigh rannóg...',
            select_form_instance: 'Roghnaigh ásc foirme...',
            no_assignments_found: 'Níor aimsíodh aon sannadh rannóige.',
            select_instance_prompt: 'Roghnaigh ásc foirme chun sannadh rannóige a fheiceáil.',
            complete_section: 'Críochnaigh rannóg',
            status_pending: 'Ar feitheamh',
            status_in_progress: 'Ar siúl',
            status_completed: 'Críochnaithe',
            status_reviewed: 'Athbhreithnithe',
            form_instance_id: 'ID cás foirme',
            assignment_created: 'Sannadh roinne cruthaithe',
            assignment_deleted: 'Sannadh rannóige scriosta',
            status_updated: 'Stádas roinne nuashonraithe',
            aria_delete_assignment: 'Scrios sannadh {name}',
            deadline: 'Spriocdháta',
            deadline_help: 'Roghnach. Taispeántar sannacháin tar éis an dáta seo mar dheireanacha.',
            overdue: 'Déanach',
            days_remaining: 'lá fágtha',
            approval_requested: 'Curtha isteach le haghaidh athbhreithnithe',
            form_approved: 'Foirm ceadaithe',
            form_rejected: 'Foirm diúltaithe',
            no_departments_guidance: 'Níl aon ranna cumraithe. Socraigh iad i Socruithe na hEagraíochta.',
            no_sections_guidance: 'Níl aon rannáin sainmhínithe sa teimpléad. Cinntigh go bhfuil rannáin ag an teimpléad foirme.',
            select_instance_first: 'Roghnaigh cás foirme ar dtús chun na rannáin atá ar fáil a lódáil.',
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Pacáistí Aighneachta',
            assemble_package: 'Tionóil pacáiste aighneachta',
            dossier: 'Comhad',
            select_dossier: 'Roghnaigh comhad...',
            package_label: 'Lipéad pacáiste',
            cover_letter: 'Litir chlúdaigh',
            checklist: 'Seicliosta',
            evidence_index: 'Innéacs fianaise',
            completeness: 'Iomláine',
            incomplete_warning: 'Ní féidir tionól: tá rannóga neamhchríochnaithe san fhoirm.',
            no_packages_found: 'Níor aimsíodh aon phacáistí aighneachta.',
            version: 'Leagan',
            package_assembled: 'Pacáiste aighneachta curtha le chéile',
            package_deleted: 'Pacáiste aighneachta scriosta',
            aria_delete_package: 'Scrios pacáiste {name}',
            status_draft: 'Dréacht',
            status_complete: 'Críochnaithe',
            status_submitted: 'Curtha isteach',
            status_accepted: 'Glactha',
            status_rejected: 'Diúltaithe',
            package_updated: 'Pacáiste aighneachta nuashonraithe',
            document_generated: 'Doiciméad ginte',
            documents: 'Doiciméid',
            generate: 'Gin',
            preview: 'Réamhamharc',
            download: 'Íoslódáil',
            download_all: 'Íoslódáil gach ceann',
            no_dossiers_guidance: 'Níl aon dossier fós. Cruthaigh ceann ó Mheasúnachtaí.',
            package_plan_help: 'Cuir san áireamh sa phlean pacáiste (giniúint uathoibríoch doiciméad ar fáil):',
            cover_letter_help: 'Litir chumhdaigh oifigiúil dírithe chuig an údarás glactha',
            checklist_help: 'Seicliosta comhlíonta a dheimhníonn go gcomhlíontar gach riachtanas aighneachta',
            evidence_index_help: 'Innéacs de dhoiciméid fianaise tacaíochta a luaitear san aighneacht',
            completeness_skipped: 'Seiceáil iomláine tréigthe — níl aon chás foirme nasctha leis an dossier seo.',
            completeness_unavailable: 'Seiceáil iomláine nach bhfuil ar fáil — ag leanúint le cóimeáil an phacáiste.',
            duplicate_package_error: 'Tá pacáiste aighneachta ann cheana féin don dossier seo.',
            doc_cover_letter: 'Litir chumhdaigh',
            doc_checklist: 'Seicliosta',
            doc_evidence_index: 'Innéacs fianaise',
            export_csv: 'Easpórtáil CSV',
            export_success: 'Naisc cásanna comhdaithe easpórtáilte go rathúil',
            no_assignments_guidance: 'Níl aon sannacháin rannóige fós. Sann rannóga do ranna agus do bhaill foirne chun tosú ar an ródú.',
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'Roghnaigh teimpléad',
            hint: 'Roghnaigh teimpléad foirme rialála nó tosaigh le foirm bhán.',
            market_filter: 'Scag de réir margaidh',
            framework_filter: 'Scag de réir creatlaí',
            all_markets: 'Gach margadh',
            all_frameworks: 'Gach creat',
            search_placeholder: 'Cuardaigh teimpléid...',
            blank_form: 'Foirm bhán / ghinearálta',
            blank_form_desc: 'Cruthaigh gan teimpléad — líon isteach réimsí de láimh.',
            no_templates: 'Níor aimsíodh teimpléid don scagaire seo.',
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Roghnaigh',
            link_error: 'Theip ar nascadh an teimpléid — is féidir leat teimpléad a nascadh níos déanaí ón Radharc Eagarthóireachta.',
            internal_badge: 'Teimpléad inmheánach',
            legacy_badge: 'As feidhm',
            draft_badge: 'Dréacht',
            upcoming_badge: 'Ag teacht',
            hidden_badge: 'Folaithe',
            show_hidden_toggle: 'Taispeáin teimpléid fholaithe / as feidhm',
            show_hidden_toggle_hint: 'Radharc riaracháin: áirítear teimpléid atá marcáilte internal_only, hidden, legacy nó draft.',
        },
        cover_letter_modal: {
            title: 'Litir chlúdaigh: réamhamharc agus eagarthóireacht',
            iframe_title: 'Réamhamharc na litreach clúdaigh',
            stage_label: 'Céim',
            stage_initial: 'Aighneacht tosaigh',
            stage_supplement: 'Freagra forlíontach',
            stage_resubmission: 'Athaighneacht',
            subject_label: 'Ábhar',
            body_label: 'Príomh-mhír',
            notes_label: 'Nótaí breise',
            refresh_preview: 'Athnuaigh réamhamharc',
            confirm: 'Deimhnigh agus sábháil',
            regenerate: 'Athghin',
            snapshot_saved: 'Litir chlúdaigh sábháilte',
        },
        related_panel: {
            title: 'Foirmeacha rialála gaolmhara',
            open_form: 'Líon foirm',
            no_forms: 'Níor aimsíodh aon fhoirmeacha gaolmhara.',
            hint_vn: 'Foirmeacha rialála VN-sonracha ar fáil — roghnaigh foirm chun an cúntóir comhlíonta a oscailt.',
            internal_badge: 'Inmheánach',
            official_badge: 'Oifigiúil',
            loading: 'Foirmeacha gaolmhara á lódáil...',
            error: 'Theip ar lódáil na bhfoirmeacha gaolmhara.',
            form_id: 'Aitheantas Foirme',
            form_name: 'Ainm na Foirme',
            framework: 'Creat Rialála',
        },
        view_form: 'Féach ar an bhfoirm',
        edit_form: 'Cuir an fhoirm in eagar',
        save_draft: 'Sábháil dréacht',
        module_settings: 'Socruithe modúil',
        validation_errors: 'Ceartaigh na hearráidí bailíochtaithe le do thoil',
        custom_name_help: 'Roghnach — ainm an teimpléid de réir réamhshocraithe más rud é go bhfágtar folamh',
        select_placeholder: 'Roghnaigh...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Roimhe seo',
        next_section: 'Ar aghaidh',
        loading: 'Á luchtú...',
        array_add: 'Cuir leis',
        array_remove: 'Bain',
        array_item_label: '{label} mír {index}',
        array_add_item: 'Cuir mír le {label}',
        array_remove_item: 'Bain mír {index} as {label}',
        legacy_banner: 'Cruthaíodh an taifead seo roimh na foirmeacha aontaithe. D’fhéadfadh roinnt sonraí a bheith éagsúil idir an t-aonán agus an t-ásc foirme.',
        field_required: 'Tá {label} riachtanach',
        section_announcement: 'Roinn {current} de {total}: {title}',
        sections_nav_label: 'Foirm ailt',
        no_sections: 'Níl aon rannóg teimpléid ar fáil.',
        section_label_tpl: 'Roinn {index}: {title}',
        field_shown_announcement: 'Taispeántar {field} anois.',
        field_required_announcement: 'Tá {field} riachtanach anois.',
        submit_blocked_hint_one: 'Tá freagra fós de dhíth ar 1 réimse riachtanach.',
        submit_blocked_hint_many: 'Tá freagra fós de dhíth ar {count} réimse riachtanacha.',
        module_settings_auto_applied: 'Cuirtear socruithe modúil ar leith i bhfeidhm go huathoibríoch.',
        operation_failed: 'Theip ar an oibríocht',
        failed_to_load_form: 'Theip ar an bhfoirm a luchtú',
        failed_to_save_form: 'Theip ar an bhfoirm a shábháil',
        failed_to_submit_form: 'Theip ar an bhfoirm a chur isteach',
        failed_to_load_template: 'Theip ar an teimpléad a luchtú',
        failed_to_save_draft: 'Theip ar an dréacht a shábháil',
        failed_to_create: 'Theip ar chruthú',
        no_content_generated: 'No content generated',
    },
    pack_catalog: {
        title: 'Catalóg pacáiste',
        install: 'Suiteáil Pack',
        uninstall: 'Dísuiteáil Pack',
        install_batch: 'Suiteáil Cluster',
        installed_tab: 'Suiteáilte',
        catalog_tab: 'Catalóg',
        pack_details: 'Pack Sonraí',
        frameworks: 'Creataí',
        requirements: 'Riachtanais',
        artifacts: 'Déantáin',
        language_pack: 'Teanga Pack',
        dependencies: 'Spleáchais',
        no_packs: 'Níl aon packs found. aimsithe',
    },
    platform_pages: {
        users: {
            title: 'Úsáideoirí',
            new: 'Nua Úsáideoir',
            invite: 'Invite Úsáideoir',
            edit: 'Cuir in eagar Úsáideoir',
            name: 'Ainm',
            email: 'Ríomhphost',
            role: 'Ról',
            status: 'Stádas',
            identity_type: 'Identity Cineál',
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. While a password change is required, the user can do nothing but choose a new password.',
            last_login: 'Logáil Isteach Deireanach',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Díghníomhachtaigh',
            reactivate: 'Athghníomhachtaigh',
            unlock: 'Díghlasáil',
            reset_password: 'Athshocraigh Pasfhocal',
            remove: 'Bain',
            remove_confirm: '"{name}" a bhaint? Tá an gníomh seo buan agus caillfidh an t-úsáideoir gach rochtain. Coinnítear an taifead chun críocha iniúchóireachta.',
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Róil',
            new: 'Nua Ról',
            edit: 'Cuir in eagar Ról',
            name: 'Ról Ainm',
            description: 'Cur síos',
            permissions: 'Ceadanna',
            users_count: 'Úsáideoirí',
            detail: 'Ról Sonra',
            all_tiers: 'Gach leibhéal',
            tier: 'Leibhéal',
            type_system: 'Córas',
            type_custom: 'Saincheaptha',
            role_name_placeholder: 'm.sh. ceannaire comhlíonta réigiúnach',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'My Próifíl',
            name: 'Ainm',
            display_name: 'Ainm taispeána',
            email: 'Ríomhphost',
            phone: 'Fón',
            department: 'Roinn',
            job_title: 'Teideal poist',
            language: 'Teanga',
            role: 'Ról',
            persona: 'Pearsana aitheanta',
            last_login: 'Logáil Isteach Deireanach',
            save: 'Sábháil athruithe',
            save_success: 'Próifíl nuashonraithe go rathúil',
            module_tips_section: 'Leideanna modúil',
            tips_toggle: 'Taispeáin leideanna rialála i modúil',
            dismissed_tips: 'Leideanna díbeartha',
            reset_dismissed: 'Athshocraigh gach leid díbeartha',
            notification_prefs: 'Roghanna fógraí',
            notification_prefs_link: 'Bainistigh roghanna fógraí →',
            change_password: 'Change Pasfhocal',
            current_password: 'Reatha Pasfhocal',
            new_password: 'Nua Pasfhocal',
            confirm_password: 'Deimhnigh pasfhocal',
            accessibility: 'Inrochtaineacht',
            high_contrast: 'Mod ardchodarsnachta',
            high_contrast_desc: 'Meaddigh an chodarsnacht le haghaidh inleiteachta nios fearr. Comhlionann WCAG 2.1 AAA.',
            passwords_no_match: 'Ní mheaitseálann na pasfhocail',
            password_changed_logout: 'Pasfhocal athraithe. Logálfar amach thú.',
            failed_update: 'Theip ar nuashonrú próifíle',
            failed_change_password: 'Theip ar athrú pasfhocail',
            none: 'Dada',
            never: 'Riamh',
            password_requirements: 'Íosméid 12 carachtar, ceannlitir, litir bheag, digit, carachtar speisialta',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'MFA Socruithe',
            mfa_enable: 'Cumasaigh MFA',
            mfa_disable: 'Díchumasaigh MFA',
            mfa_setup: 'Scanadh this QR cód with your authenticator app',
            sso_title: 'SSO Cumraíocht',
            sso_provider: 'Soláthraí',
            sso_client_id: 'ID Cliaint',
            sso_redirect: 'URI Atreoraithe',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'API Eochair',
            api_keys_new: 'Nua API Eochair',
            api_keys_name: 'Eochair Ainm',
            api_keys_last_used: 'Úsáidte go Deireanach',
            api_keys_expires: 'Éagann',
            api_keys_revoke: 'Cúlghairm',
            service_accounts_title: 'Seirbhís Cuntas',
            ip_allowlist_title: 'Liosta Ceadaithe IP',
            ip_add: 'Cuir IP Seoladh leis',
            ip_address: 'IP Seoladh',
            ip_description: 'Cur síos',
            delegations_title: 'Toscaireachtaí',
            delegations_new: 'Nua Delegation',
            delegations_from: 'From Úsáideoir',
            delegations_to: 'To Úsáideoir',
            delegations_expires: 'Éagann',
            break_glass_title: 'Break-Glass Rochtain',
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Iarratas Rochtain',
            break_glass_reason: 'Reason for Rochtain',
            break_glass_duration: 'Fad',
            session_settings_title: 'Seisiún Socruithe',
            eviction_policy_section: 'Beartas Díbeartha',
            session_timeout: 'Seisiún Timeout (minutes)',
            session_max_concurrent: 'Max Concurrent Seisiúin',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Seisiúin Ghníomhacha',
            active_sessions_device: 'Gléas',
            active_sessions_ip: 'IP Seoladh',
            active_sessions_started: 'Tosaithe',
            active_sessions_revoke: 'Cúlghairm Seisiún',
            active_sessions_confirm_terminate: 'An bhfuil tú cinnte gur mhaith leat deireadh a chur leis an seisiún seo?',
            active_sessions_confirm_terminate_current: 'Seo é do sheisiún faoi láthair. Má chuirtear deireadh leis, síneoidh tú amach láithreach. Lean ort?',
            active_sessions_confirm_terminate_all: 'An bhfuil tú cinnte gur mhaith leat deireadh a chur le gach seisiún eile? Ní bheidh ach do sheisiún reatha fós gníomhach.',
            active_sessions_terminated: 'Cuireadh deireadh leis an seisiún go rathúil.',
            active_sessions_all_terminated: 'Cuireadh deireadh le gach seisiún eile.',
            active_sessions_terminate_all: 'Deireadh a chur le Gach Seisiún Eile',
            active_sessions_last_activity: 'Gníomhaíocht Deiridh',
            active_sessions_current: 'Seisiún Reatha',
            active_sessions_revoke_selected: 'Cealaigh na cinn roghnaithe',
            active_sessions_confirm_terminate_selected: 'An bhfuil tú cinnte gur mhaith leat na seisiúin roghnaithe a chríochnú?',
            active_sessions_selected_terminated: 'Críochnaíodh na seisiúin roghnaithe go rathúil.',
            ip_allowlist_desc: 'Bainistigh seoltaí IP ceadaithe le haghaidh rochtana API.',
            ip_no_entries: 'Níl aon seoltaí IP cumraithe.',
            session_settings_desc: 'Cumraigh teorainn ama seisiúin agus teorainneacha seisiúin chomhthráthacha.',
            session_settings_save_policy: 'Sábháil Polasaí Seisiúin',
            session_settings_saved: "D'éirigh le socruithe an tseisiúin a nuashonrú.",
            session_settings_idle_timeout: 'Am istigh Díomhaoin (nóiméid)',
            session_settings_absolute_timeout: 'Teorainn Ama Iomlán (nóiméid)',
            session_settings_help_idle: 'Rachaidh an seisiún in éag tar éis na tréimhse neamhghníomhaíochta seo.',
            session_settings_help_absolute: 'Rachaidh an seisiún in éag tar éis na tréimhse iomlán seo beag beann ar ghníomhaíocht.',
            session_settings_help_max: 'Uaslíon seisiún comhuaineach in aghaidh an úsáideora.',
            sso_note: "D'fhéadfadh go n-éileoidh athruithe ar chumraíocht SSO ar úsáideoirí athfhíordheimhniú a dhéanamh.",
            service_accounts_warning: 'Ní thaispeánfar an eochair API seo ach uair amháin. Cóipeáil anois é agus é a stóráil go slán.',
            service_accounts_new: 'Cuntas Seirbhíse Nua',
            service_accounts_name: 'Ainm an Chuntais',
            service_accounts_role: 'Ról',
            service_accounts_created: 'Cruthaithe',
            service_accounts_desc: 'Bainistigh cuntais seirbhíse meaisín-go-meaisín le haghaidh API comhtháthú.',
            break_glass_warning: 'Déantar rochtain ar ghloine briste a iniúchadh agus a logáil. Bain úsáid as ach amháin le haghaidh éigeandálaí fíor.',
            break_glass_min_chars: '{count} / {min} carachtair íosta',
            break_glass_permission_count: '{count} cead(anna) roghnaithe',
            break_glass_submitted: 'Iarratas gloine briste curtha isteach. Ag feitheamh le ceadú.',
            break_glass_approved: 'Iarratas briseadh gloine ceadaithe.',
            break_glass_denied: 'Diúltaíodh don iarratas gloine briste.',
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: 'Deimhnigh Gníomhachtaithe',
            break_glass_pending: 'Iarratais ar Feitheamh',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Am fágtha',
            break_glass_expired: 'imithe in éag',
            break_glass_approved_denied_by: 'Formheasta/Diúltaithe ag',
            delegations_desc: 'Tarmligean cead sealadach idir úsáideoirí a bhainistiú.',
            delegations_my: 'Mo Thoscaireachtaí',
            delegations_to_me: 'Tarmligthe chugam',
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'Gach Cead',
            delegations_scope_specific: 'Ceadanna Sonracha',
            delegations_created: "D'éirigh leis an toscaireacht a chruthú.",
            delegations_revoked: 'Cúlghair an toscaireacht.',
            delegations_confirm_revoke: 'An bhfuil tú cinnte gur mhaith leat an tarmligean seo a chúlghairm?',
            delegations_confirm_revoke_title: 'Deimhnigh Cúlghairm',
            delegations_permission_count: '{count} cead(anna)',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Stádas',
            api_keys_prefix: 'Eochair Réimír',
            api_keys_permissions_count: 'Ceadanna',
            api_keys_ip_count: 'Srianta IP',
            api_keys_created: 'Cruthaithe',
            api_keys_confirm_revoke_title: 'Deimhnigh Cúlghairm',
            api_keys_confirm_revoke_message: 'An bhfuil tú cinnte gur mhaith leat an eochair API "{name}" a chúlghairm? Ní féidir an gníomh seo a chealú.',
            api_keys_confirm_rotate_title: 'Deimhnigh Rothlú',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: 'API eochair cruthaithe.',
            api_keys_revoked_success: 'API eochair "{name}" cúlghairthe.',
            api_keys_rotated_success: 'Tá API eochair "{name}" rothlaithe. Cóipeáil an eochair nua thíos.',
            api_keys_load_error: 'Theip ar API eochair a lódáil.',
            api_keys_create_error: 'Theip ar API eochair a chruthú.',
            api_keys_revoke_error: 'Theip ar API eochair a chúlghairm.',
            api_keys_rotate_error: 'Theip ar an eochair API a rothlú.',
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 nóiméad',
            break_glass_duration_30: '30 nóiméad',
            break_glass_duration_60: '60 nóiméad (1h)',
            break_glass_duration_120: '120 nóiméad (2h)',
            break_glass_duration_240: '240 nóiméad (4h)',
            sod_rules_title: 'Na Rialacha um Leithscaradh Dleacht',
            sod_violations_report: 'Tuarascáil Sáruithe',
            sod_create_rule: 'Cruthaigh Riail',
            sod_col_name: 'Ainm',
            sod_col_description: 'Cur síos',
            sod_col_conflict_count: 'Comhaireamh Coimhlinte',
            sod_col_severity: 'Déine',
            sod_col_status: 'Stádas',
            sod_col_actions: 'Gníomhartha',
            sod_no_rules: 'Níor aimsíodh aon rialacha SoD',
            sod_severity_warning: 'Rabhadh',
            sod_severity_blocking: 'Ag bacadh',
            sod_active: 'Gníomhach',
            sod_inactive: 'Neamhghníomhach',
            sod_edit: 'Cuir in eagar',
            sod_exceptions: 'Eisceachtaí',
            sod_deactivate: 'Díghníomhachtaigh',
            sod_confirm_deactivate_title: 'Deimhnigh Díghníomhachtú',
            sod_confirm_deactivate_message: 'An riail "{name}" a dhíghníomhachtú? Ní chuirfidh sé sáruithe in iúl a thuilleadh.',
            sod_deactivate_error: 'Theip ar an riail a dhíghníomhachtú',
            sod_rule_count: '{count} rules',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: EN placeholders pending Phase 25 (R-57) ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: 'Rochtain Athbhreithnithe',
            campaigns: 'Athbhreithniú Campaigns',
            new_campaign: 'Nua Campaign',
            campaign_name: 'Campaign Ainm',
            scope: 'Scóip',
            reviewers: 'Athbhreithneoir',
            due_date: 'Due Dáta',
            progress: 'Dul chun cinn',
            my_reviews: 'My Athbhreithnithe',
            approve: 'Ceadaigh',
            revoke: 'Cúlghairm',
            analytics: 'Ról Anailísíocht',
            analytics_title: 'Ról Anailísíocht',
            permission_usage: 'Cead Usage',
            role_coverage: 'Ról Clúdach',
            campaign_detail: 'Sonraí Feachtais',
            campaign_description: 'Cur síos',
            campaign_status: 'Stádas',
            campaign_deadline: 'Spriocdháta',
            campaign_scope_type: 'Cineál Scóip',
            campaign_completion: 'Críochnú',
            campaign_total_items: 'Míreanna Iomlána',
            campaign_pending_items: 'Ar feitheamh',
            campaign_confirmed_items: 'Deimhnithe',
            campaign_revoked_items: 'Cúlghairthe',
            campaign_escalated_items: 'Ardaithe',
            campaign_launch: 'Seoladh Feachtas',
            campaign_cancel: 'Cealaigh Feachtas',
            campaign_confirm_launch: 'An bhfuil tú cinnte gur mhaith leat an feachtas seo a sheoladh?',
            campaign_confirm_cancel: 'An bhfuil tú cinnte gur mhaith leat an feachtas seo a chur ar ceal?',
            campaign_launched: 'Seoladh feachtas go rathúil.',
            campaign_cancelled: 'Feachtas curtha ar ceal.',
            review_items: 'Míreanna Athbhreithnithe',
            review_item_user: 'Úsáideoir',
            review_item_role: 'Ról',
            review_item_reviewer: 'Athbhreithneoir',
            review_item_decision: 'Cinneadh',
            review_item_justify: 'Fírinniú',
            review_item_confirm: 'Deimhnigh',
            review_item_revoke: 'chúlghairm',
            review_item_escalate: 'Ardaigh',
            all_decisions: 'Gach Cinneadh',
            bulk_action: 'Gníomh Bulc',
            bulk_confirm: 'Bulc Deimhnigh',
            bulk_revoke: 'Bulc cúlghairm',
            selected_count: '{count} roghnaithe',
            justify_required: 'Teastaíonn fírinniú',
            scope_all_users: 'Gach Úsáideoir',
            scope_role_owner: 'Úinéir Ról',
            scope_department: 'Roinn',
            back_to_campaigns: 'Ar ais go Feachtais',
            recommendations: 'Moltaí',
            refreshing: 'Á athnuachan...',
            decide: 'Déan cinneadh',
            decision_by: 'Cinneadh Le',
            loading_items: 'Míreanna á lódáil...',
            loading_review_items: 'Míreanna athbhreithnithe á lódáil...',
            campaign_not_found: 'Feachtas gan aimsiú',
            loading_campaign: 'Sonraí feachtais á lódáil...',
            campaign_completed: 'Críochnaíodh an feachtas go rathúil.',
            confirm_cancel_title: 'Deimhnigh Cealaigh',
            justify_bulk_confirm: 'Fírinniú le mórdheimhniú:',
            justify_bulk_revoke: 'Fírinniú le bulc-chúlghairm:',
            items_selected: '{count} mhír(eanna) roghnaithe',
            justify_bulk_required: 'Teastaíonn fírinniú le haghaidh bulcghníomhartha',
            create_campaign_title: 'Cruthaigh Feachtas Athbhreithnithe Rochtana',
            name_required: 'Tá ainm ag teastáil',
            scope_type_required: 'Tá gá le cineál scóip',
            reviewer_strategy_required: 'Tá straitéis athbhreithneora ag teastáil',
            deadline_required: 'Tá spriocdháta ag teastáil',
            reviewer_id_required: 'Tá ID an Athbhreithneora ag teastáil le haghaidh straitéis úsáideora ar leith',
            invalid_json: 'JSON neamhbhailí',
            scope_filter_label: 'Scagaire Scóipe (JSON)',
            reviewer_strategy_label: 'Straitéis Athbhreithneora',
            reviewer_id_label: 'Aitheantas an Athbhreithneora',
            deadline_label: 'Spriocdháta',
            create_campaign_btn: 'Cruthaigh Feachtas',
            scope_type_label: 'Cineál Scóip',
            scope_by_role: 'De Ról',
            scope_by_department: 'De réir Roinn',
            scope_custom: 'Saincheaptha',
            reviewer_manager: 'Bainisteoir',
            reviewer_specific_user: 'Úsáideoir Sonrach',
            enter_json_filter: 'Cuir isteach JSON scagaire',
            hint_label: 'Leid:',
            role_usage: 'Úsáid Róil',
            over_privileged_users: 'Úsáideoirí Ró-phribhléid',
            role_name_col: 'Ainm an Róil',
            assigned_users: 'Úsáideoirí Sannta',
            active_users_30d: 'Úsáideoirí Gníomhacha (30d)',
            total_perms: 'Iomlán Perms',
            used_perms: 'Perms Úsáidte',
            unused_perms: 'Ceadanna Neamhúsáidte',
            no_data_available: 'Níl aon sonraí ar fáil',
            roles_col: 'Róil',
            total_col: 'Iomlán',
            used_col: 'Úsáidte',
            unused_pct: '% gan úsáid',
            current_count: 'Comhaireamh Reatha',
            recommended_count: 'Comhaireamh Molta',
            rationale: 'Réasúnaíocht',
            loading_analytics: 'Anailísíocht á lódáil...',
            loading_role_analytics: 'Sonraí anailísíochta ról á lódáil...',
            computed_at: 'Arna ríomh ag:',
            form_errors: 'Tá {count} earráid(í) san fhoirm. Ceartaigh iad le do thoil.',
            scope_filter_invalid_json: 'Tá JSON neamhbhailí ar an scagaire scóipe',
            scope_hint_all_users: 'Níl gá le scagaire -- cuirfear gach úsáideoir san áireamh.',
            review_type_label: 'Cineál Athbhreithnithe',
            review_type_required: 'Tá cineál athbhreithnithe ag teastáil',
            reviewer_ids_label: 'IDAanna Athbhreithneora',
            reviewer_ids_hint: 'Cuir isteach IDAanna úsáideoirí scartha le camóga',
            reviewer_ids_required: 'Tá ar a laghad ID athbhreithneora amháin ag teastáil',
            start_date_label: 'Dáta Tosaigh',
            start_date_required: 'Tá dáta tosaigh ag teastáil',
            end_date_label: 'Dáta Deiridh',
            end_date_required: 'Tá dáta deiridh ag teastáil',
            review_type_user_access: 'Rochtain Úsáideora',
            review_type_role_permissions: 'Ceadanna Ról',
            review_type_service_accounts: 'Cuntais Seirbhíse',
            review_type_privileged_access: 'Rochtain Phribhléideach',
            review_type_application_access: 'Rochtain Feidhlchláir',
            auto_revoke_label: 'Uath-chealú ar Dhiúltú',
            campaigns_title: 'Feachtais Athbhreithnithe Rochtana',
            create_campaign_label: '+ Cruthaigh Feachtas',
            no_campaigns_found: 'Níor aimsíodh feachtais ar bith',
            loading_campaigns: 'Feachtais á lódáil...',
            no_reviews_assigned: 'Níl aon léirmheasanna sannta duit',
            loading_reviews: 'Léirmheasanna á lódáil...',
            review_decision_title: 'Cinneadh Athbhreithnithe',
            justification_label: 'Fírinniú',
            announce_loaded_review_items: 'Lódaíodh {count} mhír léirmheasa',
            announce_loaded_campaigns: '{count} feachtas lódáilte',
            announce_decision_submitted: 'Cinneadh "{decision}" arna chur isteach le haghaidh mír athbhreithnithe',
            announce_decision_submitted_for: 'Cinneadh "{decision}" arna chur isteach i gcomhair {name}',
            announce_bulk_decision: 'Cuireadh an mórchóir {decision} i bhfeidhm ar {count} mhíreanna',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: 'Attestation Sceidealacha',
            new: 'Nua Sceideal',
            edit: 'Cuir in eagar Sceideal',
            create_title: 'Cruthaigh sceideal deimhnithe',
            edit_title: 'Cuir in eagar sceideal deimhnithe',
            schedule_history: 'Stair an sceidil',
            next_run_date: 'Dáta an chéad rith eile',
            schedule_name: 'Sceideal Ainm',
            frequency: 'Minicíocht',
            next_run: 'Ar aghaidh Rith',
            last_run: 'Last Rith',
            history: 'Attestation Stair',
            description: 'Cur síos',
            scope: 'Raon feidhme',
            status: 'Stádas',
            created_at: 'Cruthaithe',
            updated_at: 'Nuashonraithe',
            trigger_now: 'Truicear Anois',
            back_to_schedules: 'Ar Ais go Sceidil',
            confirm_trigger: 'Trigger this schedule now?',
            confirm_delete: 'Delete this schedule?',
            triggered: "D'éirigh leis an sceideal a spreagadh.",
            deleted: 'Sceideal scriosta.',
            saved: "D'éirigh leis an sceideal a shábháil.",
            campaigns_generated: 'Feachtais a Gineadh',
            run_date: 'Rith Dáta',
            run_status: 'Stádas Rith',
            create_schedule: 'Cruthaigh Sceideal',
            view_history: 'Stair Amharc',
            deactivate: 'Díghníomhachtaigh',
            overdue: 'OVERDUE',
            overdue_sr: 'Tá an sceideal thar téarma',
            confirm_trigger_title: 'Deimhnigh Gníomh',
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now?',
            confirm_deactivate_title: 'Deimhnigh Díghníomhachtú',
            confirm_deactivate_message: 'An bhfuil tú cinnte gur mhaith leat an sceideal seo a dhíghníomhachtú?',
            triggered_success: "D'éirigh leis an sceideal a spreagadh.",
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Sceideal díghníomhachtaithe.',
            name_label: 'Ainm *',
            frequency_label: 'Minicíocht *',
            scope_type_label: 'Cineál Scóip *',
            scope_filter_label: 'Scagaire Scóipe (JSON)',
            reviewer_strategy_label: 'Straitéis Athbhreithneora *',
            reviewer_id_label: 'Aitheantas an Athbhreithneora *',
            frequency_quarterly: 'Ráithiúil',
            frequency_semi_annual: 'Leathbhliantúil',
            frequency_annual: 'Bliantúil',
            scope_all_users: 'Gach Úsáideoir',
            scope_by_role: 'De Ról',
            scope_by_department: 'De réir Roinn',
            scope_custom: 'Saincheaptha',
            reviewer_manager: 'Bainisteoir',
            reviewer_role_owner: 'Úinéir Ról',
            reviewer_specific_user: 'Úsáideoir Sonrach',
            computed_next_run_date: 'Ríomh Dáta an Chéad Rith Eile',
            current_scheduled_date: 'Dáta sceidealaithe reatha',
            recomputed_frequency_changed: '(ríomh -- athraíodh an mhinicíocht)',
            estimated_days_hint: 'Measta bunaithe ar {days} lá ón lá inniu',
            validation_name_required: 'Tá ainm ag teastáil',
            validation_frequency_required: 'Tá minicíocht ag teastáil',
            validation_scope_type_required: 'Tá gá le cineál scóip',
            validation_reviewer_strategy_required: 'Tá straitéis athbhreithneora ag teastáil',
            validation_reviewer_id_required: 'Tá ID an Athbhreithneora ag teastáil le haghaidh straitéis úsáideora ar leith',
            validation_invalid_json: 'JSON neamhbhailí',
            validation_scope_filter_invalid_json: 'Tá JSON neamhbhailí ar an scagaire scóipe',
            form_errors: 'Tá {count} earráid(í) san fhoirm. Ceartaigh iad le do thoil.',
            create_schedule_btn: 'Cruthaigh Sceideal',
            schedule_history_title: 'Stair Sceidil',
            campaign_name_col: 'Ainm an Fheachtais',
            items_col: 'Míreanna',
            no_campaigns_yet: 'Níor gineadh aon fheachtas fós',
            loading_history: 'Stair á lódáil...',
            loading_schedule: 'Sceideal á lódáil...',
            total_campaigns: 'Iomlán feachtais: {count}',
            results_per_page: 'Torthaí in aghaidh an leathanaigh',
            page_size_option: '{size} / leathanach',
        },
        sod: {
            title: 'Separation of Duties Rialacha',
            new: 'Nua Riail',
            edit: 'Cuir in eagar Riail',
            create_title: 'Cruthaigh riail SoD',
            edit_title: 'Cuir in eagar riail SoD',
            rule_name: 'Riail Ainm',
            conflicting_permissions: 'Conflicting Ceadanna',
            permission_a: 'Cead A',
            permission_b: 'Cead B',
            violations: 'Sáruithe SoD',
            violation_user: 'Úsáideoir',
            violation_rule: 'Riail Violated',
            violation_date: 'Braite',
            exceptions: 'SoD Eisceacht',
            exception_reason: 'Eisceacht Reason',
            exception_approver: 'Ceadaithe Ag',
            exception_status: 'Stádas Eisceacht',
            has_exception: 'Tá Eisceacht',
            no_exception: 'Gan Eisceacht',
            grant_exception: 'Deontas Eisceacht',
            granting: 'Ag tabhairt...',
            grant_new_exception: 'Deonaigh Eisceacht Nua',
            exceptions_for: 'Eisceachtaí maidir le: {name}',
            back_to_rules: 'Ar Ais go Rialacha',
            no_violations: 'Níor aimsíodh aon sáruithe',
            no_exceptions_found: 'Níor aimsíodh aon eisceacht don riail seo',
            all_exception_statuses: 'Gach Stádas Eisceacht',
            user_id_label: 'Aitheantas Úsáideora',
            justification_label: 'Fírinniú',
            compensating_controls_label: 'Rialuithe Cúitimh',
            expires_at_label: 'In éag Ag',
            granting_exception_for: 'Eisceacht a dheonú don riail: {name}',
            severity: 'Déine',
            severity_warning: 'Rabhadh',
            severity_blocking: 'Ag bacadh',
            violation_count: '{count} sárú(í)',
            exception_count: '{count} eisceacht(í)',
            rule_description: 'Cur síos',
            confirm_delete: 'An bhfuil tú cinnte gur mhaith leat an riail seo a scriosadh?',
            deleted: 'Riail scriosta.',
            framework_code: 'Creat rialala',
            name_label: 'Ainm *',
            severity_label: 'Déine *',
            conflict_pairs_legend: 'Péirí Cead Coinbhleachta',
            conflict_pairs_hint: 'Sainmhíníonn gach péire dhá chead nár cheart don úsáideoir céanna a shealbhú.',
            placeholder_permission_a: '-- Cead A --',
            placeholder_permission_b: '-- Cead B --',
            permission_a_for_pair: 'Cead A do phéire {index}',
            permission_b_for_pair: 'Cead B do phéire {index}',
            conflict_pair_label: 'Péire coinbhleachta {index}',
            remove_conflict_pair: 'Bain an péire coinbhleachta {index}',
            add_pair: '+ Cuir Péire leis',
            placeholder_name: 'e.g. Scaradh Ceadaithe Íocaíochta',
            create_rule: 'Cruthaigh Riail',
            loading_rule: 'Riail SoD á lódáil...',
            failed_load_rule: 'Theip ar an riail a lódáil.',
            failed_create_rule: 'Theip ar an riail SoD a chruthú.',
            failed_update_rule: 'Theip ar an riail SoD a nuashonrú.',
            validation_name_required: 'Tá ainm ag teastáil.',
            validation_pair_required: 'Teastaíonn péire cead coinbhleachta amháin ar a laghad.',
            validation_pair_both_required: 'Péire {index}: Ní mór an dá chead a roghnú.',
            validation_pair_must_differ: 'Péire {index}: Caithfidh cead A agus Cead B a bheith difriúil.',
            added_conflict_pair: 'Cuireadh péire coinbhleachta leis {index}',
            removed_conflict_pair: 'Baineadh an péire coinbhleachta {index}',
            announce_loaded_violations: '{count} sáruithe lódáilte',
            announce_loaded_exceptions: 'Luchtaíodh {count} eisceachtaí',
            announce_loaded_rules: '{count} rialacha SoD lódáilte',
            announce_loading_exceptions: 'Eisceachtaí á lódáil…',
            regulatory_info: {
                sod_context: 'Cuireann rialacha SoD scaradh rialala rol i bhfeidhm mar a eilitear le PDPL. Is feidir saruithe nasctha le framework_code a chur san aireamh i bpacaisti fianaise (UC-VN-09).',
                violation_export_context: 'Cuimsionn easpuirtiu saruithe SoD framework_code, roil i gcoimhlint, deineacht, cruthoiri agus am braite le haghaidh pacaisti fianaise (P-VN-10).',
                exception_context: 'Taireachtai SoD fianaise ininiuchta: aitheantas cheadunaigh, urscaradh, rialacha cuitimh agus data eaga. Fanannn eisceachtai eagtha le feiceail (UC-VN-09 Ceim 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session events produce audit trail entries.',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions, duration, approver identity, and approval/denial timestamps.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS. Framework-specific modules require appropriate license tiers. Tier changes produce audit trail entries.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates) are marked with a compliance_critical flag and cannot be fully suppressed.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Expired delegations remain visible.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist, MFA, service accounts, and SSO changes are audit-trailed.',
        },
    },
    cmp: {
        integration: {
            title: 'CMP Comhtháthú',
            status: 'Nasc Stádas',
            endpoint: 'Pointe Deiridh CMP',
            last_sync: 'Sioncronú Deireanach',
            configure: 'Cumraigh',
        },
        purpose_mapping: {
            title: 'Cuspóir Mapping',
            cmp_purpose: 'CMP Cuspóir',
            grc_purpose: 'GRC Cuspóir',
            mapping_status: 'Mapping Stádas',
            auto_map: 'Uathmhapáil',
            new_mapping: 'Nua Mapping',
        },
        webhook: {
            title: 'Webhook Cumraíocht',
            webhook_url: 'URL Webhook',
            secret: 'Rúnda Webook',
            events: 'Cineálacha Imeachta',
            test_webhook: 'Tástáil Webhook',
        },
        bulk_sync: {
            title: 'Sioncronú Mór',
            sync_now: 'Sioncronaigh Anois',
            last_run: 'Last Rith',
            records_synced: 'Taifid Synced',
            sync_history: 'Sync Stair',
            syncing: 'Ag sioncronú...',
        },
        sync_settings: {
            sync_failures: 'Teipeanna Sioncronaithe',
            sync_secret_warning: 'Rún Sioncronaithe (sábháil anois — ní thaispeánfar arís é):',
            push_description: 'Cumraigh the GRC Suite to push case stádas updates to AesirX CMP.',
            technical_description: 'Rialaíonn an chumraíocht seo conas a bhrúnn an GRC Suite nuashonruithe stádais cáis chuig AesirX CMP.',
            direction_label: 'Treo',
            direction_value: 'GRC Suite → AesirX CMP (amach)',
            auth_label: 'Fíordheimhniú',
            auth_value: 'Síniú HMAC-SHA256 i gceanntásc X-GRC-Signature',
            retry_label: 'Bain triail as arís Polasaí',
            retry_value: '3 iarracht le cúlú easpónantúil (30s, 2n, 10n)',
            inbound_label: 'Cumraíocht Isteach',
            inbound_value: 'Tá webhook isteach (CMP → GRC) cumraithe i',
            cmp_webhook_settings: 'CMP Webhook Socruithe',
            page_title: 'Socruithe Sioncronaithe CMP',
            outbound_sync_configured: 'Sioncronú amach cumraithe.',
            configure_sync_failed: 'Theip ar chumrú sioncronaithe',
            configuration_updated: 'Cumraíocht nuashonraithe.',
            update_configuration_failed: 'Theip ar nuashonrú cumraíochta',
            connection_test_failed: 'Theip ar thástáil ceangail',
            test_result_message: 'Tástáil seolta chuig {endpoint}. Stádas: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'Nua → Under Athbhreithniú (assigned)',
                under_review_to_identity_verification: 'Á Athbhreithniú → Fíorú Aitheantais',
                under_review_to_in_progress: 'Á Athbhreithniú → Ar siúl',
                in_progress_to_completed: 'In Progress → Críochnaithe',
                in_progress_to_denied: 'Ar siúl → Diúltaithe',
                any_to_closed: 'Aon → Dúnta',
            },
        },
    },
    settings_pages: {
        webhooks: {
            title: 'Webhookeanna',
            new: 'Nua Webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Imeachtaí',
            secret: 'Rún',
            active: 'Gníomhach',
            test: 'Tástáil',
            create_failed: 'Theip ar create webhook. Bain triail eile as.',
            create_success: "D'éirigh leis an ngréasán gréasáin a chruthú.",
            deactivate_failed: 'Theip ar deactivate webhook. Bain triail eile as.',
            deactivate_success: 'Webook díghníomhaithe.',
            fetch_failed: 'Theip ar fetch webhooks. Bain triail eile as.',
            send_test_failed: 'Theip ar send test. Bain triail eile as.',
            test_failed: 'Theip ar thástáil Webhook.',
            test_success: "D'éirigh le tástáil Webhook.",
            edit_title: 'Athraigh webhook',
            announce_loaded: 'Webhookeanna luchtaithe',
            update_success: 'Nuashonraíodh webhook go rathúil.',
            update_failed: 'Theip ar nuashonrú webhook.',
            activate_success: 'Gníomhachtaíodh webhook.',
            activate_failed: 'Theip ar ghníomhachtú webhook.',
        },
        domains: {
            title: 'Fearainn',
            new: 'Nua Fearann',
            domain_name: 'Fearann Ainm',
            verified: 'Fíoraithe',
            verify: 'Fíoraigh',
            add_failed: 'Theip ar add domain. Bain triail eile as.',
            add_success: 'Fearann added successfully.',
            domain_placeholder: 'm.sh. grc.example.com',
            fetch_failed: 'Theip ar fetch domains. Bain triail eile as.',
            remove_failed: 'Theip ar remove domain. Bain triail eile as.',
            remove_success: 'Fearann removed successfully.',
            txt_record: 'TXT Taifead',
            verify_dns_failed: 'DNS verification theip.',
            verify_failed: 'Theip ar verify domain. Bain triail eile as.',
            verify_success: 'Fearann verified successfully.',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: 'Sceidil onnmhairithe',
            new: 'Nua Sceideal',
            schedule_name: 'Ainm',
            frequency: 'Minicíocht',
            format: 'Formáid',
            last_run: 'Last Rith',
            next_run: 'Ar aghaidh Rith',
            create_failed: 'Theip ar create schedule. Bain triail eile as.',
            create_success: 'Sceideal created successfully.',
            day_of_month: 'Lá den Mhí',
            day_of_week: 'Lá den tSeachtain',
            deactivate_failed: 'Theip ar deactivate schedule. Bain triail eile as.',
            deactivate_success: 'Sceideal deactivated.',
            fetch_failed: 'Theip ar fetch schedules. Bain triail eile as.',
            freq_daily: 'Laethúil',
            freq_monthly: 'Míosúil',
            freq_weekly: 'Seachtainiúil',
            recipients: 'Faighteoirí',
            time_utc: 'Am (UTC)',
            trigger_failed: 'Theip ar trigger export. Bain triail eile as.',
            trigger_success: 'Easpórtáil triggered successfully.',
            type_audit_trail: 'Iniúchadh Trail',
            type_compliance_report: 'Comhlíonadh Tuarascáil',
            type_custom: 'Saincheaptha',
            type_incident_report: 'Teagmhas Tuarascáil',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: 'Logáil ríomhphoist',
            recipient: 'Faighteoir',
            subject: 'Ábhar',
            sent_at: 'Seolta',
            status: 'Stádas',
            resend: 'Athsheol',
            fetch_failed: 'Theip ar fetch ríomhphost logáil. Bain triail eile as.',
            announce_loaded: 'Email log loaded',
        },
    },
    organizations: {
        title: 'Eagraíochtaí',
        create: 'Cruthaigh Eagraíocht',
        edit: 'Cuir in eagar Eagraíocht',
        name: 'Ainm',
        slug: 'Sluga',
        timezone: 'Crios Ama',
        locale: 'Réamhshocrú Logcháil',
        parent: 'Parent Eagraíocht',
        group: 'Grúpa',
        org_group: 'Grúpa Eagraíochtaí',
        no_group: '(Dada - níl i ngrúpa)',
        created: 'Cruthaithe',
        no_parent: '(Dada - neamhspleách)',
        no_orgs: 'Níl aon organizations aimsithe',
        load_error: 'Theip ar load organizations. Bain triail eile as.',
        save_error: 'Theip ar save organization. Bain triail eile as.',
        created_msg: 'Eagraíocht created',
        updated: 'Eagraíocht updated',
        deactivate: 'Díghníomhachtaigh',
        deactivated: 'Eagraíocht deactivated',
        confirm_deactivate: 'An bhfuil tú cinnte gur mian leat deactivate this organization?',
        loaded_results: 'Organizations luchtaithe, {count} toradh',
        form_loaded: 'Foirm Organization luchtaithe',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'Béarla (en-SAM)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'Gach creat',
        assigned_at: 'Sannta ar',
    },
    intake_configs: {
        title: 'Cumraíochtaí Iontógála Poiblí',
        create: 'Cruthaigh Config',
        edit: 'Cuir in eagar Intake Config',
        label_col: 'Lipéad',
        key: 'Intake Eochair',
        key_hint: 'Beidh an URL poiblí: /rights-request?key=',
        mapped_org: 'Sprioc Eagraíocht',
        status: 'Stádas',
        enabled: 'Cumasaithe',
        disabled: 'Díchumasaithe',
        request_types: 'Cumasaithe Iarratas Cineálacha',
        language: 'Réamhshocrú Teanga',
        public_url: 'URL Poiblí',
        no_configs: 'Níl aon intake configs found. cruthaigh one to enable public rights request forms. aimsithe',
        load_error: 'Theip ar load intake configs. Bain triail eile as.',
        save_error: 'Theip ar save. Bain triail eile as.',
        delete_error: 'Theip ar delete. Bain triail eile as.',
        created_msg: 'Cumraíocht cruthaithe',
        updated: 'Cumraíocht nuashonraithe',
        deleted: 'Cumraíocht iontógála scriosta',
        confirm_delete: 'Scrios this intake config?',
        select_types: 'Roghnaigh at least one request cineál',
        select_org: 'Roghnaigh a target organization',
        routing_mode: 'Mód Ródaithe',
        single_org: 'Eagraíocht Aonair',
        org_group_mode: 'Grúpa Eagraíochtaí',
        org_group: 'Grúpa Eagraíochtaí',
        select_group: 'Roghnaigh grúpa eagraíochtaí...',
        group_root_hint: 'Socraithe go huathoibríoch go bun-eagraíocht an ghrúpa nuair a roghnaítear an grúpa.',
        loaded_results: 'Intake configurations luchtaithe, {count} toradh',
        form_loaded: 'Foirm Intake configuration luchtaithe',
    },
    frequency: {
        weekly: 'Go Seachtainiúil',
        monthly: 'Go Míosúil',
        quarterly: 'Go Ráithiúil',
        semi_annually: 'Go Leathbhliantúil',
        annually: 'Go Bliantúil',
        custom: 'Saincheaptha',
    },
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity (IRP-01). English text held for structural
       parity; real translation deferred to Phase 25 per R-57. */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /* 2026-09-24 owner ruling — legacy instrument notice. English text held for
       structural parity; real translation deferred to Phase 25 per R-57. */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (ANON-01). English text held for
     structural parity; real translation deferred to Phase 25 per R-57. */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            service_certificates: 'Processing Service Certificate',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Féilire',
            all_tasks: 'Gach tasc',
            my_tasks: 'Mo thascanna',
            overdue: 'Thar téarma',
            board: 'Clár',
            workflows: 'Sreafaí oibre',
        },
        list: {
            title: 'Gach tasc',
            my_title: 'Mo thascanna',
            overdue_title: 'Tascanna thar téarma',
            new_task: 'Cruthaigh tasc',
            no_tasks: 'Níor aimsíodh aon tasc.',
            empty_state: 'Níor aimsíodh aon tascanna. Gintear tascanna ó shreafaí oibre modúl mar mheasúnuithe, teagmhais agus feachtais athbhreithnithe.',
            total: 'Tascanna iomlán',
            search_placeholder: 'Cuardaigh tascanna...',
            bulk_actions: 'Gníomhartha grúpa',
            change_status: 'Athraigh stádas',
            change_priority: 'Athraigh tosaíocht',
            assign: 'Sann',
            sr_my_tasks_count: '{count} tasc sannta duit',
            sr_days_overdue: '{count} lá thar téarma',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Sonra an taisc',
            description: 'Cur síos',
            workflow_progress: 'Dul chun cinn sreafa oibre',
            checklist: 'Seicliosta',
            dependencies: 'Spleáchais',
            comments: 'Nótaí tráchta',
            activity_timeline: 'Amlíne gníomhaíochta',
            assignees: 'Sanntaithe',
            add_assignee: 'Cuir sanntaí leis',
            remove_assignee: 'Bain sanntaí',
            due_date: 'Dáta dlite',
            start_date: 'Dáta tosaigh',
            sla_status: 'Stádas SLA',
            sla_on_track: 'Ar an mbóthar ceart',
            sla_at_risk: 'I mbaol',
            sla_breached: 'Sáraithe',
            tags: 'Clibeanna',
            source_entity: 'Eintiteas foinse',
            view_source: 'Féach ar fhoinse',
            add_dependency: 'Cuir spleáchas leis',
            remove_dependency: 'Bain spleáchas',
            write_comment: 'Scríobh nóta tráchta...',
            post_comment: 'Seol',
            edit_comment: 'Cuir in eagar',
            delete_comment: 'Scrios',
            add_checklist_item: 'Cuir mír leis',
            no_dependencies: 'Gan spleáchais',
            no_comments: 'Gan nótaí tráchta fós',
            blocks: 'Bacainneacha',
            blocked_by: 'Bacainnithe ag',
            related: 'Gaolmhar',
            working_days_remain: 'laethanta oibre fágtha',
            created_by: 'Cruthaithe ag',
            completed_at: 'Críochnaithe ar',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'Gan sanntaithe',
            add_assignee_placeholder: 'Cuir isteach ID úsáideora...',
            edited: 'curtha in eagar',
            timeline: 'Amlíne gníomhaíochta',
            no_timeline: 'Gan ghníomhaíocht fós',
            timeline_reason: 'Reason',
            no_workflow: 'Gan sreabhadh oibre sannta',
            add_dependency_placeholder: 'Cuir isteach ID taisc...',
            sr_status_updated: 'Nuashonraíodh an stádas go {status}',
            sr_priority_updated: 'Nuashonraíodh an tosaíocht go {priority}',
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            convert_to_workflow: 'Rith mar Shreabhadh Oibre',
            convert_modal_title: 'Tiontaigh tasc go rith sreabhadh oibre',
            convert_workflow_label: 'Sreabhadh oibre',
            convert_workflow_placeholder: 'Roghnaigh sreabhadh oibre…',
            convert_attachments_label: 'Ceangaltáin',
            convert_attachments_empty: 'Níl aon cheangaltán ar an tasc seo.',
            convert_persona_label: 'Sárú pearsana (roghnach)',
            convert_submit: 'Tosaigh rith sreabhadh oibre',
            convert_starting: 'Rith sreabhadh oibre á thosú…',
            convert_started: 'Cuireadh tús le rith an tsreafa oibre — rith tosaigh…',
            convert_disabled_in_flight: 'Tá sreabhadh oibre ón tasc seo ar siúl cheana féin.',
            linked_workflow_chip: 'Ó {workflow_title}',
            linked_chat_chip: 'Ó AI Comhrá',
            linked_more: '+{count} eile',
        },
        calendar: {
            title: 'Féilire tascanna',
            page_title: 'Féilire',
            create_task: 'Cruthaigh tasc',
            month: 'Mí',
            week: 'Seachtain',
            day: 'Lá',
            today: 'Inniu',
            more_tasks: '+{count} eile',
            no_tasks_day: 'Gan tascanna an lá seo',
            all_day: 'An lá ar fad',
            quick_create_tooltip: 'Cruthaigh tasc don lá seo',
            view_changed: 'Amharc féilire athraithe go {view}',
            tasks_in_view: '{count} tasc san amharc',
            task_in_view: '{count} tasc san amharc',
            filter_my_tasks: 'Mo thascanna',
            filter_overdue: 'Thar téarma',
            filter_high_priority: 'Tosaíocht ard',
            filter_all: 'Uile',
        },
        filters: {
            assignee: 'Sanntaí',
            me: 'Mise',
            role: 'Ról',
            status: 'Stádas',
            priority: 'Tosaíocht',
            task_type: 'Cineál taisc',
            source_module: 'Modúl foinse',
            organization: 'Eagraíocht',
            sla_breached_only: 'Taispeáin SLA sáraithe amháin',
            clear_filters: 'Glan scagairí',
            apply_filters: 'Cuir i bhfeidhm',
            source_label: 'Foinse',
            source_manual: 'Lámhleabhar',
            source_module_kind: 'Modúl-ghinte',
            source_chat: 'Ó AI Comhrá',
            source_workflow_run: 'Ó AI Rith Sreafa Oibre',
        },
        status: {
            pending: 'Ar feitheamh',
            in_progress: 'Ar siúl',
            blocked: 'Bacainnithe',
            completed: 'Críochnaithe',
            overdue: 'Thar téarma',
            escalated: 'Ardaithe',
            cancelled: 'Curtha ar ceal',
        },
        priority: {
            critical: 'Criticiúil',
            high: 'Ard',
            medium: 'Meán',
            low: 'Íseal',
        },
        task_types: {
            manual_task: 'Tasc láimhe',
            dsr_case: 'Cás DSR',
            dsr_review: 'Athbhreithniú DSR',
            dsr_escalation: 'Ardú DSR',
            dpia_review: 'Athbhreithniú DPIA',
            dpia_approval: 'Ceadú DPIA',
            transfer_review: 'Athbhreithniú aistrithe',
            incident_task: 'Tasc teagmhais',
            incident_notification: 'Fógra teagmhais',
            playbook_step: 'Céim an leabhair súgartha',
            regulator_deadline: 'Spriocdháta rialálaithe',
            monitoring_task: 'Tasc monatóireachta',
            kpi_alert: 'Foláireamh KPI',
            vendor_review: 'Athbhreithniú soláthraí',
            vendor_questionnaire: 'Ceistneoir soláthraí',
            governance_review: 'Athbhreithniú rialachais',
            drift_alert: 'Foláireamh saofa',
            governance_approval: 'Ceadú rialachais',
            deletion_task: 'Tasc scriosta',
            deletion_verification: 'Fíorú scriosta',
            access_review: 'Athbhreithniú rochtana',
            sod_alert: 'Foláireamh SoD',
            consent_workflow: 'Sreabhadh oibre toilithe',
            consent_alert: 'Foláireamh toilithe',
            property_review: 'Athbhreithniú maoine',
            inconsistency_alert: 'Foláireamh neamhréireachta',
            form_review: 'Athbhreithniú foirme',
            dossier_deadline: 'Spriocdháta comhaid',
            submission_task: 'Tasc aighneachta',
            translation_review: 'Athbhreithniú aistriúcháin',
            governance_task: 'Tasc rialachais',
            reporting_deadline: 'Spriocdháta tuairiscithe',
            scanner_alert: 'Foláireamh scanóra',
            classification_review: 'Athbhreithniú aicmithe',
            lifecycle_task: 'Tasc saolré',
            discovery_review: 'Athbhreithniú aimsithe',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Sannadh Oiliúna',
        task_type_groups: {
            training: 'Oiliúint',
            manual: 'Láimhe',
            dsr: 'DSR / Iarratais chearta',
            assessments: 'Measúnuithe',
            incidents: 'Teagmhais',
            incident_ops: 'Oibríochtaí teagmhas',
            monitoring: 'Monatóireacht',
            vendor_risk: 'Riosca soláthraí',
            governance: 'Rialachas',
            deletion: 'Scriosadh',
            access_review: 'Athbhreithniú rochtana',
            consent: 'Toiliú',
            consent_properties: 'Airíonna toilithe',
            forms: 'Foirmeacha',
            localization: 'Logánú',
            program_governance: 'Rialachas clár',
            privacy_scanner: 'Scanóir príobháideachta',
            classification: 'Aicmiú',
            discovery: 'Aimsiú',
            forseti: 'Forseti',
        },
        create: {
            title: 'Cruthaigh tasc',
            task_title: 'Teideal',
            task_description: 'Cur síos',
            task_type: 'Cineál taisc',
            task_type_label: 'Cineál taisc',
            task_type_placeholder: 'Roghnaigh cineál taisc',
            priority: 'Tosaíocht',
            assignees: 'Sanntaithe',
            assignee_search_placeholder: 'Cuardaigh úsáideoirí...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: 'Bain {name}',
            due_date: 'Dáta dlite',
            start_date: 'Dáta tosaigh',
            checklist: 'Seicliosta',
            tags: 'Clibeanna',
            add_checklist_item: 'Cuir mír seicliosta leis',
            validation: {
                title_required: 'Teideal riachtanach',
                title_max_length: 'Caithfidh an teideal a bheith 500 carachtar nó níos lú',
                description_max_length: 'Caithfidh an cur síos a bheith 5000 carachtar nó níos lú',
                due_date_before_start: 'Caithfidh an dáta dlite a bheith tar éis an dáta tosaigh',
                max_reminders: 'Uasmhéid 5 meabhrúchán in aghaidh an taisc',
            },
            reminders: {
                title: 'Meabhrúcháin',
                add: 'Cuir meabhrúchán leis',
                at_due_date: 'Ar an dáta dlite',
                minutes_before: '{count} nóiméad roimhe sin',
                hours_before: '{count} uair an chloig roimhe sin',
                days_before: '{count} lá roimhe sin',
                weeks_before: '{count} seachtainí roimhe sin',
                custom: 'Saincheaptha',
                channel_platform: 'Ardán',
                channel_email: 'Ríomhphost',
                channel_both: 'Araon',
                offset_label: 'Meabhrúchán {n}: am',
                channel_label: 'Meabhrúchán {n}: cainéal',
                remove_label: 'Bain meabhrúchán {n}',
            },
            workflow: {
                label: 'Sreabhadh oibre',
                none: 'Ceann ar bith (bainistiú stádais láimhe)',
                steps_count: '{count} chéim',
                preview_label: 'Céimeanna sreafa oibre',
            },
        },
        errors: {
            fetch_failed: 'Theip ar luchtú tascanna.',
            save_failed: 'Theip ar shábháil an taisc.',
            delete_failed: 'Theip ar scriosadh an taisc.',
            status_change_failed: 'Theip ar athrú stádas.',
            assign_failed: 'Theip ar shannadh an taisc.',
            comment_failed: 'Theip ar shábháil nóta tráchta.',
            checklist_failed: 'Theip ar an seicliosta a nuashonrú.',
            dependency_failed: 'Theip ar an spleáchas a nuashonrú.',
            workflow_failed: 'Theip ar shábháil an tsreafa oibre.',
            workflow_activate_failed: 'Theip ar ghníomhachtú an tsreafa oibre.',
            workflow_delete_failed: 'Theip ar scriosadh an tsreafa oibre.',
            template_install_failed: 'Theip ar shuiteáil teimpléid.',
            search_failed: 'Theip ar chuardach tascanna.',
            convert_failed: 'Níorbh fhéidir an sreabhadh oibre a thosú.',
            convert_quota_exhausted: 'Buicéad comhartha íseal. Breisiú suas roimh athreáchtáil.',
        },
        dependency_types: {
            blocks: 'Bacainneacha',
            blocked_by: 'Bacainnithe ag',
            related: 'Gaolmhar',
        },
        workflow: {
            current_step: 'Céim reatha',
            transition: 'Aistriú',
            history: 'Stair céimeanna',
            no_workflow: 'Gan sreabhadh oibre sannta',
            detail_title: 'Sonraí sreabha oibre',
            list_title: 'Sainmhínithe sreafa oibre',
            create_workflow: 'Cruthaigh sreabhadh oibre',
            browse_templates: 'Brabhsáil teimpléid',
            template_catalog_title: 'Catalóg teimpléad',
            name: 'Ainm',
            description: 'Cur síos',
            task_types: 'Cineálacha taisc',
            steps: 'Céimeanna',
            version: 'Leagan',
            created: 'Cruthaithe',
            actions: 'Gníomhartha',
            status_draft: 'Dréacht',
            status_active: 'Gníomhach',
            status_archived: 'Cartlannaithe',
            activate: 'Gníomhachtaigh',
            archive: 'Cartlannaigh',
            save: 'Sábháil',
            edit: 'Cuir in eagar',
            delete: 'Scrios',
            clone: 'Clónáil',
            install: 'Suiteáil',
            installed: 'Suiteáilte',
            system: 'Córas',
            general_settings: 'Socruithe ginearálta',
            default_assignment: 'Sannadh réamhshocraithe',
            default_sla: 'SLA réamhshocraithe',
            workflow_steps: 'Céimeanna sreafa oibre',
            add_step: 'Cuir céim leis',
            step_name: 'Ainm na céime',
            step_status: 'Luach stádais',
            assignment_strategy: 'Straitéis sannta',
            assignment_role: 'Ról sannta',
            sla_hours: 'Uaireanta SLA',
            sla_calendar_type: 'Cineál féilire',
            working_days: 'Laethanta oibre',
            calendar_days: 'Laethanta féilire',
            is_terminal: 'Céim deiridh',
            required_fields: 'Réimsí riachtanacha',
            checklist_template: 'Teimpléad seicliosta',
            transitions: 'Aistrithe',
            add_transition: 'Cuir aistriú leis',
            from_step: 'Ó',
            to_step: 'Chuig',
            permission: 'Cead',
            sla_action: 'Gníomh SLA',
            notify: 'Fógair',
            escalation_rules: 'Rialacha ardaithe',
            add_rule: 'Cuir riail leis',
            trigger: 'Truicear',
            threshold: 'Tairseach',
            action: 'Gníomh',
            target_role: 'Ról sprice',
            trigger_sla_threshold: 'Tairseach SLA',
            trigger_sla_breach: 'Sárú SLA',
            trigger_blocked_duration: 'Fad bacainnithe',
            trigger_manual: 'Láimhe',
            action_notify: 'Fógair',
            action_escalate: 'Ardaigh leibhéal',
            action_reassign: 'Athshann',
            sla_start: 'Tosaigh',
            sla_pause: 'Sos',
            sla_resume: 'Atosaigh',
            sla_stop: 'Stop',
            strategy_manual: 'Láimhe',
            strategy_role_based: 'Bunaithe ar ról',
            strategy_round_robin: 'Babhta Robin',
            strategy_least_loaded: 'Is lú ualaithe',
            strategy_creator: 'Cruthaitheoir',
            confirm_delete: 'An bhfuil tú cinnte gur mian leat an sreabhadh oibre seo a scriosadh?',
            confirm_archive: 'An bhfuil tú cinnte gur mian leat an sreabhadh oibre seo a chartlannú?',
            cannot_delete_active: 'Ní féidir sreafaí oibre gníomhacha a scriosadh. Cartlannaigh ar dtús.',
            validation_errors: 'Earráidí bailíochtaithe sreafa oibre',
            step_count: '{count} chéim',
            no_workflows: 'Níor aimsíodh aon sreabhadh oibre.',
            no_templates: 'Gan teimpléid ar fáil.',
            sr_templates_count: '{count} teimpléad ar fáil',
            template_installed: 'Teimpléad suiteáilte go rathúil.',
            activate_success: 'Sreabhadh oibre gníomhachtaithe.',
            archive_success: 'Sreabhadh oibre cartlannaithe.',
            save_success: 'Sreabhadh oibre sábháilte.',
            delete_success: 'Sreabhadh oibre scriosta.',
            applicable_task_types: 'Cineálacha tascanna infheidhme',
            steps_column: 'Céimeanna',
            status_value_hint: 'Eochair staid an tsreafa oibre a úsáideann trasdulacha. Litreacha beaga, uimhreacha agus folíneálacha.',
            from_status: 'Ó Staid',
            to_status: 'Go Staid',
            required_permissions: 'Ceadanna riachtanacha',
            transition_required_fields: 'Réimsí riachtanacha (trasdul)',
            auto_assign: 'Sann go huathoibríoch',
            auto_assign_role: 'Ról sannta uathoibríoch',
            threshold_percent: 'Tairseach (%)',
            blocked_hours: 'Uaireanta blocáilte',
            notify_roles: 'Cuir róil ar an eolas',
            escalate_to_role: 'Géaraigh go ról',
            notification_template: 'Teimpléad fógra',
            action_escalate_tier: 'Géaraigh sraith',
            create_modal_title: 'Cruthaigh sreabhadh oibre',
            create_modal_description: 'Sainigh céimeanna, trasdulacha agus rialacha géaraithe. Cruthaítear an sreabhadh oibre mar dhréacht.',
            create_modal_footer_note: 'Cruthaithe mar dhréacht. Gníomhachtaigh é nuair a bhailíochtaítear an graf.',
            validation_name_required: 'Tá ainm an tsreafa oibre riachtanach.',
            validation_task_types_required: 'Roghnaigh cineál tasca infheidhme amháin ar a laghad.',
            validation_min_one_step: 'Cuir céim amháin ar a laghad leis.',
            validation_min_one_transition: 'Cuir trasdul amháin ar a laghad leis.',
            validation_step_name_required: 'Tá ainm na céime riachtanach.',
            validation_status_value_required: 'Tá luach na staide riachtanach.',
            validation_status_value_duplicate: 'Caithfidh luach na staide a bheith uathúil ar fud na gcéimeanna.',
            validation_transition_endpoints_required: 'Roghnaigh ó-staid agus go-staid araon.',
            validation_transition_unknown_status: 'Tagraíonn an trasdul seo do staid nach sainíonn aon chéim.',
            validation_sla_positive: 'Caithfidh uaireanta SLA a bheith níos mó ná náid. Fág folamh é le haghaidh SLA ar bith.',
            validation_threshold_percent_range: 'Caithfidh an tairseach a bheith idir 0 agus 100.',
            validation_blocked_hours_positive: 'Caithfidh uaireanta blocáilte a bheith níos mó ná náid.',
            warning_no_terminal_step: 'Gan aon chéim chríochnaithe. Teipfidh ar an ngníomhachtú go dtí go marcáiltear céim amháin mar chríoch.',
            warning_step_no_outgoing: 'Céimeanna neamhchríochnaithe gan trasdulacha amach: {statuses}',
            warning_unreachable_steps: 'Céimeanna nach féidir a bhaint amach ón gcéad chéim: {statuses}',
            orphaned_transitions_warning: 'Tagraíonn {count} trasdul do chéim scriosta.',
            remove_orphaned_transitions: 'Bain trasdulacha dílleachta',
            transitions_renamed_announcement: 'Nuashonraíodh {count} trasdul go luach na staide nua.',
            add_checklist_item: 'Cuir mír seicliosta leis',
            checklist_item_label: 'Mír seicliosta',
        },
        columns: {
            priority: 'Tosaíocht',
            title: 'Teideal',
            status: 'Stádas',
            type: 'Cineál',
            assignees: 'Sanntaí(the)',
            due_date: 'Dáta dlite',
            source: 'Foinse',
            actions: 'Gníomhartha',
        },
        groups: {
            overdue: 'Thar téarma',
            due_today: 'Dlite inniu',
            due_this_week: 'Dlite an tseachtain seo',
            due_later: 'Dlite níos déanaí',
            no_due_date: 'Gan dáta dlite',
            closed: 'Críochnaithe nó cealaithe',
        },
        board: {
            title: 'Clár tascanna',
            drag_hint: 'Tarraing tascanna idir colúin chun stádas a athrú',
            empty_column: 'Gan tascanna',
            keyboard_hint: 'Méarchlár: eochracha saighde le nascleanúint, spás chun tasc a phiocadh suas, saighead clé/dheas chun colún a roghnú, Iontráil chun ligean, Éalú chun cealú.',
            sr_moving: 'Ag bogadh {title}. Os cionn {column}. Brúigh Iontráil chun ligean, Éalú chun cealú.',
            sr_tasks_shown: '{count} tasc á thaispeáint',
            sr_picked_up: '{title} pioctha suas',
            sr_returned: "D'fhill {title} ar {column}",
            sr_dropped: '{title} ligthe i {column}, suíomh {position} as {total}',
            sr_move_cancelled: 'Bogadh curtha ar ceal',
            sr_over_column: 'Os cionn {column}',
            sr_moved_to: '{title} bogtha go {column}',
            invalid_transition: 'Ni feidir an tasc a bhogadh o {from} go {to}.',
            terminal_status: 'Ta an tasc seo i stadas deiridh agus ni feidir e a bhogadh.',
            invalid_drop_target: 'Sprioc neamhbhaili',
        },
        team: {
            title: 'Ualach oibre foirne',
            assignee: 'Sanntaí',
            active: 'Gníomhach',
            overdue: 'Thar téarma',
            blocked: 'Bacainnithe',
            completed_7d: 'Críochnaithe (7d)',
            load: 'Ualach',
            load_high: 'Ard',
            load_medium: 'Meán',
            load_normal: 'Gnáth',
        },
        graph: {
            title: 'Graf Spleáchais',
            list_view: 'Amharc Liosta',
            graph_view: 'Graf Amharc',
            zoom_in: 'Súmáil Isteach',
            zoom_out: 'Súmáil Amach',
            reset_zoom: 'Athshocraigh',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'Uimh spleáchais a shamhlú.',
        },
        add_dependency_modal: {
            title: 'Cuir spleáchas leis',
            search_placeholder: 'Cuardaigh tascanna de réir teidil nó aitheantais...',
            no_results: 'Níor aimsíodh aon tasc.',
            this_blocks: 'Blocann an tasc seo roghnaithe',
            this_blocked_by: 'Tá an tasc seo bac le roghnaithe',
            related: 'Gaolmhar',
            max_dependencies: '10 spleáchas uasta in aghaidh an taisc.',
            circular_error: 'Ní féidir cur leis: chruthódh sé spleáchas ciorclach.',
        },
        conflict_detection: {
            title: 'Brath coinbhleachta',
            unresolved: 'neamhréitithe',
            no_conflicts: 'Níor braitheadh aon choinbhleacht',
            section: 'Roinn',
            field: 'Réimse',
            dept_a: 'Roinn A',
            value_a: 'Luach A',
            dept_b: 'Roinn B',
            value_b: 'Luach B',
            severity: 'Déine',
            status: 'Stádas',
            resolved: 'Réitithe',
            open: 'Oscailte',
            resolve: 'Réitigh',
            load_failed: 'Theip ar load conflicts',
            resolve_success: 'Coinbhleacht réitithe go rathúil',
            resolve_failed: 'Theip ar resolve conflict',
            severity_critical: 'Criticiúil',
            severity_warning: 'Rabhadh',
            severity_info: 'Eolas',
        },
        consolidation: {
            title: 'Dul chun cinn comhdhlúthaithe',
            add_child: 'Cuir fo-thasc leis',
            child_task_id: 'ID na fo-thaisc',
            department_id: 'ID na roinne',
            link: 'Nasc',
            overall_completion: 'Comhlánú iomlán',
            departments_complete: 'Ranna críochnaithe',
            overdue: 'Thar téarma',
            ready: 'Réidh',
            not_ready: 'Ní réidh',
            master_status: 'Príomhstádas',
            department_breakdown: 'Miondealú roinne',
            department: 'Roinn',
            status: 'Stádas',
            completion: 'Comhlánú',
            due_date: 'Dáta dlite',
            overdue_flag: 'Thar téarma',
            child_tasks: 'Fo-thascanna',
            task: 'Tasc',
            load_failed: 'Theip ar load consolidation data',
            child_linked: 'Fo-thasc nasctha go rathúil',
            link_failed: 'Theip ar link child task',
            confirm_unlink: 'An bhfuil tú cinnte gur mian leat an fo-thasc seo a dhínascadh?',
            child_unlinked: 'Fo-thasc dínasctha',
            unlink_failed: 'Theip ar dhínascadh fo-thaisc',
            unlink: 'Dínasc',
            status_completed: 'Críochnaithe',
            status_in_progress: 'Ar siúl',
            status_pending: 'Ar feitheamh',
            select_child_task: 'Roghnaigh fo-thasc',
            select_department: 'Roghnaigh roinn',
        },
        section_assignments: {
            title: 'Sannadh roinne',
            assign_section: 'Sann roinn',
            section_ref: 'Tag. roinne',
            section_label: 'Lipéad roinne',
            department_id: 'ID na roinne',
            department: 'Roinn',
            assignee_id: 'ID an tsannta',
            assignee: 'Sannaí',
            mandatory: 'Éigeantach',
            assign: 'Sann',
            no_assignments: 'Gan sannadh roinne',
            status: 'Stádas',
            completion: 'Comhlánú',
            mark_complete: 'Críochnaigh',
            load_failed: 'Theip ar load section assignments',
            assign_success: 'Roinn sannta go rathúil',
            assign_failed: 'Theip ar assign section',
            status_updated: 'Stádas nuashonraithe',
            status_update_failed: 'Theip ar an stádas a nuashonrú',
            section_ref_placeholder: 'e.g. 3.2',
            status_completed: 'Críochnaithe',
            status_in_progress: 'Ar Siúl',
            status_pending: 'Ar feitheamh',
            select_department: 'Roghnaigh roinn',
            select_assignee: 'Roghnaigh sannaí',
        },
        overdue_columns: {
            days_overdue: 'Laethanta thar am',
        },
        regulatory_info: {
            deadline_context: 'Agus tascanna DPIA a chruthu, ba choir go mbeadh spriocdhataai ag teacht le spriocdhatai comhdaile PDPL: ionchur roinne = 10 la oibre. Ta spriocdhatai Cibearshlandala agus Teileachumarsaide i bhfeidhm freisin.',
            overdue_deadline_context: 'Spriocdhatai rialachain o shonrai pacaiste: ionchur roinne PDPL = 10 la oibre, freagra forlionach = 15 la oibre, ath-chur isteach = 10 la oibre. Leiriitear tascanna a saragann SLA go hamharc.',
            section_assignment_context: 'Sreabhadh oibre DPIA ilroinne (PDPL-P1): sannann an DPO coda comhaid do ranna. Spriocdhataa ionchur roinne: 10 la oibre.',
            template_catalog_context: 'Cludaionn teimpleaid chorais proisis shonrach do chreat: DPIA PDPL, dearbu Dli Sonrai, freagairt teagmhas Cibearshlandala, iarratais udarais Teileachumarsaide agus claruchaan corais AI. Ni feidir teimpleaid chorais a chur in eagar.',
            workflow_instantiation_context: 'Is feidir teimpleaid suiteailte a neamhspleachas i sreabha oibre beo. Cruithaitear tascanna go huathoibrioch le spriocdhatai rite.',
        },
        overdue: {
            days_overdue: 'lá thar téarma',
        },
    },
    notifications: {
        bell: {
            title: 'Fógraí',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Fógraí',
            mark_all_read: 'Marcáil go léir mar a léitear',
            no_notifications: 'Gan fógraí',
            filter: {
                all: 'All',
                unread: 'Neamhléite',
                tasks: 'Tascanna',
                approvals: 'Ceaduithe',
                alerts: 'Foláirimh',
            },
        },
        preferences: {
            title: 'Sainroghanna Fógraí',
            description: 'Cumraigh conas a fhaigheann tú fógraí',
            category: {
                label: 'Catagóir',
                task_assigned: 'Tasc Sannta',
                task_sla_approaching: 'SLA Ag druidim',
                task_overdue: 'Tasc thar téarma',
                task_completed: 'Tasc Críochnaithe',
                task_escalated: 'Méadaíodh an Tasc',
                workflow_transition: 'Trasdul Sreabhadh Oibre',
                approval_requested: 'Faomhadh Iarrtha',
                comment_added: 'Trácht Curtha leis',
                weekly_digest: 'Achoimre na Seachtaine',
                sla_breach_report: 'SLA Tuarascáil Sárú',
                system_alert: 'Foláireamh Córais',
                // English until Phase 25 (R-57).
                rights_request_received: 'New Rights Request',
                // English until Phase 25 (R-57).
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Ardán',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Uaireanta Ciúin',
                start: 'Tosaigh',
                end: 'End',
                timezone: 'Crios ama',
            },
            digest: {
                title: 'Minicíocht Achoimre',
                daily: 'laethúil',
                weekly: 'Seachtainiúil',
                none: 'Dada',
            },
            watched: {
                title: 'Aonáin Faire',
                empty: 'Uimh aonáin faire',
            },
            save: 'Sábháil Sainroghanna',
            saving: 'Ag sábháil...',
            saved_success: 'Sábháladh na sainroghanna go rathúil',
            load_error: 'Níorbh fhéidir na sainroghanna sábháilte a luchtú. Mainneachtainí á dtaispeáint - déanfar athruithe a shábháil nuair a chliceálann tú Sábháil.',
            enabled: 'Cumasaithe',
            disabled: 'faoi mhíchumas',
            unwatch: 'Unwatch',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: 'Ciú riaracháin fógraí',
            description: 'Monatóireacht a dhéanamh ar stádas seachadta ríomhphoist agus teachtaireachtaí teipthe a aththriail',
            load_error: 'Theip ar shonraí ciú ríomhphoist a lódáil',
            retry_error: 'Theip ar an ríomhphost a aththriail',
            filter_status: 'Scag de réir stádais',
            all_statuses: 'Gach ceann',
            refresh: 'Athnuachan',
            status_queued: 'Sa chiú',
            status_sent: 'Seolta',
            status_failed: 'Teipthe',
            status_bounced: 'Preabtha',
            status_sending: 'Sending',
            col_recipient: 'Faighteoir',
            col_category: 'Catagóir',
            col_template: 'Teimpléad',
            col_status: 'Stádas',
            col_retries: 'Athiarrachtaí',
            col_created: 'Cruthaithe',
            col_sent: 'Seolta ar',
            col_actions: 'Gníomhartha',
            no_items: 'Níor aimsíodh aon mhíreanna sa chiú ríomhphoist',
            retry: 'Aththriail',
            retrying: 'Ag aththriail...',
            prev: 'Roimhe',
            next: 'Ar aghaidh',
            access_denied_title: 'Rochtain riarthóra ag teastáil',
            access_denied: 'Níl an leathanach seo ar fáil ach do riarthóirí. Déan teagmháil le do riarthóir má theastaíonn rochtain uait.',
            announce_queue_loaded: 'Luchtaíodh scuaine ríomhphoist, {count} míreanna',
            announce_processed: 'Próiseáladh {count} ríomhphost(í) ríomhphoist',
            process_now: 'Próiseáil Anois',
            processing_ellipsis: 'Ag próiseáil…',
        },
    },
    ui_assist: {
        toolbar: {
            title: 'Chomhéadain Cabhrú',
            activate: 'Gníomhachtaigh',
            deactivate: 'Díghníomhachtaigh',
            connected: 'Ceangailte',
            disconnected: 'Dícheangailte',
            disconnected_hint: 'Dícheangailte - rith `npm run sealaíochta` chun nascadh le Claude CLI',
            open_panel: 'Oscail painéal UI Assist',
            history: 'Stair',
            settings: 'Socruithe',
        },
        prompt: {
            placeholder: 'Déan cur síos ar an athrú is mian leat a dhéanamh…',
            send: 'Seol',
            cancel: 'Cealaigh',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Ag próiseáil…',
            complete: 'Críochnaigh',
            error: 'Earráid',
            abort: 'ginmhilleadh',
            abort_confirm: 'An bhfuil tú cinnte gur mhaith leat ginmhilleadh?',
            aborted_hint: 'Cuireadh isteach ar an seisiún — is féidir leat leanúint ar aghaidh ón áit seo.',
            undo: 'Cealaigh',
            redo: 'Athdhéan',
            retry: 'Bain triail eile as',
            dismiss: 'Díbhe',
        },
        label: {
            component: 'Comhpháirt',
            element: 'Eilimint',
            file: 'Comhad',
        },
        response: {
            you: 'You',
            claude: 'Claude',
            empty: 'Níl aon teachtaireachtaí fós. Roghnaigh eilimintí agus seol leid chun tús a chur leis.',
        },
        panel: {
            tab_conversation: 'Comhrá',
            tab_tasks: 'Tascanna',
            tab_sprint: 'Sprint',
            tab_sessions: 'Seisiúin',
            tab_lobby: 'stocaireacht',
            follow_up: 'Leantach',
            followup_placeholder: 'Cuir ceist leantach…',
            interrupt_placeholder: 'Clóscríobh chun cur isteach agus atreorú…',
            send_interrupt: 'Seol & Cuir Isteach',
            will_interrupt: 'Stopfaidh sé an freagra reatha agus seolfaidh sé seo ina ionad',
            expand: 'Leathnaigh go lánscáileán',
            collapse: 'Laghdaigh go painéal taobh',
            no_sessions: 'Níl aon seisiúin roimhe seo. Roghnaigh eilimintí agus seol teachtaireacht.',
            session_active: 'Gníomhach',
            session_ended: 'Críochnaithe',
            session_messages: '{count} teachtaireacht',
            switch_session: 'Athraigh go dtí an seisiún seo',
            load_session: 'Íoslódáil an stair de na seisiúin',
            session_restored: 'Seisiún athchóirithe ón stair',
            archive_session: 'Seisiún cartlainne',
            archive_confirm: 'Cartlann an seisiún seo? Scriosfar an comhad seisiúin.',
            new_session: 'Seisiún Nua',
            new_session_desc: 'Úsáid Gníomhachtaigh chun eilimint Chomhéadain a roghnú agus seisiún nua a thosú',
            start_new_session: 'Tosaigh Seisiún Nua',
            no_active_session_chat: 'Tosaigh seisiún nua nó úsáid Gníomhachtaigh chun eilimint Chomhéadain a roghnú ar an leathanach chun tosú.',
            no_active_session_tasks: 'Tosaigh seisiún nua nó roghnaigh seisiún atá ann cheana féin chun tascanna a chruthú.',
            no_active_session_sprint: 'Tosaigh seisiún nua nó roghnaigh seisiún atá ann cheana féin chun sprint a phleanáil.',
            previous_sessions: 'Seisiúin roimhe seo',
        },
        tasks: {
            none_found: 'Níor aimsíodh aon tascanna sa fhreagra. Iarr ar Claude tascanna a mholadh.',
            selected: 'selected',
            create_selected: 'Cruthaigh Tascanna',
            execute_tasks: 'Forghníomhaigh tascanna',
            creating: 'Ag cruthú…',
            created: 'tascanna cruthaithe',
            create_error: 'Theip ar an tasc a chruthú',
            plan_sprint: 'Plean Sprint',
            priority_critical_desc: 'Blocálaí — deisigh láithreach',
            priority_high_desc: 'Tábhachtach — réitigh sa sprint seo',
            priority_medium_desc: 'Gnáth — pleanáil don chéad sprint eile',
            priority_low_desc: 'Deas le bheith agat — iarrthóir don backlog',
            add_custom: 'Cuir tasc leis',
            add_title_placeholder: 'Teideal an taisc…',
            add_desc_placeholder: 'Cur síos (roghnach)',
            add_save: 'Cuir leis',
            add_cancel: 'Cealaigh',
        },
        sprint: {
            title: 'Pleanálaí Sprint',
            assigned: 'assigned',
            new_placeholder: 'Ainm sprint (m.sh. Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Riaráiste',
            move_to: 'Bog go…',
            empty: 'Cruthaigh grúpa sprint agus sann tascanna dó.',
            create_all: 'Cruthaigh Gach Tasc',
            remove: 'Bain',
            drop_here: 'Sann tascanna ón riaráiste thíos',
            add_task: 'Cuir tasc leis ón riaráiste…',
        },
        feature_disabled: 'Níl UI Assist cumasaithe',
        permission_denied: 'Níl cead agat UI Assist a úsáid',
    },
    license: {
        title: 'Ceadúnas & Pacáiste',
        current_license: 'Ceadúnas Reatha',
        license_key: 'Eochair Cheadúnais',
        activate: 'Ceadúnas a ghníomhachtú',
        deactivate: 'Ceadúnas a dhíghníomhachtú',
        validate_now: 'Bailíochtaigh Anois',
        status: 'Stádas Ceadúnais',
        active: 'Gníomhach',
        expired: 'imithe in éag',
        suspended: 'Ar fionraí',
        invalid: 'Neamhbhailí',
        pending: 'Ar feitheamh',
        no_license: 'Gan Ceadúnas',
        expires_at: 'In éag',
        last_validated: 'Bailíochtaithe go Deiridh',
        domain: 'Fearann Ceadúnaithe',
        activation_success: "D'éirigh leis an gceadúnas a ghníomhachtú.",
        activation_failed: 'Theip ar ghníomhachtú an cheadúnais.',
        domain_mismatch: 'Ní mheaitseálann an fearann ceadúnais an fearann reatha.',
        deactivation_success: 'Ceadúnas díghníomhachtaithe.',
        validation_success: "D'éirigh le bailíochtú an cheadúnais.",
        validation_failed: 'Theip ar bhailíochtú ceadúnais.',
        enter_key: "Cuir isteach d'eochair cheadúnais",
        confirm_deactivate: 'An bhfuil tú cinnte gur mhaith leat an ceadúnas seo a dhíghníomhachtú? Beidh srianta modúil i bhfeidhm bunaithe ar an gciseal réamhshocraithe.',
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'Pacáiste',
        module_column: 'Modúl',
        current_tier: 'Pacáiste Reatha',
        light: 'Solas',
        standard: 'Caighdeán',
        enterprise_unlimited: 'Enterprise Unlimited',
        change_package: 'Athraigh Pacáiste',
        upgrade: 'Uasghrádú',
        downgrade: 'íosghrádú',
        target_tier: 'Pacáiste Sprioc',
        new_license_key: 'Eochair Cheadúnais don Spriocphacáiste',
        confirm_change: 'Deimhnigh Athrú Pacáiste',
        change_success: "D'éirigh leis an bpacáiste a athrú.",
        change_failed: 'Theip ar athrú an phacáiste.',
        modules_included: 'Modúil san áireamh',
        modules_restricted: 'Níl sé ar fáil sa Phacáiste Reatha',
        upgrade_to_enable: 'Uasghrádaigh chun an modúl seo a chumasú.',
        module_availability: 'Infhaighteacht Modúl',
        comparison: 'Comparáid Pacáiste',
        power_users: 'Úsáideoirí Cumhachta',
        unlimited: 'Gan teorainn',
        impact_warning: 'Díchumasófar na modúil seo a leanas de bharr íosghrádú:',
        invalid_license: 'Níl an eochair cheadúnais a soláthraíodh bailí don phacáiste seo.',
        tier_mismatch: 'Tá an eochair cheadúnais le haghaidh sraith pacáiste difriúil.',
    },
    board_report: {
        title: 'Tuarascáil Chomhlíonta an Bhoird',
        subtitle: 'Achoimre feidhmiúcháin ar chomhlíontacht ar fud na modúl go léir',
        date_from: 'Ón dáta',
        date_to: 'Go dtí dáta',
        language: 'Teanga na tuarascála',
        generate: 'Gin tuarascáil',
        generating: 'Tuarascáil á giniúint...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'Íoslódáil PDF',
        close: 'Dún tuarascáil',
        open_board_report: 'Gin tuarascáil an bhoird',
        close_board_report: 'Dún tuarascáil an bhoird',
        report_period: 'Tréimhse tuarascála',
        generated_at: 'Ginte ar',
        generated_by: 'Ginte ag',
        organization: 'Eagraíocht',
        section_rights_requests: 'Iarratais ar chearta',
        section_vendors: 'Soláthróirí',
        section_dpias: 'MPTSanna',
        section_consent: 'Polasaithe toilithe',
        section_data_mapping: 'Mapáil sonraí',
        section_incidents: 'Teagmhais',
        section_classification: 'Aicmiú sonraí',
        section_governance: 'Rialachas',
        open: 'Oscailte',
        closed: 'Dúnta',
        sla_compliance: 'Comhlíonadh SLA',
        total: 'Iomlán',
        expired_dpa: 'DPA as feidhm',
        high_risk: 'Ardriosca',
        completed: 'Críochnaithe',
        overdue: 'Thar téarma',
        policies_active: 'Polasaithe gníomhacha',
        coverage: 'Clúdach',
        systems: 'Córais',
        completeness: 'Iomláine',
        avg_resolution_days: 'Meán réitigh (laethanta)',
        labeled: 'Lipéadaithe',
        unlabeled_systems: 'Córais gan lipéad',
        compliance_score: 'Scór comhlíonta',
        drift_events_open: 'Imeachtaí imeachta oscailte',
        no_data: 'Níl aon sonraí ar fáil don tréimhse roghnaithe.',
        error_date_range: 'Roghnaigh dáta tosaigh agus dáta deiridh.',
        table_category: 'Catagóir',
        table_metric: 'Méadrach',
        table_value: 'Luach',
        showing_chart_view: 'Ag taispeáint amharc cairte',
        showing_table_view: 'Ag taispeáint amharc tábla sonraí',
        error_generate: 'Theip ar thuarascáil an bhoird a ghiniúint.',
        playbook_evidence_title: 'Fianaise feidhmithe leabhar imirte',
        playbook_evidence_description: 'Gníomhaíocht feidhmithe in aghaidh leabhar imirte agus comhaid fianaise ceangailte don tréimhse tuairiscithe.',
        playbook_name: 'Leabhar imirte',
        playbook_execution_count: 'Feidhmithe',
        playbook_steps_total: 'Céimeanna',
        playbook_steps_required: 'Fianaise riachtanach',
        playbook_steps_attached: 'Fianaise ceangailte',
        playbook_coverage_pct: 'Clúdach',
        playbook_evidence_files: 'Comhaid fianaise',
        playbook_no_evidence: 'Níl aon fheidhmithe leabhar imirte sa tréimhse seo.',
        playbook_download_file: 'Íoslódáil comhad',
    },
    framework_module_links: {
        title: 'Nascadh Creat Rialála',
        modules: 'Modúil Nasctha',
        add_module: 'Modúl a Chur Leis',
        module_code: 'Cód Modúil',
        required: 'Riachtanach',
        notes: 'Nótaí',
        link: 'Nasc',
        no_modules: 'Níl aon mhodúl nasctha',
        personas: 'Pearsana Nasctha',
        add_persona: 'Pearsa a Chur Leis',
        persona_id: 'Aitheantóir Pearsan',
        role_description: 'Cur Síos ar an Ról',
        no_personas: 'Níl aon phearsa nasctha',
    },
    framework_channels: {
        title: 'Cainéil Aighneachta & Spriocdháta',
        channels: 'Cainéil Aighneachta',
        add_channel: 'Cainéal a Chur Leis',
        channel_type: 'Cineál',
        name: 'Ainm',
        instructions: 'Treoracha',
        active: 'Gníomhach',
        no_channels: 'Níl aon chainéal aighneachta cumraithe',
        portal: 'Tairseach',
        in_person: 'Go Pearsanta',
        postal: 'Poist',
        email: 'Ríomhphost',
        add: 'Cuir Leis',
        deadlines: 'Spriocdháta Rialála',
        add_deadline: 'Spriocdháta a Chur Leis',
        label: 'Lipéad',
        days: 'Laethanta',
        day_type: 'Cineál Lae',
        calendar_days: 'Laethanta Féilire',
        working_days: 'Laethanta Oibre',
        description: 'Cur Síos',
        no_deadlines: 'Níl aon spriocdháta cumraithe',
    },
    regulatory_updates: {
        title: 'Nuashonruithe Rialála & Deais Creat',
        coverage_dashboard: 'Deais Clúdaigh Creat',
        no_frameworks: 'Níor aimsíodh aon chreat',
        framework: 'Creat Rialála',
        status: 'Stádas',
        modules: 'Modúil',
        templates: 'Teimpléid',
        forms: 'Foirmeacha',
        channels: 'Cainéil',
        deadlines: 'Spriocdháta',
        pending: 'Nuashonruithe ar Feitheamh',
        updates: 'Nuashonruithe Rialála',
        create_update: 'Nuashonrú a Chruthú',
        framework_id: 'Aitheantóir Creat',
        update_title: 'Teideal',
        description: 'Cur Síos',
        effective_date: 'Dáta Éifeachta',
        create: 'Cruthaigh',
        no_updates: 'Níl aon nuashonrú rialála',
        impact: 'Tionchar',
        assess: 'Measúnaigh',
    },
    vehicle_monitoring: enVehicleMonitoring,
    record_links: enRecordLinks,
    module_tips: gaModuleTips,
    framework_rights: {
        be_informed: 'An ceart go gcuirfí ar an eolas thú',
        consent: 'An ceart toiliú a thabhairt',
        refuse_consent: 'An ceart toiliú a dhiúltú',
        withdraw_consent: 'An ceart toiliú a aistarraingt',
        access: 'An ceart rochtana',
        rectification: 'An ceart ceartúcháin',
        data_provision: 'An ceart sonraí a sholáthar',
        deletion: 'An ceart scriosadh a iarraidh',
        restriction_of_processing: 'An ceart srian a chur ar phróiseáil',
        object_to_processing: 'An ceart agóid a dhéanamh in aghaidh próiseála',
        complaint: 'An ceart gearán a dhéanamh',
        denunciation: 'An ceart nochtadh a dhéanamh',
        lawsuit: 'An ceart caingean dlí a thionscnamh',
        claim_damages: 'An ceart damáistí a éileamh',
        request_protection_measures: 'An ceart bearta cosanta a iarraidh',
    },
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Treoir 2002/58/CE (an Treoir um ePhríobháideacht)',
            description: 'Máthairthreoir do thrasuímh náisiúnta um ePhríobháideacht. Cumhdaíonn sí fianáin, príobháideacht na cumarsáide leictreonaí, agus margaíocht dhíreach. Caithfidh gach Ballstát í a thrasuí ina dhlí náisiúnta féin.',
        },
        EU_GDPR: {
            display_name: 'An Rialachán Ginearálta um Chosaint Sonraí (AE) 2016/679',
            description: 'Is rialachán de chuid an AE é an Rialachán Ginearálta um Chosaint Sonraí (RGCS) maidir le cosaint sonraí agus príobháideacht. Tá feidhm dhíreach aige i ngach Ballstát den AE/LEE.',
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Treoir (AE) 2022/2555 (NIS2)',
            description: "Máthairthreoir do thrasuímh náisiúnta NIS2. Cumhdaíonn sí bainistíocht riosca cibearshlándála, tuairisciú teagmhas, agus slándáil an tslabhra soláthair d'eintitis riachtanacha agus tábhachtacha.",
        },
        VN_AI_LAW_2026: {
            display_name: 'Dlí Intleachta Saorga Vítneam (134/2025/QH15) — i bhfeidhm ón 01 Márta 2026',
            description: 'Dlí Intleachta Saorga Vítneam (Luật Trí tuệ nhân tạo 134/2025/QH15) lena rialaítear forbairt, imscaradh agus úsáid córas intleachta saorga. Bunaíonn sé aicmiú riosca, dualgais trédhearcachta agus lipéadaithe, ceanglais um thuairisciú teagmhas, oibleagáidí measúnaithe tionchair agus sásra um thástáil rialaithe (sandbox) le haghaidh gníomhaíochtaí intleachta saorga in Vítneam.',
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Dlí Cibearshlándála Vítneam (ré 24/2018)',
            description: 'Dlí Cibearshlándála Vítneam (Dlí Uimh. 24/2018/QH14) lena mbunaítear ceanglais maidir le cibearshlándáil, logánú sonraí agus tuairisciú teagmhas. I bhfeidhm go dtí go dtagann Dlí 116/2025/QH15 ina ionad le héifeacht ón 2026-07-01.',
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Foraithne 53 — Cur i bhFeidhm an Dlí Cibearshlándála',
            description: 'Foraithne 53/2022/ND-CP lena gcuirtear an Dlí Cibearshlándála i bhfeidhm, lena gcumhdaítear cinneadh raon feidhme, truicir um logánú sonraí, ceanglais um láithreacht áitiúil, agus sreafaí oibre comhoibrithe leis na húdaráis.',
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Dlí um Chibearshlándáil (116/2025/QH15)',
            description: 'Dlí comhdhlúite Vítneam um Chibearshlándáil (Dlí 116/2025/QH15), a thiocfaidh in ionad Dlí 24/2018/QH14, le héifeacht ón 1 Iúil 2026. Comhdhlúthaíonn sé raon feidhme Foraithne 53/2022/NĐ-CP (féach VN_CYBERSEC_DECREE_53).',
        },
        VN_DATA_LAW: {
            display_name: 'Dlí Sonraí Vítneam',
            description: 'Dlí Sonraí Vítneam (Dlí 60/2024/QH15) lena mbunaítear ceanglais chuimsitheacha um rialachas sonraí, lena n-áirítear fardal sonraí, aicmiú, rialachas comhroinnte, agus rialachas um aistriú sonraí trasteorann. Forluíonn sé le VN_PDPL_LAW_2025 (gnéithe sonraí pearsanta) agus le VN_CYBERSEC_LAW_2025 (logánú/aicmiú sonraí).',
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Foraithne 165/2025/NĐ-CP (Cur i bhFeidhm an Dlí Sonraí)',
            description: 'Foraithne 165/2025/NĐ-CP lena gcuirtear Dlí Sonraí Vítneam (Dlí 60/2024/QH15) i bhfeidhm. Soláthraíonn sí na foirmeacha oifigiúla le haghaidh measúnaithe tionchair ar aistriú sonraí trasteorann, tuairiscithe ar mheasúnú riosca bliantúil, agus rochtana ar an mbunachar sonraí náisiúnta. Sainmhíníonn sí 11 fhoirm oifigiúil Mẫu số.',
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Dréachtfhoraithne maidir le Malartuithe Sonraí 2026',
            description: 'Dréachtfhoraithne maidir le feidhmiú ardán malairte sonraí (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Rialaíonn sí aicmiú ardán, incháilitheacht na rannpháirtithe, rialuithe ar liostú táirgí agus seirbhísí sonraí, tástáil sandbox, sreabhadh oibre idirbhirt, conarthaíocht leictreonach, deimhnithe um chearta maoine ar shonraí, agus láimhseáil gearán/díospóide. I gcomhairliúchán poiblí faoi láthair; nár fógraíodh fós. Luann sí an Dlí Sonraí 60/2024/QH15, an Dlí um Chosaint Sonraí Pearsanta 91/2025/QH15, an Dlí um Chibearshlándáil 116/2025/QH15, agus an Dlí um Idirbhearta Leictreonacha 20/2023/QH15.',
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Dlí um Thráchtáil Leictreonach (122/2025/QH15)',
            description: 'Dlí Vítneam um Thráchtáil Leictreonach (Luật Thương mại điện tử 122/2025/QH15) lena mbunaítear córas náisiúnta aontaithe le haghaidh tráchtála leictreonaí, lena gcumhdaítear feidhmíochtaí ardáin baile agus trasteorann, rialachas ardáin agus cuntasacht, cosaint tomhaltóirí, rialuithe díoltóirí agus comhpháirtithe cleamhnaithe, láimhseáil gearán, modhnóireacht agus forfheidhmiú baintí ábhar, agus ullmhacht nósanna imeachta riaracháin don chóras stáit um bhainistíocht tráchtála leictreonaí.',
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Foraithne 13/2023/NĐ-CP',
            description: 'Foraithne 13/2023/ND-CP, an ionstraim oibríochtúil le haghaidh dualgais PDPL, lena n-áirítear comhdú dosaethanna, déantáin rialachais, meicníochtaí trasteorann, agus nósanna imeachta riaracháin MoPS.',
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Dlí um Chosaint Sonraí Pearsanta (91/2025/QH15)',
            description: 'Dlí Vítneam um Chosaint Sonraí Pearsanta (Dlí 91/2025/QH15) lena mbunaítear creat cuimsitheach um chosaint sonraí pearsanta, le héifeacht ón 1 Eanáir 2026. Cumhdaíonn sé téamaí comhlíontachta A–J: bunúis dhleathacha próiseála, toiliú do shonraí íogaire, cearta na n-ábhar sonraí (rochtain/ceartú/léirscriosadh/srianadh/agóid/iniomparthacht), fógra sáraithe chuig an údarás agus chuig na hábhair shonraí, DPIA do phróiseáil ardriosca, rialachas aistrithe trasteorann, ainmniú DPO, taifid ar ghníomhaíochtaí próiseála, oibleagáidí conarthacha próiseálaithe. Oibleagáidí comhdaithe leis an údarás trí MoPS (VN_PDPL_ADMIN_PROCEDURES_778_2026). Crostagairtí: VN_PDPL_DECREE_356_2025 (foraithne cur i bhfeidhm), VN_CYBERSEC_LAW_2025 (forluíonn cibearshlándáil), VN_DATA_LAW (rialachas sonraí).',
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Foraithne 356/2025/ND-CP (Cur i bhFeidhm PDPL)',
            description: 'Foraithne 356/2025/ND-CP lena gcuirtear an Dlí um Chosaint Sonraí Pearsanta (Dlí 91/2025/QH15) i bhfeidhm. Cumhdaíonn sí comhdú dosaethanna, déantáin rialachais, meicníochtaí trasteorann, agus deimhniú soláthraithe seirbhíse. Fardal foirmeacha aguisín: Phụ lục I–VIII (measúnú tionchair, aistriú trasteorann, deimhniú). Nósanna imeachta riaracháin MoPS trí VN_PDPL_ADMIN_PROCEDURES_778_2026 (Cinneadh 778/QĐ-BCA-A05). Sreabhadh oibre deimhnithe: iarratas → eisiúint → iarraidh ar athrú → ath-eisiúint → cúlghairm. Crostagairtí: VN_PDPL_LAW_2025 (an mháthair-dlí), VN_CYBERSEC_LAW_2025 (logánú sonraí), VN_DATA_LAW (forluíonn rialachas sonraí).',
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'Nósanna Imeachta Riaracháin MPS A05 (Cinneadh 778/QĐ-BCA-A05)',
            description: 'Cinneadh 778/QĐ-BCA-A05 (Feabhra 2026) — Nósanna imeachta riaracháin um chosaint sonraí pearsanta faoi A05 (MPS). Sainmhíníonn sé nósanna imeachta um chur isteach dosaethanna, próiseáil agus eisiúint torthaí lena n-áirítear foirmeacha DLCN-HC agus foirmeacha reachtúla Mẫu số.',
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Dréachtfhoraithne Forfheidhmithe 2026',
            description: 'Dréachtfhoraithne forfheidhmithe agus smachtbhannaí le haghaidh sáruithe ar an Dlí um Chosaint Sonraí Pearsanta (Dlí 91/2025/QH15). Sainmhíníonn sí raonta pionós, fachtóirí géaraitheacha/maolaitheacha, agus nósanna imeachta forfheidhmithe. I stádas dréachta faoi láthair, ag fanacht ar fhógairt.',
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Dlí Teileachumarsáide Vítneam 2023',
            description: 'Dlí Teileachumarsáide Vítneam 2023 (Luật Viễn thông), forshraith earnála le haghaidh cuideachtaí teileachumarsáide/ISP/ardáin le hoibleagáidí maidir le rúndacht, rialú nochtaithe, agus láimhseáil iarrataí ón údarás. Crostagairtí: VN_CYBERSEC_LAW_2025 (slándáil córas faisnéise), VN_PDPL_LAW_2025 (próiseáil sonraí úsáideoirí), VN_DATA_LAW (rialachas sonraí).',
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Foraithne 163/2024/NĐ-CP (Cur i bhFeidhm an Dlí Teileachumarsáide)',
            description: 'Foraithne 163/2024/NĐ-CP lena gcuirtear an Dlí Teileachumarsáide (Dlí 24/2023/QH15) i bhfeidhm. Soláthraíonn sí na foirmeacha oifigiúla le haghaidh fógra um sheirbhísí teileachumarsáide, ceadúnaithe, clárúcháin, agus pleananna forbartha ionaid sonraí. Sainmhíníonn sí 7 bhfoirm oifigiúil Mẫu số.',
        },
    },
    framework_requirements: {
        vn_dp_draft_f: {
            title: 'Gearáin, díospóidí, agus bainistíocht riosca',
            description: 'Ní mór d’oibreoirí ardáin gearáin a ghlacadh isteach, sreabhadh oibre um láimhseáil díospóide a sholáthar le caomhnú fianaise, rialuithe um aithint riosca agus luathfholáirimh, agus athbhreithniú tréimhsiúil ar rioscaí príobháideachais, cibearshlándála agus bainistithe rochtana.',
        },
        vn_dp_draft_e: {
            title: 'Sreabhadh oibre idirbhirt agus deimhniú um chearta maoine ar shonraí',
            description: 'Éilíonn idirbhearta clárú cuntais, formheas liostaithe, conarthaíocht leictreonach, deimhniú íocaíochta, taifead seachadta, agus eisiúint deimhnithe um chearta maoine ar shonraí mar is infheidhme. Ní mór stair an idirbhirt agus inrianaitheacht a chaomhnú i gclár idirbheart.',
        },
        vn_dp_draft_d: {
            title: 'Rialuithe sandbox agus timpeallachta tástála',
            description: 'Ní mór timpeallachtaí tástála a bheith aonraithe, le rochtain shrianta, faoi mhonatóireacht slándála, agus athbhreithnithe sula mbaintear aon aschur. Ní féidir aon fhoinsesonraí a bhaint ón timpeallacht tástála, agus ní mór dúnadh/glanadh seisiún tástála a thaifeadadh.',
        },
        vn_dp_draft_c: {
            title: 'Rialuithe ar liostú táirgí agus seirbhísí sonraí',
            description: 'Ní mór do liostuithe a bheith tacaithe le fianaise foinse dlí agus le fianaise um chearta úinéireachta/úsáide, taifid bhailíochtaithe theicniúla, nochtuithe cáilíochta, agus taifid an ardáin um athbhreithniú agus formheas liostaithe. Ní mór sonraí toirmiscthe a scagadh roimh liostú.',
        },
        vn_dp_draft_b: {
            title: 'Incháilitheacht agus ionghabháil rannpháirtithe',
            description: 'Ní mór d’oibreoirí ardáin stádas dlíthiúil, cumas dlíthiúil, agus ceanglais ghnó choinníollacha a fhíorú do dhíoltóirí agus do cheannaitheoirí, cuntais idirbhirt agus íocaíochta a chlárú, agus clár rannpháirtithe a chothabháil.',
        },
        vn_dp_draft_a: {
            title: 'Aicmiú ardáin sonraí agus samhail oibríochta',
            description: 'Ní mór d’oibreoirí ardáin aicmiú ar chineál an ardáin a bhunú (ardán sonraí náisiúnta i gcomparáid le hardáin sonraí eile), incháilitheacht agus freagrachtaí an oibreora a shainiú, agus praghsáil, táillí, rialacha oibríochta agus rialachas teicniúil a dhoiciméadú.',
        },
        d165_r5: {
            title: 'Rochtain ar fhaisnéis ón mbunachar sonraí náisiúnta',
            description: 'Úsáidtear foirmeacha forordaithe (Mẫu số 07a/07b) le haghaidh iarrataí chun faisnéis a bhaint as/úsáid as an mbunachar sonraí digiteach náisiúnta.',
        },
        d165_r4: {
            title: 'Tuairisciú ar mheasúnú riosca bliantúil',
            description: 'Ní mór tuarascálacha ar mheasúnú riosca bliantúil a chomhdú agus na foirmeacha forordaithe in úsáid (Mẫu số 05a/05b, 06).',
        },
        d165_r3: {
            title: 'Fógra um fhoirceannadh aistrithe trasteorann',
            description: 'Ní mór fógra a thabhairt faoi fhoirceannadh aistrithe trasteorann agus na foirmeacha forordaithe in úsáid (Mẫu số 04a/04b).',
        },
        d165_r2: {
            title: 'Fógra ar thoradh measúnaithe',
            description: 'Eisíonn an t-údarás fógraí toraidh mheasúnaithe agus an fhoirm fhorordaithe in úsáid (Mẫu số 03).',
        },
        d165_r1: {
            title: 'Comhdú measúnaithe tionchair ar aistriú sonraí trasteorann',
            description: 'Ní mór d’eagraíochtaí dosaethanna measúnaithe tionchair um aistriú/próiseáil sonraí trasteorann a chomhdú agus na foirmeacha forordaithe in úsáid (Mẫu số 01a/01b, 02).',
        },
        a964190d_8d1d_4284_bc7d_4023e2866751: {
            title: "Toiliú d'fhianáin agus do theicneolaíochtaí dá samhail",
            description: 'Ní cheadaítear faisnéis a stóráil ar threalamh teirminéil ná a rochtain uirthi ach amháin le toiliú feasach a thugtar roimh ré, ach amháin sa chás go bhfuil an stóráil riachtanach ó thaobh na teicniúlachta de.',
        },
        '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
            title: 'Rúndacht na cumarsáide',
            description: 'Áiritheoidh na Ballstáit rúndacht na cumarsáide agus na sonraí tráchta gaolmhara trí líonra cumarsáide poiblí agus trí sheirbhísí cumarsáide leictreonaí atá ar fáil go poiblí.',
        },
        c5420073_50c6_4981_816a_398460e5f546: {
            title: 'Toiliú do mhargaíocht dhíreach (rogha-isteach)',
            description: 'Éilítear toiliú roimh ré an rannpháirtí chun córais cumarsáide leictreonaí a úsáid le haghaidh margaíochta díreach.',
        },
        c8bf67d2_874b_4647_92d7_39aaa3230819: {
            title: 'Bog-rogha-isteach do chustaiméirí atá ann cheana',
            description: 'Ceadaítear margaíocht dhíreach chuig custaiméirí atá ann cheana le haghaidh táirgí/seirbhísí dá samhail gan toiliú má sholáthraítear sásra rogha-amach.',
        },
        b546df40_9217_4204_b71c_5af5022a0ff9: {
            title: 'Fógra sáraithe do sholáthraithe teileachumarsáide',
            description: 'Tabharfaidh soláthraithe seirbhísí cumarsáide leictreonaí atá ar fáil go poiblí fógra don údarás náisiúnta inniúil faoi sháruithe ar shonraí pearsanta.',
        },
        '5bd66ae4_dee3_4611_9104_71742df55484': {
            title: 'An ceart chun agóid a dhéanamh i gcoinne margaíochta díreach',
            description: 'Tabharfar deis do rannpháirtithe agóid a dhéanamh i gcoinne teachtaireachtaí margaíochta díreach a fháil ag aon am agus saor in aisce.',
        },
        a10af31f_ba28_494e_ab50_f8679e86a0b0: {
            title: 'Coinneáil agus léirscriosadh sonraí tráchta',
            description: 'Ní mór sonraí tráchta a bhaineann le rannpháirtithe agus úsáideoirí a léirscriosadh nó a dhéanamh anaithnid nuair nach bhfuil siad de dhíth a thuilleadh chun críche tarchurtha, seachas íocaíochtaí billeála nó idirnasctha.',
        },
        '8577e60f_66a8_4651_8336_d561db9929ad': {
            title: 'Coinníollacha próiseála sonraí suímh',
            description: 'Ní féidir sonraí suímh seachas sonraí tráchta a phróiseáil ach amháin nuair a dhéantar anaithnid iad nó le toiliú an úsáideora, a mhéid agus ar feadh na tréimhse is gá.',
        },
        '7f3abb41_8713_4276_93b5_6902f419a016': {
            title: 'Dlíthiúlacht na próiseála — toiliú',
            description: 'Ní bheidh an phróiseáil dleathach ach amháin sa chás gur thug an t-ábhar sonraí toiliú a chuid sonraí pearsanta a phróiseáil chun críche shonraithe amháin nó níos mó.',
        },
        '1c2a945a_8eac_428d_8105_e80e95288ea4': {
            title: 'Dlíthiúlacht na próiseála — conradh',
            description: 'Tá an phróiseáil riachtanach chun conradh a chomhlíonadh ar páirtí ann an t-ábhar sonraí.',
        },
        '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
            title: 'Dlíthiúlacht na próiseála — oibleagáid dlí',
            description: 'Tá an phróiseáil riachtanach chun oibleagáid dlí a bhfuil an rialaitheoir faoina réir a chomhlíonadh.',
        },
        '702ea760_9e5c_4c32_babc_0b5094ca9735': {
            title: 'Dlíthiúlacht na próiseála — leasanna ríthábhachtacha',
            description: 'Tá an phróiseáil riachtanach chun leasanna ríthábhachtacha an ábhair sonraí nó duine nádúrtha eile a chosaint.',
        },
        '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
            title: 'Dlíthiúlacht na próiseála — leas an phobail',
            description: 'Tá an phróiseáil riachtanach chun cúram a chomhlíonadh ar mhaithe le leas an phobail nó i bhfeidhmiú údaráis oifigiúil.',
        },
        '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
            title: 'Dlíthiúlacht na próiseála — leasanna dlisteanacha',
            description: 'Tá an phróiseáil riachtanach chun críocha leasanna dlisteanacha an rialaitheora nó tríú páirtí.',
        },
        afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
            title: 'Próiseáil catagóirí speisialta (sonraí íogaire)',
            description: 'Tá toirmeasc ar phróiseáil catagóirí speisialta sonraí pearsanta (bunús ciníoch, tuairimí polaitiúla, sláinte, srl.) ach amháin má chomhlíontar coinníollacha sonracha Airteagal 9(2).',
        },
        '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
            title: 'Coinníollacha don toiliú',
            description: 'Ní mór don rialaitheoir a léiriú gur tugadh toiliú. Ní mór don toiliú a bheith tugtha go saorálach, sonrach, feasach agus gan athbhrí. Caithfidh tarraingt siar a bheith chomh héasca leis an toiliú a thabhairt.',
        },
        '27697f99_efef_49e6_b8e7_c2dc550d1791': {
            title: 'Toiliú linbh — seirbhísí digiteacha',
            description: 'Éilíonn próiseáil sonraí pearsanta linbh faoi bhun aois 16 (nó aois níos ísle a shocróidh an Ballstát, nach lú ná 13) toiliú tuismitheora i ndáil le seirbhísí na sochaí faisnéise.',
        },
        '53883480_508e_4f8e_984f_9354c18de285': {
            title: 'Faisnéis agus cumarsáid thrédhearcach',
            description: 'Soláthróidh an rialaitheoir faisnéis a bhaineann leis an bpróiseáil i bhfoirm achomair, thrédhearcach, sho-thuigthe agus a bheidh ar fáil go héasca.',
        },
        b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
            title: 'Faisnéis le haghaidh bailithe dhírigh',
            description: 'Sa chás go mbailítear sonraí pearsanta ón ábhar sonraí, soláthróidh an rialaitheoir an fhaisnéis shonraithe ag tráth an bhailithe.',
        },
        e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
            title: 'Faisnéis le haghaidh bailithe indírigh',
            description: 'Sa chás nár fríothadh sonraí pearsanta ón ábhar sonraí, soláthróidh an rialaitheoir an fhaisnéis shonraithe laistigh de thréimhse réasúnach.',
        },
        '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
            title: 'An ceart rochtana',
            description: 'Beidh sé de cheart ag an ábhar sonraí dearbhú a fháil go bhfuil próiseáil ar siúl agus rochtain a fháil ar na sonraí pearsanta agus ar fhaisnéis fhorlíontach.',
        },
        '0e011e25_dcea_4a1e_ae55_583e683cea44': {
            title: 'An ceart chun ceartaithe',
            description: 'Beidh sé de cheart ag an ábhar sonraí ceartú sonraí pearsanta míchruinne a fháil gan moill mhíchuí.',
        },
        e8ec5a55_f657_4a99_9b92_3e9861067d28: {
            title: 'An ceart chun léirscriosta (an ceart chun a bheith dearmadta)',
            description: 'Beidh sé de cheart ag an ábhar sonraí léirscriosadh sonraí pearsanta a fháil gan moill mhíchuí sa chás go mbaineann forais shonraithe.',
        },
        '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
            title: 'An ceart chun srianta ar phróiseáil',
            description: 'Beidh sé de cheart ag an ábhar sonraí srianadh ar an bpróiseáil a fháil sa chás go mbaineann coinníollacha sonraithe.',
        },
        '3b829862_39f4_41be_8f2b_d469469a801b': {
            title: 'Oibleagáid fógartha — ceartú/léirscriosadh/srianadh',
            description: 'Cuirfidh an rialaitheoir aon cheartú, léirscriosadh nó srianadh in iúl do gach faighteoir a nochtadh sonraí dó.',
        },
        '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
            title: 'An ceart chun iniomparthachta sonraí',
            description: 'Beidh sé de cheart ag an ábhar sonraí a chuid sonraí pearsanta a fháil i bhformáid struchtúrtha, a úsáidtear go coitianta agus atá inléite ag meaisín.',
        },
        becae433_90b9_4cc0_bd33_df50976c1561: {
            title: 'An ceart chun agóide',
            description: 'Beidh sé de cheart ag an ábhar sonraí agóid a dhéanamh i gcoinne próiseála atá bunaithe ar leas an phobail nó ar leasanna dlisteanacha, lena n-áirítear próifíliú.',
        },
        '4ef387f1_439c_4579_82a3_01565e6dc05d': {
            title: 'Cinnteoireacht uathoibrithe aonair, lena n-áirítear próifíliú',
            description: 'Beidh sé de cheart ag an ábhar sonraí gan a bheith faoi réir cinnidh atá bunaithe go heisiach ar phróiseáil uathoibrithe a tháirgeann éifeachtaí dlí nó éifeachtaí suntasacha dá samhail.',
        },
        '667ae5eb_a39a_4809_a032_923cc804eaed': {
            title: 'Fógra sáraithe don údarás maoirseachta (72u)',
            description: 'I gcás sárú sonraí pearsanta, tabharfaidh an rialaitheoir fógra don údarás maoirseachta laistigh de 72 uair an chloig ón uair a thiocfaidh sé ar an eolas, ach amháin sa chás nach dócha go dtiocfaidh riosca as.',
        },
        '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
            title: 'Fógra sáraithe do na hábhair shonraí (riosca ard)',
            description: 'Nuair is dócha go dtiocfaidh ardriosca do chearta agus do shaoirsí as sárú, cuirfidh an rialaitheoir an sárú in iúl don ábhar sonraí gan moill mhíchuí.',
        },
        '9dbb47bd_172f_4335_b7f3_4558476c4192': {
            title: 'Freagra ar iarrataí ó ábhair shonraí (30 lá)',
            description: "Freagróidh an rialaitheoir d'iarrataí ó ábhair shonraí gan moill mhíchuí agus laistigh de mhí amháin tar éis iad a fháil, agus is féidir an tréimhse sin a leathnú dhá mhí bhreise i gcás iarrataí casta.",
        },
        '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
            title: 'Measúnacht tionchair ar chosaint sonraí',
            description: 'Sa chás gur dócha go dtiocfaidh ardriosca do chearta agus do shaoirsí as an bpróiseáil, déanfaidh an rialaitheoir MTCS sula dtosaítear an phróiseáil.',
        },
        e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
            title: 'Réamhchomhairliúchán leis an údarás maoirseachta',
            description: 'Rachaidh an rialaitheoir i gcomhairle leis an údarás maoirseachta sa chás go léiríonn MTCS go dtiocfadh ardriosca as an bpróiseáil gan bearta maolaithe.',
        },
        '047b7838_bae5_4861_9c38_c99fedec9c96': {
            title: 'Liosta éigeantach próiseála MTCS',
            description: 'Tá MTCS riachtanach i gcás meastóireachta/próifíliú córasach, próiseála ar mhórscála ar chatagóirí speisialta, agus monatóireachta córasaí ar limistéir phoiblí.',
        },
        aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
            title: 'Taifid ar ghníomhaíochtaí próiseála (rialaitheoir)',
            description: 'Coinneoidh gach rialaitheoir taifead ar ghníomhaíochtaí próiseála faoina chúram, lena n-áirítear cuspóirí, catagóirí, faighteoirí, aistrithe agus coinneáil.',
        },
        b7eb6296_5962_449c_b267_d322d7919122: {
            title: 'Taifid ar ghníomhaíochtaí próiseála (próiseálaí)',
            description: 'Coinneoidh gach próiseálaí taifead ar gach catagóir gníomhaíochtaí próiseála a dhéantar thar ceann rialaitheora.',
        },
        '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
            title: 'Slándáil na próiseála',
            description: 'Cuirfidh an rialaitheoir agus an próiseálaí bearta teicniúla agus eagrúcháin iomchuí i bhfeidhm chun slándáil iomchuí don riosca a áirithiú.',
        },
        ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
            title: 'Cosaint sonraí trí dhearadh agus de réir réamhshocraithe',
            description: 'Cuirfidh an rialaitheoir bearta iomchuí i bhfeidhm chun a áirithiú nach bpróiseáiltear ach na sonraí pearsanta atá riachtanach do gach cuspóir sonrach (íoslaghdú sonraí).',
        },
        '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
            title: 'Ainmniú oifigigh cosanta sonraí',
            description: 'Ainmneoidh an rialaitheoir agus an próiseálaí OCS sa chás ina bhfuil an phróiseáil á déanamh ag údarás poiblí, ina mbaineann monatóireacht rialta agus chórasach léi, nó ina bhfuil próiseáil ar mhórscála ar chatagóirí speisialta.',
        },
        '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
            title: "Prionsabal ginearálta d'aistrithe",
            description: 'Ní dhéanfar aistriú sonraí pearsanta chuig tríú tír ach amháin má chomhlíontar coinníollacha Chaibidil V.',
        },
        '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
            title: 'Aistrithe ar bhonn cinnidh leordhóthanachta',
            description: 'Féadfar aistriú a dhéanamh sa chás gur chinn an Coimisiún go n-áirithíonn an tríú tír leibhéal leordhóthanach cosanta.',
        },
        cde8c7cd_3727_40ce_a321_e6b72b335179: {
            title: 'Aistrithe faoi réir coimircí iomchuí',
            description: 'In éagmais cinnidh leordhóthanachta, féadfar aistrithe a dhéanamh sa chás go gcuirtear coimircí iomchuí ar fáil, lena n-áirítear SCCanna nó BCRanna.',
        },
        '0e8edaa1_2156_44d9_95f2_1b284f581658': {
            title: 'Clásail chonarthacha chaighdeánacha',
            description: "Soláthraíonn clásail chonarthacha chaighdeánacha a ghlactar leis an gCoimisiún nó ag údarás maoirseachta coimircí iomchuí d'aistrithe idirnáisiúnta.",
        },
        '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
            title: 'Rialacha corparáideacha ceangailteacha',
            description: "Soláthraíonn rialacha corparáideacha ceangailteacha a fhormheasann údarás maoirseachta inniúil coimircí iomchuí d'aistrithe idirnáisiúnta laistigh de ghrúpa.",
        },
        '420ced80_0d3b_4a28_adad_14ba54df3e40': {
            title: 'Maoluithe le haghaidh staideanna sonracha',
            description: 'In éagmais leordhóthanachta nó coimircí, féadfar aistrithe a dhéanamh ar bhonn toilithe shainráite, riachtanais chonartha, leas an phobail, éileamh dlí, leasanna ríthábhachtacha, nó clár poiblí.',
        },
        c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
            title: 'Freagracht agus oiliúint an chomhlachta bainistíochta',
            description: 'Ní mór do chomhlachtaí bainistíochta na n-eintiteas riachtanach agus tábhachtach bearta um bainistíocht riosca cibearshlándála a fhormheas agus oiliúint rialta a fháil.',
        },
        ffb95a09_993c_4db0_a4bb_c48000e43b33: {
            title: 'Bearta um bainistíocht riosca cibearshlándála',
            description: 'Glacfaidh eintitis riachtanacha agus tábhachtacha bearta teicniúla, oibríochtúla agus eagrúcháin iomchuí agus comhréireacha chun rioscaí cibearshlándála a bhainistiú.',
        },
        '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
            title: 'Beartais agus nósanna imeachta um láimhseáil teagmhas',
            description: 'Cuirfidh eintitis beartais um láimhseáil teagmhas i bhfeidhm, lena n-áirítear nósanna imeachta brath, anailíse, srianta agus freagartha.',
        },
        de019eab_9992_4361_be7c_3e592f209d76: {
            title: 'Leanúnachas gnó agus bainistíocht ghéarchéime',
            description: 'Cuirfidh eintitis bearta um leanúnachas gnó i bhfeidhm, lena n-áirítear bainistíocht cúltaca, athshlánú ó thubaiste, agus bainistíocht ghéarchéime.',
        },
        '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
            title: 'Slándáil an tslabhra soláthair',
            description: 'Tabharfaidh eintitis aghaidh ar ghnéithe slándála a bhaineann le caidrimh le soláthraithe díreacha agus soláthraithe seirbhíse, lena n-áirítear measúnuithe riosca an tslabhra soláthair.',
        },
        c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
            title: 'Slándáil i gcórais líonra agus faisnéise',
            description: 'Cuirfidh eintitis slándáil i bhfeidhm i sealbhú, forbairt agus cothabháil córas líonra agus faisnéise, lena n-áirítear láimhseáil leochaileachtaí.',
        },
        '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
            title: 'Láimhseáil agus nochtadh leochaileachtaí',
            description: 'Cuirfidh eintitis beartais agus nósanna imeachta i bhfeidhm um nochtadh agus láimhseáil chomhordaithe leochaileachtaí.',
        },
        f009e65d_d55e_4c98_9532_63c150694698: {
            title: 'Éifeachtacht measúnaithe riosca cibearshlándála',
            description: 'Cuirfidh eintitis beartais agus nósanna imeachta i bhfeidhm chun éifeachtacht na mbeart um bainistíocht riosca cibearshlándála a mheas.',
        },
        '00e88824_675d_4b37_960c_757b3176c079': {
            title: 'Beartais um chripteagrafaíocht agus criptiú',
            description: 'Cuirfidh eintitis beartais agus nósanna imeachta i bhfeidhm maidir le húsáid na cripteagrafaíochta agus, mar is iomchuí, an chriptithe.',
        },
        de29d287_81f1_46b1_b327_9cbf06ddad3d: {
            title: 'Slándáil AD, rialú rochtana, bainistíocht sócmhainní',
            description: 'Cuirfidh eintitis slándáil acmhainní daonna, beartais um rialú rochtana, agus bainistíocht sócmhainní i bhfeidhm.',
        },
        '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
            title: 'Fíordheimhniú ilfhachtóireach',
            description: 'Úsáidfidh eintitis réitigh fíordheimhnithe ilfhachtóirigh nó leanúnaigh, cumarsáid ghutha/físe/téacs slán, agus córais cumarsáide éigeandála slán.',
        },
        '359474f9_1820_4e3e_9031_a593b66ccde4': {
            title: 'Luathfholáireamh chuig CSIRT (24u)',
            description: 'Cuirfidh eintitis luathfholáireamh isteach chuig an CSIRT inniúil laistigh de 24 uair an chloig ón uair a thiocfaidh siad ar an eolas faoi theagmhas suntasach.',
        },
        '66acd92c_3091_4148_b935_68b0d24308e7': {
            title: 'Fógra teagmhais chuig CSIRT (72u)',
            description: 'Cuirfidh eintitis fógra teagmhais isteach chuig an CSIRT inniúil laistigh de 72 uair an chloig ón uair a thiocfaidh siad ar an eolas faoi theagmhas suntasach.',
        },
        '68269688_06b2_43ea_8e3e_a84532086557': {
            title: 'Tuarascáil dheiridh chuig CSIRT (1 mhí)',
            description: 'Cuirfidh eintitis tuarascáil dheiridh isteach chuig an CSIRT inniúil tráth nach déanaí ná mí amháin tar éis fógra an teagmhais.',
        },
        c8512bde_b064_4926_b333_06b9c95cecd7: {
            title: 'Cinneadh infheidhmeachta do ghníomhaíochtaí intleachta saorga',
            description: 'A chinneadh an mbíonn an eagraíocht i mbun gníomhaíochtaí intleachta saorga atá faoi réir Dhlí Intleachta Saorga Vítneam agus an cinneadh infheidhmeachta a dhoiciméadú.',
        },
        '3a87ad63_179a_45ba_8743_98d865df80ab': {
            title: 'Aicmiú riosca córas intleachta saorga de réir leibhéil riosca',
            description: 'Gach córas intleachta saorga a aicmiú de réir leibhéil riosca (íosta, teoranta, ard, do-ghlactha) le modheolaíocht aicmithe threoraithe.',
        },
        '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
            title: 'Cothabháil iontrála i gClár na gCóras Intleachta Saorga',
            description: 'Clár ar chórais intleachta saorga a chothabháil le hainm córais, úinéir, cuspóir, comhthéacs imscartha, leibhéal riosca agus rannpháirtíocht díoltóra.',
        },
        '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
            title: 'Dosae um Chóras Intleachta Saorga le seicliosta déantán agus fianaise',
            description: 'Dosae um chóras a thógáil agus a chothabháil le seicliosta riachtanach déantán, leabharlann fianaise agus stair leagain.',
        },
        e92cfb42_d91b_444f_adbc_c03903844eac: {
            title: 'Oibleagáidí trédhearcachta d’oibreoirí córas intleachta saorga',
            description: 'Bearta trédhearcachta a chur i bhfeidhm do chórais intleachta saorga, lena n-áirítear fógra úsáideoirí, nochtadh rannpháirtíochta intleachta saorga agus doiciméadú córais.',
        },
        '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
            title: 'Lipéadú aschuir IS, comharthú uisce agus síniú meiteashonraí',
            description: 'Lipéadú agus comharthú uisce a chur i bhfeidhm d’ábhar a ghineann an intleacht shaorga, lena n-áirítear síniú meiteashonraí mar is infheidhme.',
        },
        '9566c94d_0dd3_419c_869f_6707d2a972f9': {
            title: 'Ceanglais nochta d’ábhar a ghineann an IS',
            description: 'Nochtadh d’úsáideoirí nuair a ghineann an intleacht shaorga an t-ábhar agus sásraí a sholáthar lenar féidir le húsáideoirí an rannpháirtíocht IS a aithint.',
        },
        '21d39ea5_df78_45c6_8710_b203db229e25': {
            title: 'Inmhínithe agus dualgais trédhearcachta atá os comhair an úsáideora',
            description: 'Aschuir inmhínithe a sholáthar do chinntí IS a bhaineann le daoine aonair agus doiciméadú trédhearcachta atá os comhair an úsáideora a chothabháil.',
        },
        f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
            title: 'Oibleagáidí maoirseachta daonna do chórais IS ardriosca',
            description: 'A chinntiú go bhfuil sásraí maoirseachta daonna i bhfeidhm do chórais IS ardriosca lena n-áirítear cumas géaraithe agus sárú.',
        },
        bd180a89_4976_4790_9e50_f1c5c97dc576: {
            title: 'Monatóireacht agus brath teagmhas IS',
            description: 'Cumais monatóireachta a chur i bhfeidhm chun teagmhais, mífheidhmeanna agus drochthorthaí córas IS a bhrath.',
        },
        e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
            title: 'Tuairisciú teagmhas tromchúiseach IS chuig an údarás',
            description: 'Teagmhais thromchúiseacha IS a thuairisciú don údarás inniúil laistigh den fhráma ama forordaithe (72 uair).',
        },
        '97f15adc_558a_49f2_bb06_86597cb9579b': {
            title: 'Comhlíonadh frámaí ama um fhógairt teagmhas IS',
            description: 'Cloí le frámaí ama fógartha do theagmhais IS lena n-áirítear an fógra tosaigh, nuashonruithe agus tuarascálacha dúnta.',
        },
        '96c33d7e_fe52_4730_adac_70c8c477c88e': {
            title: 'Measúnú tionchair IS do chórais ardriosca',
            description: 'Measúnuithe tionchair a dhéanamh do chórais IS ardriosca, ag clúdach rioscaí, maolaitheoirí, maoirseachta daonna agus pleananna monatóireachta.',
        },
        '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
            title: 'Cinneadh incháilitheachta tástála rialaithe / sandbox',
            description: 'Incháilitheacht don rannpháirtíocht i dtástáil rialaithe (sandbox) a chinneadh agus an measúnú incháilitheachta a dhoiciméadú.',
        },
        '4a6aed20_f655_4182_a760_aef1f329d5df': {
            title: 'Cur isteach iarratais sandbox agus rianú formheasta',
            description: 'Iarratais sandbox a chur isteach chuig an údarás inniúil agus stádas formheasta a rianú trí thimthriall saoil an tsuímh isteach.',
        },
        '8376988d_cd32_491e_a5b8_3340d3973358': {
            title: 'Pointí seiceála monatóireachta sandbox agus críochnú/scoir',
            description: 'Pointí seiceála monatóireachta tréimhsiúla a dhéanamh le linn rannpháirtíochta sandbox agus tuarascálacha críochnaithe/scoir a ullmhú.',
        },
        '7cb2b544_0512_4702_a092_9f1c10872376': {
            title: 'Doiciméadú díolúintí/eisceachtaí lipéadaithe le réasúnaíocht',
            description: 'Aon díolúintí nó eisceachtaí lipéadaithe a dhoiciméadú le réasúnaíocht agus fianaise tacaíochta.',
        },
        dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
            title: 'Logánú sonraí d’úsáideoirí Vítneamacha',
            description: 'Ní mór sonraí úsáideoirí Vítneamacha ar sheirbhísí a sholáthraítear in Vítneam a stóráil laistigh de chríoch Vítneam.',
        },
        '9eb86a03_e165_4963_8813_2272b638d529': {
            title: 'Tuairisciú teagmhas cibearshlándála',
            description: 'Ní mór teagmhais chibearshlándála a thuairisciú do na húdaráis inniúla go pras.',
        },
        '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
            title: 'Aicmiú córas faisnéise',
            description: 'Ní mór córais faisnéise a aicmiú de réir leibhéil tábhachta agus na bearta cuí slándála a chur i bhfeidhm.',
        },
        ffb807cf_f121_4b52_8e50_e091c33180c9: {
            title: 'Measúnú riosca cibearshlándála',
            description: 'Ní mór measúnuithe rialta riosca cibearshlándála a dhéanamh do chórais faisnéise.',
        },
        a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
            title: 'Cinneadh raon feidhme (crann cinnidh)',
            description: 'Ní mór d’eagraíochtaí a chinneadh an bhfuil siad faoi raon feidhme Foraithne 53 agus an crann cinnidh forordaithe in úsáid.',
        },
        '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
            title: 'Measúnú agus ullmhacht logánaithe sonraí',
            description: 'Ní mór d’eagraíochtaí ceanglais um logánú sonraí a mheasúnú agus ullmhacht a léiriú.',
        },
        cf24f011_dc23_4d84_aef5_994dde440571: {
            title: 'Seicliosta ullmhachta um stóráil agus coimeád',
            description: 'Ní mór seicliosta ullmhachta um stóráil agus coimeád a chomhlánú maidir le comhlíonadh logánaithe sonraí.',
        },
        b3884cb8_c785_4258_9517_9e393be4f4e1: {
            title: 'Measúnú ar cheanglas láithreachta áitiúla',
            description: 'Ní mór d’eagraíochtaí measúnú a dhéanamh ar an mbaineann ceanglais láithreachta áitiúla agus pleanáil dá réir.',
        },
        c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
            title: 'Glacadh agus fíorú iarrataí ón údarás',
            description: 'Ní mór iarrataí ón údarás a ghlacadh, a fhíorú agus a phróiseáil de réir nósanna imeachta forordaithe.',
        },
        d4571651_f39d_4c51_8f2d_7248c37f7b14: {
            title: 'Freagra ar iarraidh ón údarás laistigh den spriocdháta',
            description: 'Ní mór freagraí ar iarrataí ón údarás a sholáthar laistigh den spriocdháta forordaithe.',
        },
        '66c1df70_3456_40e9_b645_6c6bd3be702e': {
            title: 'Loga um láimhseáil iarraidh ón údarás',
            description: 'Ní mór loga ar gach gníomhaíocht láimhseála iarrata ón údarás a chothabháil.',
        },
        '04bbba2a_9804_4199_a517_054b6f4689fb': {
            title: 'Cinneadh infheidhmeachta agus raon feidhme (comhdhlúite)',
            description: 'Ní mór d’eagraíochtaí a chinneadh an bhfuil siad faoi raon feidhme an dlí chibearshlándála chomhdhlúite, ag clúdach raon feidhme an tseanDlí 24/2018 agus Foraithne 53/2022.',
        },
        '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
            title: 'Aicmiú córas faisnéise de réir leibhéal tábhachta',
            description: 'Ní mór córais faisnéise a aicmiú de réir leibhéal tábhachta (ginearálta, tábhachtach, criticiúil) le hoibleagáidí slándála comhfhreagracha.',
        },
        '83ad981e_23ff_4a92_bebd_cd24487e912f': {
            title: 'Struchtúr rialachais cibearshlándála agus pearsanra freagrach',
            description: 'Ní mór d’eagraíochtaí struchtúir rialachais cibearshlándála a bhunú agus pearsanra freagrach a ainmniú.',
        },
        '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
            title: 'Measúnú tréimhsiúil riosca cibearshlándála',
            description: 'Ní mór measúnuithe rialta riosca cibearshlándála a dhéanamh do na córais aicmithe faisnéise go léir.',
        },
        '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
            title: 'Bearta slándála in aghaidh aicme córais faisnéise',
            description: 'Ní mór bearta slándála teicniúla cuí a chur i bhfeidhm in aghaidh gach leibhéal aicmithe córas faisnéise.',
        },
        b69a8be2_ae0c_408c_b8f1_73b573eca876: {
            title: 'Rialú rochtana agus bainistíocht aitheantais',
            description: 'Ní mór bearta rialaithe rochtana agus bainistíochta aitheantais a chur i bhfeidhm do chórais faisnéise.',
        },
        ac774c26_feb7_4123_b799_c54f77bd87b5: {
            title: 'Logáil, monatóireacht agus rian iniúchóireachta',
            description: 'Ní mór do chórais faisnéise cumais logála, monatóireachta agus rian iniúchóireachta a chothabháil.',
        },
        a86cb01d_239b_44e2_9e09_e6973bcf9658: {
            title: 'Bainistíocht leochaileachtaí agus paisteáil',
            description: 'Ní mór próisis bhainistíochta leochaileachtaí agus paisteála a bhunú agus a chothabháil.',
        },
        '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
            title: 'Logánú sonraí d’úsáideoirí Vítneamacha',
            description: 'Ní mór sonraí úsáideoirí Vítneamacha ar sheirbhísí a sholáthraítear in Vítneam a stóráil laistigh de chríoch Vítneam.',
        },
        '8bee2b51_ace8_4118_89c8_010b96640016': {
            title: 'Ceanglais láithreachta áitiúla do sholáthraithe seirbhíse eachtrannacha',
            description: 'Ní mór do sholáthraithe seirbhíse eachtrannacha a fheidhmíonn in Vítneam láithreacht áitiúil a chothabháil mar atá forordaithe.',
        },
        '74d89250_b45f_41b6_bfe3_f915071580de': {
            title: 'Doiciméadú ailtireachta stórála áitiúla agus sreafaí trasteorann',
            description: 'Ní mór ailtireacht na stórála áitiúla a dhoiciméadú agus na sreabha sonraí trasteorann a léarscáiliú agus a rialú.',
        },
        '04dec6f8_3280_494c_8506_292804b16d4d': {
            title: 'Brath agus freagairt teagmhas cibearshlándála',
            description: 'Ní mór d’eagraíochtaí cumais a bheith acu chun teagmhais chibearshlándála a bhrath agus freagairt dóibh.',
        },
        faeda71b_5050_497d_b08b_e3f3f5313ad4: {
            title: 'Tuairisciú éigeantach teagmhas chuig an údarás inniúil',
            description: 'Ní mór teagmhais cháilithe chibearshlándála a thuairisciú do MPS/MIC laistigh de na frámaí ama forordaithe.',
        },
        d732b5a2_2b2c_48a0_955d_97a39daf132d: {
            title: 'Comhlíonadh fráma ama um fhógra teagmhas',
            description: 'Ní mór d’fhógraí teagmhas cloí leis na ceanglais um fhráma ama forordaithe (tuarascáil tosaigh laistigh de 24 uair, lántuarascáil laistigh de 72 uair).',
        },
        '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
            title: 'Láimhseáil iarrataí ón údarás agus comhoibriú',
            description: 'Ní mór iarrataí ón údarás a láimhseáil go pras agus comhoibriú a sholáthar de réir nósanna imeachta forordaithe.',
        },
        e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
            title: 'Clár cásanna iarrataí ón údarás agus loga nochta',
            description: 'Ní mór gach iarraidh ón údarás agus gach nochtadh a logáil i gclár cásanna le rian iniúchóireachta iomlán.',
        },
        '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
            title: 'Measúnú slándála díoltóra agus tríú páirtí',
            description: 'Ní mór do dhíoltóirí tríú páirtí a bhfuil rochtain acu ar chórais faisnéise dul faoi mheasúnú slándála.',
        },
        '69891e1b_97f6_464c_9cce_b1276794fe4a': {
            title: 'Rialuithe rochtana sonraí díoltóra agus fógra teagmhais',
            description: 'Ní mór rochtain sonraí díoltóra a dhoiciméadú agus oibleagáidí conarthacha um fhógra teagmhais a bhunú.',
        },
        d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
            title: 'Oiliúint feasachta slándála agus cleachtaí',
            description: 'Ní mór oiliúint rialta feasachta cibearshlándála agus cleachtaí boird a dhéanamh.',
        },
        f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
            title: 'Tástáil treáite tréimhsiúil agus iniúchóireacht slándála',
            description: 'Ní mór tástáil treáite tréimhsiúil agus iniúchtaí slándála a dhéanamh ar chórais aicmithe faisnéise.',
        },
        '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
            title: 'Fardal sonraí digiteacha a chothabháil',
            description: 'Ní mór d’eagraíochtaí fardal cuimsitheach sonraí digiteacha a chothabháil a chatalógann gach sócmhainn sonraí.',
        },
        '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
            title: 'Aicmiú sonraí de réir íogaireachta agus tábhachta',
            description: 'Ní mór sonraí a aicmiú de réir leibhéil íogaireachta agus tábhachta mar atá sainithe ag an Dlí Sonraí.',
        },
        b89981ac_7559_46f9_a74c_a143976dde4e: {
            title: 'Rialacha láimhseála a shainiú de réir leibhéil aicmithe',
            description: 'Ní mór rialacha láimhseála a bhunú do gach leibhéal aicmithe sonraí.',
        },
        '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
            title: 'Comhroinnt agus nochtadh sonraí a rianú le formheasanna',
            description: 'Ní mór gach gníomhaíocht chomhroinnte agus nochta sonraí a rianú le formheasanna cuí.',
        },
        '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
            title: 'Rialachas aistrithe sonraí trasteorann',
            description: 'Ní mór d’aistrithe sonraí trasteorann cloí le ceanglais rialachais faoin Dlí Sonraí.',
        },
        '006b8aa6_9379_4eff_b8ab_935f9a087601': {
            title: 'Beartais agus freagrachtaí rialachais a chothabháil',
            description: 'Ní mór d’eagraíochtaí beartais rialachais sonraí a chothabháil agus freagrachtaí rialachais a shannadh.',
        },
        f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
            title: 'Teastas rialachais bliantúil',
            description: 'Ní mór teastas bliantúil rialachais a chomhlánú chun comhlíonadh leanúnach a dheimhniú.',
        },
        f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
            title: 'Measúnú riosca sonraí',
            description: 'Ní mór measúnú riosca sonraí a dhéanamh chun rioscaí rialachais sonraí a aithint agus a mhaolú.',
        },
        '6f228998_1c61_49cf_a585_3d3e9baff55a': {
            title: 'Cuntasacht oibreoirí ardáin agus oibleagáidí rialachais',
            description: 'Struchtúir cuntasachta oibreoirí ardáin a bhunú lena n-áirítear beartais rialachais, freagrachtaí monatóireachta agus oibleagáidí forfheidhmithe.',
        },
        eee1d423_3783_4530_bc64_eef6fd9d02dc: {
            title: 'Foilsiú rialacha ardáin agus trédhearcacht faisnéise',
            description: 'Rialacha ardáin, coinníollacha trádála agus nochtadh oibríochtúil a fhoilsiú ar bhealach trédhearcach inrochtana.',
        },
        '50b57433_7a51_4e92_9c84_c748d6865b70': {
            title: 'Fógra suímh ghréasáin/feidhmchláir díolacháin r-tráchtála chuig MOIT',
            description: 'Fógra a chur isteach chuig MOIT do shuímh ghréasáin agus feidhmchláir dhíolacháin r-tráchtála trí Online.gov.vn nó cainéil ainmnithe.',
        },
        '92854abe_6654_4155_8495_f3419a8a8da0': {
            title: 'Clárú ardáin seirbhísí r-tráchtála/margaí le MOIT',
            description: 'Ardáin seirbhísí r-tráchtála agus margaí a chlárú le MOIT lena n-áirítear cur isteach an dosae agus rianú formheasta.',
        },
        b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
            title: 'Cothabháil chlár na ndíoltóirí (baile agus eachtrach)',
            description: 'Clár ar gach díoltóir ar an ardán a chothabháil lena n-áirítear díoltóirí baile agus eachtracha le hionghabháil, fíorú agus rianú timthriall saoil.',
        },
        '75778639_5235_4d13_a728_fa772c27fc0c': {
            title: 'Clár díoltóirí beochraolta agus rialuithe comhlíonta',
            description: 'Clár ar dhíoltóirí beochraolta a chothabháil le fíorú aitheantais, rianú gníomhaíochta agus rialuithe comhlíonta.',
        },
        '405f2932_742a_4bcb_9784_1a3d81f2e714': {
            title: 'Clár comhpháirtithe cleamhnaithe agus margaíochta',
            description: 'Clár ar mhargaitheoirí cleamhnaithe agus comhpháirtithe atreoraithe a chothabháil le stádas conartha, treoirlínte ábhair agus admhálacha comhlíonta.',
        },
        cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
            title: 'Liostú táirgí agus forfheidhmiú earraí toirmiscthe/coinníollacha',
            description: 'Rialuithe ar liostú táirgí a chur i bhfeidhm lena n-áirítear scagadh, monatóireacht agus gníomhaíochtaí forfheidhmithe ar earraí toirmiscthe agus coinníollacha.',
        },
        '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
            title: 'Clár gearán tomhaltóirí le hionghabháil, gníomhaíochtaí agus rian iniúchóireachta',
            description: 'Clár gearán a chothabháil le taifid ionghabhála, gníomhaíochtaí imscrúdaithe, torthaí réitigh agus rian iniúchóireachta iomlán.',
        },
        '8a21764e_e5f4_4934_9913_c235f4c462ca': {
            title: 'Nós imeachta um láimhseáil gearán agus frámaí ama réitigh',
            description: 'Nósanna imeachta um láimhseáil gearán a bhunú le frámaí ama sainithe le haghaidh admhála, imscrúdaithe agus réitigh.',
        },
        '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
            title: 'Modhnóireacht ábhair, monatóireacht agus forfheidhmiú baintí',
            description: 'Nósanna imeachta modhnóireachta ábhair, monatóireachta agus forfheidhmithe baintí a chur i bhfeidhm in aghaidh ábhair neamhdhleathaigh, thoirmiscthe nó shároitheacha.',
        },
        '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
            title: 'Clár gníomhartha baintí agus forfheidhmithe le caomhnú fianaise',
            description: 'Clár ar ghníomhartha baintí agus forfheidhmithe a chothabháil le taifid chinnidh, caomhnú fianaise agus fógraí do dhíoltóirí.',
        },
        c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
            title: 'Clár beartas agus nochtaí ardáin (leaganacha, dátaí foilsithe, glacadh)',
            description: 'Clár ar bheartais agus nochtaí an ardáin a chothabháil le stair leagain, dátaí foilsithe agus fianaise glactha.',
        },
        c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
            title: 'Dualgais um fhaisnéis tomhaltóirí — eolas cruinn ar tháirge agus frith-mheabhlaireacht',
            description: 'A chinntiú go bhfuil faisnéis tháirge agus seirbhíse cruinn agus soiléir agus bearta a chur i bhfeidhm chun cleachtais mheabhlacha ar an ardán a chosc.',
        },
        '8f49603b_454c_4470_ab07_2f845a447595': {
            title: 'Coinníollacha oibríochta agus clárú/fógra d’ardáin eachtracha',
            description: 'Cloí leis na coinníollacha oibríochta d’ardáin eachtracha a fhreastalaíonn ar úsáideoirí Vítneamacha lena n-áirítear ceanglais um chlárú nó fógra.',
        },
        e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
            title: 'Ionadaíocht áitiúil nó údarás d’ardáin eachtracha',
            description: 'Ionadaí áitiúil nó eintiteas údaraithe in Vítneam a ainmniú d’ardáin eachtracha mar a éilítear leis an dlí.',
        },
        '5719f47c_f298_429e_90c3_df6ceed856e1': {
            title: 'Comhordú láimhseála díoltóirí trasteorann agus gearán',
            description: 'Láimhseáil díoltóirí trasteorann agus réiteach gearán a chomhordú lena n-áirítear géarú agus bainistíocht fianaise.',
        },
        c0b3db29_9161_4cef_a889_94e3b85d78fe: {
            title: 'Idirghníomhaíocht leis an gcóras stáit um bhainistíocht r-tráchtála agus ullmhacht tuairiscithe',
            description: 'Ullmhú don idirghníomhaíocht leis an gcóras stáit um bhainistíocht r-tráchtála lena n-áirítear léarscáiliú réimsí sonraí agus cumas tuairiscithe.',
        },
        '145a7016_720a_4a06_badd_00145842821e': {
            title: 'Fógra um athrú ábhartha tar éis formheasta',
            description: 'Fógraí a chur isteach maidir le hathruithe ábhartha ar shonraí, beartais nó oibríochtaí an ardáin tar éis an chéad fhormheasa.',
        },
        '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
            title: 'Fógra um fhoirceannadh agus díghníomhachtú',
            description: 'Fógraí foirceannta nó díghníomhachtaithe a chur isteach agus oibríochtaí r-tráchtála á scor lena n-áirítear fógra poiblí agus láimhseáil cosanta tomhaltóirí.',
        },
        be95bc85_2b25_4de7_a3e7_154b780289e4: {
            title: 'Incháilitheacht agus cothabháil chláir mharcanna iontaoibhe agus rátála inchreidteachta',
            description: 'Incháilitheacht le haghaidh clár marcanna iontaoibhe agus rátála inchreidteachta a mheasúnú agus fianaise comhlíonta a chothabháil le haghaidh rannpháirtíochta leanúnaí.',
        },
        f639afe2_18f2_4c0a_82d9_626308994267: {
            title: 'Toiliú do phróiseáil sonraí pearsanta',
            description: 'Éilíonn próiseáil sonraí pearsanta toiliú an ábhair sonraí mura mbaineann díolúine.',
        },
        '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
            title: 'Sensitive personal data: tell the data subject the data is sensitive',
            description: 'For processing sensitive personal data, the data subject must be told that the data to be processed is sensitive personal data (Art. 11(8)). Art. 28 adds the measures for protecting it: applying the measures of Art. 26(2) and Art. 27; designating a unit with the function of protecting personal data and staff in charge of it, and exchanging their details with the specialised personal data protection agency; and notifying the data subject that their sensitive personal data is being processed, except in the cases of Art. 13(4), Art. 17 and Art. 18.',
        },
        '5a958c76_6943_4449_a15c_ddb503ce526f': {
            title: 'An ceart rochtana',
            description: 'Tá sé de cheart ag ábhair shonraí rochtain a fháil ar a sonraí pearsanta atá ag an rialaitheoir sonraí.',
        },
        '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
            title: 'An ceart ceartúcháin',
            description: 'Tá sé de cheart ag ábhair shonraí ceartú sonraí pearsanta míchruinne a iarraidh.',
        },
        '25c61e57_273f_48cb_a318_d6315ad744f1': {
            title: 'An ceart léirscriosta',
            description: 'Tá sé de cheart ag ábhair shonraí léirscriosadh a sonraí pearsanta a iarraidh.',
        },
        '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
            title: 'An ceart srian a chur ar phróiseáil',
            description: 'Tá sé de cheart ag ábhair shonraí srian a iarraidh ar phróiseáil a sonraí pearsanta.',
        },
        '77f9677a_f436_43ec_a947_3bdc44fc192a': {
            title: 'An ceart agóide',
            description: 'Tá sé de cheart ag ábhair shonraí agóid a dhéanamh in aghaidh phróiseála a sonraí pearsanta.',
        },
        c8b19e70_782c_49c9_8a71_14bef4147d6b: {
            title: 'An ceart toiliú a tharraingt siar',
            description: 'Tá sé de cheart ag ábhair shonraí toiliú a tugadh roimhe sin a tharraingt siar tráth ar bith.',
        },
        '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
            title: 'Fógra sáraithe chuig an údarás (72 uair)',
            description: 'Ní mór don rialaitheoir sonraí an Aireacht Slándála Poiblí a chur ar an eolas laistigh de 72 uair tar éis do shárú sonraí pearsanta a aimsiú.',
        },
        '98bed7ab_e814_43f5_8e70_e95c413266a1': {
            title: 'Fógra sáraithe chuig na hábhair shonraí',
            description: 'Ní mór don rialaitheoir sonraí na hábhair shonraí dá ndeachaigh sé i bhfeidhm a chur ar an eolas faoi shárú sonraí pearsanta gan moill mhíchuí.',
        },
        '404a3576_dd84_4bac_acf4_29719615c45e': {
            title: 'DPIA do phróiseáil ardriosca',
            description: 'Ní mór Measúnú Tionchair ar Chosaint Sonraí a dhéanamh do ghníomhaíochtaí próiseála ar dócha go mbeidh ardriosca mar thoradh orthu.',
        },
        '29d9ed94_9360_457c_9aca_37aefac66969': {
            title: 'Measúnú aistrithe trasteorann',
            description: 'Ní mór measúnú tionchair a dhéanamh sula n-aistrítear sonraí pearsanta lasmuigh de Vítneam.',
        },
        '9620ff08_b83c_4e1f_9350_eba835b145fe': {
            title: 'Taifead ar ghníomhaíochtaí próiseála',
            description: 'Ní mór do rialaitheoirí sonraí Taifead ar Ghníomhaíochtaí Próiseála (RoPA) a chothabháil ina ndoiciméadaítear na gníomhaíochtaí próiseála go léir.',
        },
        '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
            title: 'Taifid ar chomhaontuithe próiseála sonraí',
            description: 'Ní mór taifid ar chomhaontuithe próiseála sonraí le próiseálaithe agus fo-phróiseálaithe a chothabháil.',
        },
        '434dc319_1922_49e4_b757_37622135c4b2': {
            title: 'Coimircí d’aistrithe trasteorann',
            description: 'Éilíonn aistrithe sonraí pearsanta lasmuigh de Vítneam coimircí cuí agus measúnuithe doiciméadaithe.',
        },
        '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
            title: 'Admháil laistigh de 2 lá oibre',
            description: 'Ní mór admháil a thabhairt ar iarrataí ó ábhair shonraí laistigh de 2 lá oibre tar éis a fála.',
        },
        b15fa62d_3f52_4e42_8442_f4969e08883a: {
            title: 'Críochnú laistigh de 15 lá oibre',
            description: 'Ní mór iarrataí ó ábhair shonraí a chríochnú laistigh de 15 lá oibre, agus síneadh féideartha 5 lá oibre breise.',
        },
        '5d81233c_f671_4827_b874_827762edfc20': {
            title: 'Comhdú dosae um mheasúnú tionchair MoPS',
            description: 'Ní mór d’eagraíochtaí a dhéanann próiseáil ardriosca dosae um mheasúnú tionchair a chomhdú leis an Aireacht Slándála Poiblí agus an nós imeachta riaracháin forordaithe in úsáid.',
        },
        '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
            title: 'Comhdú dosae um aistriú trasteorann MoPS',
            description: 'Ní mór d’eagraíochtaí a aistríonn sonraí pearsanta lasmuigh de Vítneam dosae um mheasúnú tionchair aistrithe trasteorann a chomhdú leis an Aireacht Slándála Poiblí.',
        },
        '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
            title: 'Deimhniú soláthraithe seirbhíse próiseála',
            description: 'Ní mór d’eagraíochtaí a sholáthraíonn próiseáil sonraí pearsanta mar sheirbhís deimhniú próiseála seirbhíse a fháil ón Aireacht Slándála Poiblí.',
        },
        b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
            title: 'Dosae um mheasúnú tionchair próiseála nuair a thugtar truicir',
            description: 'Ní mór dosae um mheasúnú tionchair próiseála a chomhdú nuair a chomhlíontar na truicir fhorordaithe.',
        },
        '9d34a206_b85e_4649_a23b_e2c6f1398402': {
            title: 'Dosae um thionchar aistrithe trasteorann',
            description: 'Ní mór dosae um thionchar aistrithe trasteorann a ullmhú agus a chomhdú le haghaidh aistrithe sonraí idirnáisiúnta.',
        },
        '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
            title: 'Ainmniú oifigeach um chosaint sonraí',
            description: 'Ní mór oifigeach um chosaint sonraí a ainmniú mar a éilítear le Foraithne 13.',
        },
        '776183b2_2797_40e8_8d81_c4f512c566d2': {
            title: 'Déantáin rialachais (beartais, róil, nósanna imeachta)',
            description: 'Ní mór déantáin rialachais lena n-áirítear beartais, róil agus nósanna imeachta a chothabháil.',
        },
        '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
            title: 'Iomláine an phacáiste fianaise agus rian iniúchóireachta dothraochta',
            description: 'Ní mór do phacáistí fianaise a bheith iomlán agus rian iniúchóireachta dothraochta a chothabháil.',
        },
        f6826e26_8991_4a0f_9117_481490230bdd: {
            title: 'Tacaíocht do shreabhadh oibre cás comhdaithe',
            description: 'Ní mór tacú le sreabha oibre cás comhdaithe le haghaidh dosaethanna measúnaithe tionchair agus aistrithe trasteorann.',
        },
        '8a28ee33_305a_491f_a93f_90e59f8da91b': {
            title: 'Ceanglais chonarthacha próiseálaithe',
            description: 'Ní mór ceanglais chonarthacha do phróiseálaithe sonraí a bhunú agus a mhonatóireacht.',
        },
        '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
            title: 'Athbhreithniú comhlíonta bliantúil',
            description: 'Ní mór athbhreithniú comhlíonta bliantúil a dhéanamh chun comhlíonadh leanúnach a fhíorú.',
        },
        '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
            title: 'Taifid ar chur isteach comhdaithe leis an údarás',
            description: 'Ní mór taifid ar gach cur isteach comhdaithe leis an údarás a chothabháil.',
        },
        e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
            title: 'Bunúis dhleathacha do phróiseáil sonraí pearsanta',
            description: 'Ní mór do phróiseáil sonraí pearsanta a bheith bunaithe ar bhunúis dhleathacha mar atá sainithe ag an PDPL.',
        },
        c919c66c_6a74_4403_bb97_04b773d270bf: {
            title: 'Sensitive personal data: identify it, protect it, and say so when seeking consent',
            description: "Law 91/2025/QH15 defines sensitive personal data in Article 2(3) as personal data tied to an individual's privacy which, when infringed, directly affects the lawful rights and interests of agencies, organisations and individuals, on a list the Government issues; Decree 356/2025/NĐ-CP Article 4(1) is that list. The Law attaches no consent rule to sensitive personal data as a class; sector articles carry their own. Article 26(1)(a) requires the data subject's consent in collecting and processing personal data for health information and in insurance business, except in the Article 19(1) cases. Article 27(1)(b) forbids using a data subject's credit information for credit scoring, credit rating, credit information assessment or creditworthiness assessment without their consent. Article 31(3)(a) forbids location tracking by radio-frequency identification and other technologies, except with the data subject's consent, at the request of a competent authority as provided by law, or where the law provides otherwise. Among what the instruments attach to sensitive data: in processing it, an agency or organisation must set rules limiting access rights, a processing procedure and security measures (Decree 356 Article 4(2)); when consent to process it is sought, the data subject must be told that the data to be processed is sensitive personal data (Decree 356 Article 6(4)); a transfer of it must carry physical security for storage and transmission devices, encryption, anonymisation and other security measures (Decree 356 Article 7(2)); an organisation or individual in finance, banking or credit information must fully comply with the rules on protecting sensitive personal data (Law 91 Article 27(1)(a)), and where a data subject's sensitive data in that sector is disclosed or lost, whoever directly collected it must notify the specialised personal data protection agency and the data subject within 72 hours of discovering it (Decree 356 Article 8(3)); and a small business, start-up, household business or micro-enterprise that directly processes sensitive personal data does not have the option or exemption Law 91 Article 38(2)–(3) and Decree 356 Article 41 give from Articles 21 and 22 and Article 33(2) of the Law. Record which of the organisation's personal data falls on that list.",
        },
        '419cc7c3_a257_4293_a054_304dc2fcb8df': {
            title: 'Cearta ábhar sonraí (rochtain, ceartú, léirscriosadh, srianadh, agóid, iniomparthacht)',
            description: 'Tá cearta cuimsitheacha ag ábhair shonraí lena n-áirítear rochtain, ceartú, léirscriosadh, srianadh, agóid agus iniomparthacht.',
        },
        '638e6e25_3a74_4864_8bff_8e21ab82860b': {
            title: 'Fógra sáraithe chuig an údarás',
            description: 'Ní mór do rialaitheoirí sonraí an t-údarás inniúil a chur ar an eolas faoi sháruithe sonraí pearsanta laistigh den fhráma ama forordaithe.',
        },
        '6e75c111_66b1_4726_8c27_6537846edb30': {
            title: 'Fógra sáraithe chuig na hábhair shonraí',
            description: 'Ní mór do rialaitheoirí sonraí na hábhair shonraí dá ndeachaigh sé i bhfeidhm a chur ar an eolas faoi sháruithe sonraí pearsanta.',
        },
        f686123d_3bdd_4a61_a53c_efe5b52d9058: {
            title: 'Measúnú tionchair ar chosaint sonraí',
            description: 'Ní mór measúnú tionchair ar chosaint sonraí a dhéanamh do ghníomhaíochtaí próiseála ardriosca.',
        },
        '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
            title: 'Rialachas aistrithe trasteorann',
            description: 'Ní mór d’aistrithe sonraí pearsanta trasteorann cloí le ceanglais rialachais.',
        },
        cf9a5b29_46fd_4072_b877_13b8a69df82c: {
            title: 'Ainmniú oifigeach um chosaint sonraí',
            description: 'Ní mór d’eagraíochtaí oifigeach um chosaint sonraí a ainmniú mar is gá.',
        },
        f5a4965d_eb0c_47df_9933_f185ae46463b: {
            title: 'Taifid ar ghníomhaíochtaí próiseála',
            description: 'Ní mór d’eagraíochtaí taifid ar gach gníomhaíocht phróiseála a chothabháil.',
        },
        ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
            title: 'Oibleagáidí conarthacha próiseálaithe',
            description: 'Ní mór oibleagáidí conarthacha a bhunú le próiseálaithe sonraí.',
        },
        '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
            title: 'Comhdú dosae um mheasúnú tionchair',
            description: 'Ní mór dosaethanna measúnaithe tionchair a chomhdú leis an údarás inniúil mar atá forordaithe.',
        },
        '65dba62f_0788_44c1_89e3_09d802e28eb9': {
            title: 'Dosae um thionchar aistrithe trasteorann',
            description: 'Ní mór dosae um thionchar aistrithe trasteorann a ullmhú agus a chomhdú le haghaidh aistrithe sonraí idirnáisiúnta.',
        },
        '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
            title: 'Ceapadh DPO agus fógra',
            description: 'Ní mór oifigeach um chosaint sonraí a cheapadh agus an t-údarás a chur ar an eolas.',
        },
        '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
            title: 'Beartais agus nósanna imeachta rialachais',
            description: 'Ní mór beartais agus nósanna imeachta rialachais a bhunú agus a chothabháil.',
        },
        '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
            title: 'Pacáiste fianaise agus rian iniúchóireachta dothraochta',
            description: 'Ní mór do phacáistí fianaise a bheith iomlán agus rian iniúchóireachta dothraochta a chothabháil.',
        },
        '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
            title: 'Timthriall saoil cás comhdaithe',
            description: 'Ní mór timthriallta saoil cás comhdaithe a bhainistiú trí na céimeanna forordaithe.',
        },
        '83f2f346_47bb_4377_a711_3cef26742f39': {
            title: 'Ceanglais chonarthacha próiseálaithe',
            description: 'Ní mór ceanglais chonarthacha do phróiseálaithe sonraí a bhunú agus a mhonatóireacht.',
        },
        cb972e06_f773_492e_8ad5_f65d1e999b79: {
            title: 'Deimhniú soláthraithe próiseála seirbhíse',
            description: 'Ní mór d’eagraíochtaí a sholáthraíonn seirbhísí próiseála sonraí deimhniú a fháil.',
        },
        '4ba36ab5_39f0_4d79_927e_c39587bed226': {
            title: 'Taifid ar chur isteach comhdaithe leis an údarás',
            description: 'Ní mór taifid ar gach cur isteach comhdaithe leis an údarás a chothabháil.',
        },
        f7a62c88_3cda_4031_bc52_1598661a5128: {
            title: 'Athbhreithniú comhlíonta bliantúil',
            description: 'Ní mór athbhreithniú comhlíonta bliantúil a dhéanamh chun comhlíonadh leanúnach a fhíorú.',
        },
        '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
            title: 'Bearta teicniúla slándála sonraí',
            description: 'Ní mór bearta teicniúla slándála sonraí a chur i bhfeidhm agus a chothabháil.',
        },
        '45f4e745_a923_4879_94ce_5a2b41b7e886': {
            title: 'Ceanglais foirme oifigiúla Mẫu số ó Fhoraithne 356',
            description: 'Ní mór na 13 fhoirm oifigiúla Mẫu số um nósanna imeachta riaracháin (Mẫu số 01a–10) atá forordaithe ag Foraithne 356/2025/NĐ-CP a úsáid le haghaidh na gcomhdaithe comhfhreagracha leis an Aireacht Slándála Poiblí. Is léarscáileanna inmheánacha struchtúir sonraí iad na míreanna Phụ lục I–VIII, ní foirmeacha rialála oifigiúla.',
        },
        ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
            title: 'Cur isteach dosae DPIA chuig A05',
            description: 'Ní mór d’eagraíochtaí dosaethanna DPIA a chur isteach chuig A05/MPS le haghaidh gníomhaíochtaí próiseála a bhaineann le sonraí pearsanta íogaire nó próiseáil ar mhórscála.',
        },
        '003600ae_7a0f_4034_b206_72f59409b8f3': {
            title: 'Cur isteach dosae aistrithe trasteorann',
            description: 'Ní mór d’eagraíochtaí dosaethanna aistrithe sonraí trasteorann a chur isteach lena n-athbhreithniú agus lena bhformheas ag A05/MPS.',
        },
        '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
            title: 'Iarratas ar dheimhniú seirbhíse próiseála',
            description: 'Ní mór do sholáthraithe seirbhísí próiseála sonraí iarratas a dhéanamh ar dheimhnithe ó A05/MPS.',
        },
        be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
            title: 'Fógra um phróiseáil sonraí pearsanta',
            description: 'Ní mór do rialaitheoirí A05/MPS a chur ar an eolas faoi ghníomhaíochtaí próiseála sonraí pearsanta agus na foirmeacha forordaithe in úsáid (Mẫu số 01a/01b).',
        },
        '39f50471_27cc_46bd_acff_9aa2211d43ab': {
            title: 'Tuairisciú staide bliantúil',
            description: 'Ní mór do rialaitheoirí tuarascálacha bliantúla um staid phróiseála sonraí pearsanta a chur isteach (Mẫu số 09).',
        },
        '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
            title: 'Rúndacht faisnéise úsáideora ar an réamhshocrú',
            description: 'Ní mór faisnéis úsáideora a láimhseáil mar fhaisnéis rúnda ar an réamhshocrú.',
        },
        '2994510e_b1f8_4cdd_947c_d9542746433b': {
            title: 'Bunrialuithe eagrúcháin agus teicniúla cosanta',
            description: 'Ní mór bunrialuithe eagrúcháin agus teicniúla cosanta a chur i bhfeidhm.',
        },
        '8643160f_9a10_4d93_baf5_082c6a7c768a': {
            title: 'Nochtadh ach amháin le bunús cuí',
            description: 'Ní cheadaítear nochtadh faisnéise úsáideora ach amháin le bunús dlí cuí.',
        },
        c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
            title: 'Logáil éigeantach nochta agus iniúchacht',
            description: 'Ní mór gach nochtadh a logáil agus a bheith iniúchta.',
        },
        efa36bc0_abae_417a_b676_9509af37f89d: {
            title: 'Sreabhadh oibre formheasta inmheánaigh do nochtaí',
            description: 'Ní mór sreabhadh oibre formheasta inmheánaigh a bhunú do gach nochtadh.',
        },
        d086fdcd_9a23_4500_893c_db8ac62bd787: {
            title: 'Láimhseáil freagra ar iarraidh ón údarás',
            description: 'Ní mór iarrataí ón údarás a láimhseáil agus freagra a thabhairt orthu de réir nósanna imeachta forordaithe.',
        },
    },
    framework_rights_descriptions: {
        be_informed: 'Tá sé de cheart ag an ábhar sonraí eolas a fháil faoi ghníomhaíochtaí próiseála sonraí pearsanta.',
        consent: 'Tá sé de cheart ag an ábhar sonraí toiliú a thabhairt do phróiseáil a sonraí pearsanta.',
        refuse_consent: 'Tá sé de cheart ag an ábhar sonraí gan toiliú a thabhairt do phróiseáil a sonraí pearsanta.',
        withdraw_consent: 'Tá sé de cheart ag an ábhar sonraí tarraingt siar an toilithe a tugadh roimhe sin a iarraidh.',
        access: 'Tá sé de cheart ag an ábhar sonraí a sonraí pearsanta atá ag an rialaitheoir a fheiceáil agus rochtain a fháil orthu.',
        rectification: 'Tá sé de cheart ag an ábhar sonraí sonraí pearsanta míchruinne a cheartú nó a gceartú a iarraidh.',
        data_provision: 'Tá sé de cheart ag an ábhar sonraí soláthar a sonraí pearsanta a iarraidh (yêu cầu cung cấp dữ liệu cá nhân).',
        deletion: 'Tá sé de cheart ag an ábhar sonraí léirscriosadh nó scrios sonraí pearsanta a iarraidh, faoi réir eisceachtaí an dlí.',
        restriction_of_processing: 'Tá sé de cheart ag an ábhar sonraí srian ar phróiseáil a sonraí pearsanta a iarraidh.',
        object_to_processing: 'Tá sé de cheart ag an ábhar sonraí iarraidh a sheoladh ag déanamh agóide in aghaidh phróiseála a sonraí pearsanta.',
        complaint: 'Tá sé de cheart ag an ábhar sonraí gearán a thaisceadh (khiếu nại) maidir le próiseáil sonraí pearsanta.',
        denunciation: 'Tá sé de cheart ag an ábhar sonraí nochtadh (tố cáo) a dhéanamh maidir le sáruithe ar chosaint sonraí pearsanta.',
        lawsuit: 'Tá sé de cheart ag an ábhar sonraí caingean dlí (khởi kiện) a thionscnamh i leith sáruithe ar a chearta cosanta sonraí.',
        claim_damages: 'Tá sé de cheart ag an ábhar sonraí cúiteamh a éileamh as damáiste de bharr sáruithe ar chosaint sonraí pearsanta.',
        request_protection_measures: 'Tá sé de cheart ag an ábhar sonraí a iarraidh ar údaráis inniúla nó ar eagraíochtaí/dhaoine ábhartha bearta a chur i bhfeidhm chun a sonraí pearsanta a chosaint.',
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Bainistíocht riosca agus monatóireacht (dréacht)',
            steps: {
                '1': 'Catagóirí riosca a aithint',
                '2': 'Rialuithe luathfholáirimh a chothabháil',
                '3': 'Athbhreithniú a dhéanamh ar rioscaí bailithe, stórála, próiseála, comhroinnte agus idirbhirt',
                '4': 'Rioscaí príobháideachais, cibearshlándála agus bainistithe rochtana a thaifeadadh',
                '5': 'Gníomhaíochtaí leasaithe a thruicearú',
                '6': 'Stair athbhreithnithe a chaomhnú',
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Gearáin agus díospóidí (dréacht)',
            steps: {
                '1': 'Gearán / iarraidh dhíospóide a ghlacadh isteach',
                '2': 'Iomláine na faisnéise riachtanaí a sheiceáil; forlíonadh a iarraidh laistigh de 24 uair má bhíonn sí neamhiomlán',
                '3': 'Laistigh de 3 lá oibre tar éis iarraidh iomlán a fháil, na páirtithe a chur ar an eolas faoi chur chuige na láimhseála agus an fhráma ama mar a bhfuiltear ag súil leis',
                '4': 'Freagraí agus doiciméid tacaíochta a iarraidh',
                '5': 'Fíricí an dhíospóid agus freagracht an ardáin a mheasúnú',
                '6': 'Toradh an réitigh a thaifeadadh',
                '7': 'Fianaise a chaomhnú agus an cás a dhúnadh',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Seachadadh agus deimhniú um chearta maoine ar shonraí (dréacht)',
            steps: {
                '1': 'Táirge/seirbhís a sheachadadh de réir mhodh an chonartha',
                '2': 'Imeacht agus am seachadta a thaifeadadh',
                '3': 'Deimhniú um chearta maoine ar shonraí a ghiniúint',
                '4': 'Críochnú an idirbhirt a chlárú',
                '5': 'Lorgaireacht/fíorú níos déanaí a chumasú nuair is gá',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Tionscnamh idirbhirt agus conarthaíocht (dréacht)',
            steps: {
                '1': 'Athbhreithniú a dhéanamh ar an liostú agus ar thoradh an mheasúnaithe',
                '2': 'Sreabhadh oibre ceannaigh, idirbheartaíochta nó ceanta a thionscnamh',
                '3': 'Conradh leictreonach a ghiniúint',
                '4': 'Glacadh an dá pháirtí a dhaingniú',
                '5': 'Críochnú íocaíochta a thaifeadadh',
                '6': 'Seachadadh a údarú',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Rochtain ar shandbox / timpeallacht tástála (dréacht)',
            steps: {
                '1': 'Iarraidh sandbox a chur isteach',
                '2': 'Raon feidhme, sonraí ceadaithe, tréimhse ama agus ceadanna próiseála a shainiú',
                '3': 'Timpeallacht theicniúil aonraithe a sholáthar',
                '4': 'Monatóireacht a dhéanamh ar shlándáil agus rochtain le linn na tástála',
                '5': 'Aschur a athbhreithniú sula mbaintear é',
                '6': 'Eastóscadh a fhormheas ach amháin san áit a gceadaítear é',
                '7': 'Seisiún tástála a dhúnadh agus an timpeallacht a ghlanadh',
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Liostú táirgí / seirbhísí sonraí (dréacht)',
            steps: {
                '1': 'Iarratas liostaithe a chruthú',
                '2': 'Fianaise foinse agus cearta úinéireachta/úsáide a ghabháil',
                '3': 'Saintréithe teicniúla, formáid, sonraí API agus srianta a thaifeadadh',
                '4': 'Nochtuithe cáilíochta a thaifeadadh',
                '5': 'Dosae liostaithe a chur isteach lena athbhreithniú ag an ardán',
                '6': 'Déanann an t-ardán seiceálacha iomláine, dlíthiúlachta agus teicniúla',
                '7': 'Liostú a fhormheas nó a dhiúltú',
                '8': 'Má fhormheastar, an liostú a fhoilsiú leis an toradh measúnaithe ag gabháil leis',
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Ionghabháil rannpháirtithe (dréacht)',
            steps: {
                '1': 'Cineál an rannpháirtí a chinneadh: díoltóir nó ceannaitheoir',
                '2': 'Stádas dlíthiúil agus cumas dlíthiúil a fhíorú',
                '3': 'Coinníollacha breise a fhíorú má bhaineann línte gnó coinníollacha',
                '4': 'Cuntas idirbhirt a chlárú',
                '5': 'Cuntas íocaíochta a nascadh',
                '6': 'Rannpháirtíocht ardáin a fhormheas',
                '7': 'Taifead an rannpháirtí agus rian iniúchóireachta a stóráil',
            },
        },
        AI_A1: {
            name: 'Clárú / Fógra Córais Intleachta Saorga',
            steps: {
                '1': 'Seiceáil infheidhmeachta agus aicmiú córais IS',
                '2': 'Dosae cláraithe/fógartha a thógáil',
                '3': 'Formheas inmheánach',
                '4': 'Cur isteach trí chainéal',
                '5': 'Lúb leasaithe — má thugtar ar ais é, é a nuashonrú agus a chur isteach arís',
                '6': 'Dúnadh agus deimhniú a stóráil',
            },
        },
        AI_A2: {
            name: 'Sreabhadh Oibre um Aicmiú Riosca IS',
            steps: {
                '1': 'Iontráil sa Chlár um Chórais IS a chruthú',
                '2': 'An draoi aicmithe a rith',
                '3': 'Fianaise a cheangal',
                '4': 'Aicmiú a fhormheas',
                '5': 'Aschuir a nascadh leis an dosae córais',
            },
        },
        AI_A3: {
            name: 'Comhlíonadh Lipéadaithe agus Nochta',
            steps: {
                '1': 'Oibleagáidí lipéadaithe a aithint de réir córais agus cáis úsáide',
                '2': 'Fógraí lipéadaithe a chur i bhfeidhm',
                '3': 'Fianaise a ghabháil — scáileáin, cóip an chomhéadain, meiteashonraí teicniúla',
                '4': 'Díolúintí a láimhseáil agus an réasúnaíocht a thaifeadadh',
                '5': 'Formheas agus leagan',
            },
        },
        AI_A4: {
            name: 'Tuairisciú Teagmhas Tromchúiseach',
            steps: {
                '1': 'Taifead Teagmhais IS a chruthú',
                '2': 'Géire a aicmiú',
                '3': 'Gníomhartha coscartha agus gabháil fianaise a chur i gcrích',
                '4': 'Easpórtáil teimpléid Tuarascáil ar Theagmhas Tromchúiseach a ghiniúint',
                '5': 'Cur isteach chuig an údarás trí chainéal',
                '6': 'Freagraí agus dúnadh an údaráis a rianú',
            },
        },
        AI_A5: {
            name: 'Measúnú Tionchair IS',
            steps: {
                '1': 'Taifead measúnaithe a chruthú',
                '2': 'Na rannóga riachtanacha a líonadh',
                '3': 'Fianaise a cheangal — sreafaí sonraí, doiciméadú samhla, rialuithe',
                '4': 'Formheas inmheánach',
                '5': 'Tuarascáil a easpórtáil agus a stóráil sa dosae',
            },
        },
        AI_A6: {
            name: 'Tástáil Rialaithe / Rannpháirtíocht Sandbox',
            steps: {
                '1': 'Taifead incháilitheachta agus ullmhachta a chomhlánú',
                '2': 'Iarratas agus easpórtáil a ullmhú',
                '3': 'Cur isteach trí chainéal',
                '4': 'Formheas a rianú',
                '5': 'Pointí seiceála monatóireachta agus logaí a chur i gcrích',
                '6': 'Tuarascáil chríochnaithe agus pacáiste dúnta a chomhlánú',
            },
        },
        CYBERSEC_C1: {
            name: 'Measúnú Slándála ar Chóras Faisnéise Criticiúil',
            steps: {
                '1': 'Córais faisnéise a aicmiú de réir ceanglais náisiúnta cibearshlándála',
                '2': 'Measúnú slándála agus meastóireacht riosca a dhéanamh do na córais aicmithe',
                '3': 'Bearta riachtanacha slándála agus bunrialuithe a chur i bhfeidhm',
                '4': 'Fianaise comhlíonta a dhoiciméadú agus athbhreithnithe tréimhsiúla a sceidealú',
            },
        },
        CYBERSEC_C2: {
            name: 'Fógra um Theagmhas Cibearshlándála',
            steps: {
                '1': 'An teagmhas cibearshlándála a bhrath, a aicmiú agus a mheasúnú',
                '2': 'Gníomhaíochtaí freagartha láithreacha agus caomhnú fianaise a chur i gcrích',
                '3': 'Tuairisciú don údarás agus rannpháirtíocht i gcomhordú náisiúnta um fhreagairt teagmhas',
                '4': 'Athbhreithniú iarthacaiste a dhéanamh agus na ceachtanna a foghlaimíodh a dhoiciméadú',
            },
        },
        CYBERSEC_C3: {
            name: 'Fíorú Comhlíonta Logánaithe Sonraí',
            steps: {
                '1': 'Measúnú a dhéanamh an mbaineann truicir um logánú sonraí bunaithe ar chineál seirbhíse agus catagóirí sonraí',
                '2': 'Ailtireacht stórála áitiúla agus rialuithe sreabhaidh trasteorann a fhíorú',
                '3': 'Ceanglais láithreachta áitiúla (brainse / oifig ionadaíoch) a mheasúnú nuair is infheidhme',
                '4': 'Fianaise comhlíonta a dhoiciméadú agus a chothabháil le haghaidh athbhreithnithe ag an údarás',
            },
        },
        CYBERSEC_C4: {
            name: 'Freagra ar Iarraidh Comhoibrithe ón Údarás',
            steps: {
                '1': 'An iarraidh ón údarás a fháil agus dlistineacht agus raon feidhme a fhíorú',
                '2': 'An fhaisnéis a iarradh a bhailiú agus a ullmhú nó na gníomhartha riachtanacha a chur i gcrích',
                '3': 'Freagra a chur isteach chuig an údarás laistigh den fhráma ama riachtanach',
                '4': 'An nochtadh a logáil agus an cás a dhúnadh',
            },
        },
        CYBERSEC_C5: {
            name: 'Fíorú Cáilíochtaí Pearsanra Cibearshlándála',
            steps: {
                '1': 'Staid slándála an díoltóra a mheasúnú leis an gcreat measúnaithe cibearshlándála',
                '2': 'Raon feidhme rochtana sonraí an díoltóra a fhíorú agus a chlárú i loga rochtana sonraí an díoltóra',
                '3': 'Athbhreithniú a dhéanamh ar chlásal fógra teagmhais an díoltóra agus ceanglais chonarthacha cibearshlándála',
                '4': 'Athbhreithnithe tréimhsiúla slándála ar dhíoltóirí a sceidealú agus monatóireacht a dhéanamh ar chomhlíonadh',
            },
        },
        DATA_D1: {
            name: 'Bunú Fardail agus Aicmithe Sonraí',
            steps: {
                '1': 'Gach sócmhainn sonraí a chatalógú le cineálacha agus foinsí i gclár fardail sonraí',
                '2': 'Gach sócmhainn a aicmiú de réir leibhéil íogaireachta',
                '3': 'Rialacha láimhseála a shainiú in aghaidh leibhéil aicmithe',
                '4': 'Fianaise a ghabháil — scáileáin, léaráidí ailtireachta, rialuithe rochtana',
                '5': 'Athbhreithnithe tréimhsiúla a sceidealú',
            },
        },
        DATA_D2: {
            name: 'Rialachas Comhroinnte Sonraí',
            steps: {
                '1': 'Socruithe nua comhroinnte a mheasúnú trí mheasúnú riosca um chomhroinnt sonraí',
                '2': 'Sreabhadh oibre formheasta inmheánaigh a chomhlánú',
                '3': 'Imeacht comhroinnte a thaifeadadh i loga comhroinnte/nochta sonraí',
                '4': 'Fianaise a ghabháil — taifid fhormheasa, doiciméid chonarthacha',
                '5': 'Athbhreithniú tréimhsiúil ar shocruithe comhroinnte',
            },
        },
        DATA_D3: {
            name: 'Measúnú Aistrithe Sonraí Trasteorann',
            steps: {
                '1': 'Aistrithe trasteorann sa bhfardal sonraí a aithint',
                '2': 'Measúnú riosca aistrithe a dhéanamh',
                '3': 'Coimircí agus bearta rialachais a chur i bhfeidhm',
                '4': 'Doiciméadú i rannóg trasteorann an teastas rialachais',
                '5': 'Pacáiste measúnaithe a easpórtáil le haghaidh iniúchóireachta',
            },
        },
        DATA_D4: {
            name: 'Teastas Rialachais Bliantúil',
            steps: {
                '1': 'Athbhreithniú a dhéanamh ar mhaitrís RACI an rialachais',
                '2': 'Stádas an bheartais a fhíorú (rialachas, aicmiú, comhroinnt, coimeád)',
                '3': 'Pacáiste teastaithe a chomhlánú',
                '4': 'Formheas inmheánach a fháil',
                '5': 'An teastas a easpórtáil agus a chartlannú',
            },
        },
        DATA_D5: {
            name: 'Láimhseáil Teagmhas Sonraí',
            steps: {
                '1': 'An teagmhas a aicmiú trí thaifead cinnidh',
                '2': 'Freagairt teagmhais a chur i gcrích de réir an leabhar imeachtaí',
                '3': 'Fianaise a ghabháil — logaí, scáileáin, cumarsáidí',
                '4': 'Athbhreithniú iarthacaiste a dhéanamh',
                '5': 'Má bhíonn fógra don údarás riachtanach: cás cur isteach a chruthú agus é a chur isteach chuig MIC',
                '6': 'Dúnadh agus cartlannú',
            },
        },
        ECOM_P1: {
            name: 'Fógra (Suíomh Gréasáin/Feidhmchlár Díolacháin)',
            steps: {
                '1': 'Aicmiú a chinneadh: suíomh/feidhmchlár díolacháin i gcomparáid le margadh/ardán seirbhíse',
                '2': 'Taifead inmheánach dosae a chruthú le haitheantas cuideachta, sonraí an tsuímh/fheidhmchláir agus na nochtaí riachtanacha',
                '3': 'Pacáiste fianaise a ghiniúint — scáileáin nochta, URLanna beartais, stair leagain',
                '4': 'Cur isteach trí Online.gov.vn — doiciméid a uaslódáil, an stampa ama agus an deimhniú a thaifeadadh',
                '5': 'Próiseáil a rianú — má thugtar ar ais le haghaidh leasaithe, na cúiseanna a ghabháil agus athchur',
                '6': 'Dúnadh — an deimhniú a stóráil, fianaise agus rian iniúchóireachta a nascadh',
            },
        },
        ECOM_P2: {
            name: 'Clárú (Ardáin Seirbhíse / Margaí)',
            steps: {
                '1': 'Samhail an ardáin agus oibleagáidí a chinneadh — margadh i gcomparáid le measctha agus le tráchtáil shóisialta',
                '2': 'Dosae cláraithe a thógáil — foirm iarratais, scéim soláthair seirbhíse, T&Cs, rialacha an ardáin',
                '3': 'Formheasanna inmheánacha — úinéir dlí/comhlíonta a shannadh, é a fhormheas le cur isteach',
                '4': 'Cur isteach trí Online.gov.vn — an dosae a uaslódáil, an stampa ama a thaifeadadh',
                '5': 'Lúb próiseála — má thugtar ar ais le haghaidh leasaithe, é a nuashonrú agus a chur isteach arís',
                '6': 'Formheas agus oibríochtú — an deimhniú a stóráil, cláir leanúnacha comhlíonta a chothabháil',
            },
        },
        ECOM_P3: {
            name: 'Fógra um Nuashonrú/Athrú',
            steps: {
                '1': 'Truicear athraithe a aithint — athrú ábhartha ar mhíreanna an dosae',
                '2': 'Pacáiste athraithe a ghiniúint — achoimre an athraithe, doiciméid nuashonraithe, nuashonruithe fianaise',
                '3': 'An nuashonrú a chur isteach agus stádas an chur isteach a thaifeadadh',
                '4': 'Próiseáil a rianú agus dúnadh',
            },
        },
        ECOM_P4: {
            name: 'Foirceannadh / Díghníomhachtú',
            steps: {
                '1': 'Pacáiste foirceanta a ullmhú — dáta scoir, cúis, plean láimhseála cosanta tomhaltóirí, cruthúnas fógra phoiblí',
                '2': 'An foirceannadh a chur isteach — an stampa ama agus an deimhniú a thaifeadadh',
                '3': 'Dúnadh — an stádas críochnaitheach agus an fhianaise a stóráil',
            },
        },
        ECOM_P5: {
            name: 'Clár Marcanna Iontaoibhe / Rátála Inchreidteachta',
            steps: {
                '1': 'Athbhreithniú incháilitheachta agus ullmhachta',
                '2': 'Dosae a ullmhú agus an fhoirm oifigiúil in úsáid',
                '3': 'Cur isteach agus rianú',
                '4': 'Fianaise leanúnach comhlíonta a chothabháil',
            },
        },
        PDPL_P1: {
            name: 'Comhdú DPIA chuig MPS',
            steps: {
                '1': 'DPIA a chomhlánú agus conair foirme Foraithne 356 in úsáid (Mẫu số 02a/02b)',
                '2': 'Doiciméid chumhdaigh agus fianaise tacaíochta a chur le chéile',
                '5': 'An dosae agus fianaise an chur isteach a choinneáil don rian iniúchóireachta',
            },
        },
        PDPL_P2: {
            name: 'Iarratas ar Aistriú Trasteorann',
            steps: {
                '1': 'Dosae aistrithe trasteorann a chomhlánú agus foirmeacha Foraithne 356 in úsáid (Mẫu số 01a/01b, Mẫu số 09)',
            },
        },
        PDPL_P3: {
            name: 'Iarratas ar Dheimhniú Seirbhíse Próiseála Sonraí',
            steps: {
                '1': 'Dosae iarratais um dheimhniú a ullmhú de réir cheanglais Foraithne 356',
                '2': 'An t-iarratas a chur isteach chuig MPS leis na doiciméid tacaíochta riachtanacha',
                '3': 'Freagra a thabhairt ar bharúlacha athbhreithnithe MPS agus iarrataí forlíontacha',
            },
        },
        PDPL_P4: {
            name: 'Próiseáil Iarrata Cearta Ábhar Sonraí',
            steps: {
                '1': 'An iarraidh a fháil agus a logáil; aitheantas an ábhair sonraí a fhíorú laistigh de 2 lá oibre',
                '2': 'Cineál na hiarrata a mheasúnú agus an spriocdháta is infheidhme a chinneadh (10/15/20 lá de réir cineáil)',
                '3': 'Gníomhaíochtaí cur i gcrích a dhéanamh thar chórais agus ranna ábhartha',
                '4': 'Freagra a ullmhú agus a sheoladh chuig an ábhar sonraí leis an toradh agus an réasúnaíocht',
                '5': 'An cás a dhúnadh agus fianaise a choinneáil le haghaidh iniúchóireachta; aon réasúnaíocht diúltaithe a dhoiciméadú',
            },
        },
        PDPL_P5: {
            name: 'Fógra Sáraithe chuig MPS',
            steps: {
                '1': 'An sárú a bhrath agus a mheasúnú; a chinneadh an bhfuil an oibleagáid fógartha i bhfeidhm agus an spriocdháta fógra is infheidhme a fháil ó na taifid spriocdhátaí atá suiteáilte',
                '2': 'Pacáiste fógra a ullmhú de réir cheanglais an PDPL',
                '3': 'An fógra a chur isteach chuig MPS laistigh den spriocdháta fógra a eascraíonn as na taifid spriocdhátaí atá suiteáilte, arna áireamh ón aimsiú',
                '4': 'Freagra a thabhairt ar iarrataí leantacha MPS; forlíonadh agus fianaise comhordaithe a chur isteach',
            },
        },
        TELECOM_T1: {
            name: 'Láimhseáil Iarrata ón Údarás',
            steps: {
                '1': 'An iarraidh ón údarás a fháil agus a logáil trí thaifead ionghabhála',
                '2': 'Aitheantas an údaráis, bunús dlí agus raon feidhme na hiarrata a fhíorú',
                '3': 'Athbhreithniú dlíthiúil inmheánach a dhéanamh agus formheas bainistíochta a fháil',
                '4': 'Pacáiste freagra a ullmhú leis an bhfianaise tiomsaithe',
                '5': 'Litir fhreagra a dhréachtú agus a fhormheas',
                '6': 'Cás cur isteach a chruthú agus an freagra a chur isteach trí chainéal',
                '7': 'An nochtadh a logáil agus admháil an údaráis a rianú',
                '8': 'An cás a dhúnadh agus fianaise a choinneáil don rian iniúchóireachta',
            },
        },
        TELECOM_T2: {
            name: 'Bainistíocht Nochta',
            steps: {
                '1': 'Iarraidh nochta a fháil (toiliú úsáideora, oibleagáid dlí, nó iarraidh ón údarás)',
                '2': 'Sreabhadh oibre formheasta nochta a chomhlánú',
                '3': 'An nochtadh a chur i gcrích agus a thaifeadadh sa chlár loga',
                '4': 'Fianaise a ghabháil — taifid fhormheasa, taifid chumarsáide',
                '5': 'Athbhreithniú tréimhsiúil ar an loga nochta',
            },
        },
        TELECOM_T3: {
            name: 'Bunú Bheartais Rúndachta agus Rialaithe Rochtana',
            steps: {
                '1': 'Beartas rúndachta a dhréachtú agus a fhormheas',
                '2': 'Beartas rialaithe rochtana agus logála a dhréachtú agus a fhormheas',
                '3': 'Gníomhaíochtaí próiseála a dhoiciméadú i gclár próiseála faisnéise úsáideora',
                '4': 'Fianaise a ghabháil — doiciméid bheartais, fianaise cur i bhfeidhm',
                '5': 'Athbhreithnithe tréimhsiúla a sceidealú',
            },
        },
        TELECOM_T4: {
            name: 'Measúnú Teagmhas Teileachumarsáide',
            steps: {
                '1': 'An teagmhas a aicmiú agus a mheasúnú',
                '2': 'Ceanglais fógra a chinneadh',
                '3': 'Má bhíonn fógra don údarás riachtanach: cás cur isteach a chruthú',
                '4': 'Fianaise a ghabháil agus a chaomhnú',
                '5': 'Réiteach agus dúnadh',
            },
        },
        TELECOM_T5: {
            name: 'Bainistíocht Coimeádta agus Léirscriosta',
            steps: {
                '1': 'Sceideal coimeádta agus léirscriosta a shainiú in aghaidh chatagóir sonraí',
                '2': 'Nósanna imeachta léirscriosta a chur i bhfeidhm',
                '3': 'Fianaise a ghabháil — taifid léirscriosta',
                '4': 'Athbhreithniú tréimhsiúil ar sceidil',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Clárú / Fógra / Ullmhacht Riaracháin',
            'ECOM-FINE-B': 'Gearáin agus Cosaint Tomhaltóirí',
            'ECOM-FINE-C': 'Monatóireacht agus Forfheidhmiú Baintí',
            'ECOM-FINE-D': 'Oibríochtaí Trasteorann',
            'ECOM-FINE-E': 'Rialacha agus Trédhearcacht an Ardáin',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Teipeanna Láimhseála Iarrataí ón Údarás',
            'TELECOM-FINE-B': 'Rúndacht agus Nochtadh Neamhúdaraithe',
            'TELECOM-FINE-C': 'Teipeanna Rialaithe Rochtana agus Logála',
            'TELECOM-FINE-D': 'Sáruithe Coimeádta agus Léirscriosta',
            'TELECOM-FINE-E': 'Teipeanna Measúnaithe Teagmhas Teileachumarsáide',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: 'Suas le 100 milliún VND',
                fine_org: 'Suas le 200 milliún VND',
                deadlines: 'Láithreach tar éis braite',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: 'Suas le 100 milliún VND',
                fine_org: 'Suas le 200 milliún VND',
                deadlines: 'Oibleagáid leanúnach',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: 'Suas le 100 milliún VND',
                fine_org: 'Suas le 200 milliún VND',
                deadlines: 'Oibleagáid leanúnach',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: 'Suas le 100 milliún VND',
                fine_org: 'Suas le 200 milliún VND',
                deadlines: 'Oibleagáid leanúnach',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: 'Suas le 100 milliún VND',
                fine_org: 'Suas le 200 milliún VND',
                deadlines: 'Mar a shonraítear san iarraidh ón údarás',
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: 'Suas le 80 milliún VND',
                fine_org: 'Suas le 160 milliún VND',
                deadlines: 'Oibleagáid leanúnach',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: 'Suas le 80 milliún VND',
                fine_org: 'Suas le 160 milliún VND',
                deadlines: 'Oibleagáid leanúnach',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: 'Suas le 80 milliún VND',
                fine_org: 'Suas le 160 milliún VND',
                deadlines: 'Oibleagáid leanúnach',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: 'Suas le 80 milliún VND',
                fine_org: 'Suas le 160 milliún VND',
                deadlines: 'Mar a shonraítear in oibleagáidí láimhseála gearán',
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: 'Suas le 80 milliún VND',
                fine_org: 'Suas le 160 milliún VND',
                deadlines: 'Sula gcuirfear tús le hoibríochtaí r-tráchtála',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Oibríocht leanúnach an ardáin',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Oibreoir Ardáin / Riosca',
                '2': 'Oibreoir Ardáin / Slándáil',
                '3': 'Oibreoir Ardáin / Riosca',
                '4': 'Oibreoir Ardáin / Slándáil',
                '5': 'Oibreoir Ardáin',
                '6': 'Comhlíonadh',
            },
            step_outputs: {
                '1': ['Risk assessment record'],
                '2': ['Early warning log'],
                '3': ['Risk review findings'],
                '4': ['Risk register entries'],
                '5': ['Remediation record'],
                '6': ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'Cuireann rannpháirtí gearán nó iarraidh dhíospóide isteach',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Oibreoir Ardáin',
                '2': 'Oibreoir Ardáin',
                '3': 'Oibreoir Ardáin',
                '4': 'Oibreoir Ardáin',
                '5': 'Oibreoir Ardáin / Dlí',
                '6': 'Oibreoir Ardáin',
                '7': 'Comhlíonadh',
            },
            step_outputs: {
                '1': ['Complaint intake record'],
                '2': ['Completeness check', 'Supplement request (if any)'],
                '3': ['Handling approach notice'],
                '4': ['Evidence pack'],
                '5': ['Assessment findings'],
                '6': ['Dispute resolution record'],
                '7': ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'Críochnaítear seachadadh tar éis comhlíonta na gcoinníollacha conartha agus íocaíochta',
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Díoltóir / Oibreoir Ardáin',
                '2': 'Oibreoir Ardáin',
                '3': 'Oibreoir Ardáin',
                '4': 'Oibreoir Ardáin',
                '5': 'Oibreoir Ardáin',
            },
            step_outputs: {
                '1': ['Delivery record'],
                '2': ['Delivery event log entry'],
                '3': ['Property rights certificate'],
                '4': ['Transaction register entry'],
                '5': ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'Tionscnaíonn ceannaitheoir idirbheart le haghaidh sonraí, táirge nó seirbhíse',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Ceannaitheoir',
                '2': 'Ceannaitheoir / Oibreoir Ardáin',
                '3': 'Oibreoir Ardáin',
                '4': 'Ceannaitheoir / Díoltóir',
                '5': 'Oibreoir Ardáin / Íocaíocht',
                '6': 'Oibreoir Ardáin',
            },
            step_outputs: {
                '1': ['Listing review decision'],
                '2': ['Transaction initiation record'],
                '3': ['Electronic contract record'],
                '4': ['Contract acceptance record'],
                '5': ['Payment confirmation'],
                '6': ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'Ba mhaith le rannpháirtí táirgí/seirbhísí sonraí a mheasúnú sa timpeallacht aonraithe',
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Rannpháirtí',
                '2': 'Oibreoir Ardáin / Rannpháirtí',
                '3': 'Oibreoir Ardáin / TF',
                '4': 'Oibreoir Ardáin / Slándáil',
                '5': 'Oibreoir Ardáin',
                '6': 'Oibreoir Ardáin',
                '7': 'Oibreoir Ardáin / TF',
            },
            step_outputs: {
                '1': ['Sandbox request'],
                '2': ['Sandbox scope record'],
                '3': ['Provisioned environment'],
                '4': ['Security monitoring log'],
                '5': ['Output review record'],
                '6': ['Extraction approval decision'],
                '7': ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'Iarrann díoltóir sonraí, táirge nó seirbhís sonraí a liostú le trádáil',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Díoltóir',
                '2': 'Díoltóir / Dlí',
                '3': 'Díoltóir / TF',
                '4': 'Díoltóir',
                '5': 'Díoltóir',
                '6': 'Oibreoir Ardáin',
                '7': 'Oibreoir Ardáin',
                '8': 'Oibreoir Ardáin',
            },
            step_outputs: {
                '1': ['Listing application'],
                '2': ['Lawful-source verification record'],
                '3': ['Technical validation record'],
                '4': ['Quality disclosure record'],
                '5': ['Submitted listing dossier'],
                '6': ['Review findings'],
                '7': ['Listing approval record'],
                '8': ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'Téann díoltóir nó ceannaitheoir isteach san ardán sonraí',
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Oibreoir Ardáin',
                '2': 'Oibreoir Ardáin / Dlí',
                '3': 'Oibreoir Ardáin / Dlí',
                '4': 'Oibreoir Ardáin',
                '5': 'Oibreoir Ardáin',
                '6': 'Oibreoir Ardáin',
                '7': 'Comhlíonadh',
            },
            step_outputs: {
                '1': ['Participant type decision'],
                '2': ['Legal capacity verification record'],
                '3': ['Conditional-business check record'],
                '4': ['Transaction account registration record'],
                '5': ['Payment account link record'],
                '6': ['Participation approval record'],
                '7': ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: 'Tús le próiseáil sonraí pearsanta a éilíonn measúnú tionchair',
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Foireann Príobháideachais',
                '2': 'Foireann Príobháideachais',
                '3': 'Dlí / Comhlíonadh',
                '4': 'DPO / Foireann Príobháideachais',
                '5': 'Foireann Comhlíonta',
            },
            step_outputs: {
                '1': ['Completed DPIA dossier'],
                '2': ['Dossier package', 'Cover letter'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Updated dossier'],
                '5': ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: 'Rún sonraí pearsanta a aistriú lasmuigh de Vítneam',
            outputs: ['Transfer dossier', 'Submission confirmation', 'Supplement response'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Foireann Príobháideachais',
                '2': 'Dlí / Comhlíonadh',
                '3': 'DPO / Foireann Príobháideachais',
            },
            step_outputs: {
                '1': ['Transfer dossier package'],
                '2': ['Submission confirmation', 'Tracking reference'],
                '3': ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Eagraíocht a sholáthraíonn seirbhísí próiseála sonraí a éilíonn deimhniú',
            outputs: ['Certification dossier', 'Certificate', 'Re-issue and exchange record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Dlí / Comhlíonadh',
                '2': 'Dlí / Comhlíonadh',
                '3': 'DPO / Foireann Príobháideachais',
                '4': 'Foireann Comhlíonta',
            },
            step_outputs: {
                '1': ['Certification application dossier'],
                '2': ['Submission confirmation'],
                '3': ['Supplement response'],
                '4': ['Certificate record', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: 'Glacadh iarrata um chearta ábhar sonraí',
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                '1': 'Foireann Príobháideachais / Seirbhís Custaiméirí',
                '2': 'DPO / Foireann Príobháideachais',
                '3': 'Cinnirí Roinne / TF',
                '4': 'DPO / Foireann Príobháideachais',
                '5': 'Foireann Comhlíonta',
            },
            step_outputs: {
                '1': ['DSR intake record', 'Identity verification record'],
                '2': ['Request assessment', 'Deadline assignment'],
                '3': ['Fulfillment evidence', 'Department confirmations'],
                '4': ['Response letter'],
                '5': ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: 'Aimsiú sáraithe nó bhriste ar chosaint sonraí pearsanta',
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Foireann Freagartha Teagmhas / DPO',
                '2': 'DPO / Foireann Príobháideachais',
                '3': 'Dlí / Comhlíonadh',
                '4': 'DPO / Foireann Freagartha Teagmhas',
            },
            step_outputs: {
                '1': ['Breach assessment record', 'Notification decision record'],
                '2': ['Breach notification package'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL Airt. 9',
        'PDPL Art. 17': 'PDPL Airt. 17',
        'PDPL Art. 23': 'PDPL Airt. 23',
        'Decree 13 Art. 24': 'Foraithne 13 Art. 24',
        'MoPS Admin Procedures': 'Nósanna Imeachta Riaracháin MoPS',
        'Annual cycle': 'Timthriall bliantúil',
        'Decree 13/2023/NĐ-CP Art. 24': 'Foraithne 13/2023/NĐ-CP Art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Foraithne 356/2025/NĐ-CP Art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Foraithne 356/2025/NĐ-CP Art. 5 Cl. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Foraithne 356/2025/NĐ-CP Art. 5 Cl. 2–5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Foraithne 356/2025/NĐ-CP Art. 5 Cl. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Foraithne 356/2025/NĐ-CP Art. 5 Cl. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Foraithne 356/2025/NĐ-CP Art. 5 Cl. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Foraithne 356/2025/NĐ-CP Art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Dréachtfhoraithne maidir le hArdáin Sonraí 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Dlí 122/2025/QH15 Caib. III; NĐ 98/2020; NĐ 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Dlí 122/2025/QH15 Caib. IV (Cosaint Tomhaltóirí)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Dlí 122/2025/QH15 — nós imeachta oibríochtúil Online.gov.vn',
        'Law 134/2025/QH15 Article 12': 'Dlí 134/2025/QH15 Airteagal 12',
        'Law 134/2025/QH15 Article 21': 'Dlí 134/2025/QH15 Airteagal 21',
        'Law 134/2025/QH15 Article 27': 'Dlí 134/2025/QH15 Airteagal 27',
        'Law 134/2025/QH15 Articles 9–10': 'Dlí 134/2025/QH15 Airteagail 9–10',
        'Law 24/2023/QH15 — article mapping pending': 'Dlí 24/2023/QH15 — léarscáiliú airteagal ar feitheamh',
        'Law 60/2024/QH15 (internal control target)': 'Dlí 60/2024/QH15 (sprioc rialaithe inmheánaigh)',
        'Law 91/2025/QH15 Art. 21': 'Dlí 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Dlí 91/2025/QH15 Art. 23',
        'Policy-defined': 'Sainithe ag beartas',
        Quarterly: 'Ráithiúil',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'QĐ 778/QĐ-BCA-A05 nós imeachta riaracháin',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': 'sprioc rialaithe inmheánaigh (Dlí 116/2025/QH15 Art. 24(2)(b), 41(5) — fráma ama beacht ar feitheamh ionstraime cur i bhfeidhm)',
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': 'sprioc rialaithe inmheánaigh (Dlí 116/2025/QH15 Art. 41(3) — fráma ama beacht ar feitheamh ionstraime cur i bhfeidhm)',
        'source verification pending': 'fíorú foinse ar feitheamh',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': 'Circular 83/2025/TT-NHNN — Tuairisciú Iniúchta Inmheánaigh Ad-hoc chuig SBV (Sraith 5)',
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Tuarascáil Bhliantúil ICAAP (Phụ lục III) (Sraith 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Phụ lục I Bliantúil (Sraith 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Phụ lục IV Bliantúil (Sraith 5)',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Tuarascáil Bhliantúil ar Bhainistíocht Riosca (Phụ lục II) (Sraith 5)',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Freagracht as Bainistíocht Sócmhainní (Sraith 2, téama C)',
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": 'Circular 83/2025/TT-NHNN — Maoirseacht ón mBord / ó Chomhairle na mBall (Sraith 2, téama A)',
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Coistí an Bhoird (Sraith 2, téama A)',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Cinntí agus Miontuairiscí an Bhoird (Sraith 2, téama A)',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Nósanna Imeachta Rialaithe Brainse (Sraith 2, téama C)',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Measúnú Comhlíontachta ar Rialacha Inmheánacha (Sraith 2, téama D)',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Feidhm Chomhlíontachta (Sraith 2, téama D)',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Monatóireacht ar Leigheas Comhlíontachta (Sraith 2, téama D)',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Riosca Comhchruinnithe (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Coinbhleacht Leasa (Sraith 2, téama C)',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Cultúr Rialaithe (Sraith 2, téama A/B)',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Riosca Creidmheasa (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Údarás Tarmligthe (Sraith 2, téama C)',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN — Fógra um Chur Chun Feidhme Luath (Sraith 5)',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Freagracht an Ard-Stiúrthóra (Sraith 2, téama A)',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Riosca Ráta Úis sa Leabhar Baincéireachta (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Cairt an Iniúchta Inmheánaigh (Sraith 2, téama H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN — Coimisiúin Iniúchta Inmheánaigh (Téama H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Cumarsáid ar Thorthaí an Iniúchta Inmheánaigh (Sraith 2, téama H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN — Torthaí an Iniúchta Inmheánaigh agus Bearta Ceartaitheacha (Téama H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN — Leantóireacht an Iniúchta Inmheánaigh (Téama H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN — Rialachas an Iniúchta Inmheánaigh',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Neamhspleáchas an Iniúchta Inmheánaigh (Sraith 2, téama H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN — Plean Iniúchta Inmheánaigh (Sraith 2/Téama H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Tuairisciú an Iniúchta Inmheánaigh chuig SBV (Sraith 2, téama H)',
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Rialachas an Chórais Rialaithe Inmheánaigh (Sraith 2)',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Nós Imeachta Tuairiscithe Inmheánaigh (Sraith 2, téama C)',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Clár Tuarascálacha Inmheánacha (Sraith 4, téama F)',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Monatóireacht ar Athruithe Dlí (Sraith 2, téama D)',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Riosca Dlí agus Cáile (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Géarúchán ar Sárú Teorann (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Riosca Leachtachta (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Athbhreithniú Bliantúil MIS (Sraith 4, téama F)',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Cúltaca agus Leanúnachas MIS (Sraith 4, téama F)',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Meicníochtaí Géarúcháin MIS (Sraith 4, téama F)',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Iontaofacht agus Rúndacht MIS (Sraith 4, téama F)',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Foinsí Sonraí Riosca MIS (Sraith 4, téama F)',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN — Córas Faisnéise Bainistíochta (Sraith 4)',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Riosca Margaidh (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Clár Riosca Ábhartha (Sraith 3)',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Riosca Múnla (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Athbhreithniú ar Tháirge Nua (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Riosca Oibríochtúil (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Plean Leanúnachais Gnó don tSeachfhoinsiú (Sraith 2, téama G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Conarthaí Seachfhoinsithe (Sraith 2, téama G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN — Rialuithe Seachfhoinsithe',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Dícheall Cuí an tSeachfhoinsithe (Sraith 2, téama G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Soláthraí Cúltaca Seachfhoinsithe (Sraith 2, téama G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Ábharthacht an tSeachfhoinsithe (Sraith 2, téama G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Monatóireacht ar Sheachfhoinsiú (Sraith 2, téama G)',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Tagairtí Teicniúla Phụ lục V/VI (Sraith 5)',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Clár Beartas (Sraith 2, téama C)',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Eitic Ghairmiúil (Sraith 2, téama C)',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Goile Riosca (Sraith 3)',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Comhordú an Choiste Riosca (Sraith 2, téama E)',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Rialachas Sonraí Riosca (Sraith 3, téama E/F) — céimnithe go 2028-01-01',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Athbhreithniú ar Theorainneacha Riosca (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Creat Teorainneacha Riosca (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Feidhm Bainistíochta Riosca (Sraith 2, téama E)',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Modheolaíocht Riosca agus Múnlaí (Sraith 2, téama E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Tuairisciú Riosca (Sraith 2, téama E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Minicíocht an Tuairiscithe Riosca (Sraith 4, téama F)',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN — Aighneacht Leighis chuig SBV (Sraith 5)',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — Cainéil Aighneachta SBV (Sraith 5)',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Infheidhmeacht Earnála (Sraith 1)',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Deighilt Dualgas (Sraith 2, téama C)',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Maoirseacht na hArdbhainistíochta (Sraith 2, téama A)',
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Riosca Straitéiseach (Sraith 3, téama E)',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Tástáil Strus (Sraith 3, téama E) — céimnithe go 2028-01-01',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Bord Maoirseachta (Sraith 2, téama A)',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Clár Torthaí Maoirseachta (Sraith 4, téama F)',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Róil agus Géarúchán na dTrí Líne (Sraith 2, téama B)',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — Trí Líne Cosanta (Sraith 2)',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Géarúchán Sáraithe (Sraith 2, téama D)',
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (Tionól Náisiúnta)',
        'Ministry of Public Security / A05 Department': 'Aireacht Slándála Poiblí / Roinn A05',
        'Ministry of Public Security': 'Aireacht Slándála Poiblí',
        'Ministry of Information and Communications': 'Aireacht Faisnéise agus Cumarsáide',
        'Government of Vietnam': 'Rialtas Vítneam',
        'European Data Protection Board (EDPB)': 'An Bord Eorpach um Chosaint Sonraí (EDPB)',
        'ENISA / National CSIRTs': 'ENISA / CSIRTanna Náisiúnta',
        'State Bank of Vietnam': 'Banc Stáit Vítneam',
    },
    tour_titles: {
        access_accountability: 'Turas Cuntasachta Rochtana',
        assessments: 'Turas Measúnuithe',
        audit_trail: 'Turas Conaire Iniúchta',
        classification: 'Turas Aicmithe',
        compliance_forms: 'Foirmeacha Comhlíonta Turas',
        connectors: 'Nascóirí Turas',
        consent_properties: 'Toiliú Airíonna Turas',
        consent: 'Turas Toiliú',
        dashboard: 'Turas Painéal',
        data_mapping: 'Turas Mapála Sonraí',
        data_subjects: 'Turas na nÁbhar Sonraí',
        deletion_orchestration: 'Turas Ceolfhoirne Scriosta',
        discovery: 'Turas Fionnachtana',
        governance: 'Turas Rialachais',
        help_center: 'Turas Ionad Cabhrach',
        incident_ops: 'Turas Oibríochtaí Teagmhais',
        incidents: 'Turas Teagmhais',
        localization: 'Turas Logánaithe',
        monitoring: 'Turas Monatóireachta',
        onboarding: 'Turas bordála',
        organizations: 'Eagraíochtaí Turas',
        partner_workspace: 'Turas Spás Oibre Comhpháirtí',
        platform_settings: 'Turas Socruithe Ardáin',
        privacy_scanner: 'Turas Scanóir Príobháideachta',
        program_governance: 'Turas Rialachais Clár',
        rights_requests: 'Turas Iarratas ar Chearta',
        task_management: 'Turas Bainistíochta Tasc',
        vendor_risk: 'Turas Riosca Díoltóra',
    },
    tour_descriptions: {
        access_accountability: 'Mar an Ceannaire Iniúchta Inmheánaigh (P-VN-10), athbhreithniú a dhéanamh ar fhianaise rochtana, úsáid cead iniúchta, agus imscrúdaigh patrúin aimhrialta chun GDPR Airteagal 5(1)(f) sláine agus rúndacht a léiriú.',
        assessments: 'Mar a dhéanann an Fiontair DPO (P-VN-01), DPIAanna i ndiaidh Foraithne 356 Mau so 02a, bearnaí comhlíonta a shainaithint, agus measúnú a dhéanamh ar rioscaí aistrithe trasteorann faoi PDPL.',
        audit_trail: 'Mar Cheannaire an Iniúchta Inmheánaigh (P-VN-10), ceistigh fianaise tras-mhodúil, cruinnigh pacáistí iniúchta, agus easpórtálann logaí is léir go gcuirfear isteach orthu le haghaidh aighneachtaí rialála.',
        classification: 'Mar an Maor Sonraí (P-VN-05), sainmhínigh tacsanomaíochtaí aicmithe sonraí, sann lipéid íogaireachta, forfheidhmigh beartais choinneála, agus réitigh bearnaí cumhdaigh atá riachtanach do PDPL comhlíonadh.',
        compliance_forms: 'Mar an Enterprise DPO (P-VN-01), brabhsáil, comhlánaigh, agus cuir isteach foirmeacha rialála Vítneaimis oifigiúil lena n-áirítear Phu luc I, Phu luc II, agus Mau mar sin teimpléid le haghaidh comhduithe Coiste PDP.',
        connectors: 'Mar Cheannaire na gCóras TF (P-VN-05), gabháil le ceanglais ghnó maidir le comhtháthú, nascóirí a mhapáil le sreafaí sonraí comhlíonta, agus ullmhacht POC a fhíorú sula dtéann tú beo.',
        consent_properties: 'Mar an Enterprise DPO (P-VN-01), cumraigh CMP imscaradh: clárú maoine digiteach, teimpléid órga le haghaidh comhsheasmhach PDPL meirgí toilithe, agus maitrísí ceangailteacha a nascann airíonna le gníomhaíochtaí próiseála.',
        consent: "Mar an Enterprise DPO (P-VN-01), bainistigh saolré an bheartais toilithe: sainigh críocha bailiúcháin, cuir PDPL cearta aistarraingthe i bhfeidhm, agus comhtháthú le d'Ardán Bainistíochta Toiliú.",
        dashboard: 'Mar an Enterprise DPO (P-VN-01), treoraíonn tú féin timpeall an ionaid ordaithe staidiúir chomhlíonta a thugann dromchla ar PDPL spriocdhátaí, sárú CLSanna, agus KPIanna ullmhachta iniúchta.',
        data_mapping: "Mar an Maor Sonraí (P-VN-05), tóg d'fhardal ROPA, mapáil sreafaí sonraí trasteorann, agus coinnigh an clár gníomhaíochta próiseála a cheanglaítear le PDPL.",
        data_subjects: 'Mar Oifigeach Cosanta Sonraí Fiontair nó mar Cheannaire Comhlíonta Oideachais, bainistigh próifílí aontaithe ábhar sonraí a nascann taifid toilithe, iarratais ceart agus gníomhaíocht amlíne ar fud an ardáin.',
        deletion_orchestration: 'Mar an DPO (P-VN-01), fíoraigh scriosadh sonraí ó cheann ceann ar fud na gcóras inmheánach agus díoltóirí tríú páirtí, le slabhraí cruthúnais cripteagrafach do GDPR Airteagal 17 comhlíonadh ceart-scriosadh.',
        discovery: 'Mar an Maor Sonraí (P-VN-05), reáchtáil scananna uathoibrithe chun sonraí pearsanta a aimsiú thar chórais nasctha, déan athbhreithniú ar thorthaí céimnithe, agus cuir na torthaí isteach sa mhodúl Aicmithe le haghaidh PDPL lipéadú.',
        governance: 'Mar Bhainisteoir Riosca an Díoltóra (P-VN-08), an clár díoltóirí a chothabháil, slabhraí fophróiseálaithe a rianú, rioscaí aistrithe trasteorann a mheasúnú, agus leabhair imeartha rialachais a fhorfheidhmiú faoi PDPL.',
        help_center: 'Do gach duine -- OCSanna, iniúchóirí, ceannairí TF, agus bainisteoirí comhlíonta -- faigh freagraí, treoracha conas a dhéanamh, agus doiciméadú modúl-sonrach chun tacú le do sreabhadh oibre comhlíonta laethúil.',
        incident_ops: 'Mar Cheannasaí Teagmhais (P-VN-09), gníomhaigh leabhair imeartha freagartha, rianaigh SLA comhlíonadh i gcoinne na fuinneoige fógra 72 uair PDPL, bainistigh arduithe, agus comhordaigh freagairt an díoltóra.',
        incidents: 'Mar Cheannasaí Teagmhais (P-VN-09), braite agus tuairiscigh sáruithe sonraí lena ngabhann PDPL oibleagáidí fógra 72 uair an chloig don Choiste PDP, triáisiú de réir déine, agus tóg sainchomhaid teagmhais ar ghrád iniúchta.',
        localization: 'Mar an Enterprise DPO (P-VN-01), bainistigh saolré an chreata rialála: suiteáil pacáistí margaidh Vítneaimis, gníomhaigh creataí PDPL agus Foraithne 13, mapáil ceanglais do mhodúil ardáin, agus bainistigh aistriúcháin.',
        monitoring: 'Mar an Fiontair DPO (P-VN-01), bunóidh sé cláir leanúnacha um fhaireachán ar chomhlíonadh, chun athruithe rialála a rianú, KPIanna a thomhas, agus cásanna idirghníomhaíochta rialála a bhainistiú faoi PDPL.',
        onboarding: 'Mar an Traenálaí Comhlíonta (P-VN-02), cuir oiliúint ar fáil trí thurais threoraithe, lódáil sonraí samplacha réalaíocha le haghaidh cleachtaí láimhe, agus rith an draoi socraithe le haghaidh cásanna mearthosaithe.',
        organizations: 'Mar an DPO (P-VN-01), cumraigh struchtúir chomhlíonta il-eagraíochta, bunaigh ordlathas ranna, agus cuir iarratais ar ábhair sonraí ar bhealach trasna do ghrúpa corparáideach.',
        partner_workspace: 'Mar Bhainisteoir Cuntais Comhpháirtí, eagraíochtaí comhpháirtíochta ar bord a chumrú, sraitheanna seirbhíse a chumrú, táirgí insoláthartha comhlíonta a rianú, agus an margadh comhpháirtíochta a bhainistiú.',
        platform_settings: 'Mar an Riarthóir Ardáin, bainistigh an chumraíocht oibríochta go léir -- aitheantas, slándáil, comhtháthú agus rochtain ar mhodúil a thacaíonn le do chlár comhlíonta.',
        privacy_scanner: 'Mar an Anailísí Príobháideachta (P-VN-06), reáchtáil scanadh monatóireachta leanúnach chun sáruithe toiliú fianán, rianairí neamhúdaraithe, agus bearnaí beartais a bhrath -- ag giniúint fianaise maidir le ríomh-Phríobháideachas agus GDPR comhlíonadh.',
        program_governance: 'Déan nascleanúint idir Múnlaí Aibíochta, Measúnuithe, Bearnaí Fianaise, Achoimrí Feidhmiúcháin, agus go leor eile. Cuireann gach fo-mhodúl le léargas iomlánaíoch ar shláinte do chláir inmheánaigh.',
        rights_requests: 'As the Enterprise DPO (P-VN-01), manage data subject rights requests under PDPL with 15-day statutory deadlines, identity verification, and audit-grade resolution trails.',
        task_management: 'Rianaigh tascanna comhlíonta thar phearsantachtaí, rianaigh SLA spriocdhátaí, agus uathoibrithe sreafaí oibre a choinníonn d’fhoireann cuntasach.',
        vendor_risk: "Mar Bhainisteoir Riosca an Díoltóra (P-VN-08), scóráil riosca tríú páirtí, cuir athbhreithnithe geata i bhfeidhm, agus truicear athmheasúnuithe -- difriúil ó Rialachas Clár, a rianaíonn d'aibíocht inmheánach.",
    },
    consolidation: {
        title: 'Casanna Comhdhluthaithe',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Cruthaigh Cás Comhdhlúthaithe',
        case_count: 'case',
        created: 'Cruthaíodh cás comhdhlúthaithe',
        create: 'Cruthaigh Cás',
        creating: 'Ag cruthú...',
        cancel: 'Cealaigh',
        no_cases: 'Níor aimsíodh aon chásanna comhdhlúthaithe.',
        all_statuses: 'Gach Stádas',
        all_purposes: 'Gach Cuspóir',
        filter_status: 'Scag de réir stádais',
        filter_purpose: 'Scag de réir cuspóra',
        col_title: 'Teideal',
        col_purpose: 'Cuspóir',
        col_status: 'Stádas',
        col_progress: 'Dul chun cinn',
        col_deadline: 'Spriocdháta',
        col_department: 'Roinn',
        col_version: 'Leagan',
        col_evidence: 'Fianaise',
        sections_accepted: 'accepted',
        prev: 'Roimhe Seo',
        next: 'Ar aghaidh',
        page: 'Leathanach',
        prev_page: 'An leathanach roimhe seo',
        next_page: 'An chéad leathanach eile',
        field_title: 'Teideal',
        field_purpose: 'Cuspóir',
        field_framework: 'Creatchóid',
        field_deadline: 'Spriocdháta',
        field_reviewer: 'Aitheantas Úsáideora Athbhreithneora',
        title_placeholder: 'Cuir isteach teideal an cháis',
        framework_placeholder: 'm.sh., PDPD-2023',
        reviewer_placeholder: 'Cuir isteach ID úsáideora an athbhreithneora',
        // fix-sprint-20260821-2140 F6 — structural parity only. R-57 defers real
        // translation of the 14 non-EN/VI locales to Phase 25 (sprint-57.7.0); these
        // land EN-identical and are counted there rather than repaired here.
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        purposes: {
            dpia_filing: 'DPIA Comhdú',
            cross_border_filing: 'Comhdú Trasteorann',
            annual_governance_attestation: 'Fianú Rialachais Bliantúil',
            breach_package: 'Pacáiste Sárú',
            audit_inspection_pack: 'Pacáiste Iniúchta Iniúchta',
        },
        statuses: {
            draft: 'Dréacht',
            collecting: 'Ag bailiú',
            consolidating: 'Comhdhlúthú',
            review: 'Léirmheas',
            corrections: 'Ceartúcháin',
            pending_approval: 'Ceadú ar Feitheamh',
            approved: 'Ceadaithe',
            completed: 'Críochnaithe',
            closed: 'Dúnta',
            rejected: 'Diúltaithe',
        },
        section_statuses: {
            pending: 'Ar feitheamh',
            in_progress: 'Ar Siúl',
            submitted: 'Arna chur isteach',
            accepted: 'Glactar leis',
            needs_correction: 'Ceartúchán de dhíth',
            corrected: 'Ceartaithe',
        },
        flag_types: {
            missing_section: 'Rannóg ar Iarraidh',
            conflicting_entry: 'Iontráil Chodarsnach',
            stale_entry: 'Iontráil Stale',
            missing_evidence: 'Fianaise ar Iarraidh',
            incomplete_data: 'Sonraí Neamhiomlána',
            duplicate_entry: 'Iontráil Dúblach',
        },
        flag_severities: {
            critical: 'Criticiúil',
            warning: 'Rabhadh',
            info: 'Eolas',
        },
        sections_title: 'Ailt',
        no_sections: 'Níl aon rannóg sannta fós.',
        flags_title: 'Bratacha',
        no_flags: 'Níor aimsíodh aon bhratach.',
        open: 'open',
        approval_chain: 'Slabhra Formheasa',
        pending: 'Ar feitheamh',
        audit_trail: 'Rian Iniúchta',
        no_audit: 'Gan aon imeachtaí iniúchta.',
        auto_assign: 'Rannóga Uathshannadh',
        start_collection: 'Bailiúchán Tosaigh',
        review_case: 'Cás Athbhreithnithe',
        generate_export: 'Gin Easpórtáil',
        review_title: 'Athbhreithniú Comhdhlúite',
        advance_approval: 'Roimh Ré go Ceadú',
        back_to_case: 'Ar Ais go Cás',
        accept: 'Glac',
        return_section: 'Fill ar ais',
        return_comment: 'Seol nóta tráchta ar ais...',
        resolve: 'Réitigh',
        dismiss: 'Díbhe',
        resolution_notes: 'Nótaí réitigh...',
        notes: 'Nótaí',
        section_accepted: 'Alt glactha',
        section_returned: 'Alt ar ais lena cheartú',
        advanced_to_approval: 'Cás curtha ar aghaidh go dtí go gceadófar é',
        section_data: 'Sonraí Alt',
        save_progress: 'Sábháil Dul Chun Cinn',
        submit_section: 'Alt a chur isteach',
        submitting: 'Á chur isteach...',
        resubmit: 'Cuir isteach arís',
        progress_saved: 'Sábháladh an dul chun cinn',
        section_submitted: 'Alt curtha isteach',
        correction_required: 'Ceartúchán de dhíth',
        evidence_title: 'Fianaise',
        no_evidence: 'Níl aon fhianaise ceangailte.',
        add_evidence: 'Cuir Fianaise leis',
        evidence_added: 'Fianaise curtha leis',
        evidence_files: 'comhaid fianaise',
        uploaded_by: 'uaslódáilte ag',
        comments_title: 'Tuairimí',
        no_comments: 'Níl trácht ar bith fós.',
        comment_placeholder: 'Cuir nóta tráchta leis...',
        add_comment: 'Cuir Trácht leis',
        comment_added: 'Trácht curtha leis',
        view_data: 'Féach ar na sonraí a cuireadh isteach',
        export_title: 'Easpórtáil Comhad',
        export_generated: 'Easpórtáil pacáiste ginte',
        regenerate: 'Athghin',
        regenerating: 'Á athnuachan...',
        generated_at: 'Gintear ag',
        artifacts_title: 'Déantáin Easpórtála',
        artifact_type: 'Déantán',
        file_name: 'Ainm an Chomhaid',
        file_size: 'Méid',
        generated: 'Gineadh',
        download: 'Íosluchtaigh',
        artifacts: {
            master_report: 'Máistir-Thuairisc',
            evidence_bundle: 'Beart Fianaise (ZIP)',
            contributor_lineage: 'Lineage Ranníocóra',
            conflict_resolution: 'Réiteach Coimhlinte',
            approval_history: 'Stair Faomhadh',
        },
        official_forms: 'Foirmeacha Oifigiúla',
        forms_description: 'Is féidir na foirmeacha oifigiúla seo a leanas a ghiniúint ó na sonraí comhdhlúite. Tá foirmeacha oifigiúla ar leithligh ó pháipéir oibre inmheánacha.',
        announce_loaded_cases: '{count} chás comhdhlúthaithe lódáilte',
        announce_case_detail_loaded: 'Sonraí an cháis luchtaithe',
        announce_case_transitioned: 'Aistríodh an cás go {status}',
        announce_flag_resolved: 'Bratach réitithe',
        announce_flag_dismissed: 'Bristear an bhratach',
        case_detail_loaded: 'Sonraí an cháis luchtaithe',
        sections_auto_assigned: 'Déantar na hailt a shannadh go huathoibríoch ó ordlathas na ranna',
        errors: {
            load_failed: 'Theip ar lódáil na sonraí. Bain triail eile as.',
            create_failed: 'Theip ar chruthú an cháis. Bain triail eile as.',
            required_fields: 'Líon isteach na réimsí riachtanacha go léir le do thoil.',
            not_found: 'Ní bhfuarthas.',
            comment_required: 'Teastaíonn trácht nuair a bhíonn mír ar ais.',
            notes_required: 'Tá nótaí ag teastáil agus bratach á réiteach nó á díbhe.',
        },
    },
    rights_requests: {
        import: {
            title: 'Iompórtáil cásanna',
            nav_title: 'Iompórtáil Mhóir',
            description: 'Uaslódáil comhad CSV chun cásanna iarrata cearta a iompórtáil go mórchóir.',
            select_file: 'Roghnaigh comhad CSV',
            file_help: 'CSV le ceannteidil a mheaitseálann réimsí cáis.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'Comhad ró-mhór. Is é 2MB an t-uasmhéid.',
            preview: 'Réamhamharc',
            columns_detected: 'Colúin aimsíodh: {count}',
            column_mapping: 'Mapáil colún',
            import_button: 'Iompórtáil',
            import_complete: 'Iompórtáil críochnaithe',
            import_error: 'Theip ar an iompórtáil',
            result: 'Toradh na hiompórtála',
            total: 'Iomlán',
            imported: 'Iompórtáilte',
            skipped: 'Léimthe',
            failed: 'Teipthe',
            errors: 'Earráidí',
            error_row: 'Ró',
            error_field: 'Réimse',
            error_message: 'Teachtaireacht',
            history: 'Stair iompórtála',
            no_history: 'Gan stair iompórtála.',
            history_file: 'Ainm comhaid',
            history_date: 'Dáta',
        },
        batch: {
            nav_title: 'Próiseas Baisc',
            page_title: 'Próiseas Baisc DSR',
            page_subtitle: 'Upload a CSV of pending DSRs. Dréachtaíonn Forseti freagra admhála in aghaidh an iarratais agus scuainíonn sé tasc fíorúcháin in aghaidh an tsraith. Glacann an DPO léirmheas + gach ceann roimh sheoladh.',
            upload_heading: 'Uaslódáil',
            csv_hint: 'Colúin riachtanacha: request_id, requester_email, request_type. Optional: deadline (ISO-8601), notes.',
            rows_heading: '{count} rows parsed',
            submit_label: 'Rith bhaisc',
            submitting: 'Ag dréachtú…',
            col_request_id: 'Aitheantas an iarratais',
            col_email: 'Ríomhphost iarrthóra',
            col_type: 'Cineál',
            col_status: 'Stádas',
            col_actions: 'Gníomhartha',
            status_queued: 'Ciúáilte',
            status_drafted: 'Dréachtaithe',
            status_failed: 'Theip',
            status_accepted: 'Glactar leis',
            status_rejected: 'Diúltaithe',
            status_parsing_error: 'Earráid pharsála',
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Glac',
            reject: 'Diúltaigh',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    assessment_triggers: {
        title: 'Truicear Measúnaithe',
        subtitle: 'Cumraigh cruthú measúnaithe uathoibrithe agus DPO fógraí le haghaidh sreafaí oibre príobháideachais.',
        auto_create_dpia_label: 'Uathchruthaigh DPIA nuair atá an ghníomhaíocht phróiseála marcáilte mar DPIA ag teastáil',
        auto_notify_new_purpose_label: 'Cuir fógra uathoibríoch ar DPO nuair a chruthaítear cuspóir nua le catagóirí sonraí íogaire',
        auto_notify_cross_border_label: 'Cuir fógra uathoibríoch ar DPO nuair a chuirtear aistriú trasteorann leis an ngníomhaíocht phróiseála',
        default_dpo_label: 'Réamhshocrú DPO',
        default_dpo_placeholder: 'Roghnaigh réamhshocrú DPO...',
        notification_channels_label: 'Cainéil Fógraí',
        channel_in_app: 'In-App',
        channel_email: 'Email',
        save_success: 'Sábháladh socruithe truicear measúnaithe.',
        save_error: 'Theip ar shábháil socruithe truicear measúnaithe.',
        fetch_error: 'Theip ar lódáil socruithe truicear measúnaithe.',
        fetch_users_error: 'Theip ar lódáil úsáideoirí.',
        announce_loaded: 'Socruithe truicear measúnaithe luchtaithe',
        dpia_status: 'DPIA Stádas',
        dpia_linked: 'DPIA Nasctha',
        dpia_required_no_dpia: 'DPIA Ag Teastáil - Gan Cruthaithe',
        create_dpia: 'Cruthaigh DPIA',
        create_dpia_success: 'DPIA cruthaithe agus nasctha le gníomhaíocht.',
        create_dpia_error: 'Theip ar DPIA a chruthú don ghníomhaíocht.',
        view_dpia: 'Amharc ar DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    data_subjects: {
        title: 'Próifílí na nÁbhar Sonraí',
        nav_title: 'Ábhair Sonraí',
        nav: {
            profiles: 'Próifílí',
        },
        create: 'Cruthaigh Próifíl',
        edit: 'Cuir Próifíl in Eagar',
        delete: 'Scrios Próifíl',
        delete_confirm: 'An bhfuil tú cinnte gur mhaith leat próifíl an ábhair sonraí seo a scriosadh?',
        display_name: 'Ainm Taispeána',
        email: 'Email',
        phone: 'Fón',
        employee_id: 'Aitheantas Fostaí',
        student_id: 'Aitheantas an Mhic Léinn',
        notes: 'Nótaí',
        external_refs: 'Tagairtí Seachtracha',
        cases_count: 'Cásanna',
        workflows_count: 'Taifid Toilithe',
        last_activity: 'Gníomhaíocht Deiridh',
        timeline: 'Amlíne',
        link_case: 'Link Case',
        link_workflow: 'Link Workflow',
        no_profiles: 'Níor aimsíodh próifílí ábhar sonraí.',
        search_placeholder: 'Cuardaigh de réir ainm, ríomhphoist, gutháin nó aitheantais...',
        create_success: 'Cruthaíodh próifíl ábhar sonraí.',
        create_error: 'Theip ar phróifíl an ábhair sonraí a chruthú.',
        update_success: 'Nuashonraíodh próifíl an ábhair sonraí.',
        update_error: 'Theip ar phróifíl an ábhair sonraí a nuashonrú.',
        delete_success: 'Scriosadh próifíl an ábhair sonraí.',
        delete_error: 'Theip ar phróifíl an ábhair sonraí a scriosadh.',
        link_case_success: 'Cás nasctha leis an bpróifíl.',
        link_case_error: 'Theip ar an gcás a nascadh leis an bpróifíl.',
        link_workflow_success: 'Sreabhadh oibre nasctha leis an bpróifíl.',
        link_workflow_error: 'Theip ar an sreabhadh oibre a nascadh leis an bpróifíl.',
        load_error: 'Theip ar lódáil próifílí na n-ábhar sonraí.',
        detail_load_error: 'Theip ar lódáil phróifíl an ábhair sonraí.',
        timeline_load_error: 'Theip ar an amlíne a lódáil.',
        tab_cases: 'Cásanna DSR',
        tab_consent: 'Taifid Toilithe',
        tab_timeline: 'Amlíne',
        tab_consent_workflows: 'Sreafaí Oibre Toiliú',
        tab_web_consent: 'CMP Toiliú',
        web_consent_empty: 'Níor aimsíodh CMP taifead toilithe don ábhar seo.',
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: 'Theip ar CMP taifead toilithe a lódáil.',
        col_policy_version: 'Leagan Beartais',
        col_versions: 'Leaganacha',
        col_consent_version: 'Toiliú',
        col_cookie_declaration_version: 'Dearbhú Fianán',
        col_purposes: 'Cuspóirí',
        purpose_granted: 'Deonaithe',
        purpose_denied: 'Diúltaíodh',
        granted_domains: 'Deonaithe',
        denied_domains: 'Diúltaíodh',
        col_action: 'Gníomh',
        col_timestamp: 'Stampa ama',
        col_domain: 'Fearann',
        no_cases: 'Níl aon chásanna nasctha.',
        no_workflows: 'Níl aon sreabhadh oibre toilithe nasctha.',
        no_timeline: 'Gan aon imeachtaí amlíne.',
        case_id: 'Aitheantas an cháis',
        case_status: 'Stádas',
        case_type: 'Cineál Iarratas',
        case_created: 'Cruthaithe',
        workflow_id: 'Aitheantas sreafa oibre',
        workflow_type: 'Cineál',
        workflow_status: 'Stádas',
        workflow_created: 'Cruthaithe',
        timeline_date: 'Dáta',
        timeline_event: 'Imeacht',
        timeline_details: 'Sonraí',
        timeline_time: 'Am',
        timeline_type: 'Cineál',
        timeline_summary: 'Achoimre',
        type_case: 'Cás DSR',
        type_consent_workflow: 'Sreabhadh Oibre Toiliú',
        profile_info: 'Eolas Próifíl',
        announce_loaded: 'Lódaíodh próifílí ábhar sonraí',
        announce_detail_loaded: 'Lódaíodh próifíl an ábhair sonraí',
        select_case: '— Roghnaigh cás —',
        select_workflow: '— Roghnaigh sreabhadh oibre —',
        no_cases_hint: 'Ceangail cásanna chun iad a fheiceáil anseo.',
        no_workflows_hint: 'Ceangail sreafaí toilithe chun iad a fheiceáil anseo.',
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    flows: {
        map: {
            description_prefix: '{systems} córais · {flows} sreafaí · {crossBorder} trasteorann · {highRisk} riosca ard/criticiúil',
            switch_to_list: 'Athraigh go dtí an amharc liosta',
            switch_to_list_aria: 'Athraigh go dtí an radharc tábla',
            svg_aria: 'Sreabhléarscáil sonraí: {systems} córais, {flows} sreafaí, {crossBorder} sreafaí trasteorann',
            selected_system: 'Córas roghnaithe',
            drill_hint: 'Oscail cluaisín an liosta chun na sreafaí nasctha, DPIAanna agus sannacháin díoltóirí don chóras seo a chur in eagar.',
        },
    },
    packages: {
        light: {
            name: 'Solas',
            description: "Uirlisí riachtanacha comhlíonta príobháideachais d'fhoirne beaga",
        },
        standard: {
            name: 'Caighdeán',
            description: "Ardán comhlíonta atá feistithe go hiomlán d'eagraíochtaí atá ag fás",
        },
        enterprise_unlimited: {
            name: 'Enterprise Unlimited',
            description: 'Comhlíonadh ar leibhéal fiontraíochta le tacaíocht ilghnólachta agus seirbhísí tiomnaithe',
        },
    },
    forseti: {
        // Sprint 63.32.0 (Q1, #543) — the record summary's facts, rendered on screen;
        // the model prompt keeps the English summary.
        summary_fields: {
            accepted_until: 'Accepted until',
            active_timers: 'Active timers',
            actual_release: 'Actual release',
            ad_hoc_answered: 'Ad-hoc reports answered',
            ad_hoc_report_entries: 'Ad-hoc report entries',
            affected_systems: 'Affected systems',
            amended_on: 'Last amended',
            amendments: 'Amendments',
            answered_on: 'Answered',
            approval_mode: 'Approval mode',
            approved: 'Approved',
            approved_on: 'Approved',
            approved_risk_tier: 'Approved risk tier',
            artifact_kind: 'Artefact kind',
            authorisation_kind: 'Authorisation kind',
            authority: 'Authority',
            authority_reference_recorded: 'Authority reference recorded',
            authority_request: 'Authority request',
            authority_requests: 'Authority requests',
            batches: 'Batches',
            breach_notification: 'Breach notification',
            categories: 'Category tags',
            channel: 'Channel',
            claimant: 'Claimant',
            classification: 'Classification',
            classifications: 'Classifications',
            closed_on: 'Closed',
            closure_approved_on: 'Closure approved',
            completed_on: 'Completed',
            conformity_records: 'Conformity records',
            conformity_route: 'Conformity route',
            consent_cover_state: 'Consent cover',
            consent_evidence_referenced: 'Consent evidence referenced',
            corrective_measures: 'Corrective measures',
            country: 'Country',
            covered_services: 'Covered services',
            credentials: 'Credentials',
            cross_border_transfers: 'Cross-border transfers',
            cross_mapping_recorded_on: 'Cross-mapping recorded',
            cryptographic_assets: 'Cryptographic assets',
            current_version: 'Current version',
            data_categories: 'Data categories',
            data_element: 'Data element',
            data_tier: 'Data tier',
            dataset: 'Dataset',
            deadline: 'Deadline',
            decided_on: 'Decided',
            declared_on: 'Declared',
            declared_quantity: 'Declared quantity',
            deployment_context: 'Deployment',
            deposit_submission_recorded: 'Deposit submission recorded',
            description_recorded: 'Description recorded',
            destination_country: 'Destination country',
            details_recorded: 'Details recorded',
            dictionary_version: 'Dictionary version',
            document_type: 'Document type',
            documents: 'Documents',
            domains_evaluated: 'Domains evaluated',
            dpa_status: 'DPA status',
            dpo_assigned: 'DPO assigned',
            due: 'Due',
            effective_on: 'Effective',
            evidence: 'Evidence references',
            evidence_attachments: 'Evidence attachments',
            evidence_pack_linked: 'Evidence pack linked',
            expires_on: 'Expires',
            filing_linked: 'Filing record linked',
            filing_type: 'Filing type',
            financial_crime_signed: 'Financial-crime function signed',
            fine_areas: 'Fine areas cited',
            form_type: 'Form',
            format: 'Format',
            framework: 'Framework',
            gate_kind: 'Deposit gate kind',
            generated_tasks: 'Generated tasks',
            identity_linked: 'Linked to an identity record',
            identity_verification: 'Identity verification',
            incident: 'Incident',
            incidents: 'Incidents',
            intake_evidence: 'Intake evidence',
            intake_kind: 'Intake kind',
            issued_on: 'Issued',
            jurisdiction: 'Jurisdiction',
            kind: 'Kind',
            kind_declared_by_pack: 'Kind declared by an installed pack',
            legal_basis: 'Legal basis',
            legal_review_status: 'Legal review',
            legal_reviews: 'Legal reviews',
            licence_kind: 'Licence kind',
            lifecycle_events: 'Lifecycle events',
            lifecycle_stage: 'Lifecycle stage',
            linked_activities: 'Linked activities',
            linked_authority_requests: 'Linked authority requests',
            linked_dpia: 'Linked DPIA',
            linked_frameworks: 'Linked frameworks',
            location: 'Location',
            lodged_by_role: 'Lodged by',
            lodged_on: 'Lodged',
            model_version: 'Model version',
            name: 'Name',
            notification_entries: 'Notification entries',
            notify_decision: 'Notification decision',
            offerings: 'Named offerings',
            onboarding_status: 'Onboarding',
            open_findings: 'Open findings',
            ot_asset: 'OT asset',
            outcome_reports: 'Outcome reports',
            overall_score: 'Overall score',
            parcel: 'Parcel',
            period_end: 'Period end',
            period_start: 'Period start',
            periodic_report_entries: 'Periodic report entries',
            personal_data: 'Personal data',
            phases_completed: 'Phases completed',
            phases_total: 'Phases',
            planned_release: 'Planned release',
            playbook_version: 'Playbook version',
            pre_existing_on_commencement: 'Pre-existing on commencement',
            priority: 'Priority',
            privacy_signed: 'Privacy function signed',
            procedure_reference: 'Procedure reference',
            product: 'Product',
            products: 'Products',
            proof_attachments: 'Proof attachments',
            provider: 'Provider',
            provider_type: 'Provider type',
            publication_evidence: 'Publication evidence',
            published_on: 'Published',
            purpose_recorded: 'Purpose recorded',
            purposes: 'Purposes recorded',
            questions_answered: 'Questions answered',
            questions_total: 'Questions',
            raised_on: 'Raised',
            rationale_recorded: 'Rationale recorded',
            readiness_record: 'Readiness record',
            recall_scope: 'Recall scope',
            receipt_recorded: 'Receipt recorded',
            received_on: 'Received',
            recipient: 'Recipient',
            recipient_class: 'Recipient class',
            reclassification_changed_fields: 'Inputs changed since the approved run',
            reclassification_review: 'Reclassification prompt review',
            reclassification_signal_since: 'Reclassification signal since',
            reconciliation_exceptions: 'Reconciliation exceptions',
            record_kind: 'Record kind',
            recorded_on: 'Recorded',
            reference: 'Reference',
            releasable_marked_on: 'Marked releasable',
            relied_on_events: 'Parcel events relied on',
            remediation_plans: 'Remediation plans',
            remediation_tasks: 'Remediation tasks raised',
            renewal_of_earlier: 'Renewal of an earlier credential',
            replaces: 'Replaces',
            reporting_years: 'Reporting years',
            representative_consents: 'Representative consent records',
            request_type: 'Request type',
            requester_recorded: 'Requester on record',
            responses: 'Responses',
            responsible_person_recorded: 'Responsible person recorded',
            retention_duty: 'Retention duty',
            review_due: 'Review due',
            revoked_on: 'Revoked',
            rights_evidence: 'Rights evidence entries',
            risk_level: 'Risk level',
            root_cause_recorded: 'Root cause recorded',
            rule_type: 'Rule type',
            sellers: 'Sellers',
            serial_reference: 'Serial reference',
            service_type: 'Service type',
            sharing_events: 'Sharing events',
            sharing_refusals: 'Refused sharing attempts',
            shipment: 'Shipment',
            sla_state: 'Deadline clock',
            source_country: 'Source country',
            stage: 'Stage',
            state: 'State',
            status: 'Status',
            sub_status: 'Sub-status',
            subject: 'Subject',
            subject_kind: 'Subject kind',
            submission_kind: 'Submission kind',
            submitted_on: 'Submitted',
            successor_recorded: 'Successor credential recorded',
            supplied_on: 'Supplied',
            takedown_reason_recorded: 'Takedown reason recorded',
            taken_down_on: 'Taken down',
            target_date: 'Target date',
            technical_actions: 'Technical actions',
            technical_regulation: 'Technical regulation',
            title: 'Title',
            transfer_assessment_referenced: 'Transfer assessment referenced',
            transfer_mechanism: 'Transfer mechanism',
            transfer_review_state: 'Transfer review',
            transfer_source_recorded: 'Transfer source institution recorded',
            valid_from: 'Valid from',
            valid_until: 'Valid until',
            vendor: 'Vendor',
            vendor_type: 'Vendor type',
            version: 'Version',
            versions: 'Versions recorded',
            withdrawal_determination_recorded: 'Withdrawal determination recorded',
        },
        summary_codes: {
            acknowledged: 'Acknowledged',
            active: 'Active',
            all: 'All approvers',
            any: 'Any approver',
            approved: 'Approved',
            archived: 'Archived',
            blocked: 'Blocked',
            breached: 'Breached',
            cancelled: 'Cancelled',
            closed: 'Closed',
            completed: 'Completed',
            critical: 'Critical',
            declared: 'Declared',
            do_not_notify: 'Do not notify',
            draft: 'Draft',
            escalated: 'Escalated',
            expired: 'Expired',
            filed: 'Filed',
            high: 'High',
            in_progress: 'In progress',
            in_review: 'In review',
            inactive: 'Inactive',
            known_data_subject: 'A known data subject',
            labelled_claimant: 'A labelled claimant',
            lodged: 'Lodged',
            low: 'Low',
            medium: 'Medium',
            new: 'New',
            not_started: 'Not started',
            notify: 'Notify',
            open: 'Open',
            overdue: 'Overdue',
            paused: 'Paused',
            pending: 'Pending',
            personal_data: 'Personal data',
            published: 'Published',
            received: 'Received',
            rejected: 'Rejected',
            resolved: 'Resolved',
            revoked: 'Revoked',
            signed: 'Signed',
            special_category: 'Special-category personal data',
            submitted: 'Submitted',
            suspended: 'Suspended',
            taken_down: 'Taken down',
            unclassified: 'Unclassified',
            undecided: 'Undecided',
            under_review: 'Under review',
            unverified: 'Unverified',
            verified: 'Verified',
            withdrawn: 'Withdrawn',
        },
        summary_flag_yes: 'Yes',
        summary_flag_no: 'No',
        summary_not_recorded: 'Not recorded',
        summary_fields_label: 'Record facts',
        providerSettings: {
            title: 'Forseti AI Socruithe Soláthraí',
            subtitle: 'Cumraigh an soláthraí Claude don Chomhairleoir Dlí Forseti AI.',
            healthHeading: 'Sláinte soláthraí',
            statusLabel: 'Stádas',
            statusOk: 'ceart go leor',
            statusFailed: 'Níl fáil air',
            providerLabel: 'Soláthraí',
            providerClaudeApi: 'Claude API',
            providerCliRelay: 'sealaíochta Claude CLI',
            providerAesirManaged: 'Comharthaí arna mbainistiú ag Aesir',
            lastCheckedLabel: 'Ar deireadh seiceáilte',
            reasonLabel: 'Cúis',
            refreshButton: 'Athnuaigh',
            apiKeyHeading: 'Claude API eochair',
            apiKeyHint: 'Má shocraítear eochair API, gníomhófar mód Claude API. Fág gan socrú chun an sealaíocht CLI áitiúil a úsáid (modh forbróra / féin-óstáilte).',
            apiKeyLabel: 'API eochair',
            apiKeyMinLength: '32 carachtar ar a laghad. Criptithe ar fos; ní thaispeántar arís.',
            apiKeyTooShort: 'API eochair ní mór a bheith ar a laghad 32 carachtair.',
            apiKeySaved: 'API eochair shábháil. Sláinte á athnuachan…',
            apiKeyCleared: 'API eochair glanta. Úsáideann Forseti an sealaíocht áitiúil CLI anois.',
            setKeyButton: 'Sábháil API eochair',
            clearKeyButton: 'Glan API eochair (úsáid sealaíochta CLI)',
        },
        // Owner ruling 2026-10-10 — Forseti → AI Model (provider, model, effort).
        aiModel: {
            title: 'AI Model',
            sidebarLabel: 'AI Model',
            subtitle: 'Choose the AI provider, model and effort behind every Forseti request. Your organisation connects its own provider account with its own API key, and that provider bills you directly for what Forseti uses.',
            loadError: 'The AI model settings could not be read.',
            retrySubject: 'the AI model settings',
            generalHeading: 'General',
            generalDescription: 'The active provider answers every Forseti chat, workflow, tabular review and redline. Only a provider with a saved API key can be made active.',
            activeProviderLabel: 'Active provider',
            activeProviderHint: 'A provider without a saved API key cannot be selected. Save its key in its card below first.',
            providerNoKey: '{provider} (no API key saved)',
            thinkingLabel: 'Show the reasoning summary in chat',
            thinkingHint: 'When the model returns a summary of its reasoning, Forseti shows it above the answer in interactive chat. Workflows and reviews are not affected.',
            relayProviderLabel: 'Local CLI relay (development)',
            relayUrlLabel: 'Relay address',
            relayUrlHint: 'The address of the local CLI relay. Used only in development and self-hosted installations.',
            saveGeneral: 'Save general settings',
            generalSaved: 'General settings saved.',
            providersHeading: 'Providers',
            providersDescription: 'Each provider keeps its own API key, model and effort. Saving a provider does not make it active unless you tick Make this the active provider.',
            cardDescription: "Models from {provider}, called with your organisation's own API key.",
            cardDescriptionCompatible: "Any endpoint that implements the OpenAI API, such as a model gateway or a self-hosted model server, called with that endpoint's API key.",
            activeBadge: 'Active',
            keySetBadge: 'API key saved',
            keyMissingBadge: 'No API key',
            apiKeyLabel: 'API key',
            apiKeySavedHint: 'A key ending {hint} is saved. Leave this blank to keep the saved key.',
            apiKeyNoneHint: 'No key is saved. The key is stored encrypted and is never shown again after you save it.',
            removeKey: 'Remove key',
            removeKeyTitle: 'Remove the saved API key?',
            removeKeyMessage: 'Forseti cannot call {provider} again until a new key is saved. The model and effort settings stay as they are.',
            removeKeyConfirm: 'Remove key',
            removing: 'Removing the key…',
            keyRemoved: 'The {provider} API key was removed.',
            modelLabel: 'Model',
            modelHint: 'Choose a suggested model or type any model id {provider} offers.',
            modelHintCompatible: 'Type the model id exactly as the endpoint expects it.',
            modelRequired: 'Enter a model.',
            fastModelLabel: 'Fast model (optional)',
            fastModelHint: 'Used for conversation titles and tabular extraction. Leave blank to use the main model.',
            effortLabel: 'Effort',
            effortHint: 'How much reasoning the model spends on each answer. Levels the selected model does not accept are unavailable.',
            effortUnsupported: '{effort} is not available for this model.',
            efforts: {
                default: 'Model default',
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                xhigh: 'Extra high',
                max: 'Maximum',
            },
            apiModeLabel: 'API mode',
            apiModeHint: 'Automatic uses the Responses API for models that require it and Chat Completions for the rest.',
            apiModes: {
                auto: 'Automatic',
                responses: 'Responses API',
                chat_completions: 'Chat Completions API',
            },
            reasoningModeLabel: 'Reasoning mode',
            reasoningModeHint: 'Pro spends more reasoning on each answer and costs more. It is offered only for models that support it.',
            reasoningModeUnavailable: 'Pro is not available for the selected model.',
            reasoningModes: {
                standard: 'Standard',
                pro: 'Pro',
            },
            serviceTierLabel: 'Service tier',
            serviceTierHint: 'Project default uses the tier set on your provider project.',
            serviceTiers: {
                auto: 'Project default',
                default: 'Standard',
            },
            baseUrlLabel: 'Base URL',
            baseUrlHint: "The endpoint's API root over HTTPS, for example https://openrouter.ai/api/v1.",
            baseUrlRequired: "Enter the endpoint's base URL.",
            makeActiveLabel: 'Make this the active provider',
            saveButton: 'Save',
            saved: '{provider} settings saved.',
            testButton: 'Test connection',
            testing: 'Testing…',
            testNote: 'Tests the saved settings, so save your changes first. The test sends one short request containing no customer data, which your provider may charge for.',
            unsavedNote: 'This card has unsaved changes. The test uses the saved settings.',
            lastTestHeading: 'Last test',
            testNever: 'Not tested yet.',
            testPassed: 'Passed',
            testFailed: 'Failed',
            testCheckedAt: 'Checked {time}',
            testError: 'The connection test could not be run.',
            saveError: 'The settings could not be saved.',
            removeError: 'The API key could not be removed.',
            errors: {
                api_key_required: 'Save an API key for this provider before making it active.',
                api_key_required_for_new_host: 'The base URL now names another host. Enter the API key for that host before saving; the saved key is not sent to a new host.',
                invalid_api_key: 'The provider rejected this API key.',
                invalid_api_key_format: 'This does not look like a key for this provider. Check that you copied the whole key.',
                unsupported_model_effort: 'The selected model does not accept this effort level.',
                model_requires_responses: 'This model needs the Responses API. Choose Automatic or Responses API.',
                base_url_required: "Enter the endpoint's base URL.",
                invalid_base_url: 'Enter a valid HTTPS URL.',
                unsupported_provider_options: 'These options are not available for this provider.',
            },
        },
        usage: {
            title: 'Úsáid',
            recentHeading: 'Gníomhaíocht le déanaí',
            historyHeading: '12 tréimhse seo caite',
            noActivity: 'Níl aon ghníomhaíocht fós sa tréimhse seo.',
            featureLabel: 'Gné',
            whenLabel: 'Cathain',
            periodLabel: 'Tréimhse',
            byFeatureHeading: 'An mhí seo de réir gné',
            byFeatureEmpty: 'Níl aon ghníomhaíocht gné go fóill an tréimhse seo.',
            featureColumn: 'Gné',
            featureUnitsColumn: 'Aonaid',
            featureRunsColumn: 'Ritheann',
            byUserHeading: 'An mhí seo de réir úsáideora (amharc riaracháin)',
            byUserEmpty: 'Níl aon ghníomhaíocht in aghaidh an úsáideora fós sa tréimhse seo.',
            userColumn: 'Úsáideoir',
            reconciliationLinkLabel: 'Féach ar an tuarascáil srutha athmhuintearais',
            reconciliationLinkHint: 'Aesir-riarachán inmheánach amháin.',
            // Owner ruling 2026-10-10 — consumption by provider, model and effort.
            subtitleProvider: "What Forseti has used, and the provider, model and effort each request ran on. Your organisation's AI provider bills you directly for this consumption.",
            routingHeading: 'Current routing',
            routingDescription: 'The provider, model and effort each Forseti feature uses now.',
            routingProviderLabel: 'Active provider',
            routingFeatureColumn: 'Feature',
            routingModelColumn: 'Model',
            routingEffortColumn: 'Effort',
            routingChangeLink: 'Change the AI model settings',
            routingLoadError: 'The current routing could not be read.',
            routingRetrySubject: 'the current routing',
            periodHeading: 'Consumption',
            periodDescription: 'Requests and tokens for the period you choose. The current month is shown first.',
            fromLabel: 'From',
            toLabel: 'To',
            applyPeriod: 'Show period',
            periodInvalid: 'The start date must be on or before the end date.',
            summaryLoadError: 'Consumption for this period could not be read.',
            summaryRetrySubject: 'consumption for this period',
            totalsCalls: 'Requests',
            totalsInput: 'Input tokens',
            totalsOutput: 'Output tokens',
            totalsCached: 'Cached input tokens',
            byModelHeading: 'By provider, model and effort',
            byFeaturePeriodHeading: 'By feature',
            providerColumn: 'Provider',
            modelColumn: 'Model',
            effortColumn: 'Effort',
            callsColumn: 'Requests',
            inputColumn: 'Input tokens',
            outputColumn: 'Output tokens',
            cachedColumn: 'Cached input tokens',
            tokensColumn: 'Tokens (in / out)',
            timeColumn: 'Time',
            noConsumption: 'Forseti made no requests in this period.',
            notRecorded: 'Not recorded',
            recentDescription: 'The most recent requests in the selected period.',
            recentLoadError: 'Recent activity could not be read.',
            recentRetrySubject: 'recent activity',
            byUserPeriodHeading: 'By user',
            byUserBasis: 'Counted over the {count} most recent requests in this period.',
            historyMonthlyHeading: 'Monthly consumption',
            historyConsumedColumn: 'Consumed units',
            historyLoadError: 'Monthly consumption could not be read.',
            historyRetrySubject: 'monthly consumption',
            historyEmpty: 'No earlier months recorded yet.',
            featureLabels: {
                chat: 'Comhrá',
                workflow: 'Rith sreabhadh oibre',
                tabular: 'Eastóscadh tábla',
                redline: 'Léirmheas ar Redline',
                title: 'Teideal an chomhrá',
                unknown: 'Eile',
            },
        },
        reconciliation: {
            title: 'Athmhuintearas Cuóta Forseti',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes and records any drift to the structured log channel. Aesir-internal admin only.',
            backLink: 'Ar ais go Forseti Usage',
            stubHeading: 'Where reconciliation results are recorded',
            stubBody: 'This page does not show run history. Each reconciliation run writes its drift figures to the structured log channel; read them there.',
            permissionDenied: "Tá an leathanach seo teoranta do riarthóirí inmheánacha Aesir. Má chreideann tú gur cheart go mbeadh rochtain agat, déan teagmháil le d'ionadaí AesirX.",
        },
        projects: {
            title: 'Tionscadail',
            subtitle: 'Spásanna oibre le haghaidh doiciméad, comhráite, sreafaí oibre, agus léirmheasanna.',
            modeLabel: 'Amharc',
            tabMine: 'Mo thionscadail',
            tabShared: 'Roinnte liom',
            searchLabel: 'Tionscadail cuardaigh',
            searchPlaceholder: 'Cuardaigh…',
            newButton: 'Tionscadal nua',
            colName: 'Ainm',
            colVisibility: 'Infheictheacht',
            colDocs: 'Doiciméid',
            colMembers: 'Baill',
            colUpdated: 'Nuashonraithe',
            visibilityOwn: 'Féin amháin',
            visibilityShared: 'Roinnte',
            emptyMine: 'Níl aon tionscadail fós. Cruthaigh ceann le tosú.',
            emptyShared: 'Níor roinneadh aon tionscadal leat.',
            pageHint: 'Ag taispeáint {shown} de {total}.',
            createTitle: 'Tionscadal nua',
            createSubmit: 'Cruthaigh tionscadal',
            fieldName: 'Ainm',
            fieldDescription: 'Cur síos (roghnach)',
            fieldCmNumber: 'Uimhir an cháis/an ábhair (roghnach)',
            fieldVisibility: 'Infheictheacht',
            errorNameRequired: 'Tá ainm ag teastáil.',
            errorSelectorRequired: 'Roghnaigh úsáideora nó grúpa org le cur leis.',
            renameButton: 'Athainmnigh',
            renamePrompt: 'Athainmnigh an tionscadal',
            deleteButton: 'Scrios',
            deleteConfirm: 'Delete this project? It will disappear from your projects and cannot be restored from the product.',
            deleteFolderConfirm: 'Scrios an fillteán seo? Fanfaidh na doiciméid istigh sa tionscadal, gan iad a chomhdú.',
            removeMemberConfirm: 'An bhfuil fonn ort an ball seo a bhaint?',
            foldersHeading: 'Fillteáin',
            foldersEmpty: 'Níl aon fhillteán fós.',
            addFolderButton: 'Fillteán nua',
            addFolderTitle: 'Fillteán nua',
            documentsHeading: 'Doiciméid',
            documentsEmpty: 'Níl aon doiciméid ceangailte go fóill.',
            colFilename: 'Ainm comhaid',
            colAdded: 'Curtha leis',
            unattachButton: 'Dícheangail',
            membersHeading: 'Baill',
            membersEmpty: 'Úinéir amháin.',
            addMemberButton: 'Cuir ball leis',
            addMemberTitle: 'Cuir ball leis',
            addMemberSubmit: 'Cuir ball leis',
            colMember: 'Ball',
            colRole: 'Ról',
            selectorTypeLabel: 'Cuir le',
            selectorUser: 'Aitheantas Úsáideora',
            selectorGroup: 'ID grúpa org',
            fieldUserId: 'Úsáideoir',
            fieldGroupId: 'Grúpa org',
            fieldRole: 'Ról',
            roleAdmin: 'Riarachán',
            roleEditor: 'eagarthóir',
            roleViewer: 'Amharcóir',
            notFound: 'Tionscadal gan aimsiú.',
            selectUserPlaceholder: 'Roghnaigh úsáideoir…',
            selectGroupPlaceholder: 'Roghnaigh grúpa org…',
            noUsersAvailable: 'Níl aon úsáideoirí ar fáil.',
            noGroupsAvailable: 'Níl aon ghrúpa org ar fáil.',
            loadFailed: 'Theip ar na roghanna a lódáil.',
            deleteProjectTitle: 'An bhfuil fonn ort an tionscadal a scriosadh?',
            deleteFolderTitle: 'Scrios fillteán?',
            removeMemberTitle: 'An bhfuil fonn ort an ball a bhaint?',
            removeMemberConfirmGeneric: 'An bhfuil fonn ort an ball seo a bhaint den tionscadal?',
            addDocumentButton: 'Cuir doiciméad leis',
            addDocumentTitle: 'Cuir doiciméad leis an tionscadal',
            fieldDocument: 'Roghnaigh doiciméad',
            selectDocPlaceholder: 'Cuardaigh doiciméad...',
            ownerLabel: 'Úinéir an Chórais',
        },
        chat: {
            sidebarLabel: 'Comhráite',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'Do AI Comhairleoir Dlí & Comhlíonta — bunaithe ar do shonraí tionóntaí agus ar do phacáistí rialála suiteáilte.',
            personaSelectorLabel: 'Pearsa',
            personaHeading: 'Roghnaigh persona',
            startChatButton: 'Cuir tús le comhrá nua',
            startingChat: 'Comhrá á thosú…',
            recentHeading: 'Comhráite le déanaí',
            recentEmpty: 'Níl aon chomhráite fós - cuir tús leis an gceann thuas.',
            untitledChat: 'Comhrá nua',
            chatLoadFailed: 'Forseti could not load this chat.',
            streamFailed: 'Forseti could not finish this reply. Your message is still in the box.',
            personaSwitchFailed: 'The persona could not be changed.',
            renameFailed: 'The chat could not be renamed.',
            deleteFailed: 'The chat could not be deleted.',
            messageListLabel: 'Comhrá',
            detailSubtitle: 'Pearsa: {persona}',
            composeHeading: 'Seol teachtaireacht',
            composeLabel: 'Teachtaireacht Forseti',
            composeDisabledPlaceholder: 'This chat is read-only.',
            composeDisabledHint: 'You can read this conversation but not add to it.',
            switchPersonaHeading: 'Pearsa',
            presetPersona: 'Pearsa réamhshocraithe',
            changePersona: 'Athraigh pearsa',
            hidePersonaOptions: 'Folaigh roghanna pearsa',
            starterHeading: 'Tosaigh le ceann de na gníomhartha seo',
            renameButton: 'Athainmnigh',
            renamePrompt: 'Athainmnigh comhrá',
            deleteButton: 'Scrios',
            deleteConfirm: 'Delete this chat? It will disappear from your chats and cannot be restored from the product.',
            notFound: 'Comhrá gan aimsiú.',
            emptyChat: 'Níl aon teachtaireachtaí fós - abair dia duit chun comhrá a thosú.',
            roleUser: 'tu',
            roleAssistant: 'Forseti',
            roleSystem: 'Córas',
            thinkingLabel: 'Ag smaoineamh',
            toolCallPlaceholder: 'Glao uirlise: ',
            toolResultPlaceholder: 'Tool result hidden.',
            docEditPlaceholder: 'Athruithe doiciméad molta (V{version}).',
            disclaimer: 'Tacaíonn Forseti le do bhreithiúnas - ní thagann sé in ionad abhcóide ceadúnaithe.',
            sendButton: 'Seol',
            sending: 'Á sheoladh…',
            composePlaceholder: 'Iarr ar Forseti…',
            composingPlaceholder: 'Tá Forseti ag freagairt…',
            streamingPlaceholder: 'Tá Forseti ag clóscríobh…',
            promptInjectionTitle: 'Thit Forseti roinnt comharthaí rialaithe.',
            promptInjectionBody: 'Patrúin stiallacha: {patterns}. Ní fheicfidh Forseti ach téacs do theachtaireachta.',
            rateLimitTitle: 'Tá tú ag seoladh teachtaireachtaí ró-thapa.',
            rateLimitRetryAfter: 'Bain triail eile as i {seconds}s.',
            messageLength: '{count} / {max} characters',
            messageTooLong: 'Messages to Forseti are limited to {max} characters. Shorten the message or split it in two.',
            counselReviewTitle: 'Tá Forseti éiginnte anseo.',
            counselReviewBody: 'Deimhnigh leis an abhcóide ceadúnaithe sula ngníomhóidh tú ar an bhfreagra seo.',
            legalConclusionRefused: 'Dhiúltaigh Forseti conclúid dhlíthiúil a eisiúint gan aon bhunús leis. Bain triail as search_regulatory ar dtús nó formhéadaigh chuig abhcóide.',
            toolCallDone: 'Déanta.',
            toolCallArgsLabel: 'Argóintí',
            toolCallResultLabel: 'Toradh',
            toolResultLogged: 'Toradh na huirlise gafa sa loga iniúchta.',
            toolResultAria: 'toradh uirlis',
            documentEditAria: 'eagarthóireacht doiciméad',
            createTaskFromMessage: 'Cruthaigh tasc as seo',
            createTaskHeading: 'Cruthaigh tasc ón teachtaireacht seo',
            createTaskTitleLabel: 'Teideal',
            createTaskTitleRequired: 'Tá teideal ag teastáil.',
            createTaskDescriptionLabel: 'Cur síos',
            createTaskAssigneeLabel: 'Sannaí',
            createTaskAssigneePlaceholder: 'Roghnaigh sannaí…',
            createTaskAssigneeNone: '— neamhshannta —',
            createTaskDueDateLabel: 'Dáta dlite',
            createTaskWorkflowLabel: 'Sreabhadh oibre nasctha (roghnach)',
            createTaskWorkflowPlaceholder: 'Roghnaigh sreabhadh oibre…',
            createTaskWorkflowNone: '- aon cheann -',
            renameModalSubmit: 'Athainmnigh',
            renameModalSaving: 'Ag sábháil…',
            deleteConfirmTitle: 'An bhfuil fonn ort an comhrá a scriosadh?',
            createTaskSubmit: 'Cruthaigh tasc',
            createTaskSubmitting: 'Ag cruthú…',
            cancelButton: 'Cealaigh',
            autoRouteHintMessage: 'Tá an cheist seo níos mó i dteach rothaí {persona}.',
            autoRouteSwitchButton: 'Athraigh go {persona}',
            autoRouteSwitching: 'Ag aistriú…',
            handoffSuggestionMessage: 'Nó oscail comhrá nua le {persona} ag tabhairt na ceiste seo ar aghaidh.',
            handoffSuggestionButton: 'Cuir tús le comhrá nua le {persona}',
            handoffSuggestionWorking: 'Ag oscailt…',
            handoffSuggestionDismiss: 'Ní anois',
            handoffSuggestionFailed: 'The new chat could not be opened.',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Speisialtóir Comhlíontachta',
                'forseti-legal': 'Comhairleoir Dlí',
                'forseti-dpo': 'Comhairleoir DPO',
                'forseti-risk': 'Anailísí Riosca',
                'forseti-auditor': 'Súil an Iniúchóra',
            },
            exportButton: 'Easpórtáil PDF',
            exportRunning: 'Á easpórtáil…',
            exportSuccess: 'Easpórtáilte: {filename} (oscail Brabhsálaí Comhad).',
            exportOpenFile: 'Oscail i mBrabhsálaí Comhad',
            exportDownload: 'Íoslódáil PDF',
            citation: {
                verifyBadge: '[FÍORACH]',
                verifyAria: 'níorbh fhéidir an lua a fhíorú — athbhreithniú sula mbeifí ag brath',
                popoverAria: 'Foinse le haghaidh {label}',
                openSource: 'Leathanach foinse oscailte',
            },
        },
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: 'AI-imeacht a ghintear ó rian iniúchta Forseti.',
        },
        rowAction: {
            ask: 'Iarr ar Forseti',
            tooltipOpen: 'Cuir ceist ar Forseti faoi seo {recordType}',
            tooltipLocked: 'Tá an {recordType} seo faoi ghlas — is féidir le Forseti ceisteanna a fhreagairt ach ní féidir leis athruithe a dhéanamh.',
        },
        // ── Sprint 63.22.0 — the global Forseti launcher and its launchpad ──
        launcher: {
            openLabel: 'Open Forseti',
            tooltip: 'Ask Forseti (Ctrl+Shift+F)',
            modalTitle: 'Forseti',
            personaHeading: 'Choose who to ask',
            scopeHeading: 'What the chat is about',
            askAboutRecord: 'Ask about this {record}',
            generalChat: 'General chat',
            attachRecord: 'Attach a record',
            attachRecordRequired: 'Choose a record to attach, or choose General chat.',
            recordScopeLost: 'This page no longer shows a record, so this will be a general chat.',
            recentChats: 'Recent chats',
            recentEmpty: 'You have no chats yet.',
            resumeChat: 'Resume {title} — {persona}, {date}',
            startChat: 'Start chat',
            starting: 'Starting chat…',
            openFullView: 'Open in full view',
            loadFailed: 'Forseti could not load your personas and recent chats.',
            noPersonas: 'No Forseti persona is enabled for your organisation.',
        },
        advisorRunPanel: {
            // fix-sprint-20260627-2217-followup F11 — the chat's back control names
            // where it goes (WCAG 2.4.6); it returns to the launchpad.
            backToOptions: 'Back to Forseti options',
            title: 'Forseti - faoin taifead seo',
            heading: 'Forseti - {recordType}',
            summaryLabel: 'Achoimre',
            suggestedPersona: 'Pearsa molta',
            askForseti: 'Iarr ar Forseti',
            runWorkflow: 'Rith Sreabhadh Oibre',
            viewLineage: 'Féach ar Líne Iniúchta',
            createPdplEvidencePack: 'Cruthaigh pacáiste fianaise PDPL',
            prepareBreachDraft: 'Ullmhaigh dréacht fógra',
            primaryActions: {
                projectSummary: 'Achoimrigh doiciméad tionscadail',
                supplementSummary: 'Achoimrigh iarratas forlíontach',
                authoritySummary: 'Achoimrigh iarratas údaráis',
                rightsRequestResponse: 'Dréachtaigh freagra DSR',
                dpiaQuickReview: 'Rith athbhreithniú tapa DPIA',
                taskControlWalkthrough: 'Siúil trí fhianaise rialaithe',
                vendorRiskNarrative: 'Gin insint riosca soláthraí',
                incidentLetter: 'Dréachtaigh litir sáraithe PDPL',
                ropaSufficiency: 'Rith seiceáil leordhóthanachta RoPA',
                vendorDpaReview: 'Rith athbhreithniú DPA soláthraí',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'Ní ullmhaíonn Forseti ach ábhar dréachta don Mẫu số 08 nasctha. Ní chinneann sé an bhfuil fógra riachtanach agus ní chomhdaíonn ná ní sheolann sé an fhoirm.',
            breachDraftReady: 'Dréacht Mẫu số 08 réidh le hathbhreithniú',
            breachDraftReviewBeforeSelecting: 'Athbhreithnigh roimh roghnú',
            workflowHeading: 'Sreafaí oibre ábhartha',
            workflowLoading: 'Sreafaí oibre ábhartha á lódáil…',
            workflowEmpty: 'Níor aimsíodh sreabhadh oibre oiriúnach. Cruthaigh sreabhadh oibre treoraithe don taifead seo.',
            workflowError: 'Níorbh fhéidir sreafaí oibre ábhartha a lódáil.',
            selectWorkflow: 'Roghnaigh {title}',
            workflowSelected: 'Tá an sreabhadh oibre roghnaithe agus réidh le rith i gcoinne an taifid seo.',
            workflowRunSelected: 'Rith an sreabhadh oibre roghnaithe',
            workflowStarting: 'Sreabhadh oibre á thosú…',
            workflowRunError: 'Níorbh fhéidir an sreabhadh oibre seo a thosú.',
            workflowCreateGuided: 'Cruthaigh sreabhadh oibre treoraithe don taifead seo',
            lineageHeading: 'Líne iniúchta don taifead seo',
            lineageLoading: 'Líne iniúchta á lódáil…',
            lineageEmpty: 'Níl aon imeacht líne Forseti taifeadta don taifead seo.',
            lineageError: 'Níorbh fhéidir an líne iniúchta a lódáil.',
            retry: 'Bain triail eile as',
            chatContextLoading: 'Comhthéacs an taifid á lódáil…',
            chatGuidance: {
                general: 'Is féidir le Forseti achoimre a dhéanamh ar an taifead seo, faisnéis atá ar iarraidh a aithint agus céimeanna athbhreithnithe a mholadh.',
                privacy: 'Is féidir le Forseti fíricí príobháideachais a athbhreithniú, oibleagáidí na gcreataí suiteáilte a sheiceáil agus cabhrú le dréacht a ullmhú d’athbhreithniú daonna.',
                compliance: 'Is féidir le Forseti bearnaí comhlíontachta a aithint, ceanglais a bhunú ar fhoinsí suiteáilte agus na chéad chéimeanna cuntasacha eile a mholadh.',
                legal: 'Is féidir le Forseti achoimre a dhéanamh ar an taifead, ábhar foinse bunaithe a aimsiú agus cabhrú le dréacht a ullmhú d’athbhreithniú comhairleora dlí.',
                risk: 'Is féidir le Forseti rioscaí taifeadta a mheas, fíricí atá ar iarraidh a chur in iúl agus céimeanna athbhreithnithe comhréireacha a mholadh.',
                audit: 'Is féidir le Forseti bearnaí fianaise a scrúdú, an taifead a cheangal le rialuithe suiteáilte agus obair leantach atá réidh le hiniúchadh a mholadh.',
            },
            starterPrompts: {
                identifyGaps: 'Cén fhaisnéis nó fianaise atá ar iarraidh ón taifead seo?',
                reviewFrameworks: 'Cé na hoibleagáidí ónár gcreataí suiteáilte ba cheart dom a athbhreithniú don taifead seo?',
                prepareDraft: 'Cabhraigh liom dréacht a ullmhú bunaithe ar an taifead seo amháin agus ar fhoinsí suiteáilte bunaithe.',
                nextSteps: 'Cad iad na céimeanna athbhreithnithe ba cheart dom a ghlacadh anois?',
                summarizeRecord: 'Déan achoimre ar an taifead seo agus cuir in iúl na pointí a dteastaíonn athbhreithniú daonna uathu.',
                assessRisk: 'Meas na rioscaí taifeadta, aithin fíricí atá ar iarraidh agus mol céimeanna athbhreithnithe.',
            },
            disclaimer: 'Tacaíonn Forseti le do bhreithiúnas - ní thagann sé in ionad abhcóide ceadúnaithe.',
            recordType: {
                project: 'Tionscadal',
                dpia: 'DPIA',
                dossier_filing: 'Comhdú Comhad',
                transfer_assessment: 'Aistriú Trasteorann',
                vendor: 'Díoltóir',
                vendor_contract: 'Conradh Díoltóra',
                vendor_acceptance: 'Glacadh le Riosca Díoltóra',
                incident: 'teagmhas',
                incident_op: 'Oibriú Teagmhais',
                breach_notification: 'Fógra Sárú',
                authority_request: 'Iarratas Údaráis',
                supplement_request: 'Iarratas ar Fhorlíonadh',
                rights_request: 'Iarratas Cearta',
                contract: 'Conradh',
                task: 'Tasc',
                data_mapping_activity: 'Gníomhaíocht RoPA',
                compliance_rule: 'Riail Chomhlíonta',
                program: 'Clár',
                governance_document: 'Doiciméad Rialachais',
                remediation_plan: 'Plean Feabhsúcháin',
                cross_border_filing: 'Comhdú Trasteorann',
                enforcement_case: 'Enforcement Case',
                authority_direction: 'Authority Direction',
                regulated_account: 'Regulated Account',
                regulatory_placeholder: 'Regulatory Placeholder',
                sector_licence: 'Sector Licence',
                credit_information_supply: 'Credit-Information Supply',
                data_service_provider: 'Data-Service Provider Record',
                regulatory_disclosure: 'Regulatory Disclosure',
                issued_record: 'Student Record',
                publication: 'Publication',
            },
        },
        autocomplete: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this DPIA: no framework for this frame is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Iarr ar Forseti é seo a dhréachtú',
            drafting: 'Ag dréachtú…',
            hint_pick_activity: 'Roghnaigh gníomhaíocht phróiseála nasctha chun dréachtú Forseti a chumasú.',
            draft_origin_label: 'Forseti dréacht — athbhreithniú in aghaidh an réimse',
            tokens_label: '≈ {count} comharthaí ó do bhuicéad míosúil',
            accept_selected: 'Glac roghnaithe',
            reject_all: 'Diúltaigh dréacht',
            field_checkbox_label: 'Cuir dréacht Forseti i bhfeidhm ar {field}',
            field_labels: {
                description: 'Cur síos',
                purpose: 'Cuspóir',
                data_subjects: 'Ábhair sonraí',
                data_types: 'Cineálacha sonraí',
                necessity_justification: 'Riachtanas & comhréireacht',
                consultation_notes: 'Nótaí comhairliúcháin',
                dpo_opinion: 'DPO tuairim',
                system_ids: 'Córais nasctha',
                vendor_ids: 'Díoltóirí nasctha',
            },
            error_generic: 'Níorbh fhéidir le Forseti an DPIA seo a dhréachtú. Bain triail eile as nó mionnaigh an taifead gníomhaíochta.',
            variant_legend: 'Fráma fráma',
            variant_label_gdpr: 'GDPR',
            variant_none_available: 'Níl aon chreat suiteáilte ar fáil',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'ISO 27001',
            variant_label_soc2: 'SOC 2',
            conflict_banner: 'Chun tosaigh: {count} coinbhleacht/coimhlintí tras-chreatoibre maidir leis an leagan seo. Déan athbhreithniú sula nglacfar leis.',
        },
        formDraft: {
            button_label: 'Iarr ar Forseti an fhoirm seo a dhréachtú',
            drafting: 'Á dhréachtú…',
            draft_origin_label: 'Dréacht Forseti — déan athbhreithniú ar gach réimse',
            tokens_label: '≈ {count} comhartha as do chiste míosúil',
            accept_selected: 'Glac leis na cinn roghnaithe',
            reject_all: 'Diúltaigh don dréacht',
            field_checkbox_label: 'Cuir dréacht Forseti i bhfeidhm do {field}',
            excluded_note: 'Ní féidir {count} réimse a dhréachtú (tagairtí, ceangaltáin nó inléite amháin).',
            error_generic: 'Níorbh fhéidir le Forseti an fhoirm seo a dhréachtú. Bain triail eile as.',
            empty_draft: 'Níor tháirg Forseti aon dréacht réimse don fhoirm seo.',
        },
        tia: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this TIA: no framework that governs this transfer is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Iarr ar Forseti an TIA seo a dhréachtú',
            drafting: 'TIA á dhréachtú…',
            hint_pick_destination: 'Socraigh an tír chinn scríbe ar an taifead aistrithe sular féidir le Forseti dréachtú.',
            draft_origin_label: 'Dréacht TIA Forseti — athbhreithniú in aghaidh an réimse',
            tokens_label: '≈ {count} comharthaí ó do bhuicéad míosúil',
            accept_selected: 'Glac roghnaithe',
            reject_all: 'Diúltaigh dréacht',
            field_checkbox_label: 'Cuir dréacht Forseti i bhfeidhm ar {field}',
            error_generic: 'Níorbh fhéidir le Forseti an TIA seo a dhréachtú. Bain triail eile as nó mionnaigh an taifead aistrithe.',
            usage_details: 'Sonraí úsáide AI',
            applied_status: 'Draft accepted. The selected fields are saved to the transfer record.',
            recommended_updates_title: 'Nuashonruithe taifid molta',
            mechanism_recommendation: 'Meicníocht aistrithe: {value}',
            dpo_review_recommendation: 'Ask your DPO to review this sensitive transfer.',
            review_date_warning: 'Socraigh spriocdháta athbhreithnithe sula nuashonraítear an t-aistriú íogair seo.',
            processing_activity_warning: 'Nasc gníomhaíocht phróiseála / taifead ROPA amháin ar a laghad sula gcríochnaítear an TIA.',
            go_to_field: 'Téigh go dtí an réimse',
            adequacy_label: 'Réimeas cinn scríbe',
            adequacy_label_source_route: 'Transfer route',
            adequacy_status_adequacy_decision: 'EU adequacy decision (exports from the EU/EEA)',
            adequacy_status_non_adequacy_with_sccs: 'No EU adequacy decision — GDPR Art. 46 safeguard for exports from the EU/EEA',
            adequacy_status_derogation: 'GDPR Art. 49 derogation (exports from the EU/EEA)',
            adequacy_needs_action: 'Action needed',
            adequacy_for_information: 'For information',
            adequacy_status_transitional: 'aicearra idirthréimhseach/LEE',
            adequacy_status_unknown: 'Anaithnid - ceann scríbe socraithe',
            adequacy_status_source_jurisdiction_route: 'Transfer out of Vietnam — impact-assessment dossier route',
            adequacy_summary_no_destination: 'No destination country is set. Select the destination before Forseti drafts a TIA.',
            adequacy_summary_intra_eea: 'Transfer within the EEA ({code}) — for an export from the EU/EEA, no third-country TIA is required under GDPR Arts. 44–50. A transfer out of Vietnam is assessed under Law 91/2025/QH15 Art. 20 and Decree 356/2025/NĐ-CP Arts. 17–18 unless an evidenced exception applies.',
            adequacy_summary_inbound: 'Vietnam destination — an exporter in the EU/UK establishes its own transfer mechanism; the Vietnam-origin transfer impact-assessment rules do not apply to an inbound route. GDPR applies only where the record establishes an EU/EEA jurisdiction trigger.',
            adequacy_summary_us_dpf: 'US recipient certified under the Data Privacy Framework — for an export from the EU/EEA, covered by the European Commission adequacy decision of 10 July 2023 while the recipient stays on the active DPF list. Confirm the certification at https://www.dataprivacyframework.gov before relying on it.',
            adequacy_summary_us_no_dpf: 'US destination not under the DPF — for an export from the EU/EEA, no EU adequacy decision covers a non-certified recipient, and the Schrems II surveillance-law concerns (FISA §702, EO 12333) call for supplementary measures.',
            adequacy_summary_eu_adequacy: '{code} has an active European Commission adequacy decision — an export from the EU/EEA may proceed without SCCs or supplementary measures, subject to the Commission’s ongoing monitoring.',
            adequacy_summary_eu_no_adequacy: '{code} has no active EU adequacy decision. An export from the EU/EEA may rely on GDPR Art. 46 safeguards (standard contractual clauses, BCRs, certification or a code of conduct) with a Schrems II assessment of the destination, or on a GDPR Art. 49 derogation.',
            adequacy_summary_source_jurisdiction_route: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            adequacy_summary_source_jurisdiction_route_exemption: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. A possible exception is flagged from the record; it is not a confirmed exception and needs affirmative evidence and Compliance/Legal review. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            conflict_banner: 'Chun cinn: {count} coinbhleacht/coimhlintí tras-creatacha a bhaineann leis an gcóras aistrithe seo. Déan athbhreithniú sula nglacfar leis.',
            field_label_description: 'Description',
            field_label_purpose: 'Purpose',
            field_label_transfer_mechanism_detail: 'Transfer mechanism detail',
            field_label_recipient_name: 'Recipient name',
            field_label_recipient_details: 'Recipient details',
            field_label_data_subjects: 'Data subjects',
            field_label_data_types: 'Data types',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Fíorais riachtanacha ar iarraidh',
            missing_facts_hint: 'Chuir Forseti fíorais in iúl nach bhfuil sa taifead. Ní réamhroghnaítear réimsí a bhraitheann orthu — cuir na fíorais ar fáil nó glac leis an réimse go feasach.',
            risk_label: 'Riosca calabraithe',
            risk_low: 'Íseal',
            risk_medium: 'Meánach',
            risk_high: 'Ard',
            jurisdiction_label: 'Truicéir dlínse',
            jurisdiction_none_installed: 'Ní chlúdaíonn aon chreat suiteáilte an mheasúnacht seo — níor measúnaíodh aon réimeas dlíthiúil. Suiteáil pacáiste rialála cuí sula mbraithfidh tú ar na conclúidí dlíthiúla sa dréacht seo.',
            jurisdiction_none_assessed: 'Níor thug Forseti aon mheasúnacht dlínse ar ais. Níor measúnaíodh aon rud — ní torthaí é sin nach mbaineann aon réimeas leis.',
            jurisdiction_inactive: 'The framework covering this assessment is installed but no longer in force — so no legal regime was assessed. There is nothing to install: activate the current instrument, or the one that replaces it, before relying on the legal conclusions here.',
            jurisdiction_resolving: 'Ainmneacha na réimeas a measúnaíodh á lódáil…',
            trigger_applicable: 'Infheidhme',
            trigger_not_applicable: 'Gan truicéar',
            trigger_unknown: 'Anaithnid — fíoras ar iarraidh',
            recipient_role_label: 'Ról an fhaighteora',
            recipient_role_controller: 'Rialaitheoir',
            recipient_role_processor: 'Próiseálaí',
            recipient_role_joint_controller: 'Comhrialaitheoir',
            recipient_role_unknown: 'Anaithnid — gan lua',
            opt_in_hint: 'Conclúid dhlíthiúil — ná roghnaigh ach amháin má ghlacann tú léi leis na fíorais atá ar fáil.',
        },
        mcp: {
            pageTitle: 'Eochracha nascóirí MCP',
            pageSubtitle: 'Connect Claude, Slack agents and other MCP clients to Forseti. A connected client reads your organisation’s records within the access you grant, and anything it proposes to change waits in the approval queue until an administrator approves it.',
            issueButton: 'Eisigh eochair nua',
            issueModalTitle: 'Eisigh eochair MCP API eochair',
            labelField: 'Lipéad',
            labelHint: 'Cur síos gairid (m.sh. "AI saotharlann inniúlachta — taighde rialála").',
            permittedToolsField: 'Uirlisí ceadaithe',
            expiresAtField: 'In éag (roghnach)',
            expiresAtHint: 'Fág bán go n-éagann sé go deo.',
            submit: 'Eochair eisiúna',
            cancel: 'Cealaigh',
            keyDisplayedOnce: 'Cóipeáil an eochair seo anois - NÍ bheidh tú in ann í a fheiceáil arís. Déan é a stóráil i do bhainisteoir rúnda (1Password, Bainisteoir Rúin AWS, Cruinneachán, etc.).',
            keyDisplayedOnceClose: 'Chóipeáil mé é - dhúnadh',
            colLabel: 'Lipéad',
            colPrefix: 'Réimír',
            colStatus: 'Stádas',
            colTools: 'Uirlisí',
            colCreatedAt: 'Cruthaithe',
            colLastUsed: 'An uair dheireanach a úsáideadh',
            colCalls: 'Glaonna',
            colActions: 'Gníomhartha',
            statusActive: 'Gníomhach',
            statusRevoked: 'Cúlghairthe',
            statusExpired: 'imithe in éag',
            revokeAction: 'chúlghairm',
            rotateAction: 'Rothlaigh',
            confirmRevokeTitle: 'An bhfuil fonn ort an eochair MCP seo a chúlghairm?',
            confirmRevokeBody: 'Caillfidh cliaint sheachtracha Claude a úsáideann an eochair seo rochtain láithreach. Ní féidir é seo a chealú.',
            confirmRevoke: 'eochair a chúlghairm',
            rotatedTitle: 'Eochair rothlaithe',
            rotatedBody: 'Tá an eochair ghnáththéacs nua thíos. Leanann an seaneochair ag obair ar feadh 5 nóiméad agus mar sin críochnóidh aon ghlaonna eitilte.',
            emptyState: 'Níl aon eochracha MCP eisithe fós.',
            errorIssue: 'Níorbh fhéidir an eochair MCP a eisiúint.',
            errorList: 'Níorbh fhéidir eochracha MCP a lódáil.',
            errorRevoke: 'Níorbh fhéidir an eochair a chúlghairm.',
            errorRotate: 'Níorbh fhéidir an eochair a rothlú.',
            neverUsed: '—',
            neverExpires: 'Riamh',
            // Sprint 63.23.0 — the connector page leads with how to connect.
            reloadKeys: 'Reload API keys',
            issuing: 'Issuing…',
            labelRequired: 'Enter a label for the key.',
            toolsRequired: 'Choose at least one tool.',
            webhookField: 'Status callback URL (optional)',
            webhookHint: 'ComplianceOne posts the outcome of each queued write to this address, signed with a secret that is shown once.',
            webhookInvalid: 'Enter a full address, for example https://example.com/hooks/mcp.',
            webhookSecretLabel: 'Webhook signing secret',
            webhookSecretOnce: 'Copy the signing secret now. It is shown once.',
            toolGroupRead: 'Read tools',
            toolGroupWrite: 'Write tools',
            toolGroupWriteHint: 'A write tool never changes a record directly. Each call goes to the approval queue and runs only after an administrator approves it.',
            toolGroupMemory: 'Organisation memory',
            toolGroupSubagent: 'Persona handoff',
            toolQueued: 'goes to the approval queue',
            allToolsInScope: 'Every tool its scopes allow',
            copy: 'Copy',
            copyValue: 'Copy {label}',
            copiedValue: '{label} copied',
            copyFailed: 'Could not copy {label}. Select the value and copy it yourself.',
            apiKeyLabel: 'API key',
            registers: {
                clients: 'OAuth clients',
                grants: 'Connected apps',
                keys: 'API keys',
            },
            connection: {
                title: 'Connection details',
                intro: 'Every client needs the server URL. OAuth clients also use the addresses below; clients that use an API key send it as a bearer token.',
                serverUrl: 'Server URL',
                transport: 'Transport',
                transportStreamableHttp: 'Streamable HTTP',
                protectedResourceMetadataUrl: 'Protected resource metadata URL',
                asMetadataUrl: 'Authorization server metadata URL',
                authorizeUrl: 'Authorize URL',
                tokenUrl: 'Token URL',
                registrationUrl: 'Client registration URL',
                revokeUrl: 'Revocation URL',
                scopes: 'Scopes',
                loading: 'Loading connection details…',
                error: 'The connection details could not be read, so no server URL is shown.',
                reload: 'Reload connection details',
            },
            dcr: {
                label: 'Let clients register themselves',
                hint: 'When this is on, a client such as Claude can register itself and ask a signed-in user to approve it. It has no access until a user of this organisation approves it on the consent screen. Switching it off disconnects apps that registered themselves at their next call; clients registered below and API keys keep working. Switching it back on lets those apps work again.',
                stateOn: 'On — clients can register themselves.',
                stateOff: 'Off — only clients registered below can connect.',
                saving: 'Saving…',
                error: 'Could not change whether clients can register themselves.',
                confirmOffTitle: 'Switch off self-registration?',
                confirmOffBody: 'Apps that registered themselves, such as Claude, are disconnected at their next call for everyone in this organisation, and nobody can approve a new one. Clients registered below and API keys keep working. The connections are suspended, not revoked: switching self-registration back on lets them work again.',
                confirmOff: 'Switch off',
            },
            tabs: {
                label: 'How to connect',
                claude: 'Claude',
                slack: 'Slack and other agent builders',
                apiKey: 'API key',
            },
            claudeTab: {
                step1: 'In Claude, add a custom connector.',
                step2: 'Paste the server URL. Claude finds out how to sign in by itself.',
                step3: 'Sign in to ComplianceOne when asked, then read the consent screen and approve it.',
                consentHeading: 'What the consent screen shows',
                consentItemClient: 'the app’s name and the address it will send you back to;',
                consentItemOrg: 'your organisation;',
                consentItemScopes: 'what it may do — read your organisation’s records, and propose changes for approval if that is requested — and the tools that reach;',
                consentItemOrigin: 'whether the app registered itself or an administrator registered it.',
                actsAsUser: 'Claude then acts as the person who approved it, with no more access than that person’s own role gives them.',
                registrationOff: 'Clients cannot register themselves in this organisation, so Claude cannot connect with the server URL alone. Switch self-registration on, or register a client for Claude under OAuth clients.',
            },
            slackTab: {
                intro: 'Agent builders such as Slack ask for OAuth fields. Register a client under OAuth clients, then copy each value into the builder’s form.',
                colField: 'Field in the agent builder',
                colValue: 'Value',
                fieldServerUrl: 'MCP server URL',
                fieldClientId: 'Client ID',
                fieldClientSecret: 'Client secret',
                fieldAuthorizeUrl: 'Authorize URL',
                fieldTokenUrl: 'Token URL',
                fieldScopes: 'Scopes',
                valueClientId: 'Shown on the client’s row under OAuth clients.',
                valueScopes: 'Enter only the scopes shown on your client’s row under OAuth clients. A scope the client was not registered with is refused.',
                valueClientSecret: 'Shown once, when you register the client or rotate its secret. Store it in the agent builder straight away.',
                redirectNote: 'The agent builder shows the redirect URI it uses. Add it to the client exactly as shown: ComplianceOne sends people back only to a redirect URI registered on the client.',
                registerCta: 'Register a client',
            },
            apiKeyTab: {
                intro: 'Scripts and custom clients can use an API key instead of OAuth. Issue a key under API keys and send it on every request.',
                headerLabel: 'Header',
                configLabel: 'Client configuration (JSON)',
                curlLabel: 'Check the connection with curl',
                actsAsIssuer: 'A call made with a key acts as the administrator who issued it, limited to the tools chosen on the key.',
            },
            clients: {
                errorRevokedClient: 'This client has been revoked, so it has no secret to rotate.',
                paginationLabel: 'OAuth clients pages',
                intro: 'Clients that agent builders use with a client ID and secret. Clients that registered themselves are listed here too.',
                registerButton: 'Register client',
                empty: 'No OAuth clients yet.',
                error: 'The OAuth clients could not be read.',
                reload: 'Reload OAuth clients',
                colName: 'Name',
                colClientId: 'Client ID',
                colType: 'Type',
                colOrigin: 'Registered by',
                colRedirects: 'Redirect URIs',
                colScopes: 'Scopes',
                colTools: 'Tools',
                colLastUsed: 'Last used',
                colStatus: 'Status',
                colActions: 'Actions',
                typeConfidential: 'Confidential',
                typePublic: 'Public',
                originAdmin: 'An administrator',
                originDynamic: 'The client itself',
                statusActive: 'Active',
                statusRevoked: 'Revoked',
                rotateSecret: 'Rotate secret',
                revoke: 'Revoke',
                modalTitle: 'Register OAuth client',
                nameField: 'Name',
                nameHint: 'People see this name on the consent screen.',
                nameRequired: 'Enter a name.',
                typeField: 'Client type',
                typeConfidentialHint: 'Confidential — the client keeps a secret on a server. Use this for Slack and other agent builders.',
                typePublicHint: 'Public — the client cannot keep a secret (a desktop or browser app) and proves itself with PKCE instead.',
                redirectField: 'Redirect URIs',
                redirectHint: 'The exact addresses the client sends people back to. A loopback address such as localhost accepts any port.',
                redirectUriLabel: 'Redirect URI {n}',
                addRedirect: 'Add another redirect URI',
                removeRedirect: 'Remove redirect URI {n}',
                redirectInvalid: 'Enter a full address, for example https://example.com/callback.',
                redirectRequired: 'Add at least one redirect URI.',
                scopesField: 'Scopes',
                scopesRequired: 'Choose at least one scope.',
                toolsField: 'Limit to these tools (optional)',
                toolsHint: 'Leave every box clear to allow each tool the chosen scopes reach.',
                submit: 'Register client',
                saving: 'Registering…',
                errorCreate: 'Could not register the client.',
                createdTitle: 'Client registered',
                secretTitle: 'New client secret',
                secretOnce: 'Copy the client secret now. It is shown once and cannot be shown again; rotate the secret to get a new one.',
                publicNoSecret: 'This is a public client, so it has no secret. It signs in with PKCE.',
                clientIdLabel: 'Client ID',
                clientSecretLabel: 'Client secret',
                confirmRotateTitle: 'Rotate the client secret?',
                confirmRotateBody: 'The current secret stops working now. Put the new secret into the agent builder straight away.',
                errorRotate: 'Could not rotate the client secret.',
                confirmRevokeTitle: 'Revoke this OAuth client?',
                confirmRevokeBody: 'Every app connected through this client loses access now, and its connections are revoked. This cannot be undone.',
                confirmRevoke: 'Revoke client',
                errorRevoke: 'Could not revoke the client.',
            },
            grants: {
                errorNotFound: 'This connection no longer exists; it may already have been revoked.',
                paginationLabel: 'Connected apps pages',
                intro: 'Apps that people in this organisation approved on the consent screen. Revoking one stops its next call.',
                empty: 'No connected apps.',
                error: 'The connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colUser: 'Approved by',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this connection?',
                confirmRevokeBody: 'The app loses access now. The person can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke connection',
                errorRevoke: 'Could not revoke the connection.',
            },
            myApps: {
                errorNotFound: 'This app is no longer connected; it may already have been revoked.',
                title: 'Connected apps',
                intro: 'Apps you have allowed to use ComplianceOne on your behalf. Each acts as you, with no more access than your role gives you.',
                empty: 'You have not connected any apps.',
                error: 'Your connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this app?',
                confirmRevokeBody: 'The app loses access now. You can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke app',
                errorRevoke: 'Could not revoke the app.',
            },
        },
        memory: {
            pageTitle: 'Cuimhne Forseti',
            pageSubtitle: 'Brabhsáil agus coimeádaigh an comhthéacs org-shonrach atá carntha ag Forseti trasna comhráite agus cinntí. Glasáil iontrálacha coimeádaithe in aghaidh coinneála oíche agus fanann siad ar fáil do gach pearsa.',
            filterCategory: 'Catagóir',
            filterAllCategories: 'Gach catagóir',
            totalCount: '{count} iontráil',
            colAnchor: 'Ancaire',
            colCategory: 'Catagóir',
            colSource: 'Foinse',
            colContent: 'Ábhar',
            colCreatedAt: 'Cruthaithe',
            colActions: 'Gníomhartha',
            editButton: 'Cuir in eagar',
            deleteButton: 'Scrios',
            promoteButton: 'A chur chun cinn go coimeádaithe',
            emptyState: 'Níl cuimhní ar bith fós - carnfaidh Forseti comhthéacs agus tú ag comhrá.',
            categories: {
                framework_interpretation: 'Léiriú creata',
                process_preference: 'Rogha próisis',
                escalation_threshold: 'Tairseach ardaithe',
                decision_context: 'Comhthéacs cinnidh',
                glossary_term: 'Téarma gluais',
                auto_inferred: 'Auto-tátal',
            },
            sources: {
                admin_curated: 'Coimeádta',
                user_explicit: 'follasach',
                auto: 'Tátal a bhaint as',
            },
            deleteConfirmTitle: 'An bhfuil fonn ort an chuimhne seo a scriosadh?',
            deleteConfirmBody: 'Má scriostar é, bainfear an t-ancaire seo de gach tosaithe comhrá Forseti amach anseo. Ancaire: {anchor}',
            deleteConfirmLabel: 'Scrios',
            deleteCancelLabel: 'Cealaigh',
            editModalTitle: 'Cuir cuimhne in eagar',
            editAnchorLabel: 'Ancaire',
            editCategoryLabel: 'Catagóir',
            editContentLabel: 'Ábhar',
            editContentCount: '{n}/{max} carachtar',
            editEmptyError: 'Ní féidir leis an ábhar a bheith folamh.',
            editTooLongError: 'Caithfidh an t-ábhar a bheith ≤1000 carachtar.',
            editAutoPromoteHint: 'Má shábhálann tú an chuimhne uathoibríoch seo, marcálfar é mar chomhartha deimhnithe ag an úsáideoir agus bainfear den scuab coinneála é.',
            editCancelButton: 'Cealaigh',
            editSaveButton: 'Sábháil',
            editSaving: 'Ag sábháil…',
            loadFailed: 'The memories could not be loaded.',
            deleteFailed: 'The memory could not be deleted.',
            promoteFailed: 'The memory could not be promoted to curated.',
            saveFailed: 'The memory could not be saved.',
        },
        auditLineage: {
            title: 'Iniúchadh lineage',
            subtitle: 'Lorg gach AI déantán ginte atá ceangailte de thaifead. Comhráite, glaonna uirlisí, tascanna, dréachtaí fianaise, agus leaganacha doiciméad.',
            filterForm: 'Scagairí',
            recordType: 'Cineál taifead',
            recordId: 'Aitheantas Taifeadta',
            dateStart: 'Ó',
            dateEnd: 'Chuig',
            search: 'Cuardach',
            loading: 'Á lódáil…',
            exportPdf: 'Easpórtáil PDF',
            exportCsv: 'Easpórtáil CSV',
            nodesHeading: 'Nóid lineage',
            colType: 'Cineál',
            colLabel: 'Lipéad',
            colOccurredAt: 'Tharla ag',
            colActor: 'Aisteoir',
            colId: 'ID',
            noResults: 'Gan aon ghníomhaíocht chomhairleach don taifead seo.',
            draftBadge: 'AI-dréacht ginte',
            prev: 'Roimhe Seo',
            next: 'Ar aghaidh',
            viewToggleTable: 'Féach mar tábla',
            viewToggleGraph: 'Féach mar ghraf',
            graphAriaLabel: 'Graf líneála (amhairc). Úsáid an tábla don teicneolaíocht chúnta.',
            graphLegendDraft: 'Dréacht AI fianaise',
            graphEmpty: 'Gan nóid don ghraf.',
            paginationAriaLabel: 'Leathanaigh',
            exportSuccessTemplate: '{format}: {filename}',
            exportErrorTemplate: 'Theip ar easpórtáil: {message}',
            truncatedNotice: 'Níl an líneáil seo iomlán. Léann an ardán uasmhéid seasta sraitheanna ó gach foinse, agus baineadh an teorainn amach do: {sources}. Ní thaispeántar sraitheanna thar teorainn, má tá aon cheann ann. Cuir an raon dátaí i gcúinge chun iad a thabhairt isteach.',
            truncatedChatLinksNotice: 'This lineage is not complete: only the {limit} newest chats of this record are used to find the tool calls, workflow runs, tasks, evidence and document versions that were started from a chat. Anything reached only through an older chat is not shown, and narrowing the date range does not bring it in.',
            recordTypes: {
                project: 'Tionscadal',
                dpia: 'DPIA',
                transfer_assessment: 'Cross-border transfer assessment',
                dossier_filing: 'Dossier filing',
                cross_border_filing: 'Cross-border filing',
                vendor: 'Díoltóir',
                incident: 'teagmhas',
                incident_op: 'Oibriú teagmhas',
                rights_request: 'Iarratas ar chearta',
                contract: 'Conradh',
                task: 'Tasc',
                data_mapping_activity: 'Gníomhaíocht mapála sonraí',
                compliance_rule: 'Riail comhlíonta',
                program: 'Clár',
            },
            nodeTypes: {
                chat: 'Comhrá',
                tool_call: 'Glao uirlise',
                workflow_run: 'Rith sreabhadh oibre',
                task: 'Tasc',
                evidence: 'Fianaise',
                document_version: 'Leagan doiciméad',
            },
            recordIdManualHint: "This record type has no list here. Enter the record's own id, as it appears in the address of its page.",
            recordIdManualLabel: 'Record ID',
            colDetails: 'Details',
        },
        integrityCenter: {
            verdicts: {
                ok: 'ceart go leor',
                drifted: 'Drifted',
                hash_mismatch: 'Mímheaitseáil hash',
                merkle_mismatch: 'Mímheaitseáil Merkle',
                source_missing: 'Foinse in easnamh',
                not_found: 'Ní bhfuarthas',
                chain_unreachable: 'Slabhra dosroichte',
                service_unreachable: 'Ní féidir teacht ar an tseirbhís',
                pending: 'Ar feitheamh ancaireachta',
            },
        },
        evidenceDraft: {
            draftBadge: 'AI-ghinte DRÉACHT',
            pendingReviewBadge: 'Athbhreithniú ar feitheamh',
            approvedBadge: 'Ceadaithe',
            rejectedBadge: 'Diúltaithe',
            moveToReviewButton: 'Marcáil réidh le haghaidh athbhreithnithe',
            approveButton: 'Cheadaigh',
            rejectButton: 'Diúltaigh',
            rejectReasonLabel: 'Cúis diúltaithe',
            humanSignoffBanner: 'Tacaíonn Forseti le do bhreithiúnas - ní thagann sé in ionad abhcóide ceadúnaithe. AI-éilíonn fianaise a ghintear formheas an duine sula n-áirítear mar fhianaise fhoirmiúil.',
            pageTitle: 'AI dréachtaí fianaise',
            pageSubtitle: 'AIfianaise a ghintear a athbhreithniú, a cheadú nó a dhiúltú. Ní mór do dhréachtaí a bheith ceadaithe ag duine sula n-áirítear iad mar fhianaise fhoirmiúil.',
            filterAll: 'Gach inchaingne',
            filterDrafts: 'Dréachtaí amháin',
            filterPending: 'Athbhreithniú ar feitheamh',
            filterApproved: 'Ceadaithe le déanaí',
            filterRejected: 'Le déanaí diúltaithe',
            colId: 'Aitheantas Fianaise',
            colStatus: 'Stádas',
            colSource: 'Foinse',
            colCreatedAt: 'Cruthaithe',
            colActions: 'Gníomhartha',
            empty: 'Níl AI dréachtaí fianaise sa stádas seo.',
            confirmApprove: 'An bhfuil fonn ort an AI-fianaise seo a ghintear a cheadú mar fhianaise fhoirmiúil?',
            confirmReject: 'Diúltaigh an dréacht fianaise seo. Ní féidir é seo a chealú.',
            reasonRequired: 'Tá gá le cúis diúltaithe.',
            rejectModalTitle: 'Diúltaigh dréachtfhianaise',
            approveModalTitle: 'Dréacht-fhianaise a cheadú',
            operationFailed: 'Theip ar an oibríocht. Bain triail eile as.',
            moveToReviewSuccess: 'Marcáilte réidh le haghaidh athbhreithnithe.',
            approveSuccess: 'Ceadaithe.',
            rejectSuccess: 'Diúltaithe.',
            sourceLabelWorkflowRunTitle: 'Sreabhadh oibre: {title}',
            sourceLabelWorkflowRunId: 'Rith sreafa oibre {id}',
            sourceLabelChatTitle: 'Comhrá: {title}',
            sourceLabelChatId: 'Forseti comhrá {id}',
            sourceLabelEmpty: '—',
            filtersAriaLabel: 'Stádas an scagaire',
        },
        workflows: {
            pageTitle: 'Sreafaí Oibre IS',
            pageSubtitle: 'Sábháilte AI teimpléid leide a tháirgeann tascanna agus a dhréachtaíonn fianaise.',
            newWorkflow: 'Sreabh oibre nua',
            newWorkflowHeading: 'Sreabh oibre IS nua',
            tabAll: 'Gach ceann',
            tabBuiltin: 'Pacáiste ionsuite',
            tabMine: 'Mo shreafaí oibre',
            tabDetail: 'Sonraí',
            tabLinkedTasks: 'Tascanna nasctha',
            builtinBadge: 'Ionsuite',
            builtinSubtitle: 'Sreabh oibre ionsuite.',
            userSubtitle: 'Sreabh oibre saincheaptha.',
            emptyHint: 'Níl aon sreabh oibre go fóill.',
            loadTimeoutError: 'Níorbh fhéidir sreafaí oibre a luchtú tar éis 10 soicind. Bain triail eile as, nó seiceáil cluaisín Líonra an bhrabhsálaí le haghaidh iarratas ar crochadh.',
            loadError: 'Theip ar shreafaí oibre a luchtú.',
            hideError: 'Theip ar an bhfolú.',
            unhideError: 'Theip ar an taispeáint.',
            retry: 'Bain triail eile as',
            producesTasks: 'Táirgeann tascanna',
            producesEvidence: 'Táirgeann fianaise dréachta',
            hideButton: 'Folaigh ó mo liosta',
            unhideButton: 'Taispeáin',
            runButton: 'Rith',
            editButton: 'Cuir in eagar',
            shareButton: 'Roinn',
            backToList: 'Ar ais go dtí an liosta',
            requireTitleAndPrompt: 'Tá teideal agus leid riachtanach.',
            fieldTitle: 'Teideal',
            fieldDescription: 'Cur síos',
            fieldType: 'Cineál',
            fieldPersona: 'Cleamhnaíocht persona',
            fieldPractice: 'Cleachtas',
            fieldPrompt: 'Leid (Markdown)',
            fieldProducesTasks: 'Táirgeann tascanna',
            fieldProducesEvidence: 'Táirgeann fianaise dréachta',
            fieldProjectId: 'Tionscadal (roghnach)',
            fieldAttachmentIds: 'Iatáin',
            fieldParentRecord: 'Taifead máthair (roghnach)',
            fieldPersonaOverride: 'Sárú persona (roghnach)',
            typeAssistant: 'Cúntóir',
            typeTabular: 'Tablach',
            personaNone: '— gan aon —',
            parentNone: '— gan aon —',
            parentIdPlaceholder: 'Aitheantas an taifid mháthar',
            selectProjectPlaceholder: 'Roghnaigh tionscadal…',
            selectProjectFirst: 'Roghnaigh tionscadal chun iatáin a roghnú.',
            selectAttachmentsPlaceholder: 'Roghnaigh cáipéisí…',
            noAttachmentsInProject: 'Níl aon cáipéisí ceangailte ag an tionscadal seo.',
            selectParentRecordPlaceholder: 'Roghnaigh taifead…',
            selectParentRecordFirst: 'Roghnaigh cineál taifid ar dtús.',
            noRecordsForType: 'Níl aon taifid den chineál seo ar fáil.',
            shareUserSelectorLabel: 'Faighteoirí (úsáideoirí)',
            shareGroupSelectorLabel: 'Faighteoirí (grúpaí eagraíochta)',
            selectUsersPlaceholder: 'Roghnaigh úsáideoirí…',
            selectOrgGroupsPlaceholder: 'Roghnaigh grúpaí eagraíochta…',
            loadingOptions: 'Á luchtú…',
            loadFailed: 'Theip ar luchtú na roghanna.',
            parentRecordTypes: {
                dpia: 'DPIA',
                vendor: 'Díoltóir',
                incident: 'Eachtra',
                rights_request: 'Iarratas Cearta',
                contract: 'Conradh',
                data_mapping_activity: 'Gníomhaíocht RoPA',
                compliance_rule: 'Riail Chomhlíontachta',
                program: 'Clár',
            },
            cancelButton: 'Cealaigh',
            closeButton: 'Dún',
            createButton: 'Cruthaigh',
            creating: 'Á chruthú…',
            saveButton: 'Sábháil',
            saving: 'Á shábháil…',
            shareWorkflowHeading: 'Roinn sreabh oibre',
            shareIntro: 'Á roinnt: {title}',
            shareUserIds: 'Aitheantais úsáideora (scartha le spás nó camóg)',
            shareGroupIds: 'Aitheantais grúpa eagraíochta (scartha le spás nó camóg)',
            shareAllowEdit: 'Ceadaigh do faighteoirí eagarthóireacht a dhéanamh',
            shareRequireRecipient: 'Soláthair aitheantas úsáideora amháin nó aitheantas grúpa amháin ar a laghad.',
            shareSuccess: 'Roinnte.',
            sharing: 'Á roinnt…',
            runWorkflowHeading: 'Rith: {title}',
            runStarting: 'Ag tosú…',
            runStatusLabel: 'Stádas: {status}',
            runProducedCounts: 'Tascanna: {tasks} · Fianaise dréachta: {evidence} · Iatáin: {attachments}',
            runPanelHeading: 'Rith sreabha oibre',
            runPanelHeadingWithTitle: 'Rith sreabha oibre: {title}',
            runViewTasks: 'Féach ar na tascanna ginte',
            runViewEvidence: 'Féach ar dhréachtaí fianaise',
            runResultHeading: 'Toradh an chomhairleora',
            runNoResult: 'Chríochnaigh an rith ach níor thug sé toradh in-athbhreithnithe ar ais. Níor cruthaíodh aon tasc, fianaise ná ceangaltán. Bain triail eile as agus an taifead nó na cáipéisí atá riachtanach roghnaithe.',
            runCitationWarning: 'Ní fhéadfaí roinnt luanna a fhíorú go huathoibríoch. Athbhreithnigh iad sula mbraitheann tú ar an toradh seo.',
            runToolCallWarning: 'Forseti attempted a tool this workflow could not run, and returned the attempt as text. Nothing was produced from it.',
            runNoEnvelopeWarning: 'Forseti returned a result this workflow could not read, so no tasks or evidence were created from it.',
            runNoArtefactsWarning: 'This run completed without creating any tasks or draft evidence. Review the result before treating the work as done.',
            runLoadFailed: 'Níorbh fhéidir rith an tsreafa oibre a lódáil.',
            runStreamFailed: 'Theip ar an gceangal le dul chun cinn an tsreafa oibre.',
            runStatusValues: {
                pending: 'Ar feitheamh',
                running: 'Ag rith',
                completed: 'Críochnaithe',
                succeeded: "D'éirigh leis",
                failed: 'Theip',
                cancelled: 'Cealaithe',
            },
            runProgressStatus: 'Stádas: {status}',
            runProgressTaskCreated: 'Tasc cruthaithe.',
            runProgressEvidenceDrafted: 'Fianaise dréachta cruthaithe.',
            runProgressAttachmentGenerated: 'Iatán ginte.',
            guidedWorkflowTitle: 'Sreabhadh oibre treoraithe {recordType}',
            guidedWorkflowDescription: 'Cruthaithe do thaifid {recordType}. Déan athbhreithniú ar an sreabhadh oibre seo agus saincheap é sula sábhálfar é.',
            guidedWorkflowPrompt: 'Déan athbhreithniú ar an taifead {recordType} a soláthraíodh, aithin fíricí atá ar iarraidh agus mol na chéad chéimeanna bunaithe eile d’athbhreithniú daonna.',
            promptHeading: 'Leid',
            howItWorksHeading: 'Céard a tharlaíonn nuair a ritheann tú an sreabhadh oibre seo',
            builtinGuidance: 'Déanann Forseti athbhreithniú ar na taifid agus na cáipéisí a roghnaíonn tú, cuireann sé ar ais toradh in-athbhreithnithe don oibreoir agus ní chruthaíonn sé tascanna nó fianaise dhréachtaithe ach amháin nuair a thacaíonn an toradh leo. Ní dhéantar aon rud a cheadú ná a chur isteach go huathoibríoch.',
            editWorkflowHeading: 'Cuir an sreabh oibre in eagar',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list, filtered by this workflow’s ID.',
            notFound: 'Níor aimsíodh an sreabh oibre.',
            schedules: {
                tabTitle: 'Sceidil',
                sectionHeading: 'Rití sceidealta',
                empty: 'Níl aon sceidil go fóill. Cruthaigh ceann chun an sreabh oibre seo a rith go huathoibríoch.',
                addButton: 'Cuir sceideal leis',
                addHeading: 'Sceidealaigh sreabh oibre',
                editHeading: 'Cuir an sceideal in eagar',
                fieldFrequency: 'Minicíocht',
                fieldDayOfWeek: 'Lá den tseachtain',
                fieldDayOfMonth: 'Lá den mhí',
                fieldTime: 'Am (UTC)',
                fieldTimezone: 'Crios ama',
                fieldProject: 'Tionscadal (roghnach)',
                fieldAttachments: 'Iatáin réamhshocraithe',
                fieldPersonaOverride: 'Sárú persona (roghnach)',
                frequencyDaily: 'Laethúil',
                frequencyWeekly: 'Seachtainiúil',
                frequencyMonthly: 'Míosúil',
                weekdaySun: 'Dé Domhnaigh',
                weekdayMon: 'Dé Luain',
                weekdayTue: 'Dé Máirt',
                weekdayWed: 'Dé Céadaoin',
                weekdayThu: 'Déardaoin',
                weekdayFri: 'Dé hAoine',
                weekdaySat: 'Dé Sathairn',
                statusActive: 'Gníomhach',
                statusPaused: 'Sosaithe',
                nextRunLabel: 'An chéad rith eile',
                lastRunLabel: 'An rith deireanach',
                pauseButton: 'Sos',
                resumeButton: 'Atosaigh',
                deleteButton: 'Scrios',
                triggerButton: 'Rith anois',
                savingButton: 'Á shábháil…',
                saveButton: 'Sábháil',
                triggered: 'Tosaíodh an rith — féach ar stair na rití.',
            },
            branching: {
                helpHeading: 'Comhréir leid choinníollach',
                helpBody: 'Use {% if persona.slug == "forseti-legal" %}…{% endif %} to vary the prompt by context. Variables with a value: persona.slug (the persona chosen when the run starts, otherwise the workflow’s persona), org.country and org.locale (your organisation’s country and default language), and project.name (the project the run belongs to). attachment.framework_code, attachment.locale and project.tier are accepted but currently have no source, so {{ }} on them renders empty and a condition on them is always false. Nested {% if %} blocks are not supported.',
            },
            runCancelButton: 'Cancel run',
            runCancelling: 'Cancelling…',
            runCancelRequested: 'Cancellation requested. The model request already under way cannot be recalled, but when it returns its answer is discarded and no tasks, draft evidence or files are created.',
            runCancelledNote: 'This run was cancelled. No tasks, draft evidence or files were created from it.',
            runCancelFailed: 'Could not cancel the run.',
        },
        crossFrameworkConflicts: {
            pageTitle: 'Coinbhleachtaí tras-creatacha',
            pageSubtitle: 'Catalóg coimeádta de na háiteanna ina n-idirghníomhaíonn do phacaí rialála suiteáilte ar bhealaí contrártha. dromchlaí Forseti; chinneann daoine.',
            noInstalledPacks: 'Níl aon phacáistí rialála suiteáilte don eagraíocht seo — suiteáil ar a laghad dhá phaca chun aghaidh a thabhairt ar choinbhleachtaí tras-creatacha.',
            noConflicts: 'Níl aon choinbhleacht ag teacht leis na scagairí reatha.',
            allAcknowledged: 'Admhaíodh gach coinbhleacht infheicthe. Obair deas.',
            columnSeverity: 'Déine',
            columnTitle: 'Teideal',
            columnFrameworks: 'Creataí',
            columnTopic: 'Topaic',
            columnStatus: 'Stádas',
            filterFrameworkPlaceholder: 'Gach creatlach',
            filterTopicPlaceholder: 'Gach ábhar',
            filterSeverityPlaceholder: 'Gach déine',
            filterStatusPlaceholder: 'Gach stádas',
            filterStatusAcknowledged: 'Admhaítear',
            filterStatusUnacknowledged: 'Aineolach',
            severityCritical: 'Criticiúil',
            severityHigh: 'Ard',
            severityMedium: 'Meánach',
            severityLow: 'Íseal',
            statusAcknowledged: 'Admhaítear',
            statusInReview: 'In athbhreithniú',
            statusResolved: 'Réitíodh',
            statusWontFix: 'Ní bheidh a shocrú',
            statusUnacknowledged: 'Aineolach',
            drawerSummaryHeading: 'Achoimre',
            drawerSideAHeading: 'Taobh A',
            drawerSideBHeading: 'Taobh B',
            drawerRequirementsLabel: 'Ceanglais a luadh',
            drawerProvisionsLabel: 'Forálacha a luadh',
            drawerSuggestedResolutionHeading: 'Rún molta',
            drawerLegalCounselBadge: 'Athbhreithniú abhcóide dlí riachtanach',
            drawerAckHeading: 'Admháil',
            drawerAckStatusLabel: 'Stádas',
            drawerAckAssigneeLabel: 'Sannta do',
            drawerAckDueDateLabel: 'Dáta dlite',
            drawerAckNotesLabel: 'Nótaí',
            drawerAckButtonAcknowledge: 'Admháil',
            drawerAckButtonUpdate: 'Nuashonrú',
            drawerAckButtonUnacknowledge: 'Gan admháil',
            // fix-sprint-20260912-2123 F23 — R-57: structural parity, EN value carried.
            drawerCloseLabel: 'Close',
            drawerAckAssigneePlaceholder: 'User ID',
            drawerAckSaveFailed: 'Could not save the acknowledgement.',
            drawerAckUnacknowledgeFailed: 'Could not remove the acknowledgement.',
            loadFailed: 'Could not load conflicts.',
            searchPlaceholder: 'Cuardaigh teideal nó achoimre…',
            searchButton: 'Cuardach',
            searchButtonAriaLabel: 'Rith cuardach coimhlinte',
            exportSuccessTemplate: 'CSV: {filename}',
            exportSuccessTemplateXlsx: 'XLSX: {filename}',
            exportErrorTemplate: 'Theip ar easpórtáil: {message}',
            exportAriaLabel: 'Easpórtáil coinbhleachtaí',
            // fix-sprint D1 F23 — the page-level load failure now renders through
            // `withRetry`, which needs the control's label, its busy label and the
            // subject a screen reader hears after it.
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'the conflict list',
        },
        tabularReviews: {
            pageTitle: 'Athbhreithnithe Tablacha',
            pageSubtitle: 'Cuir N doiciméad i gcomparáid le tréithe M le luanna bunaithe.',
            newReview: 'Athbhreithniú nua',
            newReviewHeading: 'Athbhreithniú tablach nua',
            emptyHint: 'Níl aon athbhreithniú tablach go fóill.',
            docsLabel: '{n} cáipéis',
            colsLabel: '{n} colún',
            detailSubtitle: '{docs} cáipéis · {cols} colún',
            addColumn: 'Cuir colún leis',
            addColumnHeading: 'Cuir colún leis',
            columnInfo: 'Taispeáin leid agus formáid',
            columnInfoAria: 'Taispeáin leid agus formáid don cholún {name}',
            columnInfoPrompt: 'Leid',
            columnInfoFormat: 'Formáid aischuir',
            columnInfoPresets: 'Luachanna ceadaithe',
            columnInfoCitations: 'Lua',
            columnInfoCitationsRequired: 'Riachtanach',
            removeColumn: 'Bain colún',
            removeColumnAria: 'Bain colún {name}',
            removeColumnConfirm: 'An colún "{name}" a bhaint? Scriosfar a chealla bainte.',
            deleteReview: 'Scrios',
            deleteReviewAria: 'Scrios athbhreithniú {title}',
            deleteReviewConfirm: 'An t-athbhreithniú "{title}" a scriosadh? Baintear a cholúin, a chealla agus a phlé go buan.',
            runAll: 'Rith gach ceann',
            running: 'Ag rith…',
            cancel: 'Cealaigh',
            exportXlsx: 'Easpórtáil Excel',
            exportCsv: 'Easpórtáil CSV',
            exporting: 'Á easpórtáil…',
            backToList: 'Ar ais go dtí an liosta',
            notFound: 'Níor aimsíodh an t-athbhreithniú tablach.',
            loadFailed: 'The tabular review could not be read. Reload to try again.',
            emptyPayload: 'The server answered without the tabular review. Reload to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'this tabular review',
            cancelFailed: 'The run could not be cancelled.',
            removeColumnFailed: 'The column could not be removed.',
            exportFailed: 'The export could not be produced.',
            noDocuments: 'Cuir cáipéisí leis chun an t-athbhreithniú a thosú.',
            noColumns: 'Cuir colúin leis chun a shainiú cad atá le baint.',
            tableAriaLabel: 'Eangach bainte',
            threadsCountAria: '{n} post(anna) plé',
            startDiscussionAria: 'Tosaigh plé',
            docColumn: 'Cáipéis',
            statusPending: 'Ar feitheamh',
            statusRunning: 'Ag rith…',
            statusFailed: 'Theip: {msg}',
            statusCancelled: 'Cealaithe',
            citationsCount: '{n} lua/luanna',
            citationsView: 'Féach sleachta foinse',
            citationPage: 'Leathanach {n}',
            titleRequired: 'Tá teideal riachtanach.',
            fieldTitle: 'Teideal',
            fieldProjectId: 'Tionscadal (roghnach)',
            fieldAttachmentIds: 'Iatáin',
            fieldColumnName: 'Ainm an cholúin',
            fieldColumnNameHelp: 'Lipéad gairid, tuairisciúil don cholún seo sa ghreille bainte.',
            columnNamePlaceholder: 'm.sh. Dáta tosaithe conartha',
            fieldExtractionPrompt: 'Leid bainte',
            fieldExtractionPromptHelp: 'Déan cur síos ar an eolas is mian leat a bhaint. Bí sonrach chun torthaí níos fearr a fháil.',
            columnPromptPlaceholder: 'Cad ba chóir do Forseti a lorg sna doiciméid?',
            fieldReturnFormat: 'Formáid an aischuir',
            fieldReturnFormatHelp: 'Rialaíonn sé an chaoi a bhformáidíonn an IS a fhreagra (m.sh. mar dháta nó uimhir).',
            fieldPresets: 'Luachanna ceadaithe (ceann sa líne)',
            fieldPresetsPlaceholder: 'tá\nníl\ncuid',
            fieldCitationsRequired: 'Lua riachtanach',
            formatShortText: 'Téacs gairid',
            formatLongText: 'Téacs fada',
            formatEnum: 'Enum',
            formatNumber: 'Uimhir',
            formatDate: 'Dáta',
            formatList: 'Liosta',
            columnRequireFields: 'Tá ainm agus leid riachtanach.',
            addColumnError: 'Theip ar an gcolún a chur leis.',
            cancelButton: 'Cealaigh',
            createButton: 'Cruthaigh',
            creating: 'Á chruthú…',
            addButton: 'Cuir leis',
            adding: 'Á chur leis…',
            runConfirmHeading: 'Rith bainte',
            estimateBody: 'Tuairim ~{units} aonad inbhilleála ({confidence} muinín).',
            starting: 'Ag tosú…',
            confirmRun: 'Deimhnigh an rith',
            charts: {
                title: 'Cairteacha colún',
                tabsAria: 'Colúin chairte',
                enumAria: 'Dáileadh luachanna enum',
                numberAria: 'Histeagram dáileacháin uimhriúil',
                dateAria: 'Amlíne dátaí',
                enumEmpty: 'Níl aon chillíní críochnaithe.',
                numberEmpty: 'Níl aon luachanna uimhriúla.',
                numberExcluded: '{n} cill(eanna) eisiata (folamh / neamhuimhriúil / gan críoch).',
                dateEmpty: 'Níl aon luachanna dáta.',
                dateExcluded: '{n} cill(eanna) eisiata.',
                unsupportedFormat: 'Formáid colúin gan tacaíocht.',
            },
            threads: {
                drawerTitle: 'Plé',
                loading: 'Á luchtú…',
                empty: 'Níl aon phost go fóill. Tosaigh an comhrá.',
                composerLabel: 'Trácht nua',
                composerPlaceholder: 'Cuir trácht leis…',
                post: 'Postáil',
                posting: 'Á phostáil…',
                edit: 'Cuir in eagar',
                editLabel: 'Cuir an post in eagar',
                delete: 'Scrios',
                save: 'Sábháil',
                cancel: 'Cealaigh',
                close: 'Dún',
                editedBadge: 'eagaraithe',
                loadError: 'Theip ar an snáithe a luchtú',
                postError: 'Theip ar an bpostáil',
                editError: 'Theip ar an eagarthóireacht',
                deleteError: 'Theip ar an scriosadh',
            },
            compare: {
                pageTitle: 'Cuir athbhreithnithe i gcomparáid',
                leftReviewLabel: 'Athbhreithniú clé',
                rightReviewLabel: 'Athbhreithniú deas',
                pickReview: 'Roghnaigh athbhreithniú le cur i gcomparáid',
                noSharedDocuments: 'Níl aon cháipéisí roinnte ag na hathbhreithnithe seo — éilíonn comparáid iatán comónta amháin ar a laghad.',
                sharedDocumentsHeading: 'Cáipéisí roinnte',
                leftOnlyHeading: 'Colúin sa athbhreithniú clé amháin',
                rightOnlyHeading: 'Colúin sa athbhreithniú deas amháin',
                differs: 'Difriúil',
                identical: 'Comhionann',
                emptyValue: '—',
                compareButton: 'Cuir i gcomparáid le…',
                documentHeading: 'Cáipéis',
                errorMissingIds: 'Tá aitheantais an athbhreithnithe chlé agus dheas riachtanach.',
                errorComparisonFailed: 'Theip ar an gcomparáid.',
                errorLoadFailed: 'Theip ar luchtú na n-athbhreithnithe.',
                pickerLoading: 'Á luchtú…',
                pickerNoOtherReviews: 'Níl aon athbhreithnithe eile ar fáil le cur i gcomparáid.',
                pickerCancel: 'Cealaigh',
            },
        },
        documentReview: {
            pageTitle: 'Athbhreithniú doiciméad',
            pageSubtitle: 'Glac le nó diúltaigh AI-athruithe a mholtar ar an doiciméad seo.',
            sidePanelLabel: 'AI dearglínte',
            sidePanelHeading: 'AI dearglínte',
            noPendingEdits: 'Níl aon athruithe ar feitheamh.',
            pendingCount: '{count} ar feitheamh',
            acceptAllButton: 'Glac le gach',
            rejectAllButton: 'Diúltaigh go léir',
            acceptAllTitle: 'Glac le gach athrú ar feitheamh?',
            acceptAllConfirm: 'Glacfaidh sé seo le {count} athruithe ar feitheamh agus cruthófar leagan nua amháin nó níos mó. Lean ort?',
            editsHeading: 'Athruithe',
            editsEmpty: 'Níl aon athruithe le hathbhreithniú.',
            versionsHeading: 'Stair leagan',
            versionsEmpty: 'Níl aon leaganacha fós.',
            editLabel: 'Cuir in eagar',
            editLabelWithId: 'Cuir in eagar #{id}',
            acceptButton: 'Glac',
            rejectButton: 'Diúltaigh',
            accepting: 'Ag glacadh…',
            rejecting: 'Ag diúltú…',
            diffDeletedAria: 'Scriosta',
            diffInsertedAria: 'ionsáite',
            statusPending: 'Ar feitheamh',
            statusAccepted: 'Glactar leis',
            statusRejected: 'Diúltaithe',
            versionLabel: 'Leagan',
            versionAriaLabel: 'Leagan {n}',
            versionAriaLabelCurrent: 'Leagan {n} ({current})',
            versionCurrentTag: 'reatha',
            versionSourceLabel: 'Foinse',
            versionCreatedLabel: 'Cruthaithe',
            versionModelLabel: 'Múnla',
            sourceUpload: 'Uaslódáil',
            sourceUserUpload: 'Uaslódáil úsáideora',
            sourceAssistantEdit: 'Eagarthóir cúnta',
            sourceUserAccept: 'Ghlac an t-úsáideoir leis an eagarthóireacht',
            sourceUserReject: 'Dhiúltaigh an t-úsáideoir don eagarthóireacht',
            sourceGenerated: 'Gineadh',
            extractionExtracted: 'Téacs bainte amach',
            extractionFailed: 'Theip ar bhaint amach an téacs',
            extractionUnsupported: 'Níorbh fhéidir an téacs a bhaint amach',
            extractionPages: '{n} leathanach',
            extractionWords: '{n} focal',
            extractionReasonScanned: 'Is cosúil gur doiciméad scanta nó íomhá amháin é seo. Ní féidir le Forseti é a léamh fós — uaslódáil comhad téacsbhunaithe nó greamaigh an téacs go díreach.',
            extractionReasonEncrypted: 'Tá an comhad cosanta le pasfhocal. Bain an chosaint agus uaslódáil arís é.',
            extractionReasonMalformed: 'Níorbh fhéidir an comhad a pharsáil agus b’fhéidir go bhfuil sé truaillithe.',
            extractionReasonParserUnavailable: 'The server’s PDF reader is not installed correctly, so the text could not be read. This says nothing about your file. Ask an administrator to check the installation.',
            extractionReasonTooLarge: 'Tá an comhad ró-mhór le próiseáil.',
            extractionReasonTimeout: 'Thóg an phróiseáil rófhada. Bain triail as comhad níos lú nó níos simplí.',
            extractionReasonUnknown: 'Níl aon sonraí breise ar fáil.',
            // Sprint B - Forseti actions on the document review detail page.
            // EN values pending the next 56.x locale fan-out sprint.
            askForsetiButton: 'Ask Forseti',
            runRedlineButton: 'Run redline',
            forsetiModalTitleAsk: 'Ask Forseti — {name}',
            forsetiModalTitleRedline: 'Run redline — {name}',
            forsetiGuidanceAsk: 'Ask Forseti about this document. Pick a starter below or type your own question.',
            forsetiGuidanceRedline: 'Forseti will read this document and propose redline edits. Pick the redline starter below to begin — edits appear in the panel for you to accept or reject.',
            forsetiStarterRedline: 'Read attachment {id} and propose redline edits for legal and compliance issues',
            forsetiStarterSummarise: 'Summarise the key obligations in attachment {id}',
            forsetiStarterRisks: 'Review attachment {id} for legal risks and missing clauses',
            forsetiStarterFrameworks: 'Which compliance frameworks apply to attachment {id}?',
            textPaneLabel: 'Téacs an doiciméid',
            textPaneTitle: 'Téacs an doiciméid',
            textPaneVersion: 'Leagan {n}',
            textPaneEmpty: 'Níl aon téacs ar fáil don leagan seo. Níl téacs inbhainte ag PDFanna scanta ná ag cineálacha áirithe doiciméad.',
            downloadVersionAria: 'Download version {n}',
            downloadVersionButton: 'Download',
            rejectAllConfirm: 'This rejects {count} pending edits. The reason you give is recorded on each one.',
            rejectAllTitle: 'Reject all pending edits?',
            rejectReasonShown: 'Reason',
            rejectConfirmButton: 'Confirm rejection',
            loadRedlinesFailed: 'Failed to load redlines',
            acceptEditsFailed: 'Failed to accept edits',
            rejectEditsFailed: 'Failed to reject edits',
            batchPartialFailure: '{failed} of {total} failed: {reason}',
            actionFailed: 'Action failed',
            rejectReasonLabel: 'Reason for rejecting (optional)',
        },
        documentReviewIndex: {
            sidebarLabel: 'Athbhreithniú Cáipéise',
            pageTitle: 'Athbhreithniú Cáipéise',
            pageSubtitle: "Doiciméid i d'eagraíocht a bhfuil AI-athruithe molta acu ar feitheamh athbhreithnithe nó leaganacha a fuair Forseti.",
            colDocument: 'Cáipéis',
            colLinkedRecord: 'Taifead nasctha',
            colPendingEdits: 'Eagarthóireachtaí ar feitheamh',
            colLastActivity: 'Gníomhaíocht is déanaí',
            colOpen: 'Oscail',
            open: 'Oscail',
            empty: 'Níl aon doiciméad faoi athbhreithniú Forseti fós. Téann doiciméid isteach sa scuaine seo nuair a mholann Forseti eagarthóireacht a dhéanamh le linn comhrá, nuair a tháirgeann sreabhadh oibre AI líne dearg, nó nuair a sheolann tú doiciméad chun athbhreithniú leis an gcnaipe thuas.',
            loading: 'Á luchtú…',
            loadError: 'Theip ar luchtú na gcáipéisí faoi athbhreithniú.',
            retryButton: 'Bain triail eile as',
            noLinkedRecord: '—',
            pendingZero: 'Gan aon',
            sendForReviewButton: 'Seol cáipéis le haghaidh athbhreithnithe',
            sendForReviewTitle: 'Seol cáipéis le haghaidh athbhreithnithe',
            sendForReviewSubtitle: 'Uaslódáil doiciméad .txt, .md, .pdf nó .docx (suas le 50 MB). Cuirtear leis an scuaine athbhreithnithe é agus cuirfidh Forseti pas dearglíne ar fáil ina choinne.',
            sendForReviewTabUpload: 'Uaslódáil ceann nua',
            sendForReviewPickerLabel: 'Cuardaigh iatáin',
            sendForReviewPickerPlaceholder: 'Clóscríobh chun cuardach…',
            sendForReviewPickerEmpty: 'Níl aon iatáin chomhoiriúnacha. Bain triail as cuardach difriúil.',
            sendForReviewSubmit: 'Seol le haghaidh athbhreithnithe',
            sendForReviewCancel: 'Cealaigh',
            sendForReviewSubmitting: 'Á sheoladh…',
            sendForReviewError: 'Níorbh fhéidir an cháipéis a sheoladh le haghaidh athbhreithnithe. Bain triail eile as.',
            sendForReviewSuccess: 'Cáipéis seolta le haghaidh athbhreithnithe.',
            sendForReviewAcceptedExt: 'Glactha leis: {ext}.',
            sendForReviewMaxSize: 'Méid uasta {mb} MB.',
        },
        chats: {
            exportError: 'Theip ar an easpórtáil.',
        },
    },
    pdplEvidencePacks: {
        title: 'Paicéid Fianaise PDPL',
        subtitle: 'Aighneacht faoi rialú an chustaiméara chuig MoPS A05',
        nav: { emailProviders: 'Soláthraithe ríomhphoist' },
        providers: {
            pageTitle: 'Soláthraithe ríomhphoist',
            pageSubtitle: "Cumraigh soláthraí ríomhphoist amach d'eagraíocht le haghaidh paicéid fianaise PDPL a chur isteach chuig MoPS A05.",
            architectureRuleHeading: 'Riail ailtireachta (faoi ghlas)',
            architectureRuleBody: "Ní seoltóir é AesirX ar ríomhphoist na bpaicéad fianaise. Úsáideann gach aighneacht soláthraí atá faoi sealbhú agus rialú d'eagraíochta. Ní sheolann AesirX riamh ar do shon.",
            empty: 'Níl aon soláthraí cumraithe go fóill.',
            createCta: 'Cruthaigh soláthraí',
            statusPendingConsent: 'Ag fanacht ar thoiliú',
            statusPendingConnectionCheck: 'Ag fanacht ar sheic nasctha',
            statusActive: 'Gníomhach',
            statusConnectionCheckFailed: 'Theip ar an seic nasctha',
            statusDisabled: 'Díchumasaithe',
            acceptConsentCta: 'Glac le toiliú',
            runConnectionCheckCta: 'Reáchtáil seic nasctha',
            disableCta: 'Díchumasaigh',
            disableConfirm: 'Stopfaidh díchumasú an tsoláthraí seo na seoltaí díreacha go dtí go gcumrófar soláthraí nua agus go n-athdhearbhófar an toiliú. Lean ar aghaidh?',
            columnDisplayName: 'Ainm',
            columnFromAddress: 'Seoladh seoltóra',
            columnStatus: 'Stádas',
            columnLastCheck: 'Seic dheireanach',
            columnActions: 'Gníomhartha',
            createModalTitle: 'Soláthraí SMTP nua',
            createDisplayNameLabel: 'Ainm taispeána',
            createFromAddressLabel: 'Seoladh seoltóra',
            createReplyToLabel: 'Seoladh freagartha (roghnach)',
            createSmtpHostLabel: 'Óstach SMTP',
            createSmtpPortLabel: 'Port SMTP',
            createSmtpUsernameLabel: 'Ainm úsáideora SMTP',
            createSmtpPasswordLabel: 'Pasfhocal SMTP',
            createSmtpEncryptionLabel: 'Criptiúchán',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (intuigthe)',
            createSmtpEncryptionNone: 'Gan aon',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Ag teacht go luath',
            createOauthDisabledHint: 'Níl soláthraithe OAuth gníomhachtaithe go fóill. Úsáid SMTP go ceann tamaill.',
            createSubmitCta: 'Cruthaigh an soláthraí',
            createCancelCta: 'Cealaigh',
            createErrorInvalidFromAddress: 'Ní mór don seoladh seoltóra a bheith ina bhosca poist bailí.',
            createErrorDuplicateFromAddress: 'Tá soláthraí eile cumraithe cheana don seoladh seoltóra seo.',
            createErrorGeneric: 'Níorbh fhéidir an soláthraí a chruthú. Athbhreithnigh an fhoirm agus triail arís.',
            consentModalTitle: 'Dearbhaigh an riail ailtireachta',
            consentLocaleToggleEn: 'Béarla',
            consentLocaleToggleVi: 'Vítneaimis',
            consentAcknowledgeCta: 'Dearbhaím — glac leis an toiliú',
            consentCancelCta: 'Cealaigh',
            consentErrorMismatch: 'Athraíodh téacs an toilithe agus ní mheaitseálann sé an séanadh canónach a thuilleadh. Oscail an dialóg arís agus seol arís.',
            testModalTitle: 'Seic nasctha',
            testRunningCta: 'Á thástáil…',
            testRunCta: 'Reáchtáil seic nasctha',
            testRetryCta: 'Triail an seic arís',
            testSuccessHeading: "D'éirigh leis an nasc",
            testSuccessBody: 'Fíordheimhnithe in aghaidh {host}:{port} i {latencyMs} ms. Tá an soláthraí réidh do sheoltaí díreacha.',
            testFailureHeading: 'Theip ar an nasc',
            testFailureBody: 'Ní fhéadfaí an tástáil a chríochnú. Léigh an chúis thíos agus triail arís nuair atá sé ceartaithe.',
            testFailureReasonLabel: 'Cúis na teipe',
            testCloseCta: 'Dún',
            detailDrawerTitle: 'Sonraí an tsoláthraí',
            detailDrawerHistoryHeading: 'Stair na seiceanna nasctha',
            detailDrawerConsentHeading: 'Dearbhú toilithe',
            detailDrawerNoHistory: 'Níl aon seiceanna nasctha cláraithe go fóill.',
            detailDrawerNoConsent: 'Níl an toiliú dearbhaithe go fóill.',
            outcomeSuccess: 'Rath',
            outcomeFailure: 'Teip',
        },
        builder: {
            pageTitle: 'Cuir paicéad fianaise le chéile',
            pageSubtitle: 'Uaslódáil na 6 chuid, roghnaigh an cineál paicéid, agus cuir an ZIP deitéarmanach 8 gcuid le chéile lena chur isteach.',
            packTypeLabel: 'Cineál paicéid',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: 'Cuid 01 — Litir chumhdaigh (PDF)',
            part02AuthorityForm: 'Cuid 02 — Foirm an údaráis (PDF)',
            part03Supporting: 'Cuid 03 — Fianaise thacaíochta (≥1)',
            part04Safeguards: 'Cuid 04 — Cosaintí',
            part04SafeguardsRequiredOtia: '(riachtanach do OTIA)',
            part05PriorCorrespondence: 'Cuid 05 — Comhfhreagras roimhe seo (roghnach)',
            part06Signature: 'Cuid 06 — Fianaise sínithe agus stampa (≥1)',
            uploadPickFile: 'Roghnaigh comhad',
            uploadPickFiles: 'Roghnaigh comhaid',
            uploadRemoveFile: 'Bain',
            requiredMissing: 'Cuid riachtanach in easnamh',
            assembleCta: 'Cuir paicéad le chéile',
            assembling: 'Á chur le chéile…',
            assembleSuccessHeading: 'Paicéad curtha le chéile',
            assembleSuccessSummary: '{fileCount} comhad · {bytes} beart · SHA-256 {shaPrefix}…',
            composeNextCta: 'Cum dréacht ríomhphoist',
            warningsHeading: 'Foláirimh bhailíochtaithe',
            assembleError: 'Níorbh fhéidir an paicéad a chur le chéile. Athbhreithnigh na hionchuir agus triail arís.',
            forsetiAssistHeading: 'Cúnamh AI Forseti (roghnach)',
            forsetiAssistHint: 'Rith an PDPL Evidence Pack Assembly Advisor chun páirteanna in easnamh a thabhairt chun solais, próis litreach clúdaigh a dhréachtú agus bearnaí cosanta a chur in iúl. Ní AesirX an seoltóir — ní dhéanann an AI ach comhairle a thabhairt; ceadaíonn tú gach artafact.',
            forsetiAssistCta: 'Oscail leabharlann sreabhadh oibre Forseti',
            forsetiAiRunIdLabel: 'Aitheantas reáchtála AI Forseti nasctha (greamaigh chun cúnamh AI a thaifeadadh)',
        },
        draft: {
            pageTitle: 'Dréacht ríomhphoist',
            pageSubtitle: 'Cum agus eagraigh an dréacht ríomhphoist. Tagann seoladh díreach in eisiúint níos déanaí.',
            composeCta: 'Cum dréacht',
            composing: 'Á chumadh…',
            noDraftYet: 'Gan dréacht fós. Cum ceann le tosú.',
            localeToggleLabel: 'Teanga',
            localeEn: 'Béarla',
            localeVi: 'Vítneaimis',
            subjectLabel: 'Ábhar',
            bodyLabel: 'Corp',
            saveCta: 'Sábháil athruithe',
            saving: 'Á shábháil…',
            saveError: 'Níorbh fhéidir an dréacht a shábháil. Triail arís.',
            manifestSummaryLabel: 'Achoimre an manifest',
            coverLetterLabel: 'Litir chumhdaigh (PDF)',
            coverLetterDownloadCta: 'Íoslódáil PDF',
            editHistoryHeading: 'Stair eagarthóireachta',
            editHistoryEmpty: 'Níl aon eagarthóireacht fós.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 fógra cainéal neamhoifigiúil',
            uploadSentProofCta: 'Uaslódáil cruthú seolta de láimh',
            versionLabel: 'Leagan',
            statusDraft: 'Dréacht',
            statusEdited: 'Eagraithe',
            statusFinalized: 'Críochnaithe',
        },
        sentProof: {
            modalTitle: 'Uaslódáil cruthú seolta de láimh',
            architectureLockNote: 'Ní hé AesirX an seoltóir. Gabhann an cruthú seo an seoladh láimhe a rinne tú trí do chliant ríomhphoist féin.',
            fileLabel: 'Comhad cruthaithe (.eml, .png, .jpg)',
            envelopeFromLabel: 'Ó',
            envelopeToLabel: 'Chuig',
            envelopeSubjectLabel: 'Ábhar (ag an seoladh)',
            envelopeSentAtLabel: 'Seolta ag',
            envelopeAttachmentShaLabel: 'SHA-256 an cheangaltáin (ag an seoladh)',
            uploadCta: 'Uaslódáil cruthú',
            uploading: 'Á uaslódáil…',
            uploadSuccess: 'Cruthú seolta uaslódáilte.',
            uploadErrorInvalidMime: 'Cineál comhaid nach gceadaítear. Úsáid .eml, .png, .jpg nó .jpeg.',
            uploadErrorGeneric: 'Níorbh fhéidir an cruthú a uaslódáil. Triail arís.',
            existingProofsHeading: 'Cruthuithe seolta cláraithe',
            existingProofsEmpty: 'Níl aon chruthuithe seolta cláraithe go fóill.',
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Paicéad fianaise',
            notFoundMessage: "Níor aimsíodh an pacáiste — b'fhéidir gur baineadh é nó tá an nasc mícheart.",
            manifestHeading: 'Manifest',
            dossierLabel: 'Comhad',
            packShaLabel: 'SHA-256 an phaicéid',
            fileCountLabel: 'Comhaid',
            bytesLabel: 'Méid',
            composeDraftCta: 'Cum dréacht',
            sendComingSoonCta: 'Seoladh díreach: ag teacht go luath',
            sendComingSoonHint: 'Tá sreabhadh an tseolta dhírigh á fhorbairt go gníomhach. Bain úsáid as "Uaslódáil cruthú seolta de láimh" ar an leathanach dréachta go dtí go seachadtar é.',
            sendCta: 'Seol tríd an soláthraí cumraithe',
            sendDisabledNoProviderHint: 'Cumraigh soláthraí ríomhphoist faoi smacht an chustaiméara ar dtús. Ní sheolann AesirX thar do cheann riamh.',
            timelineHeading: 'Stair seolta',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Seol an paicéad fianaise',
            providerPickerLabel: 'Soláthraí ríomhphoist cumraithe',
            providerPickerHint: 'Ní sheolann AesirX thar do cheann riamh. Roghnaigh ceann de na soláthraithe cumraithe atá agat thíos.',
            providerPickerEmpty: 'Níl aon soláthraí gníomhach cumraithe. Cumraigh ceann ar an leathanach Soláthraithe Ríomhphoist sula seolfaidh tú.',
            providerPickerEmptyCta: 'Oscail Soláthraithe Ríomhphoist',
            draftPickerLabel: 'Dréacht ríomhphoist',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: 'Níor cumadh aon dréachtaí don phaicéad seo go fóill. Cum ceann ar an leathanach dréachta ar dtús.',
            recipientToLabel: 'Chuig',
            recipientToHint: 'Seoladh ríomhphoist amháin nó níos mó, scartha le camóga.',
            recipientCcLabel: 'Cc (scartha le camóga)',
            recipientCcHint: 'Roghnach. Liosta seoltaí ríomhphoist scartha le camóga.',
            recipientBccLabel: 'Bcc (scartha le camóga)',
            recipientBccHint: 'Roghnach. Liosta seoltaí ríomhphoist scartha le camóga.',
            consentReaffirmHeading: 'Daingnigh toiliú an tsoláthraí arís',
            submitCta: 'Seol anois',
            sending: 'Á sheoladh…',
            cancelCta: 'Cealaigh',
            sendSuccess: 'Taifeadadh an seoladh. Aistríodh an paicéad tríd an soláthraí cumraithe atá agat.',
            sendErrorGeneric: 'Níorbh fhéidir an paicéad a sheoladh. Athbhreithnigh an earráid thíos agus bain triail eile as.',
            sendErrorAlreadyFinalized: 'Baineadh úsáid as an dréacht seo cheana le haghaidh seolta rathúil. Cum dréacht nua sula seolann tú arís.',
            sendErrorInvalidRecipients: 'Níl seoladh amháin nó níos mó de na faighteoirí ina bhoscaí ríomhphoist bailí.',
            // Timeline
            timelineEmpty: 'Níor taifeadadh aon seolta go fóill. Bain úsáid as "Seol tríd an soláthraí cumraithe" thuas le haistriú tríd an soláthraí SMTP / Microsoft 365 / Google Workspace atá faoi do smacht.',
            timelineProviderLabel: 'Seolta tríd an soláthraí cumraithe agat',
            timelineSentAtLabel: 'Seolta',
            timelineRecipientLabel: 'Faighteoir',
            timelineOutcomeSent: 'Seolta',
            timelineOutcomeFailed: 'Theip air',
            timelineViewDetailsCta: 'Féach na sonraí',
            // Authority-response chips
            authorityChipNone: 'Ag fanacht ar fhreagra',
            authorityChipAcknowledgement: 'Admháil',
            authorityChipAcceptance: 'Glacadh',
            authorityChipRejection: 'Diúltú',
            authorityChipSupplementRequest: 'Iarratas ar fhorlíonadh',
            authorityChipNoResponse: 'Gan freagra',
            // Hard-copy chips
            hardCopyChipNone: 'Gan obair leantach chruachóipe',
            hardCopyChipPending: 'Postáilte — seachadadh ar feitheamh',
            hardCopyChipDelivered: 'Seachadta',
            // Drawer
            drawerTitle: 'Taifead seolta',
            drawerEnvelopeHeading: 'Clúdach',
            drawerEnvelopeFromLabel: 'Ó',
            drawerEnvelopeToLabel: 'Chuig',
            drawerEnvelopeCcLabel: 'Cc',
            drawerEnvelopeBccLabel: 'Bcc',
            drawerEnvelopeSubjectLabel: 'Ábhar',
            drawerEnvelopeMessageIdLabel: 'ID-teachtaireachta',
            drawerOutcomeHeading: 'Toradh',
            drawerSnapshotHeading: 'Léaró den slabhra coimeádta',
            drawerSnapshotPackShaLabel: 'SHA-256 an phaicéid ag am seolta',
            drawerSnapshotDraftVersionLabel: 'Leagan an dréachta ag am seolta',
            drawerSnapshotAttachmentLabel: 'ZIP a tarchuireadh',
            drawerSnapshotAttachmentDownloadCta: 'Íoslódáil ZIP',
            drawerAuthorityResponseHeading: 'Freagra an údaráis',
            drawerHardCopyHeading: 'Obair leantach chruachóipe',
            drawerSentByLabel: 'Seolta ag',
            drawerSendIdLabel: 'Aitheantas taifid seolta',
            drawerSmtpResponseLabel: 'Freagra SMTP',
            drawerFailureReasonLabel: 'Cúis na teipe',
            drawerLatencyLabel: 'Folaitheacht',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Glactha',
            drawerRejectedLabel: 'Diúltaithe',
            drawerCloseCta: 'Dún',
            // Authority-response panel
            authorityRecordCta: 'Taifead freagra',
            authorityReclassifyCta: 'Athaicmigh',
            authorityClassificationLabel: 'Aicmiú',
            authorityReceivedAtLabel: 'Faighte ag',
            authorityFreeTextLabel: 'Nótaí (roghnach)',
            authorityEvidenceLabel: 'Comhad fianaise (.eml / .pdf / .png / .jpg — roghnach)',
            authorityReasonLabel: 'Cúis an athaicmithe',
            authorityHistoryHeading: 'Stair na n-athaicmithe',
            authorityHistoryEmpty: 'Níor taifeadadh aon athaicmiú.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Sábháil an freagra',
            authoritySubmitting: 'Á shábháil…',
            authorityErrorInvalidMime: 'Níl an cineál comhaid fianaise sin ceadaithe. Bain úsáid as .eml, .pdf, .png nó .jpg.',
            authorityErrorGeneric: 'Níorbh fhéidir an freagra a shábháil. Bain triail eile as le do thoil.',
            authorityRecordedAtLabel: 'Taifeadta ag',
            authorityRecordedByLabel: 'Taifeadta ag',
            authorityEvidenceFilenameLabel: 'Fianaise',
            authorityEvidenceDownloadCta: 'Íoslódáil fianaise',
            // Hard-copy panel
            hardCopyRecordCta: 'Logáil obair leantach chruachóipe',
            hardCopyConfirmCta: 'Deimhnigh an seachadadh',
            hardCopyEditNotesCta: 'Cuir nótaí seachadta in eagar',
            hardCopyPostedAtLabel: 'Postáilte ag',
            hardCopyCourierLabel: 'Teachtaire',
            hardCopyTrackingLabel: 'Uimhir rianaithe',
            hardCopyRecipientLabel: 'Faighteoir ar an lipéad',
            hardCopyReceiptLabel: 'Fianaise admhála (.pdf / .png / .jpg — roghnach)',
            hardCopyReceiptDownloadCta: 'Íoslódáil admháil',
            hardCopyDeliveredAtLabel: 'Seachadta ag',
            hardCopyDeliveryNotesLabel: 'Nótaí seachadta (roghnach)',
            hardCopySubmitCta: 'Sábháil an obair leantach',
            hardCopySubmitting: 'Á shábháil…',
            hardCopyErrorInvalidMime: 'Níl an cineál comhaid admhála sin ceadaithe. Bain úsáid as .pdf, .png nó .jpg.',
            hardCopyErrorGeneric: 'Níorbh fhéidir an obair leantach a shábháil. Bain triail eile as le do thoil.',
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
            personal_data_processing_services: 'Personal data processing services your organization provides to others as a business',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            // Sprint 62.57.0 Slice E (§12.6.1) — the installed declarations' item text under each value.
            value_sources_label: 'In the installed framework',
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: 'The regulated-entity role your organization holds in the credit-cooperative and deposit-protection system, if any. Requirements addressed to a role apply only when you select it.',
            personal_data_processing_services: 'The personal data processing services your organization provides, or intends to provide, to other organizations as a business. Select only what your organization does: nothing is filled in from your other records. Requirements that depend on a service apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Providing and operating automated systems or software that process personal data on behalf of a controller',
                creditworthiness_scoring: 'Scoring or rating creditworthiness from personal data',
                online_collection_and_processing: 'Collecting and processing personal data online for others',
                health_service_data_processing: 'Processing personal data in health services',
                education_monitoring_data_processing: 'Processing personal data to monitor learners in education services',
                personal_data_analytics_and_mining: 'Analysing or mining personal data',
                personal_data_encryption: 'Encrypting personal data for others',
                automated_processing_emerging_technologies: 'Automated processing of personal data with emerging technologies',
                location_data_platform: 'Operating a platform that processes location data',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Your organization provides or operates a system or software that processes personal data automatically for a controller.',
                creditworthiness_scoring: 'Your organization scores or rates the creditworthiness of people for others, using their personal data.',
                online_collection_and_processing: 'Your organization collects and processes personal data online as a service to other organizations.',
                health_service_data_processing: 'Your organization processes personal data as part of the health services it provides to others.',
                education_monitoring_data_processing: 'Your organization processes personal data to monitor learners as part of the education services it provides to others.',
                personal_data_analytics_and_mining: 'Your organization analyses or mines personal data as a service to other organizations.',
                personal_data_encryption: 'Your organization encrypts personal data as a service to other organizations.',
                automated_processing_emerging_technologies: 'Your organization processes personal data automatically using emerging technologies, as a service to others.',
                location_data_platform: 'Your organization operates a platform that processes location data for others.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            // Sprint 62.57.0 Slice E (D48) — an unstated condition read as out of scope.
            out_of_scope_not_stated: 'Applies only where your organization has recorded it (none recorded)',
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
                personal_data_processing_services: 'personal data processing services provided to others',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — English until the Phase 25 locale pass (R-57).
    org_identity: {
        nav_label: 'Organisation identity',
        module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
        title: 'Organisation identity',
        description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
        section_names: 'Names and offices',
        section_registration: 'Registration',
        section_contact: 'Contact details',
        section_representative: 'Legal representative',
        field_legal_name: 'Legal name',
        field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
        field_trading_name: 'Trading name',
        field_trading_name_help: 'A short or trading name, if the organisation uses one.',
        field_head_office_address: 'Head office address',
        field_transaction_office_address: 'Transaction office address',
        field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
        field_registration_number: 'Registration Number',
        field_registration_instrument: 'Registration document',
        field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
        field_registration_issuer: 'Issued by',
        field_registration_issue_date: 'Date of issue',
        field_registration_issue_place: 'Place of issue',
        field_tax_code: 'Tax Code',
        field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
        field_phone: 'Phone',
        field_email: 'Email',
        field_website: 'Website',
        field_website_help: 'Starting with https:// or http://.',
        field_legal_representative_name: 'Full name',
        field_legal_representative_title: 'Position',
        save: 'Save identity',
        saving: 'Saving…',
        saved: 'Organisation identity saved.',
        save_failed: 'The organisation identity could not be saved.',
        fix_fields: 'Some fields need correcting before the identity can be saved.',
        load_failed: 'The organisation identity could not be read.',
        retry: 'Retry',
        conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
        conflict_reload: 'Reload the saved identity',
        conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
        loading: 'Loading the organisation identity',
        read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
        restricted_title: 'You do not have access to the organisation identity',
        restricted_message: 'Ask an administrator for the settings read permission.',
        last_saved: 'Last saved {date}',
        never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
        error_too_long: 'This value is too long.',
        error_invalid_date: 'Enter a real date.',
        error_invalid_email: 'Enter an email address, for example name@example.com.',
        error_invalid_url: 'Enter a web address starting with https:// or http://.',
        error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
        error_invalid: 'This value is not accepted.',
    },
    form_prefill: {
        draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
        fill_from_record: 'Fill from record',
        fill_from_named_record: 'Fill from {record}',
        filling: 'Filling…',
        result: 'Filled {filled} fields; {differs} differ from the record.',
        failed: 'The form could not be filled from the record.',
        from_source: 'From {source}',
        not_recorded_in_source: 'Not recorded in {source}',
        family_organisation: 'the organisation identity',
        family_dpo: 'the DPO appointment',
        family_dpia: 'the DPIA',
        family_transfer: 'the transfer assessment',
        family_incident: 'the incident',
        family_dossier: 'the dossier filing',
        record_generic: 'the record',
        hint: 'Fills empty fields only. A value you entered is never replaced.',
    },
    // Owner rulings 2026-10-26 — idea #717 and OQ-86-6. English until the
    // Phase 25 locale work (R-57); structural parity with en.ts.
    customs_tax_exports: {
        titles: {
            dossiers: 'Customs tax dossiers and documents received',
            payments: 'Customs tax payments',
            duty_free_lists: 'Duty-free lists',
            examinations: 'Customs tax examinations',
            debt_instruments: 'Customs tax debt instruments',
            transition_statement: 'Transition statement',
        },
        sections: {
            dossiers: 'Dossiers',
            documents_received: 'Documents received',
            figures_side_by_side: 'Stated figures side by side',
            deductions: 'Deductions as customs recorded them',
            usage_notifications: 'Usage notifications recorded',
            steps: 'Steps',
            office_examinations: 'At the customs office',
            determinations: 'Determinations recorded',
            refund_records: 'Refund records',
            enforcement_notices: 'Enforcement notices received',
            instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
            noted_alternatives: 'Noted alternative readings of the usage schedule',
        },
        columns: {
            paper_form_number: 'Paper form number',
            electronic_indicator_set: 'Electronic indicator set',
            matched_by_title: 'Matched by title',
            issued_by_authority: 'Issued by the authority',
            dossier: 'Dossier',
            procedure: 'Procedure',
            register_role: 'Register role',
            customs_records: 'Customs records',
            channel: 'Channel',
            signed_off_on: 'Signed off on',
            lodged_on: 'Lodged on',
            confirmation_answer: 'Confirmation answer',
            install_mode_when_made: 'Install mode when made',
            review_approval: 'Review approval',
            document: 'Document',
            kind: 'Kind',
            number_as_printed: 'Number as printed',
            received_on: 'Received on',
            stated_figures: 'Stated figures',
            stated_deadlines: 'Stated deadlines',
            reply: 'Reply',
            payment: 'Payment',
            paid_on: 'Paid on',
            stated_amount: 'Stated amount',
            payer_class: 'Payer class',
            payment_date_basis: 'Payment-date basis',
            document_number: 'Document number',
            pays: 'Pays',
            receipt: 'Receipt',
            received_document: 'Received document',
            figure: 'Figure',
            payment_figure_as_stated: 'Payment figure (as stated)',
            document_figure_as_stated: 'Document figure (as stated)',
            comparison: 'Comparison',
            closed: 'Closed',
            list: 'List',
            reference: 'Reference',
            acknowledged_on: 'Acknowledged on',
            imports_ended_on: 'Imports ended on',
            expires_on: 'Expires on',
            deduction: 'Deduction',
            customs_record: 'Customs record',
            goods_as_stated: 'Goods as stated',
            unit_as_stated: 'Unit as stated',
            cleared_as_stated: 'Cleared (as stated)',
            remaining_as_stated: 'Remaining (as stated)',
            notification: 'Notification',
            notice_kind: 'Notice kind',
            financial_year_ended_on: 'Financial year ended on',
            notified_on: 'Notified on',
            examination: 'Examination',
            purpose: 'Purpose',
            decision_signed_on: 'Decision signed on',
            decision_sent_on: 'Decision sent on',
            announced_on: 'Announced on',
            re_examination_of: 'Re-examination of',
            step: 'Step',
            occurred_on: 'Occurred on',
            refused_to_sign: 'Refused to sign',
            record: 'Record',
            authority_request: 'Authority request',
            explanation_sent_on: 'Explanation sent on',
            outcome_on: 'Outcome on',
            instrument: 'Instrument',
            role: 'Role',
            effective_on: 'Effective on',
            instalment_band: 'Instalment band',
            approved_on: 'Approved on',
            stated_debt_figure: 'Stated debt figure',
            guarantee_valid_until: 'Guarantee valid until',
            record_type: 'Record type',
            governing_date: 'Governing date',
            outcome: 'Outcome',
            reading: 'Reading',
            open: 'Open',
            determination: 'Determination',
            determined_on: 'Determined on',
            refund_record: 'Refund record',
            dossier_register_role: 'Dossier register role',
            classification: 'Classification',
            decision_document: 'Decision document',
            offset_notice_document: 'Offset notice received',
            post_refund_examination: 'Post-refund examination',
            stated_case: 'Case as the decision states it',
            stated_case_source: 'Case stated in',
            stated_case_citation: 'Provision cited for the case',
            received_as: 'Received as',
            document_date: 'Document date',
            amount_stated_for_notice: 'Amount stated for this notice',
            financial_year_reported: 'Financial year reported (ending)',
            noted_alternative_year: 'Noted alternative financial year (ending)',
            note: 'Note',
            reading_kind: 'Kind of reading',
            classification_case: 'Case the classification notice states',
            acceptance_notice: "Acceptance notice (the authority's clock)",
            handling_time: "Handling time (the authority's clock)",
            stated_case_choices: 'Installed cases matched (as stated)',
            stated_due_on: 'Due date as stated',
            period_ceiling: 'Longest instalment period',
            stated_plan_end: 'Latest end the Law allows',
            enforcement_from: "Enforcement threshold (the authority's clock)",
        },
        cells: {
            review_approved: 'Approved by {name} on {date}',
            review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
            register_role_refund: 'Refund',
            register_role_overpaid_refund: 'Refund of an overpaid amount',
            noted_not_due: 'Noted reading, not a due date',
            received_as_third_party: 'Received as a third party',
            received_as_enforced_taxpayer: 'The taxpayer under enforcement',
            clock_authority_ends: "The authority's clock ends {date} ({clock})",
            clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
            clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
            clock_period_ends: 'The period ends {date} ({clock})',
            clock_closed: 'Closed on {date}',
            clock_not_applicable: 'Not applicable to this record ({clock})',
            clock_not_live: 'Not in force yet ({clock})',
            clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
            clock_not_declared: 'No installed clock declares it',
            clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
            clock_no_date: 'No date ({clock})',
            plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
            plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
            clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
            clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
            meta_boundary: 'Boundary ({framework}): {summary}',
            meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
            meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
            meta_statement_for: 'Statement for: {date}',
            meta_nothing_remains: 'Nothing remains open under the earlier rules.',
            meta_records_remain: 'Records remain open under the earlier rules.',
            meta_transition_unavailable: 'The transition reading is not available on this installation.',
            role_notified_debt: 'Tax debt as notified',
            role_freeze: 'Freeze',
            role_freeze_end: 'End of a freeze',
            role_write_off: 'Write-off',
            role_restoration: 'Restoration',
            role_instalment_request: 'Instalment request',
            role_instalment_notice: 'Instalment notice',
            received_document_label: '{kind} {number}, dated {date}',
            received_document_label_no_number: '{kind}, received {date}',
            case_no_longer_installed: 'A case from an instrument that is no longer installed',
        },
    },
    cross_border_filing_fence: {
        register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
        refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
        open_register: 'Open {register}',
        option_owned_elsewhere: '{form} — recorded in {register}',
        picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
        advisory_title: 'This form is now recorded in {register}',
        advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
        list_marker: 'Now recorded in {register}',
    },
    // Sprint 63.43.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    sector_overlays_module: {
        nav: {
            overview: 'Overview',
        },
        overview: {
            title: 'Sector Overlays',
            subtitle: 'What your active sector overlays bring into use, grouped by sector.',
            loading: 'Loading sector overlays…',
            load_failed: 'Sector overlays could not be loaded.',
            not_entitled_title: 'Sector Overlays is not in your package',
            not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
            empty_title: 'No sector overlay is active',
            empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
            open_catalog: 'Open the Overlay Catalog',
            organization_profile: 'Organization profile',
            organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
            provided_by: 'Provided by',
            views_heading: 'Views in this sector',
            shared_views_heading: 'Shared views this sector uses',
            shared_view_location: 'In {location}',
            open_view_aria: '{view} — {location}',
            no_visible_views: "None of this sector's views is open to your role.",
            sector_count_one: '1 overlay active',
            sector_count: '{count} overlays active',
        },
    },
    // Sprint 62.47.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    product_identification: {
        common: {
            retry: 'Retry',
            loading: 'Loading…',
            cancel: 'Cancel',
            close: 'Close',
            no_access_title: 'No access',
            no_access_message: 'Your role cannot read this register.',
            install_direct: 'Installed Direct',
            install_reference: 'Installed for reference',
            reference_mode_body: 'The overlay that declares this register is installed for reference. Scope determinations can still be recorded and read here, and the sector watch read; no duty and no clock is run from them until the overlay is installed Direct from the Overlay Catalog.',
        },
        participation: {
            compulsory: 'Compulsory',
            voluntary: 'Voluntary',
            outside_scope: 'Outside the scope',
            compulsory_rules_disapplied: 'Compulsory rules disapplied',
            not_determined: 'Open determination',
            unclassified: 'No class declared',
        },
        scope: {
            title: 'Product Scope Determinations',
            subtitle: 'A person’s recorded reading, per product line, of whether the identification and traceability regime reaches it, with the instrument relied on and a second person’s review. This register records the reading; it never decides it.',
            page_guide: 'Record one determination per product line from the options the installed pack declares. Nothing is preselected or suggested. Recording again keeps the earlier version, and compulsory and voluntary lines are always counted separately.',
            load_failed: 'The scope determinations could not be loaded.',
            none_declared: 'No installed framework declares the scope-determination register.',
            applicability_heading: 'Who the regime reaches, as the installed pack states it',
            mandatory_scope: 'Compulsory',
            voluntary_scope: 'Voluntary',
            out_of_scope: 'Outside the scope',
            mandatory_rules_disapplied: 'Compulsory rules disapplied',
            list_status_note: 'Status of the lists',
            counts_heading: 'Product lines by class',
            filter_label: 'Class',
            filter_all: 'All classes',
            table_caption: 'Current determination per product line',
            col_product: 'Product line',
            col_class: 'Class',
            col_option: 'Determination',
            col_instrument: 'Instrument relied on',
            col_determined_by: 'Determined by',
            col_reviewed_by: 'Reviewed by',
            col_determined_on: 'Determined on',
            col_version: 'Version',
            col_actions: 'Actions',
            empty: 'No determination is recorded yet.',
            empty_filtered: 'No product line is in this class.',
            record_button: 'Record a determination',
            record_again: 'Record again',
            history_button: 'History',
            duties_button: 'Duties',
            form_title: 'Record a scope determination',
            field_product: 'Product line',
            field_framework: 'Framework',
            choose: 'Choose…',
            no_default_note: 'Nothing is preselected and nothing is suggested: the reading is yours.',
            instrument_required: 'Required for the determination chosen.',
            cited_basis_note_heading: 'The installed pack’s reading of the lists',
            reviewer_same: 'The reviewer must be a person other than the one who determined it.',
            field_required: 'This field is required.',
            save: 'Save',
            saving: 'Saving…',
            save_failed: 'The determination could not be saved.',
            saved: 'Determination recorded.',
            products_failed: 'The product lines could not be loaded.',
            no_products: 'No product line is in the product register yet.',
            conflict: 'Someone recorded a determination for this line meanwhile. Reload the register and try again.',
            history_title: 'Determination history — {product}',
            history_failed: 'The history could not be loaded.',
            status_current: 'Current',
            status_superseded: 'Superseded',
            duties_title: 'Duties for {product}',
            duties_failed: 'The duties could not be loaded.',
            duties_no_task: "No task or deadline is created here from these duties. Where the decree states a period, the requirement's own text states it.",
            duties_reference: 'Installed for reference: no duty is shown. Install the overlay Direct to run them.',
            duties_compulsory_heading: 'Compulsory',
            duties_voluntary_heading: 'Voluntary',
            duties_role_heading: 'Attached to a role',
            duties_open: 'Open determination: the scope of this line is still to be determined. This is not a finding that no duty applies.',
            duties_counts: 'Compulsory: {applies} · Applies to how you take part (voluntary): {voluntary}',
            duties_none: 'No requirement is declared.',
            col_requirement: 'Requirement',
            col_state: 'Status for this line',
            col_reference: 'Reference',
        },
        duty_state: {
            applies: 'Applies (compulsory)',
            applies_voluntarily: 'Applies to how you take part (voluntary)',
            not_raised_by_option: 'Not raised by this determination',
            open_determination: 'Open determination',
            role_attached: 'Attached to a role; read its condition',
            voluntary_practice: 'Voluntary practice',
        },
        watch: {
            title: 'Sector Watch',
            subtitle: 'What the installed pack tracks as still to be issued by an authority, with the status it records and when it must be checked again, and the clocks the pack places on authorities. Nothing here is owed by your organisation.',
            page_guide: 'Read each tracked output as the installed pack states it. A recheck date that has passed reads as recheck overdue; nothing is re-dated. The clocks on authorities run on the authority, not on your organisation.',
            load_failed: 'The sector watch could not be loaded.',
            items_heading: 'Outputs still to be issued',
            items_caption: 'Tracked outputs, as the installed pack states them',
            col_output: 'Output',
            col_status: 'Status',
            col_basis: 'Basis of the status',
            col_checked: 'Checked on',
            col_recheck: 'Recheck due',
            col_install: 'Install mode',
            status_not_issued: 'Not issued',
            status_not_retrieved: 'Named, text not retrieved',
            status_announced_operational: 'Announced operational',
            status_issued: 'Issued',
            recheck_overdue: 'Recheck overdue',
            existing_basis: 'Instruments recorded as the existing basis',
            note_label: 'Note',
            items_none: 'No installed framework tracks an output for this register.',
            clocks_heading: 'Clocks on authorities and periods not stated',
            clocks_caption: 'Records the installed pack places on authorities, or whose period it does not state',
            col_clock: 'Record',
            col_kind: 'What it means for your organisation',
            col_period: 'Period as stated',
            col_binds: 'Applies from',
            authority_clock: 'Clock on the ministry — nothing is owed by the organisation',
            set_by_instrument: 'Set by the ministry instrument or sector law',
            no_period: 'No period stated',
            period_months: '{n} months',
            period_days: '{n} days',
            period_other: '{n} ({unit})',
            not_stated: 'Not stated',
            clocks_none: 'No clock on an authority is declared.',
        },
    },
    // Sprint 62.57.0 Slice E (§12.6) — the processing-service certificate register.
    service_certificates: enServiceCertificates,
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        dossierFormsSource: 'Tuarascálacha agus fógraí comhlánaithe don sainchomhad',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        sourceStatusOther: 'Other status',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=ga.js.map